import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Box, Text, Badge, ButtonLink, Button, IconRocket, Dialog, Stack, List, Bleed, Columns, Column, TextLinkButton, IconInfo, IconTick, Heading, IconPromote, IconTime, IconPeople, Loader, IconResume, IconRecommended, IconVisibility, Strong, IconRenderer, IconMinus, TooltipRenderer, Inline, Divider, useResponsiveValue, ContentBlock } from "braid-design-system";
import React, { createContext, useContext, useState, useRef, useEffect, createElement } from "react";
import { calc } from "@vanilla-extract/css-utils";
import { useTranslations, useLanguage } from "@vocab/react";
import { vars } from "braid-design-system/css";
import { translations } from "../../../../../ProductSelection/components/elements/Banner/.vocab/index.mjs";
import { translations as translations$3 } from "../../../../../ProductSelection/components/elements/CallToAction/.vocab/index.mjs";
import { DisabledButton } from "../../elements/CallToAction/DisabledButton/DisabledButton.mjs";
import { useMelwaysLink } from "@seek/melways-react";
import { translations as translations$1 } from "../../../../../ProductSelection/components/elements/CallToAction/RedirectButton/.vocab/index.mjs";
import { translations as translations$2 } from "../../../../../ProductSelection/components/elements/CallToAction/SelectButton/.vocab/index.mjs";
import { formatMinorUnits } from "@seek/currency";
import { translations as translations$4 } from "../../../../../ProductSelection/components/elements/Custom/PayPerApplicationContent/.vocab/index.mjs";
import { translations as translations$6 } from "../../../../../ProductSelection/components/elements/Expectations/.vocab/index.mjs";
import { translations as translations$5 } from "../../../../../ProductSelection/components/shared/HelpTip/HelpTipDialog/.vocab/index.mjs";
import { Image as Image$1 } from "../../elements/Image/Image.mjs";
import { translations as translations$7 } from "../../../../../ProductSelection/components/elements/Inclusions/.vocab/index.mjs";
import { translations as translations$8 } from "../../../../../ProductSelection/components/elements/Price/TaxText/.vocab/index.mjs";
import { translations as translations$9 } from "../../../../../ProductSelection/components/elements/Price/CappedPrice/.vocab/index.mjs";
import { translations as translations$a } from "../../../../../ProductSelection/components/elements/Price/DetailsText/.vocab/index.mjs";
import { translations as translations$b } from "../../../../../ProductSelection/components/elements/Price/MainText/.vocab/index.mjs";
import { BarChart } from "../../elements/Rating/BarChart/BarChart.mjs";
import { Circles } from "../../elements/Rating/Circles/Circles.mjs";
import { PieChart } from "../../elements/Rating/PieChart/PieChart.mjs";
import { Section } from "../../panels/ColumnPanel/Section.mjs";
import { translations as translations$c } from "../../../../../ProductSelection/components/layouts/shared/components/Disclaimer/.vocab/index.mjs";
import { Grid } from "../shared/components/Grid/Grid.mjs";
import { GridColumn } from "../shared/components/Grid/GridColumn/GridColumn.mjs";
import { GridRow } from "../shared/components/Grid/GridRow/GridRow.mjs";
import { Upsells } from "../shared/components/Upsells/Upsells.mjs";
import useDeepCompareEffect from "use-deep-compare-effect";
import { getPanelStyle } from "../shared/utils/getPanelStyle/getPanelStyle.mjs";
import { ComparativesMatrix } from "./components/ComparativesMatrix/ComparativesMatrix.mjs";
import { GRID_COLUMNS } from "../shared/components/Grid/Grid.constants.mjs";
import { gridRowPanelConstrainedWidth, gridContainer, gridContainerSticky, gridContainerStickyActive, marginAuto } from "./GridLayout.css.mjs";
import { panelShared } from "../shared/SharedLayout.css.mjs";
const defaultContext = {
  hirer: { id: "" },
  products: { productSetId: "" }
};
const ProductSelectionContext = createContext(defaultContext);
const filterProductForDisplay = (product) => {
  var _a;
  return ((_a = product.panel) == null ? void 0 : _a.variant) !== "hidden";
};
const getProductsForDisplay = ({
  parentProducts,
  addOns,
  singleParentProduct
}) => {
  const adProducts = parentProducts.filter(filterProductForDisplay);
  const addOnProductsList = addOns.filter(
    ({ addOn }) => filterProductForDisplay(addOn)
  );
  const upsellProducts = (singleParentProduct == null ? void 0 : singleParentProduct.upgradePaths.filter(filterProductForDisplay)) ?? [];
  const allProducts = [
    ...adProducts,
    ...addOnProductsList.map(({ addOn }) => addOn),
    ...upsellProducts
  ];
  return { adProducts, addOnProductsList, upsellProducts, allProducts };
};
const WIDE_BACKGROUND = {
  new: "positiveLight",
  featured: "promote",
  beta: "promoteLight",
  current: "info",
  recommended: "promote",
  "recommended-featured": "promote",
  "limited-time-offer": "positiveLight"
};
const WIDE_LABEL = {
  new: "New",
  featured: "Featured ad",
  beta: "Beta",
  current: "Current",
  recommended: "Recommended",
  "recommended-featured": "Recommended",
  "limited-time-offer": "Limited Time Offer"
};
const NARROW_TONE = {
  new: "positive",
  featured: "promote",
  beta: "promote",
  current: "info",
  recommended: "promote",
  "recommended-featured": "promote",
  "limited-time-offer": "positive"
};
const NARROW_LABEL = {
  new: "New",
  featured: "Featured",
  beta: "Beta",
  current: "Current",
  recommended: "Recommended",
  "recommended-featured": "Recommended",
  "limited-time-offer": "Limited Time Offer"
};
const NARROW_WEIGHT = {
  new: "regular",
  featured: "strong",
  beta: "regular",
  current: "regular",
  recommended: "strong",
  "recommended-featured": "strong",
  "limited-time-offer": "regular"
};
const Banner = ({
  type,
  variant
}) => {
  const { t } = useTranslations(translations);
  if (variant === "wide") {
    const wideLabel = WIDE_LABEL[type];
    const background = WIDE_BACKGROUND[type];
    return /* @__PURE__ */ jsx(
      Box,
      {
        width: "full",
        background,
        padding: "xsmall",
        justifyContent: "center",
        alignItems: "center",
        style: {
          marginLeft: calc.negate(vars.space.medium),
          marginRight: calc.negate(vars.space.medium),
          marginTop: calc.negate(vars.space.medium),
          width: calc.add("100%", vars.space.medium, vars.space.medium),
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0
        },
        children: /* @__PURE__ */ jsx(Text, { size: "xsmall", weight: "strong", align: "center", children: t(wideLabel) })
      }
    );
  }
  const narrowLabel = NARROW_LABEL[type];
  const narrowTone = NARROW_TONE[type];
  const narrowWeight = NARROW_WEIGHT[type];
  if (type === "recommended-featured") {
    return /* @__PURE__ */ jsx(
      Box,
      {
        paddingY: "xxsmall",
        paddingX: "xsmall",
        borderRadius: "standard",
        background: "brandAccent",
        maxWidth: "content",
        children: /* @__PURE__ */ jsx(Text, { size: "xsmall", weight: "medium", maxLines: 1, tone: "neutral", children: t(narrowLabel) })
      }
    );
  }
  return /* @__PURE__ */ jsx(Badge, { weight: narrowWeight, tone: narrowTone, children: t(narrowLabel) });
};
const TrackingContext = createContext({
  trackEvent: () => {
  }
});
const RedirectButton = ({ id, url }) => {
  const { t } = useTranslations(translations$1);
  const { trackEvent } = useContext(TrackingContext);
  const onClickRedirectHandler = () => {
    trackEvent({ type: "cta", action: "clicked" });
  };
  const urlResolver = useMelwaysLink();
  return /* @__PURE__ */ jsx(
    ButtonLink,
    {
      id,
      href: urlResolver({ path: url }),
      onClick: onClickRedirectHandler,
      target: "_blank",
      variant: "ghost",
      tone: "formAccent",
      data: { testid: `select-${id}` },
      iconPosition: "trailing",
      children: t("Contact us")
    }
  );
};
const SelectButton = ({
  id,
  onClick,
  isSelected,
  isLoading,
  variant
}) => {
  const { t } = useTranslations(translations$2);
  const { trackEvent } = useContext(TrackingContext);
  const onClickAddHandler = () => {
    if (isLoading) return;
    trackEvent({
      type: "cta",
      action: isSelected ? "removed" : "added"
    });
    onClick(isSelected ? "remove" : "add");
  };
  const onClickSelectHandler = () => {
    if (isSelected || isLoading) return;
    trackEvent({ type: "cta", action: "clicked" });
    onClick("update");
  };
  const onClickCurrentHandler = () => {
    if (isSelected || isLoading) return;
    trackEvent({ type: "cta", action: "clicked" });
    onClick("remove");
  };
  const onClickHandler = () => {
    switch (variant) {
      case "add":
        return onClickAddHandler();
      case "current":
        return onClickCurrentHandler();
      case "upgrade":
      case "select":
      default:
        return onClickSelectHandler();
    }
  };
  const ctaState = {
    selected: t("Selected"),
    unselected: t("Select")
  };
  if (variant === "add") {
    ctaState.selected = `— ${t("Remove")}`;
    ctaState.unselected = t("Add");
  }
  if (variant === "upgrade") {
    ctaState.selected = t("Selected");
    ctaState.unselected = t("Upgrade");
  }
  if (variant === "current") {
    ctaState.selected = t("Current ad");
    ctaState.unselected = t("Current ad");
  }
  if (variant === "current" && isSelected) {
    return /* @__PURE__ */ jsx(DisabledButton, { content: ctaState.selected });
  }
  return /* @__PURE__ */ jsx(
    Button,
    {
      id,
      onClick: onClickHandler,
      variant: isSelected ? "solid" : "ghost",
      icon: variant === "upgrade" ? /* @__PURE__ */ jsx(IconRocket, {}) : void 0,
      iconPosition: variant === "upgrade" ? "trailing" : void 0,
      tone: "formAccent",
      loading: isLoading,
      data: { testid: `select-${id}` },
      children: isSelected ? ctaState.selected : ctaState.unselected
    }
  );
};
const CallToAction = ({
  id,
  onClick,
  isSelected,
  isLoading,
  url,
  variant
}) => {
  const { t } = useTranslations(translations$3);
  if (url) {
    return /* @__PURE__ */ jsx(RedirectButton, { id, url });
  }
  if (variant === "current-add-on") {
    return /* @__PURE__ */ jsx(DisabledButton, { content: t("Free 30-day extension on upgrade") });
  }
  return /* @__PURE__ */ jsx(
    SelectButton,
    {
      id,
      onClick,
      isSelected,
      isLoading,
      variant
    }
  );
};
const formatRoundedPrice = (price, currency, locale) => formatMinorUnits(price, {
  currency,
  locale,
  showFraction: true,
  notation: "compact"
});
const formatExactPrice = (price, currency, locale) => formatMinorUnits(price, {
  currency,
  locale,
  dropFraction: true
});
const getPriceWithTax = (price, tax) => {
  var _a;
  if (tax && tax.displayRecommendation === "inclusive") {
    const taxValue = ((_a = tax.amount) == null ? void 0 : _a.value) ?? 0;
    return {
      value: price.value === 0 ? price.value : price.value + taxValue,
      currency: price.currency
    };
  }
  return price;
};
const getDisplayPrices = (price) => ({
  displayRetailPrice: {
    price: getPriceWithTax(price.retailPrice, price.retailTax),
    tax: price.retailTax
  },
  displayNetPrice: {
    price: getPriceWithTax(price.netPrice, price.tax),
    tax: price.tax
  }
});
const isPriceDiscounted = (price) => price.type === "FixedPrice" && price.netPrice.value < price.retailPrice.value;
const isPriceFree = (price) => price.retailPrice.value === 0;
const isPriceHidden = (price) => price.type === "HiddenPrice";
const PRICE_TO_ROUND_AT = 1e8;
const isPriceRounded = (price) => price >= PRICE_TO_ROUND_AT;
const isPositiveCappedUnitPrice = (price) => Boolean(price.type === "CappedUnitPrice" && !isPriceFree(price.cappedPrice));
const isBillableUnitsHighlight = (variant) => variant === "billable-units-highlight" || variant === "billable-units-highlight-2";
const isFixedPrice = (price) => Boolean(price.type === "FixedPrice");
const isSlimVariant = (price) => isFixedPrice(price) && price.variant === "slim";
const priceHasTax = (price) => isFixedPrice(price) && Boolean(price.tax) || isPositiveCappedUnitPrice(price) && Boolean(price.unitPrice.tax);
const BillableUnitsDialog = ({
  isOpen,
  onClose,
  price,
  showRelevant
}) => {
  const { language } = useLanguage();
  if (!isPositiveCappedUnitPrice(price) || !isOpen) {
    return null;
  }
  const { displayNetPrice } = getDisplayPrices(price.unitPrice);
  const taxPrefix = {
    inclusive: "incl. ",
    exclusive: "+"
  };
  const taxType = displayNetPrice.tax && displayNetPrice.tax.displayRecommendation ? taxPrefix[displayNetPrice.tax.displayRecommendation] : void 0;
  const taxSuffix = displayNetPrice.tax ? `${taxType}${displayNetPrice.tax.code}` : void 0;
  const relevantOrQuality = showRelevant ? "relevant" : "quality";
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      id: "billableUnitsInclusionsDialog",
      title: "Pay less if we don't deliver",
      open: isOpen,
      onClose,
      closeLabel: "Close",
      children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsx(Text, { size: "small", weight: "strong", children: "How does it work?" }),
        /* @__PURE__ */ jsxs(List, { space: "xsmall", children: [
          /* @__PURE__ */ jsxs(Text, { size: "small", children: [
            "Receive at least ",
            price.maxBillableUnits,
            " ",
            relevantOrQuality,
            " ",
            "applications"
          ] }),
          /* @__PURE__ */ jsxs(Text, { size: "small", children: [
            "Pay",
            " ",
            formatExactPrice(
              displayNetPrice.price.value,
              displayNetPrice.price.currency,
              language
            ),
            " ",
            taxSuffix,
            " less for each ",
            relevantOrQuality,
            " application short of",
            " ",
            price.maxBillableUnits
          ] })
        ] }),
        /* @__PURE__ */ jsx(Text, { size: "small", weight: "strong", children: showRelevant ? "What makes an application relevant?" : "What makes a quality application?" }),
        /* @__PURE__ */ jsxs(Text, { size: "small", children: [
          "SEEK determines a ",
          relevantOrQuality,
          " application as one which claims to have both:"
        ] }),
        /* @__PURE__ */ jsxs(List, { space: "xsmall", children: [
          /* @__PURE__ */ jsx(Text, { size: "small", children: "working rights where you're hiring" }),
          /* @__PURE__ */ jsx(Text, { size: "small", children: "2+ years' experience in the advertised role" })
        ] }),
        /* @__PURE__ */ jsxs(Text, { size: "small", children: [
          "You can still filter all applications you receive according to your criteria, however this will not affect the payment terms of",
          " ",
          relevantOrQuality,
          " applications."
        ] }),
        /* @__PURE__ */ jsx(Text, { tone: "neutral", size: "xsmall", children: "SEEK does not verify claims made in candidate applications. SEEK Pass verification is not required." })
      ] })
    }
  );
};
const BillableUnitsInclusions = ({
  price,
  showRelevant
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { trackEvent } = useContext(TrackingContext);
  const onTooltipClick = () => {
    setIsOpen(true);
    trackEvent({
      type: "tooltip",
      action: "opened",
      name: "custom-billable-units-inclusions",
      content: "custom-billable-units-inclusions"
    });
  };
  const onTooltipClose = () => {
    setIsOpen(false);
  };
  const relevantOrQuality = showRelevant ? "relevant" : "quality";
  return /* @__PURE__ */ jsxs(Bleed, { horizontal: "medium", bottom: "xlarge", children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        background: "promoteLight",
        overflow: "hidden",
        padding: "medium",
        paddingBottom: "large",
        children: /* @__PURE__ */ jsxs(List, { type: "icon", tone: "promote", icon: /* @__PURE__ */ jsx(IconTick, { alignY: "lowercase" }), children: [
          /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: "top", children: [
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Text, { tone: "neutral", children: [
              "Pay less if you don't receive the number of",
              " ",
              relevantOrQuality,
              " applications"
            ] }) }),
            /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { tone: "neutral", children: /* @__PURE__ */ jsx(
              TextLinkButton,
              {
                "aria-label": `Tooltip - more information about paying less if you don't receive the number of ${relevantOrQuality} applications`,
                weight: "weak",
                hitArea: "standard",
                onClick: onTooltipClick,
                children: /* @__PURE__ */ jsx(IconInfo, {})
              }
            ) }) })
          ] }),
          /* @__PURE__ */ jsxs(Text, { tone: "neutral", children: [
            "We define ",
            relevantOrQuality,
            " as applications claiming working rights and 2+ years' experience"
          ] })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      BillableUnitsDialog,
      {
        isOpen,
        onClose: onTooltipClose,
        price,
        showRelevant
      }
    )
  ] });
};
const getCountryNameFromCode = (countryCode, locale = "en-AU") => {
  try {
    if (!countryCode) return "";
    return new Intl.DisplayNames(locale, { type: "region" }).of(
      countryCode.toUpperCase()
    ) ?? "";
  } catch {
    return "";
  }
};
const tooltipEventData = {
  name: "Pay Per Application Content",
  content: "Only pay for applications that claim to have:"
};
const PayPerApplicationContent = () => {
  var _a;
  const { t } = useTranslations(translations$4);
  const [showModal, setShowModal] = useState(false);
  const { trackEvent } = useContext(TrackingContext);
  const { job } = useContext(ProductSelectionContext);
  const countryName = getCountryNameFromCode(
    (_a = job == null ? void 0 : job.location) == null ? void 0 : _a.country.code,
    useLanguage().language
  );
  return /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
    /* @__PURE__ */ jsxs(Box, { children: [
      /* @__PURE__ */ jsx(Text, { weight: "strong", children: /* @__PURE__ */ jsx(
        TextLinkButton,
        {
          weight: "weak",
          onClick: () => {
            setShowModal(true);
            trackEvent({
              type: "tooltip",
              action: "opened",
              ...tooltipEventData
            });
          },
          children: t("only pay for applications")
        }
      ) }),
      /* @__PURE__ */ jsx(
        Dialog,
        {
          open: showModal,
          onClose: () => {
            setShowModal(false);
            trackEvent({
              type: "tooltip",
              action: "opened",
              ...tooltipEventData
            });
          },
          id: "payForSellingPoint",
          title: t("which applications are billable"),
          children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
            /* @__PURE__ */ jsx(DialogText, { children: t("applications that have 2 years or more experience") }),
            /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
              /* @__PURE__ */ jsx(DialogHeading, { children: t(
                "which of the following statements best describes your right to work in countryName",
                { countryName }
              ) }),
              /* @__PURE__ */ jsx(
                BulletPoint,
                {
                  points: [
                    t("permanent work rights no restrictions"),
                    t("temporary work rights no restrictions"),
                    t("temporary work rights with restrictions")
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
              /* @__PURE__ */ jsx(DialogHeading, { children: t("years of experience") }),
              /* @__PURE__ */ jsx(BulletPoint, { points: [t("2 years or more")] })
            ] }),
            /* @__PURE__ */ jsx(DialogText, { children: /* @__PURE__ */ jsx("span", { style: { fontStyle: "italic" }, children: t(
              "SEEK does not verify the answers provided in applications. Certsy verification is not required."
            ) }) })
          ] })
        }
      )
    ] }),
    /* @__PURE__ */ jsx(Box, { paddingLeft: "small", children: /* @__PURE__ */ jsxs(List, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { weight: "strong", children: t("right to work") }),
      /* @__PURE__ */ jsx(Text, { weight: "strong", children: t("2+ years experience") })
    ] }) })
  ] });
};
const DialogText = ({ children }) => /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children });
const DialogHeading = ({ children }) => /* @__PURE__ */ jsx(Text, { weight: "strong", tone: "secondary", children });
const BulletPoint = ({ points }) => /* @__PURE__ */ jsx(Box, { paddingLeft: "small", children: /* @__PURE__ */ jsx(List, { space: "xsmall", children: points.map((point) => /* @__PURE__ */ jsx(DialogText, { children: point }, point)) }) });
const PayPerApplicationNewPayment = () => {
  var _a;
  const { job } = useContext(ProductSelectionContext);
  const countryName = getCountryNameFromCode(
    (_a = job == null ? void 0 : job.location) == null ? void 0 : _a.country.code,
    useLanguage().language
  );
  return /* @__PURE__ */ jsx(Box, { borderRadius: "xlarge", background: "promoteLight", padding: "small", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Heading, { level: "4", children: "A new way to pay" }),
    /* @__PURE__ */ jsxs(List, { type: "icon", icon: /* @__PURE__ */ jsx(IconPromote, { alignY: "lowercase" }), children: [
      /* @__PURE__ */ jsxs(Text, { children: [
        "Only pay for applicants who claim the right to work in ",
        countryName,
        ", and have +2 years relevant experience"
      ] }),
      /* @__PURE__ */ jsx(Text, { children: "Pay at the end of your listing" }),
      /* @__PURE__ */ jsx(Text, { children: "Still get all the other applicants" })
    ] })
  ] }) });
};
const PayPerApplicationPayment = () => /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
  /* @__PURE__ */ jsx(Heading, { level: "4", icon: /* @__PURE__ */ jsx(IconTime, {}), children: "How you pay" }),
  /* @__PURE__ */ jsx(Box, { paddingLeft: "xsmall", children: /* @__PURE__ */ jsxs(List, { type: "number", children: [
    /* @__PURE__ */ jsx(Text, { children: "Pay at the end of your ad" }),
    /* @__PURE__ */ jsx(Text, { children: "Never spend more than the maximum" })
  ] }) })
] });
const PayPerApplicationPlusPremiumInclusions = () => /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
  /* @__PURE__ */ jsx(Heading, { level: "4", children: "Plus, get all the other premium features" }),
  /* @__PURE__ */ jsxs(List, { type: "icon", icon: /* @__PURE__ */ jsx(IconTick, { alignY: "lowercase" }), children: [
    /* @__PURE__ */ jsx(Text, { children: "30 day listing on SEEK" }),
    /* @__PURE__ */ jsx(Text, { children: "Your job ad emailed to relevant candidates" }),
    /* @__PURE__ */ jsx(Text, { children: "Priority listing in search" }),
    /* @__PURE__ */ jsx(Text, { children: "Priority listing in candidate email" }),
    /* @__PURE__ */ jsx(Text, { children: "Get candidates faster" })
  ] })
] });
const PayPerApplicationProcess = () => /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
  /* @__PURE__ */ jsx(Heading, { level: "4", icon: /* @__PURE__ */ jsx(IconPeople, {}), children: "How it works" }),
  /* @__PURE__ */ jsxs(List, { type: "icon", icon: /* @__PURE__ */ jsx(IconTick, { tone: "info", alignY: "lowercase" }), children: [
    /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsx(Text, { children: "Only pay for applicants who claim" }),
      /* @__PURE__ */ jsxs(List, { type: "bullet", children: [
        /* @__PURE__ */ jsx(Text, { children: "Right to work in Australia" }),
        /* @__PURE__ */ jsx(Text, { children: "More than 2 years experience in the role you’re hiring for" })
      ] })
    ] }),
    /* @__PURE__ */ jsx(Text, { children: "Still see applications that don’t meet this criteria" })
  ] })
] });
const Custom = ({
  id,
  price
}) => {
  switch (id) {
    case "ppa-content":
      return /* @__PURE__ */ jsx(PayPerApplicationContent, {});
    case "ppa-process":
      return /* @__PURE__ */ jsx(PayPerApplicationProcess, {});
    case "ppa-payment":
      return /* @__PURE__ */ jsx(PayPerApplicationPayment, {});
    case "ppa-new-payment":
      return /* @__PURE__ */ jsx(PayPerApplicationNewPayment, {});
    case "ppa-plus-premium-inclusions":
      return /* @__PURE__ */ jsx(PayPerApplicationPlusPremiumInclusions, {});
    case "billable-units-inclusions":
    case "billable-units-inclusions-2":
      return /* @__PURE__ */ jsx(
        BillableUnitsInclusions,
        {
          price,
          showRelevant: id === "billable-units-inclusions"
        }
      );
    default:
      return null;
  }
};
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function(n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _assertThisInitialized(e) {
  if (void 0 === e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  return e;
}
function _setPrototypeOf(t, e) {
  return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function(t2, e2) {
    return t2.__proto__ = e2, t2;
  }, _setPrototypeOf(t, e);
}
function _inheritsLoose(t, o) {
  t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o);
}
var define_global_default = {};
var commonjsGlobal = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof define_global_default !== "undefined" ? define_global_default : typeof self !== "undefined" ? self : {};
function getDefaultExportFromCjs(x) {
  return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, "default") ? x["default"] : x;
}
var reactIs$1 = { exports: {} };
var reactIs_production_min = {};
/** @license React v16.13.1
 * react-is.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredReactIs_production_min;
function requireReactIs_production_min() {
  if (hasRequiredReactIs_production_min) return reactIs_production_min;
  hasRequiredReactIs_production_min = 1;
  var b = "function" === typeof Symbol && Symbol.for, c = b ? Symbol.for("react.element") : 60103, d = b ? Symbol.for("react.portal") : 60106, e = b ? Symbol.for("react.fragment") : 60107, f = b ? Symbol.for("react.strict_mode") : 60108, g = b ? Symbol.for("react.profiler") : 60114, h2 = b ? Symbol.for("react.provider") : 60109, k = b ? Symbol.for("react.context") : 60110, l = b ? Symbol.for("react.async_mode") : 60111, m = b ? Symbol.for("react.concurrent_mode") : 60111, n = b ? Symbol.for("react.forward_ref") : 60112, p = b ? Symbol.for("react.suspense") : 60113, q = b ? Symbol.for("react.suspense_list") : 60120, r = b ? Symbol.for("react.memo") : 60115, t = b ? Symbol.for("react.lazy") : 60116, v2 = b ? Symbol.for("react.block") : 60121, w2 = b ? Symbol.for("react.fundamental") : 60117, x = b ? Symbol.for("react.responder") : 60118, y = b ? Symbol.for("react.scope") : 60119;
  function z(a) {
    if ("object" === typeof a && null !== a) {
      var u = a.$$typeof;
      switch (u) {
        case c:
          switch (a = a.type, a) {
            case l:
            case m:
            case e:
            case g:
            case f:
            case p:
              return a;
            default:
              switch (a = a && a.$$typeof, a) {
                case k:
                case n:
                case t:
                case r:
                case h2:
                  return a;
                default:
                  return u;
              }
          }
        case d:
          return u;
      }
    }
  }
  function A(a) {
    return z(a) === m;
  }
  reactIs_production_min.AsyncMode = l;
  reactIs_production_min.ConcurrentMode = m;
  reactIs_production_min.ContextConsumer = k;
  reactIs_production_min.ContextProvider = h2;
  reactIs_production_min.Element = c;
  reactIs_production_min.ForwardRef = n;
  reactIs_production_min.Fragment = e;
  reactIs_production_min.Lazy = t;
  reactIs_production_min.Memo = r;
  reactIs_production_min.Portal = d;
  reactIs_production_min.Profiler = g;
  reactIs_production_min.StrictMode = f;
  reactIs_production_min.Suspense = p;
  reactIs_production_min.isAsyncMode = function(a) {
    return A(a) || z(a) === l;
  };
  reactIs_production_min.isConcurrentMode = A;
  reactIs_production_min.isContextConsumer = function(a) {
    return z(a) === k;
  };
  reactIs_production_min.isContextProvider = function(a) {
    return z(a) === h2;
  };
  reactIs_production_min.isElement = function(a) {
    return "object" === typeof a && null !== a && a.$$typeof === c;
  };
  reactIs_production_min.isForwardRef = function(a) {
    return z(a) === n;
  };
  reactIs_production_min.isFragment = function(a) {
    return z(a) === e;
  };
  reactIs_production_min.isLazy = function(a) {
    return z(a) === t;
  };
  reactIs_production_min.isMemo = function(a) {
    return z(a) === r;
  };
  reactIs_production_min.isPortal = function(a) {
    return z(a) === d;
  };
  reactIs_production_min.isProfiler = function(a) {
    return z(a) === g;
  };
  reactIs_production_min.isStrictMode = function(a) {
    return z(a) === f;
  };
  reactIs_production_min.isSuspense = function(a) {
    return z(a) === p;
  };
  reactIs_production_min.isValidElementType = function(a) {
    return "string" === typeof a || "function" === typeof a || a === e || a === m || a === g || a === f || a === p || a === q || "object" === typeof a && null !== a && (a.$$typeof === t || a.$$typeof === r || a.$$typeof === h2 || a.$$typeof === k || a.$$typeof === n || a.$$typeof === w2 || a.$$typeof === x || a.$$typeof === y || a.$$typeof === v2);
  };
  reactIs_production_min.typeOf = z;
  return reactIs_production_min;
}
var reactIs_development = {};
/** @license React v16.13.1
 * react-is.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var hasRequiredReactIs_development;
function requireReactIs_development() {
  if (hasRequiredReactIs_development) return reactIs_development;
  hasRequiredReactIs_development = 1;
  if (process.env.NODE_ENV !== "production") {
    (function() {
      var hasSymbol = typeof Symbol === "function" && Symbol.for;
      var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for("react.element") : 60103;
      var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for("react.portal") : 60106;
      var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for("react.fragment") : 60107;
      var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for("react.strict_mode") : 60108;
      var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for("react.profiler") : 60114;
      var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for("react.provider") : 60109;
      var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for("react.context") : 60110;
      var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for("react.async_mode") : 60111;
      var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for("react.concurrent_mode") : 60111;
      var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for("react.forward_ref") : 60112;
      var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for("react.suspense") : 60113;
      var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for("react.suspense_list") : 60120;
      var REACT_MEMO_TYPE = hasSymbol ? Symbol.for("react.memo") : 60115;
      var REACT_LAZY_TYPE = hasSymbol ? Symbol.for("react.lazy") : 60116;
      var REACT_BLOCK_TYPE = hasSymbol ? Symbol.for("react.block") : 60121;
      var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for("react.fundamental") : 60117;
      var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for("react.responder") : 60118;
      var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for("react.scope") : 60119;
      function isValidElementType(type) {
        return typeof type === "string" || typeof type === "function" || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
        type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === "object" && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE || type.$$typeof === REACT_BLOCK_TYPE);
      }
      function typeOf(object) {
        if (typeof object === "object" && object !== null) {
          var $$typeof = object.$$typeof;
          switch ($$typeof) {
            case REACT_ELEMENT_TYPE:
              var type = object.type;
              switch (type) {
                case REACT_ASYNC_MODE_TYPE:
                case REACT_CONCURRENT_MODE_TYPE:
                case REACT_FRAGMENT_TYPE:
                case REACT_PROFILER_TYPE:
                case REACT_STRICT_MODE_TYPE:
                case REACT_SUSPENSE_TYPE:
                  return type;
                default:
                  var $$typeofType = type && type.$$typeof;
                  switch ($$typeofType) {
                    case REACT_CONTEXT_TYPE:
                    case REACT_FORWARD_REF_TYPE:
                    case REACT_LAZY_TYPE:
                    case REACT_MEMO_TYPE:
                    case REACT_PROVIDER_TYPE:
                      return $$typeofType;
                    default:
                      return $$typeof;
                  }
              }
            case REACT_PORTAL_TYPE:
              return $$typeof;
          }
        }
        return void 0;
      }
      var AsyncMode = REACT_ASYNC_MODE_TYPE;
      var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
      var ContextConsumer = REACT_CONTEXT_TYPE;
      var ContextProvider = REACT_PROVIDER_TYPE;
      var Element = REACT_ELEMENT_TYPE;
      var ForwardRef = REACT_FORWARD_REF_TYPE;
      var Fragment2 = REACT_FRAGMENT_TYPE;
      var Lazy = REACT_LAZY_TYPE;
      var Memo = REACT_MEMO_TYPE;
      var Portal = REACT_PORTAL_TYPE;
      var Profiler = REACT_PROFILER_TYPE;
      var StrictMode = REACT_STRICT_MODE_TYPE;
      var Suspense = REACT_SUSPENSE_TYPE;
      var hasWarnedAboutDeprecatedIsAsyncMode = false;
      function isAsyncMode(object) {
        {
          if (!hasWarnedAboutDeprecatedIsAsyncMode) {
            hasWarnedAboutDeprecatedIsAsyncMode = true;
            console["warn"]("The ReactIs.isAsyncMode() alias has been deprecated, and will be removed in React 17+. Update your code to use ReactIs.isConcurrentMode() instead. It has the exact same API.");
          }
        }
        return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
      }
      function isConcurrentMode(object) {
        return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
      }
      function isContextConsumer(object) {
        return typeOf(object) === REACT_CONTEXT_TYPE;
      }
      function isContextProvider(object) {
        return typeOf(object) === REACT_PROVIDER_TYPE;
      }
      function isElement(object) {
        return typeof object === "object" && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
      }
      function isForwardRef(object) {
        return typeOf(object) === REACT_FORWARD_REF_TYPE;
      }
      function isFragment(object) {
        return typeOf(object) === REACT_FRAGMENT_TYPE;
      }
      function isLazy(object) {
        return typeOf(object) === REACT_LAZY_TYPE;
      }
      function isMemo(object) {
        return typeOf(object) === REACT_MEMO_TYPE;
      }
      function isPortal(object) {
        return typeOf(object) === REACT_PORTAL_TYPE;
      }
      function isProfiler(object) {
        return typeOf(object) === REACT_PROFILER_TYPE;
      }
      function isStrictMode(object) {
        return typeOf(object) === REACT_STRICT_MODE_TYPE;
      }
      function isSuspense(object) {
        return typeOf(object) === REACT_SUSPENSE_TYPE;
      }
      reactIs_development.AsyncMode = AsyncMode;
      reactIs_development.ConcurrentMode = ConcurrentMode;
      reactIs_development.ContextConsumer = ContextConsumer;
      reactIs_development.ContextProvider = ContextProvider;
      reactIs_development.Element = Element;
      reactIs_development.ForwardRef = ForwardRef;
      reactIs_development.Fragment = Fragment2;
      reactIs_development.Lazy = Lazy;
      reactIs_development.Memo = Memo;
      reactIs_development.Portal = Portal;
      reactIs_development.Profiler = Profiler;
      reactIs_development.StrictMode = StrictMode;
      reactIs_development.Suspense = Suspense;
      reactIs_development.isAsyncMode = isAsyncMode;
      reactIs_development.isConcurrentMode = isConcurrentMode;
      reactIs_development.isContextConsumer = isContextConsumer;
      reactIs_development.isContextProvider = isContextProvider;
      reactIs_development.isElement = isElement;
      reactIs_development.isForwardRef = isForwardRef;
      reactIs_development.isFragment = isFragment;
      reactIs_development.isLazy = isLazy;
      reactIs_development.isMemo = isMemo;
      reactIs_development.isPortal = isPortal;
      reactIs_development.isProfiler = isProfiler;
      reactIs_development.isStrictMode = isStrictMode;
      reactIs_development.isSuspense = isSuspense;
      reactIs_development.isValidElementType = isValidElementType;
      reactIs_development.typeOf = typeOf;
    })();
  }
  return reactIs_development;
}
if (process.env.NODE_ENV === "production") {
  reactIs$1.exports = requireReactIs_production_min();
} else {
  reactIs$1.exports = requireReactIs_development();
}
var reactIsExports = reactIs$1.exports;
var reactIs = reactIsExports;
var REACT_STATICS = {
  childContextTypes: true,
  contextType: true,
  contextTypes: true,
  defaultProps: true,
  displayName: true,
  getDefaultProps: true,
  getDerivedStateFromError: true,
  getDerivedStateFromProps: true,
  mixins: true,
  propTypes: true,
  type: true
};
var KNOWN_STATICS = {
  name: true,
  length: true,
  prototype: true,
  caller: true,
  callee: true,
  arguments: true,
  arity: true
};
var FORWARD_REF_STATICS = {
  "$$typeof": true,
  render: true,
  defaultProps: true,
  displayName: true,
  propTypes: true
};
var MEMO_STATICS = {
  "$$typeof": true,
  compare: true,
  defaultProps: true,
  displayName: true,
  propTypes: true,
  type: true
};
var TYPE_STATICS = {};
TYPE_STATICS[reactIs.ForwardRef] = FORWARD_REF_STATICS;
TYPE_STATICS[reactIs.Memo] = MEMO_STATICS;
function getStatics(component) {
  if (reactIs.isMemo(component)) {
    return MEMO_STATICS;
  }
  return TYPE_STATICS[component["$$typeof"]] || REACT_STATICS;
}
var defineProperty = Object.defineProperty;
var getOwnPropertyNames = Object.getOwnPropertyNames;
var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;
var getPrototypeOf = Object.getPrototypeOf;
var objectPrototype = Object.prototype;
function hoistNonReactStatics(targetComponent, sourceComponent, blacklist) {
  if (typeof sourceComponent !== "string") {
    if (objectPrototype) {
      var inheritedComponent = getPrototypeOf(sourceComponent);
      if (inheritedComponent && inheritedComponent !== objectPrototype) {
        hoistNonReactStatics(targetComponent, inheritedComponent, blacklist);
      }
    }
    var keys = getOwnPropertyNames(sourceComponent);
    if (getOwnPropertySymbols) {
      keys = keys.concat(getOwnPropertySymbols(sourceComponent));
    }
    var targetStatics = getStatics(targetComponent);
    var sourceStatics = getStatics(sourceComponent);
    for (var i = 0; i < keys.length; ++i) {
      var key2 = keys[i];
      if (!KNOWN_STATICS[key2] && !(blacklist && blacklist[key2]) && !(sourceStatics && sourceStatics[key2]) && !(targetStatics && targetStatics[key2])) {
        var descriptor = getOwnPropertyDescriptor(sourceComponent, key2);
        try {
          defineProperty(targetComponent, key2, descriptor);
        } catch (e) {
        }
      }
    }
  }
  return targetComponent;
}
var hoistNonReactStatics_cjs = hoistNonReactStatics;
const hoistNonReactStatics$1 = /* @__PURE__ */ getDefaultExportFromCjs(hoistNonReactStatics_cjs);
function invariant(condition, message) {
  if (condition) return;
  var error = new Error("loadable: " + message);
  error.framesToPop = 1;
  error.name = "Invariant Violation";
  throw error;
}
var Context = /* @__PURE__ */ React.createContext();
var LOADABLE_SHARED = {
  initialChunks: {}
};
var STATUS_PENDING = "PENDING";
var STATUS_RESOLVED = "RESOLVED";
var STATUS_REJECTED = "REJECTED";
function resolveConstructor(ctor) {
  if (typeof ctor === "function") {
    return {
      requireAsync: ctor,
      resolve: function resolve() {
        return void 0;
      },
      chunkName: function chunkName() {
        return void 0;
      }
    };
  }
  return ctor;
}
var withChunkExtractor = function withChunkExtractor2(Component) {
  var LoadableWithChunkExtractor = function LoadableWithChunkExtractor2(props) {
    return React.createElement(Context.Consumer, null, function(extractor) {
      return React.createElement(Component, Object.assign({
        __chunkExtractor: extractor
      }, props));
    });
  };
  if (Component.displayName) {
    LoadableWithChunkExtractor.displayName = Component.displayName + "WithChunkExtractor";
  }
  return LoadableWithChunkExtractor;
};
var identity = function identity2(v2) {
  return v2;
};
function createLoadable(_ref) {
  var _ref$defaultResolveCo = _ref.defaultResolveComponent, defaultResolveComponent2 = _ref$defaultResolveCo === void 0 ? identity : _ref$defaultResolveCo, _render = _ref.render, onLoad2 = _ref.onLoad;
  function loadable2(loadableConstructor, options) {
    if (options === void 0) {
      options = {};
    }
    var ctor = resolveConstructor(loadableConstructor);
    var cache = {};
    function _getCacheKey(props) {
      if (options.cacheKey) {
        return options.cacheKey(props);
      }
      if (ctor.resolve) {
        return ctor.resolve(props);
      }
      return "static";
    }
    function resolve(module2, props, Loadable2) {
      var Component = options.resolveComponent ? options.resolveComponent(module2, props) : defaultResolveComponent2(module2);
      hoistNonReactStatics$1(Loadable2, Component, {
        preload: true
      });
      return Component;
    }
    var cachedLoad = function cachedLoad2(props) {
      var cacheKey = _getCacheKey(props);
      var promise = cache[cacheKey];
      if (!promise || promise.status === STATUS_REJECTED) {
        promise = ctor.requireAsync(props);
        promise.status = STATUS_PENDING;
        cache[cacheKey] = promise;
        promise.then(function() {
          promise.status = STATUS_RESOLVED;
        }, function(error) {
          console.error("loadable-components: failed to asynchronously load component", {
            fileName: ctor.resolve(props),
            chunkName: ctor.chunkName(props),
            error: error ? error.message : error
          });
          promise.status = STATUS_REJECTED;
        });
      }
      return promise;
    };
    var InnerLoadable = /* @__PURE__ */ function(_React$Component) {
      _inheritsLoose(InnerLoadable2, _React$Component);
      InnerLoadable2.getDerivedStateFromProps = function getDerivedStateFromProps(props, state) {
        var cacheKey = _getCacheKey(props);
        return _extends({}, state, {
          cacheKey,
          // change of a key triggers loading state automatically
          loading: state.loading || state.cacheKey !== cacheKey
        });
      };
      function InnerLoadable2(props) {
        var _this;
        _this = _React$Component.call(this, props) || this;
        _this.state = {
          result: null,
          error: null,
          loading: true,
          cacheKey: _getCacheKey(props)
        };
        invariant(!props.__chunkExtractor || ctor.requireSync, "SSR requires `@loadable/babel-plugin`, please install it");
        if (props.__chunkExtractor) {
          if (options.ssr === false) {
            return _assertThisInitialized(_this);
          }
          ctor.requireAsync(props)["catch"](function() {
            return null;
          });
          _this.loadSync();
          props.__chunkExtractor.addChunk(ctor.chunkName(props));
          return _assertThisInitialized(_this);
        }
        if (options.ssr !== false && // is ready - was loaded in this session
        (ctor.isReady && ctor.isReady(props) || // is ready - was loaded during SSR process
        ctor.chunkName && LOADABLE_SHARED.initialChunks[ctor.chunkName(props)])) {
          _this.loadSync();
        }
        return _this;
      }
      var _proto = InnerLoadable2.prototype;
      _proto.componentDidMount = function componentDidMount() {
        this.mounted = true;
        var cachedPromise = this.getCache();
        if (cachedPromise && cachedPromise.status === STATUS_REJECTED) {
          this.setCache();
        }
        if (this.state.loading) {
          this.loadAsync();
        }
      };
      _proto.componentDidUpdate = function componentDidUpdate(prevProps, prevState) {
        if (prevState.cacheKey !== this.state.cacheKey) {
          this.loadAsync();
        }
      };
      _proto.componentWillUnmount = function componentWillUnmount() {
        this.mounted = false;
      };
      _proto.safeSetState = function safeSetState(nextState, callback) {
        if (this.mounted) {
          this.setState(nextState, callback);
        }
      };
      _proto.getCacheKey = function getCacheKey() {
        return _getCacheKey(this.props);
      };
      _proto.getCache = function getCache() {
        return cache[this.getCacheKey()];
      };
      _proto.setCache = function setCache(value2) {
        if (value2 === void 0) {
          value2 = void 0;
        }
        cache[this.getCacheKey()] = value2;
      };
      _proto.triggerOnLoad = function triggerOnLoad() {
        var _this2 = this;
        if (onLoad2) {
          setTimeout(function() {
            onLoad2(_this2.state.result, _this2.props);
          });
        }
      };
      _proto.loadSync = function loadSync() {
        if (!this.state.loading) return;
        try {
          var loadedModule = ctor.requireSync(this.props);
          var result = resolve(loadedModule, this.props, Loadable);
          this.state.result = result;
          this.state.loading = false;
        } catch (error) {
          console.error("loadable-components: failed to synchronously load component, which expected to be available", {
            fileName: ctor.resolve(this.props),
            chunkName: ctor.chunkName(this.props),
            error: error ? error.message : error
          });
          this.state.error = error;
        }
      };
      _proto.loadAsync = function loadAsync() {
        var _this3 = this;
        var promise = this.resolveAsync();
        promise.then(function(loadedModule) {
          var result = resolve(loadedModule, _this3.props, Loadable);
          _this3.safeSetState({
            result,
            loading: false
          }, function() {
            return _this3.triggerOnLoad();
          });
        })["catch"](function(error) {
          return _this3.safeSetState({
            error,
            loading: false
          });
        });
        return promise;
      };
      _proto.resolveAsync = function resolveAsync() {
        var _this$props = this.props;
        _this$props.__chunkExtractor;
        _this$props.forwardedRef;
        var props = _objectWithoutPropertiesLoose(_this$props, ["__chunkExtractor", "forwardedRef"]);
        return cachedLoad(props);
      };
      _proto.render = function render3() {
        var _this$props2 = this.props, forwardedRef = _this$props2.forwardedRef, propFallback = _this$props2.fallback;
        _this$props2.__chunkExtractor;
        var props = _objectWithoutPropertiesLoose(_this$props2, ["forwardedRef", "fallback", "__chunkExtractor"]);
        var _this$state = this.state, error = _this$state.error, loading = _this$state.loading, result = _this$state.result;
        if (options.suspense) {
          var cachedPromise = this.getCache() || this.loadAsync();
          if (cachedPromise.status === STATUS_PENDING) {
            throw this.loadAsync();
          }
        }
        if (error) {
          throw error;
        }
        var fallback = propFallback || options.fallback || null;
        if (loading) {
          return fallback;
        }
        return _render({
          fallback,
          result,
          options,
          props: _extends({}, props, {
            ref: forwardedRef
          })
        });
      };
      return InnerLoadable2;
    }(React.Component);
    var EnhancedInnerLoadable = withChunkExtractor(InnerLoadable);
    var Loadable = React.forwardRef(function(props, ref) {
      return React.createElement(EnhancedInnerLoadable, Object.assign({
        forwardedRef: ref
      }, props));
    });
    Loadable.displayName = "Loadable";
    Loadable.preload = function(props) {
      Loadable.load(props);
    };
    Loadable.load = function(props) {
      return cachedLoad(props);
    };
    return Loadable;
  }
  function lazy2(ctor, options) {
    return loadable2(ctor, _extends({}, options, {
      suspense: true
    }));
  }
  return {
    loadable: loadable2,
    lazy: lazy2
  };
}
function defaultResolveComponent(loadedModule) {
  return loadedModule.__esModule ? loadedModule["default"] : loadedModule["default"] || loadedModule;
}
var _createLoadable = /* @__PURE__ */ createLoadable({
  defaultResolveComponent,
  render: function render(_ref) {
    var Component = _ref.result, props = _ref.props;
    return React.createElement(Component, props);
  }
}), loadable = _createLoadable.loadable, lazy = _createLoadable.lazy;
var _createLoadable$1 = /* @__PURE__ */ createLoadable({
  onLoad: function onLoad(result, props) {
    if (result && props.forwardedRef) {
      if (typeof props.forwardedRef === "function") {
        props.forwardedRef(result);
      } else {
        props.forwardedRef.current = result;
      }
    }
  },
  render: function render2(_ref) {
    var result = _ref.result, props = _ref.props;
    if (props.children) {
      return props.children(result);
    }
    return null;
  }
}), loadable$1 = _createLoadable$1.loadable, lazy$1 = _createLoadable$1.lazy;
var loadable$2 = loadable;
loadable$2.lib = loadable$1;
var lazy$2 = lazy;
lazy$2.lib = lazy$1;
const AnimationFallback = () => /* @__PURE__ */ jsx(Box, { display: "flex", justifyContent: "center", padding: "small", children: /* @__PURE__ */ jsx(Loader, {}) });
var lottie$1 = { exports: {} };
(function(module, exports) {
  typeof navigator !== "undefined" && function(global, factory) {
    module.exports = factory();
  }(commonjsGlobal, function() {
    var svgNS = "http://www.w3.org/2000/svg";
    var locationHref = "";
    var _useWebWorker = false;
    var initialDefaultFrame = -999999;
    var setWebWorker = function setWebWorker2(flag) {
      _useWebWorker = !!flag;
    };
    var getWebWorker = function getWebWorker2() {
      return _useWebWorker;
    };
    var setLocationHref = function setLocationHref2(value2) {
      locationHref = value2;
    };
    var getLocationHref = function getLocationHref2() {
      return locationHref;
    };
    function createTag(type) {
      return document.createElement(type);
    }
    function extendPrototype(sources, destination) {
      var i;
      var len = sources.length;
      var sourcePrototype;
      for (i = 0; i < len; i += 1) {
        sourcePrototype = sources[i].prototype;
        for (var attr in sourcePrototype) {
          if (Object.prototype.hasOwnProperty.call(sourcePrototype, attr)) destination.prototype[attr] = sourcePrototype[attr];
        }
      }
    }
    function getDescriptor(object, prop) {
      return Object.getOwnPropertyDescriptor(object, prop);
    }
    function createProxyFunction(prototype) {
      function ProxyFunction() {
      }
      ProxyFunction.prototype = prototype;
      return ProxyFunction;
    }
    var audioControllerFactory = function() {
      function AudioController(audioFactory) {
        this.audios = [];
        this.audioFactory = audioFactory;
        this._volume = 1;
        this._isMuted = false;
      }
      AudioController.prototype = {
        addAudio: function addAudio(audio) {
          this.audios.push(audio);
        },
        pause: function pause() {
          var i;
          var len = this.audios.length;
          for (i = 0; i < len; i += 1) {
            this.audios[i].pause();
          }
        },
        resume: function resume() {
          var i;
          var len = this.audios.length;
          for (i = 0; i < len; i += 1) {
            this.audios[i].resume();
          }
        },
        setRate: function setRate(rateValue) {
          var i;
          var len = this.audios.length;
          for (i = 0; i < len; i += 1) {
            this.audios[i].setRate(rateValue);
          }
        },
        createAudio: function createAudio(assetPath) {
          if (this.audioFactory) {
            return this.audioFactory(assetPath);
          }
          if (window.Howl) {
            return new window.Howl({
              src: [assetPath]
            });
          }
          return {
            isPlaying: false,
            play: function play() {
              this.isPlaying = true;
            },
            seek: function seek() {
              this.isPlaying = false;
            },
            playing: function playing() {
            },
            rate: function rate() {
            },
            setVolume: function setVolume() {
            }
          };
        },
        setAudioFactory: function setAudioFactory(audioFactory) {
          this.audioFactory = audioFactory;
        },
        setVolume: function setVolume(value2) {
          this._volume = value2;
          this._updateVolume();
        },
        mute: function mute() {
          this._isMuted = true;
          this._updateVolume();
        },
        unmute: function unmute() {
          this._isMuted = false;
          this._updateVolume();
        },
        getVolume: function getVolume() {
          return this._volume;
        },
        _updateVolume: function _updateVolume() {
          var i;
          var len = this.audios.length;
          for (i = 0; i < len; i += 1) {
            this.audios[i].volume(this._volume * (this._isMuted ? 0 : 1));
          }
        }
      };
      return function() {
        return new AudioController();
      };
    }();
    var createTypedArray = function() {
      function createRegularArray(type, len) {
        var i = 0;
        var arr = [];
        var value2;
        switch (type) {
          case "int16":
          case "uint8c":
            value2 = 1;
            break;
          default:
            value2 = 1.1;
            break;
        }
        for (i = 0; i < len; i += 1) {
          arr.push(value2);
        }
        return arr;
      }
      function createTypedArrayFactory(type, len) {
        if (type === "float32") {
          return new Float32Array(len);
        }
        if (type === "int16") {
          return new Int16Array(len);
        }
        if (type === "uint8c") {
          return new Uint8ClampedArray(len);
        }
        return createRegularArray(type, len);
      }
      if (typeof Uint8ClampedArray === "function" && typeof Float32Array === "function") {
        return createTypedArrayFactory;
      }
      return createRegularArray;
    }();
    function createSizedArray(len) {
      return Array.apply(null, {
        length: len
      });
    }
    function _typeof$6(obj) {
      "@babel/helpers - typeof";
      if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
        _typeof$6 = function _typeof2(obj2) {
          return typeof obj2;
        };
      } else {
        _typeof$6 = function _typeof2(obj2) {
          return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
        };
      }
      return _typeof$6(obj);
    }
    var subframeEnabled = true;
    var expressionsPlugin = null;
    var expressionsInterfaces = null;
    var idPrefix$1 = "";
    var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    var bmPow = Math.pow;
    var bmSqrt = Math.sqrt;
    var bmFloor = Math.floor;
    var bmMax = Math.max;
    var bmMin = Math.min;
    var BMMath = {};
    (function() {
      var propertyNames = ["abs", "acos", "acosh", "asin", "asinh", "atan", "atanh", "atan2", "ceil", "cbrt", "expm1", "clz32", "cos", "cosh", "exp", "floor", "fround", "hypot", "imul", "log", "log1p", "log2", "log10", "max", "min", "pow", "random", "round", "sign", "sin", "sinh", "sqrt", "tan", "tanh", "trunc", "E", "LN10", "LN2", "LOG10E", "LOG2E", "PI", "SQRT1_2", "SQRT2"];
      var i;
      var len = propertyNames.length;
      for (i = 0; i < len; i += 1) {
        BMMath[propertyNames[i]] = Math[propertyNames[i]];
      }
    })();
    BMMath.random = Math.random;
    BMMath.abs = function(val2) {
      var tOfVal = _typeof$6(val2);
      if (tOfVal === "object" && val2.length) {
        var absArr = createSizedArray(val2.length);
        var i;
        var len = val2.length;
        for (i = 0; i < len; i += 1) {
          absArr[i] = Math.abs(val2[i]);
        }
        return absArr;
      }
      return Math.abs(val2);
    };
    var defaultCurveSegments = 150;
    var degToRads = Math.PI / 180;
    var roundCorner = 0.5519;
    function styleDiv(element) {
      element.style.position = "absolute";
      element.style.top = 0;
      element.style.left = 0;
      element.style.display = "block";
      element.style.transformOrigin = "0 0";
      element.style.webkitTransformOrigin = "0 0";
      element.style.backfaceVisibility = "visible";
      element.style.webkitBackfaceVisibility = "visible";
      element.style.transformStyle = "preserve-3d";
      element.style.webkitTransformStyle = "preserve-3d";
      element.style.mozTransformStyle = "preserve-3d";
    }
    function BMEnterFrameEvent(type, currentTime, totalTime, frameMultiplier) {
      this.type = type;
      this.currentTime = currentTime;
      this.totalTime = totalTime;
      this.direction = frameMultiplier < 0 ? -1 : 1;
    }
    function BMCompleteEvent(type, frameMultiplier) {
      this.type = type;
      this.direction = frameMultiplier < 0 ? -1 : 1;
    }
    function BMCompleteLoopEvent(type, totalLoops, currentLoop, frameMultiplier) {
      this.type = type;
      this.currentLoop = currentLoop;
      this.totalLoops = totalLoops;
      this.direction = frameMultiplier < 0 ? -1 : 1;
    }
    function BMSegmentStartEvent(type, firstFrame, totalFrames) {
      this.type = type;
      this.firstFrame = firstFrame;
      this.totalFrames = totalFrames;
    }
    function BMDestroyEvent(type, target) {
      this.type = type;
      this.target = target;
    }
    function BMRenderFrameErrorEvent(nativeError, currentTime) {
      this.type = "renderFrameError";
      this.nativeError = nativeError;
      this.currentTime = currentTime;
    }
    function BMConfigErrorEvent(nativeError) {
      this.type = "configError";
      this.nativeError = nativeError;
    }
    var createElementID = /* @__PURE__ */ function() {
      var _count = 0;
      return function createID() {
        _count += 1;
        return idPrefix$1 + "__lottie_element_" + _count;
      };
    }();
    function HSVtoRGB(h2, s, v2) {
      var r;
      var g;
      var b;
      var i;
      var f;
      var p;
      var q;
      var t;
      i = Math.floor(h2 * 6);
      f = h2 * 6 - i;
      p = v2 * (1 - s);
      q = v2 * (1 - f * s);
      t = v2 * (1 - (1 - f) * s);
      switch (i % 6) {
        case 0:
          r = v2;
          g = t;
          b = p;
          break;
        case 1:
          r = q;
          g = v2;
          b = p;
          break;
        case 2:
          r = p;
          g = v2;
          b = t;
          break;
        case 3:
          r = p;
          g = q;
          b = v2;
          break;
        case 4:
          r = t;
          g = p;
          b = v2;
          break;
        case 5:
          r = v2;
          g = p;
          b = q;
          break;
      }
      return [r, g, b];
    }
    function RGBtoHSV(r, g, b) {
      var max = Math.max(r, g, b);
      var min = Math.min(r, g, b);
      var d = max - min;
      var h2;
      var s = max === 0 ? 0 : d / max;
      var v2 = max / 255;
      switch (max) {
        case min:
          h2 = 0;
          break;
        case r:
          h2 = g - b + d * (g < b ? 6 : 0);
          h2 /= 6 * d;
          break;
        case g:
          h2 = b - r + d * 2;
          h2 /= 6 * d;
          break;
        case b:
          h2 = r - g + d * 4;
          h2 /= 6 * d;
          break;
      }
      return [h2, s, v2];
    }
    function addSaturationToRGB(color, offset) {
      var hsv = RGBtoHSV(color[0] * 255, color[1] * 255, color[2] * 255);
      hsv[1] += offset;
      if (hsv[1] > 1) {
        hsv[1] = 1;
      } else if (hsv[1] <= 0) {
        hsv[1] = 0;
      }
      return HSVtoRGB(hsv[0], hsv[1], hsv[2]);
    }
    function addBrightnessToRGB(color, offset) {
      var hsv = RGBtoHSV(color[0] * 255, color[1] * 255, color[2] * 255);
      hsv[2] += offset;
      if (hsv[2] > 1) {
        hsv[2] = 1;
      } else if (hsv[2] < 0) {
        hsv[2] = 0;
      }
      return HSVtoRGB(hsv[0], hsv[1], hsv[2]);
    }
    function addHueToRGB(color, offset) {
      var hsv = RGBtoHSV(color[0] * 255, color[1] * 255, color[2] * 255);
      hsv[0] += offset / 360;
      if (hsv[0] > 1) {
        hsv[0] -= 1;
      } else if (hsv[0] < 0) {
        hsv[0] += 1;
      }
      return HSVtoRGB(hsv[0], hsv[1], hsv[2]);
    }
    var rgbToHex = function() {
      var colorMap = [];
      var i;
      var hex;
      for (i = 0; i < 256; i += 1) {
        hex = i.toString(16);
        colorMap[i] = hex.length === 1 ? "0" + hex : hex;
      }
      return function(r, g, b) {
        if (r < 0) {
          r = 0;
        }
        if (g < 0) {
          g = 0;
        }
        if (b < 0) {
          b = 0;
        }
        return "#" + colorMap[r] + colorMap[g] + colorMap[b];
      };
    }();
    var setSubframeEnabled = function setSubframeEnabled2(flag) {
      subframeEnabled = !!flag;
    };
    var getSubframeEnabled = function getSubframeEnabled2() {
      return subframeEnabled;
    };
    var setExpressionsPlugin = function setExpressionsPlugin2(value2) {
      expressionsPlugin = value2;
    };
    var getExpressionsPlugin = function getExpressionsPlugin2() {
      return expressionsPlugin;
    };
    var setExpressionInterfaces = function setExpressionInterfaces2(value2) {
      expressionsInterfaces = value2;
    };
    var getExpressionInterfaces = function getExpressionInterfaces2() {
      return expressionsInterfaces;
    };
    var setDefaultCurveSegments = function setDefaultCurveSegments2(value2) {
      defaultCurveSegments = value2;
    };
    var getDefaultCurveSegments = function getDefaultCurveSegments2() {
      return defaultCurveSegments;
    };
    var setIdPrefix = function setIdPrefix2(value2) {
      idPrefix$1 = value2;
    };
    function createNS(type) {
      return document.createElementNS(svgNS, type);
    }
    function _typeof$5(obj) {
      "@babel/helpers - typeof";
      if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
        _typeof$5 = function _typeof2(obj2) {
          return typeof obj2;
        };
      } else {
        _typeof$5 = function _typeof2(obj2) {
          return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
        };
      }
      return _typeof$5(obj);
    }
    var dataManager = /* @__PURE__ */ function() {
      var _counterId = 1;
      var processes = [];
      var workerFn;
      var workerInstance;
      var workerProxy = {
        onmessage: function onmessage() {
        },
        postMessage: function postMessage(path) {
          workerFn({
            data: path
          });
        }
      };
      var _workerSelf = {
        postMessage: function postMessage(data2) {
          workerProxy.onmessage({
            data: data2
          });
        }
      };
      function createWorker(fn) {
        if (window.Worker && window.Blob && getWebWorker()) {
          var blob = new Blob(["var _workerSelf = self; self.onmessage = ", fn.toString()], {
            type: "text/javascript"
          });
          var url = URL.createObjectURL(blob);
          return new Worker(url);
        }
        workerFn = fn;
        return workerProxy;
      }
      function setupWorker() {
        if (!workerInstance) {
          workerInstance = createWorker(function workerStart(e) {
            function dataFunctionManager() {
              function completeLayers(layers2, comps) {
                var layerData;
                var i;
                var len = layers2.length;
                var j;
                var jLen;
                var k;
                var kLen;
                for (i = 0; i < len; i += 1) {
                  layerData = layers2[i];
                  if ("ks" in layerData && !layerData.completed) {
                    layerData.completed = true;
                    if (layerData.hasMask) {
                      var maskProps = layerData.masksProperties;
                      jLen = maskProps.length;
                      for (j = 0; j < jLen; j += 1) {
                        if (maskProps[j].pt.k.i) {
                          convertPathsToAbsoluteValues(maskProps[j].pt.k);
                        } else {
                          kLen = maskProps[j].pt.k.length;
                          for (k = 0; k < kLen; k += 1) {
                            if (maskProps[j].pt.k[k].s) {
                              convertPathsToAbsoluteValues(maskProps[j].pt.k[k].s[0]);
                            }
                            if (maskProps[j].pt.k[k].e) {
                              convertPathsToAbsoluteValues(maskProps[j].pt.k[k].e[0]);
                            }
                          }
                        }
                      }
                    }
                    if (layerData.ty === 0) {
                      layerData.layers = findCompLayers(layerData.refId, comps);
                      completeLayers(layerData.layers, comps);
                    } else if (layerData.ty === 4) {
                      completeShapes(layerData.shapes);
                    } else if (layerData.ty === 5) {
                      completeText(layerData);
                    }
                  }
                }
              }
              function completeChars(chars, assets2) {
                if (chars) {
                  var i = 0;
                  var len = chars.length;
                  for (i = 0; i < len; i += 1) {
                    if (chars[i].t === 1) {
                      chars[i].data.layers = findCompLayers(chars[i].data.refId, assets2);
                      completeLayers(chars[i].data.layers, assets2);
                    }
                  }
                }
              }
              function findComp(id, comps) {
                var i = 0;
                var len = comps.length;
                while (i < len) {
                  if (comps[i].id === id) {
                    return comps[i];
                  }
                  i += 1;
                }
                return null;
              }
              function findCompLayers(id, comps) {
                var comp2 = findComp(id, comps);
                if (comp2) {
                  if (!comp2.layers.__used) {
                    comp2.layers.__used = true;
                    return comp2.layers;
                  }
                  return JSON.parse(JSON.stringify(comp2.layers));
                }
                return null;
              }
              function completeShapes(arr) {
                var i;
                var len = arr.length;
                var j;
                var jLen;
                for (i = len - 1; i >= 0; i -= 1) {
                  if (arr[i].ty === "sh") {
                    if (arr[i].ks.k.i) {
                      convertPathsToAbsoluteValues(arr[i].ks.k);
                    } else {
                      jLen = arr[i].ks.k.length;
                      for (j = 0; j < jLen; j += 1) {
                        if (arr[i].ks.k[j].s) {
                          convertPathsToAbsoluteValues(arr[i].ks.k[j].s[0]);
                        }
                        if (arr[i].ks.k[j].e) {
                          convertPathsToAbsoluteValues(arr[i].ks.k[j].e[0]);
                        }
                      }
                    }
                  } else if (arr[i].ty === "gr") {
                    completeShapes(arr[i].it);
                  }
                }
              }
              function convertPathsToAbsoluteValues(path) {
                var i;
                var len = path.i.length;
                for (i = 0; i < len; i += 1) {
                  path.i[i][0] += path.v[i][0];
                  path.i[i][1] += path.v[i][1];
                  path.o[i][0] += path.v[i][0];
                  path.o[i][1] += path.v[i][1];
                }
              }
              function checkVersion(minimum, animVersionString) {
                var animVersion = animVersionString ? animVersionString.split(".") : [100, 100, 100];
                if (minimum[0] > animVersion[0]) {
                  return true;
                }
                if (animVersion[0] > minimum[0]) {
                  return false;
                }
                if (minimum[1] > animVersion[1]) {
                  return true;
                }
                if (animVersion[1] > minimum[1]) {
                  return false;
                }
                if (minimum[2] > animVersion[2]) {
                  return true;
                }
                if (animVersion[2] > minimum[2]) {
                  return false;
                }
                return null;
              }
              var checkText = /* @__PURE__ */ function() {
                var minimumVersion = [4, 4, 14];
                function updateTextLayer(textLayer) {
                  var documentData = textLayer.t.d;
                  textLayer.t.d = {
                    k: [{
                      s: documentData,
                      t: 0
                    }]
                  };
                }
                function iterateLayers(layers2) {
                  var i;
                  var len = layers2.length;
                  for (i = 0; i < len; i += 1) {
                    if (layers2[i].ty === 5) {
                      updateTextLayer(layers2[i]);
                    }
                  }
                }
                return function(animationData2) {
                  if (checkVersion(minimumVersion, animationData2.v)) {
                    iterateLayers(animationData2.layers);
                    if (animationData2.assets) {
                      var i;
                      var len = animationData2.assets.length;
                      for (i = 0; i < len; i += 1) {
                        if (animationData2.assets[i].layers) {
                          iterateLayers(animationData2.assets[i].layers);
                        }
                      }
                    }
                  }
                };
              }();
              var checkChars = /* @__PURE__ */ function() {
                var minimumVersion = [4, 7, 99];
                return function(animationData2) {
                  if (animationData2.chars && !checkVersion(minimumVersion, animationData2.v)) {
                    var i;
                    var len = animationData2.chars.length;
                    for (i = 0; i < len; i += 1) {
                      var charData = animationData2.chars[i];
                      if (charData.data && charData.data.shapes) {
                        completeShapes(charData.data.shapes);
                        charData.data.ip = 0;
                        charData.data.op = 99999;
                        charData.data.st = 0;
                        charData.data.sr = 1;
                        charData.data.ks = {
                          p: {
                            k: [0, 0],
                            a: 0
                          },
                          s: {
                            k: [100, 100],
                            a: 0
                          },
                          a: {
                            k: [0, 0],
                            a: 0
                          },
                          r: {
                            k: 0,
                            a: 0
                          },
                          o: {
                            k: 100,
                            a: 0
                          }
                        };
                        if (!animationData2.chars[i].t) {
                          charData.data.shapes.push({
                            ty: "no"
                          });
                          charData.data.shapes[0].it.push({
                            p: {
                              k: [0, 0],
                              a: 0
                            },
                            s: {
                              k: [100, 100],
                              a: 0
                            },
                            a: {
                              k: [0, 0],
                              a: 0
                            },
                            r: {
                              k: 0,
                              a: 0
                            },
                            o: {
                              k: 100,
                              a: 0
                            },
                            sk: {
                              k: 0,
                              a: 0
                            },
                            sa: {
                              k: 0,
                              a: 0
                            },
                            ty: "tr"
                          });
                        }
                      }
                    }
                  }
                };
              }();
              var checkPathProperties = /* @__PURE__ */ function() {
                var minimumVersion = [5, 7, 15];
                function updateTextLayer(textLayer) {
                  var pathData = textLayer.t.p;
                  if (typeof pathData.a === "number") {
                    pathData.a = {
                      a: 0,
                      k: pathData.a
                    };
                  }
                  if (typeof pathData.p === "number") {
                    pathData.p = {
                      a: 0,
                      k: pathData.p
                    };
                  }
                  if (typeof pathData.r === "number") {
                    pathData.r = {
                      a: 0,
                      k: pathData.r
                    };
                  }
                }
                function iterateLayers(layers2) {
                  var i;
                  var len = layers2.length;
                  for (i = 0; i < len; i += 1) {
                    if (layers2[i].ty === 5) {
                      updateTextLayer(layers2[i]);
                    }
                  }
                }
                return function(animationData2) {
                  if (checkVersion(minimumVersion, animationData2.v)) {
                    iterateLayers(animationData2.layers);
                    if (animationData2.assets) {
                      var i;
                      var len = animationData2.assets.length;
                      for (i = 0; i < len; i += 1) {
                        if (animationData2.assets[i].layers) {
                          iterateLayers(animationData2.assets[i].layers);
                        }
                      }
                    }
                  }
                };
              }();
              var checkColors = /* @__PURE__ */ function() {
                var minimumVersion = [4, 1, 9];
                function iterateShapes(shapes) {
                  var i;
                  var len = shapes.length;
                  var j;
                  var jLen;
                  for (i = 0; i < len; i += 1) {
                    if (shapes[i].ty === "gr") {
                      iterateShapes(shapes[i].it);
                    } else if (shapes[i].ty === "fl" || shapes[i].ty === "st") {
                      if (shapes[i].c.k && shapes[i].c.k[0].i) {
                        jLen = shapes[i].c.k.length;
                        for (j = 0; j < jLen; j += 1) {
                          if (shapes[i].c.k[j].s) {
                            shapes[i].c.k[j].s[0] /= 255;
                            shapes[i].c.k[j].s[1] /= 255;
                            shapes[i].c.k[j].s[2] /= 255;
                            shapes[i].c.k[j].s[3] /= 255;
                          }
                          if (shapes[i].c.k[j].e) {
                            shapes[i].c.k[j].e[0] /= 255;
                            shapes[i].c.k[j].e[1] /= 255;
                            shapes[i].c.k[j].e[2] /= 255;
                            shapes[i].c.k[j].e[3] /= 255;
                          }
                        }
                      } else {
                        shapes[i].c.k[0] /= 255;
                        shapes[i].c.k[1] /= 255;
                        shapes[i].c.k[2] /= 255;
                        shapes[i].c.k[3] /= 255;
                      }
                    }
                  }
                }
                function iterateLayers(layers2) {
                  var i;
                  var len = layers2.length;
                  for (i = 0; i < len; i += 1) {
                    if (layers2[i].ty === 4) {
                      iterateShapes(layers2[i].shapes);
                    }
                  }
                }
                return function(animationData2) {
                  if (checkVersion(minimumVersion, animationData2.v)) {
                    iterateLayers(animationData2.layers);
                    if (animationData2.assets) {
                      var i;
                      var len = animationData2.assets.length;
                      for (i = 0; i < len; i += 1) {
                        if (animationData2.assets[i].layers) {
                          iterateLayers(animationData2.assets[i].layers);
                        }
                      }
                    }
                  }
                };
              }();
              var checkShapes = /* @__PURE__ */ function() {
                var minimumVersion = [4, 4, 18];
                function completeClosingShapes(arr) {
                  var i;
                  var len = arr.length;
                  var j;
                  var jLen;
                  for (i = len - 1; i >= 0; i -= 1) {
                    if (arr[i].ty === "sh") {
                      if (arr[i].ks.k.i) {
                        arr[i].ks.k.c = arr[i].closed;
                      } else {
                        jLen = arr[i].ks.k.length;
                        for (j = 0; j < jLen; j += 1) {
                          if (arr[i].ks.k[j].s) {
                            arr[i].ks.k[j].s[0].c = arr[i].closed;
                          }
                          if (arr[i].ks.k[j].e) {
                            arr[i].ks.k[j].e[0].c = arr[i].closed;
                          }
                        }
                      }
                    } else if (arr[i].ty === "gr") {
                      completeClosingShapes(arr[i].it);
                    }
                  }
                }
                function iterateLayers(layers2) {
                  var layerData;
                  var i;
                  var len = layers2.length;
                  var j;
                  var jLen;
                  var k;
                  var kLen;
                  for (i = 0; i < len; i += 1) {
                    layerData = layers2[i];
                    if (layerData.hasMask) {
                      var maskProps = layerData.masksProperties;
                      jLen = maskProps.length;
                      for (j = 0; j < jLen; j += 1) {
                        if (maskProps[j].pt.k.i) {
                          maskProps[j].pt.k.c = maskProps[j].cl;
                        } else {
                          kLen = maskProps[j].pt.k.length;
                          for (k = 0; k < kLen; k += 1) {
                            if (maskProps[j].pt.k[k].s) {
                              maskProps[j].pt.k[k].s[0].c = maskProps[j].cl;
                            }
                            if (maskProps[j].pt.k[k].e) {
                              maskProps[j].pt.k[k].e[0].c = maskProps[j].cl;
                            }
                          }
                        }
                      }
                    }
                    if (layerData.ty === 4) {
                      completeClosingShapes(layerData.shapes);
                    }
                  }
                }
                return function(animationData2) {
                  if (checkVersion(minimumVersion, animationData2.v)) {
                    iterateLayers(animationData2.layers);
                    if (animationData2.assets) {
                      var i;
                      var len = animationData2.assets.length;
                      for (i = 0; i < len; i += 1) {
                        if (animationData2.assets[i].layers) {
                          iterateLayers(animationData2.assets[i].layers);
                        }
                      }
                    }
                  }
                };
              }();
              function completeData(animationData2) {
                if (animationData2.__complete) {
                  return;
                }
                checkColors(animationData2);
                checkText(animationData2);
                checkChars(animationData2);
                checkPathProperties(animationData2);
                checkShapes(animationData2);
                completeLayers(animationData2.layers, animationData2.assets);
                completeChars(animationData2.chars, animationData2.assets);
                animationData2.__complete = true;
              }
              function completeText(data2) {
                if (data2.t.a.length === 0 && !("m" in data2.t.p)) ;
              }
              var moduleOb = {};
              moduleOb.completeData = completeData;
              moduleOb.checkColors = checkColors;
              moduleOb.checkChars = checkChars;
              moduleOb.checkPathProperties = checkPathProperties;
              moduleOb.checkShapes = checkShapes;
              moduleOb.completeLayers = completeLayers;
              return moduleOb;
            }
            if (!_workerSelf.dataManager) {
              _workerSelf.dataManager = dataFunctionManager();
            }
            if (!_workerSelf.assetLoader) {
              _workerSelf.assetLoader = /* @__PURE__ */ function() {
                function formatResponse(xhr) {
                  var contentTypeHeader = xhr.getResponseHeader("content-type");
                  if (contentTypeHeader && xhr.responseType === "json" && contentTypeHeader.indexOf("json") !== -1) {
                    return xhr.response;
                  }
                  if (xhr.response && _typeof$5(xhr.response) === "object") {
                    return xhr.response;
                  }
                  if (xhr.response && typeof xhr.response === "string") {
                    return JSON.parse(xhr.response);
                  }
                  if (xhr.responseText) {
                    return JSON.parse(xhr.responseText);
                  }
                  return null;
                }
                function loadAsset(path, fullPath, callback, errorCallback) {
                  var response;
                  var xhr = new XMLHttpRequest();
                  try {
                    xhr.responseType = "json";
                  } catch (err) {
                  }
                  xhr.onreadystatechange = function() {
                    if (xhr.readyState === 4) {
                      if (xhr.status === 200) {
                        response = formatResponse(xhr);
                        callback(response);
                      } else {
                        try {
                          response = formatResponse(xhr);
                          callback(response);
                        } catch (err) {
                          if (errorCallback) {
                            errorCallback(err);
                          }
                        }
                      }
                    }
                  };
                  try {
                    xhr.open(["G", "E", "T"].join(""), path, true);
                  } catch (error) {
                    xhr.open(["G", "E", "T"].join(""), fullPath + "/" + path, true);
                  }
                  xhr.send();
                }
                return {
                  load: loadAsset
                };
              }();
            }
            if (e.data.type === "loadAnimation") {
              _workerSelf.assetLoader.load(e.data.path, e.data.fullPath, function(data2) {
                _workerSelf.dataManager.completeData(data2);
                _workerSelf.postMessage({
                  id: e.data.id,
                  payload: data2,
                  status: "success"
                });
              }, function() {
                _workerSelf.postMessage({
                  id: e.data.id,
                  status: "error"
                });
              });
            } else if (e.data.type === "complete") {
              var animation = e.data.animation;
              _workerSelf.dataManager.completeData(animation);
              _workerSelf.postMessage({
                id: e.data.id,
                payload: animation,
                status: "success"
              });
            } else if (e.data.type === "loadData") {
              _workerSelf.assetLoader.load(e.data.path, e.data.fullPath, function(data2) {
                _workerSelf.postMessage({
                  id: e.data.id,
                  payload: data2,
                  status: "success"
                });
              }, function() {
                _workerSelf.postMessage({
                  id: e.data.id,
                  status: "error"
                });
              });
            }
          });
          workerInstance.onmessage = function(event) {
            var data2 = event.data;
            var id = data2.id;
            var process2 = processes[id];
            processes[id] = null;
            if (data2.status === "success") {
              process2.onComplete(data2.payload);
            } else if (process2.onError) {
              process2.onError();
            }
          };
        }
      }
      function createProcess(onComplete, onError) {
        _counterId += 1;
        var id = "processId_" + _counterId;
        processes[id] = {
          onComplete,
          onError
        };
        return id;
      }
      function loadAnimation2(path, onComplete, onError) {
        setupWorker();
        var processId = createProcess(onComplete, onError);
        workerInstance.postMessage({
          type: "loadAnimation",
          path,
          fullPath: window.location.origin + window.location.pathname,
          id: processId
        });
      }
      function loadData(path, onComplete, onError) {
        setupWorker();
        var processId = createProcess(onComplete, onError);
        workerInstance.postMessage({
          type: "loadData",
          path,
          fullPath: window.location.origin + window.location.pathname,
          id: processId
        });
      }
      function completeAnimation(anim, onComplete, onError) {
        setupWorker();
        var processId = createProcess(onComplete, onError);
        workerInstance.postMessage({
          type: "complete",
          animation: anim,
          id: processId
        });
      }
      return {
        loadAnimation: loadAnimation2,
        loadData,
        completeAnimation
      };
    }();
    var ImagePreloader = function() {
      var proxyImage = function() {
        var canvas = createTag("canvas");
        canvas.width = 1;
        canvas.height = 1;
        var ctx = canvas.getContext("2d");
        ctx.fillStyle = "rgba(0,0,0,0)";
        ctx.fillRect(0, 0, 1, 1);
        return canvas;
      }();
      function imageLoaded() {
        this.loadedAssets += 1;
        if (this.loadedAssets === this.totalImages && this.loadedFootagesCount === this.totalFootages) {
          if (this.imagesLoadedCb) {
            this.imagesLoadedCb(null);
          }
        }
      }
      function footageLoaded() {
        this.loadedFootagesCount += 1;
        if (this.loadedAssets === this.totalImages && this.loadedFootagesCount === this.totalFootages) {
          if (this.imagesLoadedCb) {
            this.imagesLoadedCb(null);
          }
        }
      }
      function getAssetsPath(assetData, assetsPath, originalPath) {
        var path = "";
        if (assetData.e) {
          path = assetData.p;
        } else if (assetsPath) {
          var imagePath = assetData.p;
          if (imagePath.indexOf("images/") !== -1) {
            imagePath = imagePath.split("/")[1];
          }
          path = assetsPath + imagePath;
        } else {
          path = originalPath;
          path += assetData.u ? assetData.u : "";
          path += assetData.p;
        }
        return path;
      }
      function testImageLoaded(img) {
        var _count = 0;
        var intervalId = setInterval((function() {
          var box = img.getBBox();
          if (box.width || _count > 500) {
            this._imageLoaded();
            clearInterval(intervalId);
          }
          _count += 1;
        }).bind(this), 50);
      }
      function createImageData(assetData) {
        var path = getAssetsPath(assetData, this.assetsPath, this.path);
        var img = createNS("image");
        if (isSafari) {
          this.testImageLoaded(img);
        } else {
          img.addEventListener("load", this._imageLoaded, false);
        }
        img.addEventListener("error", (function() {
          ob2.img = proxyImage;
          this._imageLoaded();
        }).bind(this), false);
        img.setAttributeNS("http://www.w3.org/1999/xlink", "href", path);
        if (this._elementHelper.append) {
          this._elementHelper.append(img);
        } else {
          this._elementHelper.appendChild(img);
        }
        var ob2 = {
          img,
          assetData
        };
        return ob2;
      }
      function createImgData(assetData) {
        var path = getAssetsPath(assetData, this.assetsPath, this.path);
        var img = createTag("img");
        img.crossOrigin = "anonymous";
        img.addEventListener("load", this._imageLoaded, false);
        img.addEventListener("error", (function() {
          ob2.img = proxyImage;
          this._imageLoaded();
        }).bind(this), false);
        img.src = path;
        var ob2 = {
          img,
          assetData
        };
        return ob2;
      }
      function createFootageData(data2) {
        var ob2 = {
          assetData: data2
        };
        var path = getAssetsPath(data2, this.assetsPath, this.path);
        dataManager.loadData(path, (function(footageData) {
          ob2.img = footageData;
          this._footageLoaded();
        }).bind(this), (function() {
          ob2.img = {};
          this._footageLoaded();
        }).bind(this));
        return ob2;
      }
      function loadAssets(assets2, cb) {
        this.imagesLoadedCb = cb;
        var i;
        var len = assets2.length;
        for (i = 0; i < len; i += 1) {
          if (!assets2[i].layers) {
            if (!assets2[i].t || assets2[i].t === "seq") {
              this.totalImages += 1;
              this.images.push(this._createImageData(assets2[i]));
            } else if (assets2[i].t === 3) {
              this.totalFootages += 1;
              this.images.push(this.createFootageData(assets2[i]));
            }
          }
        }
      }
      function setPath(path) {
        this.path = path || "";
      }
      function setAssetsPath(path) {
        this.assetsPath = path || "";
      }
      function getAsset(assetData) {
        var i = 0;
        var len = this.images.length;
        while (i < len) {
          if (this.images[i].assetData === assetData) {
            return this.images[i].img;
          }
          i += 1;
        }
        return null;
      }
      function destroy() {
        this.imagesLoadedCb = null;
        this.images.length = 0;
      }
      function loadedImages() {
        return this.totalImages === this.loadedAssets;
      }
      function loadedFootages() {
        return this.totalFootages === this.loadedFootagesCount;
      }
      function setCacheType(type, elementHelper) {
        if (type === "svg") {
          this._elementHelper = elementHelper;
          this._createImageData = this.createImageData.bind(this);
        } else {
          this._createImageData = this.createImgData.bind(this);
        }
      }
      function ImagePreloaderFactory() {
        this._imageLoaded = imageLoaded.bind(this);
        this._footageLoaded = footageLoaded.bind(this);
        this.testImageLoaded = testImageLoaded.bind(this);
        this.createFootageData = createFootageData.bind(this);
        this.assetsPath = "";
        this.path = "";
        this.totalImages = 0;
        this.totalFootages = 0;
        this.loadedAssets = 0;
        this.loadedFootagesCount = 0;
        this.imagesLoadedCb = null;
        this.images = [];
      }
      ImagePreloaderFactory.prototype = {
        loadAssets,
        setAssetsPath,
        setPath,
        loadedImages,
        loadedFootages,
        destroy,
        getAsset,
        createImgData,
        createImageData,
        imageLoaded,
        footageLoaded,
        setCacheType
      };
      return ImagePreloaderFactory;
    }();
    function BaseEvent() {
    }
    BaseEvent.prototype = {
      triggerEvent: function triggerEvent(eventName, args) {
        if (this._cbs[eventName]) {
          var callbacks = this._cbs[eventName];
          for (var i = 0; i < callbacks.length; i += 1) {
            callbacks[i](args);
          }
        }
      },
      addEventListener: function addEventListener(eventName, callback) {
        if (!this._cbs[eventName]) {
          this._cbs[eventName] = [];
        }
        this._cbs[eventName].push(callback);
        return (function() {
          this.removeEventListener(eventName, callback);
        }).bind(this);
      },
      removeEventListener: function removeEventListener(eventName, callback) {
        if (!callback) {
          this._cbs[eventName] = null;
        } else if (this._cbs[eventName]) {
          var i = 0;
          var len = this._cbs[eventName].length;
          while (i < len) {
            if (this._cbs[eventName][i] === callback) {
              this._cbs[eventName].splice(i, 1);
              i -= 1;
              len -= 1;
            }
            i += 1;
          }
          if (!this._cbs[eventName].length) {
            this._cbs[eventName] = null;
          }
        }
      }
    };
    var markerParser = /* @__PURE__ */ function() {
      function parsePayloadLines(payload) {
        var lines = payload.split("\r\n");
        var keys = {};
        var line;
        var keysCount = 0;
        for (var i = 0; i < lines.length; i += 1) {
          line = lines[i].split(":");
          if (line.length === 2) {
            keys[line[0]] = line[1].trim();
            keysCount += 1;
          }
        }
        if (keysCount === 0) {
          throw new Error();
        }
        return keys;
      }
      return function(_markers) {
        var markers = [];
        for (var i = 0; i < _markers.length; i += 1) {
          var _marker = _markers[i];
          var markerData = {
            time: _marker.tm,
            duration: _marker.dr
          };
          try {
            markerData.payload = JSON.parse(_markers[i].cm);
          } catch (_) {
            try {
              markerData.payload = parsePayloadLines(_markers[i].cm);
            } catch (__) {
              markerData.payload = {
                name: _markers[i].cm
              };
            }
          }
          markers.push(markerData);
        }
        return markers;
      };
    }();
    var ProjectInterface = /* @__PURE__ */ function() {
      function registerComposition(comp2) {
        this.compositions.push(comp2);
      }
      return function() {
        function _thisProjectFunction(name2) {
          var i = 0;
          var len = this.compositions.length;
          while (i < len) {
            if (this.compositions[i].data && this.compositions[i].data.nm === name2) {
              if (this.compositions[i].prepareFrame && this.compositions[i].data.xt) {
                this.compositions[i].prepareFrame(this.currentFrame);
              }
              return this.compositions[i].compInterface;
            }
            i += 1;
          }
          return null;
        }
        _thisProjectFunction.compositions = [];
        _thisProjectFunction.currentFrame = 0;
        _thisProjectFunction.registerComposition = registerComposition;
        return _thisProjectFunction;
      };
    }();
    var renderers = {};
    var registerRenderer = function registerRenderer2(key2, value2) {
      renderers[key2] = value2;
    };
    function getRenderer(key2) {
      return renderers[key2];
    }
    function getRegisteredRenderer() {
      if (renderers.canvas) {
        return "canvas";
      }
      for (var key2 in renderers) {
        if (renderers[key2]) {
          return key2;
        }
      }
      return "";
    }
    function _typeof$4(obj) {
      "@babel/helpers - typeof";
      if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
        _typeof$4 = function _typeof2(obj2) {
          return typeof obj2;
        };
      } else {
        _typeof$4 = function _typeof2(obj2) {
          return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
        };
      }
      return _typeof$4(obj);
    }
    var AnimationItem = function AnimationItem2() {
      this._cbs = [];
      this.name = "";
      this.path = "";
      this.isLoaded = false;
      this.currentFrame = 0;
      this.currentRawFrame = 0;
      this.firstFrame = 0;
      this.totalFrames = 0;
      this.frameRate = 0;
      this.frameMult = 0;
      this.playSpeed = 1;
      this.playDirection = 1;
      this.playCount = 0;
      this.animationData = {};
      this.assets = [];
      this.isPaused = true;
      this.autoplay = false;
      this.loop = true;
      this.renderer = null;
      this.animationID = createElementID();
      this.assetsPath = "";
      this.timeCompleted = 0;
      this.segmentPos = 0;
      this.isSubframeEnabled = getSubframeEnabled();
      this.segments = [];
      this._idle = true;
      this._completedLoop = false;
      this.projectInterface = ProjectInterface();
      this.imagePreloader = new ImagePreloader();
      this.audioController = audioControllerFactory();
      this.markers = [];
      this.configAnimation = this.configAnimation.bind(this);
      this.onSetupError = this.onSetupError.bind(this);
      this.onSegmentComplete = this.onSegmentComplete.bind(this);
      this.drawnFrameEvent = new BMEnterFrameEvent("drawnFrame", 0, 0, 0);
      this.expressionsPlugin = getExpressionsPlugin();
    };
    extendPrototype([BaseEvent], AnimationItem);
    AnimationItem.prototype.setParams = function(params) {
      if (params.wrapper || params.container) {
        this.wrapper = params.wrapper || params.container;
      }
      var animType = "svg";
      if (params.animType) {
        animType = params.animType;
      } else if (params.renderer) {
        animType = params.renderer;
      }
      var RendererClass = getRenderer(animType);
      this.renderer = new RendererClass(this, params.rendererSettings);
      this.imagePreloader.setCacheType(animType, this.renderer.globalData.defs);
      this.renderer.setProjectInterface(this.projectInterface);
      this.animType = animType;
      if (params.loop === "" || params.loop === null || params.loop === void 0 || params.loop === true) {
        this.loop = true;
      } else if (params.loop === false) {
        this.loop = false;
      } else {
        this.loop = parseInt(params.loop, 10);
      }
      this.autoplay = "autoplay" in params ? params.autoplay : true;
      this.name = params.name ? params.name : "";
      this.autoloadSegments = Object.prototype.hasOwnProperty.call(params, "autoloadSegments") ? params.autoloadSegments : true;
      this.assetsPath = params.assetsPath;
      this.initialSegment = params.initialSegment;
      if (params.audioFactory) {
        this.audioController.setAudioFactory(params.audioFactory);
      }
      if (params.animationData) {
        this.setupAnimation(params.animationData);
      } else if (params.path) {
        if (params.path.lastIndexOf("\\") !== -1) {
          this.path = params.path.substr(0, params.path.lastIndexOf("\\") + 1);
        } else {
          this.path = params.path.substr(0, params.path.lastIndexOf("/") + 1);
        }
        this.fileName = params.path.substr(params.path.lastIndexOf("/") + 1);
        this.fileName = this.fileName.substr(0, this.fileName.lastIndexOf(".json"));
        dataManager.loadAnimation(params.path, this.configAnimation, this.onSetupError);
      }
    };
    AnimationItem.prototype.onSetupError = function() {
      this.trigger("data_failed");
    };
    AnimationItem.prototype.setupAnimation = function(data2) {
      dataManager.completeAnimation(data2, this.configAnimation);
    };
    AnimationItem.prototype.setData = function(wrapper, animationData2) {
      if (animationData2) {
        if (_typeof$4(animationData2) !== "object") {
          animationData2 = JSON.parse(animationData2);
        }
      }
      var params = {
        wrapper,
        animationData: animationData2
      };
      var wrapperAttributes = wrapper.attributes;
      params.path = wrapperAttributes.getNamedItem("data-animation-path") ? wrapperAttributes.getNamedItem("data-animation-path").value : wrapperAttributes.getNamedItem("data-bm-path") ? wrapperAttributes.getNamedItem("data-bm-path").value : wrapperAttributes.getNamedItem("bm-path") ? wrapperAttributes.getNamedItem("bm-path").value : "";
      params.animType = wrapperAttributes.getNamedItem("data-anim-type") ? wrapperAttributes.getNamedItem("data-anim-type").value : wrapperAttributes.getNamedItem("data-bm-type") ? wrapperAttributes.getNamedItem("data-bm-type").value : wrapperAttributes.getNamedItem("bm-type") ? wrapperAttributes.getNamedItem("bm-type").value : wrapperAttributes.getNamedItem("data-bm-renderer") ? wrapperAttributes.getNamedItem("data-bm-renderer").value : wrapperAttributes.getNamedItem("bm-renderer") ? wrapperAttributes.getNamedItem("bm-renderer").value : getRegisteredRenderer() || "canvas";
      var loop = wrapperAttributes.getNamedItem("data-anim-loop") ? wrapperAttributes.getNamedItem("data-anim-loop").value : wrapperAttributes.getNamedItem("data-bm-loop") ? wrapperAttributes.getNamedItem("data-bm-loop").value : wrapperAttributes.getNamedItem("bm-loop") ? wrapperAttributes.getNamedItem("bm-loop").value : "";
      if (loop === "false") {
        params.loop = false;
      } else if (loop === "true") {
        params.loop = true;
      } else if (loop !== "") {
        params.loop = parseInt(loop, 10);
      }
      var autoplay = wrapperAttributes.getNamedItem("data-anim-autoplay") ? wrapperAttributes.getNamedItem("data-anim-autoplay").value : wrapperAttributes.getNamedItem("data-bm-autoplay") ? wrapperAttributes.getNamedItem("data-bm-autoplay").value : wrapperAttributes.getNamedItem("bm-autoplay") ? wrapperAttributes.getNamedItem("bm-autoplay").value : true;
      params.autoplay = autoplay !== "false";
      params.name = wrapperAttributes.getNamedItem("data-name") ? wrapperAttributes.getNamedItem("data-name").value : wrapperAttributes.getNamedItem("data-bm-name") ? wrapperAttributes.getNamedItem("data-bm-name").value : wrapperAttributes.getNamedItem("bm-name") ? wrapperAttributes.getNamedItem("bm-name").value : "";
      var prerender = wrapperAttributes.getNamedItem("data-anim-prerender") ? wrapperAttributes.getNamedItem("data-anim-prerender").value : wrapperAttributes.getNamedItem("data-bm-prerender") ? wrapperAttributes.getNamedItem("data-bm-prerender").value : wrapperAttributes.getNamedItem("bm-prerender") ? wrapperAttributes.getNamedItem("bm-prerender").value : "";
      if (prerender === "false") {
        params.prerender = false;
      }
      if (!params.path) {
        this.trigger("destroy");
      } else {
        this.setParams(params);
      }
    };
    AnimationItem.prototype.includeLayers = function(data2) {
      if (data2.op > this.animationData.op) {
        this.animationData.op = data2.op;
        this.totalFrames = Math.floor(data2.op - this.animationData.ip);
      }
      var layers2 = this.animationData.layers;
      var i;
      var len = layers2.length;
      var newLayers = data2.layers;
      var j;
      var jLen = newLayers.length;
      for (j = 0; j < jLen; j += 1) {
        i = 0;
        while (i < len) {
          if (layers2[i].id === newLayers[j].id) {
            layers2[i] = newLayers[j];
            break;
          }
          i += 1;
        }
      }
      if (data2.chars || data2.fonts) {
        this.renderer.globalData.fontManager.addChars(data2.chars);
        this.renderer.globalData.fontManager.addFonts(data2.fonts, this.renderer.globalData.defs);
      }
      if (data2.assets) {
        len = data2.assets.length;
        for (i = 0; i < len; i += 1) {
          this.animationData.assets.push(data2.assets[i]);
        }
      }
      this.animationData.__complete = false;
      dataManager.completeAnimation(this.animationData, this.onSegmentComplete);
    };
    AnimationItem.prototype.onSegmentComplete = function(data2) {
      this.animationData = data2;
      var expressionsPlugin2 = getExpressionsPlugin();
      if (expressionsPlugin2) {
        expressionsPlugin2.initExpressions(this);
      }
      this.loadNextSegment();
    };
    AnimationItem.prototype.loadNextSegment = function() {
      var segments = this.animationData.segments;
      if (!segments || segments.length === 0 || !this.autoloadSegments) {
        this.trigger("data_ready");
        this.timeCompleted = this.totalFrames;
        return;
      }
      var segment = segments.shift();
      this.timeCompleted = segment.time * this.frameRate;
      var segmentPath = this.path + this.fileName + "_" + this.segmentPos + ".json";
      this.segmentPos += 1;
      dataManager.loadData(segmentPath, this.includeLayers.bind(this), (function() {
        this.trigger("data_failed");
      }).bind(this));
    };
    AnimationItem.prototype.loadSegments = function() {
      var segments = this.animationData.segments;
      if (!segments) {
        this.timeCompleted = this.totalFrames;
      }
      this.loadNextSegment();
    };
    AnimationItem.prototype.imagesLoaded = function() {
      this.trigger("loaded_images");
      this.checkLoaded();
    };
    AnimationItem.prototype.preloadImages = function() {
      this.imagePreloader.setAssetsPath(this.assetsPath);
      this.imagePreloader.setPath(this.path);
      this.imagePreloader.loadAssets(this.animationData.assets, this.imagesLoaded.bind(this));
    };
    AnimationItem.prototype.configAnimation = function(animData) {
      if (!this.renderer) {
        return;
      }
      try {
        this.animationData = animData;
        if (this.initialSegment) {
          this.totalFrames = Math.floor(this.initialSegment[1] - this.initialSegment[0]);
          this.firstFrame = Math.round(this.initialSegment[0]);
        } else {
          this.totalFrames = Math.floor(this.animationData.op - this.animationData.ip);
          this.firstFrame = Math.round(this.animationData.ip);
        }
        this.renderer.configAnimation(animData);
        if (!animData.assets) {
          animData.assets = [];
        }
        this.assets = this.animationData.assets;
        this.frameRate = this.animationData.fr;
        this.frameMult = this.animationData.fr / 1e3;
        this.renderer.searchExtraCompositions(animData.assets);
        this.markers = markerParser(animData.markers || []);
        this.trigger("config_ready");
        this.preloadImages();
        this.loadSegments();
        this.updaFrameModifier();
        this.waitForFontsLoaded();
        if (this.isPaused) {
          this.audioController.pause();
        }
      } catch (error) {
        this.triggerConfigError(error);
      }
    };
    AnimationItem.prototype.waitForFontsLoaded = function() {
      if (!this.renderer) {
        return;
      }
      if (this.renderer.globalData.fontManager.isLoaded) {
        this.checkLoaded();
      } else {
        setTimeout(this.waitForFontsLoaded.bind(this), 20);
      }
    };
    AnimationItem.prototype.checkLoaded = function() {
      if (!this.isLoaded && this.renderer.globalData.fontManager.isLoaded && (this.imagePreloader.loadedImages() || this.renderer.rendererType !== "canvas") && this.imagePreloader.loadedFootages()) {
        this.isLoaded = true;
        var expressionsPlugin2 = getExpressionsPlugin();
        if (expressionsPlugin2) {
          expressionsPlugin2.initExpressions(this);
        }
        this.renderer.initItems();
        setTimeout((function() {
          this.trigger("DOMLoaded");
        }).bind(this), 0);
        this.gotoFrame();
        if (this.autoplay) {
          this.play();
        }
      }
    };
    AnimationItem.prototype.resize = function(width2, height2) {
      var _width = typeof width2 === "number" ? width2 : void 0;
      var _height = typeof height2 === "number" ? height2 : void 0;
      this.renderer.updateContainerSize(_width, _height);
    };
    AnimationItem.prototype.setSubframe = function(flag) {
      this.isSubframeEnabled = !!flag;
    };
    AnimationItem.prototype.gotoFrame = function() {
      this.currentFrame = this.isSubframeEnabled ? this.currentRawFrame : ~~this.currentRawFrame;
      if (this.timeCompleted !== this.totalFrames && this.currentFrame > this.timeCompleted) {
        this.currentFrame = this.timeCompleted;
      }
      this.trigger("enterFrame");
      this.renderFrame();
      this.trigger("drawnFrame");
    };
    AnimationItem.prototype.renderFrame = function() {
      if (this.isLoaded === false || !this.renderer) {
        return;
      }
      try {
        if (this.expressionsPlugin) {
          this.expressionsPlugin.resetFrame();
        }
        this.renderer.renderFrame(this.currentFrame + this.firstFrame);
      } catch (error) {
        this.triggerRenderFrameError(error);
      }
    };
    AnimationItem.prototype.play = function(name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      if (this.isPaused === true) {
        this.isPaused = false;
        this.trigger("_play");
        this.audioController.resume();
        if (this._idle) {
          this._idle = false;
          this.trigger("_active");
        }
      }
    };
    AnimationItem.prototype.pause = function(name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      if (this.isPaused === false) {
        this.isPaused = true;
        this.trigger("_pause");
        this._idle = true;
        this.trigger("_idle");
        this.audioController.pause();
      }
    };
    AnimationItem.prototype.togglePause = function(name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      if (this.isPaused === true) {
        this.play();
      } else {
        this.pause();
      }
    };
    AnimationItem.prototype.stop = function(name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      this.pause();
      this.playCount = 0;
      this._completedLoop = false;
      this.setCurrentRawFrameValue(0);
    };
    AnimationItem.prototype.getMarkerData = function(markerName) {
      var marker;
      for (var i = 0; i < this.markers.length; i += 1) {
        marker = this.markers[i];
        if (marker.payload && marker.payload.name === markerName) {
          return marker;
        }
      }
      return null;
    };
    AnimationItem.prototype.goToAndStop = function(value2, isFrame, name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      var numValue = Number(value2);
      if (isNaN(numValue)) {
        var marker = this.getMarkerData(value2);
        if (marker) {
          this.goToAndStop(marker.time, true);
        }
      } else if (isFrame) {
        this.setCurrentRawFrameValue(value2);
      } else {
        this.setCurrentRawFrameValue(value2 * this.frameModifier);
      }
      this.pause();
    };
    AnimationItem.prototype.goToAndPlay = function(value2, isFrame, name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      var numValue = Number(value2);
      if (isNaN(numValue)) {
        var marker = this.getMarkerData(value2);
        if (marker) {
          if (!marker.duration) {
            this.goToAndStop(marker.time, true);
          } else {
            this.playSegments([marker.time, marker.time + marker.duration], true);
          }
        }
      } else {
        this.goToAndStop(numValue, isFrame, name2);
      }
      this.play();
    };
    AnimationItem.prototype.advanceTime = function(value2) {
      if (this.isPaused === true || this.isLoaded === false) {
        return;
      }
      var nextValue = this.currentRawFrame + value2 * this.frameModifier;
      var _isComplete = false;
      if (nextValue >= this.totalFrames - 1 && this.frameModifier > 0) {
        if (!this.loop || this.playCount === this.loop) {
          if (!this.checkSegments(nextValue > this.totalFrames ? nextValue % this.totalFrames : 0)) {
            _isComplete = true;
            nextValue = this.totalFrames - 1;
          }
        } else if (nextValue >= this.totalFrames) {
          this.playCount += 1;
          if (!this.checkSegments(nextValue % this.totalFrames)) {
            this.setCurrentRawFrameValue(nextValue % this.totalFrames);
            this._completedLoop = true;
            this.trigger("loopComplete");
          }
        } else {
          this.setCurrentRawFrameValue(nextValue);
        }
      } else if (nextValue < 0) {
        if (!this.checkSegments(nextValue % this.totalFrames)) {
          if (this.loop && !(this.playCount-- <= 0 && this.loop !== true)) {
            this.setCurrentRawFrameValue(this.totalFrames + nextValue % this.totalFrames);
            if (!this._completedLoop) {
              this._completedLoop = true;
            } else {
              this.trigger("loopComplete");
            }
          } else {
            _isComplete = true;
            nextValue = 0;
          }
        }
      } else {
        this.setCurrentRawFrameValue(nextValue);
      }
      if (_isComplete) {
        this.setCurrentRawFrameValue(nextValue);
        this.pause();
        this.trigger("complete");
      }
    };
    AnimationItem.prototype.adjustSegment = function(arr, offset) {
      this.playCount = 0;
      if (arr[1] < arr[0]) {
        if (this.frameModifier > 0) {
          if (this.playSpeed < 0) {
            this.setSpeed(-this.playSpeed);
          } else {
            this.setDirection(-1);
          }
        }
        this.totalFrames = arr[0] - arr[1];
        this.timeCompleted = this.totalFrames;
        this.firstFrame = arr[1];
        this.setCurrentRawFrameValue(this.totalFrames - 1e-3 - offset);
      } else if (arr[1] > arr[0]) {
        if (this.frameModifier < 0) {
          if (this.playSpeed < 0) {
            this.setSpeed(-this.playSpeed);
          } else {
            this.setDirection(1);
          }
        }
        this.totalFrames = arr[1] - arr[0];
        this.timeCompleted = this.totalFrames;
        this.firstFrame = arr[0];
        this.setCurrentRawFrameValue(1e-3 + offset);
      }
      this.trigger("segmentStart");
    };
    AnimationItem.prototype.setSegment = function(init, end) {
      var pendingFrame = -1;
      if (this.isPaused) {
        if (this.currentRawFrame + this.firstFrame < init) {
          pendingFrame = init;
        } else if (this.currentRawFrame + this.firstFrame > end) {
          pendingFrame = end - init;
        }
      }
      this.firstFrame = init;
      this.totalFrames = end - init;
      this.timeCompleted = this.totalFrames;
      if (pendingFrame !== -1) {
        this.goToAndStop(pendingFrame, true);
      }
    };
    AnimationItem.prototype.playSegments = function(arr, forceFlag) {
      if (forceFlag) {
        this.segments.length = 0;
      }
      if (_typeof$4(arr[0]) === "object") {
        var i;
        var len = arr.length;
        for (i = 0; i < len; i += 1) {
          this.segments.push(arr[i]);
        }
      } else {
        this.segments.push(arr);
      }
      if (this.segments.length && forceFlag) {
        this.adjustSegment(this.segments.shift(), 0);
      }
      if (this.isPaused) {
        this.play();
      }
    };
    AnimationItem.prototype.resetSegments = function(forceFlag) {
      this.segments.length = 0;
      this.segments.push([this.animationData.ip, this.animationData.op]);
      if (forceFlag) {
        this.checkSegments(0);
      }
    };
    AnimationItem.prototype.checkSegments = function(offset) {
      if (this.segments.length) {
        this.adjustSegment(this.segments.shift(), offset);
        return true;
      }
      return false;
    };
    AnimationItem.prototype.destroy = function(name2) {
      if (name2 && this.name !== name2 || !this.renderer) {
        return;
      }
      this.renderer.destroy();
      this.imagePreloader.destroy();
      this.trigger("destroy");
      this._cbs = null;
      this.onEnterFrame = null;
      this.onLoopComplete = null;
      this.onComplete = null;
      this.onSegmentStart = null;
      this.onDestroy = null;
      this.renderer = null;
      this.expressionsPlugin = null;
      this.imagePreloader = null;
      this.projectInterface = null;
    };
    AnimationItem.prototype.setCurrentRawFrameValue = function(value2) {
      this.currentRawFrame = value2;
      this.gotoFrame();
    };
    AnimationItem.prototype.setSpeed = function(val2) {
      this.playSpeed = val2;
      this.updaFrameModifier();
    };
    AnimationItem.prototype.setDirection = function(val2) {
      this.playDirection = val2 < 0 ? -1 : 1;
      this.updaFrameModifier();
    };
    AnimationItem.prototype.setLoop = function(isLooping) {
      this.loop = isLooping;
    };
    AnimationItem.prototype.setVolume = function(val2, name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      this.audioController.setVolume(val2);
    };
    AnimationItem.prototype.getVolume = function() {
      return this.audioController.getVolume();
    };
    AnimationItem.prototype.mute = function(name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      this.audioController.mute();
    };
    AnimationItem.prototype.unmute = function(name2) {
      if (name2 && this.name !== name2) {
        return;
      }
      this.audioController.unmute();
    };
    AnimationItem.prototype.updaFrameModifier = function() {
      this.frameModifier = this.frameMult * this.playSpeed * this.playDirection;
      this.audioController.setRate(this.playSpeed * this.playDirection);
    };
    AnimationItem.prototype.getPath = function() {
      return this.path;
    };
    AnimationItem.prototype.getAssetsPath = function(assetData) {
      var path = "";
      if (assetData.e) {
        path = assetData.p;
      } else if (this.assetsPath) {
        var imagePath = assetData.p;
        if (imagePath.indexOf("images/") !== -1) {
          imagePath = imagePath.split("/")[1];
        }
        path = this.assetsPath + imagePath;
      } else {
        path = this.path;
        path += assetData.u ? assetData.u : "";
        path += assetData.p;
      }
      return path;
    };
    AnimationItem.prototype.getAssetData = function(id) {
      var i = 0;
      var len = this.assets.length;
      while (i < len) {
        if (id === this.assets[i].id) {
          return this.assets[i];
        }
        i += 1;
      }
      return null;
    };
    AnimationItem.prototype.hide = function() {
      this.renderer.hide();
    };
    AnimationItem.prototype.show = function() {
      this.renderer.show();
    };
    AnimationItem.prototype.getDuration = function(isFrame) {
      return isFrame ? this.totalFrames : this.totalFrames / this.frameRate;
    };
    AnimationItem.prototype.updateDocumentData = function(path, documentData, index2) {
      try {
        var element = this.renderer.getElementByPath(path);
        element.updateDocumentData(documentData, index2);
      } catch (error) {
      }
    };
    AnimationItem.prototype.trigger = function(name2) {
      if (this._cbs && this._cbs[name2]) {
        switch (name2) {
          case "enterFrame":
            this.triggerEvent(name2, new BMEnterFrameEvent(name2, this.currentFrame, this.totalFrames, this.frameModifier));
            break;
          case "drawnFrame":
            this.drawnFrameEvent.currentTime = this.currentFrame;
            this.drawnFrameEvent.totalTime = this.totalFrames;
            this.drawnFrameEvent.direction = this.frameModifier;
            this.triggerEvent(name2, this.drawnFrameEvent);
            break;
          case "loopComplete":
            this.triggerEvent(name2, new BMCompleteLoopEvent(name2, this.loop, this.playCount, this.frameMult));
            break;
          case "complete":
            this.triggerEvent(name2, new BMCompleteEvent(name2, this.frameMult));
            break;
          case "segmentStart":
            this.triggerEvent(name2, new BMSegmentStartEvent(name2, this.firstFrame, this.totalFrames));
            break;
          case "destroy":
            this.triggerEvent(name2, new BMDestroyEvent(name2, this));
            break;
          default:
            this.triggerEvent(name2);
        }
      }
      if (name2 === "enterFrame" && this.onEnterFrame) {
        this.onEnterFrame.call(this, new BMEnterFrameEvent(name2, this.currentFrame, this.totalFrames, this.frameMult));
      }
      if (name2 === "loopComplete" && this.onLoopComplete) {
        this.onLoopComplete.call(this, new BMCompleteLoopEvent(name2, this.loop, this.playCount, this.frameMult));
      }
      if (name2 === "complete" && this.onComplete) {
        this.onComplete.call(this, new BMCompleteEvent(name2, this.frameMult));
      }
      if (name2 === "segmentStart" && this.onSegmentStart) {
        this.onSegmentStart.call(this, new BMSegmentStartEvent(name2, this.firstFrame, this.totalFrames));
      }
      if (name2 === "destroy" && this.onDestroy) {
        this.onDestroy.call(this, new BMDestroyEvent(name2, this));
      }
    };
    AnimationItem.prototype.triggerRenderFrameError = function(nativeError) {
      var error = new BMRenderFrameErrorEvent(nativeError, this.currentFrame);
      this.triggerEvent("error", error);
      if (this.onError) {
        this.onError.call(this, error);
      }
    };
    AnimationItem.prototype.triggerConfigError = function(nativeError) {
      var error = new BMConfigErrorEvent(nativeError, this.currentFrame);
      this.triggerEvent("error", error);
      if (this.onError) {
        this.onError.call(this, error);
      }
    };
    var animationManager = function() {
      var moduleOb = {};
      var registeredAnimations = [];
      var initTime = 0;
      var len = 0;
      var playingAnimationsNum = 0;
      var _stopped = true;
      var _isFrozen = false;
      function removeElement(ev) {
        var i = 0;
        var animItem = ev.target;
        while (i < len) {
          if (registeredAnimations[i].animation === animItem) {
            registeredAnimations.splice(i, 1);
            i -= 1;
            len -= 1;
            if (!animItem.isPaused) {
              subtractPlayingCount();
            }
          }
          i += 1;
        }
      }
      function registerAnimation(element, animationData2) {
        if (!element) {
          return null;
        }
        var i = 0;
        while (i < len) {
          if (registeredAnimations[i].elem === element && registeredAnimations[i].elem !== null) {
            return registeredAnimations[i].animation;
          }
          i += 1;
        }
        var animItem = new AnimationItem();
        setupAnimation(animItem, element);
        animItem.setData(element, animationData2);
        return animItem;
      }
      function getRegisteredAnimations() {
        var i;
        var lenAnims = registeredAnimations.length;
        var animations = [];
        for (i = 0; i < lenAnims; i += 1) {
          animations.push(registeredAnimations[i].animation);
        }
        return animations;
      }
      function addPlayingCount() {
        playingAnimationsNum += 1;
        activate();
      }
      function subtractPlayingCount() {
        playingAnimationsNum -= 1;
      }
      function setupAnimation(animItem, element) {
        animItem.addEventListener("destroy", removeElement);
        animItem.addEventListener("_active", addPlayingCount);
        animItem.addEventListener("_idle", subtractPlayingCount);
        registeredAnimations.push({
          elem: element,
          animation: animItem
        });
        len += 1;
      }
      function loadAnimation2(params) {
        var animItem = new AnimationItem();
        setupAnimation(animItem, null);
        animItem.setParams(params);
        return animItem;
      }
      function setSpeed(val2, animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.setSpeed(val2, animation);
        }
      }
      function setDirection(val2, animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.setDirection(val2, animation);
        }
      }
      function play(animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.play(animation);
        }
      }
      function resume(nowTime) {
        var elapsedTime = nowTime - initTime;
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.advanceTime(elapsedTime);
        }
        initTime = nowTime;
        if (playingAnimationsNum && !_isFrozen) {
          window.requestAnimationFrame(resume);
        } else {
          _stopped = true;
        }
      }
      function first(nowTime) {
        initTime = nowTime;
        window.requestAnimationFrame(resume);
      }
      function pause(animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.pause(animation);
        }
      }
      function goToAndStop(value2, isFrame, animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.goToAndStop(value2, isFrame, animation);
        }
      }
      function stop(animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.stop(animation);
        }
      }
      function togglePause(animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.togglePause(animation);
        }
      }
      function destroy(animation) {
        var i;
        for (i = len - 1; i >= 0; i -= 1) {
          registeredAnimations[i].animation.destroy(animation);
        }
      }
      function searchAnimations2(animationData2, standalone2, renderer2) {
        var animElements = [].concat([].slice.call(document.getElementsByClassName("lottie")), [].slice.call(document.getElementsByClassName("bodymovin")));
        var i;
        var lenAnims = animElements.length;
        for (i = 0; i < lenAnims; i += 1) {
          if (renderer2) {
            animElements[i].setAttribute("data-bm-type", renderer2);
          }
          registerAnimation(animElements[i], animationData2);
        }
        if (standalone2 && lenAnims === 0) {
          if (!renderer2) {
            renderer2 = "svg";
          }
          var body = document.getElementsByTagName("body")[0];
          body.innerText = "";
          var div2 = createTag("div");
          div2.style.width = "100%";
          div2.style.height = "100%";
          div2.setAttribute("data-bm-type", renderer2);
          body.appendChild(div2);
          registerAnimation(div2, animationData2);
        }
      }
      function resize() {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.resize();
        }
      }
      function activate() {
        if (!_isFrozen && playingAnimationsNum) {
          if (_stopped) {
            window.requestAnimationFrame(first);
            _stopped = false;
          }
        }
      }
      function freeze() {
        _isFrozen = true;
      }
      function unfreeze() {
        _isFrozen = false;
        activate();
      }
      function setVolume(val2, animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.setVolume(val2, animation);
        }
      }
      function mute(animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.mute(animation);
        }
      }
      function unmute(animation) {
        var i;
        for (i = 0; i < len; i += 1) {
          registeredAnimations[i].animation.unmute(animation);
        }
      }
      moduleOb.registerAnimation = registerAnimation;
      moduleOb.loadAnimation = loadAnimation2;
      moduleOb.setSpeed = setSpeed;
      moduleOb.setDirection = setDirection;
      moduleOb.play = play;
      moduleOb.pause = pause;
      moduleOb.stop = stop;
      moduleOb.togglePause = togglePause;
      moduleOb.searchAnimations = searchAnimations2;
      moduleOb.resize = resize;
      moduleOb.goToAndStop = goToAndStop;
      moduleOb.destroy = destroy;
      moduleOb.freeze = freeze;
      moduleOb.unfreeze = unfreeze;
      moduleOb.setVolume = setVolume;
      moduleOb.mute = mute;
      moduleOb.unmute = unmute;
      moduleOb.getRegisteredAnimations = getRegisteredAnimations;
      return moduleOb;
    }();
    var BezierFactory = function() {
      var ob2 = {};
      ob2.getBezierEasing = getBezierEasing;
      var beziers = {};
      function getBezierEasing(a, b, c, d, nm) {
        var str = nm || ("bez_" + a + "_" + b + "_" + c + "_" + d).replace(/\./g, "p");
        if (beziers[str]) {
          return beziers[str];
        }
        var bezEasing = new BezierEasing([a, b, c, d]);
        beziers[str] = bezEasing;
        return bezEasing;
      }
      var NEWTON_ITERATIONS = 4;
      var NEWTON_MIN_SLOPE = 1e-3;
      var SUBDIVISION_PRECISION = 1e-7;
      var SUBDIVISION_MAX_ITERATIONS = 10;
      var kSplineTableSize = 11;
      var kSampleStepSize = 1 / (kSplineTableSize - 1);
      var float32ArraySupported = typeof Float32Array === "function";
      function A(aA1, aA2) {
        return 1 - 3 * aA2 + 3 * aA1;
      }
      function B(aA1, aA2) {
        return 3 * aA2 - 6 * aA1;
      }
      function C(aA1) {
        return 3 * aA1;
      }
      function calcBezier(aT, aA1, aA2) {
        return ((A(aA1, aA2) * aT + B(aA1, aA2)) * aT + C(aA1)) * aT;
      }
      function getSlope(aT, aA1, aA2) {
        return 3 * A(aA1, aA2) * aT * aT + 2 * B(aA1, aA2) * aT + C(aA1);
      }
      function binarySubdivide(aX, aA, aB, mX1, mX2) {
        var currentX, currentT, i = 0;
        do {
          currentT = aA + (aB - aA) / 2;
          currentX = calcBezier(currentT, mX1, mX2) - aX;
          if (currentX > 0) {
            aB = currentT;
          } else {
            aA = currentT;
          }
        } while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS);
        return currentT;
      }
      function newtonRaphsonIterate(aX, aGuessT, mX1, mX2) {
        for (var i = 0; i < NEWTON_ITERATIONS; ++i) {
          var currentSlope = getSlope(aGuessT, mX1, mX2);
          if (currentSlope === 0) return aGuessT;
          var currentX = calcBezier(aGuessT, mX1, mX2) - aX;
          aGuessT -= currentX / currentSlope;
        }
        return aGuessT;
      }
      function BezierEasing(points) {
        this._p = points;
        this._mSampleValues = float32ArraySupported ? new Float32Array(kSplineTableSize) : new Array(kSplineTableSize);
        this._precomputed = false;
        this.get = this.get.bind(this);
      }
      BezierEasing.prototype = {
        get: function get(x) {
          var mX1 = this._p[0], mY1 = this._p[1], mX2 = this._p[2], mY2 = this._p[3];
          if (!this._precomputed) this._precompute();
          if (mX1 === mY1 && mX2 === mY2) return x;
          if (x === 0) return 0;
          if (x === 1) return 1;
          return calcBezier(this._getTForX(x), mY1, mY2);
        },
        // Private part
        _precompute: function _precompute() {
          var mX1 = this._p[0], mY1 = this._p[1], mX2 = this._p[2], mY2 = this._p[3];
          this._precomputed = true;
          if (mX1 !== mY1 || mX2 !== mY2) {
            this._calcSampleValues();
          }
        },
        _calcSampleValues: function _calcSampleValues() {
          var mX1 = this._p[0], mX2 = this._p[2];
          for (var i = 0; i < kSplineTableSize; ++i) {
            this._mSampleValues[i] = calcBezier(i * kSampleStepSize, mX1, mX2);
          }
        },
        /**
             * getTForX chose the fastest heuristic to determine the percentage value precisely from a given X projection.
             */
        _getTForX: function _getTForX(aX) {
          var mX1 = this._p[0], mX2 = this._p[2], mSampleValues = this._mSampleValues;
          var intervalStart = 0;
          var currentSample = 1;
          var lastSample = kSplineTableSize - 1;
          for (; currentSample !== lastSample && mSampleValues[currentSample] <= aX; ++currentSample) {
            intervalStart += kSampleStepSize;
          }
          --currentSample;
          var dist = (aX - mSampleValues[currentSample]) / (mSampleValues[currentSample + 1] - mSampleValues[currentSample]);
          var guessForT = intervalStart + dist * kSampleStepSize;
          var initialSlope = getSlope(guessForT, mX1, mX2);
          if (initialSlope >= NEWTON_MIN_SLOPE) {
            return newtonRaphsonIterate(aX, guessForT, mX1, mX2);
          }
          if (initialSlope === 0) {
            return guessForT;
          }
          return binarySubdivide(aX, intervalStart, intervalStart + kSampleStepSize, mX1, mX2);
        }
      };
      return ob2;
    }();
    var pooling = /* @__PURE__ */ function() {
      function _double(arr) {
        return arr.concat(createSizedArray(arr.length));
      }
      return {
        "double": _double
      };
    }();
    var poolFactory = /* @__PURE__ */ function() {
      return function(initialLength, _create, _release) {
        var _length = 0;
        var _maxLength = initialLength;
        var pool = createSizedArray(_maxLength);
        var ob2 = {
          newElement,
          release
        };
        function newElement() {
          var element;
          if (_length) {
            _length -= 1;
            element = pool[_length];
          } else {
            element = _create();
          }
          return element;
        }
        function release(element) {
          if (_length === _maxLength) {
            pool = pooling["double"](pool);
            _maxLength *= 2;
          }
          if (_release) {
            _release(element);
          }
          pool[_length] = element;
          _length += 1;
        }
        return ob2;
      };
    }();
    var bezierLengthPool = function() {
      function create() {
        return {
          addedLength: 0,
          percents: createTypedArray("float32", getDefaultCurveSegments()),
          lengths: createTypedArray("float32", getDefaultCurveSegments())
        };
      }
      return poolFactory(8, create);
    }();
    var segmentsLengthPool = function() {
      function create() {
        return {
          lengths: [],
          totalLength: 0
        };
      }
      function release(element) {
        var i;
        var len = element.lengths.length;
        for (i = 0; i < len; i += 1) {
          bezierLengthPool.release(element.lengths[i]);
        }
        element.lengths.length = 0;
      }
      return poolFactory(8, create, release);
    }();
    function bezFunction() {
      var math = Math;
      function pointOnLine2D(x1, y1, x2, y2, x3, y3) {
        var det1 = x1 * y2 + y1 * x3 + x2 * y3 - x3 * y2 - y3 * x1 - x2 * y1;
        return det1 > -1e-3 && det1 < 1e-3;
      }
      function pointOnLine3D(x1, y1, z1, x2, y2, z2, x3, y3, z3) {
        if (z1 === 0 && z2 === 0 && z3 === 0) {
          return pointOnLine2D(x1, y1, x2, y2, x3, y3);
        }
        var dist1 = math.sqrt(math.pow(x2 - x1, 2) + math.pow(y2 - y1, 2) + math.pow(z2 - z1, 2));
        var dist2 = math.sqrt(math.pow(x3 - x1, 2) + math.pow(y3 - y1, 2) + math.pow(z3 - z1, 2));
        var dist3 = math.sqrt(math.pow(x3 - x2, 2) + math.pow(y3 - y2, 2) + math.pow(z3 - z2, 2));
        var diffDist;
        if (dist1 > dist2) {
          if (dist1 > dist3) {
            diffDist = dist1 - dist2 - dist3;
          } else {
            diffDist = dist3 - dist2 - dist1;
          }
        } else if (dist3 > dist2) {
          diffDist = dist3 - dist2 - dist1;
        } else {
          diffDist = dist2 - dist1 - dist3;
        }
        return diffDist > -1e-4 && diffDist < 1e-4;
      }
      var getBezierLength = /* @__PURE__ */ function() {
        return function(pt1, pt2, pt3, pt4) {
          var curveSegments = getDefaultCurveSegments();
          var k;
          var i;
          var len;
          var ptCoord;
          var perc;
          var addedLength = 0;
          var ptDistance;
          var point = [];
          var lastPoint = [];
          var lengthData = bezierLengthPool.newElement();
          len = pt3.length;
          for (k = 0; k < curveSegments; k += 1) {
            perc = k / (curveSegments - 1);
            ptDistance = 0;
            for (i = 0; i < len; i += 1) {
              ptCoord = bmPow(1 - perc, 3) * pt1[i] + 3 * bmPow(1 - perc, 2) * perc * pt3[i] + 3 * (1 - perc) * bmPow(perc, 2) * pt4[i] + bmPow(perc, 3) * pt2[i];
              point[i] = ptCoord;
              if (lastPoint[i] !== null) {
                ptDistance += bmPow(point[i] - lastPoint[i], 2);
              }
              lastPoint[i] = point[i];
            }
            if (ptDistance) {
              ptDistance = bmSqrt(ptDistance);
              addedLength += ptDistance;
            }
            lengthData.percents[k] = perc;
            lengthData.lengths[k] = addedLength;
          }
          lengthData.addedLength = addedLength;
          return lengthData;
        };
      }();
      function getSegmentsLength(shapeData) {
        var segmentsLength = segmentsLengthPool.newElement();
        var closed = shapeData.c;
        var pathV = shapeData.v;
        var pathO = shapeData.o;
        var pathI = shapeData.i;
        var i;
        var len = shapeData._length;
        var lengths = segmentsLength.lengths;
        var totalLength = 0;
        for (i = 0; i < len - 1; i += 1) {
          lengths[i] = getBezierLength(pathV[i], pathV[i + 1], pathO[i], pathI[i + 1]);
          totalLength += lengths[i].addedLength;
        }
        if (closed && len) {
          lengths[i] = getBezierLength(pathV[i], pathV[0], pathO[i], pathI[0]);
          totalLength += lengths[i].addedLength;
        }
        segmentsLength.totalLength = totalLength;
        return segmentsLength;
      }
      function BezierData(length2) {
        this.segmentLength = 0;
        this.points = new Array(length2);
      }
      function PointData(partial, point) {
        this.partialLength = partial;
        this.point = point;
      }
      var buildBezierData = /* @__PURE__ */ function() {
        var storedData = {};
        return function(pt1, pt2, pt3, pt4) {
          var bezierName = (pt1[0] + "_" + pt1[1] + "_" + pt2[0] + "_" + pt2[1] + "_" + pt3[0] + "_" + pt3[1] + "_" + pt4[0] + "_" + pt4[1]).replace(/\./g, "p");
          if (!storedData[bezierName]) {
            var curveSegments = getDefaultCurveSegments();
            var k;
            var i;
            var len;
            var ptCoord;
            var perc;
            var addedLength = 0;
            var ptDistance;
            var point;
            var lastPoint = null;
            if (pt1.length === 2 && (pt1[0] !== pt2[0] || pt1[1] !== pt2[1]) && pointOnLine2D(pt1[0], pt1[1], pt2[0], pt2[1], pt1[0] + pt3[0], pt1[1] + pt3[1]) && pointOnLine2D(pt1[0], pt1[1], pt2[0], pt2[1], pt2[0] + pt4[0], pt2[1] + pt4[1])) {
              curveSegments = 2;
            }
            var bezierData = new BezierData(curveSegments);
            len = pt3.length;
            for (k = 0; k < curveSegments; k += 1) {
              point = createSizedArray(len);
              perc = k / (curveSegments - 1);
              ptDistance = 0;
              for (i = 0; i < len; i += 1) {
                ptCoord = bmPow(1 - perc, 3) * pt1[i] + 3 * bmPow(1 - perc, 2) * perc * (pt1[i] + pt3[i]) + 3 * (1 - perc) * bmPow(perc, 2) * (pt2[i] + pt4[i]) + bmPow(perc, 3) * pt2[i];
                point[i] = ptCoord;
                if (lastPoint !== null) {
                  ptDistance += bmPow(point[i] - lastPoint[i], 2);
                }
              }
              ptDistance = bmSqrt(ptDistance);
              addedLength += ptDistance;
              bezierData.points[k] = new PointData(ptDistance, point);
              lastPoint = point;
            }
            bezierData.segmentLength = addedLength;
            storedData[bezierName] = bezierData;
          }
          return storedData[bezierName];
        };
      }();
      function getDistancePerc(perc, bezierData) {
        var percents = bezierData.percents;
        var lengths = bezierData.lengths;
        var len = percents.length;
        var initPos = bmFloor((len - 1) * perc);
        var lengthPos = perc * bezierData.addedLength;
        var lPerc = 0;
        if (initPos === len - 1 || initPos === 0 || lengthPos === lengths[initPos]) {
          return percents[initPos];
        }
        var dir = lengths[initPos] > lengthPos ? -1 : 1;
        var flag = true;
        while (flag) {
          if (lengths[initPos] <= lengthPos && lengths[initPos + 1] > lengthPos) {
            lPerc = (lengthPos - lengths[initPos]) / (lengths[initPos + 1] - lengths[initPos]);
            flag = false;
          } else {
            initPos += dir;
          }
          if (initPos < 0 || initPos >= len - 1) {
            if (initPos === len - 1) {
              return percents[initPos];
            }
            flag = false;
          }
        }
        return percents[initPos] + (percents[initPos + 1] - percents[initPos]) * lPerc;
      }
      function getPointInSegment(pt1, pt2, pt3, pt4, percent, bezierData) {
        var t1 = getDistancePerc(percent, bezierData);
        var u1 = 1 - t1;
        var ptX = math.round((u1 * u1 * u1 * pt1[0] + (t1 * u1 * u1 + u1 * t1 * u1 + u1 * u1 * t1) * pt3[0] + (t1 * t1 * u1 + u1 * t1 * t1 + t1 * u1 * t1) * pt4[0] + t1 * t1 * t1 * pt2[0]) * 1e3) / 1e3;
        var ptY = math.round((u1 * u1 * u1 * pt1[1] + (t1 * u1 * u1 + u1 * t1 * u1 + u1 * u1 * t1) * pt3[1] + (t1 * t1 * u1 + u1 * t1 * t1 + t1 * u1 * t1) * pt4[1] + t1 * t1 * t1 * pt2[1]) * 1e3) / 1e3;
        return [ptX, ptY];
      }
      var bezierSegmentPoints = createTypedArray("float32", 8);
      function getNewSegment(pt1, pt2, pt3, pt4, startPerc, endPerc, bezierData) {
        if (startPerc < 0) {
          startPerc = 0;
        } else if (startPerc > 1) {
          startPerc = 1;
        }
        var t0 = getDistancePerc(startPerc, bezierData);
        endPerc = endPerc > 1 ? 1 : endPerc;
        var t1 = getDistancePerc(endPerc, bezierData);
        var i;
        var len = pt1.length;
        var u0 = 1 - t0;
        var u1 = 1 - t1;
        var u0u0u0 = u0 * u0 * u0;
        var t0u0u0_3 = t0 * u0 * u0 * 3;
        var t0t0u0_3 = t0 * t0 * u0 * 3;
        var t0t0t0 = t0 * t0 * t0;
        var u0u0u1 = u0 * u0 * u1;
        var t0u0u1_3 = t0 * u0 * u1 + u0 * t0 * u1 + u0 * u0 * t1;
        var t0t0u1_3 = t0 * t0 * u1 + u0 * t0 * t1 + t0 * u0 * t1;
        var t0t0t1 = t0 * t0 * t1;
        var u0u1u1 = u0 * u1 * u1;
        var t0u1u1_3 = t0 * u1 * u1 + u0 * t1 * u1 + u0 * u1 * t1;
        var t0t1u1_3 = t0 * t1 * u1 + u0 * t1 * t1 + t0 * u1 * t1;
        var t0t1t1 = t0 * t1 * t1;
        var u1u1u1 = u1 * u1 * u1;
        var t1u1u1_3 = t1 * u1 * u1 + u1 * t1 * u1 + u1 * u1 * t1;
        var t1t1u1_3 = t1 * t1 * u1 + u1 * t1 * t1 + t1 * u1 * t1;
        var t1t1t1 = t1 * t1 * t1;
        for (i = 0; i < len; i += 1) {
          bezierSegmentPoints[i * 4] = math.round((u0u0u0 * pt1[i] + t0u0u0_3 * pt3[i] + t0t0u0_3 * pt4[i] + t0t0t0 * pt2[i]) * 1e3) / 1e3;
          bezierSegmentPoints[i * 4 + 1] = math.round((u0u0u1 * pt1[i] + t0u0u1_3 * pt3[i] + t0t0u1_3 * pt4[i] + t0t0t1 * pt2[i]) * 1e3) / 1e3;
          bezierSegmentPoints[i * 4 + 2] = math.round((u0u1u1 * pt1[i] + t0u1u1_3 * pt3[i] + t0t1u1_3 * pt4[i] + t0t1t1 * pt2[i]) * 1e3) / 1e3;
          bezierSegmentPoints[i * 4 + 3] = math.round((u1u1u1 * pt1[i] + t1u1u1_3 * pt3[i] + t1t1u1_3 * pt4[i] + t1t1t1 * pt2[i]) * 1e3) / 1e3;
        }
        return bezierSegmentPoints;
      }
      return {
        getSegmentsLength,
        getNewSegment,
        getPointInSegment,
        buildBezierData,
        pointOnLine2D,
        pointOnLine3D
      };
    }
    var bez = bezFunction();
    var initFrame = initialDefaultFrame;
    var mathAbs = Math.abs;
    function interpolateValue(frameNum, caching) {
      var offsetTime = this.offsetTime;
      var newValue;
      if (this.propType === "multidimensional") {
        newValue = createTypedArray("float32", this.pv.length);
      }
      var iterationIndex = caching.lastIndex;
      var i = iterationIndex;
      var len = this.keyframes.length - 1;
      var flag = true;
      var keyData;
      var nextKeyData;
      var keyframeMetadata;
      while (flag) {
        keyData = this.keyframes[i];
        nextKeyData = this.keyframes[i + 1];
        if (i === len - 1 && frameNum >= nextKeyData.t - offsetTime) {
          if (keyData.h) {
            keyData = nextKeyData;
          }
          iterationIndex = 0;
          break;
        }
        if (nextKeyData.t - offsetTime > frameNum) {
          iterationIndex = i;
          break;
        }
        if (i < len - 1) {
          i += 1;
        } else {
          iterationIndex = 0;
          flag = false;
        }
      }
      keyframeMetadata = this.keyframesMetadata[i] || {};
      var k;
      var kLen;
      var perc;
      var jLen;
      var j;
      var fnc;
      var nextKeyTime = nextKeyData.t - offsetTime;
      var keyTime = keyData.t - offsetTime;
      var endValue;
      if (keyData.to) {
        if (!keyframeMetadata.bezierData) {
          keyframeMetadata.bezierData = bez.buildBezierData(keyData.s, nextKeyData.s || keyData.e, keyData.to, keyData.ti);
        }
        var bezierData = keyframeMetadata.bezierData;
        if (frameNum >= nextKeyTime || frameNum < keyTime) {
          var ind = frameNum >= nextKeyTime ? bezierData.points.length - 1 : 0;
          kLen = bezierData.points[ind].point.length;
          for (k = 0; k < kLen; k += 1) {
            newValue[k] = bezierData.points[ind].point[k];
          }
        } else {
          if (keyframeMetadata.__fnct) {
            fnc = keyframeMetadata.__fnct;
          } else {
            fnc = BezierFactory.getBezierEasing(keyData.o.x, keyData.o.y, keyData.i.x, keyData.i.y, keyData.n).get;
            keyframeMetadata.__fnct = fnc;
          }
          perc = fnc((frameNum - keyTime) / (nextKeyTime - keyTime));
          var distanceInLine = bezierData.segmentLength * perc;
          var segmentPerc;
          var addedLength = caching.lastFrame < frameNum && caching._lastKeyframeIndex === i ? caching._lastAddedLength : 0;
          j = caching.lastFrame < frameNum && caching._lastKeyframeIndex === i ? caching._lastPoint : 0;
          flag = true;
          jLen = bezierData.points.length;
          while (flag) {
            addedLength += bezierData.points[j].partialLength;
            if (distanceInLine === 0 || perc === 0 || j === bezierData.points.length - 1) {
              kLen = bezierData.points[j].point.length;
              for (k = 0; k < kLen; k += 1) {
                newValue[k] = bezierData.points[j].point[k];
              }
              break;
            } else if (distanceInLine >= addedLength && distanceInLine < addedLength + bezierData.points[j + 1].partialLength) {
              segmentPerc = (distanceInLine - addedLength) / bezierData.points[j + 1].partialLength;
              kLen = bezierData.points[j].point.length;
              for (k = 0; k < kLen; k += 1) {
                newValue[k] = bezierData.points[j].point[k] + (bezierData.points[j + 1].point[k] - bezierData.points[j].point[k]) * segmentPerc;
              }
              break;
            }
            if (j < jLen - 1) {
              j += 1;
            } else {
              flag = false;
            }
          }
          caching._lastPoint = j;
          caching._lastAddedLength = addedLength - bezierData.points[j].partialLength;
          caching._lastKeyframeIndex = i;
        }
      } else {
        var outX;
        var outY;
        var inX;
        var inY;
        var keyValue;
        len = keyData.s.length;
        endValue = nextKeyData.s || keyData.e;
        if (this.sh && keyData.h !== 1) {
          if (frameNum >= nextKeyTime) {
            newValue[0] = endValue[0];
            newValue[1] = endValue[1];
            newValue[2] = endValue[2];
          } else if (frameNum <= keyTime) {
            newValue[0] = keyData.s[0];
            newValue[1] = keyData.s[1];
            newValue[2] = keyData.s[2];
          } else {
            var quatStart = createQuaternion(keyData.s);
            var quatEnd = createQuaternion(endValue);
            var time2 = (frameNum - keyTime) / (nextKeyTime - keyTime);
            quaternionToEuler(newValue, slerp(quatStart, quatEnd, time2));
          }
        } else {
          for (i = 0; i < len; i += 1) {
            if (keyData.h !== 1) {
              if (frameNum >= nextKeyTime) {
                perc = 1;
              } else if (frameNum < keyTime) {
                perc = 0;
              } else {
                if (keyData.o.x.constructor === Array) {
                  if (!keyframeMetadata.__fnct) {
                    keyframeMetadata.__fnct = [];
                  }
                  if (!keyframeMetadata.__fnct[i]) {
                    outX = keyData.o.x[i] === void 0 ? keyData.o.x[0] : keyData.o.x[i];
                    outY = keyData.o.y[i] === void 0 ? keyData.o.y[0] : keyData.o.y[i];
                    inX = keyData.i.x[i] === void 0 ? keyData.i.x[0] : keyData.i.x[i];
                    inY = keyData.i.y[i] === void 0 ? keyData.i.y[0] : keyData.i.y[i];
                    fnc = BezierFactory.getBezierEasing(outX, outY, inX, inY).get;
                    keyframeMetadata.__fnct[i] = fnc;
                  } else {
                    fnc = keyframeMetadata.__fnct[i];
                  }
                } else if (!keyframeMetadata.__fnct) {
                  outX = keyData.o.x;
                  outY = keyData.o.y;
                  inX = keyData.i.x;
                  inY = keyData.i.y;
                  fnc = BezierFactory.getBezierEasing(outX, outY, inX, inY).get;
                  keyData.keyframeMetadata = fnc;
                } else {
                  fnc = keyframeMetadata.__fnct;
                }
                perc = fnc((frameNum - keyTime) / (nextKeyTime - keyTime));
              }
            }
            endValue = nextKeyData.s || keyData.e;
            keyValue = keyData.h === 1 ? keyData.s[i] : keyData.s[i] + (endValue[i] - keyData.s[i]) * perc;
            if (this.propType === "multidimensional") {
              newValue[i] = keyValue;
            } else {
              newValue = keyValue;
            }
          }
        }
      }
      caching.lastIndex = iterationIndex;
      return newValue;
    }
    function slerp(a, b, t) {
      var out = [];
      var ax = a[0];
      var ay = a[1];
      var az = a[2];
      var aw = a[3];
      var bx = b[0];
      var by = b[1];
      var bz = b[2];
      var bw = b[3];
      var omega;
      var cosom;
      var sinom;
      var scale0;
      var scale1;
      cosom = ax * bx + ay * by + az * bz + aw * bw;
      if (cosom < 0) {
        cosom = -cosom;
        bx = -bx;
        by = -by;
        bz = -bz;
        bw = -bw;
      }
      if (1 - cosom > 1e-6) {
        omega = Math.acos(cosom);
        sinom = Math.sin(omega);
        scale0 = Math.sin((1 - t) * omega) / sinom;
        scale1 = Math.sin(t * omega) / sinom;
      } else {
        scale0 = 1 - t;
        scale1 = t;
      }
      out[0] = scale0 * ax + scale1 * bx;
      out[1] = scale0 * ay + scale1 * by;
      out[2] = scale0 * az + scale1 * bz;
      out[3] = scale0 * aw + scale1 * bw;
      return out;
    }
    function quaternionToEuler(out, quat) {
      var qx = quat[0];
      var qy = quat[1];
      var qz = quat[2];
      var qw = quat[3];
      var heading = Math.atan2(2 * qy * qw - 2 * qx * qz, 1 - 2 * qy * qy - 2 * qz * qz);
      var attitude = Math.asin(2 * qx * qy + 2 * qz * qw);
      var bank = Math.atan2(2 * qx * qw - 2 * qy * qz, 1 - 2 * qx * qx - 2 * qz * qz);
      out[0] = heading / degToRads;
      out[1] = attitude / degToRads;
      out[2] = bank / degToRads;
    }
    function createQuaternion(values) {
      var heading = values[0] * degToRads;
      var attitude = values[1] * degToRads;
      var bank = values[2] * degToRads;
      var c1 = Math.cos(heading / 2);
      var c2 = Math.cos(attitude / 2);
      var c3 = Math.cos(bank / 2);
      var s1 = Math.sin(heading / 2);
      var s2 = Math.sin(attitude / 2);
      var s3 = Math.sin(bank / 2);
      var w2 = c1 * c2 * c3 - s1 * s2 * s3;
      var x = s1 * s2 * c3 + c1 * c2 * s3;
      var y = s1 * c2 * c3 + c1 * s2 * s3;
      var z = c1 * s2 * c3 - s1 * c2 * s3;
      return [x, y, z, w2];
    }
    function getValueAtCurrentTime() {
      var frameNum = this.comp.renderedFrame - this.offsetTime;
      var initTime = this.keyframes[0].t - this.offsetTime;
      var endTime = this.keyframes[this.keyframes.length - 1].t - this.offsetTime;
      if (!(frameNum === this._caching.lastFrame || this._caching.lastFrame !== initFrame && (this._caching.lastFrame >= endTime && frameNum >= endTime || this._caching.lastFrame < initTime && frameNum < initTime))) {
        if (this._caching.lastFrame >= frameNum) {
          this._caching._lastKeyframeIndex = -1;
          this._caching.lastIndex = 0;
        }
        var renderResult = this.interpolateValue(frameNum, this._caching);
        this.pv = renderResult;
      }
      this._caching.lastFrame = frameNum;
      return this.pv;
    }
    function setVValue(val2) {
      var multipliedValue;
      if (this.propType === "unidimensional") {
        multipliedValue = val2 * this.mult;
        if (mathAbs(this.v - multipliedValue) > 1e-5) {
          this.v = multipliedValue;
          this._mdf = true;
        }
      } else {
        var i = 0;
        var len = this.v.length;
        while (i < len) {
          multipliedValue = val2[i] * this.mult;
          if (mathAbs(this.v[i] - multipliedValue) > 1e-5) {
            this.v[i] = multipliedValue;
            this._mdf = true;
          }
          i += 1;
        }
      }
    }
    function processEffectsSequence() {
      if (this.elem.globalData.frameId === this.frameId || !this.effectsSequence.length) {
        return;
      }
      if (this.lock) {
        this.setVValue(this.pv);
        return;
      }
      this.lock = true;
      this._mdf = this._isFirstFrame;
      var i;
      var len = this.effectsSequence.length;
      var finalValue = this.kf ? this.pv : this.data.k;
      for (i = 0; i < len; i += 1) {
        finalValue = this.effectsSequence[i](finalValue);
      }
      this.setVValue(finalValue);
      this._isFirstFrame = false;
      this.lock = false;
      this.frameId = this.elem.globalData.frameId;
    }
    function addEffect(effectFunction) {
      this.effectsSequence.push(effectFunction);
      this.container.addDynamicProperty(this);
    }
    function ValueProperty(elem2, data2, mult, container) {
      this.propType = "unidimensional";
      this.mult = mult || 1;
      this.data = data2;
      this.v = mult ? data2.k * mult : data2.k;
      this.pv = data2.k;
      this._mdf = false;
      this.elem = elem2;
      this.container = container;
      this.comp = elem2.comp;
      this.k = false;
      this.kf = false;
      this.vel = 0;
      this.effectsSequence = [];
      this._isFirstFrame = true;
      this.getValue = processEffectsSequence;
      this.setVValue = setVValue;
      this.addEffect = addEffect;
    }
    function MultiDimensionalProperty(elem2, data2, mult, container) {
      this.propType = "multidimensional";
      this.mult = mult || 1;
      this.data = data2;
      this._mdf = false;
      this.elem = elem2;
      this.container = container;
      this.comp = elem2.comp;
      this.k = false;
      this.kf = false;
      this.frameId = -1;
      var i;
      var len = data2.k.length;
      this.v = createTypedArray("float32", len);
      this.pv = createTypedArray("float32", len);
      this.vel = createTypedArray("float32", len);
      for (i = 0; i < len; i += 1) {
        this.v[i] = data2.k[i] * this.mult;
        this.pv[i] = data2.k[i];
      }
      this._isFirstFrame = true;
      this.effectsSequence = [];
      this.getValue = processEffectsSequence;
      this.setVValue = setVValue;
      this.addEffect = addEffect;
    }
    function KeyframedValueProperty(elem2, data2, mult, container) {
      this.propType = "unidimensional";
      this.keyframes = data2.k;
      this.keyframesMetadata = [];
      this.offsetTime = elem2.data.st;
      this.frameId = -1;
      this._caching = {
        lastFrame: initFrame,
        lastIndex: 0,
        value: 0,
        _lastKeyframeIndex: -1
      };
      this.k = true;
      this.kf = true;
      this.data = data2;
      this.mult = mult || 1;
      this.elem = elem2;
      this.container = container;
      this.comp = elem2.comp;
      this.v = initFrame;
      this.pv = initFrame;
      this._isFirstFrame = true;
      this.getValue = processEffectsSequence;
      this.setVValue = setVValue;
      this.interpolateValue = interpolateValue;
      this.effectsSequence = [getValueAtCurrentTime.bind(this)];
      this.addEffect = addEffect;
    }
    function KeyframedMultidimensionalProperty(elem2, data2, mult, container) {
      this.propType = "multidimensional";
      var i;
      var len = data2.k.length;
      var s;
      var e;
      var to;
      var ti;
      for (i = 0; i < len - 1; i += 1) {
        if (data2.k[i].to && data2.k[i].s && data2.k[i + 1] && data2.k[i + 1].s) {
          s = data2.k[i].s;
          e = data2.k[i + 1].s;
          to = data2.k[i].to;
          ti = data2.k[i].ti;
          if (s.length === 2 && !(s[0] === e[0] && s[1] === e[1]) && bez.pointOnLine2D(s[0], s[1], e[0], e[1], s[0] + to[0], s[1] + to[1]) && bez.pointOnLine2D(s[0], s[1], e[0], e[1], e[0] + ti[0], e[1] + ti[1]) || s.length === 3 && !(s[0] === e[0] && s[1] === e[1] && s[2] === e[2]) && bez.pointOnLine3D(s[0], s[1], s[2], e[0], e[1], e[2], s[0] + to[0], s[1] + to[1], s[2] + to[2]) && bez.pointOnLine3D(s[0], s[1], s[2], e[0], e[1], e[2], e[0] + ti[0], e[1] + ti[1], e[2] + ti[2])) {
            data2.k[i].to = null;
            data2.k[i].ti = null;
          }
          if (s[0] === e[0] && s[1] === e[1] && to[0] === 0 && to[1] === 0 && ti[0] === 0 && ti[1] === 0) {
            if (s.length === 2 || s[2] === e[2] && to[2] === 0 && ti[2] === 0) {
              data2.k[i].to = null;
              data2.k[i].ti = null;
            }
          }
        }
      }
      this.effectsSequence = [getValueAtCurrentTime.bind(this)];
      this.data = data2;
      this.keyframes = data2.k;
      this.keyframesMetadata = [];
      this.offsetTime = elem2.data.st;
      this.k = true;
      this.kf = true;
      this._isFirstFrame = true;
      this.mult = mult || 1;
      this.elem = elem2;
      this.container = container;
      this.comp = elem2.comp;
      this.getValue = processEffectsSequence;
      this.setVValue = setVValue;
      this.interpolateValue = interpolateValue;
      this.frameId = -1;
      var arrLen = data2.k[0].s.length;
      this.v = createTypedArray("float32", arrLen);
      this.pv = createTypedArray("float32", arrLen);
      for (i = 0; i < arrLen; i += 1) {
        this.v[i] = initFrame;
        this.pv[i] = initFrame;
      }
      this._caching = {
        lastFrame: initFrame,
        lastIndex: 0,
        value: createTypedArray("float32", arrLen)
      };
      this.addEffect = addEffect;
    }
    var PropertyFactory = /* @__PURE__ */ function() {
      function getProp(elem2, data2, type, mult, container) {
        if (data2.sid) {
          data2 = elem2.globalData.slotManager.getProp(data2);
        }
        var p;
        if (!data2.k.length) {
          p = new ValueProperty(elem2, data2, mult, container);
        } else if (typeof data2.k[0] === "number") {
          p = new MultiDimensionalProperty(elem2, data2, mult, container);
        } else {
          switch (type) {
            case 0:
              p = new KeyframedValueProperty(elem2, data2, mult, container);
              break;
            case 1:
              p = new KeyframedMultidimensionalProperty(elem2, data2, mult, container);
              break;
          }
        }
        if (p.effectsSequence.length) {
          container.addDynamicProperty(p);
        }
        return p;
      }
      var ob2 = {
        getProp
      };
      return ob2;
    }();
    function DynamicPropertyContainer() {
    }
    DynamicPropertyContainer.prototype = {
      addDynamicProperty: function addDynamicProperty(prop) {
        if (this.dynamicProperties.indexOf(prop) === -1) {
          this.dynamicProperties.push(prop);
          this.container.addDynamicProperty(this);
          this._isAnimated = true;
        }
      },
      iterateDynamicProperties: function iterateDynamicProperties() {
        this._mdf = false;
        var i;
        var len = this.dynamicProperties.length;
        for (i = 0; i < len; i += 1) {
          this.dynamicProperties[i].getValue();
          if (this.dynamicProperties[i]._mdf) {
            this._mdf = true;
          }
        }
      },
      initDynamicPropertyContainer: function initDynamicPropertyContainer(container) {
        this.container = container;
        this.dynamicProperties = [];
        this._mdf = false;
        this._isAnimated = false;
      }
    };
    var pointPool = function() {
      function create() {
        return createTypedArray("float32", 2);
      }
      return poolFactory(8, create);
    }();
    function ShapePath() {
      this.c = false;
      this._length = 0;
      this._maxLength = 8;
      this.v = createSizedArray(this._maxLength);
      this.o = createSizedArray(this._maxLength);
      this.i = createSizedArray(this._maxLength);
    }
    ShapePath.prototype.setPathData = function(closed, len) {
      this.c = closed;
      this.setLength(len);
      var i = 0;
      while (i < len) {
        this.v[i] = pointPool.newElement();
        this.o[i] = pointPool.newElement();
        this.i[i] = pointPool.newElement();
        i += 1;
      }
    };
    ShapePath.prototype.setLength = function(len) {
      while (this._maxLength < len) {
        this.doubleArrayLength();
      }
      this._length = len;
    };
    ShapePath.prototype.doubleArrayLength = function() {
      this.v = this.v.concat(createSizedArray(this._maxLength));
      this.i = this.i.concat(createSizedArray(this._maxLength));
      this.o = this.o.concat(createSizedArray(this._maxLength));
      this._maxLength *= 2;
    };
    ShapePath.prototype.setXYAt = function(x, y, type, pos, replace) {
      var arr;
      this._length = Math.max(this._length, pos + 1);
      if (this._length >= this._maxLength) {
        this.doubleArrayLength();
      }
      switch (type) {
        case "v":
          arr = this.v;
          break;
        case "i":
          arr = this.i;
          break;
        case "o":
          arr = this.o;
          break;
        default:
          arr = [];
          break;
      }
      if (!arr[pos] || arr[pos] && !replace) {
        arr[pos] = pointPool.newElement();
      }
      arr[pos][0] = x;
      arr[pos][1] = y;
    };
    ShapePath.prototype.setTripleAt = function(vX, vY, oX, oY, iX, iY, pos, replace) {
      this.setXYAt(vX, vY, "v", pos, replace);
      this.setXYAt(oX, oY, "o", pos, replace);
      this.setXYAt(iX, iY, "i", pos, replace);
    };
    ShapePath.prototype.reverse = function() {
      var newPath = new ShapePath();
      newPath.setPathData(this.c, this._length);
      var vertices = this.v;
      var outPoints = this.o;
      var inPoints = this.i;
      var init = 0;
      if (this.c) {
        newPath.setTripleAt(vertices[0][0], vertices[0][1], inPoints[0][0], inPoints[0][1], outPoints[0][0], outPoints[0][1], 0, false);
        init = 1;
      }
      var cnt = this._length - 1;
      var len = this._length;
      var i;
      for (i = init; i < len; i += 1) {
        newPath.setTripleAt(vertices[cnt][0], vertices[cnt][1], inPoints[cnt][0], inPoints[cnt][1], outPoints[cnt][0], outPoints[cnt][1], i, false);
        cnt -= 1;
      }
      return newPath;
    };
    ShapePath.prototype.length = function() {
      return this._length;
    };
    var shapePool = function() {
      function create() {
        return new ShapePath();
      }
      function release(shapePath) {
        var len = shapePath._length;
        var i;
        for (i = 0; i < len; i += 1) {
          pointPool.release(shapePath.v[i]);
          pointPool.release(shapePath.i[i]);
          pointPool.release(shapePath.o[i]);
          shapePath.v[i] = null;
          shapePath.i[i] = null;
          shapePath.o[i] = null;
        }
        shapePath._length = 0;
        shapePath.c = false;
      }
      function clone(shape) {
        var cloned = factory.newElement();
        var i;
        var len = shape._length === void 0 ? shape.v.length : shape._length;
        cloned.setLength(len);
        cloned.c = shape.c;
        for (i = 0; i < len; i += 1) {
          cloned.setTripleAt(shape.v[i][0], shape.v[i][1], shape.o[i][0], shape.o[i][1], shape.i[i][0], shape.i[i][1], i);
        }
        return cloned;
      }
      var factory = poolFactory(4, create, release);
      factory.clone = clone;
      return factory;
    }();
    function ShapeCollection() {
      this._length = 0;
      this._maxLength = 4;
      this.shapes = createSizedArray(this._maxLength);
    }
    ShapeCollection.prototype.addShape = function(shapeData) {
      if (this._length === this._maxLength) {
        this.shapes = this.shapes.concat(createSizedArray(this._maxLength));
        this._maxLength *= 2;
      }
      this.shapes[this._length] = shapeData;
      this._length += 1;
    };
    ShapeCollection.prototype.releaseShapes = function() {
      var i;
      for (i = 0; i < this._length; i += 1) {
        shapePool.release(this.shapes[i]);
      }
      this._length = 0;
    };
    var shapeCollectionPool = function() {
      var ob2 = {
        newShapeCollection,
        release
      };
      var _length = 0;
      var _maxLength = 4;
      var pool = createSizedArray(_maxLength);
      function newShapeCollection() {
        var shapeCollection;
        if (_length) {
          _length -= 1;
          shapeCollection = pool[_length];
        } else {
          shapeCollection = new ShapeCollection();
        }
        return shapeCollection;
      }
      function release(shapeCollection) {
        var i;
        var len = shapeCollection._length;
        for (i = 0; i < len; i += 1) {
          shapePool.release(shapeCollection.shapes[i]);
        }
        shapeCollection._length = 0;
        if (_length === _maxLength) {
          pool = pooling["double"](pool);
          _maxLength *= 2;
        }
        pool[_length] = shapeCollection;
        _length += 1;
      }
      return ob2;
    }();
    var ShapePropertyFactory = function() {
      var initFrame2 = -999999;
      function interpolateShape(frameNum, previousValue, caching) {
        var iterationIndex = caching.lastIndex;
        var keyPropS;
        var keyPropE;
        var isHold;
        var j;
        var k;
        var jLen;
        var kLen;
        var perc;
        var vertexValue;
        var kf = this.keyframes;
        if (frameNum < kf[0].t - this.offsetTime) {
          keyPropS = kf[0].s[0];
          isHold = true;
          iterationIndex = 0;
        } else if (frameNum >= kf[kf.length - 1].t - this.offsetTime) {
          keyPropS = kf[kf.length - 1].s ? kf[kf.length - 1].s[0] : kf[kf.length - 2].e[0];
          isHold = true;
        } else {
          var i = iterationIndex;
          var len = kf.length - 1;
          var flag = true;
          var keyData;
          var nextKeyData;
          var keyframeMetadata;
          while (flag) {
            keyData = kf[i];
            nextKeyData = kf[i + 1];
            if (nextKeyData.t - this.offsetTime > frameNum) {
              break;
            }
            if (i < len - 1) {
              i += 1;
            } else {
              flag = false;
            }
          }
          keyframeMetadata = this.keyframesMetadata[i] || {};
          isHold = keyData.h === 1;
          iterationIndex = i;
          if (!isHold) {
            if (frameNum >= nextKeyData.t - this.offsetTime) {
              perc = 1;
            } else if (frameNum < keyData.t - this.offsetTime) {
              perc = 0;
            } else {
              var fnc;
              if (keyframeMetadata.__fnct) {
                fnc = keyframeMetadata.__fnct;
              } else {
                fnc = BezierFactory.getBezierEasing(keyData.o.x, keyData.o.y, keyData.i.x, keyData.i.y).get;
                keyframeMetadata.__fnct = fnc;
              }
              perc = fnc((frameNum - (keyData.t - this.offsetTime)) / (nextKeyData.t - this.offsetTime - (keyData.t - this.offsetTime)));
            }
            keyPropE = nextKeyData.s ? nextKeyData.s[0] : keyData.e[0];
          }
          keyPropS = keyData.s[0];
        }
        jLen = previousValue._length;
        kLen = keyPropS.i[0].length;
        caching.lastIndex = iterationIndex;
        for (j = 0; j < jLen; j += 1) {
          for (k = 0; k < kLen; k += 1) {
            vertexValue = isHold ? keyPropS.i[j][k] : keyPropS.i[j][k] + (keyPropE.i[j][k] - keyPropS.i[j][k]) * perc;
            previousValue.i[j][k] = vertexValue;
            vertexValue = isHold ? keyPropS.o[j][k] : keyPropS.o[j][k] + (keyPropE.o[j][k] - keyPropS.o[j][k]) * perc;
            previousValue.o[j][k] = vertexValue;
            vertexValue = isHold ? keyPropS.v[j][k] : keyPropS.v[j][k] + (keyPropE.v[j][k] - keyPropS.v[j][k]) * perc;
            previousValue.v[j][k] = vertexValue;
          }
        }
      }
      function interpolateShapeCurrentTime() {
        var frameNum = this.comp.renderedFrame - this.offsetTime;
        var initTime = this.keyframes[0].t - this.offsetTime;
        var endTime = this.keyframes[this.keyframes.length - 1].t - this.offsetTime;
        var lastFrame = this._caching.lastFrame;
        if (!(lastFrame !== initFrame2 && (lastFrame < initTime && frameNum < initTime || lastFrame > endTime && frameNum > endTime))) {
          this._caching.lastIndex = lastFrame < frameNum ? this._caching.lastIndex : 0;
          this.interpolateShape(frameNum, this.pv, this._caching);
        }
        this._caching.lastFrame = frameNum;
        return this.pv;
      }
      function resetShape() {
        this.paths = this.localShapeCollection;
      }
      function shapesEqual(shape1, shape2) {
        if (shape1._length !== shape2._length || shape1.c !== shape2.c) {
          return false;
        }
        var i;
        var len = shape1._length;
        for (i = 0; i < len; i += 1) {
          if (shape1.v[i][0] !== shape2.v[i][0] || shape1.v[i][1] !== shape2.v[i][1] || shape1.o[i][0] !== shape2.o[i][0] || shape1.o[i][1] !== shape2.o[i][1] || shape1.i[i][0] !== shape2.i[i][0] || shape1.i[i][1] !== shape2.i[i][1]) {
            return false;
          }
        }
        return true;
      }
      function setVValue2(newPath) {
        if (!shapesEqual(this.v, newPath)) {
          this.v = shapePool.clone(newPath);
          this.localShapeCollection.releaseShapes();
          this.localShapeCollection.addShape(this.v);
          this._mdf = true;
          this.paths = this.localShapeCollection;
        }
      }
      function processEffectsSequence2() {
        if (this.elem.globalData.frameId === this.frameId) {
          return;
        }
        if (!this.effectsSequence.length) {
          this._mdf = false;
          return;
        }
        if (this.lock) {
          this.setVValue(this.pv);
          return;
        }
        this.lock = true;
        this._mdf = false;
        var finalValue;
        if (this.kf) {
          finalValue = this.pv;
        } else if (this.data.ks) {
          finalValue = this.data.ks.k;
        } else {
          finalValue = this.data.pt.k;
        }
        var i;
        var len = this.effectsSequence.length;
        for (i = 0; i < len; i += 1) {
          finalValue = this.effectsSequence[i](finalValue);
        }
        this.setVValue(finalValue);
        this.lock = false;
        this.frameId = this.elem.globalData.frameId;
      }
      function ShapeProperty(elem2, data2, type) {
        this.propType = "shape";
        this.comp = elem2.comp;
        this.container = elem2;
        this.elem = elem2;
        this.data = data2;
        this.k = false;
        this.kf = false;
        this._mdf = false;
        var pathData = type === 3 ? data2.pt.k : data2.ks.k;
        this.v = shapePool.clone(pathData);
        this.pv = shapePool.clone(this.v);
        this.localShapeCollection = shapeCollectionPool.newShapeCollection();
        this.paths = this.localShapeCollection;
        this.paths.addShape(this.v);
        this.reset = resetShape;
        this.effectsSequence = [];
      }
      function addEffect2(effectFunction) {
        this.effectsSequence.push(effectFunction);
        this.container.addDynamicProperty(this);
      }
      ShapeProperty.prototype.interpolateShape = interpolateShape;
      ShapeProperty.prototype.getValue = processEffectsSequence2;
      ShapeProperty.prototype.setVValue = setVValue2;
      ShapeProperty.prototype.addEffect = addEffect2;
      function KeyframedShapeProperty(elem2, data2, type) {
        this.propType = "shape";
        this.comp = elem2.comp;
        this.elem = elem2;
        this.container = elem2;
        this.offsetTime = elem2.data.st;
        this.keyframes = type === 3 ? data2.pt.k : data2.ks.k;
        this.keyframesMetadata = [];
        this.k = true;
        this.kf = true;
        var len = this.keyframes[0].s[0].i.length;
        this.v = shapePool.newElement();
        this.v.setPathData(this.keyframes[0].s[0].c, len);
        this.pv = shapePool.clone(this.v);
        this.localShapeCollection = shapeCollectionPool.newShapeCollection();
        this.paths = this.localShapeCollection;
        this.paths.addShape(this.v);
        this.lastFrame = initFrame2;
        this.reset = resetShape;
        this._caching = {
          lastFrame: initFrame2,
          lastIndex: 0
        };
        this.effectsSequence = [interpolateShapeCurrentTime.bind(this)];
      }
      KeyframedShapeProperty.prototype.getValue = processEffectsSequence2;
      KeyframedShapeProperty.prototype.interpolateShape = interpolateShape;
      KeyframedShapeProperty.prototype.setVValue = setVValue2;
      KeyframedShapeProperty.prototype.addEffect = addEffect2;
      var EllShapeProperty = function() {
        var cPoint = roundCorner;
        function EllShapePropertyFactory(elem2, data2) {
          this.v = shapePool.newElement();
          this.v.setPathData(true, 4);
          this.localShapeCollection = shapeCollectionPool.newShapeCollection();
          this.paths = this.localShapeCollection;
          this.localShapeCollection.addShape(this.v);
          this.d = data2.d;
          this.elem = elem2;
          this.comp = elem2.comp;
          this.frameId = -1;
          this.initDynamicPropertyContainer(elem2);
          this.p = PropertyFactory.getProp(elem2, data2.p, 1, 0, this);
          this.s = PropertyFactory.getProp(elem2, data2.s, 1, 0, this);
          if (this.dynamicProperties.length) {
            this.k = true;
          } else {
            this.k = false;
            this.convertEllToPath();
          }
        }
        EllShapePropertyFactory.prototype = {
          reset: resetShape,
          getValue: function getValue() {
            if (this.elem.globalData.frameId === this.frameId) {
              return;
            }
            this.frameId = this.elem.globalData.frameId;
            this.iterateDynamicProperties();
            if (this._mdf) {
              this.convertEllToPath();
            }
          },
          convertEllToPath: function convertEllToPath() {
            var p0 = this.p.v[0];
            var p1 = this.p.v[1];
            var s0 = this.s.v[0] / 2;
            var s1 = this.s.v[1] / 2;
            var _cw = this.d !== 3;
            var _v = this.v;
            _v.v[0][0] = p0;
            _v.v[0][1] = p1 - s1;
            _v.v[1][0] = _cw ? p0 + s0 : p0 - s0;
            _v.v[1][1] = p1;
            _v.v[2][0] = p0;
            _v.v[2][1] = p1 + s1;
            _v.v[3][0] = _cw ? p0 - s0 : p0 + s0;
            _v.v[3][1] = p1;
            _v.i[0][0] = _cw ? p0 - s0 * cPoint : p0 + s0 * cPoint;
            _v.i[0][1] = p1 - s1;
            _v.i[1][0] = _cw ? p0 + s0 : p0 - s0;
            _v.i[1][1] = p1 - s1 * cPoint;
            _v.i[2][0] = _cw ? p0 + s0 * cPoint : p0 - s0 * cPoint;
            _v.i[2][1] = p1 + s1;
            _v.i[3][0] = _cw ? p0 - s0 : p0 + s0;
            _v.i[3][1] = p1 + s1 * cPoint;
            _v.o[0][0] = _cw ? p0 + s0 * cPoint : p0 - s0 * cPoint;
            _v.o[0][1] = p1 - s1;
            _v.o[1][0] = _cw ? p0 + s0 : p0 - s0;
            _v.o[1][1] = p1 + s1 * cPoint;
            _v.o[2][0] = _cw ? p0 - s0 * cPoint : p0 + s0 * cPoint;
            _v.o[2][1] = p1 + s1;
            _v.o[3][0] = _cw ? p0 - s0 : p0 + s0;
            _v.o[3][1] = p1 - s1 * cPoint;
          }
        };
        extendPrototype([DynamicPropertyContainer], EllShapePropertyFactory);
        return EllShapePropertyFactory;
      }();
      var StarShapeProperty = function() {
        function StarShapePropertyFactory(elem2, data2) {
          this.v = shapePool.newElement();
          this.v.setPathData(true, 0);
          this.elem = elem2;
          this.comp = elem2.comp;
          this.data = data2;
          this.frameId = -1;
          this.d = data2.d;
          this.initDynamicPropertyContainer(elem2);
          if (data2.sy === 1) {
            this.ir = PropertyFactory.getProp(elem2, data2.ir, 0, 0, this);
            this.is = PropertyFactory.getProp(elem2, data2.is, 0, 0.01, this);
            this.convertToPath = this.convertStarToPath;
          } else {
            this.convertToPath = this.convertPolygonToPath;
          }
          this.pt = PropertyFactory.getProp(elem2, data2.pt, 0, 0, this);
          this.p = PropertyFactory.getProp(elem2, data2.p, 1, 0, this);
          this.r = PropertyFactory.getProp(elem2, data2.r, 0, degToRads, this);
          this.or = PropertyFactory.getProp(elem2, data2.or, 0, 0, this);
          this.os = PropertyFactory.getProp(elem2, data2.os, 0, 0.01, this);
          this.localShapeCollection = shapeCollectionPool.newShapeCollection();
          this.localShapeCollection.addShape(this.v);
          this.paths = this.localShapeCollection;
          if (this.dynamicProperties.length) {
            this.k = true;
          } else {
            this.k = false;
            this.convertToPath();
          }
        }
        StarShapePropertyFactory.prototype = {
          reset: resetShape,
          getValue: function getValue() {
            if (this.elem.globalData.frameId === this.frameId) {
              return;
            }
            this.frameId = this.elem.globalData.frameId;
            this.iterateDynamicProperties();
            if (this._mdf) {
              this.convertToPath();
            }
          },
          convertStarToPath: function convertStarToPath() {
            var numPts = Math.floor(this.pt.v) * 2;
            var angle = Math.PI * 2 / numPts;
            var longFlag = true;
            var longRad = this.or.v;
            var shortRad = this.ir.v;
            var longRound = this.os.v;
            var shortRound = this.is.v;
            var longPerimSegment = 2 * Math.PI * longRad / (numPts * 2);
            var shortPerimSegment = 2 * Math.PI * shortRad / (numPts * 2);
            var i;
            var rad;
            var roundness;
            var perimSegment;
            var currentAng = -Math.PI / 2;
            currentAng += this.r.v;
            var dir = this.data.d === 3 ? -1 : 1;
            this.v._length = 0;
            for (i = 0; i < numPts; i += 1) {
              rad = longFlag ? longRad : shortRad;
              roundness = longFlag ? longRound : shortRound;
              perimSegment = longFlag ? longPerimSegment : shortPerimSegment;
              var x = rad * Math.cos(currentAng);
              var y = rad * Math.sin(currentAng);
              var ox = x === 0 && y === 0 ? 0 : y / Math.sqrt(x * x + y * y);
              var oy = x === 0 && y === 0 ? 0 : -x / Math.sqrt(x * x + y * y);
              x += +this.p.v[0];
              y += +this.p.v[1];
              this.v.setTripleAt(x, y, x - ox * perimSegment * roundness * dir, y - oy * perimSegment * roundness * dir, x + ox * perimSegment * roundness * dir, y + oy * perimSegment * roundness * dir, i, true);
              longFlag = !longFlag;
              currentAng += angle * dir;
            }
          },
          convertPolygonToPath: function convertPolygonToPath() {
            var numPts = Math.floor(this.pt.v);
            var angle = Math.PI * 2 / numPts;
            var rad = this.or.v;
            var roundness = this.os.v;
            var perimSegment = 2 * Math.PI * rad / (numPts * 4);
            var i;
            var currentAng = -Math.PI * 0.5;
            var dir = this.data.d === 3 ? -1 : 1;
            currentAng += this.r.v;
            this.v._length = 0;
            for (i = 0; i < numPts; i += 1) {
              var x = rad * Math.cos(currentAng);
              var y = rad * Math.sin(currentAng);
              var ox = x === 0 && y === 0 ? 0 : y / Math.sqrt(x * x + y * y);
              var oy = x === 0 && y === 0 ? 0 : -x / Math.sqrt(x * x + y * y);
              x += +this.p.v[0];
              y += +this.p.v[1];
              this.v.setTripleAt(x, y, x - ox * perimSegment * roundness * dir, y - oy * perimSegment * roundness * dir, x + ox * perimSegment * roundness * dir, y + oy * perimSegment * roundness * dir, i, true);
              currentAng += angle * dir;
            }
            this.paths.length = 0;
            this.paths[0] = this.v;
          }
        };
        extendPrototype([DynamicPropertyContainer], StarShapePropertyFactory);
        return StarShapePropertyFactory;
      }();
      var RectShapeProperty = function() {
        function RectShapePropertyFactory(elem2, data2) {
          this.v = shapePool.newElement();
          this.v.c = true;
          this.localShapeCollection = shapeCollectionPool.newShapeCollection();
          this.localShapeCollection.addShape(this.v);
          this.paths = this.localShapeCollection;
          this.elem = elem2;
          this.comp = elem2.comp;
          this.frameId = -1;
          this.d = data2.d;
          this.initDynamicPropertyContainer(elem2);
          this.p = PropertyFactory.getProp(elem2, data2.p, 1, 0, this);
          this.s = PropertyFactory.getProp(elem2, data2.s, 1, 0, this);
          this.r = PropertyFactory.getProp(elem2, data2.r, 0, 0, this);
          if (this.dynamicProperties.length) {
            this.k = true;
          } else {
            this.k = false;
            this.convertRectToPath();
          }
        }
        RectShapePropertyFactory.prototype = {
          convertRectToPath: function convertRectToPath() {
            var p0 = this.p.v[0];
            var p1 = this.p.v[1];
            var v0 = this.s.v[0] / 2;
            var v1 = this.s.v[1] / 2;
            var round = bmMin(v0, v1, this.r.v);
            var cPoint = round * (1 - roundCorner);
            this.v._length = 0;
            if (this.d === 2 || this.d === 1) {
              this.v.setTripleAt(p0 + v0, p1 - v1 + round, p0 + v0, p1 - v1 + round, p0 + v0, p1 - v1 + cPoint, 0, true);
              this.v.setTripleAt(p0 + v0, p1 + v1 - round, p0 + v0, p1 + v1 - cPoint, p0 + v0, p1 + v1 - round, 1, true);
              if (round !== 0) {
                this.v.setTripleAt(p0 + v0 - round, p1 + v1, p0 + v0 - round, p1 + v1, p0 + v0 - cPoint, p1 + v1, 2, true);
                this.v.setTripleAt(p0 - v0 + round, p1 + v1, p0 - v0 + cPoint, p1 + v1, p0 - v0 + round, p1 + v1, 3, true);
                this.v.setTripleAt(p0 - v0, p1 + v1 - round, p0 - v0, p1 + v1 - round, p0 - v0, p1 + v1 - cPoint, 4, true);
                this.v.setTripleAt(p0 - v0, p1 - v1 + round, p0 - v0, p1 - v1 + cPoint, p0 - v0, p1 - v1 + round, 5, true);
                this.v.setTripleAt(p0 - v0 + round, p1 - v1, p0 - v0 + round, p1 - v1, p0 - v0 + cPoint, p1 - v1, 6, true);
                this.v.setTripleAt(p0 + v0 - round, p1 - v1, p0 + v0 - cPoint, p1 - v1, p0 + v0 - round, p1 - v1, 7, true);
              } else {
                this.v.setTripleAt(p0 - v0, p1 + v1, p0 - v0 + cPoint, p1 + v1, p0 - v0, p1 + v1, 2);
                this.v.setTripleAt(p0 - v0, p1 - v1, p0 - v0, p1 - v1 + cPoint, p0 - v0, p1 - v1, 3);
              }
            } else {
              this.v.setTripleAt(p0 + v0, p1 - v1 + round, p0 + v0, p1 - v1 + cPoint, p0 + v0, p1 - v1 + round, 0, true);
              if (round !== 0) {
                this.v.setTripleAt(p0 + v0 - round, p1 - v1, p0 + v0 - round, p1 - v1, p0 + v0 - cPoint, p1 - v1, 1, true);
                this.v.setTripleAt(p0 - v0 + round, p1 - v1, p0 - v0 + cPoint, p1 - v1, p0 - v0 + round, p1 - v1, 2, true);
                this.v.setTripleAt(p0 - v0, p1 - v1 + round, p0 - v0, p1 - v1 + round, p0 - v0, p1 - v1 + cPoint, 3, true);
                this.v.setTripleAt(p0 - v0, p1 + v1 - round, p0 - v0, p1 + v1 - cPoint, p0 - v0, p1 + v1 - round, 4, true);
                this.v.setTripleAt(p0 - v0 + round, p1 + v1, p0 - v0 + round, p1 + v1, p0 - v0 + cPoint, p1 + v1, 5, true);
                this.v.setTripleAt(p0 + v0 - round, p1 + v1, p0 + v0 - cPoint, p1 + v1, p0 + v0 - round, p1 + v1, 6, true);
                this.v.setTripleAt(p0 + v0, p1 + v1 - round, p0 + v0, p1 + v1 - round, p0 + v0, p1 + v1 - cPoint, 7, true);
              } else {
                this.v.setTripleAt(p0 - v0, p1 - v1, p0 - v0 + cPoint, p1 - v1, p0 - v0, p1 - v1, 1, true);
                this.v.setTripleAt(p0 - v0, p1 + v1, p0 - v0, p1 + v1 - cPoint, p0 - v0, p1 + v1, 2, true);
                this.v.setTripleAt(p0 + v0, p1 + v1, p0 + v0 - cPoint, p1 + v1, p0 + v0, p1 + v1, 3, true);
              }
            }
          },
          getValue: function getValue() {
            if (this.elem.globalData.frameId === this.frameId) {
              return;
            }
            this.frameId = this.elem.globalData.frameId;
            this.iterateDynamicProperties();
            if (this._mdf) {
              this.convertRectToPath();
            }
          },
          reset: resetShape
        };
        extendPrototype([DynamicPropertyContainer], RectShapePropertyFactory);
        return RectShapePropertyFactory;
      }();
      function getShapeProp(elem2, data2, type) {
        var prop;
        if (type === 3 || type === 4) {
          var dataProp = type === 3 ? data2.pt : data2.ks;
          var keys = dataProp.k;
          if (keys.length) {
            prop = new KeyframedShapeProperty(elem2, data2, type);
          } else {
            prop = new ShapeProperty(elem2, data2, type);
          }
        } else if (type === 5) {
          prop = new RectShapeProperty(elem2, data2);
        } else if (type === 6) {
          prop = new EllShapeProperty(elem2, data2);
        } else if (type === 7) {
          prop = new StarShapeProperty(elem2, data2);
        }
        if (prop.k) {
          elem2.addDynamicProperty(prop);
        }
        return prop;
      }
      function getConstructorFunction() {
        return ShapeProperty;
      }
      function getKeyframedConstructorFunction() {
        return KeyframedShapeProperty;
      }
      var ob2 = {};
      ob2.getShapeProp = getShapeProp;
      ob2.getConstructorFunction = getConstructorFunction;
      ob2.getKeyframedConstructorFunction = getKeyframedConstructorFunction;
      return ob2;
    }();
    /*!
     Transformation Matrix v2.0
     (c) Epistemex 2014-2015
     www.epistemex.com
     By Ken Fyrstenberg
     Contributions by leeoniya.
     License: MIT, header required.
     */
    var Matrix = /* @__PURE__ */ function() {
      var _cos = Math.cos;
      var _sin = Math.sin;
      var _tan = Math.tan;
      var _rnd = Math.round;
      function reset() {
        this.props[0] = 1;
        this.props[1] = 0;
        this.props[2] = 0;
        this.props[3] = 0;
        this.props[4] = 0;
        this.props[5] = 1;
        this.props[6] = 0;
        this.props[7] = 0;
        this.props[8] = 0;
        this.props[9] = 0;
        this.props[10] = 1;
        this.props[11] = 0;
        this.props[12] = 0;
        this.props[13] = 0;
        this.props[14] = 0;
        this.props[15] = 1;
        return this;
      }
      function rotate(angle) {
        if (angle === 0) {
          return this;
        }
        var mCos = _cos(angle);
        var mSin = _sin(angle);
        return this._t(mCos, -mSin, 0, 0, mSin, mCos, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
      }
      function rotateX(angle) {
        if (angle === 0) {
          return this;
        }
        var mCos = _cos(angle);
        var mSin = _sin(angle);
        return this._t(1, 0, 0, 0, 0, mCos, -mSin, 0, 0, mSin, mCos, 0, 0, 0, 0, 1);
      }
      function rotateY(angle) {
        if (angle === 0) {
          return this;
        }
        var mCos = _cos(angle);
        var mSin = _sin(angle);
        return this._t(mCos, 0, mSin, 0, 0, 1, 0, 0, -mSin, 0, mCos, 0, 0, 0, 0, 1);
      }
      function rotateZ(angle) {
        if (angle === 0) {
          return this;
        }
        var mCos = _cos(angle);
        var mSin = _sin(angle);
        return this._t(mCos, -mSin, 0, 0, mSin, mCos, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
      }
      function shear(sx, sy) {
        return this._t(1, sy, sx, 1, 0, 0);
      }
      function skew(ax, ay) {
        return this.shear(_tan(ax), _tan(ay));
      }
      function skewFromAxis(ax, angle) {
        var mCos = _cos(angle);
        var mSin = _sin(angle);
        return this._t(mCos, mSin, 0, 0, -mSin, mCos, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)._t(1, 0, 0, 0, _tan(ax), 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)._t(mCos, -mSin, 0, 0, mSin, mCos, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1);
      }
      function scale2(sx, sy, sz) {
        if (!sz && sz !== 0) {
          sz = 1;
        }
        if (sx === 1 && sy === 1 && sz === 1) {
          return this;
        }
        return this._t(sx, 0, 0, 0, 0, sy, 0, 0, 0, 0, sz, 0, 0, 0, 0, 1);
      }
      function setTransform(a, b, c, d, e, f, g, h2, i, j, k, l, m, n, o, p) {
        this.props[0] = a;
        this.props[1] = b;
        this.props[2] = c;
        this.props[3] = d;
        this.props[4] = e;
        this.props[5] = f;
        this.props[6] = g;
        this.props[7] = h2;
        this.props[8] = i;
        this.props[9] = j;
        this.props[10] = k;
        this.props[11] = l;
        this.props[12] = m;
        this.props[13] = n;
        this.props[14] = o;
        this.props[15] = p;
        return this;
      }
      function translate(tx, ty, tz) {
        tz = tz || 0;
        if (tx !== 0 || ty !== 0 || tz !== 0) {
          return this._t(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, tx, ty, tz, 1);
        }
        return this;
      }
      function transform2(a2, b2, c2, d2, e2, f2, g2, h2, i2, j2, k2, l2, m2, n2, o2, p2) {
        var _p = this.props;
        if (a2 === 1 && b2 === 0 && c2 === 0 && d2 === 0 && e2 === 0 && f2 === 1 && g2 === 0 && h2 === 0 && i2 === 0 && j2 === 0 && k2 === 1 && l2 === 0) {
          _p[12] = _p[12] * a2 + _p[15] * m2;
          _p[13] = _p[13] * f2 + _p[15] * n2;
          _p[14] = _p[14] * k2 + _p[15] * o2;
          _p[15] *= p2;
          this._identityCalculated = false;
          return this;
        }
        var a1 = _p[0];
        var b1 = _p[1];
        var c1 = _p[2];
        var d1 = _p[3];
        var e1 = _p[4];
        var f1 = _p[5];
        var g1 = _p[6];
        var h1 = _p[7];
        var i1 = _p[8];
        var j1 = _p[9];
        var k1 = _p[10];
        var l1 = _p[11];
        var m1 = _p[12];
        var n1 = _p[13];
        var o1 = _p[14];
        var p1 = _p[15];
        _p[0] = a1 * a2 + b1 * e2 + c1 * i2 + d1 * m2;
        _p[1] = a1 * b2 + b1 * f2 + c1 * j2 + d1 * n2;
        _p[2] = a1 * c2 + b1 * g2 + c1 * k2 + d1 * o2;
        _p[3] = a1 * d2 + b1 * h2 + c1 * l2 + d1 * p2;
        _p[4] = e1 * a2 + f1 * e2 + g1 * i2 + h1 * m2;
        _p[5] = e1 * b2 + f1 * f2 + g1 * j2 + h1 * n2;
        _p[6] = e1 * c2 + f1 * g2 + g1 * k2 + h1 * o2;
        _p[7] = e1 * d2 + f1 * h2 + g1 * l2 + h1 * p2;
        _p[8] = i1 * a2 + j1 * e2 + k1 * i2 + l1 * m2;
        _p[9] = i1 * b2 + j1 * f2 + k1 * j2 + l1 * n2;
        _p[10] = i1 * c2 + j1 * g2 + k1 * k2 + l1 * o2;
        _p[11] = i1 * d2 + j1 * h2 + k1 * l2 + l1 * p2;
        _p[12] = m1 * a2 + n1 * e2 + o1 * i2 + p1 * m2;
        _p[13] = m1 * b2 + n1 * f2 + o1 * j2 + p1 * n2;
        _p[14] = m1 * c2 + n1 * g2 + o1 * k2 + p1 * o2;
        _p[15] = m1 * d2 + n1 * h2 + o1 * l2 + p1 * p2;
        this._identityCalculated = false;
        return this;
      }
      function multiply(matrix) {
        var matrixProps = matrix.props;
        return this.transform(matrixProps[0], matrixProps[1], matrixProps[2], matrixProps[3], matrixProps[4], matrixProps[5], matrixProps[6], matrixProps[7], matrixProps[8], matrixProps[9], matrixProps[10], matrixProps[11], matrixProps[12], matrixProps[13], matrixProps[14], matrixProps[15]);
      }
      function isIdentity() {
        if (!this._identityCalculated) {
          this._identity = !(this.props[0] !== 1 || this.props[1] !== 0 || this.props[2] !== 0 || this.props[3] !== 0 || this.props[4] !== 0 || this.props[5] !== 1 || this.props[6] !== 0 || this.props[7] !== 0 || this.props[8] !== 0 || this.props[9] !== 0 || this.props[10] !== 1 || this.props[11] !== 0 || this.props[12] !== 0 || this.props[13] !== 0 || this.props[14] !== 0 || this.props[15] !== 1);
          this._identityCalculated = true;
        }
        return this._identity;
      }
      function equals(matr) {
        var i = 0;
        while (i < 16) {
          if (matr.props[i] !== this.props[i]) {
            return false;
          }
          i += 1;
        }
        return true;
      }
      function clone(matr) {
        var i;
        for (i = 0; i < 16; i += 1) {
          matr.props[i] = this.props[i];
        }
        return matr;
      }
      function cloneFromProps(props) {
        var i;
        for (i = 0; i < 16; i += 1) {
          this.props[i] = props[i];
        }
      }
      function applyToPoint(x, y, z) {
        return {
          x: x * this.props[0] + y * this.props[4] + z * this.props[8] + this.props[12],
          y: x * this.props[1] + y * this.props[5] + z * this.props[9] + this.props[13],
          z: x * this.props[2] + y * this.props[6] + z * this.props[10] + this.props[14]
        };
      }
      function applyToX(x, y, z) {
        return x * this.props[0] + y * this.props[4] + z * this.props[8] + this.props[12];
      }
      function applyToY(x, y, z) {
        return x * this.props[1] + y * this.props[5] + z * this.props[9] + this.props[13];
      }
      function applyToZ(x, y, z) {
        return x * this.props[2] + y * this.props[6] + z * this.props[10] + this.props[14];
      }
      function getInverseMatrix() {
        var determinant = this.props[0] * this.props[5] - this.props[1] * this.props[4];
        var a = this.props[5] / determinant;
        var b = -this.props[1] / determinant;
        var c = -this.props[4] / determinant;
        var d = this.props[0] / determinant;
        var e = (this.props[4] * this.props[13] - this.props[5] * this.props[12]) / determinant;
        var f = -(this.props[0] * this.props[13] - this.props[1] * this.props[12]) / determinant;
        var inverseMatrix = new Matrix();
        inverseMatrix.props[0] = a;
        inverseMatrix.props[1] = b;
        inverseMatrix.props[4] = c;
        inverseMatrix.props[5] = d;
        inverseMatrix.props[12] = e;
        inverseMatrix.props[13] = f;
        return inverseMatrix;
      }
      function inversePoint(pt) {
        var inverseMatrix = this.getInverseMatrix();
        return inverseMatrix.applyToPointArray(pt[0], pt[1], pt[2] || 0);
      }
      function inversePoints(pts) {
        var i;
        var len = pts.length;
        var retPts = [];
        for (i = 0; i < len; i += 1) {
          retPts[i] = inversePoint(pts[i]);
        }
        return retPts;
      }
      function applyToTriplePoints(pt1, pt2, pt3) {
        var arr = createTypedArray("float32", 6);
        if (this.isIdentity()) {
          arr[0] = pt1[0];
          arr[1] = pt1[1];
          arr[2] = pt2[0];
          arr[3] = pt2[1];
          arr[4] = pt3[0];
          arr[5] = pt3[1];
        } else {
          var p0 = this.props[0];
          var p1 = this.props[1];
          var p4 = this.props[4];
          var p5 = this.props[5];
          var p12 = this.props[12];
          var p13 = this.props[13];
          arr[0] = pt1[0] * p0 + pt1[1] * p4 + p12;
          arr[1] = pt1[0] * p1 + pt1[1] * p5 + p13;
          arr[2] = pt2[0] * p0 + pt2[1] * p4 + p12;
          arr[3] = pt2[0] * p1 + pt2[1] * p5 + p13;
          arr[4] = pt3[0] * p0 + pt3[1] * p4 + p12;
          arr[5] = pt3[0] * p1 + pt3[1] * p5 + p13;
        }
        return arr;
      }
      function applyToPointArray(x, y, z) {
        var arr;
        if (this.isIdentity()) {
          arr = [x, y, z];
        } else {
          arr = [x * this.props[0] + y * this.props[4] + z * this.props[8] + this.props[12], x * this.props[1] + y * this.props[5] + z * this.props[9] + this.props[13], x * this.props[2] + y * this.props[6] + z * this.props[10] + this.props[14]];
        }
        return arr;
      }
      function applyToPointStringified(x, y) {
        if (this.isIdentity()) {
          return x + "," + y;
        }
        var _p = this.props;
        return Math.round((x * _p[0] + y * _p[4] + _p[12]) * 100) / 100 + "," + Math.round((x * _p[1] + y * _p[5] + _p[13]) * 100) / 100;
      }
      function toCSS() {
        var i = 0;
        var props = this.props;
        var cssValue = "matrix3d(";
        var v2 = 1e4;
        while (i < 16) {
          cssValue += _rnd(props[i] * v2) / v2;
          cssValue += i === 15 ? ")" : ",";
          i += 1;
        }
        return cssValue;
      }
      function roundMatrixProperty(val2) {
        var v2 = 1e4;
        if (val2 < 1e-6 && val2 > 0 || val2 > -1e-6 && val2 < 0) {
          return _rnd(val2 * v2) / v2;
        }
        return val2;
      }
      function to2dCSS() {
        var props = this.props;
        var _a = roundMatrixProperty(props[0]);
        var _b = roundMatrixProperty(props[1]);
        var _c = roundMatrixProperty(props[4]);
        var _d = roundMatrixProperty(props[5]);
        var _e = roundMatrixProperty(props[12]);
        var _f = roundMatrixProperty(props[13]);
        return "matrix(" + _a + "," + _b + "," + _c + "," + _d + "," + _e + "," + _f + ")";
      }
      return function() {
        this.reset = reset;
        this.rotate = rotate;
        this.rotateX = rotateX;
        this.rotateY = rotateY;
        this.rotateZ = rotateZ;
        this.skew = skew;
        this.skewFromAxis = skewFromAxis;
        this.shear = shear;
        this.scale = scale2;
        this.setTransform = setTransform;
        this.translate = translate;
        this.transform = transform2;
        this.multiply = multiply;
        this.applyToPoint = applyToPoint;
        this.applyToX = applyToX;
        this.applyToY = applyToY;
        this.applyToZ = applyToZ;
        this.applyToPointArray = applyToPointArray;
        this.applyToTriplePoints = applyToTriplePoints;
        this.applyToPointStringified = applyToPointStringified;
        this.toCSS = toCSS;
        this.to2dCSS = to2dCSS;
        this.clone = clone;
        this.cloneFromProps = cloneFromProps;
        this.equals = equals;
        this.inversePoints = inversePoints;
        this.inversePoint = inversePoint;
        this.getInverseMatrix = getInverseMatrix;
        this._t = this.transform;
        this.isIdentity = isIdentity;
        this._identity = true;
        this._identityCalculated = false;
        this.props = createTypedArray("float32", 16);
        this.reset();
      };
    }();
    function _typeof$3(obj) {
      "@babel/helpers - typeof";
      if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
        _typeof$3 = function _typeof2(obj2) {
          return typeof obj2;
        };
      } else {
        _typeof$3 = function _typeof2(obj2) {
          return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
        };
      }
      return _typeof$3(obj);
    }
    var lottie = {};
    function setLocation(href) {
      setLocationHref(href);
    }
    function searchAnimations() {
      {
        animationManager.searchAnimations();
      }
    }
    function setSubframeRendering(flag) {
      setSubframeEnabled(flag);
    }
    function setPrefix(prefix) {
      setIdPrefix(prefix);
    }
    function loadAnimation(params) {
      return animationManager.loadAnimation(params);
    }
    function setQuality(value2) {
      if (typeof value2 === "string") {
        switch (value2) {
          case "high":
            setDefaultCurveSegments(200);
            break;
          default:
          case "medium":
            setDefaultCurveSegments(50);
            break;
          case "low":
            setDefaultCurveSegments(10);
            break;
        }
      } else if (!isNaN(value2) && value2 > 1) {
        setDefaultCurveSegments(value2);
      }
    }
    function inBrowser() {
      return typeof navigator !== "undefined";
    }
    function installPlugin(type, plugin) {
      if (type === "expressions") {
        setExpressionsPlugin(plugin);
      }
    }
    function getFactory(name2) {
      switch (name2) {
        case "propertyFactory":
          return PropertyFactory;
        case "shapePropertyFactory":
          return ShapePropertyFactory;
        case "matrix":
          return Matrix;
        default:
          return null;
      }
    }
    lottie.play = animationManager.play;
    lottie.pause = animationManager.pause;
    lottie.setLocationHref = setLocation;
    lottie.togglePause = animationManager.togglePause;
    lottie.setSpeed = animationManager.setSpeed;
    lottie.setDirection = animationManager.setDirection;
    lottie.stop = animationManager.stop;
    lottie.searchAnimations = searchAnimations;
    lottie.registerAnimation = animationManager.registerAnimation;
    lottie.loadAnimation = loadAnimation;
    lottie.setSubframeRendering = setSubframeRendering;
    lottie.resize = animationManager.resize;
    lottie.goToAndStop = animationManager.goToAndStop;
    lottie.destroy = animationManager.destroy;
    lottie.setQuality = setQuality;
    lottie.inBrowser = inBrowser;
    lottie.installPlugin = installPlugin;
    lottie.freeze = animationManager.freeze;
    lottie.unfreeze = animationManager.unfreeze;
    lottie.setVolume = animationManager.setVolume;
    lottie.mute = animationManager.mute;
    lottie.unmute = animationManager.unmute;
    lottie.getRegisteredAnimations = animationManager.getRegisteredAnimations;
    lottie.useWebWorker = setWebWorker;
    lottie.setIDPrefix = setPrefix;
    lottie.__getFactory = getFactory;
    lottie.version = "5.12.2";
    function checkReady() {
      if (document.readyState === "complete") {
        clearInterval(readyStateCheckInterval);
        searchAnimations();
      }
    }
    function getQueryVariable(variable) {
      var vars2 = queryString.split("&");
      for (var i = 0; i < vars2.length; i += 1) {
        var pair = vars2[i].split("=");
        if (decodeURIComponent(pair[0]) == variable) {
          return decodeURIComponent(pair[1]);
        }
      }
      return null;
    }
    var queryString = "";
    {
      var scripts = document.getElementsByTagName("script");
      var index = scripts.length - 1;
      var myScript = scripts[index] || {
        src: ""
      };
      queryString = myScript.src ? myScript.src.replace(/^[^\?]+\??/, "") : "";
      getQueryVariable("renderer");
    }
    var readyStateCheckInterval = setInterval(checkReady, 100);
    try {
      if (!((false ? "undefined" : _typeof$3(exports)) === "object" && true) && true) {
        window.bodymovin = lottie;
      }
    } catch (err) {
    }
    var ShapeModifiers = function() {
      var ob2 = {};
      var modifiers = {};
      ob2.registerModifier = registerModifier;
      ob2.getModifier = getModifier;
      function registerModifier(nm, factory) {
        if (!modifiers[nm]) {
          modifiers[nm] = factory;
        }
      }
      function getModifier(nm, elem2, data2) {
        return new modifiers[nm](elem2, data2);
      }
      return ob2;
    }();
    function ShapeModifier() {
    }
    ShapeModifier.prototype.initModifierProperties = function() {
    };
    ShapeModifier.prototype.addShapeToModifier = function() {
    };
    ShapeModifier.prototype.addShape = function(data2) {
      if (!this.closed) {
        data2.sh.container.addDynamicProperty(data2.sh);
        var shapeData = {
          shape: data2.sh,
          data: data2,
          localShapeCollection: shapeCollectionPool.newShapeCollection()
        };
        this.shapes.push(shapeData);
        this.addShapeToModifier(shapeData);
        if (this._isAnimated) {
          data2.setAsAnimated();
        }
      }
    };
    ShapeModifier.prototype.init = function(elem2, data2) {
      this.shapes = [];
      this.elem = elem2;
      this.initDynamicPropertyContainer(elem2);
      this.initModifierProperties(elem2, data2);
      this.frameId = initialDefaultFrame;
      this.closed = false;
      this.k = false;
      if (this.dynamicProperties.length) {
        this.k = true;
      } else {
        this.getValue(true);
      }
    };
    ShapeModifier.prototype.processKeys = function() {
      if (this.elem.globalData.frameId === this.frameId) {
        return;
      }
      this.frameId = this.elem.globalData.frameId;
      this.iterateDynamicProperties();
    };
    extendPrototype([DynamicPropertyContainer], ShapeModifier);
    function TrimModifier() {
    }
    extendPrototype([ShapeModifier], TrimModifier);
    TrimModifier.prototype.initModifierProperties = function(elem2, data2) {
      this.s = PropertyFactory.getProp(elem2, data2.s, 0, 0.01, this);
      this.e = PropertyFactory.getProp(elem2, data2.e, 0, 0.01, this);
      this.o = PropertyFactory.getProp(elem2, data2.o, 0, 0, this);
      this.sValue = 0;
      this.eValue = 0;
      this.getValue = this.processKeys;
      this.m = data2.m;
      this._isAnimated = !!this.s.effectsSequence.length || !!this.e.effectsSequence.length || !!this.o.effectsSequence.length;
    };
    TrimModifier.prototype.addShapeToModifier = function(shapeData) {
      shapeData.pathsData = [];
    };
    TrimModifier.prototype.calculateShapeEdges = function(s, e, shapeLength, addedLength, totalModifierLength) {
      var segments = [];
      if (e <= 1) {
        segments.push({
          s,
          e
        });
      } else if (s >= 1) {
        segments.push({
          s: s - 1,
          e: e - 1
        });
      } else {
        segments.push({
          s,
          e: 1
        });
        segments.push({
          s: 0,
          e: e - 1
        });
      }
      var shapeSegments = [];
      var i;
      var len = segments.length;
      var segmentOb;
      for (i = 0; i < len; i += 1) {
        segmentOb = segments[i];
        if (!(segmentOb.e * totalModifierLength < addedLength || segmentOb.s * totalModifierLength > addedLength + shapeLength)) {
          var shapeS;
          var shapeE;
          if (segmentOb.s * totalModifierLength <= addedLength) {
            shapeS = 0;
          } else {
            shapeS = (segmentOb.s * totalModifierLength - addedLength) / shapeLength;
          }
          if (segmentOb.e * totalModifierLength >= addedLength + shapeLength) {
            shapeE = 1;
          } else {
            shapeE = (segmentOb.e * totalModifierLength - addedLength) / shapeLength;
          }
          shapeSegments.push([shapeS, shapeE]);
        }
      }
      if (!shapeSegments.length) {
        shapeSegments.push([0, 0]);
      }
      return shapeSegments;
    };
    TrimModifier.prototype.releasePathsData = function(pathsData) {
      var i;
      var len = pathsData.length;
      for (i = 0; i < len; i += 1) {
        segmentsLengthPool.release(pathsData[i]);
      }
      pathsData.length = 0;
      return pathsData;
    };
    TrimModifier.prototype.processShapes = function(_isFirstFrame) {
      var s;
      var e;
      if (this._mdf || _isFirstFrame) {
        var o = this.o.v % 360 / 360;
        if (o < 0) {
          o += 1;
        }
        if (this.s.v > 1) {
          s = 1 + o;
        } else if (this.s.v < 0) {
          s = 0 + o;
        } else {
          s = this.s.v + o;
        }
        if (this.e.v > 1) {
          e = 1 + o;
        } else if (this.e.v < 0) {
          e = 0 + o;
        } else {
          e = this.e.v + o;
        }
        if (s > e) {
          var _s = s;
          s = e;
          e = _s;
        }
        s = Math.round(s * 1e4) * 1e-4;
        e = Math.round(e * 1e4) * 1e-4;
        this.sValue = s;
        this.eValue = e;
      } else {
        s = this.sValue;
        e = this.eValue;
      }
      var shapePaths;
      var i;
      var len = this.shapes.length;
      var j;
      var jLen;
      var pathsData;
      var pathData;
      var totalShapeLength;
      var totalModifierLength = 0;
      if (e === s) {
        for (i = 0; i < len; i += 1) {
          this.shapes[i].localShapeCollection.releaseShapes();
          this.shapes[i].shape._mdf = true;
          this.shapes[i].shape.paths = this.shapes[i].localShapeCollection;
          if (this._mdf) {
            this.shapes[i].pathsData.length = 0;
          }
        }
      } else if (!(e === 1 && s === 0 || e === 0 && s === 1)) {
        var segments = [];
        var shapeData;
        var localShapeCollection;
        for (i = 0; i < len; i += 1) {
          shapeData = this.shapes[i];
          if (!shapeData.shape._mdf && !this._mdf && !_isFirstFrame && this.m !== 2) {
            shapeData.shape.paths = shapeData.localShapeCollection;
          } else {
            shapePaths = shapeData.shape.paths;
            jLen = shapePaths._length;
            totalShapeLength = 0;
            if (!shapeData.shape._mdf && shapeData.pathsData.length) {
              totalShapeLength = shapeData.totalShapeLength;
            } else {
              pathsData = this.releasePathsData(shapeData.pathsData);
              for (j = 0; j < jLen; j += 1) {
                pathData = bez.getSegmentsLength(shapePaths.shapes[j]);
                pathsData.push(pathData);
                totalShapeLength += pathData.totalLength;
              }
              shapeData.totalShapeLength = totalShapeLength;
              shapeData.pathsData = pathsData;
            }
            totalModifierLength += totalShapeLength;
            shapeData.shape._mdf = true;
          }
        }
        var shapeS = s;
        var shapeE = e;
        var addedLength = 0;
        var edges;
        for (i = len - 1; i >= 0; i -= 1) {
          shapeData = this.shapes[i];
          if (shapeData.shape._mdf) {
            localShapeCollection = shapeData.localShapeCollection;
            localShapeCollection.releaseShapes();
            if (this.m === 2 && len > 1) {
              edges = this.calculateShapeEdges(s, e, shapeData.totalShapeLength, addedLength, totalModifierLength);
              addedLength += shapeData.totalShapeLength;
            } else {
              edges = [[shapeS, shapeE]];
            }
            jLen = edges.length;
            for (j = 0; j < jLen; j += 1) {
              shapeS = edges[j][0];
              shapeE = edges[j][1];
              segments.length = 0;
              if (shapeE <= 1) {
                segments.push({
                  s: shapeData.totalShapeLength * shapeS,
                  e: shapeData.totalShapeLength * shapeE
                });
              } else if (shapeS >= 1) {
                segments.push({
                  s: shapeData.totalShapeLength * (shapeS - 1),
                  e: shapeData.totalShapeLength * (shapeE - 1)
                });
              } else {
                segments.push({
                  s: shapeData.totalShapeLength * shapeS,
                  e: shapeData.totalShapeLength
                });
                segments.push({
                  s: 0,
                  e: shapeData.totalShapeLength * (shapeE - 1)
                });
              }
              var newShapesData = this.addShapes(shapeData, segments[0]);
              if (segments[0].s !== segments[0].e) {
                if (segments.length > 1) {
                  var lastShapeInCollection = shapeData.shape.paths.shapes[shapeData.shape.paths._length - 1];
                  if (lastShapeInCollection.c) {
                    var lastShape = newShapesData.pop();
                    this.addPaths(newShapesData, localShapeCollection);
                    newShapesData = this.addShapes(shapeData, segments[1], lastShape);
                  } else {
                    this.addPaths(newShapesData, localShapeCollection);
                    newShapesData = this.addShapes(shapeData, segments[1]);
                  }
                }
                this.addPaths(newShapesData, localShapeCollection);
              }
            }
            shapeData.shape.paths = localShapeCollection;
          }
        }
      } else if (this._mdf) {
        for (i = 0; i < len; i += 1) {
          this.shapes[i].pathsData.length = 0;
          this.shapes[i].shape._mdf = true;
        }
      }
    };
    TrimModifier.prototype.addPaths = function(newPaths, localShapeCollection) {
      var i;
      var len = newPaths.length;
      for (i = 0; i < len; i += 1) {
        localShapeCollection.addShape(newPaths[i]);
      }
    };
    TrimModifier.prototype.addSegment = function(pt1, pt2, pt3, pt4, shapePath, pos, newShape) {
      shapePath.setXYAt(pt2[0], pt2[1], "o", pos);
      shapePath.setXYAt(pt3[0], pt3[1], "i", pos + 1);
      if (newShape) {
        shapePath.setXYAt(pt1[0], pt1[1], "v", pos);
      }
      shapePath.setXYAt(pt4[0], pt4[1], "v", pos + 1);
    };
    TrimModifier.prototype.addSegmentFromArray = function(points, shapePath, pos, newShape) {
      shapePath.setXYAt(points[1], points[5], "o", pos);
      shapePath.setXYAt(points[2], points[6], "i", pos + 1);
      if (newShape) {
        shapePath.setXYAt(points[0], points[4], "v", pos);
      }
      shapePath.setXYAt(points[3], points[7], "v", pos + 1);
    };
    TrimModifier.prototype.addShapes = function(shapeData, shapeSegment, shapePath) {
      var pathsData = shapeData.pathsData;
      var shapePaths = shapeData.shape.paths.shapes;
      var i;
      var len = shapeData.shape.paths._length;
      var j;
      var jLen;
      var addedLength = 0;
      var currentLengthData;
      var segmentCount;
      var lengths;
      var segment;
      var shapes = [];
      var initPos;
      var newShape = true;
      if (!shapePath) {
        shapePath = shapePool.newElement();
        segmentCount = 0;
        initPos = 0;
      } else {
        segmentCount = shapePath._length;
        initPos = shapePath._length;
      }
      shapes.push(shapePath);
      for (i = 0; i < len; i += 1) {
        lengths = pathsData[i].lengths;
        shapePath.c = shapePaths[i].c;
        jLen = shapePaths[i].c ? lengths.length : lengths.length + 1;
        for (j = 1; j < jLen; j += 1) {
          currentLengthData = lengths[j - 1];
          if (addedLength + currentLengthData.addedLength < shapeSegment.s) {
            addedLength += currentLengthData.addedLength;
            shapePath.c = false;
          } else if (addedLength > shapeSegment.e) {
            shapePath.c = false;
            break;
          } else {
            if (shapeSegment.s <= addedLength && shapeSegment.e >= addedLength + currentLengthData.addedLength) {
              this.addSegment(shapePaths[i].v[j - 1], shapePaths[i].o[j - 1], shapePaths[i].i[j], shapePaths[i].v[j], shapePath, segmentCount, newShape);
              newShape = false;
            } else {
              segment = bez.getNewSegment(shapePaths[i].v[j - 1], shapePaths[i].v[j], shapePaths[i].o[j - 1], shapePaths[i].i[j], (shapeSegment.s - addedLength) / currentLengthData.addedLength, (shapeSegment.e - addedLength) / currentLengthData.addedLength, lengths[j - 1]);
              this.addSegmentFromArray(segment, shapePath, segmentCount, newShape);
              newShape = false;
              shapePath.c = false;
            }
            addedLength += currentLengthData.addedLength;
            segmentCount += 1;
          }
        }
        if (shapePaths[i].c && lengths.length) {
          currentLengthData = lengths[j - 1];
          if (addedLength <= shapeSegment.e) {
            var segmentLength = lengths[j - 1].addedLength;
            if (shapeSegment.s <= addedLength && shapeSegment.e >= addedLength + segmentLength) {
              this.addSegment(shapePaths[i].v[j - 1], shapePaths[i].o[j - 1], shapePaths[i].i[0], shapePaths[i].v[0], shapePath, segmentCount, newShape);
              newShape = false;
            } else {
              segment = bez.getNewSegment(shapePaths[i].v[j - 1], shapePaths[i].v[0], shapePaths[i].o[j - 1], shapePaths[i].i[0], (shapeSegment.s - addedLength) / segmentLength, (shapeSegment.e - addedLength) / segmentLength, lengths[j - 1]);
              this.addSegmentFromArray(segment, shapePath, segmentCount, newShape);
              newShape = false;
              shapePath.c = false;
            }
          } else {
            shapePath.c = false;
          }
          addedLength += currentLengthData.addedLength;
          segmentCount += 1;
        }
        if (shapePath._length) {
          shapePath.setXYAt(shapePath.v[initPos][0], shapePath.v[initPos][1], "i", initPos);
          shapePath.setXYAt(shapePath.v[shapePath._length - 1][0], shapePath.v[shapePath._length - 1][1], "o", shapePath._length - 1);
        }
        if (addedLength > shapeSegment.e) {
          break;
        }
        if (i < len - 1) {
          shapePath = shapePool.newElement();
          newShape = true;
          shapes.push(shapePath);
          segmentCount = 0;
        }
      }
      return shapes;
    };
    function PuckerAndBloatModifier() {
    }
    extendPrototype([ShapeModifier], PuckerAndBloatModifier);
    PuckerAndBloatModifier.prototype.initModifierProperties = function(elem2, data2) {
      this.getValue = this.processKeys;
      this.amount = PropertyFactory.getProp(elem2, data2.a, 0, null, this);
      this._isAnimated = !!this.amount.effectsSequence.length;
    };
    PuckerAndBloatModifier.prototype.processPath = function(path, amount) {
      var percent = amount / 100;
      var centerPoint = [0, 0];
      var pathLength = path._length;
      var i = 0;
      for (i = 0; i < pathLength; i += 1) {
        centerPoint[0] += path.v[i][0];
        centerPoint[1] += path.v[i][1];
      }
      centerPoint[0] /= pathLength;
      centerPoint[1] /= pathLength;
      var clonedPath = shapePool.newElement();
      clonedPath.c = path.c;
      var vX;
      var vY;
      var oX;
      var oY;
      var iX;
      var iY;
      for (i = 0; i < pathLength; i += 1) {
        vX = path.v[i][0] + (centerPoint[0] - path.v[i][0]) * percent;
        vY = path.v[i][1] + (centerPoint[1] - path.v[i][1]) * percent;
        oX = path.o[i][0] + (centerPoint[0] - path.o[i][0]) * -percent;
        oY = path.o[i][1] + (centerPoint[1] - path.o[i][1]) * -percent;
        iX = path.i[i][0] + (centerPoint[0] - path.i[i][0]) * -percent;
        iY = path.i[i][1] + (centerPoint[1] - path.i[i][1]) * -percent;
        clonedPath.setTripleAt(vX, vY, oX, oY, iX, iY, i);
      }
      return clonedPath;
    };
    PuckerAndBloatModifier.prototype.processShapes = function(_isFirstFrame) {
      var shapePaths;
      var i;
      var len = this.shapes.length;
      var j;
      var jLen;
      var amount = this.amount.v;
      if (amount !== 0) {
        var shapeData;
        var localShapeCollection;
        for (i = 0; i < len; i += 1) {
          shapeData = this.shapes[i];
          localShapeCollection = shapeData.localShapeCollection;
          if (!(!shapeData.shape._mdf && !this._mdf && !_isFirstFrame)) {
            localShapeCollection.releaseShapes();
            shapeData.shape._mdf = true;
            shapePaths = shapeData.shape.paths.shapes;
            jLen = shapeData.shape.paths._length;
            for (j = 0; j < jLen; j += 1) {
              localShapeCollection.addShape(this.processPath(shapePaths[j], amount));
            }
          }
          shapeData.shape.paths = shapeData.localShapeCollection;
        }
      }
      if (!this.dynamicProperties.length) {
        this._mdf = false;
      }
    };
    var TransformPropertyFactory = function() {
      var defaultVector = [0, 0];
      function applyToMatrix(mat) {
        var _mdf = this._mdf;
        this.iterateDynamicProperties();
        this._mdf = this._mdf || _mdf;
        if (this.a) {
          mat.translate(-this.a.v[0], -this.a.v[1], this.a.v[2]);
        }
        if (this.s) {
          mat.scale(this.s.v[0], this.s.v[1], this.s.v[2]);
        }
        if (this.sk) {
          mat.skewFromAxis(-this.sk.v, this.sa.v);
        }
        if (this.r) {
          mat.rotate(-this.r.v);
        } else {
          mat.rotateZ(-this.rz.v).rotateY(this.ry.v).rotateX(this.rx.v).rotateZ(-this.or.v[2]).rotateY(this.or.v[1]).rotateX(this.or.v[0]);
        }
        if (this.data.p.s) {
          if (this.data.p.z) {
            mat.translate(this.px.v, this.py.v, -this.pz.v);
          } else {
            mat.translate(this.px.v, this.py.v, 0);
          }
        } else {
          mat.translate(this.p.v[0], this.p.v[1], -this.p.v[2]);
        }
      }
      function processKeys(forceRender) {
        if (this.elem.globalData.frameId === this.frameId) {
          return;
        }
        if (this._isDirty) {
          this.precalculateMatrix();
          this._isDirty = false;
        }
        this.iterateDynamicProperties();
        if (this._mdf || forceRender) {
          var frameRate;
          this.v.cloneFromProps(this.pre.props);
          if (this.appliedTransformations < 1) {
            this.v.translate(-this.a.v[0], -this.a.v[1], this.a.v[2]);
          }
          if (this.appliedTransformations < 2) {
            this.v.scale(this.s.v[0], this.s.v[1], this.s.v[2]);
          }
          if (this.sk && this.appliedTransformations < 3) {
            this.v.skewFromAxis(-this.sk.v, this.sa.v);
          }
          if (this.r && this.appliedTransformations < 4) {
            this.v.rotate(-this.r.v);
          } else if (!this.r && this.appliedTransformations < 4) {
            this.v.rotateZ(-this.rz.v).rotateY(this.ry.v).rotateX(this.rx.v).rotateZ(-this.or.v[2]).rotateY(this.or.v[1]).rotateX(this.or.v[0]);
          }
          if (this.autoOriented) {
            var v1;
            var v2;
            frameRate = this.elem.globalData.frameRate;
            if (this.p && this.p.keyframes && this.p.getValueAtTime) {
              if (this.p._caching.lastFrame + this.p.offsetTime <= this.p.keyframes[0].t) {
                v1 = this.p.getValueAtTime((this.p.keyframes[0].t + 0.01) / frameRate, 0);
                v2 = this.p.getValueAtTime(this.p.keyframes[0].t / frameRate, 0);
              } else if (this.p._caching.lastFrame + this.p.offsetTime >= this.p.keyframes[this.p.keyframes.length - 1].t) {
                v1 = this.p.getValueAtTime(this.p.keyframes[this.p.keyframes.length - 1].t / frameRate, 0);
                v2 = this.p.getValueAtTime((this.p.keyframes[this.p.keyframes.length - 1].t - 0.05) / frameRate, 0);
              } else {
                v1 = this.p.pv;
                v2 = this.p.getValueAtTime((this.p._caching.lastFrame + this.p.offsetTime - 0.01) / frameRate, this.p.offsetTime);
              }
            } else if (this.px && this.px.keyframes && this.py.keyframes && this.px.getValueAtTime && this.py.getValueAtTime) {
              v1 = [];
              v2 = [];
              var px = this.px;
              var py = this.py;
              if (px._caching.lastFrame + px.offsetTime <= px.keyframes[0].t) {
                v1[0] = px.getValueAtTime((px.keyframes[0].t + 0.01) / frameRate, 0);
                v1[1] = py.getValueAtTime((py.keyframes[0].t + 0.01) / frameRate, 0);
                v2[0] = px.getValueAtTime(px.keyframes[0].t / frameRate, 0);
                v2[1] = py.getValueAtTime(py.keyframes[0].t / frameRate, 0);
              } else if (px._caching.lastFrame + px.offsetTime >= px.keyframes[px.keyframes.length - 1].t) {
                v1[0] = px.getValueAtTime(px.keyframes[px.keyframes.length - 1].t / frameRate, 0);
                v1[1] = py.getValueAtTime(py.keyframes[py.keyframes.length - 1].t / frameRate, 0);
                v2[0] = px.getValueAtTime((px.keyframes[px.keyframes.length - 1].t - 0.01) / frameRate, 0);
                v2[1] = py.getValueAtTime((py.keyframes[py.keyframes.length - 1].t - 0.01) / frameRate, 0);
              } else {
                v1 = [px.pv, py.pv];
                v2[0] = px.getValueAtTime((px._caching.lastFrame + px.offsetTime - 0.01) / frameRate, px.offsetTime);
                v2[1] = py.getValueAtTime((py._caching.lastFrame + py.offsetTime - 0.01) / frameRate, py.offsetTime);
              }
            } else {
              v2 = defaultVector;
              v1 = v2;
            }
            this.v.rotate(-Math.atan2(v1[1] - v2[1], v1[0] - v2[0]));
          }
          if (this.data.p && this.data.p.s) {
            if (this.data.p.z) {
              this.v.translate(this.px.v, this.py.v, -this.pz.v);
            } else {
              this.v.translate(this.px.v, this.py.v, 0);
            }
          } else {
            this.v.translate(this.p.v[0], this.p.v[1], -this.p.v[2]);
          }
        }
        this.frameId = this.elem.globalData.frameId;
      }
      function precalculateMatrix() {
        this.appliedTransformations = 0;
        this.pre.reset();
        if (!this.a.effectsSequence.length) {
          this.pre.translate(-this.a.v[0], -this.a.v[1], this.a.v[2]);
          this.appliedTransformations = 1;
        } else {
          return;
        }
        if (!this.s.effectsSequence.length) {
          this.pre.scale(this.s.v[0], this.s.v[1], this.s.v[2]);
          this.appliedTransformations = 2;
        } else {
          return;
        }
        if (this.sk) {
          if (!this.sk.effectsSequence.length && !this.sa.effectsSequence.length) {
            this.pre.skewFromAxis(-this.sk.v, this.sa.v);
            this.appliedTransformations = 3;
          } else {
            return;
          }
        }
        if (this.r) {
          if (!this.r.effectsSequence.length) {
            this.pre.rotate(-this.r.v);
            this.appliedTransformations = 4;
          }
        } else if (!this.rz.effectsSequence.length && !this.ry.effectsSequence.length && !this.rx.effectsSequence.length && !this.or.effectsSequence.length) {
          this.pre.rotateZ(-this.rz.v).rotateY(this.ry.v).rotateX(this.rx.v).rotateZ(-this.or.v[2]).rotateY(this.or.v[1]).rotateX(this.or.v[0]);
          this.appliedTransformations = 4;
        }
      }
      function autoOrient() {
      }
      function addDynamicProperty(prop) {
        this._addDynamicProperty(prop);
        this.elem.addDynamicProperty(prop);
        this._isDirty = true;
      }
      function TransformProperty(elem2, data2, container) {
        this.elem = elem2;
        this.frameId = -1;
        this.propType = "transform";
        this.data = data2;
        this.v = new Matrix();
        this.pre = new Matrix();
        this.appliedTransformations = 0;
        this.initDynamicPropertyContainer(container || elem2);
        if (data2.p && data2.p.s) {
          this.px = PropertyFactory.getProp(elem2, data2.p.x, 0, 0, this);
          this.py = PropertyFactory.getProp(elem2, data2.p.y, 0, 0, this);
          if (data2.p.z) {
            this.pz = PropertyFactory.getProp(elem2, data2.p.z, 0, 0, this);
          }
        } else {
          this.p = PropertyFactory.getProp(elem2, data2.p || {
            k: [0, 0, 0]
          }, 1, 0, this);
        }
        if (data2.rx) {
          this.rx = PropertyFactory.getProp(elem2, data2.rx, 0, degToRads, this);
          this.ry = PropertyFactory.getProp(elem2, data2.ry, 0, degToRads, this);
          this.rz = PropertyFactory.getProp(elem2, data2.rz, 0, degToRads, this);
          if (data2.or.k[0].ti) {
            var i;
            var len = data2.or.k.length;
            for (i = 0; i < len; i += 1) {
              data2.or.k[i].to = null;
              data2.or.k[i].ti = null;
            }
          }
          this.or = PropertyFactory.getProp(elem2, data2.or, 1, degToRads, this);
          this.or.sh = true;
        } else {
          this.r = PropertyFactory.getProp(elem2, data2.r || {
            k: 0
          }, 0, degToRads, this);
        }
        if (data2.sk) {
          this.sk = PropertyFactory.getProp(elem2, data2.sk, 0, degToRads, this);
          this.sa = PropertyFactory.getProp(elem2, data2.sa, 0, degToRads, this);
        }
        this.a = PropertyFactory.getProp(elem2, data2.a || {
          k: [0, 0, 0]
        }, 1, 0, this);
        this.s = PropertyFactory.getProp(elem2, data2.s || {
          k: [100, 100, 100]
        }, 1, 0.01, this);
        if (data2.o) {
          this.o = PropertyFactory.getProp(elem2, data2.o, 0, 0.01, elem2);
        } else {
          this.o = {
            _mdf: false,
            v: 1
          };
        }
        this._isDirty = true;
        if (!this.dynamicProperties.length) {
          this.getValue(true);
        }
      }
      TransformProperty.prototype = {
        applyToMatrix,
        getValue: processKeys,
        precalculateMatrix,
        autoOrient
      };
      extendPrototype([DynamicPropertyContainer], TransformProperty);
      TransformProperty.prototype.addDynamicProperty = addDynamicProperty;
      TransformProperty.prototype._addDynamicProperty = DynamicPropertyContainer.prototype.addDynamicProperty;
      function getTransformProperty(elem2, data2, container) {
        return new TransformProperty(elem2, data2, container);
      }
      return {
        getTransformProperty
      };
    }();
    function RepeaterModifier() {
    }
    extendPrototype([ShapeModifier], RepeaterModifier);
    RepeaterModifier.prototype.initModifierProperties = function(elem2, data2) {
      this.getValue = this.processKeys;
      this.c = PropertyFactory.getProp(elem2, data2.c, 0, null, this);
      this.o = PropertyFactory.getProp(elem2, data2.o, 0, null, this);
      this.tr = TransformPropertyFactory.getTransformProperty(elem2, data2.tr, this);
      this.so = PropertyFactory.getProp(elem2, data2.tr.so, 0, 0.01, this);
      this.eo = PropertyFactory.getProp(elem2, data2.tr.eo, 0, 0.01, this);
      this.data = data2;
      if (!this.dynamicProperties.length) {
        this.getValue(true);
      }
      this._isAnimated = !!this.dynamicProperties.length;
      this.pMatrix = new Matrix();
      this.rMatrix = new Matrix();
      this.sMatrix = new Matrix();
      this.tMatrix = new Matrix();
      this.matrix = new Matrix();
    };
    RepeaterModifier.prototype.applyTransforms = function(pMatrix, rMatrix, sMatrix, transform2, perc, inv) {
      var dir = inv ? -1 : 1;
      var scaleX = transform2.s.v[0] + (1 - transform2.s.v[0]) * (1 - perc);
      var scaleY = transform2.s.v[1] + (1 - transform2.s.v[1]) * (1 - perc);
      pMatrix.translate(transform2.p.v[0] * dir * perc, transform2.p.v[1] * dir * perc, transform2.p.v[2]);
      rMatrix.translate(-transform2.a.v[0], -transform2.a.v[1], transform2.a.v[2]);
      rMatrix.rotate(-transform2.r.v * dir * perc);
      rMatrix.translate(transform2.a.v[0], transform2.a.v[1], transform2.a.v[2]);
      sMatrix.translate(-transform2.a.v[0], -transform2.a.v[1], transform2.a.v[2]);
      sMatrix.scale(inv ? 1 / scaleX : scaleX, inv ? 1 / scaleY : scaleY);
      sMatrix.translate(transform2.a.v[0], transform2.a.v[1], transform2.a.v[2]);
    };
    RepeaterModifier.prototype.init = function(elem2, arr, pos, elemsData) {
      this.elem = elem2;
      this.arr = arr;
      this.pos = pos;
      this.elemsData = elemsData;
      this._currentCopies = 0;
      this._elements = [];
      this._groups = [];
      this.frameId = -1;
      this.initDynamicPropertyContainer(elem2);
      this.initModifierProperties(elem2, arr[pos]);
      while (pos > 0) {
        pos -= 1;
        this._elements.unshift(arr[pos]);
      }
      if (this.dynamicProperties.length) {
        this.k = true;
      } else {
        this.getValue(true);
      }
    };
    RepeaterModifier.prototype.resetElements = function(elements) {
      var i;
      var len = elements.length;
      for (i = 0; i < len; i += 1) {
        elements[i]._processed = false;
        if (elements[i].ty === "gr") {
          this.resetElements(elements[i].it);
        }
      }
    };
    RepeaterModifier.prototype.cloneElements = function(elements) {
      var newElements = JSON.parse(JSON.stringify(elements));
      this.resetElements(newElements);
      return newElements;
    };
    RepeaterModifier.prototype.changeGroupRender = function(elements, renderFlag) {
      var i;
      var len = elements.length;
      for (i = 0; i < len; i += 1) {
        elements[i]._render = renderFlag;
        if (elements[i].ty === "gr") {
          this.changeGroupRender(elements[i].it, renderFlag);
        }
      }
    };
    RepeaterModifier.prototype.processShapes = function(_isFirstFrame) {
      var items;
      var itemsTransform;
      var i;
      var dir;
      var cont;
      var hasReloaded = false;
      if (this._mdf || _isFirstFrame) {
        var copies = Math.ceil(this.c.v);
        if (this._groups.length < copies) {
          while (this._groups.length < copies) {
            var group = {
              it: this.cloneElements(this._elements),
              ty: "gr"
            };
            group.it.push({
              a: {
                a: 0,
                ix: 1,
                k: [0, 0]
              },
              nm: "Transform",
              o: {
                a: 0,
                ix: 7,
                k: 100
              },
              p: {
                a: 0,
                ix: 2,
                k: [0, 0]
              },
              r: {
                a: 1,
                ix: 6,
                k: [{
                  s: 0,
                  e: 0,
                  t: 0
                }, {
                  s: 0,
                  e: 0,
                  t: 1
                }]
              },
              s: {
                a: 0,
                ix: 3,
                k: [100, 100]
              },
              sa: {
                a: 0,
                ix: 5,
                k: 0
              },
              sk: {
                a: 0,
                ix: 4,
                k: 0
              },
              ty: "tr"
            });
            this.arr.splice(0, 0, group);
            this._groups.splice(0, 0, group);
            this._currentCopies += 1;
          }
          this.elem.reloadShapes();
          hasReloaded = true;
        }
        cont = 0;
        var renderFlag;
        for (i = 0; i <= this._groups.length - 1; i += 1) {
          renderFlag = cont < copies;
          this._groups[i]._render = renderFlag;
          this.changeGroupRender(this._groups[i].it, renderFlag);
          if (!renderFlag) {
            var elems = this.elemsData[i].it;
            var transformData = elems[elems.length - 1];
            if (transformData.transform.op.v !== 0) {
              transformData.transform.op._mdf = true;
              transformData.transform.op.v = 0;
            } else {
              transformData.transform.op._mdf = false;
            }
          }
          cont += 1;
        }
        this._currentCopies = copies;
        var offset = this.o.v;
        var offsetModulo = offset % 1;
        var roundOffset = offset > 0 ? Math.floor(offset) : Math.ceil(offset);
        var pProps = this.pMatrix.props;
        var rProps = this.rMatrix.props;
        var sProps = this.sMatrix.props;
        this.pMatrix.reset();
        this.rMatrix.reset();
        this.sMatrix.reset();
        this.tMatrix.reset();
        this.matrix.reset();
        var iteration = 0;
        if (offset > 0) {
          while (iteration < roundOffset) {
            this.applyTransforms(this.pMatrix, this.rMatrix, this.sMatrix, this.tr, 1, false);
            iteration += 1;
          }
          if (offsetModulo) {
            this.applyTransforms(this.pMatrix, this.rMatrix, this.sMatrix, this.tr, offsetModulo, false);
            iteration += offsetModulo;
          }
        } else if (offset < 0) {
          while (iteration > roundOffset) {
            this.applyTransforms(this.pMatrix, this.rMatrix, this.sMatrix, this.tr, 1, true);
            iteration -= 1;
          }
          if (offsetModulo) {
            this.applyTransforms(this.pMatrix, this.rMatrix, this.sMatrix, this.tr, -offsetModulo, true);
            iteration -= offsetModulo;
          }
        }
        i = this.data.m === 1 ? 0 : this._currentCopies - 1;
        dir = this.data.m === 1 ? 1 : -1;
        cont = this._currentCopies;
        var j;
        var jLen;
        while (cont) {
          items = this.elemsData[i].it;
          itemsTransform = items[items.length - 1].transform.mProps.v.props;
          jLen = itemsTransform.length;
          items[items.length - 1].transform.mProps._mdf = true;
          items[items.length - 1].transform.op._mdf = true;
          items[items.length - 1].transform.op.v = this._currentCopies === 1 ? this.so.v : this.so.v + (this.eo.v - this.so.v) * (i / (this._currentCopies - 1));
          if (iteration !== 0) {
            if (i !== 0 && dir === 1 || i !== this._currentCopies - 1 && dir === -1) {
              this.applyTransforms(this.pMatrix, this.rMatrix, this.sMatrix, this.tr, 1, false);
            }
            this.matrix.transform(rProps[0], rProps[1], rProps[2], rProps[3], rProps[4], rProps[5], rProps[6], rProps[7], rProps[8], rProps[9], rProps[10], rProps[11], rProps[12], rProps[13], rProps[14], rProps[15]);
            this.matrix.transform(sProps[0], sProps[1], sProps[2], sProps[3], sProps[4], sProps[5], sProps[6], sProps[7], sProps[8], sProps[9], sProps[10], sProps[11], sProps[12], sProps[13], sProps[14], sProps[15]);
            this.matrix.transform(pProps[0], pProps[1], pProps[2], pProps[3], pProps[4], pProps[5], pProps[6], pProps[7], pProps[8], pProps[9], pProps[10], pProps[11], pProps[12], pProps[13], pProps[14], pProps[15]);
            for (j = 0; j < jLen; j += 1) {
              itemsTransform[j] = this.matrix.props[j];
            }
            this.matrix.reset();
          } else {
            this.matrix.reset();
            for (j = 0; j < jLen; j += 1) {
              itemsTransform[j] = this.matrix.props[j];
            }
          }
          iteration += 1;
          cont -= 1;
          i += dir;
        }
      } else {
        cont = this._currentCopies;
        i = 0;
        dir = 1;
        while (cont) {
          items = this.elemsData[i].it;
          itemsTransform = items[items.length - 1].transform.mProps.v.props;
          items[items.length - 1].transform.mProps._mdf = false;
          items[items.length - 1].transform.op._mdf = false;
          cont -= 1;
          i += dir;
        }
      }
      return hasReloaded;
    };
    RepeaterModifier.prototype.addShape = function() {
    };
    function RoundCornersModifier() {
    }
    extendPrototype([ShapeModifier], RoundCornersModifier);
    RoundCornersModifier.prototype.initModifierProperties = function(elem2, data2) {
      this.getValue = this.processKeys;
      this.rd = PropertyFactory.getProp(elem2, data2.r, 0, null, this);
      this._isAnimated = !!this.rd.effectsSequence.length;
    };
    RoundCornersModifier.prototype.processPath = function(path, round) {
      var clonedPath = shapePool.newElement();
      clonedPath.c = path.c;
      var i;
      var len = path._length;
      var currentV;
      var currentI;
      var currentO;
      var closerV;
      var distance;
      var newPosPerc;
      var index2 = 0;
      var vX;
      var vY;
      var oX;
      var oY;
      var iX;
      var iY;
      for (i = 0; i < len; i += 1) {
        currentV = path.v[i];
        currentO = path.o[i];
        currentI = path.i[i];
        if (currentV[0] === currentO[0] && currentV[1] === currentO[1] && currentV[0] === currentI[0] && currentV[1] === currentI[1]) {
          if ((i === 0 || i === len - 1) && !path.c) {
            clonedPath.setTripleAt(currentV[0], currentV[1], currentO[0], currentO[1], currentI[0], currentI[1], index2);
            index2 += 1;
          } else {
            if (i === 0) {
              closerV = path.v[len - 1];
            } else {
              closerV = path.v[i - 1];
            }
            distance = Math.sqrt(Math.pow(currentV[0] - closerV[0], 2) + Math.pow(currentV[1] - closerV[1], 2));
            newPosPerc = distance ? Math.min(distance / 2, round) / distance : 0;
            iX = currentV[0] + (closerV[0] - currentV[0]) * newPosPerc;
            vX = iX;
            iY = currentV[1] - (currentV[1] - closerV[1]) * newPosPerc;
            vY = iY;
            oX = vX - (vX - currentV[0]) * roundCorner;
            oY = vY - (vY - currentV[1]) * roundCorner;
            clonedPath.setTripleAt(vX, vY, oX, oY, iX, iY, index2);
            index2 += 1;
            if (i === len - 1) {
              closerV = path.v[0];
            } else {
              closerV = path.v[i + 1];
            }
            distance = Math.sqrt(Math.pow(currentV[0] - closerV[0], 2) + Math.pow(currentV[1] - closerV[1], 2));
            newPosPerc = distance ? Math.min(distance / 2, round) / distance : 0;
            oX = currentV[0] + (closerV[0] - currentV[0]) * newPosPerc;
            vX = oX;
            oY = currentV[1] + (closerV[1] - currentV[1]) * newPosPerc;
            vY = oY;
            iX = vX - (vX - currentV[0]) * roundCorner;
            iY = vY - (vY - currentV[1]) * roundCorner;
            clonedPath.setTripleAt(vX, vY, oX, oY, iX, iY, index2);
            index2 += 1;
          }
        } else {
          clonedPath.setTripleAt(path.v[i][0], path.v[i][1], path.o[i][0], path.o[i][1], path.i[i][0], path.i[i][1], index2);
          index2 += 1;
        }
      }
      return clonedPath;
    };
    RoundCornersModifier.prototype.processShapes = function(_isFirstFrame) {
      var shapePaths;
      var i;
      var len = this.shapes.length;
      var j;
      var jLen;
      var rd = this.rd.v;
      if (rd !== 0) {
        var shapeData;
        var localShapeCollection;
        for (i = 0; i < len; i += 1) {
          shapeData = this.shapes[i];
          localShapeCollection = shapeData.localShapeCollection;
          if (!(!shapeData.shape._mdf && !this._mdf && !_isFirstFrame)) {
            localShapeCollection.releaseShapes();
            shapeData.shape._mdf = true;
            shapePaths = shapeData.shape.paths.shapes;
            jLen = shapeData.shape.paths._length;
            for (j = 0; j < jLen; j += 1) {
              localShapeCollection.addShape(this.processPath(shapePaths[j], rd));
            }
          }
          shapeData.shape.paths = shapeData.localShapeCollection;
        }
      }
      if (!this.dynamicProperties.length) {
        this._mdf = false;
      }
    };
    function floatEqual(a, b) {
      return Math.abs(a - b) * 1e5 <= Math.min(Math.abs(a), Math.abs(b));
    }
    function floatZero(f) {
      return Math.abs(f) <= 1e-5;
    }
    function lerp(p0, p1, amount) {
      return p0 * (1 - amount) + p1 * amount;
    }
    function lerpPoint(p0, p1, amount) {
      return [lerp(p0[0], p1[0], amount), lerp(p0[1], p1[1], amount)];
    }
    function quadRoots(a, b, c) {
      if (a === 0) return [];
      var s = b * b - 4 * a * c;
      if (s < 0) return [];
      var singleRoot = -b / (2 * a);
      if (s === 0) return [singleRoot];
      var delta = Math.sqrt(s) / (2 * a);
      return [singleRoot - delta, singleRoot + delta];
    }
    function polynomialCoefficients(p0, p1, p2, p3) {
      return [-p0 + 3 * p1 - 3 * p2 + p3, 3 * p0 - 6 * p1 + 3 * p2, -3 * p0 + 3 * p1, p0];
    }
    function singlePoint(p) {
      return new PolynomialBezier(p, p, p, p, false);
    }
    function PolynomialBezier(p0, p1, p2, p3, linearize) {
      if (linearize && pointEqual(p0, p1)) {
        p1 = lerpPoint(p0, p3, 1 / 3);
      }
      if (linearize && pointEqual(p2, p3)) {
        p2 = lerpPoint(p0, p3, 2 / 3);
      }
      var coeffx = polynomialCoefficients(p0[0], p1[0], p2[0], p3[0]);
      var coeffy = polynomialCoefficients(p0[1], p1[1], p2[1], p3[1]);
      this.a = [coeffx[0], coeffy[0]];
      this.b = [coeffx[1], coeffy[1]];
      this.c = [coeffx[2], coeffy[2]];
      this.d = [coeffx[3], coeffy[3]];
      this.points = [p0, p1, p2, p3];
    }
    PolynomialBezier.prototype.point = function(t) {
      return [((this.a[0] * t + this.b[0]) * t + this.c[0]) * t + this.d[0], ((this.a[1] * t + this.b[1]) * t + this.c[1]) * t + this.d[1]];
    };
    PolynomialBezier.prototype.derivative = function(t) {
      return [(3 * t * this.a[0] + 2 * this.b[0]) * t + this.c[0], (3 * t * this.a[1] + 2 * this.b[1]) * t + this.c[1]];
    };
    PolynomialBezier.prototype.tangentAngle = function(t) {
      var p = this.derivative(t);
      return Math.atan2(p[1], p[0]);
    };
    PolynomialBezier.prototype.normalAngle = function(t) {
      var p = this.derivative(t);
      return Math.atan2(p[0], p[1]);
    };
    PolynomialBezier.prototype.inflectionPoints = function() {
      var denom = this.a[1] * this.b[0] - this.a[0] * this.b[1];
      if (floatZero(denom)) return [];
      var tcusp = -0.5 * (this.a[1] * this.c[0] - this.a[0] * this.c[1]) / denom;
      var square = tcusp * tcusp - 1 / 3 * (this.b[1] * this.c[0] - this.b[0] * this.c[1]) / denom;
      if (square < 0) return [];
      var root = Math.sqrt(square);
      if (floatZero(root)) {
        if (root > 0 && root < 1) return [tcusp];
        return [];
      }
      return [tcusp - root, tcusp + root].filter(function(r) {
        return r > 0 && r < 1;
      });
    };
    PolynomialBezier.prototype.split = function(t) {
      if (t <= 0) return [singlePoint(this.points[0]), this];
      if (t >= 1) return [this, singlePoint(this.points[this.points.length - 1])];
      var p10 = lerpPoint(this.points[0], this.points[1], t);
      var p11 = lerpPoint(this.points[1], this.points[2], t);
      var p12 = lerpPoint(this.points[2], this.points[3], t);
      var p20 = lerpPoint(p10, p11, t);
      var p21 = lerpPoint(p11, p12, t);
      var p3 = lerpPoint(p20, p21, t);
      return [new PolynomialBezier(this.points[0], p10, p20, p3, true), new PolynomialBezier(p3, p21, p12, this.points[3], true)];
    };
    function extrema(bez2, comp2) {
      var min = bez2.points[0][comp2];
      var max = bez2.points[bez2.points.length - 1][comp2];
      if (min > max) {
        var e = max;
        max = min;
        min = e;
      }
      var f = quadRoots(3 * bez2.a[comp2], 2 * bez2.b[comp2], bez2.c[comp2]);
      for (var i = 0; i < f.length; i += 1) {
        if (f[i] > 0 && f[i] < 1) {
          var val2 = bez2.point(f[i])[comp2];
          if (val2 < min) min = val2;
          else if (val2 > max) max = val2;
        }
      }
      return {
        min,
        max
      };
    }
    PolynomialBezier.prototype.bounds = function() {
      return {
        x: extrema(this, 0),
        y: extrema(this, 1)
      };
    };
    PolynomialBezier.prototype.boundingBox = function() {
      var bounds = this.bounds();
      return {
        left: bounds.x.min,
        right: bounds.x.max,
        top: bounds.y.min,
        bottom: bounds.y.max,
        width: bounds.x.max - bounds.x.min,
        height: bounds.y.max - bounds.y.min,
        cx: (bounds.x.max + bounds.x.min) / 2,
        cy: (bounds.y.max + bounds.y.min) / 2
      };
    };
    function intersectData(bez2, t1, t2) {
      var box = bez2.boundingBox();
      return {
        cx: box.cx,
        cy: box.cy,
        width: box.width,
        height: box.height,
        bez: bez2,
        t: (t1 + t2) / 2,
        t1,
        t2
      };
    }
    function splitData(data2) {
      var split = data2.bez.split(0.5);
      return [intersectData(split[0], data2.t1, data2.t), intersectData(split[1], data2.t, data2.t2)];
    }
    function boxIntersect(b1, b2) {
      return Math.abs(b1.cx - b2.cx) * 2 < b1.width + b2.width && Math.abs(b1.cy - b2.cy) * 2 < b1.height + b2.height;
    }
    function intersectsImpl(d1, d2, depth, tolerance, intersections, maxRecursion) {
      if (!boxIntersect(d1, d2)) return;
      if (depth >= maxRecursion || d1.width <= tolerance && d1.height <= tolerance && d2.width <= tolerance && d2.height <= tolerance) {
        intersections.push([d1.t, d2.t]);
        return;
      }
      var d1s = splitData(d1);
      var d2s = splitData(d2);
      intersectsImpl(d1s[0], d2s[0], depth + 1, tolerance, intersections, maxRecursion);
      intersectsImpl(d1s[0], d2s[1], depth + 1, tolerance, intersections, maxRecursion);
      intersectsImpl(d1s[1], d2s[0], depth + 1, tolerance, intersections, maxRecursion);
      intersectsImpl(d1s[1], d2s[1], depth + 1, tolerance, intersections, maxRecursion);
    }
    PolynomialBezier.prototype.intersections = function(other, tolerance, maxRecursion) {
      if (tolerance === void 0) tolerance = 2;
      if (maxRecursion === void 0) maxRecursion = 7;
      var intersections = [];
      intersectsImpl(intersectData(this, 0, 1), intersectData(other, 0, 1), 0, tolerance, intersections, maxRecursion);
      return intersections;
    };
    PolynomialBezier.shapeSegment = function(shapePath, index2) {
      var nextIndex = (index2 + 1) % shapePath.length();
      return new PolynomialBezier(shapePath.v[index2], shapePath.o[index2], shapePath.i[nextIndex], shapePath.v[nextIndex], true);
    };
    PolynomialBezier.shapeSegmentInverted = function(shapePath, index2) {
      var nextIndex = (index2 + 1) % shapePath.length();
      return new PolynomialBezier(shapePath.v[nextIndex], shapePath.i[nextIndex], shapePath.o[index2], shapePath.v[index2], true);
    };
    function crossProduct(a, b) {
      return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    }
    function lineIntersection(start1, end1, start2, end2) {
      var v1 = [start1[0], start1[1], 1];
      var v2 = [end1[0], end1[1], 1];
      var v3 = [start2[0], start2[1], 1];
      var v4 = [end2[0], end2[1], 1];
      var r = crossProduct(crossProduct(v1, v2), crossProduct(v3, v4));
      if (floatZero(r[2])) return null;
      return [r[0] / r[2], r[1] / r[2]];
    }
    function polarOffset(p, angle, length2) {
      return [p[0] + Math.cos(angle) * length2, p[1] - Math.sin(angle) * length2];
    }
    function pointDistance(p1, p2) {
      return Math.hypot(p1[0] - p2[0], p1[1] - p2[1]);
    }
    function pointEqual(p1, p2) {
      return floatEqual(p1[0], p2[0]) && floatEqual(p1[1], p2[1]);
    }
    function ZigZagModifier() {
    }
    extendPrototype([ShapeModifier], ZigZagModifier);
    ZigZagModifier.prototype.initModifierProperties = function(elem2, data2) {
      this.getValue = this.processKeys;
      this.amplitude = PropertyFactory.getProp(elem2, data2.s, 0, null, this);
      this.frequency = PropertyFactory.getProp(elem2, data2.r, 0, null, this);
      this.pointsType = PropertyFactory.getProp(elem2, data2.pt, 0, null, this);
      this._isAnimated = this.amplitude.effectsSequence.length !== 0 || this.frequency.effectsSequence.length !== 0 || this.pointsType.effectsSequence.length !== 0;
    };
    function setPoint(outputBezier, point, angle, direction, amplitude, outAmplitude, inAmplitude) {
      var angO = angle - Math.PI / 2;
      var angI = angle + Math.PI / 2;
      var px = point[0] + Math.cos(angle) * direction * amplitude;
      var py = point[1] - Math.sin(angle) * direction * amplitude;
      outputBezier.setTripleAt(px, py, px + Math.cos(angO) * outAmplitude, py - Math.sin(angO) * outAmplitude, px + Math.cos(angI) * inAmplitude, py - Math.sin(angI) * inAmplitude, outputBezier.length());
    }
    function getPerpendicularVector(pt1, pt2) {
      var vector = [pt2[0] - pt1[0], pt2[1] - pt1[1]];
      var rot = -Math.PI * 0.5;
      var rotatedVector = [Math.cos(rot) * vector[0] - Math.sin(rot) * vector[1], Math.sin(rot) * vector[0] + Math.cos(rot) * vector[1]];
      return rotatedVector;
    }
    function getProjectingAngle(path, cur) {
      var prevIndex = cur === 0 ? path.length() - 1 : cur - 1;
      var nextIndex = (cur + 1) % path.length();
      var prevPoint = path.v[prevIndex];
      var nextPoint = path.v[nextIndex];
      var pVector = getPerpendicularVector(prevPoint, nextPoint);
      return Math.atan2(0, 1) - Math.atan2(pVector[1], pVector[0]);
    }
    function zigZagCorner(outputBezier, path, cur, amplitude, frequency, pointType, direction) {
      var angle = getProjectingAngle(path, cur);
      var point = path.v[cur % path._length];
      var prevPoint = path.v[cur === 0 ? path._length - 1 : cur - 1];
      var nextPoint = path.v[(cur + 1) % path._length];
      var prevDist = pointType === 2 ? Math.sqrt(Math.pow(point[0] - prevPoint[0], 2) + Math.pow(point[1] - prevPoint[1], 2)) : 0;
      var nextDist = pointType === 2 ? Math.sqrt(Math.pow(point[0] - nextPoint[0], 2) + Math.pow(point[1] - nextPoint[1], 2)) : 0;
      setPoint(outputBezier, path.v[cur % path._length], angle, direction, amplitude, nextDist / ((frequency + 1) * 2), prevDist / ((frequency + 1) * 2));
    }
    function zigZagSegment(outputBezier, segment, amplitude, frequency, pointType, direction) {
      for (var i = 0; i < frequency; i += 1) {
        var t = (i + 1) / (frequency + 1);
        var dist = pointType === 2 ? Math.sqrt(Math.pow(segment.points[3][0] - segment.points[0][0], 2) + Math.pow(segment.points[3][1] - segment.points[0][1], 2)) : 0;
        var angle = segment.normalAngle(t);
        var point = segment.point(t);
        setPoint(outputBezier, point, angle, direction, amplitude, dist / ((frequency + 1) * 2), dist / ((frequency + 1) * 2));
        direction = -direction;
      }
      return direction;
    }
    ZigZagModifier.prototype.processPath = function(path, amplitude, frequency, pointType) {
      var count = path._length;
      var clonedPath = shapePool.newElement();
      clonedPath.c = path.c;
      if (!path.c) {
        count -= 1;
      }
      if (count === 0) return clonedPath;
      var direction = -1;
      var segment = PolynomialBezier.shapeSegment(path, 0);
      zigZagCorner(clonedPath, path, 0, amplitude, frequency, pointType, direction);
      for (var i = 0; i < count; i += 1) {
        direction = zigZagSegment(clonedPath, segment, amplitude, frequency, pointType, -direction);
        if (i === count - 1 && !path.c) {
          segment = null;
        } else {
          segment = PolynomialBezier.shapeSegment(path, (i + 1) % count);
        }
        zigZagCorner(clonedPath, path, i + 1, amplitude, frequency, pointType, direction);
      }
      return clonedPath;
    };
    ZigZagModifier.prototype.processShapes = function(_isFirstFrame) {
      var shapePaths;
      var i;
      var len = this.shapes.length;
      var j;
      var jLen;
      var amplitude = this.amplitude.v;
      var frequency = Math.max(0, Math.round(this.frequency.v));
      var pointType = this.pointsType.v;
      if (amplitude !== 0) {
        var shapeData;
        var localShapeCollection;
        for (i = 0; i < len; i += 1) {
          shapeData = this.shapes[i];
          localShapeCollection = shapeData.localShapeCollection;
          if (!(!shapeData.shape._mdf && !this._mdf && !_isFirstFrame)) {
            localShapeCollection.releaseShapes();
            shapeData.shape._mdf = true;
            shapePaths = shapeData.shape.paths.shapes;
            jLen = shapeData.shape.paths._length;
            for (j = 0; j < jLen; j += 1) {
              localShapeCollection.addShape(this.processPath(shapePaths[j], amplitude, frequency, pointType));
            }
          }
          shapeData.shape.paths = shapeData.localShapeCollection;
        }
      }
      if (!this.dynamicProperties.length) {
        this._mdf = false;
      }
    };
    function linearOffset(p1, p2, amount) {
      var angle = Math.atan2(p2[0] - p1[0], p2[1] - p1[1]);
      return [polarOffset(p1, angle, amount), polarOffset(p2, angle, amount)];
    }
    function offsetSegment(segment, amount) {
      var p0;
      var p1a;
      var p1b;
      var p2b;
      var p2a;
      var p3;
      var e;
      e = linearOffset(segment.points[0], segment.points[1], amount);
      p0 = e[0];
      p1a = e[1];
      e = linearOffset(segment.points[1], segment.points[2], amount);
      p1b = e[0];
      p2b = e[1];
      e = linearOffset(segment.points[2], segment.points[3], amount);
      p2a = e[0];
      p3 = e[1];
      var p1 = lineIntersection(p0, p1a, p1b, p2b);
      if (p1 === null) p1 = p1a;
      var p2 = lineIntersection(p2a, p3, p1b, p2b);
      if (p2 === null) p2 = p2a;
      return new PolynomialBezier(p0, p1, p2, p3);
    }
    function joinLines(outputBezier, seg1, seg2, lineJoin, miterLimit) {
      var p0 = seg1.points[3];
      var p1 = seg2.points[0];
      if (lineJoin === 3) return p0;
      if (pointEqual(p0, p1)) return p0;
      if (lineJoin === 2) {
        var angleOut = -seg1.tangentAngle(1);
        var angleIn = -seg2.tangentAngle(0) + Math.PI;
        var center = lineIntersection(p0, polarOffset(p0, angleOut + Math.PI / 2, 100), p1, polarOffset(p1, angleOut + Math.PI / 2, 100));
        var radius = center ? pointDistance(center, p0) : pointDistance(p0, p1) / 2;
        var tan = polarOffset(p0, angleOut, 2 * radius * roundCorner);
        outputBezier.setXYAt(tan[0], tan[1], "o", outputBezier.length() - 1);
        tan = polarOffset(p1, angleIn, 2 * radius * roundCorner);
        outputBezier.setTripleAt(p1[0], p1[1], p1[0], p1[1], tan[0], tan[1], outputBezier.length());
        return p1;
      }
      var t0 = pointEqual(p0, seg1.points[2]) ? seg1.points[0] : seg1.points[2];
      var t1 = pointEqual(p1, seg2.points[1]) ? seg2.points[3] : seg2.points[1];
      var intersection = lineIntersection(t0, p0, p1, t1);
      if (intersection && pointDistance(intersection, p0) < miterLimit) {
        outputBezier.setTripleAt(intersection[0], intersection[1], intersection[0], intersection[1], intersection[0], intersection[1], outputBezier.length());
        return intersection;
      }
      return p0;
    }
    function getIntersection(a, b) {
      var intersect = a.intersections(b);
      if (intersect.length && floatEqual(intersect[0][0], 1)) intersect.shift();
      if (intersect.length) return intersect[0];
      return null;
    }
    function pruneSegmentIntersection(a, b) {
      var outa = a.slice();
      var outb = b.slice();
      var intersect = getIntersection(a[a.length - 1], b[0]);
      if (intersect) {
        outa[a.length - 1] = a[a.length - 1].split(intersect[0])[0];
        outb[0] = b[0].split(intersect[1])[1];
      }
      if (a.length > 1 && b.length > 1) {
        intersect = getIntersection(a[0], b[b.length - 1]);
        if (intersect) {
          return [[a[0].split(intersect[0])[0]], [b[b.length - 1].split(intersect[1])[1]]];
        }
      }
      return [outa, outb];
    }
    function pruneIntersections(segments) {
      var e;
      for (var i = 1; i < segments.length; i += 1) {
        e = pruneSegmentIntersection(segments[i - 1], segments[i]);
        segments[i - 1] = e[0];
        segments[i] = e[1];
      }
      if (segments.length > 1) {
        e = pruneSegmentIntersection(segments[segments.length - 1], segments[0]);
        segments[segments.length - 1] = e[0];
        segments[0] = e[1];
      }
      return segments;
    }
    function offsetSegmentSplit(segment, amount) {
      var flex = segment.inflectionPoints();
      var left;
      var right;
      var split;
      var mid;
      if (flex.length === 0) {
        return [offsetSegment(segment, amount)];
      }
      if (flex.length === 1 || floatEqual(flex[1], 1)) {
        split = segment.split(flex[0]);
        left = split[0];
        right = split[1];
        return [offsetSegment(left, amount), offsetSegment(right, amount)];
      }
      split = segment.split(flex[0]);
      left = split[0];
      var t = (flex[1] - flex[0]) / (1 - flex[0]);
      split = split[1].split(t);
      mid = split[0];
      right = split[1];
      return [offsetSegment(left, amount), offsetSegment(mid, amount), offsetSegment(right, amount)];
    }
    function OffsetPathModifier() {
    }
    extendPrototype([ShapeModifier], OffsetPathModifier);
    OffsetPathModifier.prototype.initModifierProperties = function(elem2, data2) {
      this.getValue = this.processKeys;
      this.amount = PropertyFactory.getProp(elem2, data2.a, 0, null, this);
      this.miterLimit = PropertyFactory.getProp(elem2, data2.ml, 0, null, this);
      this.lineJoin = data2.lj;
      this._isAnimated = this.amount.effectsSequence.length !== 0;
    };
    OffsetPathModifier.prototype.processPath = function(inputBezier, amount, lineJoin, miterLimit) {
      var outputBezier = shapePool.newElement();
      outputBezier.c = inputBezier.c;
      var count = inputBezier.length();
      if (!inputBezier.c) {
        count -= 1;
      }
      var i;
      var j;
      var segment;
      var multiSegments = [];
      for (i = 0; i < count; i += 1) {
        segment = PolynomialBezier.shapeSegment(inputBezier, i);
        multiSegments.push(offsetSegmentSplit(segment, amount));
      }
      if (!inputBezier.c) {
        for (i = count - 1; i >= 0; i -= 1) {
          segment = PolynomialBezier.shapeSegmentInverted(inputBezier, i);
          multiSegments.push(offsetSegmentSplit(segment, amount));
        }
      }
      multiSegments = pruneIntersections(multiSegments);
      var lastPoint = null;
      var lastSeg = null;
      for (i = 0; i < multiSegments.length; i += 1) {
        var multiSegment = multiSegments[i];
        if (lastSeg) lastPoint = joinLines(outputBezier, lastSeg, multiSegment[0], lineJoin, miterLimit);
        lastSeg = multiSegment[multiSegment.length - 1];
        for (j = 0; j < multiSegment.length; j += 1) {
          segment = multiSegment[j];
          if (lastPoint && pointEqual(segment.points[0], lastPoint)) {
            outputBezier.setXYAt(segment.points[1][0], segment.points[1][1], "o", outputBezier.length() - 1);
          } else {
            outputBezier.setTripleAt(segment.points[0][0], segment.points[0][1], segment.points[1][0], segment.points[1][1], segment.points[0][0], segment.points[0][1], outputBezier.length());
          }
          outputBezier.setTripleAt(segment.points[3][0], segment.points[3][1], segment.points[3][0], segment.points[3][1], segment.points[2][0], segment.points[2][1], outputBezier.length());
          lastPoint = segment.points[3];
        }
      }
      if (multiSegments.length) joinLines(outputBezier, lastSeg, multiSegments[0][0], lineJoin, miterLimit);
      return outputBezier;
    };
    OffsetPathModifier.prototype.processShapes = function(_isFirstFrame) {
      var shapePaths;
      var i;
      var len = this.shapes.length;
      var j;
      var jLen;
      var amount = this.amount.v;
      var miterLimit = this.miterLimit.v;
      var lineJoin = this.lineJoin;
      if (amount !== 0) {
        var shapeData;
        var localShapeCollection;
        for (i = 0; i < len; i += 1) {
          shapeData = this.shapes[i];
          localShapeCollection = shapeData.localShapeCollection;
          if (!(!shapeData.shape._mdf && !this._mdf && !_isFirstFrame)) {
            localShapeCollection.releaseShapes();
            shapeData.shape._mdf = true;
            shapePaths = shapeData.shape.paths.shapes;
            jLen = shapeData.shape.paths._length;
            for (j = 0; j < jLen; j += 1) {
              localShapeCollection.addShape(this.processPath(shapePaths[j], amount, lineJoin, miterLimit));
            }
          }
          shapeData.shape.paths = shapeData.localShapeCollection;
        }
      }
      if (!this.dynamicProperties.length) {
        this._mdf = false;
      }
    };
    function getFontProperties(fontData) {
      var styles = fontData.fStyle ? fontData.fStyle.split(" ") : [];
      var fWeight = "normal";
      var fStyle = "normal";
      var len = styles.length;
      var styleName;
      for (var i = 0; i < len; i += 1) {
        styleName = styles[i].toLowerCase();
        switch (styleName) {
          case "italic":
            fStyle = "italic";
            break;
          case "bold":
            fWeight = "700";
            break;
          case "black":
            fWeight = "900";
            break;
          case "medium":
            fWeight = "500";
            break;
          case "regular":
          case "normal":
            fWeight = "400";
            break;
          case "light":
          case "thin":
            fWeight = "200";
            break;
        }
      }
      return {
        style: fStyle,
        weight: fontData.fWeight || fWeight
      };
    }
    var FontManager = function() {
      var maxWaitingTime = 5e3;
      var emptyChar = {
        w: 0,
        size: 0,
        shapes: [],
        data: {
          shapes: []
        }
      };
      var combinedCharacters = [];
      combinedCharacters = combinedCharacters.concat([2304, 2305, 2306, 2307, 2362, 2363, 2364, 2364, 2366, 2367, 2368, 2369, 2370, 2371, 2372, 2373, 2374, 2375, 2376, 2377, 2378, 2379, 2380, 2381, 2382, 2383, 2387, 2388, 2389, 2390, 2391, 2402, 2403]);
      var BLACK_FLAG_CODE_POINT = 127988;
      var CANCEL_TAG_CODE_POINT = 917631;
      var A_TAG_CODE_POINT = 917601;
      var Z_TAG_CODE_POINT = 917626;
      var VARIATION_SELECTOR_16_CODE_POINT = 65039;
      var ZERO_WIDTH_JOINER_CODE_POINT = 8205;
      var REGIONAL_CHARACTER_A_CODE_POINT = 127462;
      var REGIONAL_CHARACTER_Z_CODE_POINT = 127487;
      var surrogateModifiers = ["d83cdffb", "d83cdffc", "d83cdffd", "d83cdffe", "d83cdfff"];
      function trimFontOptions(font) {
        var familyArray = font.split(",");
        var i;
        var len = familyArray.length;
        var enabledFamilies = [];
        for (i = 0; i < len; i += 1) {
          if (familyArray[i] !== "sans-serif" && familyArray[i] !== "monospace") {
            enabledFamilies.push(familyArray[i]);
          }
        }
        return enabledFamilies.join(",");
      }
      function setUpNode(font, family) {
        var parentNode = createTag("span");
        parentNode.setAttribute("aria-hidden", true);
        parentNode.style.fontFamily = family;
        var node = createTag("span");
        node.innerText = "giItT1WQy@!-/#";
        parentNode.style.position = "absolute";
        parentNode.style.left = "-10000px";
        parentNode.style.top = "-10000px";
        parentNode.style.fontSize = "300px";
        parentNode.style.fontVariant = "normal";
        parentNode.style.fontStyle = "normal";
        parentNode.style.fontWeight = "normal";
        parentNode.style.letterSpacing = "0";
        parentNode.appendChild(node);
        document.body.appendChild(parentNode);
        var width2 = node.offsetWidth;
        node.style.fontFamily = trimFontOptions(font) + ", " + family;
        return {
          node,
          w: width2,
          parent: parentNode
        };
      }
      function checkLoadedFonts() {
        var i;
        var len = this.fonts.length;
        var node;
        var w2;
        var loadedCount = len;
        for (i = 0; i < len; i += 1) {
          if (this.fonts[i].loaded) {
            loadedCount -= 1;
          } else if (this.fonts[i].fOrigin === "n" || this.fonts[i].origin === 0) {
            this.fonts[i].loaded = true;
          } else {
            node = this.fonts[i].monoCase.node;
            w2 = this.fonts[i].monoCase.w;
            if (node.offsetWidth !== w2) {
              loadedCount -= 1;
              this.fonts[i].loaded = true;
            } else {
              node = this.fonts[i].sansCase.node;
              w2 = this.fonts[i].sansCase.w;
              if (node.offsetWidth !== w2) {
                loadedCount -= 1;
                this.fonts[i].loaded = true;
              }
            }
            if (this.fonts[i].loaded) {
              this.fonts[i].sansCase.parent.parentNode.removeChild(this.fonts[i].sansCase.parent);
              this.fonts[i].monoCase.parent.parentNode.removeChild(this.fonts[i].monoCase.parent);
            }
          }
        }
        if (loadedCount !== 0 && Date.now() - this.initTime < maxWaitingTime) {
          setTimeout(this.checkLoadedFontsBinded, 20);
        } else {
          setTimeout(this.setIsLoadedBinded, 10);
        }
      }
      function createHelper(fontData, def) {
        var engine = document.body && def ? "svg" : "canvas";
        var helper;
        var fontProps = getFontProperties(fontData);
        if (engine === "svg") {
          var tHelper = createNS("text");
          tHelper.style.fontSize = "100px";
          tHelper.setAttribute("font-family", fontData.fFamily);
          tHelper.setAttribute("font-style", fontProps.style);
          tHelper.setAttribute("font-weight", fontProps.weight);
          tHelper.textContent = "1";
          if (fontData.fClass) {
            tHelper.style.fontFamily = "inherit";
            tHelper.setAttribute("class", fontData.fClass);
          } else {
            tHelper.style.fontFamily = fontData.fFamily;
          }
          def.appendChild(tHelper);
          helper = tHelper;
        } else {
          var tCanvasHelper = new OffscreenCanvas(500, 500).getContext("2d");
          tCanvasHelper.font = fontProps.style + " " + fontProps.weight + " 100px " + fontData.fFamily;
          helper = tCanvasHelper;
        }
        function measure(text2) {
          if (engine === "svg") {
            helper.textContent = text2;
            return helper.getComputedTextLength();
          }
          return helper.measureText(text2).width;
        }
        return {
          measureText: measure
        };
      }
      function addFonts(fontData, defs) {
        if (!fontData) {
          this.isLoaded = true;
          return;
        }
        if (this.chars) {
          this.isLoaded = true;
          this.fonts = fontData.list;
          return;
        }
        if (!document.body) {
          this.isLoaded = true;
          fontData.list.forEach(function(data2) {
            data2.helper = createHelper(data2);
            data2.cache = {};
          });
          this.fonts = fontData.list;
          return;
        }
        var fontArr = fontData.list;
        var i;
        var len = fontArr.length;
        var _pendingFonts = len;
        for (i = 0; i < len; i += 1) {
          var shouldLoadFont = true;
          var loadedSelector;
          var j;
          fontArr[i].loaded = false;
          fontArr[i].monoCase = setUpNode(fontArr[i].fFamily, "monospace");
          fontArr[i].sansCase = setUpNode(fontArr[i].fFamily, "sans-serif");
          if (!fontArr[i].fPath) {
            fontArr[i].loaded = true;
            _pendingFonts -= 1;
          } else if (fontArr[i].fOrigin === "p" || fontArr[i].origin === 3) {
            loadedSelector = document.querySelectorAll('style[f-forigin="p"][f-family="' + fontArr[i].fFamily + '"], style[f-origin="3"][f-family="' + fontArr[i].fFamily + '"]');
            if (loadedSelector.length > 0) {
              shouldLoadFont = false;
            }
            if (shouldLoadFont) {
              var s = createTag("style");
              s.setAttribute("f-forigin", fontArr[i].fOrigin);
              s.setAttribute("f-origin", fontArr[i].origin);
              s.setAttribute("f-family", fontArr[i].fFamily);
              s.type = "text/css";
              s.innerText = "@font-face {font-family: " + fontArr[i].fFamily + "; font-style: normal; src: url('" + fontArr[i].fPath + "');}";
              defs.appendChild(s);
            }
          } else if (fontArr[i].fOrigin === "g" || fontArr[i].origin === 1) {
            loadedSelector = document.querySelectorAll('link[f-forigin="g"], link[f-origin="1"]');
            for (j = 0; j < loadedSelector.length; j += 1) {
              if (loadedSelector[j].href.indexOf(fontArr[i].fPath) !== -1) {
                shouldLoadFont = false;
              }
            }
            if (shouldLoadFont) {
              var l = createTag("link");
              l.setAttribute("f-forigin", fontArr[i].fOrigin);
              l.setAttribute("f-origin", fontArr[i].origin);
              l.type = "text/css";
              l.rel = "stylesheet";
              l.href = fontArr[i].fPath;
              document.body.appendChild(l);
            }
          } else if (fontArr[i].fOrigin === "t" || fontArr[i].origin === 2) {
            loadedSelector = document.querySelectorAll('script[f-forigin="t"], script[f-origin="2"]');
            for (j = 0; j < loadedSelector.length; j += 1) {
              if (fontArr[i].fPath === loadedSelector[j].src) {
                shouldLoadFont = false;
              }
            }
            if (shouldLoadFont) {
              var sc = createTag("link");
              sc.setAttribute("f-forigin", fontArr[i].fOrigin);
              sc.setAttribute("f-origin", fontArr[i].origin);
              sc.setAttribute("rel", "stylesheet");
              sc.setAttribute("href", fontArr[i].fPath);
              defs.appendChild(sc);
            }
          }
          fontArr[i].helper = createHelper(fontArr[i], defs);
          fontArr[i].cache = {};
          this.fonts.push(fontArr[i]);
        }
        if (_pendingFonts === 0) {
          this.isLoaded = true;
        } else {
          setTimeout(this.checkLoadedFonts.bind(this), 100);
        }
      }
      function addChars(chars) {
        if (!chars) {
          return;
        }
        if (!this.chars) {
          this.chars = [];
        }
        var i;
        var len = chars.length;
        var j;
        var jLen = this.chars.length;
        var found;
        for (i = 0; i < len; i += 1) {
          j = 0;
          found = false;
          while (j < jLen) {
            if (this.chars[j].style === chars[i].style && this.chars[j].fFamily === chars[i].fFamily && this.chars[j].ch === chars[i].ch) {
              found = true;
            }
            j += 1;
          }
          if (!found) {
            this.chars.push(chars[i]);
            jLen += 1;
          }
        }
      }
      function getCharData(_char, style, font) {
        var i = 0;
        var len = this.chars.length;
        while (i < len) {
          if (this.chars[i].ch === _char && this.chars[i].style === style && this.chars[i].fFamily === font) {
            return this.chars[i];
          }
          i += 1;
        }
        if ((typeof _char === "string" && _char.charCodeAt(0) !== 13 || !_char) && console && console.warn && !this._warned) {
          this._warned = true;
          console.warn("Missing character from exported characters list: ", _char, style, font);
        }
        return emptyChar;
      }
      function measureText(_char2, fontName, size) {
        var fontData = this.getFontByName(fontName);
        var index2 = _char2;
        if (!fontData.cache[index2]) {
          var tHelper = fontData.helper;
          if (_char2 === " ") {
            var doubleSize = tHelper.measureText("|" + _char2 + "|");
            var singleSize = tHelper.measureText("||");
            fontData.cache[index2] = (doubleSize - singleSize) / 100;
          } else {
            fontData.cache[index2] = tHelper.measureText(_char2) / 100;
          }
        }
        return fontData.cache[index2] * size;
      }
      function getFontByName(name2) {
        var i = 0;
        var len = this.fonts.length;
        while (i < len) {
          if (this.fonts[i].fName === name2) {
            return this.fonts[i];
          }
          i += 1;
        }
        return this.fonts[0];
      }
      function getCodePoint(string) {
        var codePoint = 0;
        var first = string.charCodeAt(0);
        if (first >= 55296 && first <= 56319) {
          var second = string.charCodeAt(1);
          if (second >= 56320 && second <= 57343) {
            codePoint = (first - 55296) * 1024 + second - 56320 + 65536;
          }
        }
        return codePoint;
      }
      function isModifier(firstCharCode, secondCharCode) {
        var sum2 = firstCharCode.toString(16) + secondCharCode.toString(16);
        return surrogateModifiers.indexOf(sum2) !== -1;
      }
      function isZeroWidthJoiner(charCode) {
        return charCode === ZERO_WIDTH_JOINER_CODE_POINT;
      }
      function isVariationSelector(charCode) {
        return charCode === VARIATION_SELECTOR_16_CODE_POINT;
      }
      function isRegionalCode(string) {
        var codePoint = getCodePoint(string);
        if (codePoint >= REGIONAL_CHARACTER_A_CODE_POINT && codePoint <= REGIONAL_CHARACTER_Z_CODE_POINT) {
          return true;
        }
        return false;
      }
      function isFlagEmoji(string) {
        return isRegionalCode(string.substr(0, 2)) && isRegionalCode(string.substr(2, 2));
      }
      function isCombinedCharacter(_char3) {
        return combinedCharacters.indexOf(_char3) !== -1;
      }
      function isRegionalFlag(text2, index2) {
        var codePoint = getCodePoint(text2.substr(index2, 2));
        if (codePoint !== BLACK_FLAG_CODE_POINT) {
          return false;
        }
        var count = 0;
        index2 += 2;
        while (count < 5) {
          codePoint = getCodePoint(text2.substr(index2, 2));
          if (codePoint < A_TAG_CODE_POINT || codePoint > Z_TAG_CODE_POINT) {
            return false;
          }
          count += 1;
          index2 += 2;
        }
        return getCodePoint(text2.substr(index2, 2)) === CANCEL_TAG_CODE_POINT;
      }
      function setIsLoaded() {
        this.isLoaded = true;
      }
      var Font = function Font2() {
        this.fonts = [];
        this.chars = null;
        this.typekitLoaded = 0;
        this.isLoaded = false;
        this._warned = false;
        this.initTime = Date.now();
        this.setIsLoadedBinded = this.setIsLoaded.bind(this);
        this.checkLoadedFontsBinded = this.checkLoadedFonts.bind(this);
      };
      Font.isModifier = isModifier;
      Font.isZeroWidthJoiner = isZeroWidthJoiner;
      Font.isFlagEmoji = isFlagEmoji;
      Font.isRegionalCode = isRegionalCode;
      Font.isCombinedCharacter = isCombinedCharacter;
      Font.isRegionalFlag = isRegionalFlag;
      Font.isVariationSelector = isVariationSelector;
      Font.BLACK_FLAG_CODE_POINT = BLACK_FLAG_CODE_POINT;
      var fontPrototype = {
        addChars,
        addFonts,
        getCharData,
        getFontByName,
        measureText,
        checkLoadedFonts,
        setIsLoaded
      };
      Font.prototype = fontPrototype;
      return Font;
    }();
    function SlotManager(animationData2) {
      this.animationData = animationData2;
    }
    SlotManager.prototype.getProp = function(data2) {
      if (this.animationData.slots && this.animationData.slots[data2.sid]) {
        return Object.assign(data2, this.animationData.slots[data2.sid].p);
      }
      return data2;
    };
    function slotFactory(animationData2) {
      return new SlotManager(animationData2);
    }
    function RenderableElement() {
    }
    RenderableElement.prototype = {
      initRenderable: function initRenderable() {
        this.isInRange = false;
        this.hidden = false;
        this.isTransparent = false;
        this.renderableComponents = [];
      },
      addRenderableComponent: function addRenderableComponent(component) {
        if (this.renderableComponents.indexOf(component) === -1) {
          this.renderableComponents.push(component);
        }
      },
      removeRenderableComponent: function removeRenderableComponent(component) {
        if (this.renderableComponents.indexOf(component) !== -1) {
          this.renderableComponents.splice(this.renderableComponents.indexOf(component), 1);
        }
      },
      prepareRenderableFrame: function prepareRenderableFrame(num) {
        this.checkLayerLimits(num);
      },
      checkTransparency: function checkTransparency() {
        if (this.finalTransform.mProp.o.v <= 0) {
          if (!this.isTransparent && this.globalData.renderConfig.hideOnTransparent) {
            this.isTransparent = true;
            this.hide();
          }
        } else if (this.isTransparent) {
          this.isTransparent = false;
          this.show();
        }
      },
      /**
         * @function
         * Initializes frame related properties.
         *
         * @param {number} num
         * current frame number in Layer's time
         *
         */
      checkLayerLimits: function checkLayerLimits(num) {
        if (this.data.ip - this.data.st <= num && this.data.op - this.data.st > num) {
          if (this.isInRange !== true) {
            this.globalData._mdf = true;
            this._mdf = true;
            this.isInRange = true;
            this.show();
          }
        } else if (this.isInRange !== false) {
          this.globalData._mdf = true;
          this.isInRange = false;
          this.hide();
        }
      },
      renderRenderable: function renderRenderable() {
        var i;
        var len = this.renderableComponents.length;
        for (i = 0; i < len; i += 1) {
          this.renderableComponents[i].renderFrame(this._isFirstFrame);
        }
      },
      sourceRectAtTime: function sourceRectAtTime2() {
        return {
          top: 0,
          left: 0,
          width: 100,
          height: 100
        };
      },
      getLayerSize: function getLayerSize() {
        if (this.data.ty === 5) {
          return {
            w: this.data.textData.width,
            h: this.data.textData.height
          };
        }
        return {
          w: this.data.width,
          h: this.data.height
        };
      }
    };
    var getBlendMode = /* @__PURE__ */ function() {
      var blendModeEnums = {
        0: "source-over",
        1: "multiply",
        2: "screen",
        3: "overlay",
        4: "darken",
        5: "lighten",
        6: "color-dodge",
        7: "color-burn",
        8: "hard-light",
        9: "soft-light",
        10: "difference",
        11: "exclusion",
        12: "hue",
        13: "saturation",
        14: "color",
        15: "luminosity"
      };
      return function(mode) {
        return blendModeEnums[mode] || "";
      };
    }();
    function SliderEffect(data2, elem2, container) {
      this.p = PropertyFactory.getProp(elem2, data2.v, 0, 0, container);
    }
    function AngleEffect(data2, elem2, container) {
      this.p = PropertyFactory.getProp(elem2, data2.v, 0, 0, container);
    }
    function ColorEffect(data2, elem2, container) {
      this.p = PropertyFactory.getProp(elem2, data2.v, 1, 0, container);
    }
    function PointEffect(data2, elem2, container) {
      this.p = PropertyFactory.getProp(elem2, data2.v, 1, 0, container);
    }
    function LayerIndexEffect(data2, elem2, container) {
      this.p = PropertyFactory.getProp(elem2, data2.v, 0, 0, container);
    }
    function MaskIndexEffect(data2, elem2, container) {
      this.p = PropertyFactory.getProp(elem2, data2.v, 0, 0, container);
    }
    function CheckboxEffect(data2, elem2, container) {
      this.p = PropertyFactory.getProp(elem2, data2.v, 0, 0, container);
    }
    function NoValueEffect() {
      this.p = {};
    }
    function EffectsManager(data2, element) {
      var effects = data2.ef || [];
      this.effectElements = [];
      var i;
      var len = effects.length;
      var effectItem;
      for (i = 0; i < len; i += 1) {
        effectItem = new GroupEffect(effects[i], element);
        this.effectElements.push(effectItem);
      }
    }
    function GroupEffect(data2, element) {
      this.init(data2, element);
    }
    extendPrototype([DynamicPropertyContainer], GroupEffect);
    GroupEffect.prototype.getValue = GroupEffect.prototype.iterateDynamicProperties;
    GroupEffect.prototype.init = function(data2, element) {
      this.data = data2;
      this.effectElements = [];
      this.initDynamicPropertyContainer(element);
      var i;
      var len = this.data.ef.length;
      var eff;
      var effects = this.data.ef;
      for (i = 0; i < len; i += 1) {
        eff = null;
        switch (effects[i].ty) {
          case 0:
            eff = new SliderEffect(effects[i], element, this);
            break;
          case 1:
            eff = new AngleEffect(effects[i], element, this);
            break;
          case 2:
            eff = new ColorEffect(effects[i], element, this);
            break;
          case 3:
            eff = new PointEffect(effects[i], element, this);
            break;
          case 4:
          case 7:
            eff = new CheckboxEffect(effects[i], element, this);
            break;
          case 10:
            eff = new LayerIndexEffect(effects[i], element, this);
            break;
          case 11:
            eff = new MaskIndexEffect(effects[i], element, this);
            break;
          case 5:
            eff = new EffectsManager(effects[i], element);
            break;
          default:
            eff = new NoValueEffect(effects[i]);
            break;
        }
        if (eff) {
          this.effectElements.push(eff);
        }
      }
    };
    function BaseElement() {
    }
    BaseElement.prototype = {
      checkMasks: function checkMasks() {
        if (!this.data.hasMask) {
          return false;
        }
        var i = 0;
        var len = this.data.masksProperties.length;
        while (i < len) {
          if (this.data.masksProperties[i].mode !== "n" && this.data.masksProperties[i].cl !== false) {
            return true;
          }
          i += 1;
        }
        return false;
      },
      initExpressions: function initExpressions() {
        var expressionsInterfaces2 = getExpressionInterfaces();
        if (!expressionsInterfaces2) {
          return;
        }
        var LayerExpressionInterface2 = expressionsInterfaces2("layer");
        var EffectsExpressionInterface2 = expressionsInterfaces2("effects");
        var ShapeExpressionInterface2 = expressionsInterfaces2("shape");
        var TextExpressionInterface2 = expressionsInterfaces2("text");
        var CompExpressionInterface2 = expressionsInterfaces2("comp");
        this.layerInterface = LayerExpressionInterface2(this);
        if (this.data.hasMask && this.maskManager) {
          this.layerInterface.registerMaskInterface(this.maskManager);
        }
        var effectsInterface = EffectsExpressionInterface2.createEffectsInterface(this, this.layerInterface);
        this.layerInterface.registerEffectsInterface(effectsInterface);
        if (this.data.ty === 0 || this.data.xt) {
          this.compInterface = CompExpressionInterface2(this);
        } else if (this.data.ty === 4) {
          this.layerInterface.shapeInterface = ShapeExpressionInterface2(this.shapesData, this.itemsData, this.layerInterface);
          this.layerInterface.content = this.layerInterface.shapeInterface;
        } else if (this.data.ty === 5) {
          this.layerInterface.textInterface = TextExpressionInterface2(this);
          this.layerInterface.text = this.layerInterface.textInterface;
        }
      },
      setBlendMode: function setBlendMode() {
        var blendModeValue = getBlendMode(this.data.bm);
        var elem2 = this.baseElement || this.layerElement;
        elem2.style["mix-blend-mode"] = blendModeValue;
      },
      initBaseData: function initBaseData(data2, globalData2, comp2) {
        this.globalData = globalData2;
        this.comp = comp2;
        this.data = data2;
        this.layerId = createElementID();
        if (!this.data.sr) {
          this.data.sr = 1;
        }
        this.effectsManager = new EffectsManager(this.data, this, this.dynamicProperties);
      },
      getType: function getType() {
        return this.type;
      },
      sourceRectAtTime: function sourceRectAtTime2() {
      }
    };
    function FrameElement() {
    }
    FrameElement.prototype = {
      /**
         * @function
         * Initializes frame related properties.
         *
         */
      initFrame: function initFrame2() {
        this._isFirstFrame = false;
        this.dynamicProperties = [];
        this._mdf = false;
      },
      /**
         * @function
         * Calculates all dynamic values
         *
         * @param {number} num
         * current frame number in Layer's time
         * @param {boolean} isVisible
         * if layers is currently in range
         *
         */
      prepareProperties: function prepareProperties(num, isVisible) {
        var i;
        var len = this.dynamicProperties.length;
        for (i = 0; i < len; i += 1) {
          if (isVisible || this._isParent && this.dynamicProperties[i].propType === "transform") {
            this.dynamicProperties[i].getValue();
            if (this.dynamicProperties[i]._mdf) {
              this.globalData._mdf = true;
              this._mdf = true;
            }
          }
        }
      },
      addDynamicProperty: function addDynamicProperty(prop) {
        if (this.dynamicProperties.indexOf(prop) === -1) {
          this.dynamicProperties.push(prop);
        }
      }
    };
    function FootageElement(data2, globalData2, comp2) {
      this.initFrame();
      this.initRenderable();
      this.assetData = globalData2.getAssetData(data2.refId);
      this.footageData = globalData2.imageLoader.getAsset(this.assetData);
      this.initBaseData(data2, globalData2, comp2);
    }
    FootageElement.prototype.prepareFrame = function() {
    };
    extendPrototype([RenderableElement, BaseElement, FrameElement], FootageElement);
    FootageElement.prototype.getBaseElement = function() {
      return null;
    };
    FootageElement.prototype.renderFrame = function() {
    };
    FootageElement.prototype.destroy = function() {
    };
    FootageElement.prototype.initExpressions = function() {
      var expressionsInterfaces2 = getExpressionInterfaces();
      if (!expressionsInterfaces2) {
        return;
      }
      var FootageInterface2 = expressionsInterfaces2("footage");
      this.layerInterface = FootageInterface2(this);
    };
    FootageElement.prototype.getFootageData = function() {
      return this.footageData;
    };
    function AudioElement(data2, globalData2, comp2) {
      this.initFrame();
      this.initRenderable();
      this.assetData = globalData2.getAssetData(data2.refId);
      this.initBaseData(data2, globalData2, comp2);
      this._isPlaying = false;
      this._canPlay = false;
      var assetPath = this.globalData.getAssetsPath(this.assetData);
      this.audio = this.globalData.audioController.createAudio(assetPath);
      this._currentTime = 0;
      this.globalData.audioController.addAudio(this);
      this._volumeMultiplier = 1;
      this._volume = 1;
      this._previousVolume = null;
      this.tm = data2.tm ? PropertyFactory.getProp(this, data2.tm, 0, globalData2.frameRate, this) : {
        _placeholder: true
      };
      this.lv = PropertyFactory.getProp(this, data2.au && data2.au.lv ? data2.au.lv : {
        k: [100]
      }, 1, 0.01, this);
    }
    AudioElement.prototype.prepareFrame = function(num) {
      this.prepareRenderableFrame(num, true);
      this.prepareProperties(num, true);
      if (!this.tm._placeholder) {
        var timeRemapped = this.tm.v;
        this._currentTime = timeRemapped;
      } else {
        this._currentTime = num / this.data.sr;
      }
      this._volume = this.lv.v[0];
      var totalVolume = this._volume * this._volumeMultiplier;
      if (this._previousVolume !== totalVolume) {
        this._previousVolume = totalVolume;
        this.audio.volume(totalVolume);
      }
    };
    extendPrototype([RenderableElement, BaseElement, FrameElement], AudioElement);
    AudioElement.prototype.renderFrame = function() {
      if (this.isInRange && this._canPlay) {
        if (!this._isPlaying) {
          this.audio.play();
          this.audio.seek(this._currentTime / this.globalData.frameRate);
          this._isPlaying = true;
        } else if (!this.audio.playing() || Math.abs(this._currentTime / this.globalData.frameRate - this.audio.seek()) > 0.1) {
          this.audio.seek(this._currentTime / this.globalData.frameRate);
        }
      }
    };
    AudioElement.prototype.show = function() {
    };
    AudioElement.prototype.hide = function() {
      this.audio.pause();
      this._isPlaying = false;
    };
    AudioElement.prototype.pause = function() {
      this.audio.pause();
      this._isPlaying = false;
      this._canPlay = false;
    };
    AudioElement.prototype.resume = function() {
      this._canPlay = true;
    };
    AudioElement.prototype.setRate = function(rateValue) {
      this.audio.rate(rateValue);
    };
    AudioElement.prototype.volume = function(volumeValue) {
      this._volumeMultiplier = volumeValue;
      this._previousVolume = volumeValue * this._volume;
      this.audio.volume(this._previousVolume);
    };
    AudioElement.prototype.getBaseElement = function() {
      return null;
    };
    AudioElement.prototype.destroy = function() {
    };
    AudioElement.prototype.sourceRectAtTime = function() {
    };
    AudioElement.prototype.initExpressions = function() {
    };
    function BaseRenderer() {
    }
    BaseRenderer.prototype.checkLayers = function(num) {
      var i;
      var len = this.layers.length;
      var data2;
      this.completeLayers = true;
      for (i = len - 1; i >= 0; i -= 1) {
        if (!this.elements[i]) {
          data2 = this.layers[i];
          if (data2.ip - data2.st <= num - this.layers[i].st && data2.op - data2.st > num - this.layers[i].st) {
            this.buildItem(i);
          }
        }
        this.completeLayers = this.elements[i] ? this.completeLayers : false;
      }
      this.checkPendingElements();
    };
    BaseRenderer.prototype.createItem = function(layer) {
      switch (layer.ty) {
        case 2:
          return this.createImage(layer);
        case 0:
          return this.createComp(layer);
        case 1:
          return this.createSolid(layer);
        case 3:
          return this.createNull(layer);
        case 4:
          return this.createShape(layer);
        case 5:
          return this.createText(layer);
        case 6:
          return this.createAudio(layer);
        case 13:
          return this.createCamera(layer);
        case 15:
          return this.createFootage(layer);
        default:
          return this.createNull(layer);
      }
    };
    BaseRenderer.prototype.createCamera = function() {
      throw new Error("You're using a 3d camera. Try the html renderer.");
    };
    BaseRenderer.prototype.createAudio = function(data2) {
      return new AudioElement(data2, this.globalData, this);
    };
    BaseRenderer.prototype.createFootage = function(data2) {
      return new FootageElement(data2, this.globalData, this);
    };
    BaseRenderer.prototype.buildAllItems = function() {
      var i;
      var len = this.layers.length;
      for (i = 0; i < len; i += 1) {
        this.buildItem(i);
      }
      this.checkPendingElements();
    };
    BaseRenderer.prototype.includeLayers = function(newLayers) {
      this.completeLayers = false;
      var i;
      var len = newLayers.length;
      var j;
      var jLen = this.layers.length;
      for (i = 0; i < len; i += 1) {
        j = 0;
        while (j < jLen) {
          if (this.layers[j].id === newLayers[i].id) {
            this.layers[j] = newLayers[i];
            break;
          }
          j += 1;
        }
      }
    };
    BaseRenderer.prototype.setProjectInterface = function(pInterface) {
      this.globalData.projectInterface = pInterface;
    };
    BaseRenderer.prototype.initItems = function() {
      if (!this.globalData.progressiveLoad) {
        this.buildAllItems();
      }
    };
    BaseRenderer.prototype.buildElementParenting = function(element, parentName, hierarchy) {
      var elements = this.elements;
      var layers2 = this.layers;
      var i = 0;
      var len = layers2.length;
      while (i < len) {
        if (layers2[i].ind == parentName) {
          if (!elements[i] || elements[i] === true) {
            this.buildItem(i);
            this.addPendingElement(element);
          } else {
            hierarchy.push(elements[i]);
            elements[i].setAsParent();
            if (layers2[i].parent !== void 0) {
              this.buildElementParenting(element, layers2[i].parent, hierarchy);
            } else {
              element.setHierarchy(hierarchy);
            }
          }
        }
        i += 1;
      }
    };
    BaseRenderer.prototype.addPendingElement = function(element) {
      this.pendingElements.push(element);
    };
    BaseRenderer.prototype.searchExtraCompositions = function(assets2) {
      var i;
      var len = assets2.length;
      for (i = 0; i < len; i += 1) {
        if (assets2[i].xt) {
          var comp2 = this.createComp(assets2[i]);
          comp2.initExpressions();
          this.globalData.projectInterface.registerComposition(comp2);
        }
      }
    };
    BaseRenderer.prototype.getElementById = function(ind) {
      var i;
      var len = this.elements.length;
      for (i = 0; i < len; i += 1) {
        if (this.elements[i].data.ind === ind) {
          return this.elements[i];
        }
      }
      return null;
    };
    BaseRenderer.prototype.getElementByPath = function(path) {
      var pathValue = path.shift();
      var element;
      if (typeof pathValue === "number") {
        element = this.elements[pathValue];
      } else {
        var i;
        var len = this.elements.length;
        for (i = 0; i < len; i += 1) {
          if (this.elements[i].data.nm === pathValue) {
            element = this.elements[i];
            break;
          }
        }
      }
      if (path.length === 0) {
        return element;
      }
      return element.getElementByPath(path);
    };
    BaseRenderer.prototype.setupGlobalData = function(animData, fontsContainer) {
      this.globalData.fontManager = new FontManager();
      this.globalData.slotManager = slotFactory(animData);
      this.globalData.fontManager.addChars(animData.chars);
      this.globalData.fontManager.addFonts(animData.fonts, fontsContainer);
      this.globalData.getAssetData = this.animationItem.getAssetData.bind(this.animationItem);
      this.globalData.getAssetsPath = this.animationItem.getAssetsPath.bind(this.animationItem);
      this.globalData.imageLoader = this.animationItem.imagePreloader;
      this.globalData.audioController = this.animationItem.audioController;
      this.globalData.frameId = 0;
      this.globalData.frameRate = animData.fr;
      this.globalData.nm = animData.nm;
      this.globalData.compSize = {
        w: animData.w,
        h: animData.h
      };
    };
    var effectTypes = {
      TRANSFORM_EFFECT: "transformEFfect"
    };
    function TransformElement() {
    }
    TransformElement.prototype = {
      initTransform: function initTransform() {
        var mat = new Matrix();
        this.finalTransform = {
          mProp: this.data.ks ? TransformPropertyFactory.getTransformProperty(this, this.data.ks, this) : {
            o: 0
          },
          _matMdf: false,
          _localMatMdf: false,
          _opMdf: false,
          mat,
          localMat: mat,
          localOpacity: 1
        };
        if (this.data.ao) {
          this.finalTransform.mProp.autoOriented = true;
        }
        if (this.data.ty !== 11) ;
      },
      renderTransform: function renderTransform() {
        this.finalTransform._opMdf = this.finalTransform.mProp.o._mdf || this._isFirstFrame;
        this.finalTransform._matMdf = this.finalTransform.mProp._mdf || this._isFirstFrame;
        if (this.hierarchy) {
          var mat;
          var finalMat = this.finalTransform.mat;
          var i = 0;
          var len = this.hierarchy.length;
          if (!this.finalTransform._matMdf) {
            while (i < len) {
              if (this.hierarchy[i].finalTransform.mProp._mdf) {
                this.finalTransform._matMdf = true;
                break;
              }
              i += 1;
            }
          }
          if (this.finalTransform._matMdf) {
            mat = this.finalTransform.mProp.v.props;
            finalMat.cloneFromProps(mat);
            for (i = 0; i < len; i += 1) {
              finalMat.multiply(this.hierarchy[i].finalTransform.mProp.v);
            }
          }
        }
        if (this.finalTransform._matMdf) {
          this.finalTransform._localMatMdf = this.finalTransform._matMdf;
        }
        if (this.finalTransform._opMdf) {
          this.finalTransform.localOpacity = this.finalTransform.mProp.o.v;
        }
      },
      renderLocalTransform: function renderLocalTransform() {
        if (this.localTransforms) {
          var i = 0;
          var len = this.localTransforms.length;
          this.finalTransform._localMatMdf = this.finalTransform._matMdf;
          if (!this.finalTransform._localMatMdf || !this.finalTransform._opMdf) {
            while (i < len) {
              if (this.localTransforms[i]._mdf) {
                this.finalTransform._localMatMdf = true;
              }
              if (this.localTransforms[i]._opMdf && !this.finalTransform._opMdf) {
                this.finalTransform.localOpacity = this.finalTransform.mProp.o.v;
                this.finalTransform._opMdf = true;
              }
              i += 1;
            }
          }
          if (this.finalTransform._localMatMdf) {
            var localMat = this.finalTransform.localMat;
            this.localTransforms[0].matrix.clone(localMat);
            for (i = 1; i < len; i += 1) {
              var lmat = this.localTransforms[i].matrix;
              localMat.multiply(lmat);
            }
            localMat.multiply(this.finalTransform.mat);
          }
          if (this.finalTransform._opMdf) {
            var localOp = this.finalTransform.localOpacity;
            for (i = 0; i < len; i += 1) {
              localOp *= this.localTransforms[i].opacity * 0.01;
            }
            this.finalTransform.localOpacity = localOp;
          }
        }
      },
      searchEffectTransforms: function searchEffectTransforms() {
        if (this.renderableEffectsManager) {
          var transformEffects = this.renderableEffectsManager.getEffects(effectTypes.TRANSFORM_EFFECT);
          if (transformEffects.length) {
            this.localTransforms = [];
            this.finalTransform.localMat = new Matrix();
            var i = 0;
            var len = transformEffects.length;
            for (i = 0; i < len; i += 1) {
              this.localTransforms.push(transformEffects[i]);
            }
          }
        }
      },
      globalToLocal: function globalToLocal(pt) {
        var transforms = [];
        transforms.push(this.finalTransform);
        var flag = true;
        var comp2 = this.comp;
        while (flag) {
          if (comp2.finalTransform) {
            if (comp2.data.hasMask) {
              transforms.splice(0, 0, comp2.finalTransform);
            }
            comp2 = comp2.comp;
          } else {
            flag = false;
          }
        }
        var i;
        var len = transforms.length;
        var ptNew;
        for (i = 0; i < len; i += 1) {
          ptNew = transforms[i].mat.applyToPointArray(0, 0, 0);
          pt = [pt[0] - ptNew[0], pt[1] - ptNew[1], 0];
        }
        return pt;
      },
      mHelper: new Matrix()
    };
    function MaskElement(data2, element, globalData2) {
      this.data = data2;
      this.element = element;
      this.globalData = globalData2;
      this.storedData = [];
      this.masksProperties = this.data.masksProperties || [];
      this.maskElement = null;
      var defs = this.globalData.defs;
      var i;
      var len = this.masksProperties ? this.masksProperties.length : 0;
      this.viewData = createSizedArray(len);
      this.solidPath = "";
      var path;
      var properties = this.masksProperties;
      var count = 0;
      var currentMasks = [];
      var j;
      var jLen;
      var layerId = createElementID();
      var rect;
      var expansor;
      var feMorph;
      var x;
      var maskType = "clipPath";
      var maskRef = "clip-path";
      for (i = 0; i < len; i += 1) {
        if (properties[i].mode !== "a" && properties[i].mode !== "n" || properties[i].inv || properties[i].o.k !== 100 || properties[i].o.x) {
          maskType = "mask";
          maskRef = "mask";
        }
        if ((properties[i].mode === "s" || properties[i].mode === "i") && count === 0) {
          rect = createNS("rect");
          rect.setAttribute("fill", "#ffffff");
          rect.setAttribute("width", this.element.comp.data.w || 0);
          rect.setAttribute("height", this.element.comp.data.h || 0);
          currentMasks.push(rect);
        } else {
          rect = null;
        }
        path = createNS("path");
        if (properties[i].mode === "n") {
          this.viewData[i] = {
            op: PropertyFactory.getProp(this.element, properties[i].o, 0, 0.01, this.element),
            prop: ShapePropertyFactory.getShapeProp(this.element, properties[i], 3),
            elem: path,
            lastPath: ""
          };
          defs.appendChild(path);
        } else {
          count += 1;
          path.setAttribute("fill", properties[i].mode === "s" ? "#000000" : "#ffffff");
          path.setAttribute("clip-rule", "nonzero");
          var filterID;
          if (properties[i].x.k !== 0) {
            maskType = "mask";
            maskRef = "mask";
            x = PropertyFactory.getProp(this.element, properties[i].x, 0, null, this.element);
            filterID = createElementID();
            expansor = createNS("filter");
            expansor.setAttribute("id", filterID);
            feMorph = createNS("feMorphology");
            feMorph.setAttribute("operator", "erode");
            feMorph.setAttribute("in", "SourceGraphic");
            feMorph.setAttribute("radius", "0");
            expansor.appendChild(feMorph);
            defs.appendChild(expansor);
            path.setAttribute("stroke", properties[i].mode === "s" ? "#000000" : "#ffffff");
          } else {
            feMorph = null;
            x = null;
          }
          this.storedData[i] = {
            elem: path,
            x,
            expan: feMorph,
            lastPath: "",
            lastOperator: "",
            filterId: filterID,
            lastRadius: 0
          };
          if (properties[i].mode === "i") {
            jLen = currentMasks.length;
            var g = createNS("g");
            for (j = 0; j < jLen; j += 1) {
              g.appendChild(currentMasks[j]);
            }
            var mask2 = createNS("mask");
            mask2.setAttribute("mask-type", "alpha");
            mask2.setAttribute("id", layerId + "_" + count);
            mask2.appendChild(path);
            defs.appendChild(mask2);
            g.setAttribute("mask", "url(" + getLocationHref() + "#" + layerId + "_" + count + ")");
            currentMasks.length = 0;
            currentMasks.push(g);
          } else {
            currentMasks.push(path);
          }
          if (properties[i].inv && !this.solidPath) {
            this.solidPath = this.createLayerSolidPath();
          }
          this.viewData[i] = {
            elem: path,
            lastPath: "",
            op: PropertyFactory.getProp(this.element, properties[i].o, 0, 0.01, this.element),
            prop: ShapePropertyFactory.getShapeProp(this.element, properties[i], 3),
            invRect: rect
          };
          if (!this.viewData[i].prop.k) {
            this.drawPath(properties[i], this.viewData[i].prop.v, this.viewData[i]);
          }
        }
      }
      this.maskElement = createNS(maskType);
      len = currentMasks.length;
      for (i = 0; i < len; i += 1) {
        this.maskElement.appendChild(currentMasks[i]);
      }
      if (count > 0) {
        this.maskElement.setAttribute("id", layerId);
        this.element.maskedElement.setAttribute(maskRef, "url(" + getLocationHref() + "#" + layerId + ")");
        defs.appendChild(this.maskElement);
      }
      if (this.viewData.length) {
        this.element.addRenderableComponent(this);
      }
    }
    MaskElement.prototype.getMaskProperty = function(pos) {
      return this.viewData[pos].prop;
    };
    MaskElement.prototype.renderFrame = function(isFirstFrame) {
      var finalMat = this.element.finalTransform.mat;
      var i;
      var len = this.masksProperties.length;
      for (i = 0; i < len; i += 1) {
        if (this.viewData[i].prop._mdf || isFirstFrame) {
          this.drawPath(this.masksProperties[i], this.viewData[i].prop.v, this.viewData[i]);
        }
        if (this.viewData[i].op._mdf || isFirstFrame) {
          this.viewData[i].elem.setAttribute("fill-opacity", this.viewData[i].op.v);
        }
        if (this.masksProperties[i].mode !== "n") {
          if (this.viewData[i].invRect && (this.element.finalTransform.mProp._mdf || isFirstFrame)) {
            this.viewData[i].invRect.setAttribute("transform", finalMat.getInverseMatrix().to2dCSS());
          }
          if (this.storedData[i].x && (this.storedData[i].x._mdf || isFirstFrame)) {
            var feMorph = this.storedData[i].expan;
            if (this.storedData[i].x.v < 0) {
              if (this.storedData[i].lastOperator !== "erode") {
                this.storedData[i].lastOperator = "erode";
                this.storedData[i].elem.setAttribute("filter", "url(" + getLocationHref() + "#" + this.storedData[i].filterId + ")");
              }
              feMorph.setAttribute("radius", -this.storedData[i].x.v);
            } else {
              if (this.storedData[i].lastOperator !== "dilate") {
                this.storedData[i].lastOperator = "dilate";
                this.storedData[i].elem.setAttribute("filter", null);
              }
              this.storedData[i].elem.setAttribute("stroke-width", this.storedData[i].x.v * 2);
            }
          }
        }
      }
    };
    MaskElement.prototype.getMaskelement = function() {
      return this.maskElement;
    };
    MaskElement.prototype.createLayerSolidPath = function() {
      var path = "M0,0 ";
      path += " h" + this.globalData.compSize.w;
      path += " v" + this.globalData.compSize.h;
      path += " h-" + this.globalData.compSize.w;
      path += " v-" + this.globalData.compSize.h + " ";
      return path;
    };
    MaskElement.prototype.drawPath = function(pathData, pathNodes, viewData) {
      var pathString = " M" + pathNodes.v[0][0] + "," + pathNodes.v[0][1];
      var i;
      var len;
      len = pathNodes._length;
      for (i = 1; i < len; i += 1) {
        pathString += " C" + pathNodes.o[i - 1][0] + "," + pathNodes.o[i - 1][1] + " " + pathNodes.i[i][0] + "," + pathNodes.i[i][1] + " " + pathNodes.v[i][0] + "," + pathNodes.v[i][1];
      }
      if (pathNodes.c && len > 1) {
        pathString += " C" + pathNodes.o[i - 1][0] + "," + pathNodes.o[i - 1][1] + " " + pathNodes.i[0][0] + "," + pathNodes.i[0][1] + " " + pathNodes.v[0][0] + "," + pathNodes.v[0][1];
      }
      if (viewData.lastPath !== pathString) {
        var pathShapeValue = "";
        if (viewData.elem) {
          if (pathNodes.c) {
            pathShapeValue = pathData.inv ? this.solidPath + pathString : pathString;
          }
          viewData.elem.setAttribute("d", pathShapeValue);
        }
        viewData.lastPath = pathString;
      }
    };
    MaskElement.prototype.destroy = function() {
      this.element = null;
      this.globalData = null;
      this.maskElement = null;
      this.data = null;
      this.masksProperties = null;
    };
    var filtersFactory = function() {
      var ob2 = {};
      ob2.createFilter = createFilter;
      ob2.createAlphaToLuminanceFilter = createAlphaToLuminanceFilter;
      function createFilter(filId, skipCoordinates) {
        var fil = createNS("filter");
        fil.setAttribute("id", filId);
        if (skipCoordinates !== true) {
          fil.setAttribute("filterUnits", "objectBoundingBox");
          fil.setAttribute("x", "0%");
          fil.setAttribute("y", "0%");
          fil.setAttribute("width", "100%");
          fil.setAttribute("height", "100%");
        }
        return fil;
      }
      function createAlphaToLuminanceFilter() {
        var feColorMatrix = createNS("feColorMatrix");
        feColorMatrix.setAttribute("type", "matrix");
        feColorMatrix.setAttribute("color-interpolation-filters", "sRGB");
        feColorMatrix.setAttribute("values", "0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 1 1");
        return feColorMatrix;
      }
      return ob2;
    }();
    var featureSupport = function() {
      var ob2 = {
        maskType: true,
        svgLumaHidden: true,
        offscreenCanvas: typeof OffscreenCanvas !== "undefined"
      };
      if (/MSIE 10/i.test(navigator.userAgent) || /MSIE 9/i.test(navigator.userAgent) || /rv:11.0/i.test(navigator.userAgent) || /Edge\/\d./i.test(navigator.userAgent)) {
        ob2.maskType = false;
      }
      if (/firefox/i.test(navigator.userAgent)) {
        ob2.svgLumaHidden = false;
      }
      return ob2;
    }();
    var registeredEffects$1 = {};
    var idPrefix = "filter_result_";
    function SVGEffects(elem2) {
      var i;
      var source = "SourceGraphic";
      var len = elem2.data.ef ? elem2.data.ef.length : 0;
      var filId = createElementID();
      var fil = filtersFactory.createFilter(filId, true);
      var count = 0;
      this.filters = [];
      var filterManager;
      for (i = 0; i < len; i += 1) {
        filterManager = null;
        var type = elem2.data.ef[i].ty;
        if (registeredEffects$1[type]) {
          var Effect = registeredEffects$1[type].effect;
          filterManager = new Effect(fil, elem2.effectsManager.effectElements[i], elem2, idPrefix + count, source);
          source = idPrefix + count;
          if (registeredEffects$1[type].countsAsEffect) {
            count += 1;
          }
        }
        if (filterManager) {
          this.filters.push(filterManager);
        }
      }
      if (count) {
        elem2.globalData.defs.appendChild(fil);
        elem2.layerElement.setAttribute("filter", "url(" + getLocationHref() + "#" + filId + ")");
      }
      if (this.filters.length) {
        elem2.addRenderableComponent(this);
      }
    }
    SVGEffects.prototype.renderFrame = function(_isFirstFrame) {
      var i;
      var len = this.filters.length;
      for (i = 0; i < len; i += 1) {
        this.filters[i].renderFrame(_isFirstFrame);
      }
    };
    SVGEffects.prototype.getEffects = function(type) {
      var i;
      var len = this.filters.length;
      var effects = [];
      for (i = 0; i < len; i += 1) {
        if (this.filters[i].type === type) {
          effects.push(this.filters[i]);
        }
      }
      return effects;
    };
    function registerEffect$1(id, effect2, countsAsEffect) {
      registeredEffects$1[id] = {
        effect: effect2,
        countsAsEffect
      };
    }
    function SVGBaseElement() {
    }
    SVGBaseElement.prototype = {
      initRendererElement: function initRendererElement() {
        this.layerElement = createNS("g");
      },
      createContainerElements: function createContainerElements() {
        this.matteElement = createNS("g");
        this.transformedElement = this.layerElement;
        this.maskedElement = this.layerElement;
        this._sizeChanged = false;
        var layerElementParent = null;
        if (this.data.td) {
          this.matteMasks = {};
          var gg = createNS("g");
          gg.setAttribute("id", this.layerId);
          gg.appendChild(this.layerElement);
          layerElementParent = gg;
          this.globalData.defs.appendChild(gg);
        } else if (this.data.tt) {
          this.matteElement.appendChild(this.layerElement);
          layerElementParent = this.matteElement;
          this.baseElement = this.matteElement;
        } else {
          this.baseElement = this.layerElement;
        }
        if (this.data.ln) {
          this.layerElement.setAttribute("id", this.data.ln);
        }
        if (this.data.cl) {
          this.layerElement.setAttribute("class", this.data.cl);
        }
        if (this.data.ty === 0 && !this.data.hd) {
          var cp = createNS("clipPath");
          var pt = createNS("path");
          pt.setAttribute("d", "M0,0 L" + this.data.w + ",0 L" + this.data.w + "," + this.data.h + " L0," + this.data.h + "z");
          var clipId = createElementID();
          cp.setAttribute("id", clipId);
          cp.appendChild(pt);
          this.globalData.defs.appendChild(cp);
          if (this.checkMasks()) {
            var cpGroup = createNS("g");
            cpGroup.setAttribute("clip-path", "url(" + getLocationHref() + "#" + clipId + ")");
            cpGroup.appendChild(this.layerElement);
            this.transformedElement = cpGroup;
            if (layerElementParent) {
              layerElementParent.appendChild(this.transformedElement);
            } else {
              this.baseElement = this.transformedElement;
            }
          } else {
            this.layerElement.setAttribute("clip-path", "url(" + getLocationHref() + "#" + clipId + ")");
          }
        }
        if (this.data.bm !== 0) {
          this.setBlendMode();
        }
      },
      renderElement: function renderElement() {
        if (this.finalTransform._localMatMdf) {
          this.transformedElement.setAttribute("transform", this.finalTransform.localMat.to2dCSS());
        }
        if (this.finalTransform._opMdf) {
          this.transformedElement.setAttribute("opacity", this.finalTransform.localOpacity);
        }
      },
      destroyBaseElement: function destroyBaseElement() {
        this.layerElement = null;
        this.matteElement = null;
        this.maskManager.destroy();
      },
      getBaseElement: function getBaseElement() {
        if (this.data.hd) {
          return null;
        }
        return this.baseElement;
      },
      createRenderableComponents: function createRenderableComponents() {
        this.maskManager = new MaskElement(this.data, this, this.globalData);
        this.renderableEffectsManager = new SVGEffects(this);
        this.searchEffectTransforms();
      },
      getMatte: function getMatte(matteType) {
        if (!this.matteMasks) {
          this.matteMasks = {};
        }
        if (!this.matteMasks[matteType]) {
          var id = this.layerId + "_" + matteType;
          var filId;
          var fil;
          var useElement;
          var gg;
          if (matteType === 1 || matteType === 3) {
            var masker = createNS("mask");
            masker.setAttribute("id", id);
            masker.setAttribute("mask-type", matteType === 3 ? "luminance" : "alpha");
            useElement = createNS("use");
            useElement.setAttributeNS("http://www.w3.org/1999/xlink", "href", "#" + this.layerId);
            masker.appendChild(useElement);
            this.globalData.defs.appendChild(masker);
            if (!featureSupport.maskType && matteType === 1) {
              masker.setAttribute("mask-type", "luminance");
              filId = createElementID();
              fil = filtersFactory.createFilter(filId);
              this.globalData.defs.appendChild(fil);
              fil.appendChild(filtersFactory.createAlphaToLuminanceFilter());
              gg = createNS("g");
              gg.appendChild(useElement);
              masker.appendChild(gg);
              gg.setAttribute("filter", "url(" + getLocationHref() + "#" + filId + ")");
            }
          } else if (matteType === 2) {
            var maskGroup = createNS("mask");
            maskGroup.setAttribute("id", id);
            maskGroup.setAttribute("mask-type", "alpha");
            var maskGrouper = createNS("g");
            maskGroup.appendChild(maskGrouper);
            filId = createElementID();
            fil = filtersFactory.createFilter(filId);
            var feCTr = createNS("feComponentTransfer");
            feCTr.setAttribute("in", "SourceGraphic");
            fil.appendChild(feCTr);
            var feFunc = createNS("feFuncA");
            feFunc.setAttribute("type", "table");
            feFunc.setAttribute("tableValues", "1.0 0.0");
            feCTr.appendChild(feFunc);
            this.globalData.defs.appendChild(fil);
            var alphaRect = createNS("rect");
            alphaRect.setAttribute("width", this.comp.data.w);
            alphaRect.setAttribute("height", this.comp.data.h);
            alphaRect.setAttribute("x", "0");
            alphaRect.setAttribute("y", "0");
            alphaRect.setAttribute("fill", "#ffffff");
            alphaRect.setAttribute("opacity", "0");
            maskGrouper.setAttribute("filter", "url(" + getLocationHref() + "#" + filId + ")");
            maskGrouper.appendChild(alphaRect);
            useElement = createNS("use");
            useElement.setAttributeNS("http://www.w3.org/1999/xlink", "href", "#" + this.layerId);
            maskGrouper.appendChild(useElement);
            if (!featureSupport.maskType) {
              maskGroup.setAttribute("mask-type", "luminance");
              fil.appendChild(filtersFactory.createAlphaToLuminanceFilter());
              gg = createNS("g");
              maskGrouper.appendChild(alphaRect);
              gg.appendChild(this.layerElement);
              maskGrouper.appendChild(gg);
            }
            this.globalData.defs.appendChild(maskGroup);
          }
          this.matteMasks[matteType] = id;
        }
        return this.matteMasks[matteType];
      },
      setMatte: function setMatte(id) {
        if (!this.matteElement) {
          return;
        }
        this.matteElement.setAttribute("mask", "url(" + getLocationHref() + "#" + id + ")");
      }
    };
    function HierarchyElement() {
    }
    HierarchyElement.prototype = {
      /**
         * @function
         * Initializes hierarchy properties
         *
         */
      initHierarchy: function initHierarchy() {
        this.hierarchy = [];
        this._isParent = false;
        this.checkParenting();
      },
      /**
         * @function
         * Sets layer's hierarchy.
         * @param {array} hierarch
         * layer's parent list
         *
         */
      setHierarchy: function setHierarchy(hierarchy) {
        this.hierarchy = hierarchy;
      },
      /**
         * @function
         * Sets layer as parent.
         *
         */
      setAsParent: function setAsParent() {
        this._isParent = true;
      },
      /**
         * @function
         * Searches layer's parenting chain
         *
         */
      checkParenting: function checkParenting() {
        if (this.data.parent !== void 0) {
          this.comp.buildElementParenting(this, this.data.parent, []);
        }
      }
    };
    function RenderableDOMElement() {
    }
    (function() {
      var _prototype = {
        initElement: function initElement(data2, globalData2, comp2) {
          this.initFrame();
          this.initBaseData(data2, globalData2, comp2);
          this.initTransform(data2, globalData2, comp2);
          this.initHierarchy();
          this.initRenderable();
          this.initRendererElement();
          this.createContainerElements();
          this.createRenderableComponents();
          this.createContent();
          this.hide();
        },
        hide: function hide() {
          if (!this.hidden && (!this.isInRange || this.isTransparent)) {
            var elem2 = this.baseElement || this.layerElement;
            elem2.style.display = "none";
            this.hidden = true;
          }
        },
        show: function show() {
          if (this.isInRange && !this.isTransparent) {
            if (!this.data.hd) {
              var elem2 = this.baseElement || this.layerElement;
              elem2.style.display = "block";
            }
            this.hidden = false;
            this._isFirstFrame = true;
          }
        },
        renderFrame: function renderFrame() {
          if (this.data.hd || this.hidden) {
            return;
          }
          this.renderTransform();
          this.renderRenderable();
          this.renderLocalTransform();
          this.renderElement();
          this.renderInnerContent();
          if (this._isFirstFrame) {
            this._isFirstFrame = false;
          }
        },
        renderInnerContent: function renderInnerContent() {
        },
        prepareFrame: function prepareFrame(num) {
          this._mdf = false;
          this.prepareRenderableFrame(num);
          this.prepareProperties(num, this.isInRange);
          this.checkTransparency();
        },
        destroy: function destroy() {
          this.innerElem = null;
          this.destroyBaseElement();
        }
      };
      extendPrototype([RenderableElement, createProxyFunction(_prototype)], RenderableDOMElement);
    })();
    function IImageElement(data2, globalData2, comp2) {
      this.assetData = globalData2.getAssetData(data2.refId);
      if (this.assetData && this.assetData.sid) {
        this.assetData = globalData2.slotManager.getProp(this.assetData);
      }
      this.initElement(data2, globalData2, comp2);
      this.sourceRect = {
        top: 0,
        left: 0,
        width: this.assetData.w,
        height: this.assetData.h
      };
    }
    extendPrototype([BaseElement, TransformElement, SVGBaseElement, HierarchyElement, FrameElement, RenderableDOMElement], IImageElement);
    IImageElement.prototype.createContent = function() {
      var assetPath = this.globalData.getAssetsPath(this.assetData);
      this.innerElem = createNS("image");
      this.innerElem.setAttribute("width", this.assetData.w + "px");
      this.innerElem.setAttribute("height", this.assetData.h + "px");
      this.innerElem.setAttribute("preserveAspectRatio", this.assetData.pr || this.globalData.renderConfig.imagePreserveAspectRatio);
      this.innerElem.setAttributeNS("http://www.w3.org/1999/xlink", "href", assetPath);
      this.layerElement.appendChild(this.innerElem);
    };
    IImageElement.prototype.sourceRectAtTime = function() {
      return this.sourceRect;
    };
    function ProcessedElement(element, position2) {
      this.elem = element;
      this.pos = position2;
    }
    function IShapeElement() {
    }
    IShapeElement.prototype = {
      addShapeToModifiers: function addShapeToModifiers(data2) {
        var i;
        var len = this.shapeModifiers.length;
        for (i = 0; i < len; i += 1) {
          this.shapeModifiers[i].addShape(data2);
        }
      },
      isShapeInAnimatedModifiers: function isShapeInAnimatedModifiers(data2) {
        var i = 0;
        var len = this.shapeModifiers.length;
        while (i < len) {
          if (this.shapeModifiers[i].isAnimatedWithShape(data2)) {
            return true;
          }
        }
        return false;
      },
      renderModifiers: function renderModifiers() {
        if (!this.shapeModifiers.length) {
          return;
        }
        var i;
        var len = this.shapes.length;
        for (i = 0; i < len; i += 1) {
          this.shapes[i].sh.reset();
        }
        len = this.shapeModifiers.length;
        var shouldBreakProcess;
        for (i = len - 1; i >= 0; i -= 1) {
          shouldBreakProcess = this.shapeModifiers[i].processShapes(this._isFirstFrame);
          if (shouldBreakProcess) {
            break;
          }
        }
      },
      searchProcessedElement: function searchProcessedElement(elem2) {
        var elements = this.processedElements;
        var i = 0;
        var len = elements.length;
        while (i < len) {
          if (elements[i].elem === elem2) {
            return elements[i].pos;
          }
          i += 1;
        }
        return 0;
      },
      addProcessedElement: function addProcessedElement(elem2, pos) {
        var elements = this.processedElements;
        var i = elements.length;
        while (i) {
          i -= 1;
          if (elements[i].elem === elem2) {
            elements[i].pos = pos;
            return;
          }
        }
        elements.push(new ProcessedElement(elem2, pos));
      },
      prepareFrame: function prepareFrame(num) {
        this.prepareRenderableFrame(num);
        this.prepareProperties(num, this.isInRange);
      }
    };
    var lineCapEnum = {
      1: "butt",
      2: "round",
      3: "square"
    };
    var lineJoinEnum = {
      1: "miter",
      2: "round",
      3: "bevel"
    };
    function SVGShapeData(transformers, level, shape) {
      this.caches = [];
      this.styles = [];
      this.transformers = transformers;
      this.lStr = "";
      this.sh = shape;
      this.lvl = level;
      this._isAnimated = !!shape.k;
      var i = 0;
      var len = transformers.length;
      while (i < len) {
        if (transformers[i].mProps.dynamicProperties.length) {
          this._isAnimated = true;
          break;
        }
        i += 1;
      }
    }
    SVGShapeData.prototype.setAsAnimated = function() {
      this._isAnimated = true;
    };
    function SVGStyleData(data2, level) {
      this.data = data2;
      this.type = data2.ty;
      this.d = "";
      this.lvl = level;
      this._mdf = false;
      this.closed = data2.hd === true;
      this.pElem = createNS("path");
      this.msElem = null;
    }
    SVGStyleData.prototype.reset = function() {
      this.d = "";
      this._mdf = false;
    };
    function DashProperty(elem2, data2, renderer2, container) {
      this.elem = elem2;
      this.frameId = -1;
      this.dataProps = createSizedArray(data2.length);
      this.renderer = renderer2;
      this.k = false;
      this.dashStr = "";
      this.dashArray = createTypedArray("float32", data2.length ? data2.length - 1 : 0);
      this.dashoffset = createTypedArray("float32", 1);
      this.initDynamicPropertyContainer(container);
      var i;
      var len = data2.length || 0;
      var prop;
      for (i = 0; i < len; i += 1) {
        prop = PropertyFactory.getProp(elem2, data2[i].v, 0, 0, this);
        this.k = prop.k || this.k;
        this.dataProps[i] = {
          n: data2[i].n,
          p: prop
        };
      }
      if (!this.k) {
        this.getValue(true);
      }
      this._isAnimated = this.k;
    }
    DashProperty.prototype.getValue = function(forceRender) {
      if (this.elem.globalData.frameId === this.frameId && !forceRender) {
        return;
      }
      this.frameId = this.elem.globalData.frameId;
      this.iterateDynamicProperties();
      this._mdf = this._mdf || forceRender;
      if (this._mdf) {
        var i = 0;
        var len = this.dataProps.length;
        if (this.renderer === "svg") {
          this.dashStr = "";
        }
        for (i = 0; i < len; i += 1) {
          if (this.dataProps[i].n !== "o") {
            if (this.renderer === "svg") {
              this.dashStr += " " + this.dataProps[i].p.v;
            } else {
              this.dashArray[i] = this.dataProps[i].p.v;
            }
          } else {
            this.dashoffset[0] = this.dataProps[i].p.v;
          }
        }
      }
    };
    extendPrototype([DynamicPropertyContainer], DashProperty);
    function SVGStrokeStyleData(elem2, data2, styleOb) {
      this.initDynamicPropertyContainer(elem2);
      this.getValue = this.iterateDynamicProperties;
      this.o = PropertyFactory.getProp(elem2, data2.o, 0, 0.01, this);
      this.w = PropertyFactory.getProp(elem2, data2.w, 0, null, this);
      this.d = new DashProperty(elem2, data2.d || {}, "svg", this);
      this.c = PropertyFactory.getProp(elem2, data2.c, 1, 255, this);
      this.style = styleOb;
      this._isAnimated = !!this._isAnimated;
    }
    extendPrototype([DynamicPropertyContainer], SVGStrokeStyleData);
    function SVGFillStyleData(elem2, data2, styleOb) {
      this.initDynamicPropertyContainer(elem2);
      this.getValue = this.iterateDynamicProperties;
      this.o = PropertyFactory.getProp(elem2, data2.o, 0, 0.01, this);
      this.c = PropertyFactory.getProp(elem2, data2.c, 1, 255, this);
      this.style = styleOb;
    }
    extendPrototype([DynamicPropertyContainer], SVGFillStyleData);
    function SVGNoStyleData(elem2, data2, styleOb) {
      this.initDynamicPropertyContainer(elem2);
      this.getValue = this.iterateDynamicProperties;
      this.style = styleOb;
    }
    extendPrototype([DynamicPropertyContainer], SVGNoStyleData);
    function GradientProperty(elem2, data2, container) {
      this.data = data2;
      this.c = createTypedArray("uint8c", data2.p * 4);
      var cLength = data2.k.k[0].s ? data2.k.k[0].s.length - data2.p * 4 : data2.k.k.length - data2.p * 4;
      this.o = createTypedArray("float32", cLength);
      this._cmdf = false;
      this._omdf = false;
      this._collapsable = this.checkCollapsable();
      this._hasOpacity = cLength;
      this.initDynamicPropertyContainer(container);
      this.prop = PropertyFactory.getProp(elem2, data2.k, 1, null, this);
      this.k = this.prop.k;
      this.getValue(true);
    }
    GradientProperty.prototype.comparePoints = function(values, points) {
      var i = 0;
      var len = this.o.length / 2;
      var diff;
      while (i < len) {
        diff = Math.abs(values[i * 4] - values[points * 4 + i * 2]);
        if (diff > 0.01) {
          return false;
        }
        i += 1;
      }
      return true;
    };
    GradientProperty.prototype.checkCollapsable = function() {
      if (this.o.length / 2 !== this.c.length / 4) {
        return false;
      }
      if (this.data.k.k[0].s) {
        var i = 0;
        var len = this.data.k.k.length;
        while (i < len) {
          if (!this.comparePoints(this.data.k.k[i].s, this.data.p)) {
            return false;
          }
          i += 1;
        }
      } else if (!this.comparePoints(this.data.k.k, this.data.p)) {
        return false;
      }
      return true;
    };
    GradientProperty.prototype.getValue = function(forceRender) {
      this.prop.getValue();
      this._mdf = false;
      this._cmdf = false;
      this._omdf = false;
      if (this.prop._mdf || forceRender) {
        var i;
        var len = this.data.p * 4;
        var mult;
        var val2;
        for (i = 0; i < len; i += 1) {
          mult = i % 4 === 0 ? 100 : 255;
          val2 = Math.round(this.prop.v[i] * mult);
          if (this.c[i] !== val2) {
            this.c[i] = val2;
            this._cmdf = !forceRender;
          }
        }
        if (this.o.length) {
          len = this.prop.v.length;
          for (i = this.data.p * 4; i < len; i += 1) {
            mult = i % 2 === 0 ? 100 : 1;
            val2 = i % 2 === 0 ? Math.round(this.prop.v[i] * 100) : this.prop.v[i];
            if (this.o[i - this.data.p * 4] !== val2) {
              this.o[i - this.data.p * 4] = val2;
              this._omdf = !forceRender;
            }
          }
        }
        this._mdf = !forceRender;
      }
    };
    extendPrototype([DynamicPropertyContainer], GradientProperty);
    function SVGGradientFillStyleData(elem2, data2, styleOb) {
      this.initDynamicPropertyContainer(elem2);
      this.getValue = this.iterateDynamicProperties;
      this.initGradientData(elem2, data2, styleOb);
    }
    SVGGradientFillStyleData.prototype.initGradientData = function(elem2, data2, styleOb) {
      this.o = PropertyFactory.getProp(elem2, data2.o, 0, 0.01, this);
      this.s = PropertyFactory.getProp(elem2, data2.s, 1, null, this);
      this.e = PropertyFactory.getProp(elem2, data2.e, 1, null, this);
      this.h = PropertyFactory.getProp(elem2, data2.h || {
        k: 0
      }, 0, 0.01, this);
      this.a = PropertyFactory.getProp(elem2, data2.a || {
        k: 0
      }, 0, degToRads, this);
      this.g = new GradientProperty(elem2, data2.g, this);
      this.style = styleOb;
      this.stops = [];
      this.setGradientData(styleOb.pElem, data2);
      this.setGradientOpacity(data2, styleOb);
      this._isAnimated = !!this._isAnimated;
    };
    SVGGradientFillStyleData.prototype.setGradientData = function(pathElement, data2) {
      var gradientId = createElementID();
      var gfill = createNS(data2.t === 1 ? "linearGradient" : "radialGradient");
      gfill.setAttribute("id", gradientId);
      gfill.setAttribute("spreadMethod", "pad");
      gfill.setAttribute("gradientUnits", "userSpaceOnUse");
      var stops = [];
      var stop;
      var j;
      var jLen;
      jLen = data2.g.p * 4;
      for (j = 0; j < jLen; j += 4) {
        stop = createNS("stop");
        gfill.appendChild(stop);
        stops.push(stop);
      }
      pathElement.setAttribute(data2.ty === "gf" ? "fill" : "stroke", "url(" + getLocationHref() + "#" + gradientId + ")");
      this.gf = gfill;
      this.cst = stops;
    };
    SVGGradientFillStyleData.prototype.setGradientOpacity = function(data2, styleOb) {
      if (this.g._hasOpacity && !this.g._collapsable) {
        var stop;
        var j;
        var jLen;
        var mask2 = createNS("mask");
        var maskElement = createNS("path");
        mask2.appendChild(maskElement);
        var opacityId = createElementID();
        var maskId = createElementID();
        mask2.setAttribute("id", maskId);
        var opFill = createNS(data2.t === 1 ? "linearGradient" : "radialGradient");
        opFill.setAttribute("id", opacityId);
        opFill.setAttribute("spreadMethod", "pad");
        opFill.setAttribute("gradientUnits", "userSpaceOnUse");
        jLen = data2.g.k.k[0].s ? data2.g.k.k[0].s.length : data2.g.k.k.length;
        var stops = this.stops;
        for (j = data2.g.p * 4; j < jLen; j += 2) {
          stop = createNS("stop");
          stop.setAttribute("stop-color", "rgb(255,255,255)");
          opFill.appendChild(stop);
          stops.push(stop);
        }
        maskElement.setAttribute(data2.ty === "gf" ? "fill" : "stroke", "url(" + getLocationHref() + "#" + opacityId + ")");
        if (data2.ty === "gs") {
          maskElement.setAttribute("stroke-linecap", lineCapEnum[data2.lc || 2]);
          maskElement.setAttribute("stroke-linejoin", lineJoinEnum[data2.lj || 2]);
          if (data2.lj === 1) {
            maskElement.setAttribute("stroke-miterlimit", data2.ml);
          }
        }
        this.of = opFill;
        this.ms = mask2;
        this.ost = stops;
        this.maskId = maskId;
        styleOb.msElem = maskElement;
      }
    };
    extendPrototype([DynamicPropertyContainer], SVGGradientFillStyleData);
    function SVGGradientStrokeStyleData(elem2, data2, styleOb) {
      this.initDynamicPropertyContainer(elem2);
      this.getValue = this.iterateDynamicProperties;
      this.w = PropertyFactory.getProp(elem2, data2.w, 0, null, this);
      this.d = new DashProperty(elem2, data2.d || {}, "svg", this);
      this.initGradientData(elem2, data2, styleOb);
      this._isAnimated = !!this._isAnimated;
    }
    extendPrototype([SVGGradientFillStyleData, DynamicPropertyContainer], SVGGradientStrokeStyleData);
    function ShapeGroupData() {
      this.it = [];
      this.prevViewData = [];
      this.gr = createNS("g");
    }
    function SVGTransformData(mProps, op2, container) {
      this.transform = {
        mProps,
        op: op2,
        container
      };
      this.elements = [];
      this._isAnimated = this.transform.mProps.dynamicProperties.length || this.transform.op.effectsSequence.length;
    }
    var buildShapeString = function buildShapeString2(pathNodes, length2, closed, mat) {
      if (length2 === 0) {
        return "";
      }
      var _o = pathNodes.o;
      var _i = pathNodes.i;
      var _v = pathNodes.v;
      var i;
      var shapeString = " M" + mat.applyToPointStringified(_v[0][0], _v[0][1]);
      for (i = 1; i < length2; i += 1) {
        shapeString += " C" + mat.applyToPointStringified(_o[i - 1][0], _o[i - 1][1]) + " " + mat.applyToPointStringified(_i[i][0], _i[i][1]) + " " + mat.applyToPointStringified(_v[i][0], _v[i][1]);
      }
      if (closed && length2) {
        shapeString += " C" + mat.applyToPointStringified(_o[i - 1][0], _o[i - 1][1]) + " " + mat.applyToPointStringified(_i[0][0], _i[0][1]) + " " + mat.applyToPointStringified(_v[0][0], _v[0][1]);
        shapeString += "z";
      }
      return shapeString;
    };
    var SVGElementsRenderer = function() {
      var _identityMatrix = new Matrix();
      var _matrixHelper = new Matrix();
      var ob2 = {
        createRenderFunction
      };
      function createRenderFunction(data2) {
        switch (data2.ty) {
          case "fl":
            return renderFill;
          case "gf":
            return renderGradient;
          case "gs":
            return renderGradientStroke;
          case "st":
            return renderStroke;
          case "sh":
          case "el":
          case "rc":
          case "sr":
            return renderPath;
          case "tr":
            return renderContentTransform;
          case "no":
            return renderNoop;
          default:
            return null;
        }
      }
      function renderContentTransform(styleData, itemData, isFirstFrame) {
        if (isFirstFrame || itemData.transform.op._mdf) {
          itemData.transform.container.setAttribute("opacity", itemData.transform.op.v);
        }
        if (isFirstFrame || itemData.transform.mProps._mdf) {
          itemData.transform.container.setAttribute("transform", itemData.transform.mProps.v.to2dCSS());
        }
      }
      function renderNoop() {
      }
      function renderPath(styleData, itemData, isFirstFrame) {
        var j;
        var jLen;
        var pathStringTransformed;
        var redraw;
        var pathNodes;
        var l;
        var lLen = itemData.styles.length;
        var lvl = itemData.lvl;
        var paths;
        var mat;
        var iterations;
        var k;
        for (l = 0; l < lLen; l += 1) {
          redraw = itemData.sh._mdf || isFirstFrame;
          if (itemData.styles[l].lvl < lvl) {
            mat = _matrixHelper.reset();
            iterations = lvl - itemData.styles[l].lvl;
            k = itemData.transformers.length - 1;
            while (!redraw && iterations > 0) {
              redraw = itemData.transformers[k].mProps._mdf || redraw;
              iterations -= 1;
              k -= 1;
            }
            if (redraw) {
              iterations = lvl - itemData.styles[l].lvl;
              k = itemData.transformers.length - 1;
              while (iterations > 0) {
                mat.multiply(itemData.transformers[k].mProps.v);
                iterations -= 1;
                k -= 1;
              }
            }
          } else {
            mat = _identityMatrix;
          }
          paths = itemData.sh.paths;
          jLen = paths._length;
          if (redraw) {
            pathStringTransformed = "";
            for (j = 0; j < jLen; j += 1) {
              pathNodes = paths.shapes[j];
              if (pathNodes && pathNodes._length) {
                pathStringTransformed += buildShapeString(pathNodes, pathNodes._length, pathNodes.c, mat);
              }
            }
            itemData.caches[l] = pathStringTransformed;
          } else {
            pathStringTransformed = itemData.caches[l];
          }
          itemData.styles[l].d += styleData.hd === true ? "" : pathStringTransformed;
          itemData.styles[l]._mdf = redraw || itemData.styles[l]._mdf;
        }
      }
      function renderFill(styleData, itemData, isFirstFrame) {
        var styleElem = itemData.style;
        if (itemData.c._mdf || isFirstFrame) {
          styleElem.pElem.setAttribute("fill", "rgb(" + bmFloor(itemData.c.v[0]) + "," + bmFloor(itemData.c.v[1]) + "," + bmFloor(itemData.c.v[2]) + ")");
        }
        if (itemData.o._mdf || isFirstFrame) {
          styleElem.pElem.setAttribute("fill-opacity", itemData.o.v);
        }
      }
      function renderGradientStroke(styleData, itemData, isFirstFrame) {
        renderGradient(styleData, itemData, isFirstFrame);
        renderStroke(styleData, itemData, isFirstFrame);
      }
      function renderGradient(styleData, itemData, isFirstFrame) {
        var gfill = itemData.gf;
        var hasOpacity = itemData.g._hasOpacity;
        var pt1 = itemData.s.v;
        var pt2 = itemData.e.v;
        if (itemData.o._mdf || isFirstFrame) {
          var attr = styleData.ty === "gf" ? "fill-opacity" : "stroke-opacity";
          itemData.style.pElem.setAttribute(attr, itemData.o.v);
        }
        if (itemData.s._mdf || isFirstFrame) {
          var attr1 = styleData.t === 1 ? "x1" : "cx";
          var attr2 = attr1 === "x1" ? "y1" : "cy";
          gfill.setAttribute(attr1, pt1[0]);
          gfill.setAttribute(attr2, pt1[1]);
          if (hasOpacity && !itemData.g._collapsable) {
            itemData.of.setAttribute(attr1, pt1[0]);
            itemData.of.setAttribute(attr2, pt1[1]);
          }
        }
        var stops;
        var i;
        var len;
        var stop;
        if (itemData.g._cmdf || isFirstFrame) {
          stops = itemData.cst;
          var cValues = itemData.g.c;
          len = stops.length;
          for (i = 0; i < len; i += 1) {
            stop = stops[i];
            stop.setAttribute("offset", cValues[i * 4] + "%");
            stop.setAttribute("stop-color", "rgb(" + cValues[i * 4 + 1] + "," + cValues[i * 4 + 2] + "," + cValues[i * 4 + 3] + ")");
          }
        }
        if (hasOpacity && (itemData.g._omdf || isFirstFrame)) {
          var oValues = itemData.g.o;
          if (itemData.g._collapsable) {
            stops = itemData.cst;
          } else {
            stops = itemData.ost;
          }
          len = stops.length;
          for (i = 0; i < len; i += 1) {
            stop = stops[i];
            if (!itemData.g._collapsable) {
              stop.setAttribute("offset", oValues[i * 2] + "%");
            }
            stop.setAttribute("stop-opacity", oValues[i * 2 + 1]);
          }
        }
        if (styleData.t === 1) {
          if (itemData.e._mdf || isFirstFrame) {
            gfill.setAttribute("x2", pt2[0]);
            gfill.setAttribute("y2", pt2[1]);
            if (hasOpacity && !itemData.g._collapsable) {
              itemData.of.setAttribute("x2", pt2[0]);
              itemData.of.setAttribute("y2", pt2[1]);
            }
          }
        } else {
          var rad;
          if (itemData.s._mdf || itemData.e._mdf || isFirstFrame) {
            rad = Math.sqrt(Math.pow(pt1[0] - pt2[0], 2) + Math.pow(pt1[1] - pt2[1], 2));
            gfill.setAttribute("r", rad);
            if (hasOpacity && !itemData.g._collapsable) {
              itemData.of.setAttribute("r", rad);
            }
          }
          if (itemData.e._mdf || itemData.h._mdf || itemData.a._mdf || isFirstFrame) {
            if (!rad) {
              rad = Math.sqrt(Math.pow(pt1[0] - pt2[0], 2) + Math.pow(pt1[1] - pt2[1], 2));
            }
            var ang = Math.atan2(pt2[1] - pt1[1], pt2[0] - pt1[0]);
            var percent = itemData.h.v;
            if (percent >= 1) {
              percent = 0.99;
            } else if (percent <= -1) {
              percent = -0.99;
            }
            var dist = rad * percent;
            var x = Math.cos(ang + itemData.a.v) * dist + pt1[0];
            var y = Math.sin(ang + itemData.a.v) * dist + pt1[1];
            gfill.setAttribute("fx", x);
            gfill.setAttribute("fy", y);
            if (hasOpacity && !itemData.g._collapsable) {
              itemData.of.setAttribute("fx", x);
              itemData.of.setAttribute("fy", y);
            }
          }
        }
      }
      function renderStroke(styleData, itemData, isFirstFrame) {
        var styleElem = itemData.style;
        var d = itemData.d;
        if (d && (d._mdf || isFirstFrame) && d.dashStr) {
          styleElem.pElem.setAttribute("stroke-dasharray", d.dashStr);
          styleElem.pElem.setAttribute("stroke-dashoffset", d.dashoffset[0]);
        }
        if (itemData.c && (itemData.c._mdf || isFirstFrame)) {
          styleElem.pElem.setAttribute("stroke", "rgb(" + bmFloor(itemData.c.v[0]) + "," + bmFloor(itemData.c.v[1]) + "," + bmFloor(itemData.c.v[2]) + ")");
        }
        if (itemData.o._mdf || isFirstFrame) {
          styleElem.pElem.setAttribute("stroke-opacity", itemData.o.v);
        }
        if (itemData.w._mdf || isFirstFrame) {
          styleElem.pElem.setAttribute("stroke-width", itemData.w.v);
          if (styleElem.msElem) {
            styleElem.msElem.setAttribute("stroke-width", itemData.w.v);
          }
        }
      }
      return ob2;
    }();
    function SVGShapeElement(data2, globalData2, comp2) {
      this.shapes = [];
      this.shapesData = data2.shapes;
      this.stylesList = [];
      this.shapeModifiers = [];
      this.itemsData = [];
      this.processedElements = [];
      this.animatedContents = [];
      this.initElement(data2, globalData2, comp2);
      this.prevViewData = [];
    }
    extendPrototype([BaseElement, TransformElement, SVGBaseElement, IShapeElement, HierarchyElement, FrameElement, RenderableDOMElement], SVGShapeElement);
    SVGShapeElement.prototype.initSecondaryElement = function() {
    };
    SVGShapeElement.prototype.identityMatrix = new Matrix();
    SVGShapeElement.prototype.buildExpressionInterface = function() {
    };
    SVGShapeElement.prototype.createContent = function() {
      this.searchShapes(this.shapesData, this.itemsData, this.prevViewData, this.layerElement, 0, [], true);
      this.filterUniqueShapes();
    };
    SVGShapeElement.prototype.filterUniqueShapes = function() {
      var i;
      var len = this.shapes.length;
      var shape;
      var j;
      var jLen = this.stylesList.length;
      var style;
      var tempShapes = [];
      var areAnimated = false;
      for (j = 0; j < jLen; j += 1) {
        style = this.stylesList[j];
        areAnimated = false;
        tempShapes.length = 0;
        for (i = 0; i < len; i += 1) {
          shape = this.shapes[i];
          if (shape.styles.indexOf(style) !== -1) {
            tempShapes.push(shape);
            areAnimated = shape._isAnimated || areAnimated;
          }
        }
        if (tempShapes.length > 1 && areAnimated) {
          this.setShapesAsAnimated(tempShapes);
        }
      }
    };
    SVGShapeElement.prototype.setShapesAsAnimated = function(shapes) {
      var i;
      var len = shapes.length;
      for (i = 0; i < len; i += 1) {
        shapes[i].setAsAnimated();
      }
    };
    SVGShapeElement.prototype.createStyleElement = function(data2, level) {
      var elementData;
      var styleOb = new SVGStyleData(data2, level);
      var pathElement = styleOb.pElem;
      if (data2.ty === "st") {
        elementData = new SVGStrokeStyleData(this, data2, styleOb);
      } else if (data2.ty === "fl") {
        elementData = new SVGFillStyleData(this, data2, styleOb);
      } else if (data2.ty === "gf" || data2.ty === "gs") {
        var GradientConstructor = data2.ty === "gf" ? SVGGradientFillStyleData : SVGGradientStrokeStyleData;
        elementData = new GradientConstructor(this, data2, styleOb);
        this.globalData.defs.appendChild(elementData.gf);
        if (elementData.maskId) {
          this.globalData.defs.appendChild(elementData.ms);
          this.globalData.defs.appendChild(elementData.of);
          pathElement.setAttribute("mask", "url(" + getLocationHref() + "#" + elementData.maskId + ")");
        }
      } else if (data2.ty === "no") {
        elementData = new SVGNoStyleData(this, data2, styleOb);
      }
      if (data2.ty === "st" || data2.ty === "gs") {
        pathElement.setAttribute("stroke-linecap", lineCapEnum[data2.lc || 2]);
        pathElement.setAttribute("stroke-linejoin", lineJoinEnum[data2.lj || 2]);
        pathElement.setAttribute("fill-opacity", "0");
        if (data2.lj === 1) {
          pathElement.setAttribute("stroke-miterlimit", data2.ml);
        }
      }
      if (data2.r === 2) {
        pathElement.setAttribute("fill-rule", "evenodd");
      }
      if (data2.ln) {
        pathElement.setAttribute("id", data2.ln);
      }
      if (data2.cl) {
        pathElement.setAttribute("class", data2.cl);
      }
      if (data2.bm) {
        pathElement.style["mix-blend-mode"] = getBlendMode(data2.bm);
      }
      this.stylesList.push(styleOb);
      this.addToAnimatedContents(data2, elementData);
      return elementData;
    };
    SVGShapeElement.prototype.createGroupElement = function(data2) {
      var elementData = new ShapeGroupData();
      if (data2.ln) {
        elementData.gr.setAttribute("id", data2.ln);
      }
      if (data2.cl) {
        elementData.gr.setAttribute("class", data2.cl);
      }
      if (data2.bm) {
        elementData.gr.style["mix-blend-mode"] = getBlendMode(data2.bm);
      }
      return elementData;
    };
    SVGShapeElement.prototype.createTransformElement = function(data2, container) {
      var transformProperty = TransformPropertyFactory.getTransformProperty(this, data2, this);
      var elementData = new SVGTransformData(transformProperty, transformProperty.o, container);
      this.addToAnimatedContents(data2, elementData);
      return elementData;
    };
    SVGShapeElement.prototype.createShapeElement = function(data2, ownTransformers, level) {
      var ty = 4;
      if (data2.ty === "rc") {
        ty = 5;
      } else if (data2.ty === "el") {
        ty = 6;
      } else if (data2.ty === "sr") {
        ty = 7;
      }
      var shapeProperty = ShapePropertyFactory.getShapeProp(this, data2, ty, this);
      var elementData = new SVGShapeData(ownTransformers, level, shapeProperty);
      this.shapes.push(elementData);
      this.addShapeToModifiers(elementData);
      this.addToAnimatedContents(data2, elementData);
      return elementData;
    };
    SVGShapeElement.prototype.addToAnimatedContents = function(data2, element) {
      var i = 0;
      var len = this.animatedContents.length;
      while (i < len) {
        if (this.animatedContents[i].element === element) {
          return;
        }
        i += 1;
      }
      this.animatedContents.push({
        fn: SVGElementsRenderer.createRenderFunction(data2),
        element,
        data: data2
      });
    };
    SVGShapeElement.prototype.setElementStyles = function(elementData) {
      var arr = elementData.styles;
      var j;
      var jLen = this.stylesList.length;
      for (j = 0; j < jLen; j += 1) {
        if (!this.stylesList[j].closed) {
          arr.push(this.stylesList[j]);
        }
      }
    };
    SVGShapeElement.prototype.reloadShapes = function() {
      this._isFirstFrame = true;
      var i;
      var len = this.itemsData.length;
      for (i = 0; i < len; i += 1) {
        this.prevViewData[i] = this.itemsData[i];
      }
      this.searchShapes(this.shapesData, this.itemsData, this.prevViewData, this.layerElement, 0, [], true);
      this.filterUniqueShapes();
      len = this.dynamicProperties.length;
      for (i = 0; i < len; i += 1) {
        this.dynamicProperties[i].getValue();
      }
      this.renderModifiers();
    };
    SVGShapeElement.prototype.searchShapes = function(arr, itemsData, prevViewData, container, level, transformers, render3) {
      var ownTransformers = [].concat(transformers);
      var i;
      var len = arr.length - 1;
      var j;
      var jLen;
      var ownStyles = [];
      var ownModifiers = [];
      var currentTransform;
      var modifier;
      var processedPos;
      for (i = len; i >= 0; i -= 1) {
        processedPos = this.searchProcessedElement(arr[i]);
        if (!processedPos) {
          arr[i]._render = render3;
        } else {
          itemsData[i] = prevViewData[processedPos - 1];
        }
        if (arr[i].ty === "fl" || arr[i].ty === "st" || arr[i].ty === "gf" || arr[i].ty === "gs" || arr[i].ty === "no") {
          if (!processedPos) {
            itemsData[i] = this.createStyleElement(arr[i], level);
          } else {
            itemsData[i].style.closed = false;
          }
          if (arr[i]._render) {
            if (itemsData[i].style.pElem.parentNode !== container) {
              container.appendChild(itemsData[i].style.pElem);
            }
          }
          ownStyles.push(itemsData[i].style);
        } else if (arr[i].ty === "gr") {
          if (!processedPos) {
            itemsData[i] = this.createGroupElement(arr[i]);
          } else {
            jLen = itemsData[i].it.length;
            for (j = 0; j < jLen; j += 1) {
              itemsData[i].prevViewData[j] = itemsData[i].it[j];
            }
          }
          this.searchShapes(arr[i].it, itemsData[i].it, itemsData[i].prevViewData, itemsData[i].gr, level + 1, ownTransformers, render3);
          if (arr[i]._render) {
            if (itemsData[i].gr.parentNode !== container) {
              container.appendChild(itemsData[i].gr);
            }
          }
        } else if (arr[i].ty === "tr") {
          if (!processedPos) {
            itemsData[i] = this.createTransformElement(arr[i], container);
          }
          currentTransform = itemsData[i].transform;
          ownTransformers.push(currentTransform);
        } else if (arr[i].ty === "sh" || arr[i].ty === "rc" || arr[i].ty === "el" || arr[i].ty === "sr") {
          if (!processedPos) {
            itemsData[i] = this.createShapeElement(arr[i], ownTransformers, level);
          }
          this.setElementStyles(itemsData[i]);
        } else if (arr[i].ty === "tm" || arr[i].ty === "rd" || arr[i].ty === "ms" || arr[i].ty === "pb" || arr[i].ty === "zz" || arr[i].ty === "op") {
          if (!processedPos) {
            modifier = ShapeModifiers.getModifier(arr[i].ty);
            modifier.init(this, arr[i]);
            itemsData[i] = modifier;
            this.shapeModifiers.push(modifier);
          } else {
            modifier = itemsData[i];
            modifier.closed = false;
          }
          ownModifiers.push(modifier);
        } else if (arr[i].ty === "rp") {
          if (!processedPos) {
            modifier = ShapeModifiers.getModifier(arr[i].ty);
            itemsData[i] = modifier;
            modifier.init(this, arr, i, itemsData);
            this.shapeModifiers.push(modifier);
            render3 = false;
          } else {
            modifier = itemsData[i];
            modifier.closed = true;
          }
          ownModifiers.push(modifier);
        }
        this.addProcessedElement(arr[i], i + 1);
      }
      len = ownStyles.length;
      for (i = 0; i < len; i += 1) {
        ownStyles[i].closed = true;
      }
      len = ownModifiers.length;
      for (i = 0; i < len; i += 1) {
        ownModifiers[i].closed = true;
      }
    };
    SVGShapeElement.prototype.renderInnerContent = function() {
      this.renderModifiers();
      var i;
      var len = this.stylesList.length;
      for (i = 0; i < len; i += 1) {
        this.stylesList[i].reset();
      }
      this.renderShape();
      for (i = 0; i < len; i += 1) {
        if (this.stylesList[i]._mdf || this._isFirstFrame) {
          if (this.stylesList[i].msElem) {
            this.stylesList[i].msElem.setAttribute("d", this.stylesList[i].d);
            this.stylesList[i].d = "M0 0" + this.stylesList[i].d;
          }
          this.stylesList[i].pElem.setAttribute("d", this.stylesList[i].d || "M0 0");
        }
      }
    };
    SVGShapeElement.prototype.renderShape = function() {
      var i;
      var len = this.animatedContents.length;
      var animatedContent;
      for (i = 0; i < len; i += 1) {
        animatedContent = this.animatedContents[i];
        if ((this._isFirstFrame || animatedContent.element._isAnimated) && animatedContent.data !== true) {
          animatedContent.fn(animatedContent.data, animatedContent.element, this._isFirstFrame);
        }
      }
    };
    SVGShapeElement.prototype.destroy = function() {
      this.destroyBaseElement();
      this.shapesData = null;
      this.itemsData = null;
    };
    function LetterProps(o, sw, sc, fc, m, p) {
      this.o = o;
      this.sw = sw;
      this.sc = sc;
      this.fc = fc;
      this.m = m;
      this.p = p;
      this._mdf = {
        o: true,
        sw: !!sw,
        sc: !!sc,
        fc: !!fc,
        m: true,
        p: true
      };
    }
    LetterProps.prototype.update = function(o, sw, sc, fc, m, p) {
      this._mdf.o = false;
      this._mdf.sw = false;
      this._mdf.sc = false;
      this._mdf.fc = false;
      this._mdf.m = false;
      this._mdf.p = false;
      var updated = false;
      if (this.o !== o) {
        this.o = o;
        this._mdf.o = true;
        updated = true;
      }
      if (this.sw !== sw) {
        this.sw = sw;
        this._mdf.sw = true;
        updated = true;
      }
      if (this.sc !== sc) {
        this.sc = sc;
        this._mdf.sc = true;
        updated = true;
      }
      if (this.fc !== fc) {
        this.fc = fc;
        this._mdf.fc = true;
        updated = true;
      }
      if (this.m !== m) {
        this.m = m;
        this._mdf.m = true;
        updated = true;
      }
      if (p.length && (this.p[0] !== p[0] || this.p[1] !== p[1] || this.p[4] !== p[4] || this.p[5] !== p[5] || this.p[12] !== p[12] || this.p[13] !== p[13])) {
        this.p = p;
        this._mdf.p = true;
        updated = true;
      }
      return updated;
    };
    function TextProperty(elem2, data2) {
      this._frameId = initialDefaultFrame;
      this.pv = "";
      this.v = "";
      this.kf = false;
      this._isFirstFrame = true;
      this._mdf = false;
      if (data2.d && data2.d.sid) {
        data2.d = elem2.globalData.slotManager.getProp(data2.d);
      }
      this.data = data2;
      this.elem = elem2;
      this.comp = this.elem.comp;
      this.keysIndex = 0;
      this.canResize = false;
      this.minimumFontSize = 1;
      this.effectsSequence = [];
      this.currentData = {
        ascent: 0,
        boxWidth: this.defaultBoxWidth,
        f: "",
        fStyle: "",
        fWeight: "",
        fc: "",
        j: "",
        justifyOffset: "",
        l: [],
        lh: 0,
        lineWidths: [],
        ls: "",
        of: "",
        s: "",
        sc: "",
        sw: 0,
        t: 0,
        tr: 0,
        sz: 0,
        ps: null,
        fillColorAnim: false,
        strokeColorAnim: false,
        strokeWidthAnim: false,
        yOffset: 0,
        finalSize: 0,
        finalText: [],
        finalLineHeight: 0,
        __complete: false
      };
      this.copyData(this.currentData, this.data.d.k[0].s);
      if (!this.searchProperty()) {
        this.completeTextData(this.currentData);
      }
    }
    TextProperty.prototype.defaultBoxWidth = [0, 0];
    TextProperty.prototype.copyData = function(obj, data2) {
      for (var s in data2) {
        if (Object.prototype.hasOwnProperty.call(data2, s)) {
          obj[s] = data2[s];
        }
      }
      return obj;
    };
    TextProperty.prototype.setCurrentData = function(data2) {
      if (!data2.__complete) {
        this.completeTextData(data2);
      }
      this.currentData = data2;
      this.currentData.boxWidth = this.currentData.boxWidth || this.defaultBoxWidth;
      this._mdf = true;
    };
    TextProperty.prototype.searchProperty = function() {
      return this.searchKeyframes();
    };
    TextProperty.prototype.searchKeyframes = function() {
      this.kf = this.data.d.k.length > 1;
      if (this.kf) {
        this.addEffect(this.getKeyframeValue.bind(this));
      }
      return this.kf;
    };
    TextProperty.prototype.addEffect = function(effectFunction) {
      this.effectsSequence.push(effectFunction);
      this.elem.addDynamicProperty(this);
    };
    TextProperty.prototype.getValue = function(_finalValue) {
      if ((this.elem.globalData.frameId === this.frameId || !this.effectsSequence.length) && !_finalValue) {
        return;
      }
      this.currentData.t = this.data.d.k[this.keysIndex].s.t;
      var currentValue = this.currentData;
      var currentIndex = this.keysIndex;
      if (this.lock) {
        this.setCurrentData(this.currentData);
        return;
      }
      this.lock = true;
      this._mdf = false;
      var i;
      var len = this.effectsSequence.length;
      var finalValue = _finalValue || this.data.d.k[this.keysIndex].s;
      for (i = 0; i < len; i += 1) {
        if (currentIndex !== this.keysIndex) {
          finalValue = this.effectsSequence[i](finalValue, finalValue.t);
        } else {
          finalValue = this.effectsSequence[i](this.currentData, finalValue.t);
        }
      }
      if (currentValue !== finalValue) {
        this.setCurrentData(finalValue);
      }
      this.v = this.currentData;
      this.pv = this.v;
      this.lock = false;
      this.frameId = this.elem.globalData.frameId;
    };
    TextProperty.prototype.getKeyframeValue = function() {
      var textKeys = this.data.d.k;
      var frameNum = this.elem.comp.renderedFrame;
      var i = 0;
      var len = textKeys.length;
      while (i <= len - 1) {
        if (i === len - 1 || textKeys[i + 1].t > frameNum) {
          break;
        }
        i += 1;
      }
      if (this.keysIndex !== i) {
        this.keysIndex = i;
      }
      return this.data.d.k[this.keysIndex].s;
    };
    TextProperty.prototype.buildFinalText = function(text2) {
      var charactersArray = [];
      var i = 0;
      var len = text2.length;
      var charCode;
      var secondCharCode;
      var shouldCombine = false;
      var shouldCombineNext = false;
      var currentChars = "";
      while (i < len) {
        shouldCombine = shouldCombineNext;
        shouldCombineNext = false;
        charCode = text2.charCodeAt(i);
        currentChars = text2.charAt(i);
        if (FontManager.isCombinedCharacter(charCode)) {
          shouldCombine = true;
        } else if (charCode >= 55296 && charCode <= 56319) {
          if (FontManager.isRegionalFlag(text2, i)) {
            currentChars = text2.substr(i, 14);
          } else {
            secondCharCode = text2.charCodeAt(i + 1);
            if (secondCharCode >= 56320 && secondCharCode <= 57343) {
              if (FontManager.isModifier(charCode, secondCharCode)) {
                currentChars = text2.substr(i, 2);
                shouldCombine = true;
              } else if (FontManager.isFlagEmoji(text2.substr(i, 4))) {
                currentChars = text2.substr(i, 4);
              } else {
                currentChars = text2.substr(i, 2);
              }
            }
          }
        } else if (charCode > 56319) {
          secondCharCode = text2.charCodeAt(i + 1);
          if (FontManager.isVariationSelector(charCode)) {
            shouldCombine = true;
          }
        } else if (FontManager.isZeroWidthJoiner(charCode)) {
          shouldCombine = true;
          shouldCombineNext = true;
        }
        if (shouldCombine) {
          charactersArray[charactersArray.length - 1] += currentChars;
          shouldCombine = false;
        } else {
          charactersArray.push(currentChars);
        }
        i += currentChars.length;
      }
      return charactersArray;
    };
    TextProperty.prototype.completeTextData = function(documentData) {
      documentData.__complete = true;
      var fontManager = this.elem.globalData.fontManager;
      var data2 = this.data;
      var letters = [];
      var i;
      var len;
      var newLineFlag;
      var index2 = 0;
      var val2;
      var anchorGrouping = data2.m.g;
      var currentSize = 0;
      var currentPos = 0;
      var currentLine = 0;
      var lineWidths = [];
      var lineWidth = 0;
      var maxLineWidth = 0;
      var j;
      var jLen;
      var fontData = fontManager.getFontByName(documentData.f);
      var charData;
      var cLength = 0;
      var fontProps = getFontProperties(fontData);
      documentData.fWeight = fontProps.weight;
      documentData.fStyle = fontProps.style;
      documentData.finalSize = documentData.s;
      documentData.finalText = this.buildFinalText(documentData.t);
      len = documentData.finalText.length;
      documentData.finalLineHeight = documentData.lh;
      var trackingOffset = documentData.tr / 1e3 * documentData.finalSize;
      var charCode;
      if (documentData.sz) {
        var flag = true;
        var boxWidth = documentData.sz[0];
        var boxHeight = documentData.sz[1];
        var currentHeight;
        var finalText;
        while (flag) {
          finalText = this.buildFinalText(documentData.t);
          currentHeight = 0;
          lineWidth = 0;
          len = finalText.length;
          trackingOffset = documentData.tr / 1e3 * documentData.finalSize;
          var lastSpaceIndex = -1;
          for (i = 0; i < len; i += 1) {
            charCode = finalText[i].charCodeAt(0);
            newLineFlag = false;
            if (finalText[i] === " ") {
              lastSpaceIndex = i;
            } else if (charCode === 13 || charCode === 3) {
              lineWidth = 0;
              newLineFlag = true;
              currentHeight += documentData.finalLineHeight || documentData.finalSize * 1.2;
            }
            if (fontManager.chars) {
              charData = fontManager.getCharData(finalText[i], fontData.fStyle, fontData.fFamily);
              cLength = newLineFlag ? 0 : charData.w * documentData.finalSize / 100;
            } else {
              cLength = fontManager.measureText(finalText[i], documentData.f, documentData.finalSize);
            }
            if (lineWidth + cLength > boxWidth && finalText[i] !== " ") {
              if (lastSpaceIndex === -1) {
                len += 1;
              } else {
                i = lastSpaceIndex;
              }
              currentHeight += documentData.finalLineHeight || documentData.finalSize * 1.2;
              finalText.splice(i, lastSpaceIndex === i ? 1 : 0, "\r");
              lastSpaceIndex = -1;
              lineWidth = 0;
            } else {
              lineWidth += cLength;
              lineWidth += trackingOffset;
            }
          }
          currentHeight += fontData.ascent * documentData.finalSize / 100;
          if (this.canResize && documentData.finalSize > this.minimumFontSize && boxHeight < currentHeight) {
            documentData.finalSize -= 1;
            documentData.finalLineHeight = documentData.finalSize * documentData.lh / documentData.s;
          } else {
            documentData.finalText = finalText;
            len = documentData.finalText.length;
            flag = false;
          }
        }
      }
      lineWidth = -trackingOffset;
      cLength = 0;
      var uncollapsedSpaces = 0;
      var currentChar;
      for (i = 0; i < len; i += 1) {
        newLineFlag = false;
        currentChar = documentData.finalText[i];
        charCode = currentChar.charCodeAt(0);
        if (charCode === 13 || charCode === 3) {
          uncollapsedSpaces = 0;
          lineWidths.push(lineWidth);
          maxLineWidth = lineWidth > maxLineWidth ? lineWidth : maxLineWidth;
          lineWidth = -2 * trackingOffset;
          val2 = "";
          newLineFlag = true;
          currentLine += 1;
        } else {
          val2 = currentChar;
        }
        if (fontManager.chars) {
          charData = fontManager.getCharData(currentChar, fontData.fStyle, fontManager.getFontByName(documentData.f).fFamily);
          cLength = newLineFlag ? 0 : charData.w * documentData.finalSize / 100;
        } else {
          cLength = fontManager.measureText(val2, documentData.f, documentData.finalSize);
        }
        if (currentChar === " ") {
          uncollapsedSpaces += cLength + trackingOffset;
        } else {
          lineWidth += cLength + trackingOffset + uncollapsedSpaces;
          uncollapsedSpaces = 0;
        }
        letters.push({
          l: cLength,
          an: cLength,
          add: currentSize,
          n: newLineFlag,
          anIndexes: [],
          val: val2,
          line: currentLine,
          animatorJustifyOffset: 0
        });
        if (anchorGrouping == 2) {
          currentSize += cLength;
          if (val2 === "" || val2 === " " || i === len - 1) {
            if (val2 === "" || val2 === " ") {
              currentSize -= cLength;
            }
            while (currentPos <= i) {
              letters[currentPos].an = currentSize;
              letters[currentPos].ind = index2;
              letters[currentPos].extra = cLength;
              currentPos += 1;
            }
            index2 += 1;
            currentSize = 0;
          }
        } else if (anchorGrouping == 3) {
          currentSize += cLength;
          if (val2 === "" || i === len - 1) {
            if (val2 === "") {
              currentSize -= cLength;
            }
            while (currentPos <= i) {
              letters[currentPos].an = currentSize;
              letters[currentPos].ind = index2;
              letters[currentPos].extra = cLength;
              currentPos += 1;
            }
            currentSize = 0;
            index2 += 1;
          }
        } else {
          letters[index2].ind = index2;
          letters[index2].extra = 0;
          index2 += 1;
        }
      }
      documentData.l = letters;
      maxLineWidth = lineWidth > maxLineWidth ? lineWidth : maxLineWidth;
      lineWidths.push(lineWidth);
      if (documentData.sz) {
        documentData.boxWidth = documentData.sz[0];
        documentData.justifyOffset = 0;
      } else {
        documentData.boxWidth = maxLineWidth;
        switch (documentData.j) {
          case 1:
            documentData.justifyOffset = -documentData.boxWidth;
            break;
          case 2:
            documentData.justifyOffset = -documentData.boxWidth / 2;
            break;
          default:
            documentData.justifyOffset = 0;
        }
      }
      documentData.lineWidths = lineWidths;
      var animators = data2.a;
      var animatorData;
      var letterData;
      jLen = animators.length;
      var based;
      var ind;
      var indexes = [];
      for (j = 0; j < jLen; j += 1) {
        animatorData = animators[j];
        if (animatorData.a.sc) {
          documentData.strokeColorAnim = true;
        }
        if (animatorData.a.sw) {
          documentData.strokeWidthAnim = true;
        }
        if (animatorData.a.fc || animatorData.a.fh || animatorData.a.fs || animatorData.a.fb) {
          documentData.fillColorAnim = true;
        }
        ind = 0;
        based = animatorData.s.b;
        for (i = 0; i < len; i += 1) {
          letterData = letters[i];
          letterData.anIndexes[j] = ind;
          if (based == 1 && letterData.val !== "" || based == 2 && letterData.val !== "" && letterData.val !== " " || based == 3 && (letterData.n || letterData.val == " " || i == len - 1) || based == 4 && (letterData.n || i == len - 1)) {
            if (animatorData.s.rn === 1) {
              indexes.push(ind);
            }
            ind += 1;
          }
        }
        data2.a[j].s.totalChars = ind;
        var currentInd = -1;
        var newInd;
        if (animatorData.s.rn === 1) {
          for (i = 0; i < len; i += 1) {
            letterData = letters[i];
            if (currentInd != letterData.anIndexes[j]) {
              currentInd = letterData.anIndexes[j];
              newInd = indexes.splice(Math.floor(Math.random() * indexes.length), 1)[0];
            }
            letterData.anIndexes[j] = newInd;
          }
        }
      }
      documentData.yOffset = documentData.finalLineHeight || documentData.finalSize * 1.2;
      documentData.ls = documentData.ls || 0;
      documentData.ascent = fontData.ascent * documentData.finalSize / 100;
    };
    TextProperty.prototype.updateDocumentData = function(newData, index2) {
      index2 = index2 === void 0 ? this.keysIndex : index2;
      var dData = this.copyData({}, this.data.d.k[index2].s);
      dData = this.copyData(dData, newData);
      this.data.d.k[index2].s = dData;
      this.recalculate(index2);
      this.setCurrentData(dData);
      this.elem.addDynamicProperty(this);
    };
    TextProperty.prototype.recalculate = function(index2) {
      var dData = this.data.d.k[index2].s;
      dData.__complete = false;
      this.keysIndex = 0;
      this._isFirstFrame = true;
      this.getValue(dData);
    };
    TextProperty.prototype.canResizeFont = function(_canResize) {
      this.canResize = _canResize;
      this.recalculate(this.keysIndex);
      this.elem.addDynamicProperty(this);
    };
    TextProperty.prototype.setMinimumFontSize = function(_fontValue) {
      this.minimumFontSize = Math.floor(_fontValue) || 1;
      this.recalculate(this.keysIndex);
      this.elem.addDynamicProperty(this);
    };
    var TextSelectorProp = function() {
      var max = Math.max;
      var min = Math.min;
      var floor = Math.floor;
      function TextSelectorPropFactory(elem2, data2) {
        this._currentTextLength = -1;
        this.k = false;
        this.data = data2;
        this.elem = elem2;
        this.comp = elem2.comp;
        this.finalS = 0;
        this.finalE = 0;
        this.initDynamicPropertyContainer(elem2);
        this.s = PropertyFactory.getProp(elem2, data2.s || {
          k: 0
        }, 0, 0, this);
        if ("e" in data2) {
          this.e = PropertyFactory.getProp(elem2, data2.e, 0, 0, this);
        } else {
          this.e = {
            v: 100
          };
        }
        this.o = PropertyFactory.getProp(elem2, data2.o || {
          k: 0
        }, 0, 0, this);
        this.xe = PropertyFactory.getProp(elem2, data2.xe || {
          k: 0
        }, 0, 0, this);
        this.ne = PropertyFactory.getProp(elem2, data2.ne || {
          k: 0
        }, 0, 0, this);
        this.sm = PropertyFactory.getProp(elem2, data2.sm || {
          k: 100
        }, 0, 0, this);
        this.a = PropertyFactory.getProp(elem2, data2.a, 0, 0.01, this);
        if (!this.dynamicProperties.length) {
          this.getValue();
        }
      }
      TextSelectorPropFactory.prototype = {
        getMult: function getMult(ind) {
          if (this._currentTextLength !== this.elem.textProperty.currentData.l.length) {
            this.getValue();
          }
          var x1 = 0;
          var y1 = 0;
          var x2 = 1;
          var y2 = 1;
          if (this.ne.v > 0) {
            x1 = this.ne.v / 100;
          } else {
            y1 = -this.ne.v / 100;
          }
          if (this.xe.v > 0) {
            x2 = 1 - this.xe.v / 100;
          } else {
            y2 = 1 + this.xe.v / 100;
          }
          var easer = BezierFactory.getBezierEasing(x1, y1, x2, y2).get;
          var mult = 0;
          var s = this.finalS;
          var e = this.finalE;
          var type = this.data.sh;
          if (type === 2) {
            if (e === s) {
              mult = ind >= e ? 1 : 0;
            } else {
              mult = max(0, min(0.5 / (e - s) + (ind - s) / (e - s), 1));
            }
            mult = easer(mult);
          } else if (type === 3) {
            if (e === s) {
              mult = ind >= e ? 0 : 1;
            } else {
              mult = 1 - max(0, min(0.5 / (e - s) + (ind - s) / (e - s), 1));
            }
            mult = easer(mult);
          } else if (type === 4) {
            if (e === s) {
              mult = 0;
            } else {
              mult = max(0, min(0.5 / (e - s) + (ind - s) / (e - s), 1));
              if (mult < 0.5) {
                mult *= 2;
              } else {
                mult = 1 - 2 * (mult - 0.5);
              }
            }
            mult = easer(mult);
          } else if (type === 5) {
            if (e === s) {
              mult = 0;
            } else {
              var tot = e - s;
              ind = min(max(0, ind + 0.5 - s), e - s);
              var x = -tot / 2 + ind;
              var a = tot / 2;
              mult = Math.sqrt(1 - x * x / (a * a));
            }
            mult = easer(mult);
          } else if (type === 6) {
            if (e === s) {
              mult = 0;
            } else {
              ind = min(max(0, ind + 0.5 - s), e - s);
              mult = (1 + Math.cos(Math.PI + Math.PI * 2 * ind / (e - s))) / 2;
            }
            mult = easer(mult);
          } else {
            if (ind >= floor(s)) {
              if (ind - s < 0) {
                mult = max(0, min(min(e, 1) - (s - ind), 1));
              } else {
                mult = max(0, min(e - ind, 1));
              }
            }
            mult = easer(mult);
          }
          if (this.sm.v !== 100) {
            var smoothness = this.sm.v * 0.01;
            if (smoothness === 0) {
              smoothness = 1e-8;
            }
            var threshold = 0.5 - smoothness * 0.5;
            if (mult < threshold) {
              mult = 0;
            } else {
              mult = (mult - threshold) / smoothness;
              if (mult > 1) {
                mult = 1;
              }
            }
          }
          return mult * this.a.v;
        },
        getValue: function getValue(newCharsFlag) {
          this.iterateDynamicProperties();
          this._mdf = newCharsFlag || this._mdf;
          this._currentTextLength = this.elem.textProperty.currentData.l.length || 0;
          if (newCharsFlag && this.data.r === 2) {
            this.e.v = this._currentTextLength;
          }
          var divisor = this.data.r === 2 ? 1 : 100 / this.data.totalChars;
          var o = this.o.v / divisor;
          var s = this.s.v / divisor + o;
          var e = this.e.v / divisor + o;
          if (s > e) {
            var _s = s;
            s = e;
            e = _s;
          }
          this.finalS = s;
          this.finalE = e;
        }
      };
      extendPrototype([DynamicPropertyContainer], TextSelectorPropFactory);
      function getTextSelectorProp(elem2, data2, arr) {
        return new TextSelectorPropFactory(elem2, data2);
      }
      return {
        getTextSelectorProp
      };
    }();
    function TextAnimatorDataProperty(elem2, animatorProps, container) {
      var defaultData = {
        propType: false
      };
      var getProp = PropertyFactory.getProp;
      var textAnimatorAnimatables = animatorProps.a;
      this.a = {
        r: textAnimatorAnimatables.r ? getProp(elem2, textAnimatorAnimatables.r, 0, degToRads, container) : defaultData,
        rx: textAnimatorAnimatables.rx ? getProp(elem2, textAnimatorAnimatables.rx, 0, degToRads, container) : defaultData,
        ry: textAnimatorAnimatables.ry ? getProp(elem2, textAnimatorAnimatables.ry, 0, degToRads, container) : defaultData,
        sk: textAnimatorAnimatables.sk ? getProp(elem2, textAnimatorAnimatables.sk, 0, degToRads, container) : defaultData,
        sa: textAnimatorAnimatables.sa ? getProp(elem2, textAnimatorAnimatables.sa, 0, degToRads, container) : defaultData,
        s: textAnimatorAnimatables.s ? getProp(elem2, textAnimatorAnimatables.s, 1, 0.01, container) : defaultData,
        a: textAnimatorAnimatables.a ? getProp(elem2, textAnimatorAnimatables.a, 1, 0, container) : defaultData,
        o: textAnimatorAnimatables.o ? getProp(elem2, textAnimatorAnimatables.o, 0, 0.01, container) : defaultData,
        p: textAnimatorAnimatables.p ? getProp(elem2, textAnimatorAnimatables.p, 1, 0, container) : defaultData,
        sw: textAnimatorAnimatables.sw ? getProp(elem2, textAnimatorAnimatables.sw, 0, 0, container) : defaultData,
        sc: textAnimatorAnimatables.sc ? getProp(elem2, textAnimatorAnimatables.sc, 1, 0, container) : defaultData,
        fc: textAnimatorAnimatables.fc ? getProp(elem2, textAnimatorAnimatables.fc, 1, 0, container) : defaultData,
        fh: textAnimatorAnimatables.fh ? getProp(elem2, textAnimatorAnimatables.fh, 0, 0, container) : defaultData,
        fs: textAnimatorAnimatables.fs ? getProp(elem2, textAnimatorAnimatables.fs, 0, 0.01, container) : defaultData,
        fb: textAnimatorAnimatables.fb ? getProp(elem2, textAnimatorAnimatables.fb, 0, 0.01, container) : defaultData,
        t: textAnimatorAnimatables.t ? getProp(elem2, textAnimatorAnimatables.t, 0, 0, container) : defaultData
      };
      this.s = TextSelectorProp.getTextSelectorProp(elem2, animatorProps.s, container);
      this.s.t = animatorProps.s.t;
    }
    function TextAnimatorProperty(textData, renderType, elem2) {
      this._isFirstFrame = true;
      this._hasMaskedPath = false;
      this._frameId = -1;
      this._textData = textData;
      this._renderType = renderType;
      this._elem = elem2;
      this._animatorsData = createSizedArray(this._textData.a.length);
      this._pathData = {};
      this._moreOptions = {
        alignment: {}
      };
      this.renderedLetters = [];
      this.lettersChangedFlag = false;
      this.initDynamicPropertyContainer(elem2);
    }
    TextAnimatorProperty.prototype.searchProperties = function() {
      var i;
      var len = this._textData.a.length;
      var animatorProps;
      var getProp = PropertyFactory.getProp;
      for (i = 0; i < len; i += 1) {
        animatorProps = this._textData.a[i];
        this._animatorsData[i] = new TextAnimatorDataProperty(this._elem, animatorProps, this);
      }
      if (this._textData.p && "m" in this._textData.p) {
        this._pathData = {
          a: getProp(this._elem, this._textData.p.a, 0, 0, this),
          f: getProp(this._elem, this._textData.p.f, 0, 0, this),
          l: getProp(this._elem, this._textData.p.l, 0, 0, this),
          r: getProp(this._elem, this._textData.p.r, 0, 0, this),
          p: getProp(this._elem, this._textData.p.p, 0, 0, this),
          m: this._elem.maskManager.getMaskProperty(this._textData.p.m)
        };
        this._hasMaskedPath = true;
      } else {
        this._hasMaskedPath = false;
      }
      this._moreOptions.alignment = getProp(this._elem, this._textData.m.a, 1, 0, this);
    };
    TextAnimatorProperty.prototype.getMeasures = function(documentData, lettersChangedFlag) {
      this.lettersChangedFlag = lettersChangedFlag;
      if (!this._mdf && !this._isFirstFrame && !lettersChangedFlag && (!this._hasMaskedPath || !this._pathData.m._mdf)) {
        return;
      }
      this._isFirstFrame = false;
      var alignment = this._moreOptions.alignment.v;
      var animators = this._animatorsData;
      var textData = this._textData;
      var matrixHelper = this.mHelper;
      var renderType = this._renderType;
      var renderedLettersCount = this.renderedLetters.length;
      var xPos;
      var yPos;
      var i;
      var len;
      var letters = documentData.l;
      var pathInfo;
      var currentLength;
      var currentPoint;
      var segmentLength;
      var flag;
      var pointInd;
      var segmentInd;
      var prevPoint;
      var points;
      var segments;
      var partialLength;
      var totalLength;
      var perc;
      var tanAngle;
      var mask2;
      if (this._hasMaskedPath) {
        mask2 = this._pathData.m;
        if (!this._pathData.n || this._pathData._mdf) {
          var paths = mask2.v;
          if (this._pathData.r.v) {
            paths = paths.reverse();
          }
          pathInfo = {
            tLength: 0,
            segments: []
          };
          len = paths._length - 1;
          var bezierData;
          totalLength = 0;
          for (i = 0; i < len; i += 1) {
            bezierData = bez.buildBezierData(paths.v[i], paths.v[i + 1], [paths.o[i][0] - paths.v[i][0], paths.o[i][1] - paths.v[i][1]], [paths.i[i + 1][0] - paths.v[i + 1][0], paths.i[i + 1][1] - paths.v[i + 1][1]]);
            pathInfo.tLength += bezierData.segmentLength;
            pathInfo.segments.push(bezierData);
            totalLength += bezierData.segmentLength;
          }
          i = len;
          if (mask2.v.c) {
            bezierData = bez.buildBezierData(paths.v[i], paths.v[0], [paths.o[i][0] - paths.v[i][0], paths.o[i][1] - paths.v[i][1]], [paths.i[0][0] - paths.v[0][0], paths.i[0][1] - paths.v[0][1]]);
            pathInfo.tLength += bezierData.segmentLength;
            pathInfo.segments.push(bezierData);
            totalLength += bezierData.segmentLength;
          }
          this._pathData.pi = pathInfo;
        }
        pathInfo = this._pathData.pi;
        currentLength = this._pathData.f.v;
        segmentInd = 0;
        pointInd = 1;
        segmentLength = 0;
        flag = true;
        segments = pathInfo.segments;
        if (currentLength < 0 && mask2.v.c) {
          if (pathInfo.tLength < Math.abs(currentLength)) {
            currentLength = -Math.abs(currentLength) % pathInfo.tLength;
          }
          segmentInd = segments.length - 1;
          points = segments[segmentInd].points;
          pointInd = points.length - 1;
          while (currentLength < 0) {
            currentLength += points[pointInd].partialLength;
            pointInd -= 1;
            if (pointInd < 0) {
              segmentInd -= 1;
              points = segments[segmentInd].points;
              pointInd = points.length - 1;
            }
          }
        }
        points = segments[segmentInd].points;
        prevPoint = points[pointInd - 1];
        currentPoint = points[pointInd];
        partialLength = currentPoint.partialLength;
      }
      len = letters.length;
      xPos = 0;
      yPos = 0;
      var yOff = documentData.finalSize * 1.2 * 0.714;
      var firstLine = true;
      var animatorProps;
      var animatorSelector;
      var j;
      var jLen;
      var letterValue;
      jLen = animators.length;
      var mult;
      var ind = -1;
      var offf;
      var xPathPos;
      var yPathPos;
      var initPathPos = currentLength;
      var initSegmentInd = segmentInd;
      var initPointInd = pointInd;
      var currentLine = -1;
      var elemOpacity;
      var sc;
      var sw;
      var fc;
      var k;
      var letterSw;
      var letterSc;
      var letterFc;
      var letterM = "";
      var letterP = this.defaultPropsArray;
      var letterO;
      if (documentData.j === 2 || documentData.j === 1) {
        var animatorJustifyOffset = 0;
        var animatorFirstCharOffset = 0;
        var justifyOffsetMult = documentData.j === 2 ? -0.5 : -1;
        var lastIndex = 0;
        var isNewLine = true;
        for (i = 0; i < len; i += 1) {
          if (letters[i].n) {
            if (animatorJustifyOffset) {
              animatorJustifyOffset += animatorFirstCharOffset;
            }
            while (lastIndex < i) {
              letters[lastIndex].animatorJustifyOffset = animatorJustifyOffset;
              lastIndex += 1;
            }
            animatorJustifyOffset = 0;
            isNewLine = true;
          } else {
            for (j = 0; j < jLen; j += 1) {
              animatorProps = animators[j].a;
              if (animatorProps.t.propType) {
                if (isNewLine && documentData.j === 2) {
                  animatorFirstCharOffset += animatorProps.t.v * justifyOffsetMult;
                }
                animatorSelector = animators[j].s;
                mult = animatorSelector.getMult(letters[i].anIndexes[j], textData.a[j].s.totalChars);
                if (mult.length) {
                  animatorJustifyOffset += animatorProps.t.v * mult[0] * justifyOffsetMult;
                } else {
                  animatorJustifyOffset += animatorProps.t.v * mult * justifyOffsetMult;
                }
              }
            }
            isNewLine = false;
          }
        }
        if (animatorJustifyOffset) {
          animatorJustifyOffset += animatorFirstCharOffset;
        }
        while (lastIndex < i) {
          letters[lastIndex].animatorJustifyOffset = animatorJustifyOffset;
          lastIndex += 1;
        }
      }
      for (i = 0; i < len; i += 1) {
        matrixHelper.reset();
        elemOpacity = 1;
        if (letters[i].n) {
          xPos = 0;
          yPos += documentData.yOffset;
          yPos += firstLine ? 1 : 0;
          currentLength = initPathPos;
          firstLine = false;
          if (this._hasMaskedPath) {
            segmentInd = initSegmentInd;
            pointInd = initPointInd;
            points = segments[segmentInd].points;
            prevPoint = points[pointInd - 1];
            currentPoint = points[pointInd];
            partialLength = currentPoint.partialLength;
            segmentLength = 0;
          }
          letterM = "";
          letterFc = "";
          letterSw = "";
          letterO = "";
          letterP = this.defaultPropsArray;
        } else {
          if (this._hasMaskedPath) {
            if (currentLine !== letters[i].line) {
              switch (documentData.j) {
                case 1:
                  currentLength += totalLength - documentData.lineWidths[letters[i].line];
                  break;
                case 2:
                  currentLength += (totalLength - documentData.lineWidths[letters[i].line]) / 2;
                  break;
              }
              currentLine = letters[i].line;
            }
            if (ind !== letters[i].ind) {
              if (letters[ind]) {
                currentLength += letters[ind].extra;
              }
              currentLength += letters[i].an / 2;
              ind = letters[i].ind;
            }
            currentLength += alignment[0] * letters[i].an * 5e-3;
            var animatorOffset = 0;
            for (j = 0; j < jLen; j += 1) {
              animatorProps = animators[j].a;
              if (animatorProps.p.propType) {
                animatorSelector = animators[j].s;
                mult = animatorSelector.getMult(letters[i].anIndexes[j], textData.a[j].s.totalChars);
                if (mult.length) {
                  animatorOffset += animatorProps.p.v[0] * mult[0];
                } else {
                  animatorOffset += animatorProps.p.v[0] * mult;
                }
              }
              if (animatorProps.a.propType) {
                animatorSelector = animators[j].s;
                mult = animatorSelector.getMult(letters[i].anIndexes[j], textData.a[j].s.totalChars);
                if (mult.length) {
                  animatorOffset += animatorProps.a.v[0] * mult[0];
                } else {
                  animatorOffset += animatorProps.a.v[0] * mult;
                }
              }
            }
            flag = true;
            if (this._pathData.a.v) {
              currentLength = letters[0].an * 0.5 + (totalLength - this._pathData.f.v - letters[0].an * 0.5 - letters[letters.length - 1].an * 0.5) * ind / (len - 1);
              currentLength += this._pathData.f.v;
            }
            while (flag) {
              if (segmentLength + partialLength >= currentLength + animatorOffset || !points) {
                perc = (currentLength + animatorOffset - segmentLength) / currentPoint.partialLength;
                xPathPos = prevPoint.point[0] + (currentPoint.point[0] - prevPoint.point[0]) * perc;
                yPathPos = prevPoint.point[1] + (currentPoint.point[1] - prevPoint.point[1]) * perc;
                matrixHelper.translate(-alignment[0] * letters[i].an * 5e-3, -(alignment[1] * yOff) * 0.01);
                flag = false;
              } else if (points) {
                segmentLength += currentPoint.partialLength;
                pointInd += 1;
                if (pointInd >= points.length) {
                  pointInd = 0;
                  segmentInd += 1;
                  if (!segments[segmentInd]) {
                    if (mask2.v.c) {
                      pointInd = 0;
                      segmentInd = 0;
                      points = segments[segmentInd].points;
                    } else {
                      segmentLength -= currentPoint.partialLength;
                      points = null;
                    }
                  } else {
                    points = segments[segmentInd].points;
                  }
                }
                if (points) {
                  prevPoint = currentPoint;
                  currentPoint = points[pointInd];
                  partialLength = currentPoint.partialLength;
                }
              }
            }
            offf = letters[i].an / 2 - letters[i].add;
            matrixHelper.translate(-offf, 0, 0);
          } else {
            offf = letters[i].an / 2 - letters[i].add;
            matrixHelper.translate(-offf, 0, 0);
            matrixHelper.translate(-alignment[0] * letters[i].an * 5e-3, -alignment[1] * yOff * 0.01, 0);
          }
          for (j = 0; j < jLen; j += 1) {
            animatorProps = animators[j].a;
            if (animatorProps.t.propType) {
              animatorSelector = animators[j].s;
              mult = animatorSelector.getMult(letters[i].anIndexes[j], textData.a[j].s.totalChars);
              if (xPos !== 0 || documentData.j !== 0) {
                if (this._hasMaskedPath) {
                  if (mult.length) {
                    currentLength += animatorProps.t.v * mult[0];
                  } else {
                    currentLength += animatorProps.t.v * mult;
                  }
                } else if (mult.length) {
                  xPos += animatorProps.t.v * mult[0];
                } else {
                  xPos += animatorProps.t.v * mult;
                }
              }
            }
          }
          if (documentData.strokeWidthAnim) {
            sw = documentData.sw || 0;
          }
          if (documentData.strokeColorAnim) {
            if (documentData.sc) {
              sc = [documentData.sc[0], documentData.sc[1], documentData.sc[2]];
            } else {
              sc = [0, 0, 0];
            }
          }
          if (documentData.fillColorAnim && documentData.fc) {
            fc = [documentData.fc[0], documentData.fc[1], documentData.fc[2]];
          }
          for (j = 0; j < jLen; j += 1) {
            animatorProps = animators[j].a;
            if (animatorProps.a.propType) {
              animatorSelector = animators[j].s;
              mult = animatorSelector.getMult(letters[i].anIndexes[j], textData.a[j].s.totalChars);
              if (mult.length) {
                matrixHelper.translate(-animatorProps.a.v[0] * mult[0], -animatorProps.a.v[1] * mult[1], animatorProps.a.v[2] * mult[2]);
              } else {
                matrixHelper.translate(-animatorProps.a.v[0] * mult, -animatorProps.a.v[1] * mult, animatorProps.a.v[2] * mult);
              }
            }
          }
          for (j = 0; j < jLen; j += 1) {
            animatorProps = animators[j].a;
            if (animatorProps.s.propType) {
              animatorSelector = animators[j].s;
              mult = animatorSelector.getMult(letters[i].anIndexes[j], textData.a[j].s.totalChars);
              if (mult.length) {
                matrixHelper.scale(1 + (animatorProps.s.v[0] - 1) * mult[0], 1 + (animatorProps.s.v[1] - 1) * mult[1], 1);
              } else {
                matrixHelper.scale(1 + (animatorProps.s.v[0] - 1) * mult, 1 + (animatorProps.s.v[1] - 1) * mult, 1);
              }
            }
          }
          for (j = 0; j < jLen; j += 1) {
            animatorProps = animators[j].a;
            animatorSelector = animators[j].s;
            mult = animatorSelector.getMult(letters[i].anIndexes[j], textData.a[j].s.totalChars);
            if (animatorProps.sk.propType) {
              if (mult.length) {
                matrixHelper.skewFromAxis(-animatorProps.sk.v * mult[0], animatorProps.sa.v * mult[1]);
              } else {
                matrixHelper.skewFromAxis(-animatorProps.sk.v * mult, animatorProps.sa.v * mult);
              }
            }
            if (animatorProps.r.propType) {
              if (mult.length) {
                matrixHelper.rotateZ(-animatorProps.r.v * mult[2]);
              } else {
                matrixHelper.rotateZ(-animatorProps.r.v * mult);
              }
            }
            if (animatorProps.ry.propType) {
              if (mult.length) {
                matrixHelper.rotateY(animatorProps.ry.v * mult[1]);
              } else {
                matrixHelper.rotateY(animatorProps.ry.v * mult);
              }
            }
            if (animatorProps.rx.propType) {
              if (mult.length) {
                matrixHelper.rotateX(animatorProps.rx.v * mult[0]);
              } else {
                matrixHelper.rotateX(animatorProps.rx.v * mult);
              }
            }
            if (animatorProps.o.propType) {
              if (mult.length) {
                elemOpacity += (animatorProps.o.v * mult[0] - elemOpacity) * mult[0];
              } else {
                elemOpacity += (animatorProps.o.v * mult - elemOpacity) * mult;
              }
            }
            if (documentData.strokeWidthAnim && animatorProps.sw.propType) {
              if (mult.length) {
                sw += animatorProps.sw.v * mult[0];
              } else {
                sw += animatorProps.sw.v * mult;
              }
            }
            if (documentData.strokeColorAnim && animatorProps.sc.propType) {
              for (k = 0; k < 3; k += 1) {
                if (mult.length) {
                  sc[k] += (animatorProps.sc.v[k] - sc[k]) * mult[0];
                } else {
                  sc[k] += (animatorProps.sc.v[k] - sc[k]) * mult;
                }
              }
            }
            if (documentData.fillColorAnim && documentData.fc) {
              if (animatorProps.fc.propType) {
                for (k = 0; k < 3; k += 1) {
                  if (mult.length) {
                    fc[k] += (animatorProps.fc.v[k] - fc[k]) * mult[0];
                  } else {
                    fc[k] += (animatorProps.fc.v[k] - fc[k]) * mult;
                  }
                }
              }
              if (animatorProps.fh.propType) {
                if (mult.length) {
                  fc = addHueToRGB(fc, animatorProps.fh.v * mult[0]);
                } else {
                  fc = addHueToRGB(fc, animatorProps.fh.v * mult);
                }
              }
              if (animatorProps.fs.propType) {
                if (mult.length) {
                  fc = addSaturationToRGB(fc, animatorProps.fs.v * mult[0]);
                } else {
                  fc = addSaturationToRGB(fc, animatorProps.fs.v * mult);
                }
              }
              if (animatorProps.fb.propType) {
                if (mult.length) {
                  fc = addBrightnessToRGB(fc, animatorProps.fb.v * mult[0]);
                } else {
                  fc = addBrightnessToRGB(fc, animatorProps.fb.v * mult);
                }
              }
            }
          }
          for (j = 0; j < jLen; j += 1) {
            animatorProps = animators[j].a;
            if (animatorProps.p.propType) {
              animatorSelector = animators[j].s;
              mult = animatorSelector.getMult(letters[i].anIndexes[j], textData.a[j].s.totalChars);
              if (this._hasMaskedPath) {
                if (mult.length) {
                  matrixHelper.translate(0, animatorProps.p.v[1] * mult[0], -animatorProps.p.v[2] * mult[1]);
                } else {
                  matrixHelper.translate(0, animatorProps.p.v[1] * mult, -animatorProps.p.v[2] * mult);
                }
              } else if (mult.length) {
                matrixHelper.translate(animatorProps.p.v[0] * mult[0], animatorProps.p.v[1] * mult[1], -animatorProps.p.v[2] * mult[2]);
              } else {
                matrixHelper.translate(animatorProps.p.v[0] * mult, animatorProps.p.v[1] * mult, -animatorProps.p.v[2] * mult);
              }
            }
          }
          if (documentData.strokeWidthAnim) {
            letterSw = sw < 0 ? 0 : sw;
          }
          if (documentData.strokeColorAnim) {
            letterSc = "rgb(" + Math.round(sc[0] * 255) + "," + Math.round(sc[1] * 255) + "," + Math.round(sc[2] * 255) + ")";
          }
          if (documentData.fillColorAnim && documentData.fc) {
            letterFc = "rgb(" + Math.round(fc[0] * 255) + "," + Math.round(fc[1] * 255) + "," + Math.round(fc[2] * 255) + ")";
          }
          if (this._hasMaskedPath) {
            matrixHelper.translate(0, -documentData.ls);
            matrixHelper.translate(0, alignment[1] * yOff * 0.01 + yPos, 0);
            if (this._pathData.p.v) {
              tanAngle = (currentPoint.point[1] - prevPoint.point[1]) / (currentPoint.point[0] - prevPoint.point[0]);
              var rot = Math.atan(tanAngle) * 180 / Math.PI;
              if (currentPoint.point[0] < prevPoint.point[0]) {
                rot += 180;
              }
              matrixHelper.rotate(-rot * Math.PI / 180);
            }
            matrixHelper.translate(xPathPos, yPathPos, 0);
            currentLength -= alignment[0] * letters[i].an * 5e-3;
            if (letters[i + 1] && ind !== letters[i + 1].ind) {
              currentLength += letters[i].an / 2;
              currentLength += documentData.tr * 1e-3 * documentData.finalSize;
            }
          } else {
            matrixHelper.translate(xPos, yPos, 0);
            if (documentData.ps) {
              matrixHelper.translate(documentData.ps[0], documentData.ps[1] + documentData.ascent, 0);
            }
            switch (documentData.j) {
              case 1:
                matrixHelper.translate(letters[i].animatorJustifyOffset + documentData.justifyOffset + (documentData.boxWidth - documentData.lineWidths[letters[i].line]), 0, 0);
                break;
              case 2:
                matrixHelper.translate(letters[i].animatorJustifyOffset + documentData.justifyOffset + (documentData.boxWidth - documentData.lineWidths[letters[i].line]) / 2, 0, 0);
                break;
            }
            matrixHelper.translate(0, -documentData.ls);
            matrixHelper.translate(offf, 0, 0);
            matrixHelper.translate(alignment[0] * letters[i].an * 5e-3, alignment[1] * yOff * 0.01, 0);
            xPos += letters[i].l + documentData.tr * 1e-3 * documentData.finalSize;
          }
          if (renderType === "html") {
            letterM = matrixHelper.toCSS();
          } else if (renderType === "svg") {
            letterM = matrixHelper.to2dCSS();
          } else {
            letterP = [matrixHelper.props[0], matrixHelper.props[1], matrixHelper.props[2], matrixHelper.props[3], matrixHelper.props[4], matrixHelper.props[5], matrixHelper.props[6], matrixHelper.props[7], matrixHelper.props[8], matrixHelper.props[9], matrixHelper.props[10], matrixHelper.props[11], matrixHelper.props[12], matrixHelper.props[13], matrixHelper.props[14], matrixHelper.props[15]];
          }
          letterO = elemOpacity;
        }
        if (renderedLettersCount <= i) {
          letterValue = new LetterProps(letterO, letterSw, letterSc, letterFc, letterM, letterP);
          this.renderedLetters.push(letterValue);
          renderedLettersCount += 1;
          this.lettersChangedFlag = true;
        } else {
          letterValue = this.renderedLetters[i];
          this.lettersChangedFlag = letterValue.update(letterO, letterSw, letterSc, letterFc, letterM, letterP) || this.lettersChangedFlag;
        }
      }
    };
    TextAnimatorProperty.prototype.getValue = function() {
      if (this._elem.globalData.frameId === this._frameId) {
        return;
      }
      this._frameId = this._elem.globalData.frameId;
      this.iterateDynamicProperties();
    };
    TextAnimatorProperty.prototype.mHelper = new Matrix();
    TextAnimatorProperty.prototype.defaultPropsArray = [];
    extendPrototype([DynamicPropertyContainer], TextAnimatorProperty);
    function ITextElement() {
    }
    ITextElement.prototype.initElement = function(data2, globalData2, comp2) {
      this.lettersChangedFlag = true;
      this.initFrame();
      this.initBaseData(data2, globalData2, comp2);
      this.textProperty = new TextProperty(this, data2.t, this.dynamicProperties);
      this.textAnimator = new TextAnimatorProperty(data2.t, this.renderType, this);
      this.initTransform(data2, globalData2, comp2);
      this.initHierarchy();
      this.initRenderable();
      this.initRendererElement();
      this.createContainerElements();
      this.createRenderableComponents();
      this.createContent();
      this.hide();
      this.textAnimator.searchProperties(this.dynamicProperties);
    };
    ITextElement.prototype.prepareFrame = function(num) {
      this._mdf = false;
      this.prepareRenderableFrame(num);
      this.prepareProperties(num, this.isInRange);
    };
    ITextElement.prototype.createPathShape = function(matrixHelper, shapes) {
      var j;
      var jLen = shapes.length;
      var pathNodes;
      var shapeStr = "";
      for (j = 0; j < jLen; j += 1) {
        if (shapes[j].ty === "sh") {
          pathNodes = shapes[j].ks.k;
          shapeStr += buildShapeString(pathNodes, pathNodes.i.length, true, matrixHelper);
        }
      }
      return shapeStr;
    };
    ITextElement.prototype.updateDocumentData = function(newData, index2) {
      this.textProperty.updateDocumentData(newData, index2);
    };
    ITextElement.prototype.canResizeFont = function(_canResize) {
      this.textProperty.canResizeFont(_canResize);
    };
    ITextElement.prototype.setMinimumFontSize = function(_fontSize) {
      this.textProperty.setMinimumFontSize(_fontSize);
    };
    ITextElement.prototype.applyTextPropertiesToMatrix = function(documentData, matrixHelper, lineNumber, xPos, yPos) {
      if (documentData.ps) {
        matrixHelper.translate(documentData.ps[0], documentData.ps[1] + documentData.ascent, 0);
      }
      matrixHelper.translate(0, -documentData.ls, 0);
      switch (documentData.j) {
        case 1:
          matrixHelper.translate(documentData.justifyOffset + (documentData.boxWidth - documentData.lineWidths[lineNumber]), 0, 0);
          break;
        case 2:
          matrixHelper.translate(documentData.justifyOffset + (documentData.boxWidth - documentData.lineWidths[lineNumber]) / 2, 0, 0);
          break;
      }
      matrixHelper.translate(xPos, yPos, 0);
    };
    ITextElement.prototype.buildColor = function(colorData) {
      return "rgb(" + Math.round(colorData[0] * 255) + "," + Math.round(colorData[1] * 255) + "," + Math.round(colorData[2] * 255) + ")";
    };
    ITextElement.prototype.emptyProp = new LetterProps();
    ITextElement.prototype.destroy = function() {
    };
    ITextElement.prototype.validateText = function() {
      if (this.textProperty._mdf || this.textProperty._isFirstFrame) {
        this.buildNewText();
        this.textProperty._isFirstFrame = false;
        this.textProperty._mdf = false;
      }
    };
    var emptyShapeData = {
      shapes: []
    };
    function SVGTextLottieElement(data2, globalData2, comp2) {
      this.textSpans = [];
      this.renderType = "svg";
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([BaseElement, TransformElement, SVGBaseElement, HierarchyElement, FrameElement, RenderableDOMElement, ITextElement], SVGTextLottieElement);
    SVGTextLottieElement.prototype.createContent = function() {
      if (this.data.singleShape && !this.globalData.fontManager.chars) {
        this.textContainer = createNS("text");
      }
    };
    SVGTextLottieElement.prototype.buildTextContents = function(textArray) {
      var i = 0;
      var len = textArray.length;
      var textContents = [];
      var currentTextContent = "";
      while (i < len) {
        if (textArray[i] === String.fromCharCode(13) || textArray[i] === String.fromCharCode(3)) {
          textContents.push(currentTextContent);
          currentTextContent = "";
        } else {
          currentTextContent += textArray[i];
        }
        i += 1;
      }
      textContents.push(currentTextContent);
      return textContents;
    };
    SVGTextLottieElement.prototype.buildShapeData = function(data2, scale2) {
      if (data2.shapes && data2.shapes.length) {
        var shape = data2.shapes[0];
        if (shape.it) {
          var shapeItem = shape.it[shape.it.length - 1];
          if (shapeItem.s) {
            shapeItem.s.k[0] = scale2;
            shapeItem.s.k[1] = scale2;
          }
        }
      }
      return data2;
    };
    SVGTextLottieElement.prototype.buildNewText = function() {
      this.addDynamicProperty(this);
      var i;
      var len;
      var documentData = this.textProperty.currentData;
      this.renderedLetters = createSizedArray(documentData ? documentData.l.length : 0);
      if (documentData.fc) {
        this.layerElement.setAttribute("fill", this.buildColor(documentData.fc));
      } else {
        this.layerElement.setAttribute("fill", "rgba(0,0,0,0)");
      }
      if (documentData.sc) {
        this.layerElement.setAttribute("stroke", this.buildColor(documentData.sc));
        this.layerElement.setAttribute("stroke-width", documentData.sw);
      }
      this.layerElement.setAttribute("font-size", documentData.finalSize);
      var fontData = this.globalData.fontManager.getFontByName(documentData.f);
      if (fontData.fClass) {
        this.layerElement.setAttribute("class", fontData.fClass);
      } else {
        this.layerElement.setAttribute("font-family", fontData.fFamily);
        var fWeight = documentData.fWeight;
        var fStyle = documentData.fStyle;
        this.layerElement.setAttribute("font-style", fStyle);
        this.layerElement.setAttribute("font-weight", fWeight);
      }
      this.layerElement.setAttribute("aria-label", documentData.t);
      var letters = documentData.l || [];
      var usesGlyphs = !!this.globalData.fontManager.chars;
      len = letters.length;
      var tSpan;
      var matrixHelper = this.mHelper;
      var shapeStr = "";
      var singleShape = this.data.singleShape;
      var xPos = 0;
      var yPos = 0;
      var firstLine = true;
      var trackingOffset = documentData.tr * 1e-3 * documentData.finalSize;
      if (singleShape && !usesGlyphs && !documentData.sz) {
        var tElement = this.textContainer;
        var justify = "start";
        switch (documentData.j) {
          case 1:
            justify = "end";
            break;
          case 2:
            justify = "middle";
            break;
          default:
            justify = "start";
            break;
        }
        tElement.setAttribute("text-anchor", justify);
        tElement.setAttribute("letter-spacing", trackingOffset);
        var textContent = this.buildTextContents(documentData.finalText);
        len = textContent.length;
        yPos = documentData.ps ? documentData.ps[1] + documentData.ascent : 0;
        for (i = 0; i < len; i += 1) {
          tSpan = this.textSpans[i].span || createNS("tspan");
          tSpan.textContent = textContent[i];
          tSpan.setAttribute("x", 0);
          tSpan.setAttribute("y", yPos);
          tSpan.style.display = "inherit";
          tElement.appendChild(tSpan);
          if (!this.textSpans[i]) {
            this.textSpans[i] = {
              span: null,
              glyph: null
            };
          }
          this.textSpans[i].span = tSpan;
          yPos += documentData.finalLineHeight;
        }
        this.layerElement.appendChild(tElement);
      } else {
        var cachedSpansLength = this.textSpans.length;
        var charData;
        for (i = 0; i < len; i += 1) {
          if (!this.textSpans[i]) {
            this.textSpans[i] = {
              span: null,
              childSpan: null,
              glyph: null
            };
          }
          if (!usesGlyphs || !singleShape || i === 0) {
            tSpan = cachedSpansLength > i ? this.textSpans[i].span : createNS(usesGlyphs ? "g" : "text");
            if (cachedSpansLength <= i) {
              tSpan.setAttribute("stroke-linecap", "butt");
              tSpan.setAttribute("stroke-linejoin", "round");
              tSpan.setAttribute("stroke-miterlimit", "4");
              this.textSpans[i].span = tSpan;
              if (usesGlyphs) {
                var childSpan = createNS("g");
                tSpan.appendChild(childSpan);
                this.textSpans[i].childSpan = childSpan;
              }
              this.textSpans[i].span = tSpan;
              this.layerElement.appendChild(tSpan);
            }
            tSpan.style.display = "inherit";
          }
          matrixHelper.reset();
          if (singleShape) {
            if (letters[i].n) {
              xPos = -trackingOffset;
              yPos += documentData.yOffset;
              yPos += firstLine ? 1 : 0;
              firstLine = false;
            }
            this.applyTextPropertiesToMatrix(documentData, matrixHelper, letters[i].line, xPos, yPos);
            xPos += letters[i].l || 0;
            xPos += trackingOffset;
          }
          if (usesGlyphs) {
            charData = this.globalData.fontManager.getCharData(documentData.finalText[i], fontData.fStyle, this.globalData.fontManager.getFontByName(documentData.f).fFamily);
            var glyphElement;
            if (charData.t === 1) {
              glyphElement = new SVGCompElement(charData.data, this.globalData, this);
            } else {
              var data2 = emptyShapeData;
              if (charData.data && charData.data.shapes) {
                data2 = this.buildShapeData(charData.data, documentData.finalSize);
              }
              glyphElement = new SVGShapeElement(data2, this.globalData, this);
            }
            if (this.textSpans[i].glyph) {
              var glyph = this.textSpans[i].glyph;
              this.textSpans[i].childSpan.removeChild(glyph.layerElement);
              glyph.destroy();
            }
            this.textSpans[i].glyph = glyphElement;
            glyphElement._debug = true;
            glyphElement.prepareFrame(0);
            glyphElement.renderFrame();
            this.textSpans[i].childSpan.appendChild(glyphElement.layerElement);
            if (charData.t === 1) {
              this.textSpans[i].childSpan.setAttribute("transform", "scale(" + documentData.finalSize / 100 + "," + documentData.finalSize / 100 + ")");
            }
          } else {
            if (singleShape) {
              tSpan.setAttribute("transform", "translate(" + matrixHelper.props[12] + "," + matrixHelper.props[13] + ")");
            }
            tSpan.textContent = letters[i].val;
            tSpan.setAttributeNS("http://www.w3.org/XML/1998/namespace", "xml:space", "preserve");
          }
        }
        if (singleShape && tSpan) {
          tSpan.setAttribute("d", shapeStr);
        }
      }
      while (i < this.textSpans.length) {
        this.textSpans[i].span.style.display = "none";
        i += 1;
      }
      this._sizeChanged = true;
    };
    SVGTextLottieElement.prototype.sourceRectAtTime = function() {
      this.prepareFrame(this.comp.renderedFrame - this.data.st);
      this.renderInnerContent();
      if (this._sizeChanged) {
        this._sizeChanged = false;
        var textBox = this.layerElement.getBBox();
        this.bbox = {
          top: textBox.y,
          left: textBox.x,
          width: textBox.width,
          height: textBox.height
        };
      }
      return this.bbox;
    };
    SVGTextLottieElement.prototype.getValue = function() {
      var i;
      var len = this.textSpans.length;
      var glyphElement;
      this.renderedFrame = this.comp.renderedFrame;
      for (i = 0; i < len; i += 1) {
        glyphElement = this.textSpans[i].glyph;
        if (glyphElement) {
          glyphElement.prepareFrame(this.comp.renderedFrame - this.data.st);
          if (glyphElement._mdf) {
            this._mdf = true;
          }
        }
      }
    };
    SVGTextLottieElement.prototype.renderInnerContent = function() {
      this.validateText();
      if (!this.data.singleShape || this._mdf) {
        this.textAnimator.getMeasures(this.textProperty.currentData, this.lettersChangedFlag);
        if (this.lettersChangedFlag || this.textAnimator.lettersChangedFlag) {
          this._sizeChanged = true;
          var i;
          var len;
          var renderedLetters = this.textAnimator.renderedLetters;
          var letters = this.textProperty.currentData.l;
          len = letters.length;
          var renderedLetter;
          var textSpan;
          var glyphElement;
          for (i = 0; i < len; i += 1) {
            if (!letters[i].n) {
              renderedLetter = renderedLetters[i];
              textSpan = this.textSpans[i].span;
              glyphElement = this.textSpans[i].glyph;
              if (glyphElement) {
                glyphElement.renderFrame();
              }
              if (renderedLetter._mdf.m) {
                textSpan.setAttribute("transform", renderedLetter.m);
              }
              if (renderedLetter._mdf.o) {
                textSpan.setAttribute("opacity", renderedLetter.o);
              }
              if (renderedLetter._mdf.sw) {
                textSpan.setAttribute("stroke-width", renderedLetter.sw);
              }
              if (renderedLetter._mdf.sc) {
                textSpan.setAttribute("stroke", renderedLetter.sc);
              }
              if (renderedLetter._mdf.fc) {
                textSpan.setAttribute("fill", renderedLetter.fc);
              }
            }
          }
        }
      }
    };
    function ISolidElement(data2, globalData2, comp2) {
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([IImageElement], ISolidElement);
    ISolidElement.prototype.createContent = function() {
      var rect = createNS("rect");
      rect.setAttribute("width", this.data.sw);
      rect.setAttribute("height", this.data.sh);
      rect.setAttribute("fill", this.data.sc);
      this.layerElement.appendChild(rect);
    };
    function NullElement(data2, globalData2, comp2) {
      this.initFrame();
      this.initBaseData(data2, globalData2, comp2);
      this.initFrame();
      this.initTransform(data2, globalData2, comp2);
      this.initHierarchy();
    }
    NullElement.prototype.prepareFrame = function(num) {
      this.prepareProperties(num, true);
    };
    NullElement.prototype.renderFrame = function() {
    };
    NullElement.prototype.getBaseElement = function() {
      return null;
    };
    NullElement.prototype.destroy = function() {
    };
    NullElement.prototype.sourceRectAtTime = function() {
    };
    NullElement.prototype.hide = function() {
    };
    extendPrototype([BaseElement, TransformElement, HierarchyElement, FrameElement], NullElement);
    function SVGRendererBase() {
    }
    extendPrototype([BaseRenderer], SVGRendererBase);
    SVGRendererBase.prototype.createNull = function(data2) {
      return new NullElement(data2, this.globalData, this);
    };
    SVGRendererBase.prototype.createShape = function(data2) {
      return new SVGShapeElement(data2, this.globalData, this);
    };
    SVGRendererBase.prototype.createText = function(data2) {
      return new SVGTextLottieElement(data2, this.globalData, this);
    };
    SVGRendererBase.prototype.createImage = function(data2) {
      return new IImageElement(data2, this.globalData, this);
    };
    SVGRendererBase.prototype.createSolid = function(data2) {
      return new ISolidElement(data2, this.globalData, this);
    };
    SVGRendererBase.prototype.configAnimation = function(animData) {
      this.svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      this.svgElement.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
      if (this.renderConfig.viewBoxSize) {
        this.svgElement.setAttribute("viewBox", this.renderConfig.viewBoxSize);
      } else {
        this.svgElement.setAttribute("viewBox", "0 0 " + animData.w + " " + animData.h);
      }
      if (!this.renderConfig.viewBoxOnly) {
        this.svgElement.setAttribute("width", animData.w);
        this.svgElement.setAttribute("height", animData.h);
        this.svgElement.style.width = "100%";
        this.svgElement.style.height = "100%";
        this.svgElement.style.transform = "translate3d(0,0,0)";
        this.svgElement.style.contentVisibility = this.renderConfig.contentVisibility;
      }
      if (this.renderConfig.width) {
        this.svgElement.setAttribute("width", this.renderConfig.width);
      }
      if (this.renderConfig.height) {
        this.svgElement.setAttribute("height", this.renderConfig.height);
      }
      if (this.renderConfig.className) {
        this.svgElement.setAttribute("class", this.renderConfig.className);
      }
      if (this.renderConfig.id) {
        this.svgElement.setAttribute("id", this.renderConfig.id);
      }
      if (this.renderConfig.focusable !== void 0) {
        this.svgElement.setAttribute("focusable", this.renderConfig.focusable);
      }
      this.svgElement.setAttribute("preserveAspectRatio", this.renderConfig.preserveAspectRatio);
      this.animationItem.wrapper.appendChild(this.svgElement);
      var defs = this.globalData.defs;
      this.setupGlobalData(animData, defs);
      this.globalData.progressiveLoad = this.renderConfig.progressiveLoad;
      this.data = animData;
      var maskElement = createNS("clipPath");
      var rect = createNS("rect");
      rect.setAttribute("width", animData.w);
      rect.setAttribute("height", animData.h);
      rect.setAttribute("x", 0);
      rect.setAttribute("y", 0);
      var maskId = createElementID();
      maskElement.setAttribute("id", maskId);
      maskElement.appendChild(rect);
      this.layerElement.setAttribute("clip-path", "url(" + getLocationHref() + "#" + maskId + ")");
      defs.appendChild(maskElement);
      this.layers = animData.layers;
      this.elements = createSizedArray(animData.layers.length);
    };
    SVGRendererBase.prototype.destroy = function() {
      if (this.animationItem.wrapper) {
        this.animationItem.wrapper.innerText = "";
      }
      this.layerElement = null;
      this.globalData.defs = null;
      var i;
      var len = this.layers ? this.layers.length : 0;
      for (i = 0; i < len; i += 1) {
        if (this.elements[i] && this.elements[i].destroy) {
          this.elements[i].destroy();
        }
      }
      this.elements.length = 0;
      this.destroyed = true;
      this.animationItem = null;
    };
    SVGRendererBase.prototype.updateContainerSize = function() {
    };
    SVGRendererBase.prototype.findIndexByInd = function(ind) {
      var i = 0;
      var len = this.layers.length;
      for (i = 0; i < len; i += 1) {
        if (this.layers[i].ind === ind) {
          return i;
        }
      }
      return -1;
    };
    SVGRendererBase.prototype.buildItem = function(pos) {
      var elements = this.elements;
      if (elements[pos] || this.layers[pos].ty === 99) {
        return;
      }
      elements[pos] = true;
      var element = this.createItem(this.layers[pos]);
      elements[pos] = element;
      if (getExpressionsPlugin()) {
        if (this.layers[pos].ty === 0) {
          this.globalData.projectInterface.registerComposition(element);
        }
        element.initExpressions();
      }
      this.appendElementInPos(element, pos);
      if (this.layers[pos].tt) {
        var elementIndex = "tp" in this.layers[pos] ? this.findIndexByInd(this.layers[pos].tp) : pos - 1;
        if (elementIndex === -1) {
          return;
        }
        if (!this.elements[elementIndex] || this.elements[elementIndex] === true) {
          this.buildItem(elementIndex);
          this.addPendingElement(element);
        } else {
          var matteElement = elements[elementIndex];
          var matteMask = matteElement.getMatte(this.layers[pos].tt);
          element.setMatte(matteMask);
        }
      }
    };
    SVGRendererBase.prototype.checkPendingElements = function() {
      while (this.pendingElements.length) {
        var element = this.pendingElements.pop();
        element.checkParenting();
        if (element.data.tt) {
          var i = 0;
          var len = this.elements.length;
          while (i < len) {
            if (this.elements[i] === element) {
              var elementIndex = "tp" in element.data ? this.findIndexByInd(element.data.tp) : i - 1;
              var matteElement = this.elements[elementIndex];
              var matteMask = matteElement.getMatte(this.layers[i].tt);
              element.setMatte(matteMask);
              break;
            }
            i += 1;
          }
        }
      }
    };
    SVGRendererBase.prototype.renderFrame = function(num) {
      if (this.renderedFrame === num || this.destroyed) {
        return;
      }
      if (num === null) {
        num = this.renderedFrame;
      } else {
        this.renderedFrame = num;
      }
      this.globalData.frameNum = num;
      this.globalData.frameId += 1;
      this.globalData.projectInterface.currentFrame = num;
      this.globalData._mdf = false;
      var i;
      var len = this.layers.length;
      if (!this.completeLayers) {
        this.checkLayers(num);
      }
      for (i = len - 1; i >= 0; i -= 1) {
        if (this.completeLayers || this.elements[i]) {
          this.elements[i].prepareFrame(num - this.layers[i].st);
        }
      }
      if (this.globalData._mdf) {
        for (i = 0; i < len; i += 1) {
          if (this.completeLayers || this.elements[i]) {
            this.elements[i].renderFrame();
          }
        }
      }
    };
    SVGRendererBase.prototype.appendElementInPos = function(element, pos) {
      var newElement = element.getBaseElement();
      if (!newElement) {
        return;
      }
      var i = 0;
      var nextElement;
      while (i < pos) {
        if (this.elements[i] && this.elements[i] !== true && this.elements[i].getBaseElement()) {
          nextElement = this.elements[i].getBaseElement();
        }
        i += 1;
      }
      if (nextElement) {
        this.layerElement.insertBefore(newElement, nextElement);
      } else {
        this.layerElement.appendChild(newElement);
      }
    };
    SVGRendererBase.prototype.hide = function() {
      this.layerElement.style.display = "none";
    };
    SVGRendererBase.prototype.show = function() {
      this.layerElement.style.display = "block";
    };
    function ICompElement() {
    }
    extendPrototype([BaseElement, TransformElement, HierarchyElement, FrameElement, RenderableDOMElement], ICompElement);
    ICompElement.prototype.initElement = function(data2, globalData2, comp2) {
      this.initFrame();
      this.initBaseData(data2, globalData2, comp2);
      this.initTransform(data2, globalData2, comp2);
      this.initRenderable();
      this.initHierarchy();
      this.initRendererElement();
      this.createContainerElements();
      this.createRenderableComponents();
      if (this.data.xt || !globalData2.progressiveLoad) {
        this.buildAllItems();
      }
      this.hide();
    };
    ICompElement.prototype.prepareFrame = function(num) {
      this._mdf = false;
      this.prepareRenderableFrame(num);
      this.prepareProperties(num, this.isInRange);
      if (!this.isInRange && !this.data.xt) {
        return;
      }
      if (!this.tm._placeholder) {
        var timeRemapped = this.tm.v;
        if (timeRemapped === this.data.op) {
          timeRemapped = this.data.op - 1;
        }
        this.renderedFrame = timeRemapped;
      } else {
        this.renderedFrame = num / this.data.sr;
      }
      var i;
      var len = this.elements.length;
      if (!this.completeLayers) {
        this.checkLayers(this.renderedFrame);
      }
      for (i = len - 1; i >= 0; i -= 1) {
        if (this.completeLayers || this.elements[i]) {
          this.elements[i].prepareFrame(this.renderedFrame - this.layers[i].st);
          if (this.elements[i]._mdf) {
            this._mdf = true;
          }
        }
      }
    };
    ICompElement.prototype.renderInnerContent = function() {
      var i;
      var len = this.layers.length;
      for (i = 0; i < len; i += 1) {
        if (this.completeLayers || this.elements[i]) {
          this.elements[i].renderFrame();
        }
      }
    };
    ICompElement.prototype.setElements = function(elems) {
      this.elements = elems;
    };
    ICompElement.prototype.getElements = function() {
      return this.elements;
    };
    ICompElement.prototype.destroyElements = function() {
      var i;
      var len = this.layers.length;
      for (i = 0; i < len; i += 1) {
        if (this.elements[i]) {
          this.elements[i].destroy();
        }
      }
    };
    ICompElement.prototype.destroy = function() {
      this.destroyElements();
      this.destroyBaseElement();
    };
    function SVGCompElement(data2, globalData2, comp2) {
      this.layers = data2.layers;
      this.supports3d = true;
      this.completeLayers = false;
      this.pendingElements = [];
      this.elements = this.layers ? createSizedArray(this.layers.length) : [];
      this.initElement(data2, globalData2, comp2);
      this.tm = data2.tm ? PropertyFactory.getProp(this, data2.tm, 0, globalData2.frameRate, this) : {
        _placeholder: true
      };
    }
    extendPrototype([SVGRendererBase, ICompElement, SVGBaseElement], SVGCompElement);
    SVGCompElement.prototype.createComp = function(data2) {
      return new SVGCompElement(data2, this.globalData, this);
    };
    function SVGRenderer(animationItem, config) {
      this.animationItem = animationItem;
      this.layers = null;
      this.renderedFrame = -1;
      this.svgElement = createNS("svg");
      var ariaLabel = "";
      if (config && config.title) {
        var titleElement = createNS("title");
        var titleId = createElementID();
        titleElement.setAttribute("id", titleId);
        titleElement.textContent = config.title;
        this.svgElement.appendChild(titleElement);
        ariaLabel += titleId;
      }
      if (config && config.description) {
        var descElement = createNS("desc");
        var descId = createElementID();
        descElement.setAttribute("id", descId);
        descElement.textContent = config.description;
        this.svgElement.appendChild(descElement);
        ariaLabel += " " + descId;
      }
      if (ariaLabel) {
        this.svgElement.setAttribute("aria-labelledby", ariaLabel);
      }
      var defs = createNS("defs");
      this.svgElement.appendChild(defs);
      var maskElement = createNS("g");
      this.svgElement.appendChild(maskElement);
      this.layerElement = maskElement;
      this.renderConfig = {
        preserveAspectRatio: config && config.preserveAspectRatio || "xMidYMid meet",
        imagePreserveAspectRatio: config && config.imagePreserveAspectRatio || "xMidYMid slice",
        contentVisibility: config && config.contentVisibility || "visible",
        progressiveLoad: config && config.progressiveLoad || false,
        hideOnTransparent: !(config && config.hideOnTransparent === false),
        viewBoxOnly: config && config.viewBoxOnly || false,
        viewBoxSize: config && config.viewBoxSize || false,
        className: config && config.className || "",
        id: config && config.id || "",
        focusable: config && config.focusable,
        filterSize: {
          width: config && config.filterSize && config.filterSize.width || "100%",
          height: config && config.filterSize && config.filterSize.height || "100%",
          x: config && config.filterSize && config.filterSize.x || "0%",
          y: config && config.filterSize && config.filterSize.y || "0%"
        },
        width: config && config.width,
        height: config && config.height,
        runExpressions: !config || config.runExpressions === void 0 || config.runExpressions
      };
      this.globalData = {
        _mdf: false,
        frameNum: -1,
        defs,
        renderConfig: this.renderConfig
      };
      this.elements = [];
      this.pendingElements = [];
      this.destroyed = false;
      this.rendererType = "svg";
    }
    extendPrototype([SVGRendererBase], SVGRenderer);
    SVGRenderer.prototype.createComp = function(data2) {
      return new SVGCompElement(data2, this.globalData, this);
    };
    function ShapeTransformManager() {
      this.sequences = {};
      this.sequenceList = [];
      this.transform_key_count = 0;
    }
    ShapeTransformManager.prototype = {
      addTransformSequence: function addTransformSequence(transforms) {
        var i;
        var len = transforms.length;
        var key2 = "_";
        for (i = 0; i < len; i += 1) {
          key2 += transforms[i].transform.key + "_";
        }
        var sequence = this.sequences[key2];
        if (!sequence) {
          sequence = {
            transforms: [].concat(transforms),
            finalTransform: new Matrix(),
            _mdf: false
          };
          this.sequences[key2] = sequence;
          this.sequenceList.push(sequence);
        }
        return sequence;
      },
      processSequence: function processSequence(sequence, isFirstFrame) {
        var i = 0;
        var len = sequence.transforms.length;
        var _mdf = isFirstFrame;
        while (i < len && !isFirstFrame) {
          if (sequence.transforms[i].transform.mProps._mdf) {
            _mdf = true;
            break;
          }
          i += 1;
        }
        if (_mdf) {
          sequence.finalTransform.reset();
          for (i = len - 1; i >= 0; i -= 1) {
            sequence.finalTransform.multiply(sequence.transforms[i].transform.mProps.v);
          }
        }
        sequence._mdf = _mdf;
      },
      processSequences: function processSequences(isFirstFrame) {
        var i;
        var len = this.sequenceList.length;
        for (i = 0; i < len; i += 1) {
          this.processSequence(this.sequenceList[i], isFirstFrame);
        }
      },
      getNewKey: function getNewKey() {
        this.transform_key_count += 1;
        return "_" + this.transform_key_count;
      }
    };
    var lumaLoader = function lumaLoader2() {
      var id = "__lottie_element_luma_buffer";
      var lumaBuffer = null;
      var lumaBufferCtx = null;
      var svg = null;
      function createLumaSvgFilter() {
        var _svg = createNS("svg");
        var fil = createNS("filter");
        var matrix = createNS("feColorMatrix");
        fil.setAttribute("id", id);
        matrix.setAttribute("type", "matrix");
        matrix.setAttribute("color-interpolation-filters", "sRGB");
        matrix.setAttribute("values", "0.3, 0.3, 0.3, 0, 0, 0.3, 0.3, 0.3, 0, 0, 0.3, 0.3, 0.3, 0, 0, 0.3, 0.3, 0.3, 0, 0");
        fil.appendChild(matrix);
        _svg.appendChild(fil);
        _svg.setAttribute("id", id + "_svg");
        if (featureSupport.svgLumaHidden) {
          _svg.style.display = "none";
        }
        return _svg;
      }
      function loadLuma() {
        if (!lumaBuffer) {
          svg = createLumaSvgFilter();
          document.body.appendChild(svg);
          lumaBuffer = createTag("canvas");
          lumaBufferCtx = lumaBuffer.getContext("2d");
          lumaBufferCtx.filter = "url(#" + id + ")";
          lumaBufferCtx.fillStyle = "rgba(0,0,0,0)";
          lumaBufferCtx.fillRect(0, 0, 1, 1);
        }
      }
      function getLuma(canvas) {
        if (!lumaBuffer) {
          loadLuma();
        }
        lumaBuffer.width = canvas.width;
        lumaBuffer.height = canvas.height;
        lumaBufferCtx.filter = "url(#" + id + ")";
        return lumaBuffer;
      }
      return {
        load: loadLuma,
        get: getLuma
      };
    };
    function createCanvas(width2, height2) {
      if (featureSupport.offscreenCanvas) {
        return new OffscreenCanvas(width2, height2);
      }
      var canvas = createTag("canvas");
      canvas.width = width2;
      canvas.height = height2;
      return canvas;
    }
    var assetLoader = function() {
      return {
        loadLumaCanvas: lumaLoader.load,
        getLumaCanvas: lumaLoader.get,
        createCanvas
      };
    }();
    var registeredEffects = {};
    function CVEffects(elem2) {
      var i;
      var len = elem2.data.ef ? elem2.data.ef.length : 0;
      this.filters = [];
      var filterManager;
      for (i = 0; i < len; i += 1) {
        filterManager = null;
        var type = elem2.data.ef[i].ty;
        if (registeredEffects[type]) {
          var Effect = registeredEffects[type].effect;
          filterManager = new Effect(elem2.effectsManager.effectElements[i], elem2);
        }
        if (filterManager) {
          this.filters.push(filterManager);
        }
      }
      if (this.filters.length) {
        elem2.addRenderableComponent(this);
      }
    }
    CVEffects.prototype.renderFrame = function(_isFirstFrame) {
      var i;
      var len = this.filters.length;
      for (i = 0; i < len; i += 1) {
        this.filters[i].renderFrame(_isFirstFrame);
      }
    };
    CVEffects.prototype.getEffects = function(type) {
      var i;
      var len = this.filters.length;
      var effects = [];
      for (i = 0; i < len; i += 1) {
        if (this.filters[i].type === type) {
          effects.push(this.filters[i]);
        }
      }
      return effects;
    };
    function registerEffect(id, effect2) {
      registeredEffects[id] = {
        effect: effect2
      };
    }
    function CVMaskElement(data2, element) {
      this.data = data2;
      this.element = element;
      this.masksProperties = this.data.masksProperties || [];
      this.viewData = createSizedArray(this.masksProperties.length);
      var i;
      var len = this.masksProperties.length;
      var hasMasks = false;
      for (i = 0; i < len; i += 1) {
        if (this.masksProperties[i].mode !== "n") {
          hasMasks = true;
        }
        this.viewData[i] = ShapePropertyFactory.getShapeProp(this.element, this.masksProperties[i], 3);
      }
      this.hasMasks = hasMasks;
      if (hasMasks) {
        this.element.addRenderableComponent(this);
      }
    }
    CVMaskElement.prototype.renderFrame = function() {
      if (!this.hasMasks) {
        return;
      }
      var transform2 = this.element.finalTransform.mat;
      var ctx = this.element.canvasContext;
      var i;
      var len = this.masksProperties.length;
      var pt;
      var pts;
      var data2;
      ctx.beginPath();
      for (i = 0; i < len; i += 1) {
        if (this.masksProperties[i].mode !== "n") {
          if (this.masksProperties[i].inv) {
            ctx.moveTo(0, 0);
            ctx.lineTo(this.element.globalData.compSize.w, 0);
            ctx.lineTo(this.element.globalData.compSize.w, this.element.globalData.compSize.h);
            ctx.lineTo(0, this.element.globalData.compSize.h);
            ctx.lineTo(0, 0);
          }
          data2 = this.viewData[i].v;
          pt = transform2.applyToPointArray(data2.v[0][0], data2.v[0][1], 0);
          ctx.moveTo(pt[0], pt[1]);
          var j;
          var jLen = data2._length;
          for (j = 1; j < jLen; j += 1) {
            pts = transform2.applyToTriplePoints(data2.o[j - 1], data2.i[j], data2.v[j]);
            ctx.bezierCurveTo(pts[0], pts[1], pts[2], pts[3], pts[4], pts[5]);
          }
          pts = transform2.applyToTriplePoints(data2.o[j - 1], data2.i[0], data2.v[0]);
          ctx.bezierCurveTo(pts[0], pts[1], pts[2], pts[3], pts[4], pts[5]);
        }
      }
      this.element.globalData.renderer.save(true);
      ctx.clip();
    };
    CVMaskElement.prototype.getMaskProperty = MaskElement.prototype.getMaskProperty;
    CVMaskElement.prototype.destroy = function() {
      this.element = null;
    };
    function CVBaseElement() {
    }
    var operationsMap = {
      1: "source-in",
      2: "source-out",
      3: "source-in",
      4: "source-out"
    };
    CVBaseElement.prototype = {
      createElements: function createElements() {
      },
      initRendererElement: function initRendererElement() {
      },
      createContainerElements: function createContainerElements() {
        if (this.data.tt >= 1) {
          this.buffers = [];
          var canvasContext = this.globalData.canvasContext;
          var bufferCanvas = assetLoader.createCanvas(canvasContext.canvas.width, canvasContext.canvas.height);
          this.buffers.push(bufferCanvas);
          var bufferCanvas2 = assetLoader.createCanvas(canvasContext.canvas.width, canvasContext.canvas.height);
          this.buffers.push(bufferCanvas2);
          if (this.data.tt >= 3 && !document._isProxy) {
            assetLoader.loadLumaCanvas();
          }
        }
        this.canvasContext = this.globalData.canvasContext;
        this.transformCanvas = this.globalData.transformCanvas;
        this.renderableEffectsManager = new CVEffects(this);
        this.searchEffectTransforms();
      },
      createContent: function createContent() {
      },
      setBlendMode: function setBlendMode() {
        var globalData2 = this.globalData;
        if (globalData2.blendMode !== this.data.bm) {
          globalData2.blendMode = this.data.bm;
          var blendModeValue = getBlendMode(this.data.bm);
          globalData2.canvasContext.globalCompositeOperation = blendModeValue;
        }
      },
      createRenderableComponents: function createRenderableComponents() {
        this.maskManager = new CVMaskElement(this.data, this);
        this.transformEffects = this.renderableEffectsManager.getEffects(effectTypes.TRANSFORM_EFFECT);
      },
      hideElement: function hideElement() {
        if (!this.hidden && (!this.isInRange || this.isTransparent)) {
          this.hidden = true;
        }
      },
      showElement: function showElement() {
        if (this.isInRange && !this.isTransparent) {
          this.hidden = false;
          this._isFirstFrame = true;
          this.maskManager._isFirstFrame = true;
        }
      },
      clearCanvas: function clearCanvas(canvasContext) {
        canvasContext.clearRect(this.transformCanvas.tx, this.transformCanvas.ty, this.transformCanvas.w * this.transformCanvas.sx, this.transformCanvas.h * this.transformCanvas.sy);
      },
      prepareLayer: function prepareLayer() {
        if (this.data.tt >= 1) {
          var buffer = this.buffers[0];
          var bufferCtx = buffer.getContext("2d");
          this.clearCanvas(bufferCtx);
          bufferCtx.drawImage(this.canvasContext.canvas, 0, 0);
          this.currentTransform = this.canvasContext.getTransform();
          this.canvasContext.setTransform(1, 0, 0, 1, 0, 0);
          this.clearCanvas(this.canvasContext);
          this.canvasContext.setTransform(this.currentTransform);
        }
      },
      exitLayer: function exitLayer() {
        if (this.data.tt >= 1) {
          var buffer = this.buffers[1];
          var bufferCtx = buffer.getContext("2d");
          this.clearCanvas(bufferCtx);
          bufferCtx.drawImage(this.canvasContext.canvas, 0, 0);
          this.canvasContext.setTransform(1, 0, 0, 1, 0, 0);
          this.clearCanvas(this.canvasContext);
          this.canvasContext.setTransform(this.currentTransform);
          var mask2 = this.comp.getElementById("tp" in this.data ? this.data.tp : this.data.ind - 1);
          mask2.renderFrame(true);
          this.canvasContext.setTransform(1, 0, 0, 1, 0, 0);
          if (this.data.tt >= 3 && !document._isProxy) {
            var lumaBuffer = assetLoader.getLumaCanvas(this.canvasContext.canvas);
            var lumaBufferCtx = lumaBuffer.getContext("2d");
            lumaBufferCtx.drawImage(this.canvasContext.canvas, 0, 0);
            this.clearCanvas(this.canvasContext);
            this.canvasContext.drawImage(lumaBuffer, 0, 0);
          }
          this.canvasContext.globalCompositeOperation = operationsMap[this.data.tt];
          this.canvasContext.drawImage(buffer, 0, 0);
          this.canvasContext.globalCompositeOperation = "destination-over";
          this.canvasContext.drawImage(this.buffers[0], 0, 0);
          this.canvasContext.setTransform(this.currentTransform);
          this.canvasContext.globalCompositeOperation = "source-over";
        }
      },
      renderFrame: function renderFrame(forceRender) {
        if (this.hidden || this.data.hd) {
          return;
        }
        if (this.data.td === 1 && !forceRender) {
          return;
        }
        this.renderTransform();
        this.renderRenderable();
        this.renderLocalTransform();
        this.setBlendMode();
        var forceRealStack = this.data.ty === 0;
        this.prepareLayer();
        this.globalData.renderer.save(forceRealStack);
        this.globalData.renderer.ctxTransform(this.finalTransform.localMat.props);
        this.globalData.renderer.ctxOpacity(this.finalTransform.localOpacity);
        this.renderInnerContent();
        this.globalData.renderer.restore(forceRealStack);
        this.exitLayer();
        if (this.maskManager.hasMasks) {
          this.globalData.renderer.restore(true);
        }
        if (this._isFirstFrame) {
          this._isFirstFrame = false;
        }
      },
      destroy: function destroy() {
        this.canvasContext = null;
        this.data = null;
        this.globalData = null;
        this.maskManager.destroy();
      },
      mHelper: new Matrix()
    };
    CVBaseElement.prototype.hide = CVBaseElement.prototype.hideElement;
    CVBaseElement.prototype.show = CVBaseElement.prototype.showElement;
    function CVShapeData(element, data2, styles, transformsManager) {
      this.styledShapes = [];
      this.tr = [0, 0, 0, 0, 0, 0];
      var ty = 4;
      if (data2.ty === "rc") {
        ty = 5;
      } else if (data2.ty === "el") {
        ty = 6;
      } else if (data2.ty === "sr") {
        ty = 7;
      }
      this.sh = ShapePropertyFactory.getShapeProp(element, data2, ty, element);
      var i;
      var len = styles.length;
      var styledShape;
      for (i = 0; i < len; i += 1) {
        if (!styles[i].closed) {
          styledShape = {
            transforms: transformsManager.addTransformSequence(styles[i].transforms),
            trNodes: []
          };
          this.styledShapes.push(styledShape);
          styles[i].elements.push(styledShape);
        }
      }
    }
    CVShapeData.prototype.setAsAnimated = SVGShapeData.prototype.setAsAnimated;
    function CVShapeElement(data2, globalData2, comp2) {
      this.shapes = [];
      this.shapesData = data2.shapes;
      this.stylesList = [];
      this.itemsData = [];
      this.prevViewData = [];
      this.shapeModifiers = [];
      this.processedElements = [];
      this.transformsManager = new ShapeTransformManager();
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([BaseElement, TransformElement, CVBaseElement, IShapeElement, HierarchyElement, FrameElement, RenderableElement], CVShapeElement);
    CVShapeElement.prototype.initElement = RenderableDOMElement.prototype.initElement;
    CVShapeElement.prototype.transformHelper = {
      opacity: 1,
      _opMdf: false
    };
    CVShapeElement.prototype.dashResetter = [];
    CVShapeElement.prototype.createContent = function() {
      this.searchShapes(this.shapesData, this.itemsData, this.prevViewData, true, []);
    };
    CVShapeElement.prototype.createStyleElement = function(data2, transforms) {
      var styleElem = {
        data: data2,
        type: data2.ty,
        preTransforms: this.transformsManager.addTransformSequence(transforms),
        transforms: [],
        elements: [],
        closed: data2.hd === true
      };
      var elementData = {};
      if (data2.ty === "fl" || data2.ty === "st") {
        elementData.c = PropertyFactory.getProp(this, data2.c, 1, 255, this);
        if (!elementData.c.k) {
          styleElem.co = "rgb(" + bmFloor(elementData.c.v[0]) + "," + bmFloor(elementData.c.v[1]) + "," + bmFloor(elementData.c.v[2]) + ")";
        }
      } else if (data2.ty === "gf" || data2.ty === "gs") {
        elementData.s = PropertyFactory.getProp(this, data2.s, 1, null, this);
        elementData.e = PropertyFactory.getProp(this, data2.e, 1, null, this);
        elementData.h = PropertyFactory.getProp(this, data2.h || {
          k: 0
        }, 0, 0.01, this);
        elementData.a = PropertyFactory.getProp(this, data2.a || {
          k: 0
        }, 0, degToRads, this);
        elementData.g = new GradientProperty(this, data2.g, this);
      }
      elementData.o = PropertyFactory.getProp(this, data2.o, 0, 0.01, this);
      if (data2.ty === "st" || data2.ty === "gs") {
        styleElem.lc = lineCapEnum[data2.lc || 2];
        styleElem.lj = lineJoinEnum[data2.lj || 2];
        if (data2.lj == 1) {
          styleElem.ml = data2.ml;
        }
        elementData.w = PropertyFactory.getProp(this, data2.w, 0, null, this);
        if (!elementData.w.k) {
          styleElem.wi = elementData.w.v;
        }
        if (data2.d) {
          var d = new DashProperty(this, data2.d, "canvas", this);
          elementData.d = d;
          if (!elementData.d.k) {
            styleElem.da = elementData.d.dashArray;
            styleElem["do"] = elementData.d.dashoffset[0];
          }
        }
      } else {
        styleElem.r = data2.r === 2 ? "evenodd" : "nonzero";
      }
      this.stylesList.push(styleElem);
      elementData.style = styleElem;
      return elementData;
    };
    CVShapeElement.prototype.createGroupElement = function() {
      var elementData = {
        it: [],
        prevViewData: []
      };
      return elementData;
    };
    CVShapeElement.prototype.createTransformElement = function(data2) {
      var elementData = {
        transform: {
          opacity: 1,
          _opMdf: false,
          key: this.transformsManager.getNewKey(),
          op: PropertyFactory.getProp(this, data2.o, 0, 0.01, this),
          mProps: TransformPropertyFactory.getTransformProperty(this, data2, this)
        }
      };
      return elementData;
    };
    CVShapeElement.prototype.createShapeElement = function(data2) {
      var elementData = new CVShapeData(this, data2, this.stylesList, this.transformsManager);
      this.shapes.push(elementData);
      this.addShapeToModifiers(elementData);
      return elementData;
    };
    CVShapeElement.prototype.reloadShapes = function() {
      this._isFirstFrame = true;
      var i;
      var len = this.itemsData.length;
      for (i = 0; i < len; i += 1) {
        this.prevViewData[i] = this.itemsData[i];
      }
      this.searchShapes(this.shapesData, this.itemsData, this.prevViewData, true, []);
      len = this.dynamicProperties.length;
      for (i = 0; i < len; i += 1) {
        this.dynamicProperties[i].getValue();
      }
      this.renderModifiers();
      this.transformsManager.processSequences(this._isFirstFrame);
    };
    CVShapeElement.prototype.addTransformToStyleList = function(transform2) {
      var i;
      var len = this.stylesList.length;
      for (i = 0; i < len; i += 1) {
        if (!this.stylesList[i].closed) {
          this.stylesList[i].transforms.push(transform2);
        }
      }
    };
    CVShapeElement.prototype.removeTransformFromStyleList = function() {
      var i;
      var len = this.stylesList.length;
      for (i = 0; i < len; i += 1) {
        if (!this.stylesList[i].closed) {
          this.stylesList[i].transforms.pop();
        }
      }
    };
    CVShapeElement.prototype.closeStyles = function(styles) {
      var i;
      var len = styles.length;
      for (i = 0; i < len; i += 1) {
        styles[i].closed = true;
      }
    };
    CVShapeElement.prototype.searchShapes = function(arr, itemsData, prevViewData, shouldRender, transforms) {
      var i;
      var len = arr.length - 1;
      var j;
      var jLen;
      var ownStyles = [];
      var ownModifiers = [];
      var processedPos;
      var modifier;
      var currentTransform;
      var ownTransforms = [].concat(transforms);
      for (i = len; i >= 0; i -= 1) {
        processedPos = this.searchProcessedElement(arr[i]);
        if (!processedPos) {
          arr[i]._shouldRender = shouldRender;
        } else {
          itemsData[i] = prevViewData[processedPos - 1];
        }
        if (arr[i].ty === "fl" || arr[i].ty === "st" || arr[i].ty === "gf" || arr[i].ty === "gs") {
          if (!processedPos) {
            itemsData[i] = this.createStyleElement(arr[i], ownTransforms);
          } else {
            itemsData[i].style.closed = false;
          }
          ownStyles.push(itemsData[i].style);
        } else if (arr[i].ty === "gr") {
          if (!processedPos) {
            itemsData[i] = this.createGroupElement(arr[i]);
          } else {
            jLen = itemsData[i].it.length;
            for (j = 0; j < jLen; j += 1) {
              itemsData[i].prevViewData[j] = itemsData[i].it[j];
            }
          }
          this.searchShapes(arr[i].it, itemsData[i].it, itemsData[i].prevViewData, shouldRender, ownTransforms);
        } else if (arr[i].ty === "tr") {
          if (!processedPos) {
            currentTransform = this.createTransformElement(arr[i]);
            itemsData[i] = currentTransform;
          }
          ownTransforms.push(itemsData[i]);
          this.addTransformToStyleList(itemsData[i]);
        } else if (arr[i].ty === "sh" || arr[i].ty === "rc" || arr[i].ty === "el" || arr[i].ty === "sr") {
          if (!processedPos) {
            itemsData[i] = this.createShapeElement(arr[i]);
          }
        } else if (arr[i].ty === "tm" || arr[i].ty === "rd" || arr[i].ty === "pb" || arr[i].ty === "zz" || arr[i].ty === "op") {
          if (!processedPos) {
            modifier = ShapeModifiers.getModifier(arr[i].ty);
            modifier.init(this, arr[i]);
            itemsData[i] = modifier;
            this.shapeModifiers.push(modifier);
          } else {
            modifier = itemsData[i];
            modifier.closed = false;
          }
          ownModifiers.push(modifier);
        } else if (arr[i].ty === "rp") {
          if (!processedPos) {
            modifier = ShapeModifiers.getModifier(arr[i].ty);
            itemsData[i] = modifier;
            modifier.init(this, arr, i, itemsData);
            this.shapeModifiers.push(modifier);
            shouldRender = false;
          } else {
            modifier = itemsData[i];
            modifier.closed = true;
          }
          ownModifiers.push(modifier);
        }
        this.addProcessedElement(arr[i], i + 1);
      }
      this.removeTransformFromStyleList();
      this.closeStyles(ownStyles);
      len = ownModifiers.length;
      for (i = 0; i < len; i += 1) {
        ownModifiers[i].closed = true;
      }
    };
    CVShapeElement.prototype.renderInnerContent = function() {
      this.transformHelper.opacity = 1;
      this.transformHelper._opMdf = false;
      this.renderModifiers();
      this.transformsManager.processSequences(this._isFirstFrame);
      this.renderShape(this.transformHelper, this.shapesData, this.itemsData, true);
    };
    CVShapeElement.prototype.renderShapeTransform = function(parentTransform, groupTransform) {
      if (parentTransform._opMdf || groupTransform.op._mdf || this._isFirstFrame) {
        groupTransform.opacity = parentTransform.opacity;
        groupTransform.opacity *= groupTransform.op.v;
        groupTransform._opMdf = true;
      }
    };
    CVShapeElement.prototype.drawLayer = function() {
      var i;
      var len = this.stylesList.length;
      var j;
      var jLen;
      var k;
      var kLen;
      var elems;
      var nodes;
      var renderer2 = this.globalData.renderer;
      var ctx = this.globalData.canvasContext;
      var type;
      var currentStyle;
      for (i = 0; i < len; i += 1) {
        currentStyle = this.stylesList[i];
        type = currentStyle.type;
        if (!((type === "st" || type === "gs") && currentStyle.wi === 0 || !currentStyle.data._shouldRender || currentStyle.coOp === 0 || this.globalData.currentGlobalAlpha === 0)) {
          renderer2.save();
          elems = currentStyle.elements;
          if (type === "st" || type === "gs") {
            renderer2.ctxStrokeStyle(type === "st" ? currentStyle.co : currentStyle.grd);
            renderer2.ctxLineWidth(currentStyle.wi);
            renderer2.ctxLineCap(currentStyle.lc);
            renderer2.ctxLineJoin(currentStyle.lj);
            renderer2.ctxMiterLimit(currentStyle.ml || 0);
          } else {
            renderer2.ctxFillStyle(type === "fl" ? currentStyle.co : currentStyle.grd);
          }
          renderer2.ctxOpacity(currentStyle.coOp);
          if (type !== "st" && type !== "gs") {
            ctx.beginPath();
          }
          renderer2.ctxTransform(currentStyle.preTransforms.finalTransform.props);
          jLen = elems.length;
          for (j = 0; j < jLen; j += 1) {
            if (type === "st" || type === "gs") {
              ctx.beginPath();
              if (currentStyle.da) {
                ctx.setLineDash(currentStyle.da);
                ctx.lineDashOffset = currentStyle["do"];
              }
            }
            nodes = elems[j].trNodes;
            kLen = nodes.length;
            for (k = 0; k < kLen; k += 1) {
              if (nodes[k].t === "m") {
                ctx.moveTo(nodes[k].p[0], nodes[k].p[1]);
              } else if (nodes[k].t === "c") {
                ctx.bezierCurveTo(nodes[k].pts[0], nodes[k].pts[1], nodes[k].pts[2], nodes[k].pts[3], nodes[k].pts[4], nodes[k].pts[5]);
              } else {
                ctx.closePath();
              }
            }
            if (type === "st" || type === "gs") {
              renderer2.ctxStroke();
              if (currentStyle.da) {
                ctx.setLineDash(this.dashResetter);
              }
            }
          }
          if (type !== "st" && type !== "gs") {
            this.globalData.renderer.ctxFill(currentStyle.r);
          }
          renderer2.restore();
        }
      }
    };
    CVShapeElement.prototype.renderShape = function(parentTransform, items, data2, isMain) {
      var i;
      var len = items.length - 1;
      var groupTransform;
      groupTransform = parentTransform;
      for (i = len; i >= 0; i -= 1) {
        if (items[i].ty === "tr") {
          groupTransform = data2[i].transform;
          this.renderShapeTransform(parentTransform, groupTransform);
        } else if (items[i].ty === "sh" || items[i].ty === "el" || items[i].ty === "rc" || items[i].ty === "sr") {
          this.renderPath(items[i], data2[i]);
        } else if (items[i].ty === "fl") {
          this.renderFill(items[i], data2[i], groupTransform);
        } else if (items[i].ty === "st") {
          this.renderStroke(items[i], data2[i], groupTransform);
        } else if (items[i].ty === "gf" || items[i].ty === "gs") {
          this.renderGradientFill(items[i], data2[i], groupTransform);
        } else if (items[i].ty === "gr") {
          this.renderShape(groupTransform, items[i].it, data2[i].it);
        } else if (items[i].ty === "tm") ;
      }
      if (isMain) {
        this.drawLayer();
      }
    };
    CVShapeElement.prototype.renderStyledShape = function(styledShape, shape) {
      if (this._isFirstFrame || shape._mdf || styledShape.transforms._mdf) {
        var shapeNodes = styledShape.trNodes;
        var paths = shape.paths;
        var i;
        var len;
        var j;
        var jLen = paths._length;
        shapeNodes.length = 0;
        var groupTransformMat = styledShape.transforms.finalTransform;
        for (j = 0; j < jLen; j += 1) {
          var pathNodes = paths.shapes[j];
          if (pathNodes && pathNodes.v) {
            len = pathNodes._length;
            for (i = 1; i < len; i += 1) {
              if (i === 1) {
                shapeNodes.push({
                  t: "m",
                  p: groupTransformMat.applyToPointArray(pathNodes.v[0][0], pathNodes.v[0][1], 0)
                });
              }
              shapeNodes.push({
                t: "c",
                pts: groupTransformMat.applyToTriplePoints(pathNodes.o[i - 1], pathNodes.i[i], pathNodes.v[i])
              });
            }
            if (len === 1) {
              shapeNodes.push({
                t: "m",
                p: groupTransformMat.applyToPointArray(pathNodes.v[0][0], pathNodes.v[0][1], 0)
              });
            }
            if (pathNodes.c && len) {
              shapeNodes.push({
                t: "c",
                pts: groupTransformMat.applyToTriplePoints(pathNodes.o[i - 1], pathNodes.i[0], pathNodes.v[0])
              });
              shapeNodes.push({
                t: "z"
              });
            }
          }
        }
        styledShape.trNodes = shapeNodes;
      }
    };
    CVShapeElement.prototype.renderPath = function(pathData, itemData) {
      if (pathData.hd !== true && pathData._shouldRender) {
        var i;
        var len = itemData.styledShapes.length;
        for (i = 0; i < len; i += 1) {
          this.renderStyledShape(itemData.styledShapes[i], itemData.sh);
        }
      }
    };
    CVShapeElement.prototype.renderFill = function(styleData, itemData, groupTransform) {
      var styleElem = itemData.style;
      if (itemData.c._mdf || this._isFirstFrame) {
        styleElem.co = "rgb(" + bmFloor(itemData.c.v[0]) + "," + bmFloor(itemData.c.v[1]) + "," + bmFloor(itemData.c.v[2]) + ")";
      }
      if (itemData.o._mdf || groupTransform._opMdf || this._isFirstFrame) {
        styleElem.coOp = itemData.o.v * groupTransform.opacity;
      }
    };
    CVShapeElement.prototype.renderGradientFill = function(styleData, itemData, groupTransform) {
      var styleElem = itemData.style;
      var grd;
      if (!styleElem.grd || itemData.g._mdf || itemData.s._mdf || itemData.e._mdf || styleData.t !== 1 && (itemData.h._mdf || itemData.a._mdf)) {
        var ctx = this.globalData.canvasContext;
        var pt1 = itemData.s.v;
        var pt2 = itemData.e.v;
        if (styleData.t === 1) {
          grd = ctx.createLinearGradient(pt1[0], pt1[1], pt2[0], pt2[1]);
        } else {
          var rad = Math.sqrt(Math.pow(pt1[0] - pt2[0], 2) + Math.pow(pt1[1] - pt2[1], 2));
          var ang = Math.atan2(pt2[1] - pt1[1], pt2[0] - pt1[0]);
          var percent = itemData.h.v;
          if (percent >= 1) {
            percent = 0.99;
          } else if (percent <= -1) {
            percent = -0.99;
          }
          var dist = rad * percent;
          var x = Math.cos(ang + itemData.a.v) * dist + pt1[0];
          var y = Math.sin(ang + itemData.a.v) * dist + pt1[1];
          grd = ctx.createRadialGradient(x, y, 0, pt1[0], pt1[1], rad);
        }
        var i;
        var len = styleData.g.p;
        var cValues = itemData.g.c;
        var opacity = 1;
        for (i = 0; i < len; i += 1) {
          if (itemData.g._hasOpacity && itemData.g._collapsable) {
            opacity = itemData.g.o[i * 2 + 1];
          }
          grd.addColorStop(cValues[i * 4] / 100, "rgba(" + cValues[i * 4 + 1] + "," + cValues[i * 4 + 2] + "," + cValues[i * 4 + 3] + "," + opacity + ")");
        }
        styleElem.grd = grd;
      }
      styleElem.coOp = itemData.o.v * groupTransform.opacity;
    };
    CVShapeElement.prototype.renderStroke = function(styleData, itemData, groupTransform) {
      var styleElem = itemData.style;
      var d = itemData.d;
      if (d && (d._mdf || this._isFirstFrame)) {
        styleElem.da = d.dashArray;
        styleElem["do"] = d.dashoffset[0];
      }
      if (itemData.c._mdf || this._isFirstFrame) {
        styleElem.co = "rgb(" + bmFloor(itemData.c.v[0]) + "," + bmFloor(itemData.c.v[1]) + "," + bmFloor(itemData.c.v[2]) + ")";
      }
      if (itemData.o._mdf || groupTransform._opMdf || this._isFirstFrame) {
        styleElem.coOp = itemData.o.v * groupTransform.opacity;
      }
      if (itemData.w._mdf || this._isFirstFrame) {
        styleElem.wi = itemData.w.v;
      }
    };
    CVShapeElement.prototype.destroy = function() {
      this.shapesData = null;
      this.globalData = null;
      this.canvasContext = null;
      this.stylesList.length = 0;
      this.itemsData.length = 0;
    };
    function CVTextElement(data2, globalData2, comp2) {
      this.textSpans = [];
      this.yOffset = 0;
      this.fillColorAnim = false;
      this.strokeColorAnim = false;
      this.strokeWidthAnim = false;
      this.stroke = false;
      this.fill = false;
      this.justifyOffset = 0;
      this.currentRender = null;
      this.renderType = "canvas";
      this.values = {
        fill: "rgba(0,0,0,0)",
        stroke: "rgba(0,0,0,0)",
        sWidth: 0,
        fValue: ""
      };
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([BaseElement, TransformElement, CVBaseElement, HierarchyElement, FrameElement, RenderableElement, ITextElement], CVTextElement);
    CVTextElement.prototype.tHelper = createTag("canvas").getContext("2d");
    CVTextElement.prototype.buildNewText = function() {
      var documentData = this.textProperty.currentData;
      this.renderedLetters = createSizedArray(documentData.l ? documentData.l.length : 0);
      var hasFill = false;
      if (documentData.fc) {
        hasFill = true;
        this.values.fill = this.buildColor(documentData.fc);
      } else {
        this.values.fill = "rgba(0,0,0,0)";
      }
      this.fill = hasFill;
      var hasStroke = false;
      if (documentData.sc) {
        hasStroke = true;
        this.values.stroke = this.buildColor(documentData.sc);
        this.values.sWidth = documentData.sw;
      }
      var fontData = this.globalData.fontManager.getFontByName(documentData.f);
      var i;
      var len;
      var letters = documentData.l;
      var matrixHelper = this.mHelper;
      this.stroke = hasStroke;
      this.values.fValue = documentData.finalSize + "px " + this.globalData.fontManager.getFontByName(documentData.f).fFamily;
      len = documentData.finalText.length;
      var charData;
      var shapeData;
      var k;
      var kLen;
      var shapes;
      var j;
      var jLen;
      var pathNodes;
      var commands;
      var pathArr;
      var singleShape = this.data.singleShape;
      var trackingOffset = documentData.tr * 1e-3 * documentData.finalSize;
      var xPos = 0;
      var yPos = 0;
      var firstLine = true;
      var cnt = 0;
      for (i = 0; i < len; i += 1) {
        charData = this.globalData.fontManager.getCharData(documentData.finalText[i], fontData.fStyle, this.globalData.fontManager.getFontByName(documentData.f).fFamily);
        shapeData = charData && charData.data || {};
        matrixHelper.reset();
        if (singleShape && letters[i].n) {
          xPos = -trackingOffset;
          yPos += documentData.yOffset;
          yPos += firstLine ? 1 : 0;
          firstLine = false;
        }
        shapes = shapeData.shapes ? shapeData.shapes[0].it : [];
        jLen = shapes.length;
        matrixHelper.scale(documentData.finalSize / 100, documentData.finalSize / 100);
        if (singleShape) {
          this.applyTextPropertiesToMatrix(documentData, matrixHelper, letters[i].line, xPos, yPos);
        }
        commands = createSizedArray(jLen - 1);
        var commandsCounter = 0;
        for (j = 0; j < jLen; j += 1) {
          if (shapes[j].ty === "sh") {
            kLen = shapes[j].ks.k.i.length;
            pathNodes = shapes[j].ks.k;
            pathArr = [];
            for (k = 1; k < kLen; k += 1) {
              if (k === 1) {
                pathArr.push(matrixHelper.applyToX(pathNodes.v[0][0], pathNodes.v[0][1], 0), matrixHelper.applyToY(pathNodes.v[0][0], pathNodes.v[0][1], 0));
              }
              pathArr.push(matrixHelper.applyToX(pathNodes.o[k - 1][0], pathNodes.o[k - 1][1], 0), matrixHelper.applyToY(pathNodes.o[k - 1][0], pathNodes.o[k - 1][1], 0), matrixHelper.applyToX(pathNodes.i[k][0], pathNodes.i[k][1], 0), matrixHelper.applyToY(pathNodes.i[k][0], pathNodes.i[k][1], 0), matrixHelper.applyToX(pathNodes.v[k][0], pathNodes.v[k][1], 0), matrixHelper.applyToY(pathNodes.v[k][0], pathNodes.v[k][1], 0));
            }
            pathArr.push(matrixHelper.applyToX(pathNodes.o[k - 1][0], pathNodes.o[k - 1][1], 0), matrixHelper.applyToY(pathNodes.o[k - 1][0], pathNodes.o[k - 1][1], 0), matrixHelper.applyToX(pathNodes.i[0][0], pathNodes.i[0][1], 0), matrixHelper.applyToY(pathNodes.i[0][0], pathNodes.i[0][1], 0), matrixHelper.applyToX(pathNodes.v[0][0], pathNodes.v[0][1], 0), matrixHelper.applyToY(pathNodes.v[0][0], pathNodes.v[0][1], 0));
            commands[commandsCounter] = pathArr;
            commandsCounter += 1;
          }
        }
        if (singleShape) {
          xPos += letters[i].l;
          xPos += trackingOffset;
        }
        if (this.textSpans[cnt]) {
          this.textSpans[cnt].elem = commands;
        } else {
          this.textSpans[cnt] = {
            elem: commands
          };
        }
        cnt += 1;
      }
    };
    CVTextElement.prototype.renderInnerContent = function() {
      this.validateText();
      var ctx = this.canvasContext;
      ctx.font = this.values.fValue;
      this.globalData.renderer.ctxLineCap("butt");
      this.globalData.renderer.ctxLineJoin("miter");
      this.globalData.renderer.ctxMiterLimit(4);
      if (!this.data.singleShape) {
        this.textAnimator.getMeasures(this.textProperty.currentData, this.lettersChangedFlag);
      }
      var i;
      var len;
      var j;
      var jLen;
      var k;
      var kLen;
      var renderedLetters = this.textAnimator.renderedLetters;
      var letters = this.textProperty.currentData.l;
      len = letters.length;
      var renderedLetter;
      var lastFill = null;
      var lastStroke = null;
      var lastStrokeW = null;
      var commands;
      var pathArr;
      var renderer2 = this.globalData.renderer;
      for (i = 0; i < len; i += 1) {
        if (!letters[i].n) {
          renderedLetter = renderedLetters[i];
          if (renderedLetter) {
            renderer2.save();
            renderer2.ctxTransform(renderedLetter.p);
            renderer2.ctxOpacity(renderedLetter.o);
          }
          if (this.fill) {
            if (renderedLetter && renderedLetter.fc) {
              if (lastFill !== renderedLetter.fc) {
                renderer2.ctxFillStyle(renderedLetter.fc);
                lastFill = renderedLetter.fc;
              }
            } else if (lastFill !== this.values.fill) {
              lastFill = this.values.fill;
              renderer2.ctxFillStyle(this.values.fill);
            }
            commands = this.textSpans[i].elem;
            jLen = commands.length;
            this.globalData.canvasContext.beginPath();
            for (j = 0; j < jLen; j += 1) {
              pathArr = commands[j];
              kLen = pathArr.length;
              this.globalData.canvasContext.moveTo(pathArr[0], pathArr[1]);
              for (k = 2; k < kLen; k += 6) {
                this.globalData.canvasContext.bezierCurveTo(pathArr[k], pathArr[k + 1], pathArr[k + 2], pathArr[k + 3], pathArr[k + 4], pathArr[k + 5]);
              }
            }
            this.globalData.canvasContext.closePath();
            renderer2.ctxFill();
          }
          if (this.stroke) {
            if (renderedLetter && renderedLetter.sw) {
              if (lastStrokeW !== renderedLetter.sw) {
                lastStrokeW = renderedLetter.sw;
                renderer2.ctxLineWidth(renderedLetter.sw);
              }
            } else if (lastStrokeW !== this.values.sWidth) {
              lastStrokeW = this.values.sWidth;
              renderer2.ctxLineWidth(this.values.sWidth);
            }
            if (renderedLetter && renderedLetter.sc) {
              if (lastStroke !== renderedLetter.sc) {
                lastStroke = renderedLetter.sc;
                renderer2.ctxStrokeStyle(renderedLetter.sc);
              }
            } else if (lastStroke !== this.values.stroke) {
              lastStroke = this.values.stroke;
              renderer2.ctxStrokeStyle(this.values.stroke);
            }
            commands = this.textSpans[i].elem;
            jLen = commands.length;
            this.globalData.canvasContext.beginPath();
            for (j = 0; j < jLen; j += 1) {
              pathArr = commands[j];
              kLen = pathArr.length;
              this.globalData.canvasContext.moveTo(pathArr[0], pathArr[1]);
              for (k = 2; k < kLen; k += 6) {
                this.globalData.canvasContext.bezierCurveTo(pathArr[k], pathArr[k + 1], pathArr[k + 2], pathArr[k + 3], pathArr[k + 4], pathArr[k + 5]);
              }
            }
            this.globalData.canvasContext.closePath();
            renderer2.ctxStroke();
          }
          if (renderedLetter) {
            this.globalData.renderer.restore();
          }
        }
      }
    };
    function CVImageElement(data2, globalData2, comp2) {
      this.assetData = globalData2.getAssetData(data2.refId);
      this.img = globalData2.imageLoader.getAsset(this.assetData);
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([BaseElement, TransformElement, CVBaseElement, HierarchyElement, FrameElement, RenderableElement], CVImageElement);
    CVImageElement.prototype.initElement = SVGShapeElement.prototype.initElement;
    CVImageElement.prototype.prepareFrame = IImageElement.prototype.prepareFrame;
    CVImageElement.prototype.createContent = function() {
      if (this.img.width && (this.assetData.w !== this.img.width || this.assetData.h !== this.img.height)) {
        var canvas = createTag("canvas");
        canvas.width = this.assetData.w;
        canvas.height = this.assetData.h;
        var ctx = canvas.getContext("2d");
        var imgW = this.img.width;
        var imgH = this.img.height;
        var imgRel = imgW / imgH;
        var canvasRel = this.assetData.w / this.assetData.h;
        var widthCrop;
        var heightCrop;
        var par = this.assetData.pr || this.globalData.renderConfig.imagePreserveAspectRatio;
        if (imgRel > canvasRel && par === "xMidYMid slice" || imgRel < canvasRel && par !== "xMidYMid slice") {
          heightCrop = imgH;
          widthCrop = heightCrop * canvasRel;
        } else {
          widthCrop = imgW;
          heightCrop = widthCrop / canvasRel;
        }
        ctx.drawImage(this.img, (imgW - widthCrop) / 2, (imgH - heightCrop) / 2, widthCrop, heightCrop, 0, 0, this.assetData.w, this.assetData.h);
        this.img = canvas;
      }
    };
    CVImageElement.prototype.renderInnerContent = function() {
      this.canvasContext.drawImage(this.img, 0, 0);
    };
    CVImageElement.prototype.destroy = function() {
      this.img = null;
    };
    function CVSolidElement(data2, globalData2, comp2) {
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([BaseElement, TransformElement, CVBaseElement, HierarchyElement, FrameElement, RenderableElement], CVSolidElement);
    CVSolidElement.prototype.initElement = SVGShapeElement.prototype.initElement;
    CVSolidElement.prototype.prepareFrame = IImageElement.prototype.prepareFrame;
    CVSolidElement.prototype.renderInnerContent = function() {
      this.globalData.renderer.ctxFillStyle(this.data.sc);
      this.globalData.renderer.ctxFillRect(0, 0, this.data.sw, this.data.sh);
    };
    function CanvasRendererBase() {
    }
    extendPrototype([BaseRenderer], CanvasRendererBase);
    CanvasRendererBase.prototype.createShape = function(data2) {
      return new CVShapeElement(data2, this.globalData, this);
    };
    CanvasRendererBase.prototype.createText = function(data2) {
      return new CVTextElement(data2, this.globalData, this);
    };
    CanvasRendererBase.prototype.createImage = function(data2) {
      return new CVImageElement(data2, this.globalData, this);
    };
    CanvasRendererBase.prototype.createSolid = function(data2) {
      return new CVSolidElement(data2, this.globalData, this);
    };
    CanvasRendererBase.prototype.createNull = SVGRenderer.prototype.createNull;
    CanvasRendererBase.prototype.ctxTransform = function(props) {
      if (props[0] === 1 && props[1] === 0 && props[4] === 0 && props[5] === 1 && props[12] === 0 && props[13] === 0) {
        return;
      }
      this.canvasContext.transform(props[0], props[1], props[4], props[5], props[12], props[13]);
    };
    CanvasRendererBase.prototype.ctxOpacity = function(op2) {
      this.canvasContext.globalAlpha *= op2 < 0 ? 0 : op2;
    };
    CanvasRendererBase.prototype.ctxFillStyle = function(value2) {
      this.canvasContext.fillStyle = value2;
    };
    CanvasRendererBase.prototype.ctxStrokeStyle = function(value2) {
      this.canvasContext.strokeStyle = value2;
    };
    CanvasRendererBase.prototype.ctxLineWidth = function(value2) {
      this.canvasContext.lineWidth = value2;
    };
    CanvasRendererBase.prototype.ctxLineCap = function(value2) {
      this.canvasContext.lineCap = value2;
    };
    CanvasRendererBase.prototype.ctxLineJoin = function(value2) {
      this.canvasContext.lineJoin = value2;
    };
    CanvasRendererBase.prototype.ctxMiterLimit = function(value2) {
      this.canvasContext.miterLimit = value2;
    };
    CanvasRendererBase.prototype.ctxFill = function(rule) {
      this.canvasContext.fill(rule);
    };
    CanvasRendererBase.prototype.ctxFillRect = function(x, y, w2, h2) {
      this.canvasContext.fillRect(x, y, w2, h2);
    };
    CanvasRendererBase.prototype.ctxStroke = function() {
      this.canvasContext.stroke();
    };
    CanvasRendererBase.prototype.reset = function() {
      if (!this.renderConfig.clearCanvas) {
        this.canvasContext.restore();
        return;
      }
      this.contextData.reset();
    };
    CanvasRendererBase.prototype.save = function() {
      this.canvasContext.save();
    };
    CanvasRendererBase.prototype.restore = function(actionFlag) {
      if (!this.renderConfig.clearCanvas) {
        this.canvasContext.restore();
        return;
      }
      if (actionFlag) {
        this.globalData.blendMode = "source-over";
      }
      this.contextData.restore(actionFlag);
    };
    CanvasRendererBase.prototype.configAnimation = function(animData) {
      if (this.animationItem.wrapper) {
        this.animationItem.container = createTag("canvas");
        var containerStyle = this.animationItem.container.style;
        containerStyle.width = "100%";
        containerStyle.height = "100%";
        var origin = "0px 0px 0px";
        containerStyle.transformOrigin = origin;
        containerStyle.mozTransformOrigin = origin;
        containerStyle.webkitTransformOrigin = origin;
        containerStyle["-webkit-transform"] = origin;
        containerStyle.contentVisibility = this.renderConfig.contentVisibility;
        this.animationItem.wrapper.appendChild(this.animationItem.container);
        this.canvasContext = this.animationItem.container.getContext("2d");
        if (this.renderConfig.className) {
          this.animationItem.container.setAttribute("class", this.renderConfig.className);
        }
        if (this.renderConfig.id) {
          this.animationItem.container.setAttribute("id", this.renderConfig.id);
        }
      } else {
        this.canvasContext = this.renderConfig.context;
      }
      this.contextData.setContext(this.canvasContext);
      this.data = animData;
      this.layers = animData.layers;
      this.transformCanvas = {
        w: animData.w,
        h: animData.h,
        sx: 0,
        sy: 0,
        tx: 0,
        ty: 0
      };
      this.setupGlobalData(animData, document.body);
      this.globalData.canvasContext = this.canvasContext;
      this.globalData.renderer = this;
      this.globalData.isDashed = false;
      this.globalData.progressiveLoad = this.renderConfig.progressiveLoad;
      this.globalData.transformCanvas = this.transformCanvas;
      this.elements = createSizedArray(animData.layers.length);
      this.updateContainerSize();
    };
    CanvasRendererBase.prototype.updateContainerSize = function(width2, height2) {
      this.reset();
      var elementWidth;
      var elementHeight;
      if (width2) {
        elementWidth = width2;
        elementHeight = height2;
        this.canvasContext.canvas.width = elementWidth;
        this.canvasContext.canvas.height = elementHeight;
      } else {
        if (this.animationItem.wrapper && this.animationItem.container) {
          elementWidth = this.animationItem.wrapper.offsetWidth;
          elementHeight = this.animationItem.wrapper.offsetHeight;
        } else {
          elementWidth = this.canvasContext.canvas.width;
          elementHeight = this.canvasContext.canvas.height;
        }
        this.canvasContext.canvas.width = elementWidth * this.renderConfig.dpr;
        this.canvasContext.canvas.height = elementHeight * this.renderConfig.dpr;
      }
      var elementRel;
      var animationRel;
      if (this.renderConfig.preserveAspectRatio.indexOf("meet") !== -1 || this.renderConfig.preserveAspectRatio.indexOf("slice") !== -1) {
        var par = this.renderConfig.preserveAspectRatio.split(" ");
        var fillType = par[1] || "meet";
        var pos = par[0] || "xMidYMid";
        var xPos = pos.substr(0, 4);
        var yPos = pos.substr(4);
        elementRel = elementWidth / elementHeight;
        animationRel = this.transformCanvas.w / this.transformCanvas.h;
        if (animationRel > elementRel && fillType === "meet" || animationRel < elementRel && fillType === "slice") {
          this.transformCanvas.sx = elementWidth / (this.transformCanvas.w / this.renderConfig.dpr);
          this.transformCanvas.sy = elementWidth / (this.transformCanvas.w / this.renderConfig.dpr);
        } else {
          this.transformCanvas.sx = elementHeight / (this.transformCanvas.h / this.renderConfig.dpr);
          this.transformCanvas.sy = elementHeight / (this.transformCanvas.h / this.renderConfig.dpr);
        }
        if (xPos === "xMid" && (animationRel < elementRel && fillType === "meet" || animationRel > elementRel && fillType === "slice")) {
          this.transformCanvas.tx = (elementWidth - this.transformCanvas.w * (elementHeight / this.transformCanvas.h)) / 2 * this.renderConfig.dpr;
        } else if (xPos === "xMax" && (animationRel < elementRel && fillType === "meet" || animationRel > elementRel && fillType === "slice")) {
          this.transformCanvas.tx = (elementWidth - this.transformCanvas.w * (elementHeight / this.transformCanvas.h)) * this.renderConfig.dpr;
        } else {
          this.transformCanvas.tx = 0;
        }
        if (yPos === "YMid" && (animationRel > elementRel && fillType === "meet" || animationRel < elementRel && fillType === "slice")) {
          this.transformCanvas.ty = (elementHeight - this.transformCanvas.h * (elementWidth / this.transformCanvas.w)) / 2 * this.renderConfig.dpr;
        } else if (yPos === "YMax" && (animationRel > elementRel && fillType === "meet" || animationRel < elementRel && fillType === "slice")) {
          this.transformCanvas.ty = (elementHeight - this.transformCanvas.h * (elementWidth / this.transformCanvas.w)) * this.renderConfig.dpr;
        } else {
          this.transformCanvas.ty = 0;
        }
      } else if (this.renderConfig.preserveAspectRatio === "none") {
        this.transformCanvas.sx = elementWidth / (this.transformCanvas.w / this.renderConfig.dpr);
        this.transformCanvas.sy = elementHeight / (this.transformCanvas.h / this.renderConfig.dpr);
        this.transformCanvas.tx = 0;
        this.transformCanvas.ty = 0;
      } else {
        this.transformCanvas.sx = this.renderConfig.dpr;
        this.transformCanvas.sy = this.renderConfig.dpr;
        this.transformCanvas.tx = 0;
        this.transformCanvas.ty = 0;
      }
      this.transformCanvas.props = [this.transformCanvas.sx, 0, 0, 0, 0, this.transformCanvas.sy, 0, 0, 0, 0, 1, 0, this.transformCanvas.tx, this.transformCanvas.ty, 0, 1];
      this.ctxTransform(this.transformCanvas.props);
      this.canvasContext.beginPath();
      this.canvasContext.rect(0, 0, this.transformCanvas.w, this.transformCanvas.h);
      this.canvasContext.closePath();
      this.canvasContext.clip();
      this.renderFrame(this.renderedFrame, true);
    };
    CanvasRendererBase.prototype.destroy = function() {
      if (this.renderConfig.clearCanvas && this.animationItem.wrapper) {
        this.animationItem.wrapper.innerText = "";
      }
      var i;
      var len = this.layers ? this.layers.length : 0;
      for (i = len - 1; i >= 0; i -= 1) {
        if (this.elements[i] && this.elements[i].destroy) {
          this.elements[i].destroy();
        }
      }
      this.elements.length = 0;
      this.globalData.canvasContext = null;
      this.animationItem.container = null;
      this.destroyed = true;
    };
    CanvasRendererBase.prototype.renderFrame = function(num, forceRender) {
      if (this.renderedFrame === num && this.renderConfig.clearCanvas === true && !forceRender || this.destroyed || num === -1) {
        return;
      }
      this.renderedFrame = num;
      this.globalData.frameNum = num - this.animationItem._isFirstFrame;
      this.globalData.frameId += 1;
      this.globalData._mdf = !this.renderConfig.clearCanvas || forceRender;
      this.globalData.projectInterface.currentFrame = num;
      var i;
      var len = this.layers.length;
      if (!this.completeLayers) {
        this.checkLayers(num);
      }
      for (i = len - 1; i >= 0; i -= 1) {
        if (this.completeLayers || this.elements[i]) {
          this.elements[i].prepareFrame(num - this.layers[i].st);
        }
      }
      if (this.globalData._mdf) {
        if (this.renderConfig.clearCanvas === true) {
          this.canvasContext.clearRect(0, 0, this.transformCanvas.w, this.transformCanvas.h);
        } else {
          this.save();
        }
        for (i = len - 1; i >= 0; i -= 1) {
          if (this.completeLayers || this.elements[i]) {
            this.elements[i].renderFrame();
          }
        }
        if (this.renderConfig.clearCanvas !== true) {
          this.restore();
        }
      }
    };
    CanvasRendererBase.prototype.buildItem = function(pos) {
      var elements = this.elements;
      if (elements[pos] || this.layers[pos].ty === 99) {
        return;
      }
      var element = this.createItem(this.layers[pos], this, this.globalData);
      elements[pos] = element;
      element.initExpressions();
    };
    CanvasRendererBase.prototype.checkPendingElements = function() {
      while (this.pendingElements.length) {
        var element = this.pendingElements.pop();
        element.checkParenting();
      }
    };
    CanvasRendererBase.prototype.hide = function() {
      this.animationItem.container.style.display = "none";
    };
    CanvasRendererBase.prototype.show = function() {
      this.animationItem.container.style.display = "block";
    };
    function CanvasContext() {
      this.opacity = -1;
      this.transform = createTypedArray("float32", 16);
      this.fillStyle = "";
      this.strokeStyle = "";
      this.lineWidth = "";
      this.lineCap = "";
      this.lineJoin = "";
      this.miterLimit = "";
      this.id = Math.random();
    }
    function CVContextData() {
      this.stack = [];
      this.cArrPos = 0;
      this.cTr = new Matrix();
      var i;
      var len = 15;
      for (i = 0; i < len; i += 1) {
        var canvasContext = new CanvasContext();
        this.stack[i] = canvasContext;
      }
      this._length = len;
      this.nativeContext = null;
      this.transformMat = new Matrix();
      this.currentOpacity = 1;
      this.currentFillStyle = "";
      this.appliedFillStyle = "";
      this.currentStrokeStyle = "";
      this.appliedStrokeStyle = "";
      this.currentLineWidth = "";
      this.appliedLineWidth = "";
      this.currentLineCap = "";
      this.appliedLineCap = "";
      this.currentLineJoin = "";
      this.appliedLineJoin = "";
      this.appliedMiterLimit = "";
      this.currentMiterLimit = "";
    }
    CVContextData.prototype.duplicate = function() {
      var newLength = this._length * 2;
      var i = 0;
      for (i = this._length; i < newLength; i += 1) {
        this.stack[i] = new CanvasContext();
      }
      this._length = newLength;
    };
    CVContextData.prototype.reset = function() {
      this.cArrPos = 0;
      this.cTr.reset();
      this.stack[this.cArrPos].opacity = 1;
    };
    CVContextData.prototype.restore = function(forceRestore) {
      this.cArrPos -= 1;
      var currentContext = this.stack[this.cArrPos];
      var transform2 = currentContext.transform;
      var i;
      var arr = this.cTr.props;
      for (i = 0; i < 16; i += 1) {
        arr[i] = transform2[i];
      }
      if (forceRestore) {
        this.nativeContext.restore();
        var prevStack = this.stack[this.cArrPos + 1];
        this.appliedFillStyle = prevStack.fillStyle;
        this.appliedStrokeStyle = prevStack.strokeStyle;
        this.appliedLineWidth = prevStack.lineWidth;
        this.appliedLineCap = prevStack.lineCap;
        this.appliedLineJoin = prevStack.lineJoin;
        this.appliedMiterLimit = prevStack.miterLimit;
      }
      this.nativeContext.setTransform(transform2[0], transform2[1], transform2[4], transform2[5], transform2[12], transform2[13]);
      if (forceRestore || currentContext.opacity !== -1 && this.currentOpacity !== currentContext.opacity) {
        this.nativeContext.globalAlpha = currentContext.opacity;
        this.currentOpacity = currentContext.opacity;
      }
      this.currentFillStyle = currentContext.fillStyle;
      this.currentStrokeStyle = currentContext.strokeStyle;
      this.currentLineWidth = currentContext.lineWidth;
      this.currentLineCap = currentContext.lineCap;
      this.currentLineJoin = currentContext.lineJoin;
      this.currentMiterLimit = currentContext.miterLimit;
    };
    CVContextData.prototype.save = function(saveOnNativeFlag) {
      if (saveOnNativeFlag) {
        this.nativeContext.save();
      }
      var props = this.cTr.props;
      if (this._length <= this.cArrPos) {
        this.duplicate();
      }
      var currentStack = this.stack[this.cArrPos];
      var i;
      for (i = 0; i < 16; i += 1) {
        currentStack.transform[i] = props[i];
      }
      this.cArrPos += 1;
      var newStack = this.stack[this.cArrPos];
      newStack.opacity = currentStack.opacity;
      newStack.fillStyle = currentStack.fillStyle;
      newStack.strokeStyle = currentStack.strokeStyle;
      newStack.lineWidth = currentStack.lineWidth;
      newStack.lineCap = currentStack.lineCap;
      newStack.lineJoin = currentStack.lineJoin;
      newStack.miterLimit = currentStack.miterLimit;
    };
    CVContextData.prototype.setOpacity = function(value2) {
      this.stack[this.cArrPos].opacity = value2;
    };
    CVContextData.prototype.setContext = function(value2) {
      this.nativeContext = value2;
    };
    CVContextData.prototype.fillStyle = function(value2) {
      if (this.stack[this.cArrPos].fillStyle !== value2) {
        this.currentFillStyle = value2;
        this.stack[this.cArrPos].fillStyle = value2;
      }
    };
    CVContextData.prototype.strokeStyle = function(value2) {
      if (this.stack[this.cArrPos].strokeStyle !== value2) {
        this.currentStrokeStyle = value2;
        this.stack[this.cArrPos].strokeStyle = value2;
      }
    };
    CVContextData.prototype.lineWidth = function(value2) {
      if (this.stack[this.cArrPos].lineWidth !== value2) {
        this.currentLineWidth = value2;
        this.stack[this.cArrPos].lineWidth = value2;
      }
    };
    CVContextData.prototype.lineCap = function(value2) {
      if (this.stack[this.cArrPos].lineCap !== value2) {
        this.currentLineCap = value2;
        this.stack[this.cArrPos].lineCap = value2;
      }
    };
    CVContextData.prototype.lineJoin = function(value2) {
      if (this.stack[this.cArrPos].lineJoin !== value2) {
        this.currentLineJoin = value2;
        this.stack[this.cArrPos].lineJoin = value2;
      }
    };
    CVContextData.prototype.miterLimit = function(value2) {
      if (this.stack[this.cArrPos].miterLimit !== value2) {
        this.currentMiterLimit = value2;
        this.stack[this.cArrPos].miterLimit = value2;
      }
    };
    CVContextData.prototype.transform = function(props) {
      this.transformMat.cloneFromProps(props);
      var currentTransform = this.cTr;
      this.transformMat.multiply(currentTransform);
      currentTransform.cloneFromProps(this.transformMat.props);
      var trProps = currentTransform.props;
      this.nativeContext.setTransform(trProps[0], trProps[1], trProps[4], trProps[5], trProps[12], trProps[13]);
    };
    CVContextData.prototype.opacity = function(op2) {
      var currentOpacity = this.stack[this.cArrPos].opacity;
      currentOpacity *= op2 < 0 ? 0 : op2;
      if (this.stack[this.cArrPos].opacity !== currentOpacity) {
        if (this.currentOpacity !== op2) {
          this.nativeContext.globalAlpha = op2;
          this.currentOpacity = op2;
        }
        this.stack[this.cArrPos].opacity = currentOpacity;
      }
    };
    CVContextData.prototype.fill = function(rule) {
      if (this.appliedFillStyle !== this.currentFillStyle) {
        this.appliedFillStyle = this.currentFillStyle;
        this.nativeContext.fillStyle = this.appliedFillStyle;
      }
      this.nativeContext.fill(rule);
    };
    CVContextData.prototype.fillRect = function(x, y, w2, h2) {
      if (this.appliedFillStyle !== this.currentFillStyle) {
        this.appliedFillStyle = this.currentFillStyle;
        this.nativeContext.fillStyle = this.appliedFillStyle;
      }
      this.nativeContext.fillRect(x, y, w2, h2);
    };
    CVContextData.prototype.stroke = function() {
      if (this.appliedStrokeStyle !== this.currentStrokeStyle) {
        this.appliedStrokeStyle = this.currentStrokeStyle;
        this.nativeContext.strokeStyle = this.appliedStrokeStyle;
      }
      if (this.appliedLineWidth !== this.currentLineWidth) {
        this.appliedLineWidth = this.currentLineWidth;
        this.nativeContext.lineWidth = this.appliedLineWidth;
      }
      if (this.appliedLineCap !== this.currentLineCap) {
        this.appliedLineCap = this.currentLineCap;
        this.nativeContext.lineCap = this.appliedLineCap;
      }
      if (this.appliedLineJoin !== this.currentLineJoin) {
        this.appliedLineJoin = this.currentLineJoin;
        this.nativeContext.lineJoin = this.appliedLineJoin;
      }
      if (this.appliedMiterLimit !== this.currentMiterLimit) {
        this.appliedMiterLimit = this.currentMiterLimit;
        this.nativeContext.miterLimit = this.appliedMiterLimit;
      }
      this.nativeContext.stroke();
    };
    function CVCompElement(data2, globalData2, comp2) {
      this.completeLayers = false;
      this.layers = data2.layers;
      this.pendingElements = [];
      this.elements = createSizedArray(this.layers.length);
      this.initElement(data2, globalData2, comp2);
      this.tm = data2.tm ? PropertyFactory.getProp(this, data2.tm, 0, globalData2.frameRate, this) : {
        _placeholder: true
      };
    }
    extendPrototype([CanvasRendererBase, ICompElement, CVBaseElement], CVCompElement);
    CVCompElement.prototype.renderInnerContent = function() {
      var ctx = this.canvasContext;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(this.data.w, 0);
      ctx.lineTo(this.data.w, this.data.h);
      ctx.lineTo(0, this.data.h);
      ctx.lineTo(0, 0);
      ctx.clip();
      var i;
      var len = this.layers.length;
      for (i = len - 1; i >= 0; i -= 1) {
        if (this.completeLayers || this.elements[i]) {
          this.elements[i].renderFrame();
        }
      }
    };
    CVCompElement.prototype.destroy = function() {
      var i;
      var len = this.layers.length;
      for (i = len - 1; i >= 0; i -= 1) {
        if (this.elements[i]) {
          this.elements[i].destroy();
        }
      }
      this.layers = null;
      this.elements = null;
    };
    CVCompElement.prototype.createComp = function(data2) {
      return new CVCompElement(data2, this.globalData, this);
    };
    function CanvasRenderer(animationItem, config) {
      this.animationItem = animationItem;
      this.renderConfig = {
        clearCanvas: config && config.clearCanvas !== void 0 ? config.clearCanvas : true,
        context: config && config.context || null,
        progressiveLoad: config && config.progressiveLoad || false,
        preserveAspectRatio: config && config.preserveAspectRatio || "xMidYMid meet",
        imagePreserveAspectRatio: config && config.imagePreserveAspectRatio || "xMidYMid slice",
        contentVisibility: config && config.contentVisibility || "visible",
        className: config && config.className || "",
        id: config && config.id || "",
        runExpressions: !config || config.runExpressions === void 0 || config.runExpressions
      };
      this.renderConfig.dpr = config && config.dpr || 1;
      if (this.animationItem.wrapper) {
        this.renderConfig.dpr = config && config.dpr || window.devicePixelRatio || 1;
      }
      this.renderedFrame = -1;
      this.globalData = {
        frameNum: -1,
        _mdf: false,
        renderConfig: this.renderConfig,
        currentGlobalAlpha: -1
      };
      this.contextData = new CVContextData();
      this.elements = [];
      this.pendingElements = [];
      this.transformMat = new Matrix();
      this.completeLayers = false;
      this.rendererType = "canvas";
      if (this.renderConfig.clearCanvas) {
        this.ctxTransform = this.contextData.transform.bind(this.contextData);
        this.ctxOpacity = this.contextData.opacity.bind(this.contextData);
        this.ctxFillStyle = this.contextData.fillStyle.bind(this.contextData);
        this.ctxStrokeStyle = this.contextData.strokeStyle.bind(this.contextData);
        this.ctxLineWidth = this.contextData.lineWidth.bind(this.contextData);
        this.ctxLineCap = this.contextData.lineCap.bind(this.contextData);
        this.ctxLineJoin = this.contextData.lineJoin.bind(this.contextData);
        this.ctxMiterLimit = this.contextData.miterLimit.bind(this.contextData);
        this.ctxFill = this.contextData.fill.bind(this.contextData);
        this.ctxFillRect = this.contextData.fillRect.bind(this.contextData);
        this.ctxStroke = this.contextData.stroke.bind(this.contextData);
        this.save = this.contextData.save.bind(this.contextData);
      }
    }
    extendPrototype([CanvasRendererBase], CanvasRenderer);
    CanvasRenderer.prototype.createComp = function(data2) {
      return new CVCompElement(data2, this.globalData, this);
    };
    function HBaseElement() {
    }
    HBaseElement.prototype = {
      checkBlendMode: function checkBlendMode() {
      },
      initRendererElement: function initRendererElement() {
        this.baseElement = createTag(this.data.tg || "div");
        if (this.data.hasMask) {
          this.svgElement = createNS("svg");
          this.layerElement = createNS("g");
          this.maskedElement = this.layerElement;
          this.svgElement.appendChild(this.layerElement);
          this.baseElement.appendChild(this.svgElement);
        } else {
          this.layerElement = this.baseElement;
        }
        styleDiv(this.baseElement);
      },
      createContainerElements: function createContainerElements() {
        this.renderableEffectsManager = new CVEffects(this);
        this.transformedElement = this.baseElement;
        this.maskedElement = this.layerElement;
        if (this.data.ln) {
          this.layerElement.setAttribute("id", this.data.ln);
        }
        if (this.data.cl) {
          this.layerElement.setAttribute("class", this.data.cl);
        }
        if (this.data.bm !== 0) {
          this.setBlendMode();
        }
      },
      renderElement: function renderElement() {
        var transformedElementStyle = this.transformedElement ? this.transformedElement.style : {};
        if (this.finalTransform._matMdf) {
          var matrixValue = this.finalTransform.mat.toCSS();
          transformedElementStyle.transform = matrixValue;
          transformedElementStyle.webkitTransform = matrixValue;
        }
        if (this.finalTransform._opMdf) {
          transformedElementStyle.opacity = this.finalTransform.mProp.o.v;
        }
      },
      renderFrame: function renderFrame() {
        if (this.data.hd || this.hidden) {
          return;
        }
        this.renderTransform();
        this.renderRenderable();
        this.renderElement();
        this.renderInnerContent();
        if (this._isFirstFrame) {
          this._isFirstFrame = false;
        }
      },
      destroy: function destroy() {
        this.layerElement = null;
        this.transformedElement = null;
        if (this.matteElement) {
          this.matteElement = null;
        }
        if (this.maskManager) {
          this.maskManager.destroy();
          this.maskManager = null;
        }
      },
      createRenderableComponents: function createRenderableComponents() {
        this.maskManager = new MaskElement(this.data, this, this.globalData);
      },
      addEffects: function addEffects() {
      },
      setMatte: function setMatte() {
      }
    };
    HBaseElement.prototype.getBaseElement = SVGBaseElement.prototype.getBaseElement;
    HBaseElement.prototype.destroyBaseElement = HBaseElement.prototype.destroy;
    HBaseElement.prototype.buildElementParenting = BaseRenderer.prototype.buildElementParenting;
    function HSolidElement(data2, globalData2, comp2) {
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([BaseElement, TransformElement, HBaseElement, HierarchyElement, FrameElement, RenderableDOMElement], HSolidElement);
    HSolidElement.prototype.createContent = function() {
      var rect;
      if (this.data.hasMask) {
        rect = createNS("rect");
        rect.setAttribute("width", this.data.sw);
        rect.setAttribute("height", this.data.sh);
        rect.setAttribute("fill", this.data.sc);
        this.svgElement.setAttribute("width", this.data.sw);
        this.svgElement.setAttribute("height", this.data.sh);
      } else {
        rect = createTag("div");
        rect.style.width = this.data.sw + "px";
        rect.style.height = this.data.sh + "px";
        rect.style.backgroundColor = this.data.sc;
      }
      this.layerElement.appendChild(rect);
    };
    function HShapeElement(data2, globalData2, comp2) {
      this.shapes = [];
      this.shapesData = data2.shapes;
      this.stylesList = [];
      this.shapeModifiers = [];
      this.itemsData = [];
      this.processedElements = [];
      this.animatedContents = [];
      this.shapesContainer = createNS("g");
      this.initElement(data2, globalData2, comp2);
      this.prevViewData = [];
      this.currentBBox = {
        x: 999999,
        y: -999999,
        h: 0,
        w: 0
      };
    }
    extendPrototype([BaseElement, TransformElement, HSolidElement, SVGShapeElement, HBaseElement, HierarchyElement, FrameElement, RenderableElement], HShapeElement);
    HShapeElement.prototype._renderShapeFrame = HShapeElement.prototype.renderInnerContent;
    HShapeElement.prototype.createContent = function() {
      var cont;
      this.baseElement.style.fontSize = 0;
      if (this.data.hasMask) {
        this.layerElement.appendChild(this.shapesContainer);
        cont = this.svgElement;
      } else {
        cont = createNS("svg");
        var size = this.comp.data ? this.comp.data : this.globalData.compSize;
        cont.setAttribute("width", size.w);
        cont.setAttribute("height", size.h);
        cont.appendChild(this.shapesContainer);
        this.layerElement.appendChild(cont);
      }
      this.searchShapes(this.shapesData, this.itemsData, this.prevViewData, this.shapesContainer, 0, [], true);
      this.filterUniqueShapes();
      this.shapeCont = cont;
    };
    HShapeElement.prototype.getTransformedPoint = function(transformers, point) {
      var i;
      var len = transformers.length;
      for (i = 0; i < len; i += 1) {
        point = transformers[i].mProps.v.applyToPointArray(point[0], point[1], 0);
      }
      return point;
    };
    HShapeElement.prototype.calculateShapeBoundingBox = function(item, boundingBox) {
      var shape = item.sh.v;
      var transformers = item.transformers;
      var i;
      var len = shape._length;
      var vPoint;
      var oPoint;
      var nextIPoint;
      var nextVPoint;
      if (len <= 1) {
        return;
      }
      for (i = 0; i < len - 1; i += 1) {
        vPoint = this.getTransformedPoint(transformers, shape.v[i]);
        oPoint = this.getTransformedPoint(transformers, shape.o[i]);
        nextIPoint = this.getTransformedPoint(transformers, shape.i[i + 1]);
        nextVPoint = this.getTransformedPoint(transformers, shape.v[i + 1]);
        this.checkBounds(vPoint, oPoint, nextIPoint, nextVPoint, boundingBox);
      }
      if (shape.c) {
        vPoint = this.getTransformedPoint(transformers, shape.v[i]);
        oPoint = this.getTransformedPoint(transformers, shape.o[i]);
        nextIPoint = this.getTransformedPoint(transformers, shape.i[0]);
        nextVPoint = this.getTransformedPoint(transformers, shape.v[0]);
        this.checkBounds(vPoint, oPoint, nextIPoint, nextVPoint, boundingBox);
      }
    };
    HShapeElement.prototype.checkBounds = function(vPoint, oPoint, nextIPoint, nextVPoint, boundingBox) {
      this.getBoundsOfCurve(vPoint, oPoint, nextIPoint, nextVPoint);
      var bounds = this.shapeBoundingBox;
      boundingBox.x = bmMin(bounds.left, boundingBox.x);
      boundingBox.xMax = bmMax(bounds.right, boundingBox.xMax);
      boundingBox.y = bmMin(bounds.top, boundingBox.y);
      boundingBox.yMax = bmMax(bounds.bottom, boundingBox.yMax);
    };
    HShapeElement.prototype.shapeBoundingBox = {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0
    };
    HShapeElement.prototype.tempBoundingBox = {
      x: 0,
      xMax: 0,
      y: 0,
      yMax: 0,
      width: 0,
      height: 0
    };
    HShapeElement.prototype.getBoundsOfCurve = function(p0, p1, p2, p3) {
      var bounds = [[p0[0], p3[0]], [p0[1], p3[1]]];
      for (var a, b, c, t, b2ac, t1, t2, i = 0; i < 2; ++i) {
        b = 6 * p0[i] - 12 * p1[i] + 6 * p2[i];
        a = -3 * p0[i] + 9 * p1[i] - 9 * p2[i] + 3 * p3[i];
        c = 3 * p1[i] - 3 * p0[i];
        b |= 0;
        a |= 0;
        c |= 0;
        if (a === 0 && b === 0) ;
        else if (a === 0) {
          t = -c / b;
          if (t > 0 && t < 1) {
            bounds[i].push(this.calculateF(t, p0, p1, p2, p3, i));
          }
        } else {
          b2ac = b * b - 4 * c * a;
          if (b2ac >= 0) {
            t1 = (-b + bmSqrt(b2ac)) / (2 * a);
            if (t1 > 0 && t1 < 1) bounds[i].push(this.calculateF(t1, p0, p1, p2, p3, i));
            t2 = (-b - bmSqrt(b2ac)) / (2 * a);
            if (t2 > 0 && t2 < 1) bounds[i].push(this.calculateF(t2, p0, p1, p2, p3, i));
          }
        }
      }
      this.shapeBoundingBox.left = bmMin.apply(null, bounds[0]);
      this.shapeBoundingBox.top = bmMin.apply(null, bounds[1]);
      this.shapeBoundingBox.right = bmMax.apply(null, bounds[0]);
      this.shapeBoundingBox.bottom = bmMax.apply(null, bounds[1]);
    };
    HShapeElement.prototype.calculateF = function(t, p0, p1, p2, p3, i) {
      return bmPow(1 - t, 3) * p0[i] + 3 * bmPow(1 - t, 2) * t * p1[i] + 3 * (1 - t) * bmPow(t, 2) * p2[i] + bmPow(t, 3) * p3[i];
    };
    HShapeElement.prototype.calculateBoundingBox = function(itemsData, boundingBox) {
      var i;
      var len = itemsData.length;
      for (i = 0; i < len; i += 1) {
        if (itemsData[i] && itemsData[i].sh) {
          this.calculateShapeBoundingBox(itemsData[i], boundingBox);
        } else if (itemsData[i] && itemsData[i].it) {
          this.calculateBoundingBox(itemsData[i].it, boundingBox);
        } else if (itemsData[i] && itemsData[i].style && itemsData[i].w) {
          this.expandStrokeBoundingBox(itemsData[i].w, boundingBox);
        }
      }
    };
    HShapeElement.prototype.expandStrokeBoundingBox = function(widthProperty, boundingBox) {
      var width2 = 0;
      if (widthProperty.keyframes) {
        for (var i = 0; i < widthProperty.keyframes.length; i += 1) {
          var kfw = widthProperty.keyframes[i].s;
          if (kfw > width2) {
            width2 = kfw;
          }
        }
        width2 *= widthProperty.mult;
      } else {
        width2 = widthProperty.v * widthProperty.mult;
      }
      boundingBox.x -= width2;
      boundingBox.xMax += width2;
      boundingBox.y -= width2;
      boundingBox.yMax += width2;
    };
    HShapeElement.prototype.currentBoxContains = function(box) {
      return this.currentBBox.x <= box.x && this.currentBBox.y <= box.y && this.currentBBox.width + this.currentBBox.x >= box.x + box.width && this.currentBBox.height + this.currentBBox.y >= box.y + box.height;
    };
    HShapeElement.prototype.renderInnerContent = function() {
      this._renderShapeFrame();
      if (!this.hidden && (this._isFirstFrame || this._mdf)) {
        var tempBoundingBox = this.tempBoundingBox;
        var max = 999999;
        tempBoundingBox.x = max;
        tempBoundingBox.xMax = -max;
        tempBoundingBox.y = max;
        tempBoundingBox.yMax = -max;
        this.calculateBoundingBox(this.itemsData, tempBoundingBox);
        tempBoundingBox.width = tempBoundingBox.xMax < tempBoundingBox.x ? 0 : tempBoundingBox.xMax - tempBoundingBox.x;
        tempBoundingBox.height = tempBoundingBox.yMax < tempBoundingBox.y ? 0 : tempBoundingBox.yMax - tempBoundingBox.y;
        if (this.currentBoxContains(tempBoundingBox)) {
          return;
        }
        var changed = false;
        if (this.currentBBox.w !== tempBoundingBox.width) {
          this.currentBBox.w = tempBoundingBox.width;
          this.shapeCont.setAttribute("width", tempBoundingBox.width);
          changed = true;
        }
        if (this.currentBBox.h !== tempBoundingBox.height) {
          this.currentBBox.h = tempBoundingBox.height;
          this.shapeCont.setAttribute("height", tempBoundingBox.height);
          changed = true;
        }
        if (changed || this.currentBBox.x !== tempBoundingBox.x || this.currentBBox.y !== tempBoundingBox.y) {
          this.currentBBox.w = tempBoundingBox.width;
          this.currentBBox.h = tempBoundingBox.height;
          this.currentBBox.x = tempBoundingBox.x;
          this.currentBBox.y = tempBoundingBox.y;
          this.shapeCont.setAttribute("viewBox", this.currentBBox.x + " " + this.currentBBox.y + " " + this.currentBBox.w + " " + this.currentBBox.h);
          var shapeStyle = this.shapeCont.style;
          var shapeTransform = "translate(" + this.currentBBox.x + "px," + this.currentBBox.y + "px)";
          shapeStyle.transform = shapeTransform;
          shapeStyle.webkitTransform = shapeTransform;
        }
      }
    };
    function HTextElement(data2, globalData2, comp2) {
      this.textSpans = [];
      this.textPaths = [];
      this.currentBBox = {
        x: 999999,
        y: -999999,
        h: 0,
        w: 0
      };
      this.renderType = "svg";
      this.isMasked = false;
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([BaseElement, TransformElement, HBaseElement, HierarchyElement, FrameElement, RenderableDOMElement, ITextElement], HTextElement);
    HTextElement.prototype.createContent = function() {
      this.isMasked = this.checkMasks();
      if (this.isMasked) {
        this.renderType = "svg";
        this.compW = this.comp.data.w;
        this.compH = this.comp.data.h;
        this.svgElement.setAttribute("width", this.compW);
        this.svgElement.setAttribute("height", this.compH);
        var g = createNS("g");
        this.maskedElement.appendChild(g);
        this.innerElem = g;
      } else {
        this.renderType = "html";
        this.innerElem = this.layerElement;
      }
      this.checkParenting();
    };
    HTextElement.prototype.buildNewText = function() {
      var documentData = this.textProperty.currentData;
      this.renderedLetters = createSizedArray(documentData.l ? documentData.l.length : 0);
      var innerElemStyle = this.innerElem.style;
      var textColor = documentData.fc ? this.buildColor(documentData.fc) : "rgba(0,0,0,0)";
      innerElemStyle.fill = textColor;
      innerElemStyle.color = textColor;
      if (documentData.sc) {
        innerElemStyle.stroke = this.buildColor(documentData.sc);
        innerElemStyle.strokeWidth = documentData.sw + "px";
      }
      var fontData = this.globalData.fontManager.getFontByName(documentData.f);
      if (!this.globalData.fontManager.chars) {
        innerElemStyle.fontSize = documentData.finalSize + "px";
        innerElemStyle.lineHeight = documentData.finalSize + "px";
        if (fontData.fClass) {
          this.innerElem.className = fontData.fClass;
        } else {
          innerElemStyle.fontFamily = fontData.fFamily;
          var fWeight = documentData.fWeight;
          var fStyle = documentData.fStyle;
          innerElemStyle.fontStyle = fStyle;
          innerElemStyle.fontWeight = fWeight;
        }
      }
      var i;
      var len;
      var letters = documentData.l;
      len = letters.length;
      var tSpan;
      var tParent;
      var tCont;
      var matrixHelper = this.mHelper;
      var shapes;
      var shapeStr = "";
      var cnt = 0;
      for (i = 0; i < len; i += 1) {
        if (this.globalData.fontManager.chars) {
          if (!this.textPaths[cnt]) {
            tSpan = createNS("path");
            tSpan.setAttribute("stroke-linecap", lineCapEnum[1]);
            tSpan.setAttribute("stroke-linejoin", lineJoinEnum[2]);
            tSpan.setAttribute("stroke-miterlimit", "4");
          } else {
            tSpan = this.textPaths[cnt];
          }
          if (!this.isMasked) {
            if (this.textSpans[cnt]) {
              tParent = this.textSpans[cnt];
              tCont = tParent.children[0];
            } else {
              tParent = createTag("div");
              tParent.style.lineHeight = 0;
              tCont = createNS("svg");
              tCont.appendChild(tSpan);
              styleDiv(tParent);
            }
          }
        } else if (!this.isMasked) {
          if (this.textSpans[cnt]) {
            tParent = this.textSpans[cnt];
            tSpan = this.textPaths[cnt];
          } else {
            tParent = createTag("span");
            styleDiv(tParent);
            tSpan = createTag("span");
            styleDiv(tSpan);
            tParent.appendChild(tSpan);
          }
        } else {
          tSpan = this.textPaths[cnt] ? this.textPaths[cnt] : createNS("text");
        }
        if (this.globalData.fontManager.chars) {
          var charData = this.globalData.fontManager.getCharData(documentData.finalText[i], fontData.fStyle, this.globalData.fontManager.getFontByName(documentData.f).fFamily);
          var shapeData;
          if (charData) {
            shapeData = charData.data;
          } else {
            shapeData = null;
          }
          matrixHelper.reset();
          if (shapeData && shapeData.shapes && shapeData.shapes.length) {
            shapes = shapeData.shapes[0].it;
            matrixHelper.scale(documentData.finalSize / 100, documentData.finalSize / 100);
            shapeStr = this.createPathShape(matrixHelper, shapes);
            tSpan.setAttribute("d", shapeStr);
          }
          if (!this.isMasked) {
            this.innerElem.appendChild(tParent);
            if (shapeData && shapeData.shapes) {
              document.body.appendChild(tCont);
              var boundingBox = tCont.getBBox();
              tCont.setAttribute("width", boundingBox.width + 2);
              tCont.setAttribute("height", boundingBox.height + 2);
              tCont.setAttribute("viewBox", boundingBox.x - 1 + " " + (boundingBox.y - 1) + " " + (boundingBox.width + 2) + " " + (boundingBox.height + 2));
              var tContStyle = tCont.style;
              var tContTranslation = "translate(" + (boundingBox.x - 1) + "px," + (boundingBox.y - 1) + "px)";
              tContStyle.transform = tContTranslation;
              tContStyle.webkitTransform = tContTranslation;
              letters[i].yOffset = boundingBox.y - 1;
            } else {
              tCont.setAttribute("width", 1);
              tCont.setAttribute("height", 1);
            }
            tParent.appendChild(tCont);
          } else {
            this.innerElem.appendChild(tSpan);
          }
        } else {
          tSpan.textContent = letters[i].val;
          tSpan.setAttributeNS("http://www.w3.org/XML/1998/namespace", "xml:space", "preserve");
          if (!this.isMasked) {
            this.innerElem.appendChild(tParent);
            var tStyle = tSpan.style;
            var tSpanTranslation = "translate3d(0," + -documentData.finalSize / 1.2 + "px,0)";
            tStyle.transform = tSpanTranslation;
            tStyle.webkitTransform = tSpanTranslation;
          } else {
            this.innerElem.appendChild(tSpan);
          }
        }
        if (!this.isMasked) {
          this.textSpans[cnt] = tParent;
        } else {
          this.textSpans[cnt] = tSpan;
        }
        this.textSpans[cnt].style.display = "block";
        this.textPaths[cnt] = tSpan;
        cnt += 1;
      }
      while (cnt < this.textSpans.length) {
        this.textSpans[cnt].style.display = "none";
        cnt += 1;
      }
    };
    HTextElement.prototype.renderInnerContent = function() {
      this.validateText();
      var svgStyle;
      if (this.data.singleShape) {
        if (!this._isFirstFrame && !this.lettersChangedFlag) {
          return;
        }
        if (this.isMasked && this.finalTransform._matMdf) {
          this.svgElement.setAttribute("viewBox", -this.finalTransform.mProp.p.v[0] + " " + -this.finalTransform.mProp.p.v[1] + " " + this.compW + " " + this.compH);
          svgStyle = this.svgElement.style;
          var translation = "translate(" + -this.finalTransform.mProp.p.v[0] + "px," + -this.finalTransform.mProp.p.v[1] + "px)";
          svgStyle.transform = translation;
          svgStyle.webkitTransform = translation;
        }
      }
      this.textAnimator.getMeasures(this.textProperty.currentData, this.lettersChangedFlag);
      if (!this.lettersChangedFlag && !this.textAnimator.lettersChangedFlag) {
        return;
      }
      var i;
      var len;
      var count = 0;
      var renderedLetters = this.textAnimator.renderedLetters;
      var letters = this.textProperty.currentData.l;
      len = letters.length;
      var renderedLetter;
      var textSpan;
      var textPath;
      for (i = 0; i < len; i += 1) {
        if (letters[i].n) {
          count += 1;
        } else {
          textSpan = this.textSpans[i];
          textPath = this.textPaths[i];
          renderedLetter = renderedLetters[count];
          count += 1;
          if (renderedLetter._mdf.m) {
            if (!this.isMasked) {
              textSpan.style.webkitTransform = renderedLetter.m;
              textSpan.style.transform = renderedLetter.m;
            } else {
              textSpan.setAttribute("transform", renderedLetter.m);
            }
          }
          textSpan.style.opacity = renderedLetter.o;
          if (renderedLetter.sw && renderedLetter._mdf.sw) {
            textPath.setAttribute("stroke-width", renderedLetter.sw);
          }
          if (renderedLetter.sc && renderedLetter._mdf.sc) {
            textPath.setAttribute("stroke", renderedLetter.sc);
          }
          if (renderedLetter.fc && renderedLetter._mdf.fc) {
            textPath.setAttribute("fill", renderedLetter.fc);
            textPath.style.color = renderedLetter.fc;
          }
        }
      }
      if (this.innerElem.getBBox && !this.hidden && (this._isFirstFrame || this._mdf)) {
        var boundingBox = this.innerElem.getBBox();
        if (this.currentBBox.w !== boundingBox.width) {
          this.currentBBox.w = boundingBox.width;
          this.svgElement.setAttribute("width", boundingBox.width);
        }
        if (this.currentBBox.h !== boundingBox.height) {
          this.currentBBox.h = boundingBox.height;
          this.svgElement.setAttribute("height", boundingBox.height);
        }
        var margin = 1;
        if (this.currentBBox.w !== boundingBox.width + margin * 2 || this.currentBBox.h !== boundingBox.height + margin * 2 || this.currentBBox.x !== boundingBox.x - margin || this.currentBBox.y !== boundingBox.y - margin) {
          this.currentBBox.w = boundingBox.width + margin * 2;
          this.currentBBox.h = boundingBox.height + margin * 2;
          this.currentBBox.x = boundingBox.x - margin;
          this.currentBBox.y = boundingBox.y - margin;
          this.svgElement.setAttribute("viewBox", this.currentBBox.x + " " + this.currentBBox.y + " " + this.currentBBox.w + " " + this.currentBBox.h);
          svgStyle = this.svgElement.style;
          var svgTransform = "translate(" + this.currentBBox.x + "px," + this.currentBBox.y + "px)";
          svgStyle.transform = svgTransform;
          svgStyle.webkitTransform = svgTransform;
        }
      }
    };
    function HCameraElement(data2, globalData2, comp2) {
      this.initFrame();
      this.initBaseData(data2, globalData2, comp2);
      this.initHierarchy();
      var getProp = PropertyFactory.getProp;
      this.pe = getProp(this, data2.pe, 0, 0, this);
      if (data2.ks.p.s) {
        this.px = getProp(this, data2.ks.p.x, 1, 0, this);
        this.py = getProp(this, data2.ks.p.y, 1, 0, this);
        this.pz = getProp(this, data2.ks.p.z, 1, 0, this);
      } else {
        this.p = getProp(this, data2.ks.p, 1, 0, this);
      }
      if (data2.ks.a) {
        this.a = getProp(this, data2.ks.a, 1, 0, this);
      }
      if (data2.ks.or.k.length && data2.ks.or.k[0].to) {
        var i;
        var len = data2.ks.or.k.length;
        for (i = 0; i < len; i += 1) {
          data2.ks.or.k[i].to = null;
          data2.ks.or.k[i].ti = null;
        }
      }
      this.or = getProp(this, data2.ks.or, 1, degToRads, this);
      this.or.sh = true;
      this.rx = getProp(this, data2.ks.rx, 0, degToRads, this);
      this.ry = getProp(this, data2.ks.ry, 0, degToRads, this);
      this.rz = getProp(this, data2.ks.rz, 0, degToRads, this);
      this.mat = new Matrix();
      this._prevMat = new Matrix();
      this._isFirstFrame = true;
      this.finalTransform = {
        mProp: this
      };
    }
    extendPrototype([BaseElement, FrameElement, HierarchyElement], HCameraElement);
    HCameraElement.prototype.setup = function() {
      var i;
      var len = this.comp.threeDElements.length;
      var comp2;
      var perspectiveStyle;
      var containerStyle;
      for (i = 0; i < len; i += 1) {
        comp2 = this.comp.threeDElements[i];
        if (comp2.type === "3d") {
          perspectiveStyle = comp2.perspectiveElem.style;
          containerStyle = comp2.container.style;
          var perspective = this.pe.v + "px";
          var origin = "0px 0px 0px";
          var matrix = "matrix3d(1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1)";
          perspectiveStyle.perspective = perspective;
          perspectiveStyle.webkitPerspective = perspective;
          containerStyle.transformOrigin = origin;
          containerStyle.mozTransformOrigin = origin;
          containerStyle.webkitTransformOrigin = origin;
          perspectiveStyle.transform = matrix;
          perspectiveStyle.webkitTransform = matrix;
        }
      }
    };
    HCameraElement.prototype.createElements = function() {
    };
    HCameraElement.prototype.hide = function() {
    };
    HCameraElement.prototype.renderFrame = function() {
      var _mdf = this._isFirstFrame;
      var i;
      var len;
      if (this.hierarchy) {
        len = this.hierarchy.length;
        for (i = 0; i < len; i += 1) {
          _mdf = this.hierarchy[i].finalTransform.mProp._mdf || _mdf;
        }
      }
      if (_mdf || this.pe._mdf || this.p && this.p._mdf || this.px && (this.px._mdf || this.py._mdf || this.pz._mdf) || this.rx._mdf || this.ry._mdf || this.rz._mdf || this.or._mdf || this.a && this.a._mdf) {
        this.mat.reset();
        if (this.hierarchy) {
          len = this.hierarchy.length - 1;
          for (i = len; i >= 0; i -= 1) {
            var mTransf = this.hierarchy[i].finalTransform.mProp;
            this.mat.translate(-mTransf.p.v[0], -mTransf.p.v[1], mTransf.p.v[2]);
            this.mat.rotateX(-mTransf.or.v[0]).rotateY(-mTransf.or.v[1]).rotateZ(mTransf.or.v[2]);
            this.mat.rotateX(-mTransf.rx.v).rotateY(-mTransf.ry.v).rotateZ(mTransf.rz.v);
            this.mat.scale(1 / mTransf.s.v[0], 1 / mTransf.s.v[1], 1 / mTransf.s.v[2]);
            this.mat.translate(mTransf.a.v[0], mTransf.a.v[1], mTransf.a.v[2]);
          }
        }
        if (this.p) {
          this.mat.translate(-this.p.v[0], -this.p.v[1], this.p.v[2]);
        } else {
          this.mat.translate(-this.px.v, -this.py.v, this.pz.v);
        }
        if (this.a) {
          var diffVector;
          if (this.p) {
            diffVector = [this.p.v[0] - this.a.v[0], this.p.v[1] - this.a.v[1], this.p.v[2] - this.a.v[2]];
          } else {
            diffVector = [this.px.v - this.a.v[0], this.py.v - this.a.v[1], this.pz.v - this.a.v[2]];
          }
          var mag = Math.sqrt(Math.pow(diffVector[0], 2) + Math.pow(diffVector[1], 2) + Math.pow(diffVector[2], 2));
          var lookDir = [diffVector[0] / mag, diffVector[1] / mag, diffVector[2] / mag];
          var lookLengthOnXZ = Math.sqrt(lookDir[2] * lookDir[2] + lookDir[0] * lookDir[0]);
          var mRotationX = Math.atan2(lookDir[1], lookLengthOnXZ);
          var mRotationY = Math.atan2(lookDir[0], -lookDir[2]);
          this.mat.rotateY(mRotationY).rotateX(-mRotationX);
        }
        this.mat.rotateX(-this.rx.v).rotateY(-this.ry.v).rotateZ(this.rz.v);
        this.mat.rotateX(-this.or.v[0]).rotateY(-this.or.v[1]).rotateZ(this.or.v[2]);
        this.mat.translate(this.globalData.compSize.w / 2, this.globalData.compSize.h / 2, 0);
        this.mat.translate(0, 0, this.pe.v);
        var hasMatrixChanged = !this._prevMat.equals(this.mat);
        if ((hasMatrixChanged || this.pe._mdf) && this.comp.threeDElements) {
          len = this.comp.threeDElements.length;
          var comp2;
          var perspectiveStyle;
          var containerStyle;
          for (i = 0; i < len; i += 1) {
            comp2 = this.comp.threeDElements[i];
            if (comp2.type === "3d") {
              if (hasMatrixChanged) {
                var matValue = this.mat.toCSS();
                containerStyle = comp2.container.style;
                containerStyle.transform = matValue;
                containerStyle.webkitTransform = matValue;
              }
              if (this.pe._mdf) {
                perspectiveStyle = comp2.perspectiveElem.style;
                perspectiveStyle.perspective = this.pe.v + "px";
                perspectiveStyle.webkitPerspective = this.pe.v + "px";
              }
            }
          }
          this.mat.clone(this._prevMat);
        }
      }
      this._isFirstFrame = false;
    };
    HCameraElement.prototype.prepareFrame = function(num) {
      this.prepareProperties(num, true);
    };
    HCameraElement.prototype.destroy = function() {
    };
    HCameraElement.prototype.getBaseElement = function() {
      return null;
    };
    function HImageElement(data2, globalData2, comp2) {
      this.assetData = globalData2.getAssetData(data2.refId);
      this.initElement(data2, globalData2, comp2);
    }
    extendPrototype([BaseElement, TransformElement, HBaseElement, HSolidElement, HierarchyElement, FrameElement, RenderableElement], HImageElement);
    HImageElement.prototype.createContent = function() {
      var assetPath = this.globalData.getAssetsPath(this.assetData);
      var img = new Image();
      if (this.data.hasMask) {
        this.imageElem = createNS("image");
        this.imageElem.setAttribute("width", this.assetData.w + "px");
        this.imageElem.setAttribute("height", this.assetData.h + "px");
        this.imageElem.setAttributeNS("http://www.w3.org/1999/xlink", "href", assetPath);
        this.layerElement.appendChild(this.imageElem);
        this.baseElement.setAttribute("width", this.assetData.w);
        this.baseElement.setAttribute("height", this.assetData.h);
      } else {
        this.layerElement.appendChild(img);
      }
      img.crossOrigin = "anonymous";
      img.src = assetPath;
      if (this.data.ln) {
        this.baseElement.setAttribute("id", this.data.ln);
      }
    };
    function HybridRendererBase(animationItem, config) {
      this.animationItem = animationItem;
      this.layers = null;
      this.renderedFrame = -1;
      this.renderConfig = {
        className: config && config.className || "",
        imagePreserveAspectRatio: config && config.imagePreserveAspectRatio || "xMidYMid slice",
        hideOnTransparent: !(config && config.hideOnTransparent === false),
        filterSize: {
          width: config && config.filterSize && config.filterSize.width || "400%",
          height: config && config.filterSize && config.filterSize.height || "400%",
          x: config && config.filterSize && config.filterSize.x || "-100%",
          y: config && config.filterSize && config.filterSize.y || "-100%"
        }
      };
      this.globalData = {
        _mdf: false,
        frameNum: -1,
        renderConfig: this.renderConfig
      };
      this.pendingElements = [];
      this.elements = [];
      this.threeDElements = [];
      this.destroyed = false;
      this.camera = null;
      this.supports3d = true;
      this.rendererType = "html";
    }
    extendPrototype([BaseRenderer], HybridRendererBase);
    HybridRendererBase.prototype.buildItem = SVGRenderer.prototype.buildItem;
    HybridRendererBase.prototype.checkPendingElements = function() {
      while (this.pendingElements.length) {
        var element = this.pendingElements.pop();
        element.checkParenting();
      }
    };
    HybridRendererBase.prototype.appendElementInPos = function(element, pos) {
      var newDOMElement = element.getBaseElement();
      if (!newDOMElement) {
        return;
      }
      var layer = this.layers[pos];
      if (!layer.ddd || !this.supports3d) {
        if (this.threeDElements) {
          this.addTo3dContainer(newDOMElement, pos);
        } else {
          var i = 0;
          var nextDOMElement;
          var nextLayer;
          var tmpDOMElement;
          while (i < pos) {
            if (this.elements[i] && this.elements[i] !== true && this.elements[i].getBaseElement) {
              nextLayer = this.elements[i];
              tmpDOMElement = this.layers[i].ddd ? this.getThreeDContainerByPos(i) : nextLayer.getBaseElement();
              nextDOMElement = tmpDOMElement || nextDOMElement;
            }
            i += 1;
          }
          if (nextDOMElement) {
            if (!layer.ddd || !this.supports3d) {
              this.layerElement.insertBefore(newDOMElement, nextDOMElement);
            }
          } else if (!layer.ddd || !this.supports3d) {
            this.layerElement.appendChild(newDOMElement);
          }
        }
      } else {
        this.addTo3dContainer(newDOMElement, pos);
      }
    };
    HybridRendererBase.prototype.createShape = function(data2) {
      if (!this.supports3d) {
        return new SVGShapeElement(data2, this.globalData, this);
      }
      return new HShapeElement(data2, this.globalData, this);
    };
    HybridRendererBase.prototype.createText = function(data2) {
      if (!this.supports3d) {
        return new SVGTextLottieElement(data2, this.globalData, this);
      }
      return new HTextElement(data2, this.globalData, this);
    };
    HybridRendererBase.prototype.createCamera = function(data2) {
      this.camera = new HCameraElement(data2, this.globalData, this);
      return this.camera;
    };
    HybridRendererBase.prototype.createImage = function(data2) {
      if (!this.supports3d) {
        return new IImageElement(data2, this.globalData, this);
      }
      return new HImageElement(data2, this.globalData, this);
    };
    HybridRendererBase.prototype.createSolid = function(data2) {
      if (!this.supports3d) {
        return new ISolidElement(data2, this.globalData, this);
      }
      return new HSolidElement(data2, this.globalData, this);
    };
    HybridRendererBase.prototype.createNull = SVGRenderer.prototype.createNull;
    HybridRendererBase.prototype.getThreeDContainerByPos = function(pos) {
      var i = 0;
      var len = this.threeDElements.length;
      while (i < len) {
        if (this.threeDElements[i].startPos <= pos && this.threeDElements[i].endPos >= pos) {
          return this.threeDElements[i].perspectiveElem;
        }
        i += 1;
      }
      return null;
    };
    HybridRendererBase.prototype.createThreeDContainer = function(pos, type) {
      var perspectiveElem = createTag("div");
      var style;
      var containerStyle;
      styleDiv(perspectiveElem);
      var container = createTag("div");
      styleDiv(container);
      if (type === "3d") {
        style = perspectiveElem.style;
        style.width = this.globalData.compSize.w + "px";
        style.height = this.globalData.compSize.h + "px";
        var center = "50% 50%";
        style.webkitTransformOrigin = center;
        style.mozTransformOrigin = center;
        style.transformOrigin = center;
        containerStyle = container.style;
        var matrix = "matrix3d(1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1)";
        containerStyle.transform = matrix;
        containerStyle.webkitTransform = matrix;
      }
      perspectiveElem.appendChild(container);
      var threeDContainerData = {
        container,
        perspectiveElem,
        startPos: pos,
        endPos: pos,
        type
      };
      this.threeDElements.push(threeDContainerData);
      return threeDContainerData;
    };
    HybridRendererBase.prototype.build3dContainers = function() {
      var i;
      var len = this.layers.length;
      var lastThreeDContainerData;
      var currentContainer = "";
      for (i = 0; i < len; i += 1) {
        if (this.layers[i].ddd && this.layers[i].ty !== 3) {
          if (currentContainer !== "3d") {
            currentContainer = "3d";
            lastThreeDContainerData = this.createThreeDContainer(i, "3d");
          }
          lastThreeDContainerData.endPos = Math.max(lastThreeDContainerData.endPos, i);
        } else {
          if (currentContainer !== "2d") {
            currentContainer = "2d";
            lastThreeDContainerData = this.createThreeDContainer(i, "2d");
          }
          lastThreeDContainerData.endPos = Math.max(lastThreeDContainerData.endPos, i);
        }
      }
      len = this.threeDElements.length;
      for (i = len - 1; i >= 0; i -= 1) {
        this.resizerElem.appendChild(this.threeDElements[i].perspectiveElem);
      }
    };
    HybridRendererBase.prototype.addTo3dContainer = function(elem2, pos) {
      var i = 0;
      var len = this.threeDElements.length;
      while (i < len) {
        if (pos <= this.threeDElements[i].endPos) {
          var j = this.threeDElements[i].startPos;
          var nextElement;
          while (j < pos) {
            if (this.elements[j] && this.elements[j].getBaseElement) {
              nextElement = this.elements[j].getBaseElement();
            }
            j += 1;
          }
          if (nextElement) {
            this.threeDElements[i].container.insertBefore(elem2, nextElement);
          } else {
            this.threeDElements[i].container.appendChild(elem2);
          }
          break;
        }
        i += 1;
      }
    };
    HybridRendererBase.prototype.configAnimation = function(animData) {
      var resizerElem = createTag("div");
      var wrapper = this.animationItem.wrapper;
      var style = resizerElem.style;
      style.width = animData.w + "px";
      style.height = animData.h + "px";
      this.resizerElem = resizerElem;
      styleDiv(resizerElem);
      style.transformStyle = "flat";
      style.mozTransformStyle = "flat";
      style.webkitTransformStyle = "flat";
      if (this.renderConfig.className) {
        resizerElem.setAttribute("class", this.renderConfig.className);
      }
      wrapper.appendChild(resizerElem);
      style.overflow = "hidden";
      var svg = createNS("svg");
      svg.setAttribute("width", "1");
      svg.setAttribute("height", "1");
      styleDiv(svg);
      this.resizerElem.appendChild(svg);
      var defs = createNS("defs");
      svg.appendChild(defs);
      this.data = animData;
      this.setupGlobalData(animData, svg);
      this.globalData.defs = defs;
      this.layers = animData.layers;
      this.layerElement = this.resizerElem;
      this.build3dContainers();
      this.updateContainerSize();
    };
    HybridRendererBase.prototype.destroy = function() {
      if (this.animationItem.wrapper) {
        this.animationItem.wrapper.innerText = "";
      }
      this.animationItem.container = null;
      this.globalData.defs = null;
      var i;
      var len = this.layers ? this.layers.length : 0;
      for (i = 0; i < len; i += 1) {
        if (this.elements[i] && this.elements[i].destroy) {
          this.elements[i].destroy();
        }
      }
      this.elements.length = 0;
      this.destroyed = true;
      this.animationItem = null;
    };
    HybridRendererBase.prototype.updateContainerSize = function() {
      var elementWidth = this.animationItem.wrapper.offsetWidth;
      var elementHeight = this.animationItem.wrapper.offsetHeight;
      var elementRel = elementWidth / elementHeight;
      var animationRel = this.globalData.compSize.w / this.globalData.compSize.h;
      var sx;
      var sy;
      var tx;
      var ty;
      if (animationRel > elementRel) {
        sx = elementWidth / this.globalData.compSize.w;
        sy = elementWidth / this.globalData.compSize.w;
        tx = 0;
        ty = (elementHeight - this.globalData.compSize.h * (elementWidth / this.globalData.compSize.w)) / 2;
      } else {
        sx = elementHeight / this.globalData.compSize.h;
        sy = elementHeight / this.globalData.compSize.h;
        tx = (elementWidth - this.globalData.compSize.w * (elementHeight / this.globalData.compSize.h)) / 2;
        ty = 0;
      }
      var style = this.resizerElem.style;
      style.webkitTransform = "matrix3d(" + sx + ",0,0,0,0," + sy + ",0,0,0,0,1,0," + tx + "," + ty + ",0,1)";
      style.transform = style.webkitTransform;
    };
    HybridRendererBase.prototype.renderFrame = SVGRenderer.prototype.renderFrame;
    HybridRendererBase.prototype.hide = function() {
      this.resizerElem.style.display = "none";
    };
    HybridRendererBase.prototype.show = function() {
      this.resizerElem.style.display = "block";
    };
    HybridRendererBase.prototype.initItems = function() {
      this.buildAllItems();
      if (this.camera) {
        this.camera.setup();
      } else {
        var cWidth = this.globalData.compSize.w;
        var cHeight = this.globalData.compSize.h;
        var i;
        var len = this.threeDElements.length;
        for (i = 0; i < len; i += 1) {
          var style = this.threeDElements[i].perspectiveElem.style;
          style.webkitPerspective = Math.sqrt(Math.pow(cWidth, 2) + Math.pow(cHeight, 2)) + "px";
          style.perspective = style.webkitPerspective;
        }
      }
    };
    HybridRendererBase.prototype.searchExtraCompositions = function(assets2) {
      var i;
      var len = assets2.length;
      var floatingContainer = createTag("div");
      for (i = 0; i < len; i += 1) {
        if (assets2[i].xt) {
          var comp2 = this.createComp(assets2[i], floatingContainer, this.globalData.comp, null);
          comp2.initExpressions();
          this.globalData.projectInterface.registerComposition(comp2);
        }
      }
    };
    function HCompElement(data2, globalData2, comp2) {
      this.layers = data2.layers;
      this.supports3d = !data2.hasMask;
      this.completeLayers = false;
      this.pendingElements = [];
      this.elements = this.layers ? createSizedArray(this.layers.length) : [];
      this.initElement(data2, globalData2, comp2);
      this.tm = data2.tm ? PropertyFactory.getProp(this, data2.tm, 0, globalData2.frameRate, this) : {
        _placeholder: true
      };
    }
    extendPrototype([HybridRendererBase, ICompElement, HBaseElement], HCompElement);
    HCompElement.prototype._createBaseContainerElements = HCompElement.prototype.createContainerElements;
    HCompElement.prototype.createContainerElements = function() {
      this._createBaseContainerElements();
      if (this.data.hasMask) {
        this.svgElement.setAttribute("width", this.data.w);
        this.svgElement.setAttribute("height", this.data.h);
        this.transformedElement = this.baseElement;
      } else {
        this.transformedElement = this.layerElement;
      }
    };
    HCompElement.prototype.addTo3dContainer = function(elem2, pos) {
      var j = 0;
      var nextElement;
      while (j < pos) {
        if (this.elements[j] && this.elements[j].getBaseElement) {
          nextElement = this.elements[j].getBaseElement();
        }
        j += 1;
      }
      if (nextElement) {
        this.layerElement.insertBefore(elem2, nextElement);
      } else {
        this.layerElement.appendChild(elem2);
      }
    };
    HCompElement.prototype.createComp = function(data2) {
      if (!this.supports3d) {
        return new SVGCompElement(data2, this.globalData, this);
      }
      return new HCompElement(data2, this.globalData, this);
    };
    function HybridRenderer(animationItem, config) {
      this.animationItem = animationItem;
      this.layers = null;
      this.renderedFrame = -1;
      this.renderConfig = {
        className: config && config.className || "",
        imagePreserveAspectRatio: config && config.imagePreserveAspectRatio || "xMidYMid slice",
        hideOnTransparent: !(config && config.hideOnTransparent === false),
        filterSize: {
          width: config && config.filterSize && config.filterSize.width || "400%",
          height: config && config.filterSize && config.filterSize.height || "400%",
          x: config && config.filterSize && config.filterSize.x || "-100%",
          y: config && config.filterSize && config.filterSize.y || "-100%"
        },
        runExpressions: !config || config.runExpressions === void 0 || config.runExpressions
      };
      this.globalData = {
        _mdf: false,
        frameNum: -1,
        renderConfig: this.renderConfig
      };
      this.pendingElements = [];
      this.elements = [];
      this.threeDElements = [];
      this.destroyed = false;
      this.camera = null;
      this.supports3d = true;
      this.rendererType = "html";
    }
    extendPrototype([HybridRendererBase], HybridRenderer);
    HybridRenderer.prototype.createComp = function(data2) {
      if (!this.supports3d) {
        return new SVGCompElement(data2, this.globalData, this);
      }
      return new HCompElement(data2, this.globalData, this);
    };
    var CompExpressionInterface = /* @__PURE__ */ function() {
      return function(comp2) {
        function _thisLayerFunction(name2) {
          var i = 0;
          var len = comp2.layers.length;
          while (i < len) {
            if (comp2.layers[i].nm === name2 || comp2.layers[i].ind === name2) {
              return comp2.elements[i].layerInterface;
            }
            i += 1;
          }
          return null;
        }
        Object.defineProperty(_thisLayerFunction, "_name", {
          value: comp2.data.nm
        });
        _thisLayerFunction.layer = _thisLayerFunction;
        _thisLayerFunction.pixelAspect = 1;
        _thisLayerFunction.height = comp2.data.h || comp2.globalData.compSize.h;
        _thisLayerFunction.width = comp2.data.w || comp2.globalData.compSize.w;
        _thisLayerFunction.pixelAspect = 1;
        _thisLayerFunction.frameDuration = 1 / comp2.globalData.frameRate;
        _thisLayerFunction.displayStartTime = 0;
        _thisLayerFunction.numLayers = comp2.layers.length;
        return _thisLayerFunction;
      };
    }();
    function _typeof$2(obj) {
      "@babel/helpers - typeof";
      if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
        _typeof$2 = function _typeof2(obj2) {
          return typeof obj2;
        };
      } else {
        _typeof$2 = function _typeof2(obj2) {
          return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
        };
      }
      return _typeof$2(obj);
    }
    function seedRandom(pool, math) {
      var global = this, width2 = 256, chunks = 6, digits = 52, rngname = "random", startdenom = math.pow(width2, chunks), significance = math.pow(2, digits), overflow = significance * 2, mask2 = width2 - 1, nodecrypto;
      function seedrandom(seed, options, callback) {
        var key2 = [];
        options = options === true ? {
          entropy: true
        } : options || {};
        var shortseed = mixkey(flatten(options.entropy ? [seed, tostring(pool)] : seed === null ? autoseed() : seed, 3), key2);
        var arc4 = new ARC4(key2);
        var prng = function prng2() {
          var n = arc4.g(chunks), d = startdenom, x = 0;
          while (n < significance) {
            n = (n + x) * width2;
            d *= width2;
            x = arc4.g(1);
          }
          while (n >= overflow) {
            n /= 2;
            d /= 2;
            x >>>= 1;
          }
          return (n + x) / d;
        };
        prng.int32 = function() {
          return arc4.g(4) | 0;
        };
        prng.quick = function() {
          return arc4.g(4) / 4294967296;
        };
        prng["double"] = prng;
        mixkey(tostring(arc4.S), pool);
        return (options.pass || callback || function(prng2, seed2, is_math_call, state) {
          if (state) {
            if (state.S) {
              copy(state, arc4);
            }
            prng2.state = function() {
              return copy(arc4, {});
            };
          }
          if (is_math_call) {
            math[rngname] = prng2;
            return seed2;
          } else return prng2;
        })(prng, shortseed, "global" in options ? options.global : this == math, options.state);
      }
      math["seed" + rngname] = seedrandom;
      function ARC4(key2) {
        var t, keylen = key2.length, me = this, i = 0, j = me.i = me.j = 0, s = me.S = [];
        if (!keylen) {
          key2 = [keylen++];
        }
        while (i < width2) {
          s[i] = i++;
        }
        for (i = 0; i < width2; i++) {
          s[i] = s[j = mask2 & j + key2[i % keylen] + (t = s[i])];
          s[j] = t;
        }
        me.g = function(count) {
          var t2, r = 0, i2 = me.i, j2 = me.j, s2 = me.S;
          while (count--) {
            t2 = s2[i2 = mask2 & i2 + 1];
            r = r * width2 + s2[mask2 & (s2[i2] = s2[j2 = mask2 & j2 + t2]) + (s2[j2] = t2)];
          }
          me.i = i2;
          me.j = j2;
          return r;
        };
      }
      function copy(f, t) {
        t.i = f.i;
        t.j = f.j;
        t.S = f.S.slice();
        return t;
      }
      function flatten(obj, depth) {
        var result = [], typ = _typeof$2(obj), prop;
        if (depth && typ == "object") {
          for (prop in obj) {
            try {
              result.push(flatten(obj[prop], depth - 1));
            } catch (e) {
            }
          }
        }
        return result.length ? result : typ == "string" ? obj : obj + "\0";
      }
      function mixkey(seed, key2) {
        var stringseed = seed + "", smear, j = 0;
        while (j < stringseed.length) {
          key2[mask2 & j] = mask2 & (smear ^= key2[mask2 & j] * 19) + stringseed.charCodeAt(j++);
        }
        return tostring(key2);
      }
      function autoseed() {
        try {
          if (nodecrypto) ;
          var out = new Uint8Array(width2);
          (global.crypto || global.msCrypto).getRandomValues(out);
          return tostring(out);
        } catch (e) {
          var browser = global.navigator, plugins = browser && browser.plugins;
          return [+/* @__PURE__ */ new Date(), global, plugins, global.screen, tostring(pool)];
        }
      }
      function tostring(a) {
        return String.fromCharCode.apply(0, a);
      }
      mixkey(math.random(), pool);
    }
    function initialize$2(BMMath2) {
      seedRandom([], BMMath2);
    }
    var propTypes = {
      SHAPE: "shape"
    };
    function _typeof$1(obj) {
      "@babel/helpers - typeof";
      if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
        _typeof$1 = function _typeof2(obj2) {
          return typeof obj2;
        };
      } else {
        _typeof$1 = function _typeof2(obj2) {
          return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
        };
      }
      return _typeof$1(obj);
    }
    var ExpressionManager = function() {
      var ob = {};
      var Math = BMMath;
      var window = null;
      var document = null;
      var XMLHttpRequest = null;
      var fetch = null;
      var frames = null;
      var _lottieGlobal = {};
      initialize$2(BMMath);
      function resetFrame() {
        _lottieGlobal = {};
      }
      function $bm_isInstanceOfArray(arr) {
        return arr.constructor === Array || arr.constructor === Float32Array;
      }
      function isNumerable(tOfV, v2) {
        return tOfV === "number" || v2 instanceof Number || tOfV === "boolean" || tOfV === "string";
      }
      function $bm_neg(a) {
        var tOfA = _typeof$1(a);
        if (tOfA === "number" || a instanceof Number || tOfA === "boolean") {
          return -a;
        }
        if ($bm_isInstanceOfArray(a)) {
          var i;
          var lenA = a.length;
          var retArr = [];
          for (i = 0; i < lenA; i += 1) {
            retArr[i] = -a[i];
          }
          return retArr;
        }
        if (a.propType) {
          return a.v;
        }
        return -a;
      }
      var easeInBez = BezierFactory.getBezierEasing(0.333, 0, 0.833, 0.833, "easeIn").get;
      var easeOutBez = BezierFactory.getBezierEasing(0.167, 0.167, 0.667, 1, "easeOut").get;
      var easeInOutBez = BezierFactory.getBezierEasing(0.33, 0, 0.667, 1, "easeInOut").get;
      function sum(a, b) {
        var tOfA = _typeof$1(a);
        var tOfB = _typeof$1(b);
        if (isNumerable(tOfA, a) && isNumerable(tOfB, b) || tOfA === "string" || tOfB === "string") {
          return a + b;
        }
        if ($bm_isInstanceOfArray(a) && isNumerable(tOfB, b)) {
          a = a.slice(0);
          a[0] += b;
          return a;
        }
        if (isNumerable(tOfA, a) && $bm_isInstanceOfArray(b)) {
          b = b.slice(0);
          b[0] = a + b[0];
          return b;
        }
        if ($bm_isInstanceOfArray(a) && $bm_isInstanceOfArray(b)) {
          var i = 0;
          var lenA = a.length;
          var lenB = b.length;
          var retArr = [];
          while (i < lenA || i < lenB) {
            if ((typeof a[i] === "number" || a[i] instanceof Number) && (typeof b[i] === "number" || b[i] instanceof Number)) {
              retArr[i] = a[i] + b[i];
            } else {
              retArr[i] = b[i] === void 0 ? a[i] : a[i] || b[i];
            }
            i += 1;
          }
          return retArr;
        }
        return 0;
      }
      var add = sum;
      function sub(a, b) {
        var tOfA = _typeof$1(a);
        var tOfB = _typeof$1(b);
        if (isNumerable(tOfA, a) && isNumerable(tOfB, b)) {
          if (tOfA === "string") {
            a = parseInt(a, 10);
          }
          if (tOfB === "string") {
            b = parseInt(b, 10);
          }
          return a - b;
        }
        if ($bm_isInstanceOfArray(a) && isNumerable(tOfB, b)) {
          a = a.slice(0);
          a[0] -= b;
          return a;
        }
        if (isNumerable(tOfA, a) && $bm_isInstanceOfArray(b)) {
          b = b.slice(0);
          b[0] = a - b[0];
          return b;
        }
        if ($bm_isInstanceOfArray(a) && $bm_isInstanceOfArray(b)) {
          var i = 0;
          var lenA = a.length;
          var lenB = b.length;
          var retArr = [];
          while (i < lenA || i < lenB) {
            if ((typeof a[i] === "number" || a[i] instanceof Number) && (typeof b[i] === "number" || b[i] instanceof Number)) {
              retArr[i] = a[i] - b[i];
            } else {
              retArr[i] = b[i] === void 0 ? a[i] : a[i] || b[i];
            }
            i += 1;
          }
          return retArr;
        }
        return 0;
      }
      function mul(a, b) {
        var tOfA = _typeof$1(a);
        var tOfB = _typeof$1(b);
        var arr;
        if (isNumerable(tOfA, a) && isNumerable(tOfB, b)) {
          return a * b;
        }
        var i;
        var len;
        if ($bm_isInstanceOfArray(a) && isNumerable(tOfB, b)) {
          len = a.length;
          arr = createTypedArray("float32", len);
          for (i = 0; i < len; i += 1) {
            arr[i] = a[i] * b;
          }
          return arr;
        }
        if (isNumerable(tOfA, a) && $bm_isInstanceOfArray(b)) {
          len = b.length;
          arr = createTypedArray("float32", len);
          for (i = 0; i < len; i += 1) {
            arr[i] = a * b[i];
          }
          return arr;
        }
        return 0;
      }
      function div(a, b) {
        var tOfA = _typeof$1(a);
        var tOfB = _typeof$1(b);
        var arr;
        if (isNumerable(tOfA, a) && isNumerable(tOfB, b)) {
          return a / b;
        }
        var i;
        var len;
        if ($bm_isInstanceOfArray(a) && isNumerable(tOfB, b)) {
          len = a.length;
          arr = createTypedArray("float32", len);
          for (i = 0; i < len; i += 1) {
            arr[i] = a[i] / b;
          }
          return arr;
        }
        if (isNumerable(tOfA, a) && $bm_isInstanceOfArray(b)) {
          len = b.length;
          arr = createTypedArray("float32", len);
          for (i = 0; i < len; i += 1) {
            arr[i] = a / b[i];
          }
          return arr;
        }
        return 0;
      }
      function mod(a, b) {
        if (typeof a === "string") {
          a = parseInt(a, 10);
        }
        if (typeof b === "string") {
          b = parseInt(b, 10);
        }
        return a % b;
      }
      var $bm_sum = sum;
      var $bm_sub = sub;
      var $bm_mul = mul;
      var $bm_div = div;
      var $bm_mod = mod;
      function clamp(num, min, max) {
        if (min > max) {
          var mm = max;
          max = min;
          min = mm;
        }
        return Math.min(Math.max(num, min), max);
      }
      function radiansToDegrees(val2) {
        return val2 / degToRads;
      }
      var radians_to_degrees = radiansToDegrees;
      function degreesToRadians(val2) {
        return val2 * degToRads;
      }
      var degrees_to_radians = radiansToDegrees;
      var helperLengthArray = [0, 0, 0, 0, 0, 0];
      function length(arr1, arr2) {
        if (typeof arr1 === "number" || arr1 instanceof Number) {
          arr2 = arr2 || 0;
          return Math.abs(arr1 - arr2);
        }
        if (!arr2) {
          arr2 = helperLengthArray;
        }
        var i;
        var len = Math.min(arr1.length, arr2.length);
        var addedLength = 0;
        for (i = 0; i < len; i += 1) {
          addedLength += Math.pow(arr2[i] - arr1[i], 2);
        }
        return Math.sqrt(addedLength);
      }
      function normalize(vec) {
        return div(vec, length(vec));
      }
      function rgbToHsl(val2) {
        var r = val2[0];
        var g = val2[1];
        var b = val2[2];
        var max = Math.max(r, g, b);
        var min = Math.min(r, g, b);
        var h2;
        var s;
        var l = (max + min) / 2;
        if (max === min) {
          h2 = 0;
          s = 0;
        } else {
          var d = max - min;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          switch (max) {
            case r:
              h2 = (g - b) / d + (g < b ? 6 : 0);
              break;
            case g:
              h2 = (b - r) / d + 2;
              break;
            case b:
              h2 = (r - g) / d + 4;
              break;
          }
          h2 /= 6;
        }
        return [h2, s, l, val2[3]];
      }
      function hue2rgb(p, q, t) {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      }
      function hslToRgb(val2) {
        var h2 = val2[0];
        var s = val2[1];
        var l = val2[2];
        var r;
        var g;
        var b;
        if (s === 0) {
          r = l;
          b = l;
          g = l;
        } else {
          var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
          var p = 2 * l - q;
          r = hue2rgb(p, q, h2 + 1 / 3);
          g = hue2rgb(p, q, h2);
          b = hue2rgb(p, q, h2 - 1 / 3);
        }
        return [r, g, b, val2[3]];
      }
      function linear(t, tMin, tMax, value1, value2) {
        if (value1 === void 0 || value2 === void 0) {
          value1 = tMin;
          value2 = tMax;
          tMin = 0;
          tMax = 1;
        }
        if (tMax < tMin) {
          var _tMin = tMax;
          tMax = tMin;
          tMin = _tMin;
        }
        if (t <= tMin) {
          return value1;
        }
        if (t >= tMax) {
          return value2;
        }
        var perc = tMax === tMin ? 0 : (t - tMin) / (tMax - tMin);
        if (!value1.length) {
          return value1 + (value2 - value1) * perc;
        }
        var i;
        var len = value1.length;
        var arr = createTypedArray("float32", len);
        for (i = 0; i < len; i += 1) {
          arr[i] = value1[i] + (value2[i] - value1[i]) * perc;
        }
        return arr;
      }
      function random(min, max) {
        if (max === void 0) {
          if (min === void 0) {
            min = 0;
            max = 1;
          } else {
            max = min;
            min = void 0;
          }
        }
        if (max.length) {
          var i;
          var len = max.length;
          if (!min) {
            min = createTypedArray("float32", len);
          }
          var arr = createTypedArray("float32", len);
          var rnd = BMMath.random();
          for (i = 0; i < len; i += 1) {
            arr[i] = min[i] + rnd * (max[i] - min[i]);
          }
          return arr;
        }
        if (min === void 0) {
          min = 0;
        }
        var rndm = BMMath.random();
        return min + rndm * (max - min);
      }
      function createPath(points, inTangents, outTangents, closed) {
        var i;
        var len = points.length;
        var path = shapePool.newElement();
        path.setPathData(!!closed, len);
        var arrPlaceholder = [0, 0];
        var inVertexPoint;
        var outVertexPoint;
        for (i = 0; i < len; i += 1) {
          inVertexPoint = inTangents && inTangents[i] ? inTangents[i] : arrPlaceholder;
          outVertexPoint = outTangents && outTangents[i] ? outTangents[i] : arrPlaceholder;
          path.setTripleAt(points[i][0], points[i][1], outVertexPoint[0] + points[i][0], outVertexPoint[1] + points[i][1], inVertexPoint[0] + points[i][0], inVertexPoint[1] + points[i][1], i, true);
        }
        return path;
      }
      function initiateExpression(elem, data, property) {
        function noOp(_value) {
          return _value;
        }
        if (!elem.globalData.renderConfig.runExpressions) {
          return noOp;
        }
        var val = data.x;
        var needsVelocity = /velocity(?![\w\d])/.test(val);
        var _needsRandom = val.indexOf("random") !== -1;
        var elemType = elem.data.ty;
        var transform;
        var $bm_transform;
        var content;
        var effect;
        var thisProperty = property;
        thisProperty.valueAtTime = thisProperty.getValueAtTime;
        Object.defineProperty(thisProperty, "value", {
          get: function get() {
            return thisProperty.v;
          }
        });
        elem.comp.frameDuration = 1 / elem.comp.globalData.frameRate;
        elem.comp.displayStartTime = 0;
        var inPoint = elem.data.ip / elem.comp.globalData.frameRate;
        var outPoint = elem.data.op / elem.comp.globalData.frameRate;
        var width = elem.data.sw ? elem.data.sw : 0;
        var height = elem.data.sh ? elem.data.sh : 0;
        var name = elem.data.nm;
        var loopIn;
        var loop_in;
        var loopOut;
        var loop_out;
        var smooth;
        var toWorld;
        var fromWorld;
        var fromComp;
        var toComp;
        var fromCompToSurface;
        var position;
        var rotation;
        var anchorPoint;
        var scale;
        var thisLayer;
        var thisComp;
        var mask;
        var valueAtTime;
        var velocityAtTime;
        var scoped_bm_rt;
        var expression_function = eval("[function _expression_function(){" + val + ";scoped_bm_rt=$bm_rt}]")[0];
        var numKeys = property.kf ? data.k.length : 0;
        var active = !this.data || this.data.hd !== true;
        var wiggle = (function wiggle2(freq, amp) {
          var iWiggle;
          var j;
          var lenWiggle = this.pv.length ? this.pv.length : 1;
          var addedAmps = createTypedArray("float32", lenWiggle);
          freq = 5;
          var iterations = Math.floor(time * freq);
          iWiggle = 0;
          j = 0;
          while (iWiggle < iterations) {
            for (j = 0; j < lenWiggle; j += 1) {
              addedAmps[j] += -amp + amp * 2 * BMMath.random();
            }
            iWiggle += 1;
          }
          var periods = time * freq;
          var perc = periods - Math.floor(periods);
          var arr = createTypedArray("float32", lenWiggle);
          if (lenWiggle > 1) {
            for (j = 0; j < lenWiggle; j += 1) {
              arr[j] = this.pv[j] + addedAmps[j] + (-amp + amp * 2 * BMMath.random()) * perc;
            }
            return arr;
          }
          return this.pv + addedAmps[0] + (-amp + amp * 2 * BMMath.random()) * perc;
        }).bind(this);
        if (thisProperty.loopIn) {
          loopIn = thisProperty.loopIn.bind(thisProperty);
          loop_in = loopIn;
        }
        if (thisProperty.loopOut) {
          loopOut = thisProperty.loopOut.bind(thisProperty);
          loop_out = loopOut;
        }
        if (thisProperty.smooth) {
          smooth = thisProperty.smooth.bind(thisProperty);
        }
        function loopInDuration(type, duration) {
          return loopIn(type, duration, true);
        }
        function loopOutDuration(type, duration) {
          return loopOut(type, duration, true);
        }
        if (this.getValueAtTime) {
          valueAtTime = this.getValueAtTime.bind(this);
        }
        if (this.getVelocityAtTime) {
          velocityAtTime = this.getVelocityAtTime.bind(this);
        }
        var comp = elem.comp.globalData.projectInterface.bind(elem.comp.globalData.projectInterface);
        function lookAt(elem1, elem2) {
          var fVec = [elem2[0] - elem1[0], elem2[1] - elem1[1], elem2[2] - elem1[2]];
          var pitch = Math.atan2(fVec[0], Math.sqrt(fVec[1] * fVec[1] + fVec[2] * fVec[2])) / degToRads;
          var yaw = -Math.atan2(fVec[1], fVec[2]) / degToRads;
          return [yaw, pitch, 0];
        }
        function easeOut(t, tMin, tMax, val1, val2) {
          return applyEase(easeOutBez, t, tMin, tMax, val1, val2);
        }
        function easeIn(t, tMin, tMax, val1, val2) {
          return applyEase(easeInBez, t, tMin, tMax, val1, val2);
        }
        function ease(t, tMin, tMax, val1, val2) {
          return applyEase(easeInOutBez, t, tMin, tMax, val1, val2);
        }
        function applyEase(fn, t, tMin, tMax, val1, val2) {
          if (val1 === void 0) {
            val1 = tMin;
            val2 = tMax;
          } else {
            t = (t - tMin) / (tMax - tMin);
          }
          if (t > 1) {
            t = 1;
          } else if (t < 0) {
            t = 0;
          }
          var mult = fn(t);
          if ($bm_isInstanceOfArray(val1)) {
            var iKey;
            var lenKey = val1.length;
            var arr = createTypedArray("float32", lenKey);
            for (iKey = 0; iKey < lenKey; iKey += 1) {
              arr[iKey] = (val2[iKey] - val1[iKey]) * mult + val1[iKey];
            }
            return arr;
          }
          return (val2 - val1) * mult + val1;
        }
        function nearestKey(time2) {
          var iKey;
          var lenKey = data.k.length;
          var index2;
          var keyTime;
          if (!data.k.length || typeof data.k[0] === "number") {
            index2 = 0;
            keyTime = 0;
          } else {
            index2 = -1;
            time2 *= elem.comp.globalData.frameRate;
            if (time2 < data.k[0].t) {
              index2 = 1;
              keyTime = data.k[0].t;
            } else {
              for (iKey = 0; iKey < lenKey - 1; iKey += 1) {
                if (time2 === data.k[iKey].t) {
                  index2 = iKey + 1;
                  keyTime = data.k[iKey].t;
                  break;
                } else if (time2 > data.k[iKey].t && time2 < data.k[iKey + 1].t) {
                  if (time2 - data.k[iKey].t > data.k[iKey + 1].t - time2) {
                    index2 = iKey + 2;
                    keyTime = data.k[iKey + 1].t;
                  } else {
                    index2 = iKey + 1;
                    keyTime = data.k[iKey].t;
                  }
                  break;
                }
              }
              if (index2 === -1) {
                index2 = iKey + 1;
                keyTime = data.k[iKey].t;
              }
            }
          }
          var obKey = {};
          obKey.index = index2;
          obKey.time = keyTime / elem.comp.globalData.frameRate;
          return obKey;
        }
        function key(ind) {
          var obKey;
          var iKey;
          var lenKey;
          if (!data.k.length || typeof data.k[0] === "number") {
            throw new Error("The property has no keyframe at index " + ind);
          }
          ind -= 1;
          obKey = {
            time: data.k[ind].t / elem.comp.globalData.frameRate,
            value: []
          };
          var arr = Object.prototype.hasOwnProperty.call(data.k[ind], "s") ? data.k[ind].s : data.k[ind - 1].e;
          lenKey = arr.length;
          for (iKey = 0; iKey < lenKey; iKey += 1) {
            obKey[iKey] = arr[iKey];
            obKey.value[iKey] = arr[iKey];
          }
          return obKey;
        }
        function framesToTime(fr2, fps) {
          if (!fps) {
            fps = elem.comp.globalData.frameRate;
          }
          return fr2 / fps;
        }
        function timeToFrames(t, fps) {
          if (!t && t !== 0) {
            t = time;
          }
          if (!fps) {
            fps = elem.comp.globalData.frameRate;
          }
          return t * fps;
        }
        function seedRandom(seed) {
          BMMath.seedrandom(randSeed + seed);
        }
        function sourceRectAtTime() {
          return elem.sourceRectAtTime();
        }
        function substring(init, end) {
          if (typeof value === "string") {
            if (end === void 0) {
              return value.substring(init);
            }
            return value.substring(init, end);
          }
          return "";
        }
        function substr(init, end) {
          if (typeof value === "string") {
            if (end === void 0) {
              return value.substr(init);
            }
            return value.substr(init, end);
          }
          return "";
        }
        function posterizeTime(framesPerSecond) {
          time = framesPerSecond === 0 ? 0 : Math.floor(time * framesPerSecond) / framesPerSecond;
          value = valueAtTime(time);
        }
        var time;
        var velocity;
        var value;
        var text;
        var textIndex;
        var textTotal;
        var selectorValue;
        var index = elem.data.ind;
        var hasParent = !!(elem.hierarchy && elem.hierarchy.length);
        var parent;
        var randSeed = Math.floor(Math.random() * 1e6);
        var globalData = elem.globalData;
        function executeExpression(_value) {
          value = _value;
          if (this.frameExpressionId === elem.globalData.frameId && this.propType !== "textSelector") {
            return value;
          }
          if (this.propType === "textSelector") {
            textIndex = this.textIndex;
            textTotal = this.textTotal;
            selectorValue = this.selectorValue;
          }
          if (!thisLayer) {
            text = elem.layerInterface.text;
            thisLayer = elem.layerInterface;
            thisComp = elem.comp.compInterface;
            toWorld = thisLayer.toWorld.bind(thisLayer);
            fromWorld = thisLayer.fromWorld.bind(thisLayer);
            fromComp = thisLayer.fromComp.bind(thisLayer);
            toComp = thisLayer.toComp.bind(thisLayer);
            mask = thisLayer.mask ? thisLayer.mask.bind(thisLayer) : null;
            fromCompToSurface = fromComp;
          }
          if (!transform) {
            transform = elem.layerInterface("ADBE Transform Group");
            $bm_transform = transform;
            if (transform) {
              anchorPoint = transform.anchorPoint;
            }
          }
          if (elemType === 4 && !content) {
            content = thisLayer("ADBE Root Vectors Group");
          }
          if (!effect) {
            effect = thisLayer(4);
          }
          hasParent = !!(elem.hierarchy && elem.hierarchy.length);
          if (hasParent && !parent) {
            parent = elem.hierarchy[0].layerInterface;
          }
          time = this.comp.renderedFrame / this.comp.globalData.frameRate;
          if (_needsRandom) {
            seedRandom(randSeed + time);
          }
          if (needsVelocity) {
            velocity = velocityAtTime(time);
          }
          expression_function();
          this.frameExpressionId = elem.globalData.frameId;
          scoped_bm_rt = scoped_bm_rt.propType === propTypes.SHAPE ? scoped_bm_rt.v : scoped_bm_rt;
          return scoped_bm_rt;
        }
        executeExpression.__preventDeadCodeRemoval = [$bm_transform, anchorPoint, time, velocity, inPoint, outPoint, width, height, name, loop_in, loop_out, smooth, toComp, fromCompToSurface, toWorld, fromWorld, mask, position, rotation, scale, thisComp, numKeys, active, wiggle, loopInDuration, loopOutDuration, comp, lookAt, easeOut, easeIn, ease, nearestKey, key, text, textIndex, textTotal, selectorValue, framesToTime, timeToFrames, sourceRectAtTime, substring, substr, posterizeTime, index, globalData];
        return executeExpression;
      }
      ob.initiateExpression = initiateExpression;
      ob.__preventDeadCodeRemoval = [window, document, XMLHttpRequest, fetch, frames, $bm_neg, add, $bm_sum, $bm_sub, $bm_mul, $bm_div, $bm_mod, clamp, radians_to_degrees, degreesToRadians, degrees_to_radians, normalize, rgbToHsl, hslToRgb, linear, random, createPath, _lottieGlobal];
      ob.resetFrame = resetFrame;
      return ob;
    }();
    var Expressions = function() {
      var ob2 = {};
      ob2.initExpressions = initExpressions;
      ob2.resetFrame = ExpressionManager.resetFrame;
      function initExpressions(animation) {
        var stackCount = 0;
        var registers = [];
        function pushExpression() {
          stackCount += 1;
        }
        function popExpression() {
          stackCount -= 1;
          if (stackCount === 0) {
            releaseInstances();
          }
        }
        function registerExpressionProperty(expression) {
          if (registers.indexOf(expression) === -1) {
            registers.push(expression);
          }
        }
        function releaseInstances() {
          var i;
          var len = registers.length;
          for (i = 0; i < len; i += 1) {
            registers[i].release();
          }
          registers.length = 0;
        }
        animation.renderer.compInterface = CompExpressionInterface(animation.renderer);
        animation.renderer.globalData.projectInterface.registerComposition(animation.renderer);
        animation.renderer.globalData.pushExpression = pushExpression;
        animation.renderer.globalData.popExpression = popExpression;
        animation.renderer.globalData.registerExpressionProperty = registerExpressionProperty;
      }
      return ob2;
    }();
    var MaskManagerInterface = function() {
      function MaskInterface(mask2, data2) {
        this._mask = mask2;
        this._data = data2;
      }
      Object.defineProperty(MaskInterface.prototype, "maskPath", {
        get: function get() {
          if (this._mask.prop.k) {
            this._mask.prop.getValue();
          }
          return this._mask.prop;
        }
      });
      Object.defineProperty(MaskInterface.prototype, "maskOpacity", {
        get: function get() {
          if (this._mask.op.k) {
            this._mask.op.getValue();
          }
          return this._mask.op.v * 100;
        }
      });
      var MaskManager = function MaskManager2(maskManager) {
        var _masksInterfaces = createSizedArray(maskManager.viewData.length);
        var i;
        var len = maskManager.viewData.length;
        for (i = 0; i < len; i += 1) {
          _masksInterfaces[i] = new MaskInterface(maskManager.viewData[i], maskManager.masksProperties[i]);
        }
        var maskFunction = function maskFunction2(name2) {
          i = 0;
          while (i < len) {
            if (maskManager.masksProperties[i].nm === name2) {
              return _masksInterfaces[i];
            }
            i += 1;
          }
          return null;
        };
        return maskFunction;
      };
      return MaskManager;
    }();
    var ExpressionPropertyInterface = /* @__PURE__ */ function() {
      var defaultUnidimensionalValue = {
        pv: 0,
        v: 0,
        mult: 1
      };
      var defaultMultidimensionalValue = {
        pv: [0, 0, 0],
        v: [0, 0, 0],
        mult: 1
      };
      function completeProperty(expressionValue, property2, type) {
        Object.defineProperty(expressionValue, "velocity", {
          get: function get() {
            return property2.getVelocityAtTime(property2.comp.currentFrame);
          }
        });
        expressionValue.numKeys = property2.keyframes ? property2.keyframes.length : 0;
        expressionValue.key = function(pos) {
          if (!expressionValue.numKeys) {
            return 0;
          }
          var value2 = "";
          if ("s" in property2.keyframes[pos - 1]) {
            value2 = property2.keyframes[pos - 1].s;
          } else if ("e" in property2.keyframes[pos - 2]) {
            value2 = property2.keyframes[pos - 2].e;
          } else {
            value2 = property2.keyframes[pos - 2].s;
          }
          var valueProp = type === "unidimensional" ? new Number(value2) : Object.assign({}, value2);
          valueProp.time = property2.keyframes[pos - 1].t / property2.elem.comp.globalData.frameRate;
          valueProp.value = type === "unidimensional" ? value2[0] : value2;
          return valueProp;
        };
        expressionValue.valueAtTime = property2.getValueAtTime;
        expressionValue.speedAtTime = property2.getSpeedAtTime;
        expressionValue.velocityAtTime = property2.getVelocityAtTime;
        expressionValue.propertyGroup = property2.propertyGroup;
      }
      function UnidimensionalPropertyInterface(property2) {
        if (!property2 || !("pv" in property2)) {
          property2 = defaultUnidimensionalValue;
        }
        var mult = 1 / property2.mult;
        var val2 = property2.pv * mult;
        var expressionValue = new Number(val2);
        expressionValue.value = val2;
        completeProperty(expressionValue, property2, "unidimensional");
        return function() {
          if (property2.k) {
            property2.getValue();
          }
          val2 = property2.v * mult;
          if (expressionValue.value !== val2) {
            expressionValue = new Number(val2);
            expressionValue.value = val2;
            completeProperty(expressionValue, property2, "unidimensional");
          }
          return expressionValue;
        };
      }
      function MultidimensionalPropertyInterface(property2) {
        if (!property2 || !("pv" in property2)) {
          property2 = defaultMultidimensionalValue;
        }
        var mult = 1 / property2.mult;
        var len = property2.data && property2.data.l || property2.pv.length;
        var expressionValue = createTypedArray("float32", len);
        var arrValue = createTypedArray("float32", len);
        expressionValue.value = arrValue;
        completeProperty(expressionValue, property2, "multidimensional");
        return function() {
          if (property2.k) {
            property2.getValue();
          }
          for (var i = 0; i < len; i += 1) {
            arrValue[i] = property2.v[i] * mult;
            expressionValue[i] = arrValue[i];
          }
          return expressionValue;
        };
      }
      function defaultGetter() {
        return defaultUnidimensionalValue;
      }
      return function(property2) {
        if (!property2) {
          return defaultGetter;
        }
        if (property2.propType === "unidimensional") {
          return UnidimensionalPropertyInterface(property2);
        }
        return MultidimensionalPropertyInterface(property2);
      };
    }();
    var TransformExpressionInterface = /* @__PURE__ */ function() {
      return function(transform2) {
        function _thisFunction(name2) {
          switch (name2) {
            case "scale":
            case "Scale":
            case "ADBE Scale":
            case 6:
              return _thisFunction.scale;
            case "rotation":
            case "Rotation":
            case "ADBE Rotation":
            case "ADBE Rotate Z":
            case 10:
              return _thisFunction.rotation;
            case "ADBE Rotate X":
              return _thisFunction.xRotation;
            case "ADBE Rotate Y":
              return _thisFunction.yRotation;
            case "position":
            case "Position":
            case "ADBE Position":
            case 2:
              return _thisFunction.position;
            case "ADBE Position_0":
              return _thisFunction.xPosition;
            case "ADBE Position_1":
              return _thisFunction.yPosition;
            case "ADBE Position_2":
              return _thisFunction.zPosition;
            case "anchorPoint":
            case "AnchorPoint":
            case "Anchor Point":
            case "ADBE AnchorPoint":
            case 1:
              return _thisFunction.anchorPoint;
            case "opacity":
            case "Opacity":
            case 11:
              return _thisFunction.opacity;
            default:
              return null;
          }
        }
        Object.defineProperty(_thisFunction, "rotation", {
          get: ExpressionPropertyInterface(transform2.r || transform2.rz)
        });
        Object.defineProperty(_thisFunction, "zRotation", {
          get: ExpressionPropertyInterface(transform2.rz || transform2.r)
        });
        Object.defineProperty(_thisFunction, "xRotation", {
          get: ExpressionPropertyInterface(transform2.rx)
        });
        Object.defineProperty(_thisFunction, "yRotation", {
          get: ExpressionPropertyInterface(transform2.ry)
        });
        Object.defineProperty(_thisFunction, "scale", {
          get: ExpressionPropertyInterface(transform2.s)
        });
        var _px;
        var _py;
        var _pz;
        var _transformFactory;
        if (transform2.p) {
          _transformFactory = ExpressionPropertyInterface(transform2.p);
        } else {
          _px = ExpressionPropertyInterface(transform2.px);
          _py = ExpressionPropertyInterface(transform2.py);
          if (transform2.pz) {
            _pz = ExpressionPropertyInterface(transform2.pz);
          }
        }
        Object.defineProperty(_thisFunction, "position", {
          get: function get() {
            if (transform2.p) {
              return _transformFactory();
            }
            return [_px(), _py(), _pz ? _pz() : 0];
          }
        });
        Object.defineProperty(_thisFunction, "xPosition", {
          get: ExpressionPropertyInterface(transform2.px)
        });
        Object.defineProperty(_thisFunction, "yPosition", {
          get: ExpressionPropertyInterface(transform2.py)
        });
        Object.defineProperty(_thisFunction, "zPosition", {
          get: ExpressionPropertyInterface(transform2.pz)
        });
        Object.defineProperty(_thisFunction, "anchorPoint", {
          get: ExpressionPropertyInterface(transform2.a)
        });
        Object.defineProperty(_thisFunction, "opacity", {
          get: ExpressionPropertyInterface(transform2.o)
        });
        Object.defineProperty(_thisFunction, "skew", {
          get: ExpressionPropertyInterface(transform2.sk)
        });
        Object.defineProperty(_thisFunction, "skewAxis", {
          get: ExpressionPropertyInterface(transform2.sa)
        });
        Object.defineProperty(_thisFunction, "orientation", {
          get: ExpressionPropertyInterface(transform2.or)
        });
        return _thisFunction;
      };
    }();
    var LayerExpressionInterface = /* @__PURE__ */ function() {
      function getMatrix(time2) {
        var toWorldMat = new Matrix();
        if (time2 !== void 0) {
          var propMatrix = this._elem.finalTransform.mProp.getValueAtTime(time2);
          propMatrix.clone(toWorldMat);
        } else {
          var transformMat = this._elem.finalTransform.mProp;
          transformMat.applyToMatrix(toWorldMat);
        }
        return toWorldMat;
      }
      function toWorldVec(arr, time2) {
        var toWorldMat = this.getMatrix(time2);
        toWorldMat.props[12] = 0;
        toWorldMat.props[13] = 0;
        toWorldMat.props[14] = 0;
        return this.applyPoint(toWorldMat, arr);
      }
      function toWorld2(arr, time2) {
        var toWorldMat = this.getMatrix(time2);
        return this.applyPoint(toWorldMat, arr);
      }
      function fromWorldVec(arr, time2) {
        var toWorldMat = this.getMatrix(time2);
        toWorldMat.props[12] = 0;
        toWorldMat.props[13] = 0;
        toWorldMat.props[14] = 0;
        return this.invertPoint(toWorldMat, arr);
      }
      function fromWorld2(arr, time2) {
        var toWorldMat = this.getMatrix(time2);
        return this.invertPoint(toWorldMat, arr);
      }
      function applyPoint(matrix, arr) {
        if (this._elem.hierarchy && this._elem.hierarchy.length) {
          var i;
          var len = this._elem.hierarchy.length;
          for (i = 0; i < len; i += 1) {
            this._elem.hierarchy[i].finalTransform.mProp.applyToMatrix(matrix);
          }
        }
        return matrix.applyToPointArray(arr[0], arr[1], arr[2] || 0);
      }
      function invertPoint(matrix, arr) {
        if (this._elem.hierarchy && this._elem.hierarchy.length) {
          var i;
          var len = this._elem.hierarchy.length;
          for (i = 0; i < len; i += 1) {
            this._elem.hierarchy[i].finalTransform.mProp.applyToMatrix(matrix);
          }
        }
        return matrix.inversePoint(arr);
      }
      function fromComp2(arr) {
        var toWorldMat = new Matrix();
        toWorldMat.reset();
        this._elem.finalTransform.mProp.applyToMatrix(toWorldMat);
        if (this._elem.hierarchy && this._elem.hierarchy.length) {
          var i;
          var len = this._elem.hierarchy.length;
          for (i = 0; i < len; i += 1) {
            this._elem.hierarchy[i].finalTransform.mProp.applyToMatrix(toWorldMat);
          }
          return toWorldMat.inversePoint(arr);
        }
        return toWorldMat.inversePoint(arr);
      }
      function sampleImage() {
        return [1, 1, 1, 1];
      }
      return function(elem2) {
        var transformInterface;
        function _registerMaskInterface(maskManager) {
          _thisLayerFunction.mask = new MaskManagerInterface(maskManager, elem2);
        }
        function _registerEffectsInterface(effects) {
          _thisLayerFunction.effect = effects;
        }
        function _thisLayerFunction(name2) {
          switch (name2) {
            case "ADBE Root Vectors Group":
            case "Contents":
            case 2:
              return _thisLayerFunction.shapeInterface;
            case 1:
            case 6:
            case "Transform":
            case "transform":
            case "ADBE Transform Group":
              return transformInterface;
            case 4:
            case "ADBE Effect Parade":
            case "effects":
            case "Effects":
              return _thisLayerFunction.effect;
            case "ADBE Text Properties":
              return _thisLayerFunction.textInterface;
            default:
              return null;
          }
        }
        _thisLayerFunction.getMatrix = getMatrix;
        _thisLayerFunction.invertPoint = invertPoint;
        _thisLayerFunction.applyPoint = applyPoint;
        _thisLayerFunction.toWorld = toWorld2;
        _thisLayerFunction.toWorldVec = toWorldVec;
        _thisLayerFunction.fromWorld = fromWorld2;
        _thisLayerFunction.fromWorldVec = fromWorldVec;
        _thisLayerFunction.toComp = toWorld2;
        _thisLayerFunction.fromComp = fromComp2;
        _thisLayerFunction.sampleImage = sampleImage;
        _thisLayerFunction.sourceRectAtTime = elem2.sourceRectAtTime.bind(elem2);
        _thisLayerFunction._elem = elem2;
        transformInterface = TransformExpressionInterface(elem2.finalTransform.mProp);
        var anchorPointDescriptor = getDescriptor(transformInterface, "anchorPoint");
        Object.defineProperties(_thisLayerFunction, {
          hasParent: {
            get: function get() {
              return elem2.hierarchy.length;
            }
          },
          parent: {
            get: function get() {
              return elem2.hierarchy[0].layerInterface;
            }
          },
          rotation: getDescriptor(transformInterface, "rotation"),
          scale: getDescriptor(transformInterface, "scale"),
          position: getDescriptor(transformInterface, "position"),
          opacity: getDescriptor(transformInterface, "opacity"),
          anchorPoint: anchorPointDescriptor,
          anchor_point: anchorPointDescriptor,
          transform: {
            get: function get() {
              return transformInterface;
            }
          },
          active: {
            get: function get() {
              return elem2.isInRange;
            }
          }
        });
        _thisLayerFunction.startTime = elem2.data.st;
        _thisLayerFunction.index = elem2.data.ind;
        _thisLayerFunction.source = elem2.data.refId;
        _thisLayerFunction.height = elem2.data.ty === 0 ? elem2.data.h : 100;
        _thisLayerFunction.width = elem2.data.ty === 0 ? elem2.data.w : 100;
        _thisLayerFunction.inPoint = elem2.data.ip / elem2.comp.globalData.frameRate;
        _thisLayerFunction.outPoint = elem2.data.op / elem2.comp.globalData.frameRate;
        _thisLayerFunction._name = elem2.data.nm;
        _thisLayerFunction.registerMaskInterface = _registerMaskInterface;
        _thisLayerFunction.registerEffectsInterface = _registerEffectsInterface;
        return _thisLayerFunction;
      };
    }();
    var propertyGroupFactory = /* @__PURE__ */ function() {
      return function(interfaceFunction, parentPropertyGroup) {
        return function(val2) {
          val2 = val2 === void 0 ? 1 : val2;
          if (val2 <= 0) {
            return interfaceFunction;
          }
          return parentPropertyGroup(val2 - 1);
        };
      };
    }();
    var PropertyInterface = /* @__PURE__ */ function() {
      return function(propertyName, propertyGroup) {
        var interfaceFunction = {
          _name: propertyName
        };
        function _propertyGroup(val2) {
          val2 = val2 === void 0 ? 1 : val2;
          if (val2 <= 0) {
            return interfaceFunction;
          }
          return propertyGroup(val2 - 1);
        }
        return _propertyGroup;
      };
    }();
    var EffectsExpressionInterface = /* @__PURE__ */ function() {
      var ob2 = {
        createEffectsInterface
      };
      function createEffectsInterface(elem2, propertyGroup) {
        if (elem2.effectsManager) {
          var effectElements = [];
          var effectsData = elem2.data.ef;
          var i;
          var len = elem2.effectsManager.effectElements.length;
          for (i = 0; i < len; i += 1) {
            effectElements.push(createGroupInterface(effectsData[i], elem2.effectsManager.effectElements[i], propertyGroup, elem2));
          }
          var effects = elem2.data.ef || [];
          var groupInterface = function groupInterface2(name2) {
            i = 0;
            len = effects.length;
            while (i < len) {
              if (name2 === effects[i].nm || name2 === effects[i].mn || name2 === effects[i].ix) {
                return effectElements[i];
              }
              i += 1;
            }
            return null;
          };
          Object.defineProperty(groupInterface, "numProperties", {
            get: function get() {
              return effects.length;
            }
          });
          return groupInterface;
        }
        return null;
      }
      function createGroupInterface(data2, elements, propertyGroup, elem2) {
        function groupInterface(name2) {
          var effects = data2.ef;
          var i2 = 0;
          var len2 = effects.length;
          while (i2 < len2) {
            if (name2 === effects[i2].nm || name2 === effects[i2].mn || name2 === effects[i2].ix) {
              if (effects[i2].ty === 5) {
                return effectElements[i2];
              }
              return effectElements[i2]();
            }
            i2 += 1;
          }
          throw new Error();
        }
        var _propertyGroup = propertyGroupFactory(groupInterface, propertyGroup);
        var effectElements = [];
        var i;
        var len = data2.ef.length;
        for (i = 0; i < len; i += 1) {
          if (data2.ef[i].ty === 5) {
            effectElements.push(createGroupInterface(data2.ef[i], elements.effectElements[i], elements.effectElements[i].propertyGroup, elem2));
          } else {
            effectElements.push(createValueInterface(elements.effectElements[i], data2.ef[i].ty, elem2, _propertyGroup));
          }
        }
        if (data2.mn === "ADBE Color Control") {
          Object.defineProperty(groupInterface, "color", {
            get: function get() {
              return effectElements[0]();
            }
          });
        }
        Object.defineProperties(groupInterface, {
          numProperties: {
            get: function get() {
              return data2.np;
            }
          },
          _name: {
            value: data2.nm
          },
          propertyGroup: {
            value: _propertyGroup
          }
        });
        groupInterface.enabled = data2.en !== 0;
        groupInterface.active = groupInterface.enabled;
        return groupInterface;
      }
      function createValueInterface(element, type, elem2, propertyGroup) {
        var expressionProperty = ExpressionPropertyInterface(element.p);
        function interfaceFunction() {
          if (type === 10) {
            return elem2.comp.compInterface(element.p.v);
          }
          return expressionProperty();
        }
        if (element.p.setGroupProperty) {
          element.p.setGroupProperty(PropertyInterface("", propertyGroup));
        }
        return interfaceFunction;
      }
      return ob2;
    }();
    var ShapePathInterface = /* @__PURE__ */ function() {
      return function pathInterfaceFactory(shape, view, propertyGroup) {
        var prop = view.sh;
        function interfaceFunction(val2) {
          if (val2 === "Shape" || val2 === "shape" || val2 === "Path" || val2 === "path" || val2 === "ADBE Vector Shape" || val2 === 2) {
            return interfaceFunction.path;
          }
          return null;
        }
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        prop.setGroupProperty(PropertyInterface("Path", _propertyGroup));
        Object.defineProperties(interfaceFunction, {
          path: {
            get: function get() {
              if (prop.k) {
                prop.getValue();
              }
              return prop;
            }
          },
          shape: {
            get: function get() {
              if (prop.k) {
                prop.getValue();
              }
              return prop;
            }
          },
          _name: {
            value: shape.nm
          },
          ix: {
            value: shape.ix
          },
          propertyIndex: {
            value: shape.ix
          },
          mn: {
            value: shape.mn
          },
          propertyGroup: {
            value: propertyGroup
          }
        });
        return interfaceFunction;
      };
    }();
    var ShapeExpressionInterface = /* @__PURE__ */ function() {
      function iterateElements(shapes, view, propertyGroup) {
        var arr = [];
        var i;
        var len = shapes ? shapes.length : 0;
        for (i = 0; i < len; i += 1) {
          if (shapes[i].ty === "gr") {
            arr.push(groupInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "fl") {
            arr.push(fillInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "st") {
            arr.push(strokeInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "tm") {
            arr.push(trimInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "tr") ;
          else if (shapes[i].ty === "el") {
            arr.push(ellipseInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "sr") {
            arr.push(starInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "sh") {
            arr.push(ShapePathInterface(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "rc") {
            arr.push(rectInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "rd") {
            arr.push(roundedInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "rp") {
            arr.push(repeaterInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else if (shapes[i].ty === "gf") {
            arr.push(gradientFillInterfaceFactory(shapes[i], view[i], propertyGroup));
          } else {
            arr.push(defaultInterfaceFactory(shapes[i], view[i]));
          }
        }
        return arr;
      }
      function contentsInterfaceFactory(shape, view, propertyGroup) {
        var interfaces2;
        var interfaceFunction = function _interfaceFunction(value2) {
          var i = 0;
          var len = interfaces2.length;
          while (i < len) {
            if (interfaces2[i]._name === value2 || interfaces2[i].mn === value2 || interfaces2[i].propertyIndex === value2 || interfaces2[i].ix === value2 || interfaces2[i].ind === value2) {
              return interfaces2[i];
            }
            i += 1;
          }
          if (typeof value2 === "number") {
            return interfaces2[value2 - 1];
          }
          return null;
        };
        interfaceFunction.propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        interfaces2 = iterateElements(shape.it, view.it, interfaceFunction.propertyGroup);
        interfaceFunction.numProperties = interfaces2.length;
        var transformInterface = transformInterfaceFactory(shape.it[shape.it.length - 1], view.it[view.it.length - 1], interfaceFunction.propertyGroup);
        interfaceFunction.transform = transformInterface;
        interfaceFunction.propertyIndex = shape.cix;
        interfaceFunction._name = shape.nm;
        return interfaceFunction;
      }
      function groupInterfaceFactory(shape, view, propertyGroup) {
        var interfaceFunction = function _interfaceFunction(value2) {
          switch (value2) {
            case "ADBE Vectors Group":
            case "Contents":
            case 2:
              return interfaceFunction.content;
            default:
              return interfaceFunction.transform;
          }
        };
        interfaceFunction.propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        var content2 = contentsInterfaceFactory(shape, view, interfaceFunction.propertyGroup);
        var transformInterface = transformInterfaceFactory(shape.it[shape.it.length - 1], view.it[view.it.length - 1], interfaceFunction.propertyGroup);
        interfaceFunction.content = content2;
        interfaceFunction.transform = transformInterface;
        Object.defineProperty(interfaceFunction, "_name", {
          get: function get() {
            return shape.nm;
          }
        });
        interfaceFunction.numProperties = shape.np;
        interfaceFunction.propertyIndex = shape.ix;
        interfaceFunction.nm = shape.nm;
        interfaceFunction.mn = shape.mn;
        return interfaceFunction;
      }
      function fillInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(val2) {
          if (val2 === "Color" || val2 === "color") {
            return interfaceFunction.color;
          }
          if (val2 === "Opacity" || val2 === "opacity") {
            return interfaceFunction.opacity;
          }
          return null;
        }
        Object.defineProperties(interfaceFunction, {
          color: {
            get: ExpressionPropertyInterface(view.c)
          },
          opacity: {
            get: ExpressionPropertyInterface(view.o)
          },
          _name: {
            value: shape.nm
          },
          mn: {
            value: shape.mn
          }
        });
        view.c.setGroupProperty(PropertyInterface("Color", propertyGroup));
        view.o.setGroupProperty(PropertyInterface("Opacity", propertyGroup));
        return interfaceFunction;
      }
      function gradientFillInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(val2) {
          if (val2 === "Start Point" || val2 === "start point") {
            return interfaceFunction.startPoint;
          }
          if (val2 === "End Point" || val2 === "end point") {
            return interfaceFunction.endPoint;
          }
          if (val2 === "Opacity" || val2 === "opacity") {
            return interfaceFunction.opacity;
          }
          return null;
        }
        Object.defineProperties(interfaceFunction, {
          startPoint: {
            get: ExpressionPropertyInterface(view.s)
          },
          endPoint: {
            get: ExpressionPropertyInterface(view.e)
          },
          opacity: {
            get: ExpressionPropertyInterface(view.o)
          },
          type: {
            get: function get() {
              return "a";
            }
          },
          _name: {
            value: shape.nm
          },
          mn: {
            value: shape.mn
          }
        });
        view.s.setGroupProperty(PropertyInterface("Start Point", propertyGroup));
        view.e.setGroupProperty(PropertyInterface("End Point", propertyGroup));
        view.o.setGroupProperty(PropertyInterface("Opacity", propertyGroup));
        return interfaceFunction;
      }
      function defaultInterfaceFactory() {
        function interfaceFunction() {
          return null;
        }
        return interfaceFunction;
      }
      function strokeInterfaceFactory(shape, view, propertyGroup) {
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        var _dashPropertyGroup = propertyGroupFactory(dashOb, _propertyGroup);
        function addPropertyToDashOb(i2) {
          Object.defineProperty(dashOb, shape.d[i2].nm, {
            get: ExpressionPropertyInterface(view.d.dataProps[i2].p)
          });
        }
        var i;
        var len = shape.d ? shape.d.length : 0;
        var dashOb = {};
        for (i = 0; i < len; i += 1) {
          addPropertyToDashOb(i);
          view.d.dataProps[i].p.setGroupProperty(_dashPropertyGroup);
        }
        function interfaceFunction(val2) {
          if (val2 === "Color" || val2 === "color") {
            return interfaceFunction.color;
          }
          if (val2 === "Opacity" || val2 === "opacity") {
            return interfaceFunction.opacity;
          }
          if (val2 === "Stroke Width" || val2 === "stroke width") {
            return interfaceFunction.strokeWidth;
          }
          return null;
        }
        Object.defineProperties(interfaceFunction, {
          color: {
            get: ExpressionPropertyInterface(view.c)
          },
          opacity: {
            get: ExpressionPropertyInterface(view.o)
          },
          strokeWidth: {
            get: ExpressionPropertyInterface(view.w)
          },
          dash: {
            get: function get() {
              return dashOb;
            }
          },
          _name: {
            value: shape.nm
          },
          mn: {
            value: shape.mn
          }
        });
        view.c.setGroupProperty(PropertyInterface("Color", _propertyGroup));
        view.o.setGroupProperty(PropertyInterface("Opacity", _propertyGroup));
        view.w.setGroupProperty(PropertyInterface("Stroke Width", _propertyGroup));
        return interfaceFunction;
      }
      function trimInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(val2) {
          if (val2 === shape.e.ix || val2 === "End" || val2 === "end") {
            return interfaceFunction.end;
          }
          if (val2 === shape.s.ix) {
            return interfaceFunction.start;
          }
          if (val2 === shape.o.ix) {
            return interfaceFunction.offset;
          }
          return null;
        }
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        interfaceFunction.propertyIndex = shape.ix;
        view.s.setGroupProperty(PropertyInterface("Start", _propertyGroup));
        view.e.setGroupProperty(PropertyInterface("End", _propertyGroup));
        view.o.setGroupProperty(PropertyInterface("Offset", _propertyGroup));
        interfaceFunction.propertyIndex = shape.ix;
        interfaceFunction.propertyGroup = propertyGroup;
        Object.defineProperties(interfaceFunction, {
          start: {
            get: ExpressionPropertyInterface(view.s)
          },
          end: {
            get: ExpressionPropertyInterface(view.e)
          },
          offset: {
            get: ExpressionPropertyInterface(view.o)
          },
          _name: {
            value: shape.nm
          }
        });
        interfaceFunction.mn = shape.mn;
        return interfaceFunction;
      }
      function transformInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(value2) {
          if (shape.a.ix === value2 || value2 === "Anchor Point") {
            return interfaceFunction.anchorPoint;
          }
          if (shape.o.ix === value2 || value2 === "Opacity") {
            return interfaceFunction.opacity;
          }
          if (shape.p.ix === value2 || value2 === "Position") {
            return interfaceFunction.position;
          }
          if (shape.r.ix === value2 || value2 === "Rotation" || value2 === "ADBE Vector Rotation") {
            return interfaceFunction.rotation;
          }
          if (shape.s.ix === value2 || value2 === "Scale") {
            return interfaceFunction.scale;
          }
          if (shape.sk && shape.sk.ix === value2 || value2 === "Skew") {
            return interfaceFunction.skew;
          }
          if (shape.sa && shape.sa.ix === value2 || value2 === "Skew Axis") {
            return interfaceFunction.skewAxis;
          }
          return null;
        }
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        view.transform.mProps.o.setGroupProperty(PropertyInterface("Opacity", _propertyGroup));
        view.transform.mProps.p.setGroupProperty(PropertyInterface("Position", _propertyGroup));
        view.transform.mProps.a.setGroupProperty(PropertyInterface("Anchor Point", _propertyGroup));
        view.transform.mProps.s.setGroupProperty(PropertyInterface("Scale", _propertyGroup));
        view.transform.mProps.r.setGroupProperty(PropertyInterface("Rotation", _propertyGroup));
        if (view.transform.mProps.sk) {
          view.transform.mProps.sk.setGroupProperty(PropertyInterface("Skew", _propertyGroup));
          view.transform.mProps.sa.setGroupProperty(PropertyInterface("Skew Angle", _propertyGroup));
        }
        view.transform.op.setGroupProperty(PropertyInterface("Opacity", _propertyGroup));
        Object.defineProperties(interfaceFunction, {
          opacity: {
            get: ExpressionPropertyInterface(view.transform.mProps.o)
          },
          position: {
            get: ExpressionPropertyInterface(view.transform.mProps.p)
          },
          anchorPoint: {
            get: ExpressionPropertyInterface(view.transform.mProps.a)
          },
          scale: {
            get: ExpressionPropertyInterface(view.transform.mProps.s)
          },
          rotation: {
            get: ExpressionPropertyInterface(view.transform.mProps.r)
          },
          skew: {
            get: ExpressionPropertyInterface(view.transform.mProps.sk)
          },
          skewAxis: {
            get: ExpressionPropertyInterface(view.transform.mProps.sa)
          },
          _name: {
            value: shape.nm
          }
        });
        interfaceFunction.ty = "tr";
        interfaceFunction.mn = shape.mn;
        interfaceFunction.propertyGroup = propertyGroup;
        return interfaceFunction;
      }
      function ellipseInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(value2) {
          if (shape.p.ix === value2) {
            return interfaceFunction.position;
          }
          if (shape.s.ix === value2) {
            return interfaceFunction.size;
          }
          return null;
        }
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        interfaceFunction.propertyIndex = shape.ix;
        var prop = view.sh.ty === "tm" ? view.sh.prop : view.sh;
        prop.s.setGroupProperty(PropertyInterface("Size", _propertyGroup));
        prop.p.setGroupProperty(PropertyInterface("Position", _propertyGroup));
        Object.defineProperties(interfaceFunction, {
          size: {
            get: ExpressionPropertyInterface(prop.s)
          },
          position: {
            get: ExpressionPropertyInterface(prop.p)
          },
          _name: {
            value: shape.nm
          }
        });
        interfaceFunction.mn = shape.mn;
        return interfaceFunction;
      }
      function starInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(value2) {
          if (shape.p.ix === value2) {
            return interfaceFunction.position;
          }
          if (shape.r.ix === value2) {
            return interfaceFunction.rotation;
          }
          if (shape.pt.ix === value2) {
            return interfaceFunction.points;
          }
          if (shape.or.ix === value2 || value2 === "ADBE Vector Star Outer Radius") {
            return interfaceFunction.outerRadius;
          }
          if (shape.os.ix === value2) {
            return interfaceFunction.outerRoundness;
          }
          if (shape.ir && (shape.ir.ix === value2 || value2 === "ADBE Vector Star Inner Radius")) {
            return interfaceFunction.innerRadius;
          }
          if (shape.is && shape.is.ix === value2) {
            return interfaceFunction.innerRoundness;
          }
          return null;
        }
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        var prop = view.sh.ty === "tm" ? view.sh.prop : view.sh;
        interfaceFunction.propertyIndex = shape.ix;
        prop.or.setGroupProperty(PropertyInterface("Outer Radius", _propertyGroup));
        prop.os.setGroupProperty(PropertyInterface("Outer Roundness", _propertyGroup));
        prop.pt.setGroupProperty(PropertyInterface("Points", _propertyGroup));
        prop.p.setGroupProperty(PropertyInterface("Position", _propertyGroup));
        prop.r.setGroupProperty(PropertyInterface("Rotation", _propertyGroup));
        if (shape.ir) {
          prop.ir.setGroupProperty(PropertyInterface("Inner Radius", _propertyGroup));
          prop.is.setGroupProperty(PropertyInterface("Inner Roundness", _propertyGroup));
        }
        Object.defineProperties(interfaceFunction, {
          position: {
            get: ExpressionPropertyInterface(prop.p)
          },
          rotation: {
            get: ExpressionPropertyInterface(prop.r)
          },
          points: {
            get: ExpressionPropertyInterface(prop.pt)
          },
          outerRadius: {
            get: ExpressionPropertyInterface(prop.or)
          },
          outerRoundness: {
            get: ExpressionPropertyInterface(prop.os)
          },
          innerRadius: {
            get: ExpressionPropertyInterface(prop.ir)
          },
          innerRoundness: {
            get: ExpressionPropertyInterface(prop.is)
          },
          _name: {
            value: shape.nm
          }
        });
        interfaceFunction.mn = shape.mn;
        return interfaceFunction;
      }
      function rectInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(value2) {
          if (shape.p.ix === value2) {
            return interfaceFunction.position;
          }
          if (shape.r.ix === value2) {
            return interfaceFunction.roundness;
          }
          if (shape.s.ix === value2 || value2 === "Size" || value2 === "ADBE Vector Rect Size") {
            return interfaceFunction.size;
          }
          return null;
        }
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        var prop = view.sh.ty === "tm" ? view.sh.prop : view.sh;
        interfaceFunction.propertyIndex = shape.ix;
        prop.p.setGroupProperty(PropertyInterface("Position", _propertyGroup));
        prop.s.setGroupProperty(PropertyInterface("Size", _propertyGroup));
        prop.r.setGroupProperty(PropertyInterface("Rotation", _propertyGroup));
        Object.defineProperties(interfaceFunction, {
          position: {
            get: ExpressionPropertyInterface(prop.p)
          },
          roundness: {
            get: ExpressionPropertyInterface(prop.r)
          },
          size: {
            get: ExpressionPropertyInterface(prop.s)
          },
          _name: {
            value: shape.nm
          }
        });
        interfaceFunction.mn = shape.mn;
        return interfaceFunction;
      }
      function roundedInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(value2) {
          if (shape.r.ix === value2 || value2 === "Round Corners 1") {
            return interfaceFunction.radius;
          }
          return null;
        }
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        var prop = view;
        interfaceFunction.propertyIndex = shape.ix;
        prop.rd.setGroupProperty(PropertyInterface("Radius", _propertyGroup));
        Object.defineProperties(interfaceFunction, {
          radius: {
            get: ExpressionPropertyInterface(prop.rd)
          },
          _name: {
            value: shape.nm
          }
        });
        interfaceFunction.mn = shape.mn;
        return interfaceFunction;
      }
      function repeaterInterfaceFactory(shape, view, propertyGroup) {
        function interfaceFunction(value2) {
          if (shape.c.ix === value2 || value2 === "Copies") {
            return interfaceFunction.copies;
          }
          if (shape.o.ix === value2 || value2 === "Offset") {
            return interfaceFunction.offset;
          }
          return null;
        }
        var _propertyGroup = propertyGroupFactory(interfaceFunction, propertyGroup);
        var prop = view;
        interfaceFunction.propertyIndex = shape.ix;
        prop.c.setGroupProperty(PropertyInterface("Copies", _propertyGroup));
        prop.o.setGroupProperty(PropertyInterface("Offset", _propertyGroup));
        Object.defineProperties(interfaceFunction, {
          copies: {
            get: ExpressionPropertyInterface(prop.c)
          },
          offset: {
            get: ExpressionPropertyInterface(prop.o)
          },
          _name: {
            value: shape.nm
          }
        });
        interfaceFunction.mn = shape.mn;
        return interfaceFunction;
      }
      return function(shapes, view, propertyGroup) {
        var interfaces2;
        function _interfaceFunction(value2) {
          if (typeof value2 === "number") {
            value2 = value2 === void 0 ? 1 : value2;
            if (value2 === 0) {
              return propertyGroup;
            }
            return interfaces2[value2 - 1];
          }
          var i = 0;
          var len = interfaces2.length;
          while (i < len) {
            if (interfaces2[i]._name === value2) {
              return interfaces2[i];
            }
            i += 1;
          }
          return null;
        }
        function parentGroupWrapper() {
          return propertyGroup;
        }
        _interfaceFunction.propertyGroup = propertyGroupFactory(_interfaceFunction, parentGroupWrapper);
        interfaces2 = iterateElements(shapes, view, _interfaceFunction.propertyGroup);
        _interfaceFunction.numProperties = interfaces2.length;
        _interfaceFunction._name = "Contents";
        return _interfaceFunction;
      };
    }();
    var TextExpressionInterface = /* @__PURE__ */ function() {
      return function(elem2) {
        var _sourceText;
        function _thisLayerFunction(name2) {
          switch (name2) {
            case "ADBE Text Document":
              return _thisLayerFunction.sourceText;
            default:
              return null;
          }
        }
        Object.defineProperty(_thisLayerFunction, "sourceText", {
          get: function get() {
            elem2.textProperty.getValue();
            var stringValue = elem2.textProperty.currentData.t;
            if (!_sourceText || stringValue !== _sourceText.value) {
              _sourceText = new String(stringValue);
              _sourceText.value = stringValue || new String(stringValue);
              Object.defineProperty(_sourceText, "style", {
                get: function get2() {
                  return {
                    fillColor: elem2.textProperty.currentData.fc
                  };
                }
              });
            }
            return _sourceText;
          }
        });
        return _thisLayerFunction;
      };
    }();
    function _typeof(obj) {
      "@babel/helpers - typeof";
      if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
        _typeof = function _typeof2(obj2) {
          return typeof obj2;
        };
      } else {
        _typeof = function _typeof2(obj2) {
          return obj2 && typeof Symbol === "function" && obj2.constructor === Symbol && obj2 !== Symbol.prototype ? "symbol" : typeof obj2;
        };
      }
      return _typeof(obj);
    }
    var FootageInterface = /* @__PURE__ */ function() {
      var outlineInterfaceFactory = function outlineInterfaceFactory2(elem2) {
        var currentPropertyName = "";
        var currentProperty = elem2.getFootageData();
        function init() {
          currentPropertyName = "";
          currentProperty = elem2.getFootageData();
          return searchProperty;
        }
        function searchProperty(value2) {
          if (currentProperty[value2]) {
            currentPropertyName = value2;
            currentProperty = currentProperty[value2];
            if (_typeof(currentProperty) === "object") {
              return searchProperty;
            }
            return currentProperty;
          }
          var propertyNameIndex = value2.indexOf(currentPropertyName);
          if (propertyNameIndex !== -1) {
            var index2 = parseInt(value2.substr(propertyNameIndex + currentPropertyName.length), 10);
            currentProperty = currentProperty[index2];
            if (_typeof(currentProperty) === "object") {
              return searchProperty;
            }
            return currentProperty;
          }
          return "";
        }
        return init;
      };
      var dataInterfaceFactory = function dataInterfaceFactory2(elem2) {
        function interfaceFunction(value2) {
          if (value2 === "Outline") {
            return interfaceFunction.outlineInterface();
          }
          return null;
        }
        interfaceFunction._name = "Outline";
        interfaceFunction.outlineInterface = outlineInterfaceFactory(elem2);
        return interfaceFunction;
      };
      return function(elem2) {
        function _interfaceFunction(value2) {
          if (value2 === "Data") {
            return _interfaceFunction.dataInterface;
          }
          return null;
        }
        _interfaceFunction._name = "Data";
        _interfaceFunction.dataInterface = dataInterfaceFactory(elem2);
        return _interfaceFunction;
      };
    }();
    var interfaces = {
      layer: LayerExpressionInterface,
      effects: EffectsExpressionInterface,
      comp: CompExpressionInterface,
      shape: ShapeExpressionInterface,
      text: TextExpressionInterface,
      footage: FootageInterface
    };
    function getInterface(type) {
      return interfaces[type] || null;
    }
    var expressionHelpers = /* @__PURE__ */ function() {
      function searchExpressions(elem2, data2, prop) {
        if (data2.x) {
          prop.k = true;
          prop.x = true;
          prop.initiateExpression = ExpressionManager.initiateExpression;
          prop.effectsSequence.push(prop.initiateExpression(elem2, data2, prop).bind(prop));
        }
      }
      function getValueAtTime(frameNum) {
        frameNum *= this.elem.globalData.frameRate;
        frameNum -= this.offsetTime;
        if (frameNum !== this._cachingAtTime.lastFrame) {
          this._cachingAtTime.lastIndex = this._cachingAtTime.lastFrame < frameNum ? this._cachingAtTime.lastIndex : 0;
          this._cachingAtTime.value = this.interpolateValue(frameNum, this._cachingAtTime);
          this._cachingAtTime.lastFrame = frameNum;
        }
        return this._cachingAtTime.value;
      }
      function getSpeedAtTime(frameNum) {
        var delta = -0.01;
        var v1 = this.getValueAtTime(frameNum);
        var v2 = this.getValueAtTime(frameNum + delta);
        var speed = 0;
        if (v1.length) {
          var i;
          for (i = 0; i < v1.length; i += 1) {
            speed += Math.pow(v2[i] - v1[i], 2);
          }
          speed = Math.sqrt(speed) * 100;
        } else {
          speed = 0;
        }
        return speed;
      }
      function getVelocityAtTime(frameNum) {
        if (this.vel !== void 0) {
          return this.vel;
        }
        var delta = -1e-3;
        var v1 = this.getValueAtTime(frameNum);
        var v2 = this.getValueAtTime(frameNum + delta);
        var velocity2;
        if (v1.length) {
          velocity2 = createTypedArray("float32", v1.length);
          var i;
          for (i = 0; i < v1.length; i += 1) {
            velocity2[i] = (v2[i] - v1[i]) / delta;
          }
        } else {
          velocity2 = (v2 - v1) / delta;
        }
        return velocity2;
      }
      function getStaticValueAtTime() {
        return this.pv;
      }
      function setGroupProperty(propertyGroup) {
        this.propertyGroup = propertyGroup;
      }
      return {
        searchExpressions,
        getSpeedAtTime,
        getVelocityAtTime,
        getValueAtTime,
        getStaticValueAtTime,
        setGroupProperty
      };
    }();
    function addPropertyDecorator() {
      function loopOut2(type, duration, durationFlag) {
        if (!this.k || !this.keyframes) {
          return this.pv;
        }
        type = type ? type.toLowerCase() : "";
        var currentFrame = this.comp.renderedFrame;
        var keyframes = this.keyframes;
        var lastKeyFrame = keyframes[keyframes.length - 1].t;
        if (currentFrame <= lastKeyFrame) {
          return this.pv;
        }
        var cycleDuration;
        var firstKeyFrame;
        if (!durationFlag) {
          if (!duration || duration > keyframes.length - 1) {
            duration = keyframes.length - 1;
          }
          firstKeyFrame = keyframes[keyframes.length - 1 - duration].t;
          cycleDuration = lastKeyFrame - firstKeyFrame;
        } else {
          if (!duration) {
            cycleDuration = Math.max(0, lastKeyFrame - this.elem.data.ip);
          } else {
            cycleDuration = Math.abs(lastKeyFrame - this.elem.comp.globalData.frameRate * duration);
          }
          firstKeyFrame = lastKeyFrame - cycleDuration;
        }
        var i;
        var len;
        var ret;
        if (type === "pingpong") {
          var iterations = Math.floor((currentFrame - firstKeyFrame) / cycleDuration);
          if (iterations % 2 !== 0) {
            return this.getValueAtTime((cycleDuration - (currentFrame - firstKeyFrame) % cycleDuration + firstKeyFrame) / this.comp.globalData.frameRate, 0);
          }
        } else if (type === "offset") {
          var initV = this.getValueAtTime(firstKeyFrame / this.comp.globalData.frameRate, 0);
          var endV = this.getValueAtTime(lastKeyFrame / this.comp.globalData.frameRate, 0);
          var current = this.getValueAtTime(((currentFrame - firstKeyFrame) % cycleDuration + firstKeyFrame) / this.comp.globalData.frameRate, 0);
          var repeats = Math.floor((currentFrame - firstKeyFrame) / cycleDuration);
          if (this.pv.length) {
            ret = new Array(initV.length);
            len = ret.length;
            for (i = 0; i < len; i += 1) {
              ret[i] = (endV[i] - initV[i]) * repeats + current[i];
            }
            return ret;
          }
          return (endV - initV) * repeats + current;
        } else if (type === "continue") {
          var lastValue = this.getValueAtTime(lastKeyFrame / this.comp.globalData.frameRate, 0);
          var nextLastValue = this.getValueAtTime((lastKeyFrame - 1e-3) / this.comp.globalData.frameRate, 0);
          if (this.pv.length) {
            ret = new Array(lastValue.length);
            len = ret.length;
            for (i = 0; i < len; i += 1) {
              ret[i] = lastValue[i] + (lastValue[i] - nextLastValue[i]) * ((currentFrame - lastKeyFrame) / this.comp.globalData.frameRate) / 5e-4;
            }
            return ret;
          }
          return lastValue + (lastValue - nextLastValue) * ((currentFrame - lastKeyFrame) / 1e-3);
        }
        return this.getValueAtTime(((currentFrame - firstKeyFrame) % cycleDuration + firstKeyFrame) / this.comp.globalData.frameRate, 0);
      }
      function loopIn2(type, duration, durationFlag) {
        if (!this.k) {
          return this.pv;
        }
        type = type ? type.toLowerCase() : "";
        var currentFrame = this.comp.renderedFrame;
        var keyframes = this.keyframes;
        var firstKeyFrame = keyframes[0].t;
        if (currentFrame >= firstKeyFrame) {
          return this.pv;
        }
        var cycleDuration;
        var lastKeyFrame;
        if (!durationFlag) {
          if (!duration || duration > keyframes.length - 1) {
            duration = keyframes.length - 1;
          }
          lastKeyFrame = keyframes[duration].t;
          cycleDuration = lastKeyFrame - firstKeyFrame;
        } else {
          if (!duration) {
            cycleDuration = Math.max(0, this.elem.data.op - firstKeyFrame);
          } else {
            cycleDuration = Math.abs(this.elem.comp.globalData.frameRate * duration);
          }
          lastKeyFrame = firstKeyFrame + cycleDuration;
        }
        var i;
        var len;
        var ret;
        if (type === "pingpong") {
          var iterations = Math.floor((firstKeyFrame - currentFrame) / cycleDuration);
          if (iterations % 2 === 0) {
            return this.getValueAtTime(((firstKeyFrame - currentFrame) % cycleDuration + firstKeyFrame) / this.comp.globalData.frameRate, 0);
          }
        } else if (type === "offset") {
          var initV = this.getValueAtTime(firstKeyFrame / this.comp.globalData.frameRate, 0);
          var endV = this.getValueAtTime(lastKeyFrame / this.comp.globalData.frameRate, 0);
          var current = this.getValueAtTime((cycleDuration - (firstKeyFrame - currentFrame) % cycleDuration + firstKeyFrame) / this.comp.globalData.frameRate, 0);
          var repeats = Math.floor((firstKeyFrame - currentFrame) / cycleDuration) + 1;
          if (this.pv.length) {
            ret = new Array(initV.length);
            len = ret.length;
            for (i = 0; i < len; i += 1) {
              ret[i] = current[i] - (endV[i] - initV[i]) * repeats;
            }
            return ret;
          }
          return current - (endV - initV) * repeats;
        } else if (type === "continue") {
          var firstValue = this.getValueAtTime(firstKeyFrame / this.comp.globalData.frameRate, 0);
          var nextFirstValue = this.getValueAtTime((firstKeyFrame + 1e-3) / this.comp.globalData.frameRate, 0);
          if (this.pv.length) {
            ret = new Array(firstValue.length);
            len = ret.length;
            for (i = 0; i < len; i += 1) {
              ret[i] = firstValue[i] + (firstValue[i] - nextFirstValue[i]) * (firstKeyFrame - currentFrame) / 1e-3;
            }
            return ret;
          }
          return firstValue + (firstValue - nextFirstValue) * (firstKeyFrame - currentFrame) / 1e-3;
        }
        return this.getValueAtTime((cycleDuration - ((firstKeyFrame - currentFrame) % cycleDuration + firstKeyFrame)) / this.comp.globalData.frameRate, 0);
      }
      function smooth2(width2, samples) {
        if (!this.k) {
          return this.pv;
        }
        width2 = (width2 || 0.4) * 0.5;
        samples = Math.floor(samples || 5);
        if (samples <= 1) {
          return this.pv;
        }
        var currentTime = this.comp.renderedFrame / this.comp.globalData.frameRate;
        var initFrame2 = currentTime - width2;
        var endFrame = currentTime + width2;
        var sampleFrequency = samples > 1 ? (endFrame - initFrame2) / (samples - 1) : 1;
        var i = 0;
        var j = 0;
        var value2;
        if (this.pv.length) {
          value2 = createTypedArray("float32", this.pv.length);
        } else {
          value2 = 0;
        }
        var sampleValue;
        while (i < samples) {
          sampleValue = this.getValueAtTime(initFrame2 + i * sampleFrequency);
          if (this.pv.length) {
            for (j = 0; j < this.pv.length; j += 1) {
              value2[j] += sampleValue[j];
            }
          } else {
            value2 += sampleValue;
          }
          i += 1;
        }
        if (this.pv.length) {
          for (j = 0; j < this.pv.length; j += 1) {
            value2[j] /= samples;
          }
        } else {
          value2 /= samples;
        }
        return value2;
      }
      function getTransformValueAtTime(time2) {
        if (!this._transformCachingAtTime) {
          this._transformCachingAtTime = {
            v: new Matrix()
          };
        }
        var matrix = this._transformCachingAtTime.v;
        matrix.cloneFromProps(this.pre.props);
        if (this.appliedTransformations < 1) {
          var anchor = this.a.getValueAtTime(time2);
          matrix.translate(-anchor[0] * this.a.mult, -anchor[1] * this.a.mult, anchor[2] * this.a.mult);
        }
        if (this.appliedTransformations < 2) {
          var scale2 = this.s.getValueAtTime(time2);
          matrix.scale(scale2[0] * this.s.mult, scale2[1] * this.s.mult, scale2[2] * this.s.mult);
        }
        if (this.sk && this.appliedTransformations < 3) {
          var skew = this.sk.getValueAtTime(time2);
          var skewAxis = this.sa.getValueAtTime(time2);
          matrix.skewFromAxis(-skew * this.sk.mult, skewAxis * this.sa.mult);
        }
        if (this.r && this.appliedTransformations < 4) {
          var rotation2 = this.r.getValueAtTime(time2);
          matrix.rotate(-rotation2 * this.r.mult);
        } else if (!this.r && this.appliedTransformations < 4) {
          var rotationZ = this.rz.getValueAtTime(time2);
          var rotationY = this.ry.getValueAtTime(time2);
          var rotationX = this.rx.getValueAtTime(time2);
          var orientation = this.or.getValueAtTime(time2);
          matrix.rotateZ(-rotationZ * this.rz.mult).rotateY(rotationY * this.ry.mult).rotateX(rotationX * this.rx.mult).rotateZ(-orientation[2] * this.or.mult).rotateY(orientation[1] * this.or.mult).rotateX(orientation[0] * this.or.mult);
        }
        if (this.data.p && this.data.p.s) {
          var positionX = this.px.getValueAtTime(time2);
          var positionY = this.py.getValueAtTime(time2);
          if (this.data.p.z) {
            var positionZ = this.pz.getValueAtTime(time2);
            matrix.translate(positionX * this.px.mult, positionY * this.py.mult, -positionZ * this.pz.mult);
          } else {
            matrix.translate(positionX * this.px.mult, positionY * this.py.mult, 0);
          }
        } else {
          var position2 = this.p.getValueAtTime(time2);
          matrix.translate(position2[0] * this.p.mult, position2[1] * this.p.mult, -position2[2] * this.p.mult);
        }
        return matrix;
      }
      function getTransformStaticValueAtTime() {
        return this.v.clone(new Matrix());
      }
      var getTransformProperty = TransformPropertyFactory.getTransformProperty;
      TransformPropertyFactory.getTransformProperty = function(elem2, data2, container) {
        var prop = getTransformProperty(elem2, data2, container);
        if (prop.dynamicProperties.length) {
          prop.getValueAtTime = getTransformValueAtTime.bind(prop);
        } else {
          prop.getValueAtTime = getTransformStaticValueAtTime.bind(prop);
        }
        prop.setGroupProperty = expressionHelpers.setGroupProperty;
        return prop;
      };
      var propertyGetProp = PropertyFactory.getProp;
      PropertyFactory.getProp = function(elem2, data2, type, mult, container) {
        var prop = propertyGetProp(elem2, data2, type, mult, container);
        if (prop.kf) {
          prop.getValueAtTime = expressionHelpers.getValueAtTime.bind(prop);
        } else {
          prop.getValueAtTime = expressionHelpers.getStaticValueAtTime.bind(prop);
        }
        prop.setGroupProperty = expressionHelpers.setGroupProperty;
        prop.loopOut = loopOut2;
        prop.loopIn = loopIn2;
        prop.smooth = smooth2;
        prop.getVelocityAtTime = expressionHelpers.getVelocityAtTime.bind(prop);
        prop.getSpeedAtTime = expressionHelpers.getSpeedAtTime.bind(prop);
        prop.numKeys = data2.a === 1 ? data2.k.length : 0;
        prop.propertyIndex = data2.ix;
        var value2 = 0;
        if (type !== 0) {
          value2 = createTypedArray("float32", data2.a === 1 ? data2.k[0].s.length : data2.k.length);
        }
        prop._cachingAtTime = {
          lastFrame: initialDefaultFrame,
          lastIndex: 0,
          value: value2
        };
        expressionHelpers.searchExpressions(elem2, data2, prop);
        if (prop.k) {
          container.addDynamicProperty(prop);
        }
        return prop;
      };
      function getShapeValueAtTime(frameNum) {
        if (!this._cachingAtTime) {
          this._cachingAtTime = {
            shapeValue: shapePool.clone(this.pv),
            lastIndex: 0,
            lastTime: initialDefaultFrame
          };
        }
        frameNum *= this.elem.globalData.frameRate;
        frameNum -= this.offsetTime;
        if (frameNum !== this._cachingAtTime.lastTime) {
          this._cachingAtTime.lastIndex = this._cachingAtTime.lastTime < frameNum ? this._caching.lastIndex : 0;
          this._cachingAtTime.lastTime = frameNum;
          this.interpolateShape(frameNum, this._cachingAtTime.shapeValue, this._cachingAtTime);
        }
        return this._cachingAtTime.shapeValue;
      }
      var ShapePropertyConstructorFunction = ShapePropertyFactory.getConstructorFunction();
      var KeyframedShapePropertyConstructorFunction = ShapePropertyFactory.getKeyframedConstructorFunction();
      function ShapeExpressions() {
      }
      ShapeExpressions.prototype = {
        vertices: function vertices(prop, time2) {
          if (this.k) {
            this.getValue();
          }
          var shapePath = this.v;
          if (time2 !== void 0) {
            shapePath = this.getValueAtTime(time2, 0);
          }
          var i;
          var len = shapePath._length;
          var vertices2 = shapePath[prop];
          var points = shapePath.v;
          var arr = createSizedArray(len);
          for (i = 0; i < len; i += 1) {
            if (prop === "i" || prop === "o") {
              arr[i] = [vertices2[i][0] - points[i][0], vertices2[i][1] - points[i][1]];
            } else {
              arr[i] = [vertices2[i][0], vertices2[i][1]];
            }
          }
          return arr;
        },
        points: function points(time2) {
          return this.vertices("v", time2);
        },
        inTangents: function inTangents(time2) {
          return this.vertices("i", time2);
        },
        outTangents: function outTangents(time2) {
          return this.vertices("o", time2);
        },
        isClosed: function isClosed() {
          return this.v.c;
        },
        pointOnPath: function pointOnPath(perc, time2) {
          var shapePath = this.v;
          if (time2 !== void 0) {
            shapePath = this.getValueAtTime(time2, 0);
          }
          if (!this._segmentsLength) {
            this._segmentsLength = bez.getSegmentsLength(shapePath);
          }
          var segmentsLength = this._segmentsLength;
          var lengths = segmentsLength.lengths;
          var lengthPos = segmentsLength.totalLength * perc;
          var i = 0;
          var len = lengths.length;
          var accumulatedLength = 0;
          var pt;
          while (i < len) {
            if (accumulatedLength + lengths[i].addedLength > lengthPos) {
              var initIndex = i;
              var endIndex = shapePath.c && i === len - 1 ? 0 : i + 1;
              var segmentPerc = (lengthPos - accumulatedLength) / lengths[i].addedLength;
              pt = bez.getPointInSegment(shapePath.v[initIndex], shapePath.v[endIndex], shapePath.o[initIndex], shapePath.i[endIndex], segmentPerc, lengths[i]);
              break;
            } else {
              accumulatedLength += lengths[i].addedLength;
            }
            i += 1;
          }
          if (!pt) {
            pt = shapePath.c ? [shapePath.v[0][0], shapePath.v[0][1]] : [shapePath.v[shapePath._length - 1][0], shapePath.v[shapePath._length - 1][1]];
          }
          return pt;
        },
        vectorOnPath: function vectorOnPath(perc, time2, vectorType) {
          if (perc == 1) {
            perc = this.v.c;
          } else if (perc == 0) {
            perc = 0.999;
          }
          var pt1 = this.pointOnPath(perc, time2);
          var pt2 = this.pointOnPath(perc + 1e-3, time2);
          var xLength = pt2[0] - pt1[0];
          var yLength = pt2[1] - pt1[1];
          var magnitude = Math.sqrt(Math.pow(xLength, 2) + Math.pow(yLength, 2));
          if (magnitude === 0) {
            return [0, 0];
          }
          var unitVector = vectorType === "tangent" ? [xLength / magnitude, yLength / magnitude] : [-yLength / magnitude, xLength / magnitude];
          return unitVector;
        },
        tangentOnPath: function tangentOnPath(perc, time2) {
          return this.vectorOnPath(perc, time2, "tangent");
        },
        normalOnPath: function normalOnPath(perc, time2) {
          return this.vectorOnPath(perc, time2, "normal");
        },
        setGroupProperty: expressionHelpers.setGroupProperty,
        getValueAtTime: expressionHelpers.getStaticValueAtTime
      };
      extendPrototype([ShapeExpressions], ShapePropertyConstructorFunction);
      extendPrototype([ShapeExpressions], KeyframedShapePropertyConstructorFunction);
      KeyframedShapePropertyConstructorFunction.prototype.getValueAtTime = getShapeValueAtTime;
      KeyframedShapePropertyConstructorFunction.prototype.initiateExpression = ExpressionManager.initiateExpression;
      var propertyGetShapeProp = ShapePropertyFactory.getShapeProp;
      ShapePropertyFactory.getShapeProp = function(elem2, data2, type, arr, trims) {
        var prop = propertyGetShapeProp(elem2, data2, type, arr, trims);
        prop.propertyIndex = data2.ix;
        prop.lock = false;
        if (type === 3) {
          expressionHelpers.searchExpressions(elem2, data2.pt, prop);
        } else if (type === 4) {
          expressionHelpers.searchExpressions(elem2, data2.ks, prop);
        }
        if (prop.k) {
          elem2.addDynamicProperty(prop);
        }
        return prop;
      };
    }
    function initialize$1() {
      addPropertyDecorator();
    }
    function addDecorator() {
      function searchExpressions() {
        if (this.data.d.x) {
          this.calculateExpression = ExpressionManager.initiateExpression.bind(this)(this.elem, this.data.d, this);
          this.addEffect(this.getExpressionValue.bind(this));
          return true;
        }
        return null;
      }
      TextProperty.prototype.getExpressionValue = function(currentValue, text2) {
        var newValue = this.calculateExpression(text2);
        if (currentValue.t !== newValue) {
          var newData = {};
          this.copyData(newData, currentValue);
          newData.t = newValue.toString();
          newData.__complete = false;
          return newData;
        }
        return currentValue;
      };
      TextProperty.prototype.searchProperty = function() {
        var isKeyframed = this.searchKeyframes();
        var hasExpressions = this.searchExpressions();
        this.kf = isKeyframed || hasExpressions;
        return this.kf;
      };
      TextProperty.prototype.searchExpressions = searchExpressions;
    }
    function initialize() {
      addDecorator();
    }
    function SVGComposableEffect() {
    }
    SVGComposableEffect.prototype = {
      createMergeNode: function createMergeNode(resultId, ins) {
        var feMerge = createNS("feMerge");
        feMerge.setAttribute("result", resultId);
        var feMergeNode;
        var i;
        for (i = 0; i < ins.length; i += 1) {
          feMergeNode = createNS("feMergeNode");
          feMergeNode.setAttribute("in", ins[i]);
          feMerge.appendChild(feMergeNode);
          feMerge.appendChild(feMergeNode);
        }
        return feMerge;
      }
    };
    var linearFilterValue = "0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0 0 0";
    function SVGTintFilter(filter, filterManager, elem2, id, source) {
      this.filterManager = filterManager;
      var feColorMatrix = createNS("feColorMatrix");
      feColorMatrix.setAttribute("type", "matrix");
      feColorMatrix.setAttribute("color-interpolation-filters", "linearRGB");
      feColorMatrix.setAttribute("values", linearFilterValue + " 1 0");
      this.linearFilter = feColorMatrix;
      feColorMatrix.setAttribute("result", id + "_tint_1");
      filter.appendChild(feColorMatrix);
      feColorMatrix = createNS("feColorMatrix");
      feColorMatrix.setAttribute("type", "matrix");
      feColorMatrix.setAttribute("color-interpolation-filters", "sRGB");
      feColorMatrix.setAttribute("values", "1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 1 0");
      feColorMatrix.setAttribute("result", id + "_tint_2");
      filter.appendChild(feColorMatrix);
      this.matrixFilter = feColorMatrix;
      var feMerge = this.createMergeNode(id, [source, id + "_tint_1", id + "_tint_2"]);
      filter.appendChild(feMerge);
    }
    extendPrototype([SVGComposableEffect], SVGTintFilter);
    SVGTintFilter.prototype.renderFrame = function(forceRender) {
      if (forceRender || this.filterManager._mdf) {
        var colorBlack = this.filterManager.effectElements[0].p.v;
        var colorWhite = this.filterManager.effectElements[1].p.v;
        var opacity = this.filterManager.effectElements[2].p.v / 100;
        this.linearFilter.setAttribute("values", linearFilterValue + " " + opacity + " 0");
        this.matrixFilter.setAttribute("values", colorWhite[0] - colorBlack[0] + " 0 0 0 " + colorBlack[0] + " " + (colorWhite[1] - colorBlack[1]) + " 0 0 0 " + colorBlack[1] + " " + (colorWhite[2] - colorBlack[2]) + " 0 0 0 " + colorBlack[2] + " 0 0 0 1 0");
      }
    };
    function SVGFillFilter(filter, filterManager, elem2, id) {
      this.filterManager = filterManager;
      var feColorMatrix = createNS("feColorMatrix");
      feColorMatrix.setAttribute("type", "matrix");
      feColorMatrix.setAttribute("color-interpolation-filters", "sRGB");
      feColorMatrix.setAttribute("values", "1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 1 0");
      feColorMatrix.setAttribute("result", id);
      filter.appendChild(feColorMatrix);
      this.matrixFilter = feColorMatrix;
    }
    SVGFillFilter.prototype.renderFrame = function(forceRender) {
      if (forceRender || this.filterManager._mdf) {
        var color = this.filterManager.effectElements[2].p.v;
        var opacity = this.filterManager.effectElements[6].p.v;
        this.matrixFilter.setAttribute("values", "0 0 0 0 " + color[0] + " 0 0 0 0 " + color[1] + " 0 0 0 0 " + color[2] + " 0 0 0 " + opacity + " 0");
      }
    };
    function SVGStrokeEffect(fil, filterManager, elem2) {
      this.initialized = false;
      this.filterManager = filterManager;
      this.elem = elem2;
      this.paths = [];
    }
    SVGStrokeEffect.prototype.initialize = function() {
      var elemChildren = this.elem.layerElement.children || this.elem.layerElement.childNodes;
      var path;
      var groupPath;
      var i;
      var len;
      if (this.filterManager.effectElements[1].p.v === 1) {
        len = this.elem.maskManager.masksProperties.length;
        i = 0;
      } else {
        i = this.filterManager.effectElements[0].p.v - 1;
        len = i + 1;
      }
      groupPath = createNS("g");
      groupPath.setAttribute("fill", "none");
      groupPath.setAttribute("stroke-linecap", "round");
      groupPath.setAttribute("stroke-dashoffset", 1);
      for (i; i < len; i += 1) {
        path = createNS("path");
        groupPath.appendChild(path);
        this.paths.push({
          p: path,
          m: i
        });
      }
      if (this.filterManager.effectElements[10].p.v === 3) {
        var mask2 = createNS("mask");
        var id = createElementID();
        mask2.setAttribute("id", id);
        mask2.setAttribute("mask-type", "alpha");
        mask2.appendChild(groupPath);
        this.elem.globalData.defs.appendChild(mask2);
        var g = createNS("g");
        g.setAttribute("mask", "url(" + getLocationHref() + "#" + id + ")");
        while (elemChildren[0]) {
          g.appendChild(elemChildren[0]);
        }
        this.elem.layerElement.appendChild(g);
        this.masker = mask2;
        groupPath.setAttribute("stroke", "#fff");
      } else if (this.filterManager.effectElements[10].p.v === 1 || this.filterManager.effectElements[10].p.v === 2) {
        if (this.filterManager.effectElements[10].p.v === 2) {
          elemChildren = this.elem.layerElement.children || this.elem.layerElement.childNodes;
          while (elemChildren.length) {
            this.elem.layerElement.removeChild(elemChildren[0]);
          }
        }
        this.elem.layerElement.appendChild(groupPath);
        this.elem.layerElement.removeAttribute("mask");
        groupPath.setAttribute("stroke", "#fff");
      }
      this.initialized = true;
      this.pathMasker = groupPath;
    };
    SVGStrokeEffect.prototype.renderFrame = function(forceRender) {
      if (!this.initialized) {
        this.initialize();
      }
      var i;
      var len = this.paths.length;
      var mask2;
      var path;
      for (i = 0; i < len; i += 1) {
        if (this.paths[i].m !== -1) {
          mask2 = this.elem.maskManager.viewData[this.paths[i].m];
          path = this.paths[i].p;
          if (forceRender || this.filterManager._mdf || mask2.prop._mdf) {
            path.setAttribute("d", mask2.lastPath);
          }
          if (forceRender || this.filterManager.effectElements[9].p._mdf || this.filterManager.effectElements[4].p._mdf || this.filterManager.effectElements[7].p._mdf || this.filterManager.effectElements[8].p._mdf || mask2.prop._mdf) {
            var dasharrayValue;
            if (this.filterManager.effectElements[7].p.v !== 0 || this.filterManager.effectElements[8].p.v !== 100) {
              var s = Math.min(this.filterManager.effectElements[7].p.v, this.filterManager.effectElements[8].p.v) * 0.01;
              var e = Math.max(this.filterManager.effectElements[7].p.v, this.filterManager.effectElements[8].p.v) * 0.01;
              var l = path.getTotalLength();
              dasharrayValue = "0 0 0 " + l * s + " ";
              var lineLength = l * (e - s);
              var segment = 1 + this.filterManager.effectElements[4].p.v * 2 * this.filterManager.effectElements[9].p.v * 0.01;
              var units = Math.floor(lineLength / segment);
              var j;
              for (j = 0; j < units; j += 1) {
                dasharrayValue += "1 " + this.filterManager.effectElements[4].p.v * 2 * this.filterManager.effectElements[9].p.v * 0.01 + " ";
              }
              dasharrayValue += "0 " + l * 10 + " 0 0";
            } else {
              dasharrayValue = "1 " + this.filterManager.effectElements[4].p.v * 2 * this.filterManager.effectElements[9].p.v * 0.01;
            }
            path.setAttribute("stroke-dasharray", dasharrayValue);
          }
        }
      }
      if (forceRender || this.filterManager.effectElements[4].p._mdf) {
        this.pathMasker.setAttribute("stroke-width", this.filterManager.effectElements[4].p.v * 2);
      }
      if (forceRender || this.filterManager.effectElements[6].p._mdf) {
        this.pathMasker.setAttribute("opacity", this.filterManager.effectElements[6].p.v);
      }
      if (this.filterManager.effectElements[10].p.v === 1 || this.filterManager.effectElements[10].p.v === 2) {
        if (forceRender || this.filterManager.effectElements[3].p._mdf) {
          var color = this.filterManager.effectElements[3].p.v;
          this.pathMasker.setAttribute("stroke", "rgb(" + bmFloor(color[0] * 255) + "," + bmFloor(color[1] * 255) + "," + bmFloor(color[2] * 255) + ")");
        }
      }
    };
    function SVGTritoneFilter(filter, filterManager, elem2, id) {
      this.filterManager = filterManager;
      var feColorMatrix = createNS("feColorMatrix");
      feColorMatrix.setAttribute("type", "matrix");
      feColorMatrix.setAttribute("color-interpolation-filters", "linearRGB");
      feColorMatrix.setAttribute("values", "0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0.3333 0.3333 0.3333 0 0 0 0 0 1 0");
      filter.appendChild(feColorMatrix);
      var feComponentTransfer = createNS("feComponentTransfer");
      feComponentTransfer.setAttribute("color-interpolation-filters", "sRGB");
      feComponentTransfer.setAttribute("result", id);
      this.matrixFilter = feComponentTransfer;
      var feFuncR = createNS("feFuncR");
      feFuncR.setAttribute("type", "table");
      feComponentTransfer.appendChild(feFuncR);
      this.feFuncR = feFuncR;
      var feFuncG = createNS("feFuncG");
      feFuncG.setAttribute("type", "table");
      feComponentTransfer.appendChild(feFuncG);
      this.feFuncG = feFuncG;
      var feFuncB = createNS("feFuncB");
      feFuncB.setAttribute("type", "table");
      feComponentTransfer.appendChild(feFuncB);
      this.feFuncB = feFuncB;
      filter.appendChild(feComponentTransfer);
    }
    SVGTritoneFilter.prototype.renderFrame = function(forceRender) {
      if (forceRender || this.filterManager._mdf) {
        var color1 = this.filterManager.effectElements[0].p.v;
        var color2 = this.filterManager.effectElements[1].p.v;
        var color3 = this.filterManager.effectElements[2].p.v;
        var tableR = color3[0] + " " + color2[0] + " " + color1[0];
        var tableG = color3[1] + " " + color2[1] + " " + color1[1];
        var tableB = color3[2] + " " + color2[2] + " " + color1[2];
        this.feFuncR.setAttribute("tableValues", tableR);
        this.feFuncG.setAttribute("tableValues", tableG);
        this.feFuncB.setAttribute("tableValues", tableB);
      }
    };
    function SVGProLevelsFilter(filter, filterManager, elem2, id) {
      this.filterManager = filterManager;
      var effectElements = this.filterManager.effectElements;
      var feComponentTransfer = createNS("feComponentTransfer");
      if (effectElements[10].p.k || effectElements[10].p.v !== 0 || effectElements[11].p.k || effectElements[11].p.v !== 1 || effectElements[12].p.k || effectElements[12].p.v !== 1 || effectElements[13].p.k || effectElements[13].p.v !== 0 || effectElements[14].p.k || effectElements[14].p.v !== 1) {
        this.feFuncR = this.createFeFunc("feFuncR", feComponentTransfer);
      }
      if (effectElements[17].p.k || effectElements[17].p.v !== 0 || effectElements[18].p.k || effectElements[18].p.v !== 1 || effectElements[19].p.k || effectElements[19].p.v !== 1 || effectElements[20].p.k || effectElements[20].p.v !== 0 || effectElements[21].p.k || effectElements[21].p.v !== 1) {
        this.feFuncG = this.createFeFunc("feFuncG", feComponentTransfer);
      }
      if (effectElements[24].p.k || effectElements[24].p.v !== 0 || effectElements[25].p.k || effectElements[25].p.v !== 1 || effectElements[26].p.k || effectElements[26].p.v !== 1 || effectElements[27].p.k || effectElements[27].p.v !== 0 || effectElements[28].p.k || effectElements[28].p.v !== 1) {
        this.feFuncB = this.createFeFunc("feFuncB", feComponentTransfer);
      }
      if (effectElements[31].p.k || effectElements[31].p.v !== 0 || effectElements[32].p.k || effectElements[32].p.v !== 1 || effectElements[33].p.k || effectElements[33].p.v !== 1 || effectElements[34].p.k || effectElements[34].p.v !== 0 || effectElements[35].p.k || effectElements[35].p.v !== 1) {
        this.feFuncA = this.createFeFunc("feFuncA", feComponentTransfer);
      }
      if (this.feFuncR || this.feFuncG || this.feFuncB || this.feFuncA) {
        feComponentTransfer.setAttribute("color-interpolation-filters", "sRGB");
        filter.appendChild(feComponentTransfer);
      }
      if (effectElements[3].p.k || effectElements[3].p.v !== 0 || effectElements[4].p.k || effectElements[4].p.v !== 1 || effectElements[5].p.k || effectElements[5].p.v !== 1 || effectElements[6].p.k || effectElements[6].p.v !== 0 || effectElements[7].p.k || effectElements[7].p.v !== 1) {
        feComponentTransfer = createNS("feComponentTransfer");
        feComponentTransfer.setAttribute("color-interpolation-filters", "sRGB");
        feComponentTransfer.setAttribute("result", id);
        filter.appendChild(feComponentTransfer);
        this.feFuncRComposed = this.createFeFunc("feFuncR", feComponentTransfer);
        this.feFuncGComposed = this.createFeFunc("feFuncG", feComponentTransfer);
        this.feFuncBComposed = this.createFeFunc("feFuncB", feComponentTransfer);
      }
    }
    SVGProLevelsFilter.prototype.createFeFunc = function(type, feComponentTransfer) {
      var feFunc = createNS(type);
      feFunc.setAttribute("type", "table");
      feComponentTransfer.appendChild(feFunc);
      return feFunc;
    };
    SVGProLevelsFilter.prototype.getTableValue = function(inputBlack, inputWhite, gamma, outputBlack, outputWhite) {
      var cnt = 0;
      var segments = 256;
      var perc;
      var min = Math.min(inputBlack, inputWhite);
      var max = Math.max(inputBlack, inputWhite);
      var table = Array.call(null, {
        length: segments
      });
      var colorValue;
      var pos = 0;
      var outputDelta = outputWhite - outputBlack;
      var inputDelta = inputWhite - inputBlack;
      while (cnt <= 256) {
        perc = cnt / 256;
        if (perc <= min) {
          colorValue = inputDelta < 0 ? outputWhite : outputBlack;
        } else if (perc >= max) {
          colorValue = inputDelta < 0 ? outputBlack : outputWhite;
        } else {
          colorValue = outputBlack + outputDelta * Math.pow((perc - inputBlack) / inputDelta, 1 / gamma);
        }
        table[pos] = colorValue;
        pos += 1;
        cnt += 256 / (segments - 1);
      }
      return table.join(" ");
    };
    SVGProLevelsFilter.prototype.renderFrame = function(forceRender) {
      if (forceRender || this.filterManager._mdf) {
        var val2;
        var effectElements = this.filterManager.effectElements;
        if (this.feFuncRComposed && (forceRender || effectElements[3].p._mdf || effectElements[4].p._mdf || effectElements[5].p._mdf || effectElements[6].p._mdf || effectElements[7].p._mdf)) {
          val2 = this.getTableValue(effectElements[3].p.v, effectElements[4].p.v, effectElements[5].p.v, effectElements[6].p.v, effectElements[7].p.v);
          this.feFuncRComposed.setAttribute("tableValues", val2);
          this.feFuncGComposed.setAttribute("tableValues", val2);
          this.feFuncBComposed.setAttribute("tableValues", val2);
        }
        if (this.feFuncR && (forceRender || effectElements[10].p._mdf || effectElements[11].p._mdf || effectElements[12].p._mdf || effectElements[13].p._mdf || effectElements[14].p._mdf)) {
          val2 = this.getTableValue(effectElements[10].p.v, effectElements[11].p.v, effectElements[12].p.v, effectElements[13].p.v, effectElements[14].p.v);
          this.feFuncR.setAttribute("tableValues", val2);
        }
        if (this.feFuncG && (forceRender || effectElements[17].p._mdf || effectElements[18].p._mdf || effectElements[19].p._mdf || effectElements[20].p._mdf || effectElements[21].p._mdf)) {
          val2 = this.getTableValue(effectElements[17].p.v, effectElements[18].p.v, effectElements[19].p.v, effectElements[20].p.v, effectElements[21].p.v);
          this.feFuncG.setAttribute("tableValues", val2);
        }
        if (this.feFuncB && (forceRender || effectElements[24].p._mdf || effectElements[25].p._mdf || effectElements[26].p._mdf || effectElements[27].p._mdf || effectElements[28].p._mdf)) {
          val2 = this.getTableValue(effectElements[24].p.v, effectElements[25].p.v, effectElements[26].p.v, effectElements[27].p.v, effectElements[28].p.v);
          this.feFuncB.setAttribute("tableValues", val2);
        }
        if (this.feFuncA && (forceRender || effectElements[31].p._mdf || effectElements[32].p._mdf || effectElements[33].p._mdf || effectElements[34].p._mdf || effectElements[35].p._mdf)) {
          val2 = this.getTableValue(effectElements[31].p.v, effectElements[32].p.v, effectElements[33].p.v, effectElements[34].p.v, effectElements[35].p.v);
          this.feFuncA.setAttribute("tableValues", val2);
        }
      }
    };
    function SVGDropShadowEffect(filter, filterManager, elem2, id, source) {
      var globalFilterSize = filterManager.container.globalData.renderConfig.filterSize;
      var filterSize = filterManager.data.fs || globalFilterSize;
      filter.setAttribute("x", filterSize.x || globalFilterSize.x);
      filter.setAttribute("y", filterSize.y || globalFilterSize.y);
      filter.setAttribute("width", filterSize.width || globalFilterSize.width);
      filter.setAttribute("height", filterSize.height || globalFilterSize.height);
      this.filterManager = filterManager;
      var feGaussianBlur = createNS("feGaussianBlur");
      feGaussianBlur.setAttribute("in", "SourceAlpha");
      feGaussianBlur.setAttribute("result", id + "_drop_shadow_1");
      feGaussianBlur.setAttribute("stdDeviation", "0");
      this.feGaussianBlur = feGaussianBlur;
      filter.appendChild(feGaussianBlur);
      var feOffset = createNS("feOffset");
      feOffset.setAttribute("dx", "25");
      feOffset.setAttribute("dy", "0");
      feOffset.setAttribute("in", id + "_drop_shadow_1");
      feOffset.setAttribute("result", id + "_drop_shadow_2");
      this.feOffset = feOffset;
      filter.appendChild(feOffset);
      var feFlood = createNS("feFlood");
      feFlood.setAttribute("flood-color", "#00ff00");
      feFlood.setAttribute("flood-opacity", "1");
      feFlood.setAttribute("result", id + "_drop_shadow_3");
      this.feFlood = feFlood;
      filter.appendChild(feFlood);
      var feComposite = createNS("feComposite");
      feComposite.setAttribute("in", id + "_drop_shadow_3");
      feComposite.setAttribute("in2", id + "_drop_shadow_2");
      feComposite.setAttribute("operator", "in");
      feComposite.setAttribute("result", id + "_drop_shadow_4");
      filter.appendChild(feComposite);
      var feMerge = this.createMergeNode(id, [id + "_drop_shadow_4", source]);
      filter.appendChild(feMerge);
    }
    extendPrototype([SVGComposableEffect], SVGDropShadowEffect);
    SVGDropShadowEffect.prototype.renderFrame = function(forceRender) {
      if (forceRender || this.filterManager._mdf) {
        if (forceRender || this.filterManager.effectElements[4].p._mdf) {
          this.feGaussianBlur.setAttribute("stdDeviation", this.filterManager.effectElements[4].p.v / 4);
        }
        if (forceRender || this.filterManager.effectElements[0].p._mdf) {
          var col = this.filterManager.effectElements[0].p.v;
          this.feFlood.setAttribute("flood-color", rgbToHex(Math.round(col[0] * 255), Math.round(col[1] * 255), Math.round(col[2] * 255)));
        }
        if (forceRender || this.filterManager.effectElements[1].p._mdf) {
          this.feFlood.setAttribute("flood-opacity", this.filterManager.effectElements[1].p.v / 255);
        }
        if (forceRender || this.filterManager.effectElements[2].p._mdf || this.filterManager.effectElements[3].p._mdf) {
          var distance = this.filterManager.effectElements[3].p.v;
          var angle = (this.filterManager.effectElements[2].p.v - 90) * degToRads;
          var x = distance * Math.cos(angle);
          var y = distance * Math.sin(angle);
          this.feOffset.setAttribute("dx", x);
          this.feOffset.setAttribute("dy", y);
        }
      }
    };
    var _svgMatteSymbols = [];
    function SVGMatte3Effect(filterElem, filterManager, elem2) {
      this.initialized = false;
      this.filterManager = filterManager;
      this.filterElem = filterElem;
      this.elem = elem2;
      elem2.matteElement = createNS("g");
      elem2.matteElement.appendChild(elem2.layerElement);
      elem2.matteElement.appendChild(elem2.transformedElement);
      elem2.baseElement = elem2.matteElement;
    }
    SVGMatte3Effect.prototype.findSymbol = function(mask2) {
      var i = 0;
      var len = _svgMatteSymbols.length;
      while (i < len) {
        if (_svgMatteSymbols[i] === mask2) {
          return _svgMatteSymbols[i];
        }
        i += 1;
      }
      return null;
    };
    SVGMatte3Effect.prototype.replaceInParent = function(mask2, symbolId) {
      var parentNode = mask2.layerElement.parentNode;
      if (!parentNode) {
        return;
      }
      var children = parentNode.children;
      var i = 0;
      var len = children.length;
      while (i < len) {
        if (children[i] === mask2.layerElement) {
          break;
        }
        i += 1;
      }
      var nextChild;
      if (i <= len - 2) {
        nextChild = children[i + 1];
      }
      var useElem = createNS("use");
      useElem.setAttribute("href", "#" + symbolId);
      if (nextChild) {
        parentNode.insertBefore(useElem, nextChild);
      } else {
        parentNode.appendChild(useElem);
      }
    };
    SVGMatte3Effect.prototype.setElementAsMask = function(elem2, mask2) {
      if (!this.findSymbol(mask2)) {
        var symbolId = createElementID();
        var masker = createNS("mask");
        masker.setAttribute("id", mask2.layerId);
        masker.setAttribute("mask-type", "alpha");
        _svgMatteSymbols.push(mask2);
        var defs = elem2.globalData.defs;
        defs.appendChild(masker);
        var symbol = createNS("symbol");
        symbol.setAttribute("id", symbolId);
        this.replaceInParent(mask2, symbolId);
        symbol.appendChild(mask2.layerElement);
        defs.appendChild(symbol);
        var useElem = createNS("use");
        useElem.setAttribute("href", "#" + symbolId);
        masker.appendChild(useElem);
        mask2.data.hd = false;
        mask2.show();
      }
      elem2.setMatte(mask2.layerId);
    };
    SVGMatte3Effect.prototype.initialize = function() {
      var ind = this.filterManager.effectElements[0].p.v;
      var elements = this.elem.comp.elements;
      var i = 0;
      var len = elements.length;
      while (i < len) {
        if (elements[i] && elements[i].data.ind === ind) {
          this.setElementAsMask(this.elem, elements[i]);
        }
        i += 1;
      }
      this.initialized = true;
    };
    SVGMatte3Effect.prototype.renderFrame = function() {
      if (!this.initialized) {
        this.initialize();
      }
    };
    function SVGGaussianBlurEffect(filter, filterManager, elem2, id) {
      filter.setAttribute("x", "-100%");
      filter.setAttribute("y", "-100%");
      filter.setAttribute("width", "300%");
      filter.setAttribute("height", "300%");
      this.filterManager = filterManager;
      var feGaussianBlur = createNS("feGaussianBlur");
      feGaussianBlur.setAttribute("result", id);
      filter.appendChild(feGaussianBlur);
      this.feGaussianBlur = feGaussianBlur;
    }
    SVGGaussianBlurEffect.prototype.renderFrame = function(forceRender) {
      if (forceRender || this.filterManager._mdf) {
        var kBlurrinessToSigma = 0.3;
        var sigma = this.filterManager.effectElements[0].p.v * kBlurrinessToSigma;
        var dimensions = this.filterManager.effectElements[1].p.v;
        var sigmaX = dimensions == 3 ? 0 : sigma;
        var sigmaY = dimensions == 2 ? 0 : sigma;
        this.feGaussianBlur.setAttribute("stdDeviation", sigmaX + " " + sigmaY);
        var edgeMode = this.filterManager.effectElements[2].p.v == 1 ? "wrap" : "duplicate";
        this.feGaussianBlur.setAttribute("edgeMode", edgeMode);
      }
    };
    function TransformEffect() {
    }
    TransformEffect.prototype.init = function(effectsManager) {
      this.effectsManager = effectsManager;
      this.type = effectTypes.TRANSFORM_EFFECT;
      this.matrix = new Matrix();
      this.opacity = -1;
      this._mdf = false;
      this._opMdf = false;
    };
    TransformEffect.prototype.renderFrame = function(forceFrame) {
      this._opMdf = false;
      this._mdf = false;
      if (forceFrame || this.effectsManager._mdf) {
        var effectElements = this.effectsManager.effectElements;
        var anchor = effectElements[0].p.v;
        var position2 = effectElements[1].p.v;
        var isUniformScale = effectElements[2].p.v === 1;
        var scaleHeight = effectElements[3].p.v;
        var scaleWidth = isUniformScale ? scaleHeight : effectElements[4].p.v;
        var skew = effectElements[5].p.v;
        var skewAxis = effectElements[6].p.v;
        var rotation2 = effectElements[7].p.v;
        this.matrix.reset();
        this.matrix.translate(-anchor[0], -anchor[1], anchor[2]);
        this.matrix.scale(scaleWidth * 0.01, scaleHeight * 0.01, 1);
        this.matrix.rotate(-rotation2 * degToRads);
        this.matrix.skewFromAxis(-skew * degToRads, (skewAxis + 90) * degToRads);
        this.matrix.translate(position2[0], position2[1], 0);
        this._mdf = true;
        if (this.opacity !== effectElements[8].p.v) {
          this.opacity = effectElements[8].p.v;
          this._opMdf = true;
        }
      }
    };
    function SVGTransformEffect(_, filterManager) {
      this.init(filterManager);
    }
    extendPrototype([TransformEffect], SVGTransformEffect);
    function CVTransformEffect(effectsManager) {
      this.init(effectsManager);
    }
    extendPrototype([TransformEffect], CVTransformEffect);
    registerRenderer("canvas", CanvasRenderer);
    registerRenderer("html", HybridRenderer);
    registerRenderer("svg", SVGRenderer);
    ShapeModifiers.registerModifier("tm", TrimModifier);
    ShapeModifiers.registerModifier("pb", PuckerAndBloatModifier);
    ShapeModifiers.registerModifier("rp", RepeaterModifier);
    ShapeModifiers.registerModifier("rd", RoundCornersModifier);
    ShapeModifiers.registerModifier("zz", ZigZagModifier);
    ShapeModifiers.registerModifier("op", OffsetPathModifier);
    setExpressionsPlugin(Expressions);
    setExpressionInterfaces(getInterface);
    initialize$1();
    initialize();
    registerEffect$1(20, SVGTintFilter, true);
    registerEffect$1(21, SVGFillFilter, true);
    registerEffect$1(22, SVGStrokeEffect, false);
    registerEffect$1(23, SVGTritoneFilter, true);
    registerEffect$1(24, SVGProLevelsFilter, true);
    registerEffect$1(25, SVGDropShadowEffect, true);
    registerEffect$1(28, SVGMatte3Effect, false);
    registerEffect$1(29, SVGGaussianBlurEffect, true);
    registerEffect$1(35, SVGTransformEffect, false);
    registerEffect(35, CVTransformEffect);
    return lottie;
  });
})(lottie$1, lottie$1.exports);
var lottieExports = lottie$1.exports;
const lottie = /* @__PURE__ */ getDefaultExportFromCjs(lottieExports);
const LottieAnimation$1 = ({
  lottieFile,
  altText
}) => {
  const animationContainer = useRef(null);
  useEffect(() => {
    if (!animationContainer.current) {
      return;
    }
    const animation = lottie.loadAnimation({
      container: animationContainer.current,
      animationData: lottieFile,
      renderer: "svg",
      loop: true,
      autoplay: true
    });
    return () => {
      animation.destroy();
    };
  }, [lottieFile]);
  return /* @__PURE__ */ jsx(Box, { ref: animationContainer, role: "img", "aria-label": altText });
};
const LottieAnimation$2 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  LottieAnimation: LottieAnimation$1
}, Symbol.toStringTag, { value: "Module" }));
const assets$a = [
  {
    id: "8",
    layers: [
      {
        ind: 7,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 20
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "15",
    layers: [
      {
        ind: 14,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                18,
                18
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                36,
                36
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.23,
                            0.14
                          ],
                          [
                            0.12,
                            0.24
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.14,
                            0.23
                          ],
                          [
                            0,
                            0.27
                          ],
                          [
                            -0.14,
                            0.23
                          ],
                          [
                            -0.24,
                            0.12
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.5,
                            -0.99
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.14,
                            -0.23
                          ],
                          [
                            0,
                            -0.27
                          ],
                          [
                            0.14,
                            -0.23
                          ],
                          [
                            0.25,
                            -0.12
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.23,
                            -0.14
                          ],
                          [
                            0.27,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.27,
                            0
                          ],
                          [
                            -0.23,
                            -0.14
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.24,
                            -0.12
                          ],
                          [
                            -0.14,
                            -0.23
                          ],
                          [
                            0,
                            -0.27
                          ],
                          [
                            0.14,
                            -0.23
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.5,
                            -0.99
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.25,
                            0.12
                          ],
                          [
                            0.14,
                            0.23
                          ],
                          [
                            0,
                            0.27
                          ],
                          [
                            -0.14,
                            0.23
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.12,
                            0.24
                          ],
                          [
                            -0.23,
                            0.14
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            15.3,
                            27
                          ],
                          [
                            14.53,
                            26.78
                          ],
                          [
                            13.99,
                            26.19
                          ],
                          [
                            10.8,
                            19.8
                          ],
                          [
                            4.41,
                            16.61
                          ],
                          [
                            3.82,
                            16.07
                          ],
                          [
                            3.6,
                            15.3
                          ],
                          [
                            3.82,
                            14.53
                          ],
                          [
                            4.41,
                            13.99
                          ],
                          [
                            10.8,
                            10.8
                          ],
                          [
                            13.99,
                            4.41
                          ],
                          [
                            16.61,
                            4.41
                          ],
                          [
                            19.8,
                            10.8
                          ],
                          [
                            26.19,
                            13.99
                          ],
                          [
                            26.79,
                            14.53
                          ],
                          [
                            27,
                            15.3
                          ],
                          [
                            26.79,
                            16.07
                          ],
                          [
                            26.19,
                            16.61
                          ],
                          [
                            19.8,
                            19.8
                          ],
                          [
                            16.61,
                            26.19
                          ],
                          [
                            16.07,
                            26.78
                          ],
                          [
                            15.3,
                            27
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.14,
                            -0.29
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.29,
                            0.14
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.14,
                            0.28
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.28,
                            -0.14
                          ]
                        ],
                        o: [
                          [
                            0,
                            0
                          ],
                          [
                            0.28,
                            0.14
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.14,
                            -0.29
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.29,
                            -0.14
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.14,
                            0.28
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            8.33,
                            15.3
                          ],
                          [
                            12.54,
                            17.4
                          ],
                          [
                            13.2,
                            18.06
                          ],
                          [
                            15.3,
                            22.27
                          ],
                          [
                            17.4,
                            18.06
                          ],
                          [
                            18.06,
                            17.4
                          ],
                          [
                            22.27,
                            15.3
                          ],
                          [
                            18.06,
                            13.2
                          ],
                          [
                            17.4,
                            12.54
                          ],
                          [
                            15.3,
                            8.33
                          ],
                          [
                            13.2,
                            12.54
                          ],
                          [
                            12.54,
                            13.2
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        o: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            3.58,
                            3.47
                          ],
                          [
                            5.04,
                            0.55
                          ],
                          [
                            6.5,
                            3.47
                          ],
                          [
                            9.43,
                            4.93
                          ],
                          [
                            6.5,
                            6.39
                          ],
                          [
                            5.04,
                            9.32
                          ],
                          [
                            3.58,
                            6.39
                          ],
                          [
                            0.65,
                            4.93
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "gf",
                    e: {
                      a: 0,
                      k: [
                        24.83,
                        26.8
                      ]
                    },
                    g: {
                      p: 2,
                      k: {
                        a: 0,
                        k: [
                          0,
                          0.43,
                          0.34,
                          0.9,
                          1,
                          0.9,
                          0.01,
                          0.47,
                          0,
                          1,
                          1,
                          1
                        ]
                      }
                    },
                    t: 1,
                    o: {
                      a: 0,
                      k: 100
                    },
                    s: {
                      a: 0,
                      k: [
                        0.95,
                        24.79
                      ]
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "18",
    layers: [
      {
        ind: 17,
        ty: 0,
        parent: 13,
        ks: {},
        w: 36,
        h: 36,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "15"
      },
      {
        ind: 13,
        ty: 3,
        ks: {
          s: {
            a: 0,
            k: [
              55.56,
              55.56
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "24",
    layers: [
      {
        ind: 23,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26,
                26
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                52,
                52
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: false,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            13.26,
                            0
                          ],
                          [
                            0,
                            13.26
                          ],
                          [
                            -13.26,
                            0
                          ],
                          [
                            0,
                            -13.26
                          ]
                        ],
                        o: [
                          [
                            0,
                            13.26
                          ],
                          [
                            -13.26,
                            0
                          ],
                          [
                            0,
                            -13.26
                          ],
                          [
                            13.26,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            50,
                            26
                          ],
                          [
                            26,
                            50
                          ],
                          [
                            2,
                            26
                          ],
                          [
                            26,
                            2
                          ],
                          [
                            50,
                            26
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "gs",
                    o: {
                      a: 0,
                      k: 100
                    },
                    e: {
                      a: 0,
                      k: [
                        47.71,
                        51.65
                      ]
                    },
                    g: {
                      p: 2,
                      k: {
                        a: 0,
                        k: [
                          0,
                          0.43,
                          0.34,
                          0.9,
                          1,
                          0.9,
                          0.01,
                          0.47,
                          0,
                          1,
                          1,
                          1
                        ]
                      }
                    },
                    t: 1,
                    a: {
                      a: 0,
                      k: 0
                    },
                    h: {
                      a: 0,
                      k: 0
                    },
                    s: {
                      a: 0,
                      k: [
                        0.58,
                        47.67
                      ]
                    },
                    lc: 1,
                    lj: 1,
                    ml: 4,
                    w: {
                      a: 0,
                      k: 4
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    },
                    s: {
                      a: 0,
                      k: [
                        75,
                        75
                      ]
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "27",
    layers: [
      {
        ind: 26,
        ty: 0,
        parent: 22,
        ks: {},
        w: 52,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "24"
      },
      {
        ind: 22,
        ty: 3,
        ks: {
          s: {
            a: 0,
            k: [
              138.46,
              138.46
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "36",
    layers: [
      {
        ind: 35,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "39",
    layers: [
      {
        ind: 38,
        ty: 0,
        parent: 34,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "36"
      },
      {
        ind: 34,
        ty: 3,
        parent: 33,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 33,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "45",
    layers: [
      {
        ind: 41,
        ty: 0,
        parent: 32,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "39"
      },
      {
        ind: 32,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 43,
        ty: 4,
        parent: 42,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 42,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 44,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "54",
    layers: [
      {
        ind: 53,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "57",
    layers: [
      {
        ind: 56,
        ty: 0,
        parent: 52,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "54"
      },
      {
        ind: 52,
        ty: 3,
        parent: 51,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 51,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "63",
    layers: [
      {
        ind: 59,
        ty: 0,
        parent: 50,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "57"
      },
      {
        ind: 50,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 61,
        ty: 4,
        parent: 60,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 60,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 62,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "72",
    layers: [
      {
        ind: 71,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "75",
    layers: [
      {
        ind: 74,
        ty: 0,
        parent: 70,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "72"
      },
      {
        ind: 70,
        ty: 3,
        parent: 69,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 69,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "81",
    layers: [
      {
        ind: 77,
        ty: 0,
        parent: 68,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "75"
      },
      {
        ind: 68,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 79,
        ty: 4,
        parent: 78,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 78,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 80,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "90",
    layers: [
      {
        ind: 89,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "93",
    layers: [
      {
        ind: 92,
        ty: 0,
        parent: 88,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "90"
      },
      {
        ind: 88,
        ty: 3,
        parent: 87,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 87,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "99",
    layers: [
      {
        ind: 95,
        ty: 0,
        parent: 86,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "93"
      },
      {
        ind: 86,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 97,
        ty: 4,
        parent: 96,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 96,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 98,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "108",
    layers: [
      {
        ind: 107,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "111",
    layers: [
      {
        ind: 110,
        ty: 0,
        parent: 106,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "108"
      },
      {
        ind: 106,
        ty: 3,
        parent: 105,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 105,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "117",
    layers: [
      {
        ind: 113,
        ty: 0,
        parent: 104,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "111"
      },
      {
        ind: 104,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 115,
        ty: 4,
        parent: 114,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 114,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 116,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "126",
    layers: [
      {
        ind: 125,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "129",
    layers: [
      {
        ind: 128,
        ty: 0,
        parent: 124,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "126"
      },
      {
        ind: 124,
        ty: 3,
        parent: 123,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 123,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "135",
    layers: [
      {
        ind: 131,
        ty: 0,
        parent: 122,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "129"
      },
      {
        ind: 122,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 133,
        ty: 4,
        parent: 132,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 132,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 134,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "144",
    layers: [
      {
        ind: 143,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "147",
    layers: [
      {
        ind: 146,
        ty: 0,
        parent: 142,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "144"
      },
      {
        ind: 142,
        ty: 3,
        parent: 141,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 141,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "153",
    layers: [
      {
        ind: 149,
        ty: 0,
        parent: 140,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "147"
      },
      {
        ind: 140,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 151,
        ty: 4,
        parent: 150,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 150,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 152,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "162",
    layers: [
      {
        ind: 161,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "165",
    layers: [
      {
        ind: 164,
        ty: 0,
        parent: 160,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "162"
      },
      {
        ind: 160,
        ty: 3,
        parent: 159,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 159,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "171",
    layers: [
      {
        ind: 167,
        ty: 0,
        parent: 158,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "165"
      },
      {
        ind: 158,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 169,
        ty: 4,
        parent: 168,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 168,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 170,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "180",
    layers: [
      {
        ind: 179,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "183",
    layers: [
      {
        ind: 182,
        ty: 0,
        parent: 178,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "180"
      },
      {
        ind: 178,
        ty: 3,
        parent: 177,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 177,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "189",
    layers: [
      {
        ind: 185,
        ty: 0,
        parent: 176,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "183"
      },
      {
        ind: 176,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 187,
        ty: 4,
        parent: 186,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 186,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 188,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "198",
    layers: [
      {
        ind: 197,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "201",
    layers: [
      {
        ind: 200,
        ty: 0,
        parent: 196,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "198"
      },
      {
        ind: 196,
        ty: 3,
        parent: 195,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 195,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "207",
    layers: [
      {
        ind: 203,
        ty: 0,
        parent: 194,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "201"
      },
      {
        ind: 194,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 205,
        ty: 4,
        parent: 204,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 204,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 206,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "216",
    layers: [
      {
        ind: 215,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "219",
    layers: [
      {
        ind: 218,
        ty: 0,
        parent: 214,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "216"
      },
      {
        ind: 214,
        ty: 3,
        parent: 213,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 213,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "225",
    layers: [
      {
        ind: 221,
        ty: 0,
        parent: 212,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "219"
      },
      {
        ind: 212,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 223,
        ty: 4,
        parent: 222,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 222,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 224,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "234",
    layers: [
      {
        ind: 233,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "237",
    layers: [
      {
        ind: 236,
        ty: 0,
        parent: 232,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "234"
      },
      {
        ind: 232,
        ty: 3,
        parent: 231,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 231,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "243",
    layers: [
      {
        ind: 239,
        ty: 0,
        parent: 230,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "237"
      },
      {
        ind: 230,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 241,
        ty: 4,
        parent: 240,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 240,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 242,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "252",
    layers: [
      {
        ind: 251,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "255",
    layers: [
      {
        ind: 254,
        ty: 0,
        parent: 250,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "252"
      },
      {
        ind: 250,
        ty: 3,
        parent: 249,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 249,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "261",
    layers: [
      {
        ind: 257,
        ty: 0,
        parent: 248,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "255"
      },
      {
        ind: 248,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 259,
        ty: 4,
        parent: 258,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 258,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 260,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "270",
    layers: [
      {
        ind: 269,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "273",
    layers: [
      {
        ind: 272,
        ty: 0,
        parent: 268,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "270"
      },
      {
        ind: 268,
        ty: 3,
        parent: 267,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 267,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "279",
    layers: [
      {
        ind: 275,
        ty: 0,
        parent: 266,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "273"
      },
      {
        ind: 266,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 277,
        ty: 4,
        parent: 276,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 276,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 278,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "288",
    layers: [
      {
        ind: 287,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "291",
    layers: [
      {
        ind: 290,
        ty: 0,
        parent: 286,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "288"
      },
      {
        ind: 286,
        ty: 3,
        parent: 285,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 285,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "297",
    layers: [
      {
        ind: 293,
        ty: 0,
        parent: 284,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "291"
      },
      {
        ind: 284,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 295,
        ty: 4,
        parent: 294,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 294,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 296,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "306",
    layers: [
      {
        ind: 305,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "309",
    layers: [
      {
        ind: 308,
        ty: 0,
        parent: 304,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "306"
      },
      {
        ind: 304,
        ty: 3,
        parent: 303,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 303,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "315",
    layers: [
      {
        ind: 311,
        ty: 0,
        parent: 302,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "309"
      },
      {
        ind: 302,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 313,
        ty: 4,
        parent: 312,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 312,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 314,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "324",
    layers: [
      {
        ind: 323,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "327",
    layers: [
      {
        ind: 326,
        ty: 0,
        parent: 322,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "324"
      },
      {
        ind: 322,
        ty: 3,
        parent: 321,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 321,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "333",
    layers: [
      {
        ind: 329,
        ty: 0,
        parent: 320,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "327"
      },
      {
        ind: 320,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 331,
        ty: 4,
        parent: 330,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 330,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 332,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "342",
    layers: [
      {
        ind: 341,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "345",
    layers: [
      {
        ind: 344,
        ty: 0,
        parent: 340,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "342"
      },
      {
        ind: 340,
        ty: 3,
        parent: 339,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 339,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "351",
    layers: [
      {
        ind: 347,
        ty: 0,
        parent: 338,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "345"
      },
      {
        ind: 338,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 349,
        ty: 4,
        parent: 348,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 348,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 350,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "360",
    layers: [
      {
        ind: 359,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "363",
    layers: [
      {
        ind: 362,
        ty: 0,
        parent: 358,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "360"
      },
      {
        ind: 358,
        ty: 3,
        parent: 357,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 357,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "369",
    layers: [
      {
        ind: 365,
        ty: 0,
        parent: 356,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "363"
      },
      {
        ind: 356,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 367,
        ty: 4,
        parent: 366,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 366,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 368,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "378",
    layers: [
      {
        ind: 377,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "381",
    layers: [
      {
        ind: 380,
        ty: 0,
        parent: 376,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "378"
      },
      {
        ind: 376,
        ty: 3,
        parent: 375,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 375,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "387",
    layers: [
      {
        ind: 383,
        ty: 0,
        parent: 374,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "381"
      },
      {
        ind: 374,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 385,
        ty: 4,
        parent: 384,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 384,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 386,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "396",
    layers: [
      {
        ind: 395,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "399",
    layers: [
      {
        ind: 398,
        ty: 0,
        parent: 394,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "396"
      },
      {
        ind: 394,
        ty: 3,
        parent: 393,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 393,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "405",
    layers: [
      {
        ind: 401,
        ty: 0,
        parent: 392,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "399"
      },
      {
        ind: 392,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 403,
        ty: 4,
        parent: 402,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 402,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 404,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "414",
    layers: [
      {
        ind: 413,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "417",
    layers: [
      {
        ind: 416,
        ty: 0,
        parent: 412,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "414"
      },
      {
        ind: 412,
        ty: 3,
        parent: 411,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 411,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "423",
    layers: [
      {
        ind: 419,
        ty: 0,
        parent: 410,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "417"
      },
      {
        ind: 410,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 421,
        ty: 4,
        parent: 420,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 420,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 422,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "432",
    layers: [
      {
        ind: 431,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "435",
    layers: [
      {
        ind: 434,
        ty: 0,
        parent: 430,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "432"
      },
      {
        ind: 430,
        ty: 3,
        parent: 429,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 429,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "441",
    layers: [
      {
        ind: 437,
        ty: 0,
        parent: 428,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "435"
      },
      {
        ind: 428,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 439,
        ty: 4,
        parent: 438,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 438,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 440,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "450",
    layers: [
      {
        ind: 449,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "453",
    layers: [
      {
        ind: 452,
        ty: 0,
        parent: 448,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "450"
      },
      {
        ind: 448,
        ty: 3,
        parent: 447,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 447,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "459",
    layers: [
      {
        ind: 455,
        ty: 0,
        parent: 446,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "453"
      },
      {
        ind: 446,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 457,
        ty: 4,
        parent: 456,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 456,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 458,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "468",
    layers: [
      {
        ind: 467,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "471",
    layers: [
      {
        ind: 470,
        ty: 0,
        parent: 466,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "468"
      },
      {
        ind: 466,
        ty: 3,
        parent: 465,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 465,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "477",
    layers: [
      {
        ind: 473,
        ty: 0,
        parent: 464,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "471"
      },
      {
        ind: 464,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 475,
        ty: 4,
        parent: 474,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 474,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 476,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "486",
    layers: [
      {
        ind: 485,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "489",
    layers: [
      {
        ind: 488,
        ty: 0,
        parent: 484,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "486"
      },
      {
        ind: 484,
        ty: 3,
        parent: 483,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 483,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "495",
    layers: [
      {
        ind: 491,
        ty: 0,
        parent: 482,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "489"
      },
      {
        ind: 482,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 493,
        ty: 4,
        parent: 492,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 492,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 494,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "504",
    layers: [
      {
        ind: 503,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "507",
    layers: [
      {
        ind: 506,
        ty: 0,
        parent: 502,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "504"
      },
      {
        ind: 502,
        ty: 3,
        parent: 501,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 501,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "513",
    layers: [
      {
        ind: 509,
        ty: 0,
        parent: 500,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "507"
      },
      {
        ind: 500,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 511,
        ty: 4,
        parent: 510,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 510,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 512,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "522",
    layers: [
      {
        ind: 521,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "525",
    layers: [
      {
        ind: 524,
        ty: 0,
        parent: 520,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "522"
      },
      {
        ind: 520,
        ty: 3,
        parent: 519,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 519,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "531",
    layers: [
      {
        ind: 527,
        ty: 0,
        parent: 518,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "525"
      },
      {
        ind: 518,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 529,
        ty: 4,
        parent: 528,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 528,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 530,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "540",
    layers: [
      {
        ind: 539,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "543",
    layers: [
      {
        ind: 542,
        ty: 0,
        parent: 538,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "540"
      },
      {
        ind: 538,
        ty: 3,
        parent: 537,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 537,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "549",
    layers: [
      {
        ind: 545,
        ty: 0,
        parent: 536,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "543"
      },
      {
        ind: 536,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 547,
        ty: 4,
        parent: 546,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 546,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 548,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "558",
    layers: [
      {
        ind: 557,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "561",
    layers: [
      {
        ind: 560,
        ty: 0,
        parent: 556,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "558"
      },
      {
        ind: 556,
        ty: 3,
        parent: 555,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 555,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "567",
    layers: [
      {
        ind: 563,
        ty: 0,
        parent: 554,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "561"
      },
      {
        ind: 554,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 565,
        ty: 4,
        parent: 564,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 564,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 566,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "576",
    layers: [
      {
        ind: 575,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "579",
    layers: [
      {
        ind: 578,
        ty: 0,
        parent: 574,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "576"
      },
      {
        ind: 574,
        ty: 3,
        parent: 573,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 573,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "585",
    layers: [
      {
        ind: 581,
        ty: 0,
        parent: 572,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "579"
      },
      {
        ind: 572,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 583,
        ty: 4,
        parent: 582,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 582,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 584,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "594",
    layers: [
      {
        ind: 593,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "597",
    layers: [
      {
        ind: 596,
        ty: 0,
        parent: 592,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "594"
      },
      {
        ind: 592,
        ty: 3,
        parent: 591,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 591,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "603",
    layers: [
      {
        ind: 599,
        ty: 0,
        parent: 590,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "597"
      },
      {
        ind: 590,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 601,
        ty: 4,
        parent: 600,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 600,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 602,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "612",
    layers: [
      {
        ind: 611,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "615",
    layers: [
      {
        ind: 614,
        ty: 0,
        parent: 610,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "612"
      },
      {
        ind: 610,
        ty: 3,
        parent: 609,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 609,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "621",
    layers: [
      {
        ind: 617,
        ty: 0,
        parent: 608,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "615"
      },
      {
        ind: 608,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 619,
        ty: 4,
        parent: 618,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 618,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 620,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "630",
    layers: [
      {
        ind: 629,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "633",
    layers: [
      {
        ind: 632,
        ty: 0,
        parent: 628,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "630"
      },
      {
        ind: 628,
        ty: 3,
        parent: 627,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 627,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "639",
    layers: [
      {
        ind: 635,
        ty: 0,
        parent: 626,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "633"
      },
      {
        ind: 626,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 637,
        ty: 4,
        parent: 636,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 636,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 638,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "648",
    layers: [
      {
        ind: 647,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "651",
    layers: [
      {
        ind: 650,
        ty: 0,
        parent: 646,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "648"
      },
      {
        ind: 646,
        ty: 3,
        parent: 645,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 645,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "657",
    layers: [
      {
        ind: 653,
        ty: 0,
        parent: 644,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "651"
      },
      {
        ind: 644,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 655,
        ty: 4,
        parent: 654,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 654,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 656,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "666",
    layers: [
      {
        ind: 665,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "669",
    layers: [
      {
        ind: 668,
        ty: 0,
        parent: 664,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "666"
      },
      {
        ind: 664,
        ty: 3,
        parent: 663,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 663,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "675",
    layers: [
      {
        ind: 671,
        ty: 0,
        parent: 662,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "669"
      },
      {
        ind: 662,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 673,
        ty: 4,
        parent: 672,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 672,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 674,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "684",
    layers: [
      {
        ind: 683,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "687",
    layers: [
      {
        ind: 686,
        ty: 0,
        parent: 682,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "684"
      },
      {
        ind: 682,
        ty: 3,
        parent: 681,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 681,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "693",
    layers: [
      {
        ind: 689,
        ty: 0,
        parent: 680,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "687"
      },
      {
        ind: 680,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 691,
        ty: 4,
        parent: 690,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 690,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 692,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "702",
    layers: [
      {
        ind: 701,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "705",
    layers: [
      {
        ind: 704,
        ty: 0,
        parent: 700,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "702"
      },
      {
        ind: 700,
        ty: 3,
        parent: 699,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 699,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "711",
    layers: [
      {
        ind: 707,
        ty: 0,
        parent: 698,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "705"
      },
      {
        ind: 698,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 709,
        ty: 4,
        parent: 708,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 708,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 710,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "720",
    layers: [
      {
        ind: 719,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "723",
    layers: [
      {
        ind: 722,
        ty: 0,
        parent: 718,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "720"
      },
      {
        ind: 718,
        ty: 3,
        parent: 717,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 717,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "729",
    layers: [
      {
        ind: 725,
        ty: 0,
        parent: 716,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "723"
      },
      {
        ind: 716,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 727,
        ty: 4,
        parent: 726,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 726,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 728,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "738",
    layers: [
      {
        ind: 737,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "741",
    layers: [
      {
        ind: 740,
        ty: 0,
        parent: 736,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "738"
      },
      {
        ind: 736,
        ty: 3,
        parent: 735,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 735,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "747",
    layers: [
      {
        ind: 743,
        ty: 0,
        parent: 734,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "741"
      },
      {
        ind: 734,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 745,
        ty: 4,
        parent: 744,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 744,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 746,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "756",
    layers: [
      {
        ind: 755,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "759",
    layers: [
      {
        ind: 758,
        ty: 0,
        parent: 754,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "756"
      },
      {
        ind: 754,
        ty: 3,
        parent: 753,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 753,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "765",
    layers: [
      {
        ind: 761,
        ty: 0,
        parent: 752,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "759"
      },
      {
        ind: 752,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 763,
        ty: 4,
        parent: 762,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 762,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 764,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "776",
    layers: [
      {
        ind: 775,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "779",
    layers: [
      {
        ind: 778,
        ty: 0,
        parent: 774,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "776"
      },
      {
        ind: 774,
        ty: 3,
        parent: 773,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 773,
        ty: 3,
        ks: {
          a: {
            a: 0,
            k: [
              7,
              9.5
            ]
          },
          p: {
            a: 0,
            k: [
              20,
              19.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 294,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.613,
                    0.613
                  ],
                  y: [
                    0.589,
                    0.589
                  ]
                },
                o: {
                  x: [
                    0.321,
                    0.321
                  ],
                  y: [
                    0.033,
                    0.033
                  ]
                }
              },
              {
                t: 297.882,
                s: [
                  138.6,
                  138.6
                ],
                i: {
                  x: [
                    0.591,
                    0.591
                  ],
                  y: [
                    0.993,
                    0.993
                  ]
                },
                o: {
                  x: [
                    0.319,
                    0.319
                  ],
                  y: [
                    0.678,
                    0.678
                  ]
                }
              },
              {
                t: 301.764,
                s: [
                  157.93,
                  157.93
                ],
                i: {
                  x: [
                    0.447,
                    0.447
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 309.522,
                s: [
                  148.74,
                  148.74
                ],
                i: {
                  x: [
                    0.442,
                    0.442
                  ],
                  y: [
                    0.994,
                    0.994
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 317.376,
                s: [
                  150.2,
                  150.2
                ],
                i: {
                  x: [
                    0.446,
                    0.446
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.281,
                    0.281
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 325.14,
                s: [
                  149.97,
                  149.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 336,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 491.4,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "785",
    layers: [
      {
        ind: 781,
        ty: 0,
        parent: 772,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "779"
      },
      {
        ind: 772,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 783,
        ty: 4,
        parent: 782,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 782,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 784,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "789",
    layers: [
      {
        ind: 771,
        ty: 4,
        parent: 770,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    26.024,
                    25.988
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    54.365,
                    54.292
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    26.024,
                    25.988
                  ]
                },
                r: {
                  a: 0,
                  k: 45
                },
                s: {
                  a: 0,
                  k: [
                    52.048,
                    51.975
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 294.6,
                      s: [
                        0,
                        0,
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 295.32072,
                      s: [
                        0.698,
                        8e-3,
                        0.365
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 296.04144,
                      s: [
                        1,
                        8e-3,
                        0.545
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 297.48288,
                      s: [
                        0.878,
                        8e-3,
                        0.459
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 298.94148,
                      s: [
                        0.906,
                        8e-3,
                        0.471
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 300.38292,
                      s: [
                        0.902,
                        8e-3,
                        0.471
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        0.902,
                        8e-3,
                        0.471
                      ],
                      h: 1
                    }
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 294.6,
                      s: [
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 295.32072,
                      s: [
                        1.544
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 296.04144,
                      s: [
                        2.317
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 297.48288,
                      s: [
                        1.95
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 298.94148,
                      s: [
                        2.008
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 300.38292,
                      s: [
                        1.999
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 302.4,
                      s: [
                        2
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        2
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 788,
        ty: 4,
        td: 1,
        parent: 770,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 787,
        ty: 0,
        tt: 1,
        parent: 770,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "785"
      },
      {
        ind: 770,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              2,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "793",
    layers: [
      {
        ind: 791,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              2,
              2
            ]
          },
          p: {
            a: 0,
            k: [
              38.583,
              38.583
            ]
          }
        },
        w: 56,
        h: 56,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "789"
      },
      {
        ind: 792,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                65.186,
                65.15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                130.372,
                130.299
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "801",
    layers: [
      {
        ind: 800,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "804",
    layers: [
      {
        ind: 803,
        ty: 0,
        parent: 799,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "801"
      },
      {
        ind: 799,
        ty: 3,
        parent: 798,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 798,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "810",
    layers: [
      {
        ind: 806,
        ty: 0,
        parent: 797,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "804"
      },
      {
        ind: 797,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 808,
        ty: 4,
        parent: 807,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 807,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 809,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "821",
    layers: [
      {
        ind: 820,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "824",
    layers: [
      {
        ind: 823,
        ty: 0,
        parent: 819,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "821"
      },
      {
        ind: 819,
        ty: 3,
        parent: 818,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 818,
        ty: 3,
        ks: {
          a: {
            a: 0,
            k: [
              7,
              9.5
            ]
          },
          p: {
            a: 0,
            k: [
              20,
              19.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 382.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.613,
                    0.613
                  ],
                  y: [
                    0.589,
                    0.589
                  ]
                },
                o: {
                  x: [
                    0.321,
                    0.321
                  ],
                  y: [
                    0.033,
                    0.033
                  ]
                }
              },
              {
                t: 386.682,
                s: [
                  138.6,
                  138.6
                ],
                i: {
                  x: [
                    0.591,
                    0.591
                  ],
                  y: [
                    0.993,
                    0.993
                  ]
                },
                o: {
                  x: [
                    0.319,
                    0.319
                  ],
                  y: [
                    0.678,
                    0.678
                  ]
                }
              },
              {
                t: 390.564,
                s: [
                  157.93,
                  157.93
                ],
                i: {
                  x: [
                    0.447,
                    0.447
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 398.322,
                s: [
                  148.74,
                  148.74
                ],
                i: {
                  x: [
                    0.442,
                    0.442
                  ],
                  y: [
                    0.994,
                    0.994
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 406.176,
                s: [
                  150.2,
                  150.2
                ],
                i: {
                  x: [
                    0.446,
                    0.446
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.281,
                    0.281
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 413.94,
                s: [
                  149.97,
                  149.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 424.8,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 491.4,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "830",
    layers: [
      {
        ind: 826,
        ty: 0,
        parent: 817,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "824"
      },
      {
        ind: 817,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 828,
        ty: 4,
        parent: 827,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 827,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 829,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "834",
    layers: [
      {
        ind: 816,
        ty: 4,
        parent: 815,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    26.024,
                    25.988
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    54.365,
                    54.292
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    26.024,
                    25.988
                  ]
                },
                r: {
                  a: 0,
                  k: 45
                },
                s: {
                  a: 0,
                  k: [
                    52.048,
                    51.975
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 382.8,
                      s: [
                        0,
                        0,
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 383.52072,
                      s: [
                        0.698,
                        8e-3,
                        0.365
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 384.24144,
                      s: [
                        1,
                        8e-3,
                        0.545
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 385.68288,
                      s: [
                        0.878,
                        8e-3,
                        0.459
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 387.14148,
                      s: [
                        0.906,
                        8e-3,
                        0.471
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 388.58292,
                      s: [
                        0.902,
                        8e-3,
                        0.471
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        0.902,
                        8e-3,
                        0.471
                      ],
                      h: 1
                    }
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 382.8,
                      s: [
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 383.52072,
                      s: [
                        1.544
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 384.24144,
                      s: [
                        2.317
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 385.68288,
                      s: [
                        1.95
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 387.14148,
                      s: [
                        2.008
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 388.58292,
                      s: [
                        1.999
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 390.6,
                      s: [
                        2
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        2
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 833,
        ty: 4,
        td: 1,
        parent: 815,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 832,
        ty: 0,
        tt: 1,
        parent: 815,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "830"
      },
      {
        ind: 815,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              2,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "838",
    layers: [
      {
        ind: 836,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              2,
              2
            ]
          },
          p: {
            a: 0,
            k: [
              38.583,
              38.583
            ]
          }
        },
        w: 56,
        h: 56,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "834"
      },
      {
        ind: 837,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                65.186,
                65.15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                130.372,
                130.299
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "846",
    layers: [
      {
        ind: 845,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "849",
    layers: [
      {
        ind: 848,
        ty: 0,
        parent: 844,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "846"
      },
      {
        ind: 844,
        ty: 3,
        parent: 843,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 843,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "855",
    layers: [
      {
        ind: 851,
        ty: 0,
        parent: 842,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "849"
      },
      {
        ind: 842,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 853,
        ty: 4,
        parent: 852,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.284,
                1.282
              ]
            },
            s: {
              a: 0,
              k: [
                2.568,
                2.565
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 852,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              4.965,
              44.281
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 854,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "864",
    layers: [
      {
        ind: 863,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "867",
    layers: [
      {
        ind: 866,
        ty: 0,
        parent: 862,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "864"
      },
      {
        ind: 862,
        ty: 3,
        parent: 861,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 861,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              10
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "871",
    layers: [
      {
        ind: 869,
        ty: 0,
        parent: 860,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "867"
      },
      {
        ind: 860,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 870,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "882",
    layers: [
      {
        ind: 881,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "885",
    layers: [
      {
        ind: 884,
        ty: 0,
        parent: 880,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "882"
      },
      {
        ind: 880,
        ty: 3,
        parent: 879,
        ks: {
          s: {
            a: 0,
            k: [
              50,
              48.72
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 879,
        ty: 3,
        ks: {
          a: {
            a: 0,
            k: [
              7,
              9.5
            ]
          },
          p: {
            a: 0,
            k: [
              20,
              19.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 136.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.613,
                    0.613
                  ],
                  y: [
                    0.589,
                    0.589
                  ]
                },
                o: {
                  x: [
                    0.321,
                    0.321
                  ],
                  y: [
                    0.033,
                    0.033
                  ]
                }
              },
              {
                t: 140.082,
                s: [
                  138.6,
                  138.6
                ],
                i: {
                  x: [
                    0.591,
                    0.591
                  ],
                  y: [
                    0.993,
                    0.993
                  ]
                },
                o: {
                  x: [
                    0.319,
                    0.319
                  ],
                  y: [
                    0.678,
                    0.678
                  ]
                }
              },
              {
                t: 143.964,
                s: [
                  157.93,
                  157.93
                ],
                i: {
                  x: [
                    0.447,
                    0.447
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 151.722,
                s: [
                  148.74,
                  148.74
                ],
                i: {
                  x: [
                    0.442,
                    0.442
                  ],
                  y: [
                    0.994,
                    0.994
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 159.576,
                s: [
                  150.2,
                  150.2
                ],
                i: {
                  x: [
                    0.446,
                    0.446
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.281,
                    0.281
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 167.34,
                s: [
                  149.97,
                  149.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 178.2,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 491.4,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "889",
    layers: [
      {
        ind: 887,
        ty: 0,
        parent: 878,
        ks: {
          a: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          },
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        w: 39.0358,
        h: 38.9812,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "885"
      },
      {
        ind: 878,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.506,
              6.497
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 888,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "893",
    layers: [
      {
        ind: 877,
        ty: 4,
        parent: 876,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    26.024,
                    25.988
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    54.365,
                    54.292
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    26.024,
                    25.988
                  ]
                },
                r: {
                  a: 0,
                  k: 45
                },
                s: {
                  a: 0,
                  k: [
                    52.048,
                    51.975
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 137.58,
                      s: [
                        0,
                        0,
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 138.30072,
                      s: [
                        0.698,
                        8e-3,
                        0.365
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 139.02144,
                      s: [
                        1,
                        8e-3,
                        0.545
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 140.46288,
                      s: [
                        0.878,
                        8e-3,
                        0.459
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 141.92148,
                      s: [
                        0.906,
                        8e-3,
                        0.471
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 143.36292,
                      s: [
                        0.902,
                        8e-3,
                        0.471
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        0.902,
                        8e-3,
                        0.471
                      ],
                      h: 1
                    }
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 137.58,
                      s: [
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 138.30072,
                      s: [
                        1.544
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 139.02144,
                      s: [
                        2.317
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 140.46288,
                      s: [
                        1.95
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 141.92148,
                      s: [
                        2.008
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 143.36292,
                      s: [
                        1.999
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 145.38,
                      s: [
                        2
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        2
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 892,
        ty: 4,
        td: 1,
        parent: 876,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 891,
        ty: 0,
        tt: 1,
        parent: 876,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "889"
      },
      {
        ind: 876,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              2,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  },
  {
    id: "897",
    layers: [
      {
        ind: 895,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              2,
              2
            ]
          },
          p: {
            a: 0,
            k: [
              38.583,
              38.583
            ]
          }
        },
        w: 56,
        h: 56,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "893"
      },
      {
        ind: 896,
        ty: 4,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                65.186,
                65.15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                130.372,
                130.299
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "902",
    layers: [
      {
        ind: 10,
        ty: 0,
        parent: 6,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 48,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 421.8,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 469.8,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 491.4,
                s: [
                  100
                ],
                h: 1
              }
            ]
          }
        },
        w: 255,
        h: 238,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "8"
      },
      {
        ind: 20,
        ty: 0,
        parent: 12,
        ks: {},
        w: 20,
        h: 20,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "18"
      },
      {
        ind: 12,
        ty: 3,
        parent: 11,
        ks: {
          p: {
            a: 0,
            k: [
              -1,
              0
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 11,
        ty: 3,
        parent: 6,
        ks: {
          a: {
            a: 0,
            k: [
              10.5,
              10.5
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  130.5,
                  70.5
                ],
                i: {
                  x: [
                    0.628,
                    0.701
                  ],
                  y: [
                    0.691,
                    0.4
                  ]
                },
                o: {
                  x: [
                    0.102,
                    0.382
                  ],
                  y: [
                    0.112,
                    0.028
                  ]
                }
              },
              {
                t: 11.004,
                s: [
                  166.976,
                  83.02646568169894
                ],
                i: {
                  x: [
                    0.659,
                    0.65
                  ],
                  y: [
                    0.999,
                    0.6
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.338
                  ],
                  y: [
                    0.592,
                    0.247
                  ]
                }
              },
              {
                t: 22.008,
                s: [
                  183.505,
                  117.36798160652035
                ],
                h: 1
              },
              {
                t: 22.008,
                s: [
                  183.505,
                  117.36798160417504
                ],
                i: {
                  x: [
                    0.671,
                    1
                  ],
                  y: [
                    0.35,
                    1
                  ]
                },
                o: {
                  x: [
                    0.338,
                    0
                  ],
                  y: [
                    9e-3,
                    0
                  ]
                }
              },
              {
                t: 22.812,
                s: [
                  183.44395495906252,
                  120.239
                ],
                i: {
                  x: [
                    0.864,
                    0.708
                  ],
                  y: [
                    0.736,
                    0.766
                  ]
                },
                o: {
                  x: [
                    0.506,
                    0.397
                  ],
                  y: [
                    0.061,
                    0.433
                  ]
                }
              },
              {
                t: 33.864,
                s: [
                  169.731,
                  156.38977689780785
                ],
                i: {
                  x: [
                    0.394,
                    0.662
                  ],
                  y: [
                    0.306,
                    0.785
                  ]
                },
                o: {
                  x: [
                    9e-3,
                    0.338
                  ],
                  y: [
                    7e-3,
                    0.452
                  ]
                }
              },
              {
                t: 39.792,
                s: [
                  152.298,
                  168.0580194916278
                ],
                h: 1
              },
              {
                t: 39.792,
                s: [
                  152.298,
                  168.05801949181244
                ],
                i: {
                  x: [
                    0.913,
                    0.64
                  ],
                  y: [
                    0.91,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.474,
                    0.314
                  ],
                  y: [
                    0.455,
                    0.667
                  ]
                }
              },
              {
                t: 45.63,
                s: [
                  131.828808798705,
                  171.501
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.656,
                    0
                  ],
                  y: [
                    0.655,
                    0
                  ]
                }
              },
              {
                t: 45.72,
                s: [
                  131.5,
                  171.5
                ],
                i: {
                  x: [
                    0.668,
                    1
                  ],
                  y: [
                    0.668,
                    1
                  ]
                },
                o: {
                  x: [
                    0.334,
                    0
                  ],
                  y: [
                    0.335,
                    0
                  ]
                }
              },
              {
                t: 45.972,
                s: [
                  130.58566831161147,
                  171.507
                ],
                i: {
                  x: [
                    0.699,
                    0.671
                  ],
                  y: [
                    1,
                    0.46
                  ]
                },
                o: {
                  x: [
                    0.414,
                    0.373
                  ],
                  y: [
                    0.631,
                    0
                  ]
                }
              },
              {
                t: 68.322,
                s: [
                  77.492,
                  122.66727557293342
                ],
                i: {
                  x: [
                    0.663,
                    0.666
                  ],
                  y: [
                    0.338,
                    0.666
                  ]
                },
                o: {
                  x: [
                    0.33,
                    0.333
                  ],
                  y: [
                    8e-3,
                    0.332
                  ]
                }
              },
              {
                t: 68.706,
                s: [
                  77.51244314538765,
                  121.287
                ],
                i: {
                  x: [
                    0.615,
                    0.704
                  ],
                  y: [
                    0.292,
                    0.767
                  ]
                },
                o: {
                  x: [
                    0.276,
                    0.388
                  ],
                  y: [
                    0.021,
                    0.435
                  ]
                }
              },
              {
                t: 79.884,
                s: [
                  92.968,
                  85.34559800994347
                ],
                i: {
                  x: [
                    0.845,
                    0.665
                  ],
                  y: [
                    0.829,
                    0.778
                  ]
                },
                o: {
                  x: [
                    0.46,
                    0.339
                  ],
                  y: [
                    0.392,
                    0.445
                  ]
                }
              },
              {
                t: 85.662,
                s: [
                  110.23,
                  74.21652987150162
                ],
                h: 1
              },
              {
                t: 85.662,
                s: [
                  110.23,
                  74.21652987181322
                ],
                i: {
                  x: [
                    0.85,
                    0.646
                  ],
                  y: [
                    0.842,
                    0.966
                  ]
                },
                o: {
                  x: [
                    0.255,
                    0.319
                  ],
                  y: [
                    0.24,
                    0.634
                  ]
                }
              },
              {
                t: 91.44,
                s: [
                  130.5,
                  70.5
                ],
                i: {
                  x: [
                    0.628,
                    0.701
                  ],
                  y: [
                    0.691,
                    0.4
                  ]
                },
                o: {
                  x: [
                    0.102,
                    0.382
                  ],
                  y: [
                    0.112,
                    0.028
                  ]
                }
              },
              {
                t: 102.444,
                s: [
                  166.976,
                  83.02646568169894
                ],
                i: {
                  x: [
                    0.659,
                    0.65
                  ],
                  y: [
                    0.999,
                    0.6
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.338
                  ],
                  y: [
                    0.592,
                    0.247
                  ]
                }
              },
              {
                t: 113.448,
                s: [
                  183.505,
                  117.36798160652035
                ],
                h: 1
              },
              {
                t: 113.448,
                s: [
                  183.505,
                  117.36798160417504
                ],
                i: {
                  x: [
                    0.671,
                    1
                  ],
                  y: [
                    0.35,
                    1
                  ]
                },
                o: {
                  x: [
                    0.338,
                    0
                  ],
                  y: [
                    9e-3,
                    0
                  ]
                }
              },
              {
                t: 114.252,
                s: [
                  183.44395495906252,
                  120.239
                ],
                i: {
                  x: [
                    0.864,
                    0.708
                  ],
                  y: [
                    0.736,
                    0.766
                  ]
                },
                o: {
                  x: [
                    0.506,
                    0.397
                  ],
                  y: [
                    0.061,
                    0.433
                  ]
                }
              },
              {
                t: 125.304,
                s: [
                  169.731,
                  156.38977689780785
                ],
                i: {
                  x: [
                    0.394,
                    0.662
                  ],
                  y: [
                    0.306,
                    0.785
                  ]
                },
                o: {
                  x: [
                    9e-3,
                    0.338
                  ],
                  y: [
                    7e-3,
                    0.452
                  ]
                }
              },
              {
                t: 131.232,
                s: [
                  152.298,
                  168.0580194916278
                ],
                h: 1
              },
              {
                t: 131.232,
                s: [
                  152.298,
                  168.05801949181244
                ],
                i: {
                  x: [
                    0.913,
                    0.64
                  ],
                  y: [
                    0.91,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.474,
                    0.314
                  ],
                  y: [
                    0.455,
                    0.667
                  ]
                }
              },
              {
                t: 137.07,
                s: [
                  131.828808798705,
                  171.501
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.656,
                    0
                  ],
                  y: [
                    0.655,
                    0
                  ]
                }
              },
              {
                t: 137.16,
                s: [
                  131.5,
                  171.5
                ],
                i: {
                  x: [
                    0.668,
                    1
                  ],
                  y: [
                    0.668,
                    1
                  ]
                },
                o: {
                  x: [
                    0.334,
                    0
                  ],
                  y: [
                    0.335,
                    0
                  ]
                }
              },
              {
                t: 137.412,
                s: [
                  130.58581776871355,
                  171.507
                ],
                i: {
                  x: [
                    0.699,
                    0.675
                  ],
                  y: [
                    1,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.414,
                    0.375
                  ],
                  y: [
                    0.631,
                    0
                  ]
                }
              },
              {
                t: 159.768,
                s: [
                  77.492,
                  122.64580399064388
                ],
                i: {
                  x: [
                    0.661,
                    1
                  ],
                  y: [
                    0.338,
                    1
                  ]
                },
                o: {
                  x: [
                    0.328,
                    0
                  ],
                  y: [
                    0.013,
                    0
                  ]
                }
              },
              {
                t: 160.116,
                s: [
                  77.50909332863284,
                  121.396
                ],
                i: {
                  x: [
                    0.586,
                    0.702
                  ],
                  y: [
                    0.237,
                    0.765
                  ]
                },
                o: {
                  x: [
                    0.246,
                    0.387
                  ],
                  y: [
                    0.017,
                    0.432
                  ]
                }
              },
              {
                t: 171.294,
                s: [
                  92.895,
                  85.41513465180145
                ],
                i: {
                  x: [
                    0.869,
                    0.664
                  ],
                  y: [
                    0.856,
                    0.776
                  ]
                },
                o: {
                  x: [
                    0.47,
                    0.338
                  ],
                  y: [
                    0.399,
                    0.443
                  ]
                }
              },
              {
                t: 177.054,
                s: [
                  110.083,
                  74.28223656314911
                ],
                h: 1
              },
              {
                t: 177.054,
                s: [
                  110.083,
                  74.28223656385762
                ],
                i: {
                  x: [
                    0.876,
                    0.646
                  ],
                  y: [
                    0.87,
                    0.96
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.319
                  ],
                  y: [
                    0.253,
                    0.626
                  ]
                }
              },
              {
                t: 182.82,
                s: [
                  130.279,
                  70.504
                ],
                i: {
                  x: [
                    1,
                    0.668
                  ],
                  y: [
                    1,
                    0.504
                  ]
                },
                o: {
                  x: [
                    0,
                    0.335
                  ],
                  y: [
                    0,
                    0.17
                  ]
                }
              },
              {
                t: 182.88,
                s: [
                  130.72,
                  70.505
                ],
                i: {
                  x: [
                    0.636,
                    0.701
                  ],
                  y: [
                    0.698,
                    0.403
                  ]
                },
                o: {
                  x: [
                    0.121,
                    0.382
                  ],
                  y: [
                    0.133,
                    0.031
                  ]
                }
              },
              {
                t: 193.854,
                s: [
                  167.057,
                  83.09328554088542
                ],
                i: {
                  x: [
                    0.664,
                    0.651
                  ],
                  y: [
                    0.999,
                    0.601
                  ]
                },
                o: {
                  x: [
                    0.33,
                    0.338
                  ],
                  y: [
                    0.605,
                    0.248
                  ]
                }
              },
              {
                t: 204.822,
                s: [
                  183.505,
                  117.34747853662358
                ],
                h: 1
              },
              {
                t: 204.822,
                s: [
                  183.505,
                  117.3474785455572
                ],
                i: {
                  x: [
                    0.671,
                    1
                  ],
                  y: [
                    0.349,
                    1
                  ]
                },
                o: {
                  x: [
                    0.338,
                    0
                  ],
                  y: [
                    7e-3,
                    0
                  ]
                }
              },
              {
                t: 205.662,
                s: [
                  183.43869623942646,
                  120.346
                ],
                i: {
                  x: [
                    0.864,
                    0.708
                  ],
                  y: [
                    0.736,
                    0.765
                  ]
                },
                o: {
                  x: [
                    0.506,
                    0.396
                  ],
                  y: [
                    0.064,
                    0.432
                  ]
                }
              },
              {
                t: 216.684,
                s: [
                  169.732,
                  156.3920077845482
                ],
                i: {
                  x: [
                    0.394,
                    0.662
                  ],
                  y: [
                    0.307,
                    0.785
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.338
                  ],
                  y: [
                    9e-3,
                    0.451
                  ]
                }
              },
              {
                t: 222.612,
                s: [
                  152.299,
                  168.05697249919325
                ],
                h: 1
              },
              {
                t: 222.612,
                s: [
                  152.299,
                  168.05697250045432
                ],
                i: {
                  x: [
                    0.913,
                    0.64
                  ],
                  y: [
                    0.91,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.473,
                    0.314
                  ],
                  y: [
                    0.454,
                    0.666
                  ]
                }
              },
              {
                t: 228.45,
                s: [
                  131.82882641924147,
                  171.501
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.656,
                    0
                  ],
                  y: [
                    0.655,
                    0
                  ]
                }
              },
              {
                t: 228.54,
                s: [
                  131.5,
                  171.5
                ],
                i: {
                  x: [
                    0.668,
                    1
                  ],
                  y: [
                    0.668,
                    1
                  ]
                },
                o: {
                  x: [
                    0.334,
                    0
                  ],
                  y: [
                    0.335,
                    0
                  ]
                }
              },
              {
                t: 228.792,
                s: [
                  130.58581776871355,
                  171.507
                ],
                i: {
                  x: [
                    0.699,
                    0.675
                  ],
                  y: [
                    1,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.414,
                    0.375
                  ],
                  y: [
                    0.631,
                    0
                  ]
                }
              },
              {
                t: 251.148,
                s: [
                  77.492,
                  122.64580399064388
                ],
                i: {
                  x: [
                    0.661,
                    1
                  ],
                  y: [
                    0.338,
                    1
                  ]
                },
                o: {
                  x: [
                    0.328,
                    0
                  ],
                  y: [
                    0.013,
                    0
                  ]
                }
              },
              {
                t: 251.496,
                s: [
                  77.50909332863284,
                  121.396
                ],
                i: {
                  x: [
                    0.586,
                    0.702
                  ],
                  y: [
                    0.237,
                    0.765
                  ]
                },
                o: {
                  x: [
                    0.246,
                    0.387
                  ],
                  y: [
                    0.017,
                    0.432
                  ]
                }
              },
              {
                t: 262.674,
                s: [
                  92.895,
                  85.41513465180145
                ],
                i: {
                  x: [
                    0.869,
                    0.664
                  ],
                  y: [
                    0.856,
                    0.776
                  ]
                },
                o: {
                  x: [
                    0.47,
                    0.338
                  ],
                  y: [
                    0.399,
                    0.443
                  ]
                }
              },
              {
                t: 268.434,
                s: [
                  110.083,
                  74.28223656314911
                ],
                h: 1
              },
              {
                t: 268.434,
                s: [
                  110.083,
                  74.28223656385762
                ],
                i: {
                  x: [
                    0.876,
                    0.646
                  ],
                  y: [
                    0.87,
                    0.96
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.319
                  ],
                  y: [
                    0.253,
                    0.626
                  ]
                }
              },
              {
                t: 274.2,
                s: [
                  130.279,
                  70.504
                ],
                i: {
                  x: [
                    1,
                    0.668
                  ],
                  y: [
                    1,
                    0.504
                  ]
                },
                o: {
                  x: [
                    0,
                    0.335
                  ],
                  y: [
                    0,
                    0.17
                  ]
                }
              },
              {
                t: 274.26,
                s: [
                  130.72,
                  70.505
                ],
                i: {
                  x: [
                    0.636,
                    0.701
                  ],
                  y: [
                    0.698,
                    0.403
                  ]
                },
                o: {
                  x: [
                    0.121,
                    0.382
                  ],
                  y: [
                    0.133,
                    0.031
                  ]
                }
              },
              {
                t: 285.234,
                s: [
                  167.057,
                  83.09328554088542
                ],
                i: {
                  x: [
                    0.664,
                    0.651
                  ],
                  y: [
                    0.999,
                    0.601
                  ]
                },
                o: {
                  x: [
                    0.33,
                    0.338
                  ],
                  y: [
                    0.605,
                    0.248
                  ]
                }
              },
              {
                t: 296.202,
                s: [
                  183.505,
                  117.34747853662358
                ],
                h: 1
              },
              {
                t: 296.202,
                s: [
                  183.505,
                  117.3474785455572
                ],
                i: {
                  x: [
                    0.671,
                    1
                  ],
                  y: [
                    0.349,
                    1
                  ]
                },
                o: {
                  x: [
                    0.338,
                    0
                  ],
                  y: [
                    7e-3,
                    0
                  ]
                }
              },
              {
                t: 297.042,
                s: [
                  183.43869623942646,
                  120.346
                ],
                i: {
                  x: [
                    0.864,
                    0.708
                  ],
                  y: [
                    0.736,
                    0.765
                  ]
                },
                o: {
                  x: [
                    0.506,
                    0.396
                  ],
                  y: [
                    0.064,
                    0.432
                  ]
                }
              },
              {
                t: 308.064,
                s: [
                  169.732,
                  156.3920077845482
                ],
                i: {
                  x: [
                    0.394,
                    0.662
                  ],
                  y: [
                    0.307,
                    0.785
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.338
                  ],
                  y: [
                    9e-3,
                    0.451
                  ]
                }
              },
              {
                t: 313.992,
                s: [
                  152.299,
                  168.05697249919325
                ],
                h: 1
              },
              {
                t: 313.992,
                s: [
                  152.299,
                  168.05697250045432
                ],
                i: {
                  x: [
                    0.913,
                    0.64
                  ],
                  y: [
                    0.91,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.473,
                    0.314
                  ],
                  y: [
                    0.454,
                    0.666
                  ]
                }
              },
              {
                t: 319.83,
                s: [
                  131.82882641924147,
                  171.501
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.656,
                    0
                  ],
                  y: [
                    0.655,
                    0
                  ]
                }
              },
              {
                t: 319.92,
                s: [
                  131.5,
                  171.5
                ],
                i: {
                  x: [
                    0.668,
                    1
                  ],
                  y: [
                    0.668,
                    1
                  ]
                },
                o: {
                  x: [
                    0.334,
                    0
                  ],
                  y: [
                    0.335,
                    0
                  ]
                }
              },
              {
                t: 320.172,
                s: [
                  130.58582589792425,
                  171.507
                ],
                i: {
                  x: [
                    0.699,
                    0.682
                  ],
                  y: [
                    1,
                    0.478
                  ]
                },
                o: {
                  x: [
                    0.414,
                    0.379
                  ],
                  y: [
                    0.631,
                    0
                  ]
                }
              },
              {
                t: 342.528,
                s: [
                  77.492,
                  122.6454664644251
                ],
                i: {
                  x: [
                    0.662,
                    1
                  ],
                  y: [
                    0.342,
                    1
                  ]
                },
                o: {
                  x: [
                    0.329,
                    0
                  ],
                  y: [
                    0.015,
                    0
                  ]
                }
              },
              {
                t: 342.816,
                s: [
                  77.5037769056292,
                  121.613
                ],
                i: {
                  x: [
                    0.584,
                    0.698
                  ],
                  y: [
                    0.23,
                    0.762
                  ]
                },
                o: {
                  x: [
                    0.244,
                    0.382
                  ],
                  y: [
                    0.014,
                    0.425
                  ]
                }
              },
              {
                t: 353.994,
                s: [
                  92.743,
                  85.55521187888212
                ],
                i: {
                  x: [
                    0.909,
                    0.662
                  ],
                  y: [
                    0.899,
                    0.773
                  ]
                },
                o: {
                  x: [
                    0.485,
                    0.335
                  ],
                  y: [
                    0.412,
                    0.438
                  ]
                }
              },
              {
                t: 359.724,
                s: [
                  109.788,
                  74.40696675104218
                ],
                h: 1
              },
              {
                t: 359.724,
                s: [
                  109.788,
                  74.40696675091579
                ],
                i: {
                  x: [
                    0.912,
                    0.646
                  ],
                  y: [
                    0.908,
                    0.95
                  ]
                },
                o: {
                  x: [
                    0.29,
                    0.318
                  ],
                  y: [
                    0.272,
                    0.612
                  ]
                }
              },
              {
                t: 365.46,
                s: [
                  129.838,
                  70.514
                ],
                i: {
                  x: [
                    1,
                    0.666
                  ],
                  y: [
                    1,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0,
                    0.334
                  ],
                  y: [
                    0,
                    0.667
                  ]
                }
              },
              {
                t: 365.49,
                s: [
                  130.05783333346866,
                  70.514
                ],
                i: {
                  x: [
                    1,
                    0.669
                  ],
                  y: [
                    1,
                    0.339
                  ]
                },
                o: {
                  x: [
                    0,
                    0.336
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 365.64,
                s: [
                  131.157,
                  70.518
                ],
                i: {
                  x: [
                    0.652,
                    0.701
                  ],
                  y: [
                    0.712,
                    0.409
                  ]
                },
                o: {
                  x: [
                    0.169,
                    0.381
                  ],
                  y: [
                    0.186,
                    0.038
                  ]
                }
              },
              {
                t: 376.554,
                s: [
                  167.226,
                  83.22760609281917
                ],
                i: {
                  x: [
                    0.671,
                    0.652
                  ],
                  y: [
                    1,
                    0.603
                  ]
                },
                o: {
                  x: [
                    0.341,
                    0.339
                  ],
                  y: [
                    0.625,
                    0.249
                  ]
                }
              },
              {
                t: 387.468,
                s: [
                  183.505,
                  117.36395953380301
                ],
                h: 1
              },
              {
                t: 387.468,
                s: [
                  183.505,
                  117.36395954090003
                ],
                i: {
                  x: [
                    0.672,
                    1
                  ],
                  y: [
                    0.353,
                    1
                  ]
                },
                o: {
                  x: [
                    0.338,
                    0
                  ],
                  y: [
                    0.011,
                    0
                  ]
                }
              },
              {
                t: 388.362,
                s: [
                  183.429169678823,
                  120.553
                ],
                i: {
                  x: [
                    0.863,
                    0.706
                  ],
                  y: [
                    0.734,
                    0.764
                  ]
                },
                o: {
                  x: [
                    0.505,
                    0.394
                  ],
                  y: [
                    0.068,
                    0.43
                  ]
                }
              },
              {
                t: 399.324,
                s: [
                  169.728,
                  156.39562211581838
                ],
                i: {
                  x: [
                    0.394,
                    0.661
                  ],
                  y: [
                    0.306,
                    0.784
                  ]
                },
                o: {
                  x: [
                    9e-3,
                    0.337
                  ],
                  y: [
                    7e-3,
                    0.45
                  ]
                }
              },
              {
                t: 405.252,
                s: [
                  152.296,
                  168.05542290552762
                ],
                h: 1
              },
              {
                t: 405.252,
                s: [
                  152.296,
                  168.05542290792036
                ],
                i: {
                  x: [
                    0.911,
                    0.639
                  ],
                  y: [
                    0.907,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.472,
                    0.313
                  ],
                  y: [
                    0.453,
                    0.664
                  ]
                }
              },
              {
                t: 411.084,
                s: [
                  131.85065524542,
                  171.501
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.655,
                    0
                  ],
                  y: [
                    0.654,
                    0
                  ]
                }
              },
              {
                t: 411.18,
                s: [
                  131.5,
                  171.5
                ],
                i: {
                  x: [
                    0.668,
                    1
                  ],
                  y: [
                    0.668,
                    1
                  ]
                },
                o: {
                  x: [
                    0.334,
                    0
                  ],
                  y: [
                    0.335,
                    0
                  ]
                }
              },
              {
                t: 411.432,
                s: [
                  130.58566831161147,
                  171.507
                ],
                i: {
                  x: [
                    0.699,
                    0.671
                  ],
                  y: [
                    1,
                    0.46
                  ]
                },
                o: {
                  x: [
                    0.414,
                    0.373
                  ],
                  y: [
                    0.631,
                    0
                  ]
                }
              },
              {
                t: 433.782,
                s: [
                  77.492,
                  122.66727557293342
                ],
                i: {
                  x: [
                    0.663,
                    0.666
                  ],
                  y: [
                    0.338,
                    0.666
                  ]
                },
                o: {
                  x: [
                    0.33,
                    0.333
                  ],
                  y: [
                    8e-3,
                    0.332
                  ]
                }
              },
              {
                t: 434.166,
                s: [
                  77.51244314538765,
                  121.287
                ],
                i: {
                  x: [
                    0.615,
                    0.704
                  ],
                  y: [
                    0.292,
                    0.767
                  ]
                },
                o: {
                  x: [
                    0.276,
                    0.388
                  ],
                  y: [
                    0.021,
                    0.435
                  ]
                }
              },
              {
                t: 445.344,
                s: [
                  92.968,
                  85.34559800994347
                ],
                i: {
                  x: [
                    0.845,
                    0.665
                  ],
                  y: [
                    0.829,
                    0.778
                  ]
                },
                o: {
                  x: [
                    0.46,
                    0.339
                  ],
                  y: [
                    0.392,
                    0.445
                  ]
                }
              },
              {
                t: 451.122,
                s: [
                  110.23,
                  74.21652987150162
                ],
                h: 1
              },
              {
                t: 451.122,
                s: [
                  110.23,
                  74.21652987181322
                ],
                i: {
                  x: [
                    0.85,
                    0.646
                  ],
                  y: [
                    0.842,
                    0.966
                  ]
                },
                o: {
                  x: [
                    0.255,
                    0.319
                  ],
                  y: [
                    0.24,
                    0.634
                  ]
                }
              },
              {
                t: 456.9,
                s: [
                  130.5,
                  70.5
                ],
                i: {
                  x: [
                    0.628,
                    0.661
                  ],
                  y: [
                    0.691,
                    0.321
                  ]
                },
                o: {
                  x: [
                    0.1,
                    0.348
                  ],
                  y: [
                    0.111,
                    0.026
                  ]
                }
              },
              {
                t: 467.904,
                s: [
                  166.974,
                  83.06395808541309
                ],
                i: {
                  x: [
                    0.662,
                    0.643
                  ],
                  y: [
                    0.78,
                    0.583
                  ]
                },
                o: {
                  x: [
                    0.328,
                    0.315
                  ],
                  y: [
                    0.43,
                    0.254
                  ]
                }
              },
              {
                t: 474.15,
                s: [
                  180.12450100787763,
                  100.801
                ],
                i: {
                  x: [
                    0.665,
                    0.66
                  ],
                  y: [
                    0.999,
                    0.651
                  ]
                },
                o: {
                  x: [
                    0.331,
                    0.324
                  ],
                  y: [
                    0.639,
                    0.309
                  ]
                }
              },
              {
                t: 478.902,
                s: [
                  183.505,
                  117.31372959469105
                ],
                i: {
                  x: [
                    0.863,
                    0.685
                  ],
                  y: [
                    0.715,
                    0.759
                  ]
                },
                o: {
                  x: [
                    0.506,
                    0.337
                  ],
                  y: [
                    0,
                    0.368
                  ]
                }
              },
              {
                t: 491.4,
                s: [
                  168.149,
                  158.029
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 96.84,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 99.54,
                s: [
                  -20,
                  -20
                ],
                i: {
                  x: [
                    0.25,
                    0.25
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 107.64,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 135.84,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 138.54,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.25,
                    0.25
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 146.64,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 256.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 258.9,
                s: [
                  -20,
                  -20
                ],
                i: {
                  x: [
                    0.25,
                    0.25
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 267,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 299.64,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 302.34,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.25,
                    0.25
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 310.44,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 342.24,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 344.94,
                s: [
                  -20,
                  -20
                ],
                i: {
                  x: [
                    0.25,
                    0.25
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 353.04,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 387.24,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 389.94,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.25,
                    0.25
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.2,
                    0.2
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 398.04,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 491.4,
                s: [
                  100,
                  100
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 29,
        ty: 0,
        parent: 21,
        ks: {},
        w: 72,
        h: 72,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "27"
      },
      {
        ind: 21,
        ty: 3,
        parent: 6,
        ks: {
          a: {
            a: 0,
            k: [
              36,
              36
            ]
          },
          p: {
            a: 0,
            k: [
              130,
              120
            ]
          },
          r: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 39.6,
                s: [
                  0
                ],
                i: {
                  x: 0,
                  y: 1
                },
                o: {
                  x: 0.5,
                  y: 0
                }
              },
              {
                t: 190.2,
                s: [
                  360
                ],
                i: {
                  x: 0,
                  y: 1
                },
                o: {
                  x: 0.5,
                  y: 0
                }
              },
              {
                t: 340.8,
                s: [
                  720
                ],
                i: {
                  x: 0,
                  y: 1
                },
                o: {
                  x: 0.5,
                  y: 0
                }
              },
              {
                t: 491.4,
                s: [
                  1080
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 88.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 136.2,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    0.613,
                    0.613
                  ],
                  y: [
                    0.589,
                    0.589
                  ]
                },
                o: {
                  x: [
                    0.321,
                    0.321
                  ],
                  y: [
                    0.033,
                    0.033
                  ]
                }
              },
              {
                t: 140.082,
                s: [
                  111.4,
                  111.4
                ],
                i: {
                  x: [
                    0.591,
                    0.591
                  ],
                  y: [
                    0.993,
                    0.993
                  ]
                },
                o: {
                  x: [
                    0.319,
                    0.319
                  ],
                  y: [
                    0.678,
                    0.678
                  ]
                }
              },
              {
                t: 143.964,
                s: [
                  92.07,
                  92.07
                ],
                i: {
                  x: [
                    0.447,
                    0.447
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 151.722,
                s: [
                  101.26,
                  101.26
                ],
                i: {
                  x: [
                    0.442,
                    0.442
                  ],
                  y: [
                    0.994,
                    0.994
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 159.576,
                s: [
                  99.8,
                  99.8
                ],
                i: {
                  x: [
                    0.446,
                    0.446
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.281,
                    0.281
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 167.34,
                s: [
                  100.03,
                  100.03
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 178.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 245.76,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 293.76,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    0.613,
                    0.613
                  ],
                  y: [
                    0.589,
                    0.589
                  ]
                },
                o: {
                  x: [
                    0.321,
                    0.321
                  ],
                  y: [
                    0.033,
                    0.033
                  ]
                }
              },
              {
                t: 297.642,
                s: [
                  111.4,
                  111.4
                ],
                i: {
                  x: [
                    0.591,
                    0.591
                  ],
                  y: [
                    0.993,
                    0.993
                  ]
                },
                o: {
                  x: [
                    0.319,
                    0.319
                  ],
                  y: [
                    0.678,
                    0.678
                  ]
                }
              },
              {
                t: 301.524,
                s: [
                  92.07,
                  92.07
                ],
                i: {
                  x: [
                    0.447,
                    0.447
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 309.282,
                s: [
                  101.26,
                  101.26
                ],
                i: {
                  x: [
                    0.442,
                    0.442
                  ],
                  y: [
                    0.994,
                    0.994
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 317.136,
                s: [
                  99.8,
                  99.8
                ],
                i: {
                  x: [
                    0.446,
                    0.446
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.281,
                    0.281
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 324.9,
                s: [
                  100.03,
                  100.03
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 334.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 382.8,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    0.613,
                    0.613
                  ],
                  y: [
                    0.589,
                    0.589
                  ]
                },
                o: {
                  x: [
                    0.321,
                    0.321
                  ],
                  y: [
                    0.033,
                    0.033
                  ]
                }
              },
              {
                t: 386.682,
                s: [
                  111.4,
                  111.4
                ],
                i: {
                  x: [
                    0.591,
                    0.591
                  ],
                  y: [
                    0.993,
                    0.993
                  ]
                },
                o: {
                  x: [
                    0.319,
                    0.319
                  ],
                  y: [
                    0.678,
                    0.678
                  ]
                }
              },
              {
                t: 390.564,
                s: [
                  92.07,
                  92.07
                ],
                i: {
                  x: [
                    0.447,
                    0.447
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 398.322,
                s: [
                  101.26,
                  101.26
                ],
                i: {
                  x: [
                    0.442,
                    0.442
                  ],
                  y: [
                    0.994,
                    0.994
                  ]
                },
                o: {
                  x: [
                    0.279,
                    0.279
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 406.176,
                s: [
                  99.8,
                  99.8
                ],
                i: {
                  x: [
                    0.446,
                    0.446
                  ],
                  y: [
                    0.987,
                    0.987
                  ]
                },
                o: {
                  x: [
                    0.281,
                    0.281
                  ],
                  y: [
                    0.02,
                    0.02
                  ]
                }
              },
              {
                t: 413.94,
                s: [
                  100.03,
                  100.03
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 424.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 491.4,
                s: [
                  100,
                  100
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 48,
        ty: 4,
        td: 1,
        parent: 31,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 47,
        ty: 0,
        tt: 1,
        parent: 31,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "45"
      },
      {
        ind: 31,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              393,
              233
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 66,
        ty: 4,
        td: 1,
        parent: 49,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 65,
        ty: 0,
        tt: 1,
        parent: 49,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "63"
      },
      {
        ind: 49,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              393,
              79
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 84,
        ty: 4,
        td: 1,
        parent: 67,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 83,
        ty: 0,
        tt: 1,
        parent: 67,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "81"
      },
      {
        ind: 67,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              227,
              233
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 102,
        ty: 4,
        td: 1,
        parent: 85,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 101,
        ty: 0,
        tt: 1,
        parent: 85,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "99"
      },
      {
        ind: 85,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              227,
              79
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 120,
        ty: 4,
        td: 1,
        parent: 103,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 119,
        ty: 0,
        tt: 1,
        parent: 103,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "117"
      },
      {
        ind: 103,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              393,
              -75
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 138,
        ty: 4,
        td: 1,
        parent: 121,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 137,
        ty: 0,
        tt: 1,
        parent: 121,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "135"
      },
      {
        ind: 121,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              310,
              -75
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 156,
        ty: 4,
        td: 1,
        parent: 139,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 155,
        ty: 0,
        tt: 1,
        parent: 139,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "153"
      },
      {
        ind: 139,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              -22,
              79
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 174,
        ty: 4,
        td: 1,
        parent: 157,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 173,
        ty: 0,
        tt: 1,
        parent: 157,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "171"
      },
      {
        ind: 157,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              61,
              233
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 192,
        ty: 4,
        td: 1,
        parent: 175,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 191,
        ty: 0,
        tt: 1,
        parent: 175,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "189"
      },
      {
        ind: 175,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              61,
              79
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 210,
        ty: 4,
        td: 1,
        parent: 193,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 209,
        ty: 0,
        tt: 1,
        parent: 193,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "207"
      },
      {
        ind: 193,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              -22,
              -75
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 228,
        ty: 4,
        td: 1,
        parent: 211,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 227,
        ty: 0,
        tt: 1,
        parent: 211,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "225"
      },
      {
        ind: 211,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              61,
              -75
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 246,
        ty: 4,
        td: 1,
        parent: 229,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 245,
        ty: 0,
        tt: 1,
        parent: 229,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "243"
      },
      {
        ind: 229,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              102,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 264,
        ty: 4,
        td: 1,
        parent: 247,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 263,
        ty: 0,
        tt: 1,
        parent: 247,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "261"
      },
      {
        ind: 247,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              102,
              -152
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 282,
        ty: 4,
        td: 1,
        parent: 265,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 281,
        ty: 0,
        tt: 1,
        parent: 265,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "279"
      },
      {
        ind: 265,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              102,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 300,
        ty: 4,
        td: 1,
        parent: 283,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 299,
        ty: 0,
        tt: 1,
        parent: 283,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "297"
      },
      {
        ind: 283,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              102,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 318,
        ty: 4,
        td: 1,
        parent: 301,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 317,
        ty: 0,
        tt: 1,
        parent: 301,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "315"
      },
      {
        ind: 301,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              -64,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 336,
        ty: 4,
        td: 1,
        parent: 319,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 335,
        ty: 0,
        tt: 1,
        parent: 319,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "333"
      },
      {
        ind: 319,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              -64,
              -152
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 354,
        ty: 4,
        td: 1,
        parent: 337,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 353,
        ty: 0,
        tt: 1,
        parent: 337,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "351"
      },
      {
        ind: 337,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              -64,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 372,
        ty: 4,
        td: 1,
        parent: 355,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 371,
        ty: 0,
        tt: 1,
        parent: 355,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "369"
      },
      {
        ind: 355,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              -64,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 390,
        ty: 4,
        td: 1,
        parent: 373,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 389,
        ty: 0,
        tt: 1,
        parent: 373,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "387"
      },
      {
        ind: 373,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              18,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 408,
        ty: 4,
        td: 1,
        parent: 391,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 407,
        ty: 0,
        tt: 1,
        parent: 391,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "405"
      },
      {
        ind: 391,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              18,
              -152
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 426,
        ty: 4,
        td: 1,
        parent: 409,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 425,
        ty: 0,
        tt: 1,
        parent: 409,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "423"
      },
      {
        ind: 409,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              18,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 444,
        ty: 4,
        td: 1,
        parent: 427,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 443,
        ty: 0,
        tt: 1,
        parent: 427,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "441"
      },
      {
        ind: 427,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              18,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 462,
        ty: 4,
        td: 1,
        parent: 445,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 461,
        ty: 0,
        tt: 1,
        parent: 445,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "459"
      },
      {
        ind: 445,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              351,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 480,
        ty: 4,
        td: 1,
        parent: 463,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 479,
        ty: 0,
        tt: 1,
        parent: 463,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "477"
      },
      {
        ind: 463,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              351,
              -152
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 498,
        ty: 4,
        td: 1,
        parent: 481,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 497,
        ty: 0,
        tt: 1,
        parent: 481,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "495"
      },
      {
        ind: 481,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              351,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 516,
        ty: 4,
        td: 1,
        parent: 499,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 515,
        ty: 0,
        tt: 1,
        parent: 499,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "513"
      },
      {
        ind: 499,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              351,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 534,
        ty: 4,
        td: 1,
        parent: 517,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 533,
        ty: 0,
        tt: 1,
        parent: 517,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "531"
      },
      {
        ind: 517,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              517,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 552,
        ty: 4,
        td: 1,
        parent: 535,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 551,
        ty: 0,
        tt: 1,
        parent: 535,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "549"
      },
      {
        ind: 535,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              517,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 570,
        ty: 4,
        td: 1,
        parent: 553,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 569,
        ty: 0,
        tt: 1,
        parent: 553,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "567"
      },
      {
        ind: 553,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              144,
              233
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 588,
        ty: 4,
        td: 1,
        parent: 571,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 587,
        ty: 0,
        tt: 1,
        parent: 571,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "585"
      },
      {
        ind: 571,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              144,
              79
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 606,
        ty: 4,
        td: 1,
        parent: 589,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 605,
        ty: 0,
        tt: 1,
        parent: 589,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "603"
      },
      {
        ind: 589,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              227,
              -75
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 624,
        ty: 4,
        td: 1,
        parent: 607,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 623,
        ty: 0,
        tt: 1,
        parent: 607,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "621"
      },
      {
        ind: 607,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              268,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 642,
        ty: 4,
        td: 1,
        parent: 625,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 641,
        ty: 0,
        tt: 1,
        parent: 625,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "639"
      },
      {
        ind: 625,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              268,
              -152
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 660,
        ty: 4,
        td: 1,
        parent: 643,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 659,
        ty: 0,
        tt: 1,
        parent: 643,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "657"
      },
      {
        ind: 643,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              268,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 678,
        ty: 4,
        td: 1,
        parent: 661,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 677,
        ty: 0,
        tt: 1,
        parent: 661,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "675"
      },
      {
        ind: 661,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              268,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 696,
        ty: 4,
        td: 1,
        parent: 679,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 695,
        ty: 0,
        tt: 1,
        parent: 679,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "693"
      },
      {
        ind: 679,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              434,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 714,
        ty: 4,
        td: 1,
        parent: 697,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 713,
        ty: 0,
        tt: 1,
        parent: 697,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "711"
      },
      {
        ind: 697,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              434,
              -152
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 732,
        ty: 4,
        td: 1,
        parent: 715,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 731,
        ty: 0,
        tt: 1,
        parent: 715,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "729"
      },
      {
        ind: 715,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              434,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 750,
        ty: 4,
        td: 1,
        parent: 733,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 749,
        ty: 0,
        tt: 1,
        parent: 733,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "747"
      },
      {
        ind: 733,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              434,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 768,
        ty: 4,
        td: 1,
        parent: 751,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 767,
        ty: 0,
        tt: 1,
        parent: 751,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "765"
      },
      {
        ind: 751,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              310,
              233
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 795,
        ty: 0,
        parent: 769,
        ks: {
          p: {
            a: 0,
            k: [
              -38.583,
              -38.583
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 294.6,
                      s: [
                        0,
                        0,
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 295.32072,
                      s: [
                        0.475,
                        0,
                        0.247
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 296.04144,
                      s: [
                        0.714,
                        0,
                        0.373
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 297.48288,
                      s: [
                        0.6,
                        0,
                        0.314
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 298.94148,
                      s: [
                        0.62,
                        0,
                        0.322
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 300.38292,
                      s: [
                        0.616,
                        0,
                        0.322
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        0.616,
                        0,
                        0.322
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 294.6,
                      s: [
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 295.32072,
                      s: [
                        51
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 296.04144,
                      s: [
                        77
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 297.48288,
                      s: [
                        65
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 298.94148,
                      s: [
                        67
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 300.38292,
                      s: [
                        66
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        66
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        135
                      ],
                      h: 1
                    },
                    {
                      t: 294.6,
                      s: [
                        135
                      ],
                      i: {
                        x: 0.882,
                        y: 0.734
                      },
                      o: {
                        x: 0.554,
                        y: 0.027
                      }
                    },
                    {
                      t: 295.32,
                      s: [
                        140.87
                      ],
                      i: {
                        x: 0.623,
                        y: 0.986
                      },
                      o: {
                        x: 0.411,
                        y: 0.396
                      }
                    },
                    {
                      t: 296.04,
                      s: [
                        154.59
                      ],
                      i: {
                        x: 0.533,
                        y: 0.633
                      },
                      o: {
                        x: 0.311,
                        y: 0.023
                      }
                    },
                    {
                      t: 296.76,
                      s: [
                        147.71
                      ],
                      i: {
                        x: 0.503,
                        y: 0.985
                      },
                      o: {
                        x: 0.197,
                        y: 0.476
                      }
                    },
                    {
                      t: 297.48,
                      s: [
                        145.47
                      ],
                      i: {
                        x: 0.458,
                        y: 0.993
                      },
                      o: {
                        x: 0.286,
                        y: 1e-3
                      }
                    },
                    {
                      t: 298.944,
                      s: [
                        146.45
                      ],
                      i: {
                        x: 0.443,
                        y: 0.987
                      },
                      o: {
                        x: 0.28,
                        y: 0.02
                      }
                    },
                    {
                      t: 300.384,
                      s: [
                        146.29
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 302.4,
                      s: [
                        146.31
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        146.31
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        11.314
                      ],
                      h: 1
                    },
                    {
                      t: 294.6,
                      s: [
                        11.314
                      ],
                      i: {
                        x: 0.613,
                        y: 0.592
                      },
                      o: {
                        x: 0.321,
                        y: 0.034
                      }
                    },
                    {
                      t: 295.32,
                      s: [
                        5.337
                      ],
                      i: {
                        x: 0.583,
                        y: 0.993
                      },
                      o: {
                        x: 0.315,
                        y: 0.686
                      }
                    },
                    {
                      t: 296.04,
                      s: [
                        2.443
                      ],
                      i: {
                        x: 0.452,
                        y: 0.987
                      },
                      o: {
                        x: 0.284,
                        y: 0.02
                      }
                    },
                    {
                      t: 297.48,
                      s: [
                        3.794
                      ],
                      i: {
                        x: 0.441,
                        y: 0.993
                      },
                      o: {
                        x: 0.279,
                        y: 1e-3
                      }
                    },
                    {
                      t: 298.944,
                      s: [
                        3.576
                      ],
                      i: {
                        x: 0.445,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 300.384,
                      s: [
                        3.61
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 302.4,
                      s: [
                        3.605
                      ],
                      h: 1
                    },
                    {
                      t: 302.4,
                      s: [
                        3.606
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        3.606
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        16
                      ],
                      h: 1
                    },
                    {
                      t: 294.6,
                      s: [
                        16
                      ],
                      i: {
                        x: 0.612,
                        y: 0.588
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 295.32,
                      s: [
                        22.18
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 296.04,
                      s: [
                        25.27
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 297.48,
                      s: [
                        23.8
                      ],
                      i: {
                        x: 0.442,
                        y: 0.993
                      },
                      o: {
                        x: 0.279,
                        y: 1e-3
                      }
                    },
                    {
                      t: 298.944,
                      s: [
                        24.03
                      ],
                      i: {
                        x: 0.445,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 300.384,
                      s: [
                        23.99
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 302.4,
                      s: [
                        24
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        24
                      ],
                      h: 1
                    }
                  ]
                }
              }
            ]
          }
        ],
        w: 130.372,
        h: 130.2992,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "793"
      },
      {
        ind: 769,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              310,
              79
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 813,
        ty: 4,
        td: 1,
        parent: 796,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 812,
        ty: 0,
        tt: 1,
        parent: 796,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "810"
      },
      {
        ind: 796,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              144,
              -75
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 840,
        ty: 0,
        parent: 814,
        ks: {
          p: {
            a: 0,
            k: [
              -38.583,
              -38.583
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 382.8,
                      s: [
                        0,
                        0,
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 383.52072,
                      s: [
                        0.475,
                        0,
                        0.247
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 384.24144,
                      s: [
                        0.714,
                        0,
                        0.373
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 385.68288,
                      s: [
                        0.6,
                        0,
                        0.314
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 387.14148,
                      s: [
                        0.62,
                        0,
                        0.322
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 388.58292,
                      s: [
                        0.616,
                        0,
                        0.322
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        0.616,
                        0,
                        0.322
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 382.8,
                      s: [
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 383.52072,
                      s: [
                        51
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 384.24144,
                      s: [
                        77
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 385.68288,
                      s: [
                        65
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 387.14148,
                      s: [
                        67
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 388.58292,
                      s: [
                        66
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        66
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        135
                      ],
                      h: 1
                    },
                    {
                      t: 382.8,
                      s: [
                        135
                      ],
                      i: {
                        x: 0.882,
                        y: 0.734
                      },
                      o: {
                        x: 0.554,
                        y: 0.027
                      }
                    },
                    {
                      t: 383.52,
                      s: [
                        140.87
                      ],
                      i: {
                        x: 0.623,
                        y: 0.986
                      },
                      o: {
                        x: 0.411,
                        y: 0.396
                      }
                    },
                    {
                      t: 384.24,
                      s: [
                        154.59
                      ],
                      i: {
                        x: 0.533,
                        y: 0.633
                      },
                      o: {
                        x: 0.311,
                        y: 0.023
                      }
                    },
                    {
                      t: 384.96,
                      s: [
                        147.71
                      ],
                      i: {
                        x: 0.503,
                        y: 0.985
                      },
                      o: {
                        x: 0.197,
                        y: 0.476
                      }
                    },
                    {
                      t: 385.68,
                      s: [
                        145.47
                      ],
                      i: {
                        x: 0.458,
                        y: 0.993
                      },
                      o: {
                        x: 0.286,
                        y: 1e-3
                      }
                    },
                    {
                      t: 387.144,
                      s: [
                        146.45
                      ],
                      i: {
                        x: 0.443,
                        y: 0.987
                      },
                      o: {
                        x: 0.28,
                        y: 0.02
                      }
                    },
                    {
                      t: 388.584,
                      s: [
                        146.29
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 390.6,
                      s: [
                        146.31
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        146.31
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        11.314
                      ],
                      h: 1
                    },
                    {
                      t: 382.8,
                      s: [
                        11.314
                      ],
                      i: {
                        x: 0.613,
                        y: 0.592
                      },
                      o: {
                        x: 0.321,
                        y: 0.034
                      }
                    },
                    {
                      t: 383.52,
                      s: [
                        5.337
                      ],
                      i: {
                        x: 0.583,
                        y: 0.993
                      },
                      o: {
                        x: 0.315,
                        y: 0.686
                      }
                    },
                    {
                      t: 384.24,
                      s: [
                        2.443
                      ],
                      i: {
                        x: 0.452,
                        y: 0.987
                      },
                      o: {
                        x: 0.284,
                        y: 0.02
                      }
                    },
                    {
                      t: 385.68,
                      s: [
                        3.794
                      ],
                      i: {
                        x: 0.441,
                        y: 0.993
                      },
                      o: {
                        x: 0.279,
                        y: 1e-3
                      }
                    },
                    {
                      t: 387.144,
                      s: [
                        3.576
                      ],
                      i: {
                        x: 0.445,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 388.584,
                      s: [
                        3.61
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 390.6,
                      s: [
                        3.605
                      ],
                      h: 1
                    },
                    {
                      t: 390.6,
                      s: [
                        3.606
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        3.606
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        16
                      ],
                      h: 1
                    },
                    {
                      t: 382.8,
                      s: [
                        16
                      ],
                      i: {
                        x: 0.612,
                        y: 0.588
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 383.52,
                      s: [
                        22.18
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 384.24,
                      s: [
                        25.27
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 385.68,
                      s: [
                        23.8
                      ],
                      i: {
                        x: 0.442,
                        y: 0.993
                      },
                      o: {
                        x: 0.279,
                        y: 1e-3
                      }
                    },
                    {
                      t: 387.144,
                      s: [
                        24.03
                      ],
                      i: {
                        x: 0.445,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 388.584,
                      s: [
                        23.99
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 390.6,
                      s: [
                        24
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        24
                      ],
                      h: 1
                    }
                  ]
                }
              }
            ]
          }
        ],
        w: 130.372,
        h: 130.2992,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "838"
      },
      {
        ind: 814,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              185,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 858,
        ty: 4,
        td: 1,
        parent: 841,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 857,
        ty: 0,
        tt: 1,
        parent: 841,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "855"
      },
      {
        ind: 841,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              185,
              -152
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 874,
        ty: 4,
        td: 1,
        parent: 859,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.024,
                25.988
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.048,
                51.975
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 873,
        ty: 0,
        tt: 1,
        parent: 859,
        ks: {},
        w: 53,
        h: 52,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "871"
      },
      {
        ind: 859,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              185,
              156
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 899,
        ty: 0,
        parent: 875,
        ks: {
          p: {
            a: 0,
            k: [
              -38.583,
              -38.583
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 137.58,
                      s: [
                        0,
                        0,
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 138.30072,
                      s: [
                        0.475,
                        0,
                        0.247
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 139.02144,
                      s: [
                        0.714,
                        0,
                        0.373
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 140.46288,
                      s: [
                        0.6,
                        0,
                        0.314
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 141.92148,
                      s: [
                        0.62,
                        0,
                        0.322
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 143.36292,
                      s: [
                        0.616,
                        0,
                        0.322
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        0.616,
                        0,
                        0.322
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 137.58,
                      s: [
                        0
                      ],
                      i: {
                        x: 0.613,
                        y: 0.589
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 138.30072,
                      s: [
                        51
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 139.02144,
                      s: [
                        77
                      ],
                      i: {
                        x: 0.447,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 140.46288,
                      s: [
                        65
                      ],
                      i: {
                        x: 0.442,
                        y: 0.994
                      },
                      o: {
                        x: 0.279,
                        y: 0
                      }
                    },
                    {
                      t: 141.92148,
                      s: [
                        67
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 143.36292,
                      s: [
                        66
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        66
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        135
                      ],
                      h: 1
                    },
                    {
                      t: 137.58,
                      s: [
                        135
                      ],
                      i: {
                        x: 0.882,
                        y: 0.734
                      },
                      o: {
                        x: 0.554,
                        y: 0.027
                      }
                    },
                    {
                      t: 138.3,
                      s: [
                        140.87
                      ],
                      i: {
                        x: 0.623,
                        y: 0.986
                      },
                      o: {
                        x: 0.411,
                        y: 0.396
                      }
                    },
                    {
                      t: 139.02,
                      s: [
                        154.59
                      ],
                      i: {
                        x: 0.533,
                        y: 0.633
                      },
                      o: {
                        x: 0.311,
                        y: 0.023
                      }
                    },
                    {
                      t: 139.74,
                      s: [
                        147.71
                      ],
                      i: {
                        x: 0.503,
                        y: 0.985
                      },
                      o: {
                        x: 0.197,
                        y: 0.476
                      }
                    },
                    {
                      t: 140.46,
                      s: [
                        145.47
                      ],
                      i: {
                        x: 0.458,
                        y: 0.993
                      },
                      o: {
                        x: 0.286,
                        y: 1e-3
                      }
                    },
                    {
                      t: 141.924,
                      s: [
                        146.45
                      ],
                      i: {
                        x: 0.443,
                        y: 0.987
                      },
                      o: {
                        x: 0.28,
                        y: 0.02
                      }
                    },
                    {
                      t: 143.364,
                      s: [
                        146.29
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 145.38,
                      s: [
                        146.31
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        146.31
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        11.314
                      ],
                      h: 1
                    },
                    {
                      t: 137.58,
                      s: [
                        11.314
                      ],
                      i: {
                        x: 0.613,
                        y: 0.592
                      },
                      o: {
                        x: 0.321,
                        y: 0.034
                      }
                    },
                    {
                      t: 138.3,
                      s: [
                        5.337
                      ],
                      i: {
                        x: 0.583,
                        y: 0.993
                      },
                      o: {
                        x: 0.315,
                        y: 0.686
                      }
                    },
                    {
                      t: 139.02,
                      s: [
                        2.443
                      ],
                      i: {
                        x: 0.452,
                        y: 0.987
                      },
                      o: {
                        x: 0.284,
                        y: 0.02
                      }
                    },
                    {
                      t: 140.46,
                      s: [
                        3.794
                      ],
                      i: {
                        x: 0.441,
                        y: 0.993
                      },
                      o: {
                        x: 0.279,
                        y: 1e-3
                      }
                    },
                    {
                      t: 141.924,
                      s: [
                        3.576
                      ],
                      i: {
                        x: 0.445,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 143.364,
                      s: [
                        3.61
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 145.38,
                      s: [
                        3.605
                      ],
                      h: 1
                    },
                    {
                      t: 145.38,
                      s: [
                        3.606
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        3.606
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        16
                      ],
                      h: 1
                    },
                    {
                      t: 137.58,
                      s: [
                        16
                      ],
                      i: {
                        x: 0.612,
                        y: 0.588
                      },
                      o: {
                        x: 0.321,
                        y: 0.033
                      }
                    },
                    {
                      t: 138.3,
                      s: [
                        22.18
                      ],
                      i: {
                        x: 0.591,
                        y: 0.993
                      },
                      o: {
                        x: 0.319,
                        y: 0.678
                      }
                    },
                    {
                      t: 139.02,
                      s: [
                        25.27
                      ],
                      i: {
                        x: 0.446,
                        y: 0.987
                      },
                      o: {
                        x: 0.279,
                        y: 0.02
                      }
                    },
                    {
                      t: 140.46,
                      s: [
                        23.8
                      ],
                      i: {
                        x: 0.442,
                        y: 0.993
                      },
                      o: {
                        x: 0.279,
                        y: 1e-3
                      }
                    },
                    {
                      t: 141.924,
                      s: [
                        24.03
                      ],
                      i: {
                        x: 0.445,
                        y: 0.987
                      },
                      o: {
                        x: 0.281,
                        y: 0.02
                      }
                    },
                    {
                      t: 143.364,
                      s: [
                        23.99
                      ],
                      i: {
                        x: 1,
                        y: 1
                      },
                      o: {
                        x: 0,
                        y: 0
                      }
                    },
                    {
                      t: 145.38,
                      s: [
                        24
                      ],
                      h: 1
                    },
                    {
                      t: 491.4,
                      s: [
                        24
                      ],
                      h: 1
                    }
                  ]
                }
              }
            ]
          }
        ],
        w: 130.372,
        h: 130.2992,
        ip: 0,
        op: 492.4,
        st: 0,
        refId: "897"
      },
      {
        ind: 875,
        ty: 3,
        parent: 30,
        ks: {
          p: {
            a: 0,
            k: [
              185,
              2
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 30,
        ty: 3,
        parent: 6,
        ks: {
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  2,
                  92
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 88.2,
                s: [
                  2,
                  92
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 136.2,
                s: [
                  -81,
                  92
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 197.76,
                s: [
                  -81,
                  92
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 245.76,
                s: [
                  -163,
                  92
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 293.76,
                s: [
                  -206,
                  15
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 334.8,
                s: [
                  -206,
                  15
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 382.8,
                s: [
                  -81,
                  -62
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 491.4,
                s: [
                  -81,
                  -62
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 901,
        ty: 4,
        parent: 900,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 900,
        ty: 3,
        parent: 6,
        ks: {},
        ip: 0,
        op: 492.4,
        st: 0
      },
      {
        ind: 6,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              271,
              214
            ]
          }
        },
        ip: 0,
        op: 492.4,
        st: 0
      }
    ]
  }
];
const fr$a = 60;
const h$a = 238;
const ip$a = 0;
const layers$a = [
  {
    ind: 4,
    ty: 4,
    td: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 492.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 3,
    ty: 4,
    tt: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 492.4,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 492.4,
    st: 0
  },
  {
    ind: 905,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 492.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 904,
    ty: 0,
    tt: 1,
    parent: 5,
    ks: {
      a: {
        a: 0,
        k: [
          271,
          214
        ]
      }
    },
    w: 843,
    h: 591,
    ip: 0,
    op: 492.4,
    st: 0,
    refId: "902"
  },
  {
    ind: 5,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 492.4,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    parent: 0,
    ks: {},
    ip: 0,
    op: 492.4,
    st: 0
  },
  {
    ind: 0,
    ty: 3,
    ks: {},
    ip: 0,
    op: 492.4,
    st: 0
  }
];
const meta$a = {
  g: "https://jitter.video"
};
const op$a = 491.4;
const v$a = "5.7.4";
const w$a = 255;
const aiTargeting = {
  assets: assets$a,
  fr: fr$a,
  h: h$a,
  ip: ip$a,
  layers: layers$a,
  meta: meta$a,
  op: op$a,
  v: v$a,
  w: w$a
};
const AiTargetingAnimation = ({ alt }) => /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: aiTargeting, altText: alt });
const assets$9 = [
  {
    id: "60",
    layers: [
      {
        ind: 57,
        ty: 4,
        parent: 56,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                32.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 23.369
            },
            s: {
              a: 0,
              k: [
                65,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 56,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              0
            ]
          }
        },
        ip: 0,
        op: 201.4,
        st: 0
      },
      {
        ind: 59,
        ty: 4,
        parent: 58,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                3.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 23.369
            },
            s: {
              a: 0,
              k: [
                7,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 58,
        ty: 3,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0
      }
    ]
  },
  {
    id: "69",
    layers: [
      {
        ind: 66,
        ty: 4,
        parent: 65,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                32.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 23.369
            },
            s: {
              a: 0,
              k: [
                65,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 65,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              0
            ]
          }
        },
        ip: 0,
        op: 201.4,
        st: 0
      },
      {
        ind: 68,
        ty: 4,
        parent: 67,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                3.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 23.369
            },
            s: {
              a: 0,
              k: [
                7,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 67,
        ty: 3,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0
      }
    ]
  },
  {
    id: "78",
    layers: [
      {
        ind: 75,
        ty: 4,
        parent: 74,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                32.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 23.369
            },
            s: {
              a: 0,
              k: [
                65,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 74,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              0
            ]
          }
        },
        ip: 0,
        op: 201.4,
        st: 0
      },
      {
        ind: 77,
        ty: 4,
        parent: 76,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                3.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 23.369
            },
            s: {
              a: 0,
              k: [
                7,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 76,
        ty: 3,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0
      }
    ]
  },
  {
    id: "86",
    layers: [
      {
        ind: 85,
        ty: 4,
        parent: 84,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                16.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                255,
                33
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 84,
        ty: 3,
        ks: {},
        ip: 0,
        op: 201.4,
        st: 0
      }
    ]
  }
];
const fr$9 = 60;
const h$9 = 238;
const ip$9 = 0;
const layers$9 = [
  {
    ind: 4,
    ty: 4,
    parent: 3,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "el",
        p: {
          a: 0,
          k: [
            13.5,
            13.5
          ]
        },
        s: {
          a: 0,
          k: [
            27,
            27
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.118,
            0.278,
            0.663
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 3,
    ty: 3,
    parent: 2,
    ks: {
      a: {
        a: 0,
        k: [
          13.5,
          13.5
        ]
      },
      p: {
        a: 0,
        k: [
          13.5,
          13.5
        ]
      },
      r: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              -45
            ],
            i: {
              x: 0.613,
              y: 0.589
            },
            o: {
              x: 0.321,
              y: 0.033
            }
          },
          {
            t: 3.16008,
            s: [
              -10.26
            ],
            i: {
              x: 0.591,
              y: 0.993
            },
            o: {
              x: 0.319,
              y: 0.678
            }
          },
          {
            t: 6.32016,
            s: [
              7.14
            ],
            i: {
              x: 0.447,
              y: 0.987
            },
            o: {
              x: 0.279,
              y: 0.02
            }
          },
          {
            t: 12.64032,
            s: [
              -1.13
            ],
            i: {
              x: 0.442,
              y: 0.994
            },
            o: {
              x: 0.279,
              y: 0
            }
          },
          {
            t: 19.03572,
            s: [
              0.18
            ],
            i: {
              x: 0.446,
              y: 0.987
            },
            o: {
              x: 0.281,
              y: 0.02
            }
          },
          {
            t: 25.35588,
            s: [
              -0.03
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 34.2,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 200.4,
            s: [
              0
            ],
            h: 1
          }
        ]
      },
      s: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0,
              0
            ],
            i: {
              x: [
                0.613,
                0.613
              ],
              y: [
                0.589,
                0.589
              ]
            },
            o: {
              x: [
                0.321,
                0.321
              ],
              y: [
                0.033,
                0.033
              ]
            }
          },
          {
            t: 3.162,
            s: [
              77.2,
              77.2
            ],
            i: {
              x: [
                0.591,
                0.591
              ],
              y: [
                0.993,
                0.993
              ]
            },
            o: {
              x: [
                0.319,
                0.319
              ],
              y: [
                0.678,
                0.678
              ]
            }
          },
          {
            t: 6.318,
            s: [
              115.86,
              115.86
            ],
            i: {
              x: [
                0.447,
                0.447
              ],
              y: [
                0.987,
                0.987
              ]
            },
            o: {
              x: [
                0.279,
                0.279
              ],
              y: [
                0.02,
                0.02
              ]
            }
          },
          {
            t: 12.642,
            s: [
              97.48,
              97.48
            ],
            i: {
              x: [
                0.442,
                0.442
              ],
              y: [
                0.994,
                0.994
              ]
            },
            o: {
              x: [
                0.279,
                0.279
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 19.038,
            s: [
              100.4,
              100.4
            ],
            i: {
              x: [
                0.446,
                0.446
              ],
              y: [
                0.987,
                0.987
              ]
            },
            o: {
              x: [
                0.281,
                0.281
              ],
              y: [
                0.02,
                0.02
              ]
            }
          },
          {
            t: 25.356,
            s: [
              99.94,
              99.94
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 34.2,
            s: [
              100,
              100
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 200.4,
            s: [
              100,
              100
            ],
            h: 1
          }
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {
      p: {
        a: 0,
        k: [
          22,
          64
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 7,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 6,
    ty: 4,
    tt: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 5,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 13,
    ty: 4,
    parent: 12,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            65,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            130,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 12,
    ty: 3,
    parent: 11,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          63
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 15,
    ty: 4,
    parent: 14,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            65,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            130,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 14,
    ty: 3,
    parent: 11,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          48
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 17,
    ty: 4,
    parent: 16,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            65,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            130,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 16,
    ty: 3,
    parent: 11,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          33
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 19,
    ty: 4,
    parent: 18,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            65,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            130,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 18,
    ty: 3,
    parent: 11,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          18
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 21,
    ty: 4,
    parent: 20,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            23.5,
            5
          ]
        },
        r: {
          a: 0,
          k: 100
        },
        s: {
          a: 0,
          k: [
            47,
            10
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 20,
    ty: 3,
    parent: 11,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 11,
    ty: 3,
    parent: 10,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          173
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 24,
    ty: 4,
    parent: 23,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            65,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            130,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 23,
    ty: 3,
    parent: 22,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          63
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 26,
    ty: 4,
    parent: 25,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            65,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            130,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 25,
    ty: 3,
    parent: 22,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          48
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 28,
    ty: 4,
    parent: 27,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            65,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            130,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 27,
    ty: 3,
    parent: 22,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          33
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 30,
    ty: 4,
    parent: 29,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            65,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            130,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 29,
    ty: 3,
    parent: 22,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          18
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 32,
    ty: 4,
    parent: 31,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            23.5,
            5
          ]
        },
        r: {
          a: 0,
          k: 100
        },
        s: {
          a: 0,
          k: [
            47,
            10
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 31,
    ty: 3,
    parent: 22,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 22,
    ty: 3,
    parent: 10,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          91
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 36,
    ty: 4,
    parent: 35,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            19,
            6.5
          ]
        },
        r: {
          a: 0,
          k: 2
        },
        s: {
          a: 0,
          k: [
            38,
            13
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 35,
    ty: 3,
    parent: 34,
    ks: {
      p: {
        a: 0,
        k: [
          46,
          0
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 38,
    ty: 4,
    parent: 37,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            19,
            6.5
          ]
        },
        r: {
          a: 0,
          k: 2
        },
        s: {
          a: 0,
          k: [
            38,
            13
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 37,
    ty: 3,
    parent: 34,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 34,
    ty: 3,
    parent: 33,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          66
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 40,
    ty: 4,
    parent: 39,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            44,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            88,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 39,
    ty: 3,
    parent: 33,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          51
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 42,
    ty: 4,
    parent: 41,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            44,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            88,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 41,
    ty: 3,
    parent: 33,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          36
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 44,
    ty: 4,
    parent: 43,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            44,
            3.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            88,
            7
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 43,
    ty: 3,
    parent: 33,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          21
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 46,
    ty: 4,
    parent: 45,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            25.5,
            6.5
          ]
        },
        r: {
          a: 0,
          k: 31
        },
        s: {
          a: 0,
          k: [
            51,
            13
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 45,
    ty: 3,
    parent: 33,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 33,
    ty: 3,
    parent: 10,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 10,
    ty: 3,
    parent: 9,
    ks: {
      p: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0,
              0
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 112.2,
            s: [
              0,
              0
            ],
            i: {
              x: [
                1,
                0.613
              ],
              y: [
                1,
                0.589
              ]
            },
            o: {
              x: [
                0,
                0.321
              ],
              y: [
                0,
                0.033
              ]
            }
          },
          {
            t: 116.634,
            s: [
              0,
              51.723
            ],
            i: {
              x: [
                1,
                0.591
              ],
              y: [
                1,
                0.993
              ]
            },
            o: {
              x: [
                0,
                0.319
              ],
              y: [
                0,
                0.678
              ]
            }
          },
          {
            t: 121.068,
            s: [
              0,
              77.629
            ],
            i: {
              x: [
                1,
                0.447
              ],
              y: [
                1,
                0.987
              ]
            },
            o: {
              x: [
                0,
                0.279
              ],
              y: [
                0,
                0.02
              ]
            }
          },
          {
            t: 129.942,
            s: [
              0,
              65.315
            ],
            i: {
              x: [
                1,
                0.442
              ],
              y: [
                1,
                0.994
              ]
            },
            o: {
              x: [
                0,
                0.279
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 138.918,
            s: [
              0,
              67.267
            ],
            i: {
              x: [
                1,
                0.446
              ],
              y: [
                1,
                0.987
              ]
            },
            o: {
              x: [
                0,
                0.281
              ],
              y: [
                0,
                0.02
              ]
            }
          },
          {
            t: 147.786,
            s: [
              0,
              66.958
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 160.2,
            s: [
              0,
              67.003
            ],
            h: 1
          },
          {
            t: 160.2,
            s: [
              0,
              67
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 200.4,
            s: [
              0,
              67
            ],
            h: 1
          }
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 48,
    ty: 4,
    parent: 47,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                65,
                0
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 112.2,
              s: [
                65,
                0
              ],
              i: {
                x: [
                  1,
                  0.613
                ],
                y: [
                  1,
                  0.589
                ]
              },
              o: {
                x: [
                  0,
                  0.321
                ],
                y: [
                  0,
                  0.033
                ]
              }
            },
            {
              t: 116.634,
              s: [
                65,
                23.546
              ],
              i: {
                x: [
                  1,
                  0.591
                ],
                y: [
                  1,
                  0.993
                ]
              },
              o: {
                x: [
                  0,
                  0.319
                ],
                y: [
                  0,
                  0.678
                ]
              }
            },
            {
              t: 121.068,
              s: [
                65,
                35.338
              ],
              i: {
                x: [
                  1,
                  0.447
                ],
                y: [
                  1,
                  0.987
                ]
              },
              o: {
                x: [
                  0,
                  0.279
                ],
                y: [
                  0,
                  0.02
                ]
              }
            },
            {
              t: 129.942,
              s: [
                65,
                29.733
              ],
              i: {
                x: [
                  1,
                  0.442
                ],
                y: [
                  1,
                  0.994
                ]
              },
              o: {
                x: [
                  0,
                  0.279
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 138.918,
              s: [
                65,
                30.622
              ],
              i: {
                x: [
                  1,
                  0.446
                ],
                y: [
                  1,
                  0.987
                ]
              },
              o: {
                x: [
                  0,
                  0.281
                ],
                y: [
                  0,
                  0.02
                ]
              }
            },
            {
              t: 147.786,
              s: [
                65,
                30.481
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 160.2,
              s: [
                65,
                30.501
              ],
              h: 1
            },
            {
              t: 160.2,
              s: [
                65,
                30.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 200.4,
              s: [
                65,
                30.5
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                130,
                0
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 112.2,
              s: [
                130,
                0
              ],
              i: {
                x: [
                  1,
                  0.613
                ],
                y: [
                  1,
                  0.589
                ]
              },
              o: {
                x: [
                  0,
                  0.321
                ],
                y: [
                  0,
                  0.033
                ]
              }
            },
            {
              t: 116.634,
              s: [
                130,
                47.091
              ],
              i: {
                x: [
                  1,
                  0.591
                ],
                y: [
                  1,
                  0.993
                ]
              },
              o: {
                x: [
                  0,
                  0.319
                ],
                y: [
                  0,
                  0.678
                ]
              }
            },
            {
              t: 121.068,
              s: [
                130,
                70.677
              ],
              i: {
                x: [
                  1,
                  0.447
                ],
                y: [
                  1,
                  0.987
                ]
              },
              o: {
                x: [
                  0,
                  0.279
                ],
                y: [
                  0,
                  0.02
                ]
              }
            },
            {
              t: 129.942,
              s: [
                130,
                59.466
              ],
              i: {
                x: [
                  1,
                  0.442
                ],
                y: [
                  1,
                  0.994
                ]
              },
              o: {
                x: [
                  0,
                  0.279
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 138.918,
              s: [
                130,
                61.243
              ],
              i: {
                x: [
                  1,
                  0.446
                ],
                y: [
                  1,
                  0.987
                ]
              },
              o: {
                x: [
                  0,
                  0.281
                ],
                y: [
                  0,
                  0.02
                ]
              }
            },
            {
              t: 147.786,
              s: [
                130,
                60.961
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 160.2,
              s: [
                130,
                61.003
              ],
              h: 1
            },
            {
              t: 160.2,
              s: [
                130,
                61
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 200.4,
              s: [
                130,
                61
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.118,
            0.278,
            0.663
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 47,
    ty: 3,
    parent: 9,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 9,
    ty: 3,
    parent: 8,
    ks: {
      p: {
        a: 0,
        k: [
          99,
          0
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 51,
    ty: 4,
    parent: 50,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            45.5,
            37
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 0,
          k: [
            91,
            74
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 50,
    ty: 3,
    parent: 49,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          229
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 53,
    ty: 4,
    parent: 52,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            45.5,
            37
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 0,
          k: [
            91,
            74
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 52,
    ty: 3,
    parent: 49,
    ks: {
      p: {
        a: 0,
        k: [
          0,
          144
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 63,
    ty: 4,
    td: 1,
    parent: 55,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                0,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 52.8,
              s: [
                0,
                3.5
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 54.24,
              s: [
                2.702,
                3.5
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 55.68,
              s: [
                4.055,
                3.5
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 58.566,
              s: [
                3.412,
                3.5
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 61.482,
              s: [
                3.514,
                3.5
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 64.368,
              s: [
                3.498,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 68.4,
              s: [
                3.5,
                3.5
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 72.834,
              s: [
                29.748,
                3.5
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 77.268,
              s: [
                42.894,
                3.5
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 86.142,
              s: [
                36.645,
                3.5
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 95.118,
              s: [
                37.636,
                3.5
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 103.986,
              s: [
                37.478,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 116.4,
              s: [
                37.502,
                3.5
              ],
              h: 1
            },
            {
              t: 116.4,
              s: [
                37.5,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 200.4,
              s: [
                37.5,
                3.5
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 100
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                0,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 52.8,
              s: [
                0,
                7
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 54.24,
              s: [
                5.404,
                7
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 55.68,
              s: [
                8.11,
                7
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 58.566,
              s: [
                6.824,
                7
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 61.482,
              s: [
                7.028,
                7
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 64.368,
              s: [
                6.996,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 68.4,
              s: [
                7,
                7
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 72.834,
              s: [
                59.495,
                7
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 77.268,
              s: [
                85.787,
                7
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 86.142,
              s: [
                73.29,
                7
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 95.118,
              s: [
                75.271,
                7
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 103.986,
              s: [
                74.957,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 116.4,
              s: [
                75.003,
                7
              ],
              h: 1
            },
            {
              t: 116.4,
              s: [
                75,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 200.4,
              s: [
                75,
                7
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 62,
    ty: 0,
    tt: 1,
    parent: 55,
    ks: {},
    w: 75,
    h: 7,
    ip: 0,
    op: 201.4,
    st: 0,
    refId: "60"
  },
  {
    ind: 55,
    ty: 3,
    parent: 54,
    ks: {
      p: {
        a: 0,
        k: [
          8,
          71
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 72,
    ty: 4,
    td: 1,
    parent: 64,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                0,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 43.2,
              s: [
                0,
                3.5
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 44.64,
              s: [
                2.702,
                3.5
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 46.08,
              s: [
                4.055,
                3.5
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 48.966,
              s: [
                3.412,
                3.5
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 51.882,
              s: [
                3.514,
                3.5
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 54.768,
              s: [
                3.498,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                3.5,
                3.5
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 63.234,
              s: [
                29.748,
                3.5
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 67.668,
              s: [
                42.894,
                3.5
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 76.542,
              s: [
                36.645,
                3.5
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 85.518,
              s: [
                37.636,
                3.5
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 94.386,
              s: [
                37.478,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                37.502,
                3.5
              ],
              h: 1
            },
            {
              t: 106.8,
              s: [
                37.5,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 200.4,
              s: [
                37.5,
                3.5
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 100
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                0,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 43.2,
              s: [
                0,
                7
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 44.64,
              s: [
                5.404,
                7
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 46.08,
              s: [
                8.11,
                7
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 48.966,
              s: [
                6.824,
                7
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 51.882,
              s: [
                7.028,
                7
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 54.768,
              s: [
                6.996,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                7,
                7
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 63.234,
              s: [
                59.495,
                7
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 67.668,
              s: [
                85.787,
                7
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 76.542,
              s: [
                73.29,
                7
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 85.518,
              s: [
                75.271,
                7
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 94.386,
              s: [
                74.957,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                75.003,
                7
              ],
              h: 1
            },
            {
              t: 106.8,
              s: [
                75,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 200.4,
              s: [
                75,
                7
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 71,
    ty: 0,
    tt: 1,
    parent: 64,
    ks: {},
    w: 75,
    h: 7,
    ip: 0,
    op: 201.4,
    st: 0,
    refId: "69"
  },
  {
    ind: 64,
    ty: 3,
    parent: 54,
    ks: {
      p: {
        a: 0,
        k: [
          8,
          59
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 81,
    ty: 4,
    td: 1,
    parent: 73,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                0,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 31.8,
              s: [
                0,
                3.5
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 33.24,
              s: [
                2.702,
                3.5
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 34.68,
              s: [
                4.055,
                3.5
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 37.566,
              s: [
                3.412,
                3.5
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 40.482,
              s: [
                3.514,
                3.5
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 43.368,
              s: [
                3.498,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 47.4,
              s: [
                3.5,
                3.5
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 51.834,
              s: [
                29.748,
                3.5
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 56.268,
              s: [
                42.894,
                3.5
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 65.142,
              s: [
                36.645,
                3.5
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 74.118,
              s: [
                37.636,
                3.5
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 82.986,
              s: [
                37.478,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 95.4,
              s: [
                37.502,
                3.5
              ],
              h: 1
            },
            {
              t: 95.4,
              s: [
                37.5,
                3.5
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 200.4,
              s: [
                37.5,
                3.5
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 100
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                0,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 31.8,
              s: [
                0,
                7
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 33.24,
              s: [
                5.404,
                7
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 34.68,
              s: [
                8.11,
                7
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 37.566,
              s: [
                6.824,
                7
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 40.482,
              s: [
                7.028,
                7
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 43.368,
              s: [
                6.996,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 47.4,
              s: [
                7,
                7
              ],
              i: {
                x: [
                  0.613,
                  1
                ],
                y: [
                  0.589,
                  1
                ]
              },
              o: {
                x: [
                  0.321,
                  0
                ],
                y: [
                  0.033,
                  0
                ]
              }
            },
            {
              t: 51.834,
              s: [
                59.495,
                7
              ],
              i: {
                x: [
                  0.591,
                  1
                ],
                y: [
                  0.993,
                  1
                ]
              },
              o: {
                x: [
                  0.319,
                  0
                ],
                y: [
                  0.678,
                  0
                ]
              }
            },
            {
              t: 56.268,
              s: [
                85.787,
                7
              ],
              i: {
                x: [
                  0.447,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 65.142,
              s: [
                73.29,
                7
              ],
              i: {
                x: [
                  0.442,
                  1
                ],
                y: [
                  0.994,
                  1
                ]
              },
              o: {
                x: [
                  0.279,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 74.118,
              s: [
                75.271,
                7
              ],
              i: {
                x: [
                  0.446,
                  1
                ],
                y: [
                  0.987,
                  1
                ]
              },
              o: {
                x: [
                  0.281,
                  0
                ],
                y: [
                  0.02,
                  0
                ]
              }
            },
            {
              t: 82.986,
              s: [
                74.957,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 95.4,
              s: [
                75.003,
                7
              ],
              h: 1
            },
            {
              t: 95.4,
              s: [
                75,
                7
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 200.4,
              s: [
                75,
                7
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 80,
    ty: 0,
    tt: 1,
    parent: 73,
    ks: {},
    w: 75,
    h: 7,
    ip: 0,
    op: 201.4,
    st: 0,
    refId: "78"
  },
  {
    ind: 73,
    ty: 3,
    parent: 54,
    ks: {
      p: {
        a: 0,
        k: [
          8,
          47
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 82,
    ty: 4,
    parent: 54,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            45.5,
            66.5
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 0,
          k: [
            91,
            133
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 54,
    ty: 3,
    parent: 49,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 49,
    ty: 3,
    parent: 8,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 8,
    ty: 3,
    parent: 1,
    ks: {
      p: {
        a: 0,
        k: [
          13,
          53
        ]
      }
    },
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 89,
    ty: 4,
    td: 1,
    parent: 83,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 88,
    ty: 0,
    tt: 1,
    parent: 83,
    ks: {},
    w: 255,
    h: 33,
    ip: 0,
    op: 201.4,
    st: 0,
    refId: "86"
  },
  {
    ind: 83,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 91,
    ty: 4,
    parent: 90,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            1,
            1,
            1
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 90,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    parent: 0,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  },
  {
    ind: 0,
    ty: 3,
    ks: {},
    ip: 0,
    op: 201.4,
    st: 0
  }
];
const meta$9 = {
  g: "https://jitter.video"
};
const op$9 = 200.4;
const v$9 = "5.7.4";
const w$9 = 255;
const branding = {
  assets: assets$9,
  fr: fr$9,
  h: h$9,
  ip: ip$9,
  layers: layers$9,
  meta: meta$9,
  op: op$9,
  v: v$9,
  w: w$9
};
const BrandingAnimation = ({ alt }) => /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: branding, altText: alt });
const assets$8 = [
  {
    h: 3,
    id: "0",
    p: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGMAAAADCAYAAACHz7HPAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAYSURBVHgB7cAxAQAAAMKg9U9tDQ8ogGsDBKcAAbNbxGsAAAAASUVORK5CYII=",
    u: "",
    w: 99,
    e: 1
  },
  {
    id: "7",
    layers: [
      {
        ind: 6,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 20
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "18",
    layers: [
      {
        ind: 15,
        ty: 4,
        parent: 14,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    2.4
                  ],
                  [
                    3.1,
                    0
                  ],
                  [
                    0.6,
                    -2.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    2,
                    -1.7
                  ]
                ],
                v: [
                  [
                    6.5,
                    9.7
                  ],
                  [
                    1,
                    14.4
                  ],
                  [
                    1,
                    16.6
                  ],
                  [
                    11.9,
                    16.6
                  ],
                  [
                    11.9,
                    14.4
                  ],
                  [
                    4.4,
                    14.4
                  ],
                  [
                    8.1,
                    11.3
                  ],
                  [
                    11.9,
                    5.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    1.2,
                    4.5
                  ],
                  [
                    3.4,
                    5.4
                  ],
                  [
                    6.5,
                    3
                  ],
                  [
                    9.3,
                    5.6
                  ],
                  [
                    6.5,
                    9.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.5,
                    -2.1
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -2.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 14,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              12.901,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 17,
        ty: 4,
        parent: 16,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 16,
        ty: 3,
        parent: 13,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 13,
        ty: 3,
        parent: 12,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 12,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              0.52,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "25",
    layers: [
      {
        ind: 24,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    3.8
                  ],
                  [
                    1.3,
                    5.7
                  ],
                  [
                    4.8,
                    3.6
                  ],
                  [
                    4.8,
                    16.6
                  ],
                  [
                    7.2,
                    16.6
                  ],
                  [
                    7.2,
                    1
                  ],
                  [
                    4.9,
                    1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "29",
    layers: [
      {
        ind: 28,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "32",
    layers: [
      {
        ind: 27,
        ty: 0,
        parent: 23,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 64.545907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 95.04,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 64.548,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 95.04,
                s: [
                  12.901,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  12.901,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 8,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "25"
      },
      {
        ind: 31,
        ty: 0,
        parent: 23,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 63.54,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 73.564806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 63.54,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 73.566,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "29"
      },
      {
        ind: 23,
        ty: 3,
        parent: 22,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 22,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              1.691,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 21,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "40",
    layers: [
      {
        ind: 39,
        ty: 4,
        parent: 38,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    4.5
                  ],
                  [
                    3.9,
                    0
                  ],
                  [
                    0,
                    -4.5
                  ],
                  [
                    -3.9,
                    0
                  ]
                ],
                v: [
                  [
                    7.6,
                    16.7
                  ],
                  [
                    14.2,
                    8.7
                  ],
                  [
                    7.6,
                    0.7
                  ],
                  [
                    1.1,
                    8.8
                  ],
                  [
                    7.6,
                    16.7
                  ]
                ],
                o: [
                  [
                    3.9,
                    0
                  ],
                  [
                    0,
                    -4.5
                  ],
                  [
                    -3.9,
                    0
                  ],
                  [
                    0,
                    4.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -2.5,
                    0
                  ],
                  [
                    0,
                    -3.1
                  ],
                  [
                    2.5,
                    0
                  ],
                  [
                    0,
                    3.2
                  ]
                ],
                v: [
                  [
                    3.6,
                    8.7
                  ],
                  [
                    7.6,
                    3
                  ],
                  [
                    11.6,
                    8.8
                  ],
                  [
                    7.6,
                    14.5
                  ],
                  [
                    3.6,
                    8.7
                  ]
                ],
                o: [
                  [
                    0,
                    -3.2
                  ],
                  [
                    2.5,
                    0
                  ],
                  [
                    0,
                    3.2
                  ],
                  [
                    -2.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 38,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -1,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "44",
    layers: [
      {
        ind: 43,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "47",
    layers: [
      {
        ind: 42,
        ty: 0,
        parent: 37,
        ks: {
          a: {
            a: 0,
            k: [
              -1,
              0
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 137.145907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 167.64,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 137.148,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 167.64,
                s: [
                  12.901,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  12.901,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 14,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "40"
      },
      {
        ind: 46,
        ty: 0,
        parent: 37,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 136.14,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 146.164806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 136.14,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 146.166,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "44"
      },
      {
        ind: 37,
        ty: 3,
        parent: 36,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 36,
        ty: 3,
        parent: 35,
        ks: {
          p: {
            a: 0,
            k: [
              -0.933,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 35,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "54",
    layers: [
      {
        ind: 53,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.9,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    4.4
                  ],
                  [
                    1.2,
                    1.2
                  ],
                  [
                    1.9,
                    0
                  ],
                  [
                    0,
                    -3.2
                  ],
                  [
                    -3,
                    0
                  ],
                  [
                    -0.9,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    3,
                    0
                  ]
                ],
                v: [
                  [
                    5.7,
                    14.6
                  ],
                  [
                    2.5,
                    13.3
                  ],
                  [
                    1.2,
                    15
                  ],
                  [
                    5.7,
                    16.8
                  ],
                  [
                    12.6,
                    8.2
                  ],
                  [
                    10.8,
                    2.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    0.9,
                    6.1
                  ],
                  [
                    6.1,
                    11.2
                  ],
                  [
                    10.2,
                    9.2
                  ],
                  [
                    10.2,
                    9.2
                  ],
                  [
                    5.7,
                    14.6
                  ]
                ],
                o: [
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    1.1
                  ],
                  [
                    4.9,
                    0
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -1,
                    -1
                  ],
                  [
                    -3.1,
                    0
                  ],
                  [
                    0,
                    3
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    2.6
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    -2
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0,
                    1.9
                  ]
                ],
                v: [
                  [
                    3.4,
                    6
                  ],
                  [
                    6.6,
                    3
                  ],
                  [
                    9.9,
                    6
                  ],
                  [
                    6.6,
                    9.1
                  ],
                  [
                    3.4,
                    6
                  ]
                ],
                o: [
                  [
                    0,
                    -1.9
                  ],
                  [
                    1.9,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    -1.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "58",
    layers: [
      {
        ind: 57,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    2.4
                  ],
                  [
                    3.1,
                    0
                  ],
                  [
                    0.6,
                    -2.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    2,
                    -1.7
                  ]
                ],
                v: [
                  [
                    6.5,
                    9.7
                  ],
                  [
                    1,
                    14.4
                  ],
                  [
                    1,
                    16.6
                  ],
                  [
                    11.9,
                    16.6
                  ],
                  [
                    11.9,
                    14.4
                  ],
                  [
                    4.4,
                    14.4
                  ],
                  [
                    8.1,
                    11.3
                  ],
                  [
                    11.9,
                    5.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    1.2,
                    4.5
                  ],
                  [
                    3.4,
                    5.4
                  ],
                  [
                    6.5,
                    3
                  ],
                  [
                    9.3,
                    5.6
                  ],
                  [
                    6.5,
                    9.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.5,
                    -2.1
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -2.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "61",
    layers: [
      {
        ind: 56,
        ty: 0,
        parent: 52,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 209.745907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 240.24,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  13.101,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 209.748,
                s: [
                  13.101,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 240.24,
                s: [
                  13.101,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  13.101,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 13,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "54"
      },
      {
        ind: 60,
        ty: 0,
        parent: 52,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 208.74,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 218.764806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 208.74,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 218.766,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "58"
      },
      {
        ind: 52,
        ty: 3,
        parent: 51,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 51,
        ty: 3,
        parent: 50,
        ks: {
          p: {
            a: 0,
            k: [
              -1.028,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 50,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "94",
    layers: [
      {
        ind: 93,
        ty: 2,
        parent: 92,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 92,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "110",
    layers: [
      {
        ind: 109,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                17.5,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                35,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.41,
                            -1.91
                          ],
                          [
                            -0.92,
                            0.09
                          ],
                          [
                            1.69,
                            -0.98
                          ],
                          [
                            -0.37,
                            -0.85
                          ],
                          [
                            1.45,
                            1.31
                          ],
                          [
                            0.69,
                            -0.62
                          ],
                          [
                            -0.79,
                            1.79
                          ],
                          [
                            0.8,
                            0.46
                          ],
                          [
                            -1.95,
                            -0.21
                          ],
                          [
                            -0.2,
                            0.91
                          ]
                        ],
                        o: [
                          [
                            0.41,
                            -1.91
                          ],
                          [
                            0.19,
                            0.91
                          ],
                          [
                            1.94,
                            -0.21
                          ],
                          [
                            -0.8,
                            0.46
                          ],
                          [
                            0.8,
                            1.79
                          ],
                          [
                            -0.69,
                            -0.62
                          ],
                          [
                            -1.45,
                            1.31
                          ],
                          [
                            0.38,
                            -0.85
                          ],
                          [
                            -1.7,
                            -0.98
                          ],
                          [
                            0.92,
                            0.09
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            11.5,
                            8.55
                          ],
                          [
                            15.05,
                            8.55
                          ],
                          [
                            17.01,
                            9.98
                          ],
                          [
                            18.1,
                            13.34
                          ],
                          [
                            17.35,
                            15.65
                          ],
                          [
                            14.49,
                            17.73
                          ],
                          [
                            12.06,
                            17.73
                          ],
                          [
                            9.2,
                            15.65
                          ],
                          [
                            8.45,
                            13.34
                          ],
                          [
                            9.54,
                            9.98
                          ],
                          [
                            11.5,
                            8.55
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.9,
                        0.01,
                        0.47,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "118",
    layers: [
      {
        ind: 117,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "121",
    layers: [
      {
        ind: 120,
        ty: 0,
        parent: 116,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "118"
      },
      {
        ind: 116,
        ty: 3,
        parent: 115,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 115,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              7.796,
              2.612
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "127",
    layers: [
      {
        ind: 123,
        ty: 0,
        parent: 114,
        ks: {
          a: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          },
          p: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          }
        },
        w: 39.4996,
        h: 39.7037,
        ip: 0,
        op: 361,
        st: 0,
        refId: "121"
      },
      {
        ind: 114,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 125,
        ty: 4,
        parent: 124,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.299,
                1.306
              ]
            },
            s: {
              a: 0,
              k: [
                2.599,
                2.612
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 124,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.024,
              45.102
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 126,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.333,
                26.469
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.666,
                52.938
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "132",
    layers: [
      {
        ind: 78,
        ty: 4,
        parent: 77,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.03
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    -0.91
                  ],
                  [
                    -0.95,
                    0
                  ],
                  [
                    -0.28,
                    0.51
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.39,
                    0
                  ],
                  [
                    0.01,
                    0.49
                  ]
                ],
                v: [
                  [
                    1,
                    4.56
                  ],
                  [
                    3.43,
                    4.56
                  ],
                  [
                    3.44,
                    4.34
                  ],
                  [
                    1.88,
                    2.68
                  ],
                  [
                    0.26,
                    4.34
                  ],
                  [
                    1.94,
                    6.01
                  ],
                  [
                    3.31,
                    5.24
                  ],
                  [
                    2.8,
                    4.89
                  ],
                  [
                    1.93,
                    5.39
                  ],
                  [
                    1,
                    4.56
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.01,
                    -0.08
                  ],
                  [
                    0,
                    -0.97
                  ],
                  [
                    -0.93,
                    0
                  ],
                  [
                    0,
                    0.95
                  ],
                  [
                    0.56,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.19,
                    0.35
                  ],
                  [
                    -0.47,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.44,
                    0
                  ],
                  [
                    -0.02,
                    -0.46
                  ]
                ],
                v: [
                  [
                    1.03,
                    4.02
                  ],
                  [
                    1.88,
                    3.29
                  ],
                  [
                    2.71,
                    4.02
                  ]
                ],
                o: [
                  [
                    0.07,
                    -0.44
                  ],
                  [
                    0.44,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 77,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              13.464,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 80,
        ty: 4,
        parent: 79,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.69,
                    0
                  ],
                  [
                    -0.13,
                    0.05
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.07,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.44,
                    4.8
                  ],
                  [
                    1.44,
                    3.39
                  ],
                  [
                    2.23,
                    3.39
                  ],
                  [
                    2.23,
                    2.75
                  ],
                  [
                    1.44,
                    2.75
                  ],
                  [
                    1.44,
                    1.76
                  ],
                  [
                    0.73,
                    1.76
                  ],
                  [
                    0.73,
                    2.75
                  ],
                  [
                    0.14,
                    2.75
                  ],
                  [
                    0.14,
                    3.39
                  ],
                  [
                    0.73,
                    3.39
                  ],
                  [
                    0.73,
                    4.92
                  ],
                  [
                    1.84,
                    5.99
                  ],
                  [
                    2.22,
                    5.92
                  ],
                  [
                    2.22,
                    5.31
                  ],
                  [
                    1.99,
                    5.34
                  ],
                  [
                    1.44,
                    4.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.71
                  ],
                  [
                    0.09,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.09,
                    0.02
                  ],
                  [
                    -0.35,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 79,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              11.022,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 82,
        ty: 4,
        parent: 81,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ]
                ],
                v: [
                  [
                    0.37,
                    1.71
                  ],
                  [
                    0.83,
                    2.17
                  ],
                  [
                    1.3,
                    1.71
                  ],
                  [
                    0.83,
                    1.23
                  ],
                  [
                    0.37,
                    1.71
                  ]
                ],
                o: [
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.48,
                    5.95
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    1.19,
                    2.75
                  ],
                  [
                    1.19,
                    5.95
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 81,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              9.354,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 84,
        ty: 4,
        parent: 83,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    2.75
                  ],
                  [
                    1.46,
                    5.95
                  ],
                  [
                    2.17,
                    5.95
                  ],
                  [
                    3.51,
                    2.75
                  ],
                  [
                    2.76,
                    2.75
                  ],
                  [
                    1.82,
                    5.12
                  ],
                  [
                    0.88,
                    2.75
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 83,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              5.73,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 86,
        ty: 4,
        parent: 85,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.82,
                    0
                  ],
                  [
                    0.31,
                    -0.41
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    -0.46
                  ]
                ],
                v: [
                  [
                    2.64,
                    4.15
                  ],
                  [
                    2.64,
                    5.95
                  ],
                  [
                    3.35,
                    5.95
                  ],
                  [
                    3.35,
                    3.97
                  ],
                  [
                    2.15,
                    2.68
                  ],
                  [
                    1.18,
                    3.18
                  ],
                  [
                    1.18,
                    2.75
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    0.48,
                    5.95
                  ],
                  [
                    1.19,
                    5.95
                  ],
                  [
                    1.19,
                    4.17
                  ],
                  [
                    1.93,
                    3.36
                  ],
                  [
                    2.64,
                    4.15
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    -0.25,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.49
                  ],
                  [
                    0.49,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 85,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              1.932,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 88,
        ty: 4,
        parent: 87,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    5.95
                  ],
                  [
                    1.34,
                    5.95
                  ],
                  [
                    1.34,
                    1.25
                  ],
                  [
                    0.6,
                    1.25
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 87,
        ty: 3,
        parent: 76,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 76,
        ty: 3,
        parent: 75,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 75,
        ty: 3,
        parent: 74,
        ks: {
          p: {
            a: 0,
            k: [
              2.072,
              1.141
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 74,
        ty: 3,
        parent: 73,
        ks: {
          p: {
            a: 0,
            k: [
              34.315,
              4.741
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 90,
        ty: 4,
        parent: 89,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 89,
        ty: 3,
        parent: 73,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 73,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 96,
        ty: 0,
        parent: 91,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "94"
      },
      {
        ind: 91,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 100,
        ty: 4,
        parent: 99,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 99,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 102,
        ty: 4,
        parent: 101,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 101,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 105,
        ty: 4,
        parent: 104,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 104,
        ty: 3,
        parent: 103,
        ks: {
          p: {
            a: 0,
            k: [
              14.813,
              4.266
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 112,
        ty: 0,
        parent: 108,
        ks: {},
        w: 35,
        h: 35,
        ip: 0,
        op: 361,
        st: 0,
        refId: "110"
      },
      {
        ind: 108,
        ty: 3,
        parent: 107,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 107,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              -10.395,
              -5.224
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 106,
        ty: 3,
        parent: 103,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 103,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              -0.173
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 98,
        ty: 3,
        parent: 97,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 97,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 130,
        ty: 4,
        td: 1,
        parent: 113,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.333,
                26.469
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.666,
                52.938
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 129,
        ty: 0,
        tt: 1,
        parent: 113,
        ks: {},
        w: 53,
        h: 53,
        ip: 0,
        op: 361,
        st: 0,
        refId: "127"
      },
      {
        ind: 113,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              10.649,
              22.519
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 131,
        ty: 4,
        parent: 72,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 72,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              6
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "136",
    layers: [
      {
        ind: 134,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              13,
              6
            ]
          },
          p: {
            a: 0,
            k: [
              58.6,
              51.6
            ]
          }
        },
        w: 191,
        h: 102,
        ip: 0,
        op: 361,
        st: 0,
        refId: "132"
      },
      {
        ind: 135,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                147.345,
                99.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                294.69,
                199.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "162",
    layers: [
      {
        ind: 161,
        ty: 2,
        parent: 160,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 160,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "178",
    layers: [
      {
        ind: 177,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "181",
    layers: [
      {
        ind: 180,
        ty: 0,
        parent: 176,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "178"
      },
      {
        ind: 176,
        ty: 3,
        parent: 175,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 175,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "187",
    layers: [
      {
        ind: 183,
        ty: 0,
        parent: 174,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "181"
      },
      {
        ind: 174,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 185,
        ty: 4,
        parent: 184,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 184,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 186,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "192",
    layers: [
      {
        ind: 146,
        ty: 4,
        parent: 145,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.05
                  ],
                  [
                    1.49,
                    0
                  ],
                  [
                    0,
                    -1.36
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    -0.41,
                    0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.59,
                    0
                  ],
                  [
                    0.02,
                    0.74
                  ]
                ],
                v: [
                  [
                    1.49,
                    6.84
                  ],
                  [
                    5.14,
                    6.84
                  ],
                  [
                    5.16,
                    6.5
                  ],
                  [
                    2.82,
                    4.02
                  ],
                  [
                    0.4,
                    6.51
                  ],
                  [
                    2.92,
                    9.01
                  ],
                  [
                    4.97,
                    7.85
                  ],
                  [
                    4.19,
                    7.34
                  ],
                  [
                    2.9,
                    8.09
                  ],
                  [
                    1.49,
                    6.84
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.02,
                    -0.12
                  ],
                  [
                    0,
                    -1.45
                  ],
                  [
                    -1.39,
                    0
                  ],
                  [
                    0,
                    1.43
                  ],
                  [
                    0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.28,
                    0.53
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    -0.04,
                    -0.68
                  ]
                ],
                v: [
                  [
                    1.55,
                    6.03
                  ],
                  [
                    2.82,
                    4.94
                  ],
                  [
                    4.07,
                    6.03
                  ]
                ],
                o: [
                  [
                    0.1,
                    -0.66
                  ],
                  [
                    0.67,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 145,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              20.196,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 148,
        ty: 4,
        parent: 147,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    -0.2,
                    0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.6
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.2
                  ],
                  [
                    2.2,
                    5.1
                  ],
                  [
                    3.3,
                    5.1
                  ],
                  [
                    3.3,
                    4.1
                  ],
                  [
                    2.2,
                    4.1
                  ],
                  [
                    2.2,
                    2.6
                  ],
                  [
                    1.1,
                    2.6
                  ],
                  [
                    1.1,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ],
                  [
                    0.2,
                    5.1
                  ],
                  [
                    1.1,
                    5.1
                  ],
                  [
                    1.1,
                    7.4
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    3.3,
                    8
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    2.2,
                    7.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 147,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              16.533,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 150,
        ty: 4,
        parent: 149,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ]
                ],
                v: [
                  [
                    0.5,
                    2.6
                  ],
                  [
                    1.3,
                    3.3
                  ],
                  [
                    2,
                    2.6
                  ],
                  [
                    1.3,
                    1.8
                  ],
                  [
                    0.5,
                    2.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.7,
                    8.9
                  ],
                  [
                    0.7,
                    4.1
                  ],
                  [
                    1.8,
                    4.1
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 149,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              14.031,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 152,
        ty: 4,
        parent: 151,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.2,
                    4.1
                  ],
                  [
                    2.2,
                    8.9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    5.3,
                    4.1
                  ],
                  [
                    4.1,
                    4.1
                  ],
                  [
                    2.7,
                    7.7
                  ],
                  [
                    1.3,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 151,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              8.595,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 154,
        ty: 4,
        parent: 153,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 153,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              2.898,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 156,
        ty: 4,
        parent: 155,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.9,
                    8.9
                  ],
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    1.9
                  ],
                  [
                    0.9,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 155,
        ty: 3,
        parent: 144,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 144,
        ty: 3,
        parent: 143,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 143,
        ty: 3,
        parent: 142,
        ks: {
          p: {
            a: 0,
            k: [
              2.517,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 142,
        ty: 3,
        parent: 141,
        ks: {
          p: {
            a: 0,
            k: [
              29.582,
              2.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 158,
        ty: 4,
        parent: 157,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 157,
        ty: 3,
        parent: 141,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 141,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 164,
        ty: 0,
        parent: 159,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "162"
      },
      {
        ind: 159,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 168,
        ty: 4,
        parent: 167,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 167,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 170,
        ty: 4,
        parent: 169,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 169,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 172,
        ty: 4,
        parent: 171,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.883,
                5.926
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.767,
                11.852
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 171,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 166,
        ty: 3,
        parent: 165,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 165,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 190,
        ty: 4,
        td: 1,
        parent: 173,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 189,
        ty: 0,
        tt: 1,
        parent: 173,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "187"
      },
      {
        ind: 173,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 191,
        ty: 4,
        parent: 140,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 140,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              1
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "196",
    layers: [
      {
        ind: 194,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              3,
              1
            ]
          },
          p: {
            a: 0,
            k: [
              48.6,
              46.6
            ]
          }
        },
        w: 181,
        h: 97,
        ip: 0,
        op: 361,
        st: 0,
        refId: "192"
      },
      {
        ind: 195,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                137.345,
                94.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                274.69,
                189.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "222",
    layers: [
      {
        ind: 221,
        ty: 2,
        parent: 220,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 220,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "238",
    layers: [
      {
        ind: 237,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "241",
    layers: [
      {
        ind: 240,
        ty: 0,
        parent: 236,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "238"
      },
      {
        ind: 236,
        ty: 3,
        parent: 235,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 235,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "247",
    layers: [
      {
        ind: 243,
        ty: 0,
        parent: 234,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "241"
      },
      {
        ind: 234,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 245,
        ty: 4,
        parent: 244,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 244,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 246,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "252",
    layers: [
      {
        ind: 206,
        ty: 4,
        parent: 205,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.05
                  ],
                  [
                    1.49,
                    0
                  ],
                  [
                    0,
                    -1.36
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    -0.41,
                    0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.59,
                    0
                  ],
                  [
                    0.02,
                    0.74
                  ]
                ],
                v: [
                  [
                    1.49,
                    6.84
                  ],
                  [
                    5.14,
                    6.84
                  ],
                  [
                    5.16,
                    6.5
                  ],
                  [
                    2.82,
                    4.02
                  ],
                  [
                    0.4,
                    6.51
                  ],
                  [
                    2.92,
                    9.01
                  ],
                  [
                    4.97,
                    7.85
                  ],
                  [
                    4.19,
                    7.34
                  ],
                  [
                    2.9,
                    8.09
                  ],
                  [
                    1.49,
                    6.84
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.02,
                    -0.12
                  ],
                  [
                    0,
                    -1.45
                  ],
                  [
                    -1.39,
                    0
                  ],
                  [
                    0,
                    1.43
                  ],
                  [
                    0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.28,
                    0.53
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    -0.04,
                    -0.68
                  ]
                ],
                v: [
                  [
                    1.55,
                    6.03
                  ],
                  [
                    2.82,
                    4.94
                  ],
                  [
                    4.07,
                    6.03
                  ]
                ],
                o: [
                  [
                    0.1,
                    -0.66
                  ],
                  [
                    0.67,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 205,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              20.196,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 208,
        ty: 4,
        parent: 207,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    -0.2,
                    0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.6
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.2
                  ],
                  [
                    2.2,
                    5.1
                  ],
                  [
                    3.3,
                    5.1
                  ],
                  [
                    3.3,
                    4.1
                  ],
                  [
                    2.2,
                    4.1
                  ],
                  [
                    2.2,
                    2.6
                  ],
                  [
                    1.1,
                    2.6
                  ],
                  [
                    1.1,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ],
                  [
                    0.2,
                    5.1
                  ],
                  [
                    1.1,
                    5.1
                  ],
                  [
                    1.1,
                    7.4
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    3.3,
                    8
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    2.2,
                    7.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 207,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              16.533,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 210,
        ty: 4,
        parent: 209,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ]
                ],
                v: [
                  [
                    0.5,
                    2.6
                  ],
                  [
                    1.3,
                    3.3
                  ],
                  [
                    2,
                    2.6
                  ],
                  [
                    1.3,
                    1.8
                  ],
                  [
                    0.5,
                    2.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.7,
                    8.9
                  ],
                  [
                    0.7,
                    4.1
                  ],
                  [
                    1.8,
                    4.1
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 209,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              14.031,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 212,
        ty: 4,
        parent: 211,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.2,
                    4.1
                  ],
                  [
                    2.2,
                    8.9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    5.3,
                    4.1
                  ],
                  [
                    4.1,
                    4.1
                  ],
                  [
                    2.7,
                    7.7
                  ],
                  [
                    1.3,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 211,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              8.595,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 214,
        ty: 4,
        parent: 213,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 213,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              2.898,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 216,
        ty: 4,
        parent: 215,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.9,
                    8.9
                  ],
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    1.9
                  ],
                  [
                    0.9,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 215,
        ty: 3,
        parent: 204,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 204,
        ty: 3,
        parent: 203,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 203,
        ty: 3,
        parent: 202,
        ks: {
          p: {
            a: 0,
            k: [
              2.517,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 202,
        ty: 3,
        parent: 201,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 52.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.986,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 62.382,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 65.778,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 67.476,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 69.174,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 70.026,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 70.878,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 71.304,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.736,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.952,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.168,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 218,
        ty: 4,
        parent: 217,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 59.562,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 72.6,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 217,
        ty: 3,
        parent: 201,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 52.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.968,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 62.352,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 65.736,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 67.428,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 69.126,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 69.978,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 70.824,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 71.25,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.676,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.892,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.108,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 201,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 224,
        ty: 0,
        parent: 219,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "222"
      },
      {
        ind: 219,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 228,
        ty: 4,
        parent: 227,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 227,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 230,
        ty: 4,
        parent: 229,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 229,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 232,
        ty: 4,
        parent: 231,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 231,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 226,
        ty: 3,
        parent: 225,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 225,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 250,
        ty: 4,
        td: 1,
        parent: 233,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 249,
        ty: 0,
        tt: 1,
        parent: 233,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "247"
      },
      {
        ind: 233,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 251,
        ty: 4,
        parent: 200,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 200,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "256",
    layers: [
      {
        ind: 254,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "252"
      },
      {
        ind: 255,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "282",
    layers: [
      {
        ind: 281,
        ty: 2,
        parent: 280,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 280,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "298",
    layers: [
      {
        ind: 297,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "301",
    layers: [
      {
        ind: 300,
        ty: 0,
        parent: 296,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "298"
      },
      {
        ind: 296,
        ty: 3,
        parent: 295,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 295,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "307",
    layers: [
      {
        ind: 303,
        ty: 0,
        parent: 294,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "301"
      },
      {
        ind: 294,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 305,
        ty: 4,
        parent: 304,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 304,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 306,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "312",
    layers: [
      {
        ind: 266,
        ty: 4,
        parent: 265,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.05
                  ],
                  [
                    1.49,
                    0
                  ],
                  [
                    0,
                    -1.36
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    -0.41,
                    0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.59,
                    0
                  ],
                  [
                    0.02,
                    0.74
                  ]
                ],
                v: [
                  [
                    1.49,
                    6.84
                  ],
                  [
                    5.14,
                    6.84
                  ],
                  [
                    5.16,
                    6.5
                  ],
                  [
                    2.82,
                    4.02
                  ],
                  [
                    0.4,
                    6.51
                  ],
                  [
                    2.92,
                    9.01
                  ],
                  [
                    4.97,
                    7.85
                  ],
                  [
                    4.19,
                    7.34
                  ],
                  [
                    2.9,
                    8.09
                  ],
                  [
                    1.49,
                    6.84
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.02,
                    -0.12
                  ],
                  [
                    0,
                    -1.45
                  ],
                  [
                    -1.39,
                    0
                  ],
                  [
                    0,
                    1.43
                  ],
                  [
                    0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.28,
                    0.53
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    -0.04,
                    -0.68
                  ]
                ],
                v: [
                  [
                    1.55,
                    6.03
                  ],
                  [
                    2.82,
                    4.94
                  ],
                  [
                    4.07,
                    6.03
                  ]
                ],
                o: [
                  [
                    0.1,
                    -0.66
                  ],
                  [
                    0.67,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 265,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              20.196,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 268,
        ty: 4,
        parent: 267,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    -0.2,
                    0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.6
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.2
                  ],
                  [
                    2.2,
                    5.1
                  ],
                  [
                    3.3,
                    5.1
                  ],
                  [
                    3.3,
                    4.1
                  ],
                  [
                    2.2,
                    4.1
                  ],
                  [
                    2.2,
                    2.6
                  ],
                  [
                    1.1,
                    2.6
                  ],
                  [
                    1.1,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ],
                  [
                    0.2,
                    5.1
                  ],
                  [
                    1.1,
                    5.1
                  ],
                  [
                    1.1,
                    7.4
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    3.3,
                    8
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    2.2,
                    7.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 267,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              16.533,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 270,
        ty: 4,
        parent: 269,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ]
                ],
                v: [
                  [
                    0.5,
                    2.6
                  ],
                  [
                    1.3,
                    3.3
                  ],
                  [
                    2,
                    2.6
                  ],
                  [
                    1.3,
                    1.8
                  ],
                  [
                    0.5,
                    2.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.7,
                    8.9
                  ],
                  [
                    0.7,
                    4.1
                  ],
                  [
                    1.8,
                    4.1
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 269,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              14.031,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 272,
        ty: 4,
        parent: 271,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.2,
                    4.1
                  ],
                  [
                    2.2,
                    8.9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    5.3,
                    4.1
                  ],
                  [
                    4.1,
                    4.1
                  ],
                  [
                    2.7,
                    7.7
                  ],
                  [
                    1.3,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 271,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              8.595,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 274,
        ty: 4,
        parent: 273,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 273,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              2.898,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 276,
        ty: 4,
        parent: 275,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.9,
                    8.9
                  ],
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    1.9
                  ],
                  [
                    0.9,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 275,
        ty: 3,
        parent: 264,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 264,
        ty: 3,
        parent: 263,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 263,
        ty: 3,
        parent: 262,
        ks: {
          p: {
            a: 0,
            k: [
              2.517,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 262,
        ty: 3,
        parent: 261,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 124.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 131.586,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 134.982,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 138.378,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 140.076,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 141.774,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 142.626,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 143.478,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 143.904,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.336,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.552,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.768,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 278,
        ty: 4,
        parent: 277,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 132.162,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 145.2,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 277,
        ty: 3,
        parent: 261,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 124.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 131.568,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 134.952,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 138.336,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 140.028,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 141.726,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 142.578,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 143.424,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 143.85,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.276,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.492,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.708,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 261,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 284,
        ty: 0,
        parent: 279,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "282"
      },
      {
        ind: 279,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 288,
        ty: 4,
        parent: 287,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 287,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 290,
        ty: 4,
        parent: 289,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 289,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 292,
        ty: 4,
        parent: 291,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 291,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 286,
        ty: 3,
        parent: 285,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 285,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 310,
        ty: 4,
        td: 1,
        parent: 293,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 309,
        ty: 0,
        tt: 1,
        parent: 293,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "307"
      },
      {
        ind: 293,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 311,
        ty: 4,
        parent: 260,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 260,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "316",
    layers: [
      {
        ind: 314,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "312"
      },
      {
        ind: 315,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "342",
    layers: [
      {
        ind: 341,
        ty: 2,
        parent: 340,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 340,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "358",
    layers: [
      {
        ind: 357,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "361",
    layers: [
      {
        ind: 360,
        ty: 0,
        parent: 356,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "358"
      },
      {
        ind: 356,
        ty: 3,
        parent: 355,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 355,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "367",
    layers: [
      {
        ind: 363,
        ty: 0,
        parent: 354,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "361"
      },
      {
        ind: 354,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 365,
        ty: 4,
        parent: 364,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 364,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 366,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "372",
    layers: [
      {
        ind: 326,
        ty: 4,
        parent: 325,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.05
                  ],
                  [
                    1.49,
                    0
                  ],
                  [
                    0,
                    -1.36
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    -0.41,
                    0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.59,
                    0
                  ],
                  [
                    0.02,
                    0.74
                  ]
                ],
                v: [
                  [
                    1.49,
                    6.84
                  ],
                  [
                    5.14,
                    6.84
                  ],
                  [
                    5.16,
                    6.5
                  ],
                  [
                    2.82,
                    4.02
                  ],
                  [
                    0.4,
                    6.51
                  ],
                  [
                    2.92,
                    9.01
                  ],
                  [
                    4.97,
                    7.85
                  ],
                  [
                    4.19,
                    7.34
                  ],
                  [
                    2.9,
                    8.09
                  ],
                  [
                    1.49,
                    6.84
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.02,
                    -0.12
                  ],
                  [
                    0,
                    -1.45
                  ],
                  [
                    -1.39,
                    0
                  ],
                  [
                    0,
                    1.43
                  ],
                  [
                    0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.28,
                    0.53
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    -0.04,
                    -0.68
                  ]
                ],
                v: [
                  [
                    1.55,
                    6.03
                  ],
                  [
                    2.82,
                    4.94
                  ],
                  [
                    4.07,
                    6.03
                  ]
                ],
                o: [
                  [
                    0.1,
                    -0.66
                  ],
                  [
                    0.67,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 204.762,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 217.8,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 325,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              20.196,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 328,
        ty: 4,
        parent: 327,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    -0.2,
                    0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.6
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.2
                  ],
                  [
                    2.2,
                    5.1
                  ],
                  [
                    3.3,
                    5.1
                  ],
                  [
                    3.3,
                    4.1
                  ],
                  [
                    2.2,
                    4.1
                  ],
                  [
                    2.2,
                    2.6
                  ],
                  [
                    1.1,
                    2.6
                  ],
                  [
                    1.1,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ],
                  [
                    0.2,
                    5.1
                  ],
                  [
                    1.1,
                    5.1
                  ],
                  [
                    1.1,
                    7.4
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    3.3,
                    8
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    2.2,
                    7.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 204.762,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 217.8,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 327,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              16.533,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 330,
        ty: 4,
        parent: 329,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ]
                ],
                v: [
                  [
                    0.5,
                    2.6
                  ],
                  [
                    1.3,
                    3.3
                  ],
                  [
                    2,
                    2.6
                  ],
                  [
                    1.3,
                    1.8
                  ],
                  [
                    0.5,
                    2.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.7,
                    8.9
                  ],
                  [
                    0.7,
                    4.1
                  ],
                  [
                    1.8,
                    4.1
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 204.762,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 217.8,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 329,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              14.031,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 332,
        ty: 4,
        parent: 331,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.2,
                    4.1
                  ],
                  [
                    2.2,
                    8.9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    5.3,
                    4.1
                  ],
                  [
                    4.1,
                    4.1
                  ],
                  [
                    2.7,
                    7.7
                  ],
                  [
                    1.3,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 204.762,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 217.8,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 331,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              8.595,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 334,
        ty: 4,
        parent: 333,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 204.762,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 217.8,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 333,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              2.898,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 336,
        ty: 4,
        parent: 335,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.9,
                    8.9
                  ],
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    1.9
                  ],
                  [
                    0.9,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 204.762,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 217.8,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 335,
        ty: 3,
        parent: 324,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 324,
        ty: 3,
        parent: 323,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 323,
        ty: 3,
        parent: 322,
        ks: {
          p: {
            a: 0,
            k: [
              2.517,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 322,
        ty: 3,
        parent: 321,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 224.586,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 227.982,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 231.378,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 233.076,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 234.774,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 235.626,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 236.478,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 236.904,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 237.336,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 237.552,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 237.768,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 338,
        ty: 4,
        parent: 337,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 204.762,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 217.8,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 337,
        ty: 3,
        parent: 321,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 197.4,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 204.168,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 207.552,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 210.936,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 212.628,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 214.326,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 215.178,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 216.024,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 216.45,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 216.876,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.092,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.308,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 321,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 344,
        ty: 0,
        parent: 339,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "342"
      },
      {
        ind: 339,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 348,
        ty: 4,
        parent: 347,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 347,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 350,
        ty: 4,
        parent: 349,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 349,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 352,
        ty: 4,
        parent: 351,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 351,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 346,
        ty: 3,
        parent: 345,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 345,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 370,
        ty: 4,
        td: 1,
        parent: 353,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 369,
        ty: 0,
        tt: 1,
        parent: 353,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "367"
      },
      {
        ind: 353,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 371,
        ty: 4,
        parent: 320,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 320,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "376",
    layers: [
      {
        ind: 374,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "372"
      },
      {
        ind: 375,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "402",
    layers: [
      {
        ind: 401,
        ty: 2,
        parent: 400,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 400,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "418",
    layers: [
      {
        ind: 417,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "421",
    layers: [
      {
        ind: 420,
        ty: 0,
        parent: 416,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "418"
      },
      {
        ind: 416,
        ty: 3,
        parent: 415,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 415,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "427",
    layers: [
      {
        ind: 423,
        ty: 0,
        parent: 414,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "421"
      },
      {
        ind: 414,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 425,
        ty: 4,
        parent: 424,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 424,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 426,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "432",
    layers: [
      {
        ind: 386,
        ty: 4,
        parent: 385,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.05
                  ],
                  [
                    1.49,
                    0
                  ],
                  [
                    0,
                    -1.36
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    -0.41,
                    0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.59,
                    0
                  ],
                  [
                    0.02,
                    0.74
                  ]
                ],
                v: [
                  [
                    1.49,
                    6.84
                  ],
                  [
                    5.14,
                    6.84
                  ],
                  [
                    5.16,
                    6.5
                  ],
                  [
                    2.82,
                    4.02
                  ],
                  [
                    0.4,
                    6.51
                  ],
                  [
                    2.92,
                    9.01
                  ],
                  [
                    4.97,
                    7.85
                  ],
                  [
                    4.19,
                    7.34
                  ],
                  [
                    2.9,
                    8.09
                  ],
                  [
                    1.49,
                    6.84
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.02,
                    -0.12
                  ],
                  [
                    0,
                    -1.45
                  ],
                  [
                    -1.39,
                    0
                  ],
                  [
                    0,
                    1.43
                  ],
                  [
                    0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.28,
                    0.53
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    -0.04,
                    -0.68
                  ]
                ],
                v: [
                  [
                    1.55,
                    6.03
                  ],
                  [
                    2.82,
                    4.94
                  ],
                  [
                    4.07,
                    6.03
                  ]
                ],
                o: [
                  [
                    0.1,
                    -0.66
                  ],
                  [
                    0.67,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 385,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              20.196,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 388,
        ty: 4,
        parent: 387,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    -0.2,
                    0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.6
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.2
                  ],
                  [
                    2.2,
                    5.1
                  ],
                  [
                    3.3,
                    5.1
                  ],
                  [
                    3.3,
                    4.1
                  ],
                  [
                    2.2,
                    4.1
                  ],
                  [
                    2.2,
                    2.6
                  ],
                  [
                    1.1,
                    2.6
                  ],
                  [
                    1.1,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ],
                  [
                    0.2,
                    5.1
                  ],
                  [
                    1.1,
                    5.1
                  ],
                  [
                    1.1,
                    7.4
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    3.3,
                    8
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    2.2,
                    7.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 387,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              16.533,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 390,
        ty: 4,
        parent: 389,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ]
                ],
                v: [
                  [
                    0.5,
                    2.6
                  ],
                  [
                    1.3,
                    3.3
                  ],
                  [
                    2,
                    2.6
                  ],
                  [
                    1.3,
                    1.8
                  ],
                  [
                    0.5,
                    2.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.7,
                    8.9
                  ],
                  [
                    0.7,
                    4.1
                  ],
                  [
                    1.8,
                    4.1
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 389,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              14.031,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 392,
        ty: 4,
        parent: 391,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.2,
                    4.1
                  ],
                  [
                    2.2,
                    8.9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    5.3,
                    4.1
                  ],
                  [
                    4.1,
                    4.1
                  ],
                  [
                    2.7,
                    7.7
                  ],
                  [
                    1.3,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 391,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              8.595,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 394,
        ty: 4,
        parent: 393,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 393,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              2.898,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 396,
        ty: 4,
        parent: 395,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.9,
                    8.9
                  ],
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    1.9
                  ],
                  [
                    0.9,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 395,
        ty: 3,
        parent: 384,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 384,
        ty: 3,
        parent: 383,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 383,
        ty: 3,
        parent: 382,
        ks: {
          p: {
            a: 0,
            k: [
              2.517,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 382,
        ty: 3,
        parent: 381,
        ks: {
          p: {
            a: 0,
            k: [
              29.582,
              2.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 398,
        ty: 4,
        parent: 397,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    1,
                    1,
                    1
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 397,
        ty: 3,
        parent: 381,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 381,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 404,
        ty: 0,
        parent: 399,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "402"
      },
      {
        ind: 399,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 408,
        ty: 4,
        parent: 407,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 407,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 410,
        ty: 4,
        parent: 409,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 409,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 412,
        ty: 4,
        parent: 411,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 411,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 406,
        ty: 3,
        parent: 405,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 405,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 430,
        ty: 4,
        td: 1,
        parent: 413,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 429,
        ty: 0,
        tt: 1,
        parent: 413,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "427"
      },
      {
        ind: 413,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 431,
        ty: 4,
        parent: 380,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 380,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              1
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "436",
    layers: [
      {
        ind: 434,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              3,
              1
            ]
          },
          p: {
            a: 0,
            k: [
              48.6,
              46.6
            ]
          }
        },
        w: 181,
        h: 97,
        ip: 0,
        op: 361,
        st: 0,
        refId: "432"
      },
      {
        ind: 435,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                137.345,
                94.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                274.69,
                189.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "462",
    layers: [
      {
        ind: 461,
        ty: 2,
        parent: 460,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 460,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "478",
    layers: [
      {
        ind: 477,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                17.5,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                35,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.41,
                            -1.91
                          ],
                          [
                            -0.92,
                            0.09
                          ],
                          [
                            1.69,
                            -0.98
                          ],
                          [
                            -0.37,
                            -0.85
                          ],
                          [
                            1.45,
                            1.31
                          ],
                          [
                            0.69,
                            -0.62
                          ],
                          [
                            -0.79,
                            1.79
                          ],
                          [
                            0.8,
                            0.46
                          ],
                          [
                            -1.95,
                            -0.21
                          ],
                          [
                            -0.2,
                            0.91
                          ]
                        ],
                        o: [
                          [
                            0.41,
                            -1.91
                          ],
                          [
                            0.19,
                            0.91
                          ],
                          [
                            1.94,
                            -0.21
                          ],
                          [
                            -0.8,
                            0.46
                          ],
                          [
                            0.8,
                            1.79
                          ],
                          [
                            -0.69,
                            -0.62
                          ],
                          [
                            -1.45,
                            1.31
                          ],
                          [
                            0.38,
                            -0.85
                          ],
                          [
                            -1.7,
                            -0.98
                          ],
                          [
                            0.92,
                            0.09
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            11.5,
                            8.55
                          ],
                          [
                            15.05,
                            8.55
                          ],
                          [
                            17.01,
                            9.98
                          ],
                          [
                            18.1,
                            13.34
                          ],
                          [
                            17.35,
                            15.65
                          ],
                          [
                            14.49,
                            17.73
                          ],
                          [
                            12.06,
                            17.73
                          ],
                          [
                            9.2,
                            15.65
                          ],
                          [
                            8.45,
                            13.34
                          ],
                          [
                            9.54,
                            9.98
                          ],
                          [
                            11.5,
                            8.55
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.9,
                        0.01,
                        0.47,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "486",
    layers: [
      {
        ind: 485,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "489",
    layers: [
      {
        ind: 488,
        ty: 0,
        parent: 484,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "486"
      },
      {
        ind: 484,
        ty: 3,
        parent: 483,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 483,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.233,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "495",
    layers: [
      {
        ind: 491,
        ty: 0,
        parent: 482,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "489"
      },
      {
        ind: 482,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 493,
        ty: 4,
        parent: 492,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 492,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 494,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "500",
    layers: [
      {
        ind: 446,
        ty: 4,
        parent: 445,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.03
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    -0.91
                  ],
                  [
                    -0.95,
                    0
                  ],
                  [
                    -0.28,
                    0.51
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.39,
                    0
                  ],
                  [
                    0.01,
                    0.49
                  ]
                ],
                v: [
                  [
                    1,
                    4.56
                  ],
                  [
                    3.43,
                    4.56
                  ],
                  [
                    3.44,
                    4.34
                  ],
                  [
                    1.88,
                    2.68
                  ],
                  [
                    0.26,
                    4.34
                  ],
                  [
                    1.94,
                    6.01
                  ],
                  [
                    3.31,
                    5.24
                  ],
                  [
                    2.8,
                    4.89
                  ],
                  [
                    1.93,
                    5.39
                  ],
                  [
                    1,
                    4.56
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.01,
                    -0.08
                  ],
                  [
                    0,
                    -0.97
                  ],
                  [
                    -0.93,
                    0
                  ],
                  [
                    0,
                    0.95
                  ],
                  [
                    0.56,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.19,
                    0.35
                  ],
                  [
                    -0.47,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.44,
                    0
                  ],
                  [
                    -0.02,
                    -0.46
                  ]
                ],
                v: [
                  [
                    1.03,
                    4.02
                  ],
                  [
                    1.88,
                    3.29
                  ],
                  [
                    2.71,
                    4.02
                  ]
                ],
                o: [
                  [
                    0.07,
                    -0.44
                  ],
                  [
                    0.44,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 445,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              13.464,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 448,
        ty: 4,
        parent: 447,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.69,
                    0
                  ],
                  [
                    -0.13,
                    0.05
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.07,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.44,
                    4.8
                  ],
                  [
                    1.44,
                    3.39
                  ],
                  [
                    2.23,
                    3.39
                  ],
                  [
                    2.23,
                    2.75
                  ],
                  [
                    1.44,
                    2.75
                  ],
                  [
                    1.44,
                    1.76
                  ],
                  [
                    0.73,
                    1.76
                  ],
                  [
                    0.73,
                    2.75
                  ],
                  [
                    0.14,
                    2.75
                  ],
                  [
                    0.14,
                    3.39
                  ],
                  [
                    0.73,
                    3.39
                  ],
                  [
                    0.73,
                    4.92
                  ],
                  [
                    1.84,
                    5.99
                  ],
                  [
                    2.22,
                    5.92
                  ],
                  [
                    2.22,
                    5.31
                  ],
                  [
                    1.99,
                    5.34
                  ],
                  [
                    1.44,
                    4.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.71
                  ],
                  [
                    0.09,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.09,
                    0.02
                  ],
                  [
                    -0.35,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 447,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              11.022,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 450,
        ty: 4,
        parent: 449,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ]
                ],
                v: [
                  [
                    0.37,
                    1.71
                  ],
                  [
                    0.83,
                    2.17
                  ],
                  [
                    1.3,
                    1.71
                  ],
                  [
                    0.83,
                    1.23
                  ],
                  [
                    0.37,
                    1.71
                  ]
                ],
                o: [
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.48,
                    5.95
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    1.19,
                    2.75
                  ],
                  [
                    1.19,
                    5.95
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 449,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              9.354,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 452,
        ty: 4,
        parent: 451,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    2.75
                  ],
                  [
                    1.46,
                    5.95
                  ],
                  [
                    2.17,
                    5.95
                  ],
                  [
                    3.51,
                    2.75
                  ],
                  [
                    2.76,
                    2.75
                  ],
                  [
                    1.82,
                    5.12
                  ],
                  [
                    0.88,
                    2.75
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 451,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              5.73,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 454,
        ty: 4,
        parent: 453,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.82,
                    0
                  ],
                  [
                    0.31,
                    -0.41
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    -0.46
                  ]
                ],
                v: [
                  [
                    2.64,
                    4.15
                  ],
                  [
                    2.64,
                    5.95
                  ],
                  [
                    3.35,
                    5.95
                  ],
                  [
                    3.35,
                    3.97
                  ],
                  [
                    2.15,
                    2.68
                  ],
                  [
                    1.18,
                    3.18
                  ],
                  [
                    1.18,
                    2.75
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    0.48,
                    5.95
                  ],
                  [
                    1.19,
                    5.95
                  ],
                  [
                    1.19,
                    4.17
                  ],
                  [
                    1.93,
                    3.36
                  ],
                  [
                    2.64,
                    4.15
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    -0.25,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.49
                  ],
                  [
                    0.49,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 453,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              1.932,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 456,
        ty: 4,
        parent: 455,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    5.95
                  ],
                  [
                    1.34,
                    5.95
                  ],
                  [
                    1.34,
                    1.25
                  ],
                  [
                    0.6,
                    1.25
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 455,
        ty: 3,
        parent: 444,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 444,
        ty: 3,
        parent: 443,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 443,
        ty: 3,
        parent: 442,
        ks: {
          p: {
            a: 0,
            k: [
              2.072,
              1.141
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 442,
        ty: 3,
        parent: 441,
        ks: {
          p: {
            a: 0,
            k: [
              34.315,
              4.741
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 458,
        ty: 4,
        parent: 457,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.129,
                    0.322,
                    0.584
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    1,
                    1,
                    1
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 457,
        ty: 3,
        parent: 441,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 441,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 464,
        ty: 0,
        parent: 459,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "462"
      },
      {
        ind: 459,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 468,
        ty: 4,
        parent: 467,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 467,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 470,
        ty: 4,
        parent: 469,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 469,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 473,
        ty: 4,
        parent: 472,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 472,
        ty: 3,
        parent: 471,
        ks: {
          p: {
            a: 0,
            k: [
              14.813,
              4.266
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 480,
        ty: 0,
        parent: 476,
        ks: {},
        w: 35,
        h: 35,
        ip: 0,
        op: 361,
        st: 0,
        refId: "478"
      },
      {
        ind: 476,
        ty: 3,
        parent: 475,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 475,
        ty: 3,
        parent: 474,
        ks: {
          p: {
            a: 0,
            k: [
              -10.395,
              -5.224
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 474,
        ty: 3,
        parent: 471,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 471,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              -0.173
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 466,
        ty: 3,
        parent: 465,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 465,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 498,
        ty: 4,
        td: 1,
        parent: 481,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 497,
        ty: 0,
        tt: 1,
        parent: 481,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "495"
      },
      {
        ind: 481,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 499,
        ty: 4,
        parent: 440,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 440,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              6
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "504",
    layers: [
      {
        ind: 502,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              13,
              6
            ]
          },
          p: {
            a: 0,
            k: [
              58.6,
              51.6
            ]
          }
        },
        w: 191,
        h: 102,
        ip: 0,
        op: 361,
        st: 0,
        refId: "500"
      },
      {
        ind: 503,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                147.345,
                99.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                294.69,
                199.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "507",
    layers: [
      {
        ind: 138,
        ty: 0,
        parent: 71,
        ks: {
          p: {
            a: 0,
            k: [
              -58.6,
              -51.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 294.69,
        h: 199.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "136"
      },
      {
        ind: 71,
        ty: 3,
        parent: 70,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 198,
        ty: 0,
        parent: 139,
        ks: {
          p: {
            a: 0,
            k: [
              -48.6,
              -46.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 274.69,
        h: 189.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "196"
      },
      {
        ind: 139,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              194.056,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 258,
        ty: 0,
        parent: 199,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "256"
      },
      {
        ind: 199,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              388.112,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 318,
        ty: 0,
        parent: 259,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "316"
      },
      {
        ind: 259,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              582.167,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 378,
        ty: 0,
        parent: 319,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "376"
      },
      {
        ind: 319,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              776.223,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 438,
        ty: 0,
        parent: 379,
        ks: {
          p: {
            a: 0,
            k: [
              -48.6,
              -46.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 274.69,
        h: 189.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "436"
      },
      {
        ind: 379,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              970.279,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 506,
        ty: 0,
        parent: 439,
        ks: {
          p: {
            a: 0,
            k: [
              -58.6,
              -51.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 294.69,
        h: 199.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "504"
      },
      {
        ind: 439,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              1164.335,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 70,
        ty: 3,
        ks: {
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  -328.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.6,
                s: [
                  -328.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 120.6,
                s: [
                  -523.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 145.2,
                s: [
                  -523.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 193.2,
                s: [
                  -718.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.8,
                s: [
                  -718.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 265.8,
                s: [
                  -913.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  -913.709,
                  58.074
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  }
];
const fr$8 = 60;
const h$8 = 238;
const ip$8 = 0;
const layers$8 = [
  {
    ind: 5,
    ty: 4,
    td: 1,
    parent: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 4,
    ty: 4,
    tt: 1,
    parent: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 3,
    ty: 3,
    parent: 2,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 9,
    ty: 0,
    parent: 2,
    ks: {
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 48,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 281.4,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 329.4,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              100
            ],
            h: 1
          }
        ]
      }
    },
    w: 255,
    h: 238,
    ip: 0,
    op: 361,
    st: 0,
    refId: "7"
  },
  {
    ind: 20,
    ty: 0,
    parent: 11,
    ks: {
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 60.6,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 63.54,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          -0.386,
          0
        ]
      }
    },
    w: 26,
    h: 17,
    ip: 0,
    op: 361,
    st: 0,
    refId: "18"
  },
  {
    ind: 34,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          0,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 63.54,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 72.54,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 132.54,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 136.14,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          0.386,
          0
        ]
      }
    },
    w: 22,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "32"
  },
  {
    ind: 49,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          1,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 136.14,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 145.14,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 205.14,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 208.74,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      }
    },
    w: 29,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "47"
  },
  {
    ind: 63,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          1,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 208.74,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 217.74,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              100
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          0.773,
          0
        ]
      }
    },
    w: 27,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "61"
  },
  {
    ind: 11,
    ty: 3,
    parent: 10,
    ks: {
      p: {
        a: 0,
        k: [
          14.679,
          20.415
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 66,
    ty: 4,
    parent: 65,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "sh",
        ks: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35.1,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 64,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 65,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 66,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 67,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.5,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 68,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.5,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 69,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.4,
                      1.2
                    ],
                    [
                      34.9,
                      6.3
                    ],
                    [
                      9.4,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 70,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.4
                    ],
                    [
                      36.3,
                      1.2
                    ],
                    [
                      34.9,
                      6.3
                    ],
                    [
                      9.3,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 71,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.9,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.4
                    ],
                    [
                      36.2,
                      1.2
                    ],
                    [
                      34.8,
                      6.2
                    ],
                    [
                      9.3,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.9
                    ],
                    [
                      6.9,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.8
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 72,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.9,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.6,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.5
                    ],
                    [
                      36.1,
                      1.1
                    ],
                    [
                      34.7,
                      6.2
                    ],
                    [
                      9.3,
                      15.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.9
                    ],
                    [
                      6.9,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.8
                    ],
                    [
                      -5.6,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 73,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.6,
                      -8.3
                    ],
                    [
                      -6,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.9,
                      12.6
                    ],
                    [
                      35.9,
                      1.1
                    ],
                    [
                      34.5,
                      6.2
                    ],
                    [
                      9.2,
                      15.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.8
                    ],
                    [
                      6.8,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.7
                    ],
                    [
                      -5.6,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 74,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.5,
                      -8.3
                    ],
                    [
                      -5.9,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.8,
                      12.7
                    ],
                    [
                      35.6,
                      1
                    ],
                    [
                      34.3,
                      6.1
                    ],
                    [
                      9.2,
                      15.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.8
                    ],
                    [
                      6.8,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.6
                    ],
                    [
                      -5.5,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 75,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.7,
                      10.2
                    ],
                    [
                      -11.6,
                      -3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.4,
                      -8.3
                    ],
                    [
                      -5.9,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.7,
                      12.8
                    ],
                    [
                      35.3,
                      0.9
                    ],
                    [
                      34,
                      6
                    ],
                    [
                      9.1,
                      15.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.2,
                      -9.8
                    ],
                    [
                      6.7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.5
                    ],
                    [
                      -5.4,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 76,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      10.1
                    ],
                    [
                      -11.6,
                      -2.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.3,
                      -8.3
                    ],
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.6,
                      13
                    ],
                    [
                      34.8,
                      0.8
                    ],
                    [
                      33.6,
                      5.9
                    ],
                    [
                      9,
                      15.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.2,
                      -9.7
                    ],
                    [
                      6.5,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.3
                    ],
                    [
                      -5.3,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 77,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      10.1
                    ],
                    [
                      -11.5,
                      -2.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.2,
                      -8.3
                    ],
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.4,
                      13.3
                    ],
                    [
                      34.1,
                      0.6
                    ],
                    [
                      33.1,
                      5.8
                    ],
                    [
                      8.8,
                      16.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.1,
                      -9.6
                    ],
                    [
                      6.3,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.4,
                      -2
                    ],
                    [
                      -5.2,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 78,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.1,
                      10.1
                    ],
                    [
                      -11.5,
                      -2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5,
                      -8.2
                    ],
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.2,
                      13.6
                    ],
                    [
                      33.5,
                      0.5
                    ],
                    [
                      32.5,
                      5.7
                    ],
                    [
                      8.7,
                      16.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7,
                      -9.5
                    ],
                    [
                      6.1,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.4,
                      -1.8
                    ],
                    [
                      -5,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 79,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6,
                      10.1
                    ],
                    [
                      -11.4,
                      -1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.9,
                      -8.2
                    ],
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.1,
                      13.9
                    ],
                    [
                      32.9,
                      0.4
                    ],
                    [
                      32,
                      5.6
                    ],
                    [
                      8.5,
                      16.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7,
                      -9.4
                    ],
                    [
                      6,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.3,
                      -1.5
                    ],
                    [
                      -4.9,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 80,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.9,
                      10
                    ],
                    [
                      -11.4,
                      -1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.8,
                      -8.2
                    ],
                    [
                      -5.6,
                      -7.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4,
                      14.1
                    ],
                    [
                      32.4,
                      0.3
                    ],
                    [
                      31.6,
                      5.6
                    ],
                    [
                      8.5,
                      16.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.9,
                      -9.3
                    ],
                    [
                      5.9,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.3,
                      -1.4
                    ],
                    [
                      -4.8,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 81,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.7,
                      10
                    ],
                    [
                      -11.3,
                      -1.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.7,
                      -8.2
                    ],
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.9,
                      14.3
                    ],
                    [
                      32,
                      0.2
                    ],
                    [
                      31.3,
                      5.5
                    ],
                    [
                      8.4,
                      16.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.9,
                      -9.3
                    ],
                    [
                      5.7,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1.2
                    ],
                    [
                      -4.7,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 82,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.7,
                      10
                    ],
                    [
                      -11.3,
                      -1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.6,
                      -8.1
                    ],
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.8,
                      14.4
                    ],
                    [
                      31.7,
                      0.2
                    ],
                    [
                      31.1,
                      5.5
                    ],
                    [
                      8.3,
                      17.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.2
                    ],
                    [
                      5.7,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1.1
                    ],
                    [
                      -4.6,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 83,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.6,
                      10
                    ],
                    [
                      -11.2,
                      -1.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.6,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.7,
                      14.5
                    ],
                    [
                      31.4,
                      0.2
                    ],
                    [
                      30.9,
                      5.5
                    ],
                    [
                      8.3,
                      17.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.2
                    ],
                    [
                      5.6,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1
                    ],
                    [
                      -4.6,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 84,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.5,
                      10
                    ],
                    [
                      -11.2,
                      -1.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.5,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.7,
                      14.6
                    ],
                    [
                      31.2,
                      0.2
                    ],
                    [
                      30.7,
                      5.4
                    ],
                    [
                      8.2,
                      17.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.1
                    ],
                    [
                      5.5,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -1
                    ],
                    [
                      -4.5,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 85,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.5,
                      10
                    ],
                    [
                      -11.2,
                      -1.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.5,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.6,
                      14.7
                    ],
                    [
                      31,
                      0.1
                    ],
                    [
                      30.5,
                      5.4
                    ],
                    [
                      8.2,
                      17.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.1
                    ],
                    [
                      5.5,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.9
                    ],
                    [
                      -4.5,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 86,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.6,
                      14.8
                    ],
                    [
                      30.9,
                      0.1
                    ],
                    [
                      30.4,
                      5.4
                    ],
                    [
                      8.1,
                      17.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9.1
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.8
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 87,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      14.8
                    ],
                    [
                      30.7,
                      0.1
                    ],
                    [
                      30.3,
                      5.4
                    ],
                    [
                      8.1,
                      17.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9.1
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.8
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 88,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      14.9
                    ],
                    [
                      30.6,
                      0.1
                    ],
                    [
                      30.2,
                      5.4
                    ],
                    [
                      8.1,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.7
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 89,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      15
                    ],
                    [
                      30.5,
                      0.1
                    ],
                    [
                      30.1,
                      5.4
                    ],
                    [
                      8.1,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.7
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 90,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      15
                    ],
                    [
                      30.4,
                      0.1
                    ],
                    [
                      30,
                      5.4
                    ],
                    [
                      8,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.7
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 91,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15
                    ],
                    [
                      30.3,
                      0.1
                    ],
                    [
                      29.9,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 92,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30.2,
                      0.1
                    ],
                    [
                      29.8,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 93,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30.1,
                      0.1
                    ],
                    [
                      29.8,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 94,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30,
                      0.1
                    ],
                    [
                      29.7,
                      5.4
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 95,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.2
                    ],
                    [
                      30,
                      0.1
                    ],
                    [
                      29.7,
                      5.4
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 96,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.9,
                      0
                    ],
                    [
                      29.6,
                      5.3
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 97,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.9,
                      0
                    ],
                    [
                      29.6,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 98,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 99,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 100,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 101,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 102,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 103,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 104,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 105,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 106,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 107,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 108,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 109,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 110,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 111,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 112,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 137,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 138,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 139,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 140,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 141,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.2,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 142,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.4,
                      0
                    ],
                    [
                      29.2,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 143,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.5
                    ],
                    [
                      29.3,
                      0
                    ],
                    [
                      29.1,
                      5.3
                    ],
                    [
                      7.8,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 144,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.5
                    ],
                    [
                      29.2,
                      0
                    ],
                    [
                      29,
                      5.3
                    ],
                    [
                      7.8,
                      18
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.8
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 145,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.1,
                      15.6
                    ],
                    [
                      29,
                      0
                    ],
                    [
                      28.9,
                      5.3
                    ],
                    [
                      7.8,
                      18
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.8
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.2
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 146,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.9,
                      9.8
                    ],
                    [
                      -10.8,
                      -0.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.1,
                      15.7
                    ],
                    [
                      28.8,
                      0
                    ],
                    [
                      28.7,
                      5.3
                    ],
                    [
                      7.7,
                      18.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.8
                    ],
                    [
                      4.9,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.8,
                      -0.2
                    ],
                    [
                      -4,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 147,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.8,
                      9.8
                    ],
                    [
                      -10.8,
                      -0.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.9,
                      -8
                    ],
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3,
                      15.8
                    ],
                    [
                      28.5,
                      0
                    ],
                    [
                      28.5,
                      5.3
                    ],
                    [
                      7.7,
                      18.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.7
                    ],
                    [
                      4.8,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.8,
                      -0.1
                    ],
                    [
                      -3.9,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 148,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.7,
                      9.7
                    ],
                    [
                      -10.7,
                      0.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.9,
                      -7.9
                    ],
                    [
                      -5.2,
                      -7.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.9,
                      16
                    ],
                    [
                      28.1,
                      0
                    ],
                    [
                      28.1,
                      5.3
                    ],
                    [
                      7.6,
                      18.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.4,
                      -8.7
                    ],
                    [
                      4.7,
                      -9.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.7,
                      0.1
                    ],
                    [
                      -3.9,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 149,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.6,
                      9.7
                    ],
                    [
                      -10.6,
                      0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.7,
                      -7.9
                    ],
                    [
                      -5.2,
                      -7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.8,
                      16.3
                    ],
                    [
                      27.5,
                      0
                    ],
                    [
                      27.7,
                      5.3
                    ],
                    [
                      7.5,
                      18.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.4,
                      -8.6
                    ],
                    [
                      4.6,
                      -9.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.6,
                      0.2
                    ],
                    [
                      -3.7,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 150,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.4,
                      9.6
                    ],
                    [
                      -10.5,
                      0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.6,
                      -7.9
                    ],
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.7,
                      16.6
                    ],
                    [
                      26.8,
                      0
                    ],
                    [
                      27.1,
                      5.3
                    ],
                    [
                      7.4,
                      18.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.3,
                      -8.5
                    ],
                    [
                      4.4,
                      -9.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.5,
                      0.5
                    ],
                    [
                      -3.6,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 151,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.3,
                      9.6
                    ],
                    [
                      -10.3,
                      0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.5,
                      -7.8
                    ],
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.5,
                      16.9
                    ],
                    [
                      26.2,
                      0.1
                    ],
                    [
                      26.6,
                      5.4
                    ],
                    [
                      7.3,
                      19.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.2,
                      -8.4
                    ],
                    [
                      4.3,
                      -9.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.4,
                      0.7
                    ],
                    [
                      -3.5,
                      7.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 152,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.1,
                      9.5
                    ],
                    [
                      -10.2,
                      1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.4,
                      -7.8
                    ],
                    [
                      -5,
                      -6.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.4,
                      17.2
                    ],
                    [
                      25.6,
                      0.1
                    ],
                    [
                      26.1,
                      5.4
                    ],
                    [
                      7.2,
                      19.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.2,
                      -8.3
                    ],
                    [
                      4.1,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.3,
                      0.8
                    ],
                    [
                      -3.4,
                      7.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 153,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4,
                      9.5
                    ],
                    [
                      -10.1,
                      1.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.3,
                      -7.7
                    ],
                    [
                      -5,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.3,
                      17.4
                    ],
                    [
                      25.2,
                      0.2
                    ],
                    [
                      25.7,
                      5.5
                    ],
                    [
                      7.1,
                      19.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.1,
                      -8.3
                    ],
                    [
                      4,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.2,
                      0.9
                    ],
                    [
                      -3.3,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 154,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.9,
                      9.5
                    ],
                    [
                      -10,
                      1.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.2,
                      -7.7
                    ],
                    [
                      -5,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.2,
                      17.5
                    ],
                    [
                      24.8,
                      0.2
                    ],
                    [
                      25.5,
                      5.5
                    ],
                    [
                      7.1,
                      19.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.1,
                      -8.2
                    ],
                    [
                      3.9,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.2,
                      1
                    ],
                    [
                      -3.2,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 155,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.9,
                      9.4
                    ],
                    [
                      -10,
                      1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.2,
                      -7.7
                    ],
                    [
                      -4.9,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.2,
                      17.7
                    ],
                    [
                      24.5,
                      0.3
                    ],
                    [
                      25.2,
                      5.5
                    ],
                    [
                      7,
                      19.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.2
                    ],
                    [
                      3.9,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.1,
                      1.1
                    ],
                    [
                      -3.2,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 156,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.8,
                      9.4
                    ],
                    [
                      -9.9,
                      1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.1,
                      -7.7
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      17.8
                    ],
                    [
                      24.3,
                      0.3
                    ],
                    [
                      25,
                      5.5
                    ],
                    [
                      7,
                      19.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.8,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.1,
                      1.2
                    ],
                    [
                      -3.1,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 157,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.8,
                      9.4
                    ],
                    [
                      -9.9,
                      1.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.1,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      17.9
                    ],
                    [
                      24.1,
                      0.3
                    ],
                    [
                      24.8,
                      5.6
                    ],
                    [
                      6.9,
                      19.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.8,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.2
                    ],
                    [
                      -3.1,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 158,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.4
                    ],
                    [
                      -9.8,
                      1.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      18
                    ],
                    [
                      23.9,
                      0.4
                    ],
                    [
                      24.7,
                      5.6
                    ],
                    [
                      6.9,
                      20
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.7,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.3
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 159,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.3
                    ],
                    [
                      -9.8,
                      1.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.1
                    ],
                    [
                      23.7,
                      0.4
                    ],
                    [
                      24.6,
                      5.6
                    ],
                    [
                      6.9,
                      20
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8
                    ],
                    [
                      3.7,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.3
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 160,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.3
                    ],
                    [
                      -9.7,
                      1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.1
                    ],
                    [
                      23.6,
                      0.4
                    ],
                    [
                      24.5,
                      5.6
                    ],
                    [
                      6.9,
                      20.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.7,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 161,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.2
                    ],
                    [
                      23.5,
                      0.4
                    ],
                    [
                      24.4,
                      5.7
                    ],
                    [
                      6.8,
                      20.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 162,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.2
                    ],
                    [
                      23.3,
                      0.4
                    ],
                    [
                      24.3,
                      5.7
                    ],
                    [
                      6.8,
                      20.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 163,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.3
                    ],
                    [
                      23.2,
                      0.5
                    ],
                    [
                      24.2,
                      5.7
                    ],
                    [
                      6.8,
                      20.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 164,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.6,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.3
                    ],
                    [
                      23.2,
                      0.5
                    ],
                    [
                      24.1,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 165,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      23.1,
                      0.5
                    ],
                    [
                      24,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 166,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      23,
                      0.5
                    ],
                    [
                      24,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 167,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      22.9,
                      0.5
                    ],
                    [
                      23.9,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 168,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.9,
                      0.5
                    ],
                    [
                      23.9,
                      5.7
                    ],
                    [
                      6.8,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 169,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.8,
                      0.5
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 170,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.8,
                      0.6
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 171,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.5,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 172,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 173,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 174,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 175,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 176,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 177,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 178,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 179,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 180,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 181,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 182,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 183,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 184,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 185,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 209,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 210,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 211,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 212,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 213,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.4,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 214,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.4,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 215,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.8
                    ],
                    [
                      22.3,
                      0.7
                    ],
                    [
                      23.4,
                      5.8
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 216,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.8
                    ],
                    [
                      22.2,
                      0.7
                    ],
                    [
                      23.3,
                      5.9
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 217,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.2
                    ],
                    [
                      -9.4,
                      2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      18.9
                    ],
                    [
                      22.1,
                      0.7
                    ],
                    [
                      23.2,
                      5.9
                    ],
                    [
                      6.6,
                      20.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.8
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 218,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.1
                    ],
                    [
                      -9.3,
                      2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      19
                    ],
                    [
                      21.9,
                      0.8
                    ],
                    [
                      23.1,
                      5.9
                    ],
                    [
                      6.6,
                      20.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.8
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 219,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.2,
                      9.1
                    ],
                    [
                      -9.3,
                      2.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.6,
                      -7.4
                    ],
                    [
                      -4.7,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      19.1
                    ],
                    [
                      21.6,
                      0.8
                    ],
                    [
                      22.9,
                      6
                    ],
                    [
                      6.6,
                      20.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      3.2,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.9
                    ],
                    [
                      -2.6,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 220,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.1,
                      9.1
                    ],
                    [
                      -9.2,
                      2.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.6,
                      -7.4
                    ],
                    [
                      -4.6,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.6,
                      19.2
                    ],
                    [
                      21.3,
                      0.9
                    ],
                    [
                      22.6,
                      6
                    ],
                    [
                      6.5,
                      21
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      3.1,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.5,
                      1.9
                    ],
                    [
                      -2.6,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 221,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3,
                      9
                    ],
                    [
                      -9.1,
                      2.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.5,
                      -7.4
                    ],
                    [
                      -4.6,
                      -6.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.5,
                      19.5
                    ],
                    [
                      20.9,
                      1
                    ],
                    [
                      22.2,
                      6.1
                    ],
                    [
                      6.5,
                      21.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.6,
                      -7.6
                    ],
                    [
                      3,
                      -9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.4,
                      2
                    ],
                    [
                      -2.5,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 222,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.9,
                      9
                    ],
                    [
                      -8.9,
                      2.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.4,
                      -7.3
                    ],
                    [
                      -4.6,
                      -6.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.4,
                      19.8
                    ],
                    [
                      20.3,
                      1.2
                    ],
                    [
                      21.7,
                      6.3
                    ],
                    [
                      6.4,
                      21.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      2.9,
                      -9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.3,
                      2.2
                    ],
                    [
                      -2.4,
                      7.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 223,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.8,
                      8.9
                    ],
                    [
                      -8.7,
                      2.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.2,
                      -7.2
                    ],
                    [
                      -4.5,
                      -6.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.3,
                      20.1
                    ],
                    [
                      19.6,
                      1.4
                    ],
                    [
                      21.2,
                      6.5
                    ],
                    [
                      6.3,
                      21.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      2.8,
                      -8.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.1,
                      2.3
                    ],
                    [
                      -2.2,
                      7.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 224,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.6,
                      8.8
                    ],
                    [
                      -8.5,
                      3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.1,
                      -7.2
                    ],
                    [
                      -4.4,
                      -6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.2,
                      20.4
                    ],
                    [
                      19,
                      1.6
                    ],
                    [
                      20.7,
                      6.6
                    ],
                    [
                      6.2,
                      22
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      2.6,
                      -8.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      2.5
                    ],
                    [
                      -2.1,
                      7.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 225,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.5,
                      8.7
                    ],
                    [
                      -8.4,
                      3.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.1,
                      -7.1
                    ],
                    [
                      -4.4,
                      -5.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.1,
                      20.7
                    ],
                    [
                      18.5,
                      1.8
                    ],
                    [
                      20.3,
                      6.8
                    ],
                    [
                      6.2,
                      22.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      2.5,
                      -8.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      2.6
                    ],
                    [
                      -2.1,
                      7.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 226,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.4,
                      8.7
                    ],
                    [
                      -8.3,
                      3.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2,
                      -7.1
                    ],
                    [
                      -4.4,
                      -5.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.1,
                      20.9
                    ],
                    [
                      18.1,
                      1.9
                    ],
                    [
                      20,
                      6.9
                    ],
                    [
                      6.1,
                      22.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      2.4,
                      -8.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      2.6
                    ],
                    [
                      -2,
                      7.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 227,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.4,
                      8.6
                    ],
                    [
                      -8.2,
                      3.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21
                    ],
                    [
                      17.8,
                      2
                    ],
                    [
                      19.8,
                      7
                    ],
                    [
                      6.1,
                      22.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      2.4,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.7,
                      2.7
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 228,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.3,
                      8.6
                    ],
                    [
                      -8.1,
                      3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.1
                    ],
                    [
                      17.6,
                      2.1
                    ],
                    [
                      19.5,
                      7.1
                    ],
                    [
                      6,
                      22.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      2.3,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.6,
                      2.8
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 229,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.3,
                      8.6
                    ],
                    [
                      -8.1,
                      3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.3
                    ],
                    [
                      17.4,
                      2.2
                    ],
                    [
                      19.4,
                      7.1
                    ],
                    [
                      6,
                      22.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      2.3,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.6,
                      2.8
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 230,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -8,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.4
                    ],
                    [
                      17.2,
                      2.3
                    ],
                    [
                      19.2,
                      7.2
                    ],
                    [
                      6,
                      22.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7.1
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      2.8
                    ],
                    [
                      -1.8,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 231,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -7.9,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.3,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.4
                    ],
                    [
                      17,
                      2.4
                    ],
                    [
                      19.1,
                      7.3
                    ],
                    [
                      6,
                      22.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 232,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -7.9,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.5
                    ],
                    [
                      16.9,
                      2.5
                    ],
                    [
                      19,
                      7.3
                    ],
                    [
                      6,
                      22.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 233,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.5
                    ],
                    [
                      -7.9,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.6
                    ],
                    [
                      16.7,
                      2.5
                    ],
                    [
                      18.9,
                      7.4
                    ],
                    [
                      6,
                      22.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 234,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.5
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.6
                    ],
                    [
                      16.6,
                      2.6
                    ],
                    [
                      18.8,
                      7.4
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 235,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.7
                    ],
                    [
                      16.5,
                      2.6
                    ],
                    [
                      18.7,
                      7.4
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 236,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.7
                    ],
                    [
                      16.4,
                      2.6
                    ],
                    [
                      18.6,
                      7.5
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 237,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.8
                    ],
                    [
                      16.4,
                      2.7
                    ],
                    [
                      18.6,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 238,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.8
                    ],
                    [
                      16.3,
                      2.7
                    ],
                    [
                      18.5,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 239,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.2,
                      2.7
                    ],
                    [
                      18.5,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 240,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.2,
                      2.8
                    ],
                    [
                      18.4,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 241,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.1,
                      2.8
                    ],
                    [
                      18.4,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 242,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.1,
                      2.8
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 243,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.8
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 244,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.9
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 245,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 246,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 247,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 248,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 249,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 250,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 251,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 252,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 253,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 254,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 255,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 256,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 257,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.902,
            8e-3,
            0.471
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 65,
    ty: 3,
    parent: 64,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 68,
    ty: 4,
    parent: 67,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "sh",
        ks: {
          a: 0,
          k: {
            c: true,
            i: [
              [
                0,
                0
              ],
              [
                -10.6,
                9.9
              ],
              [
                -10.9,
                -9.6
              ],
              [
                8.3,
                -12
              ],
              [
                0,
                0
              ],
              [
                8.9,
                7.9
              ],
              [
                8.7,
                -8.1
              ],
              [
                -7,
                -9.6
              ],
              [
                0,
                0
              ]
            ],
            v: [
              [
                5.5,
                45.8
              ],
              [
                9.1,
                7.6
              ],
              [
                46.9,
                7.1
              ],
              [
                51.5,
                45.2
              ],
              [
                47.2,
                42.2
              ],
              [
                43.5,
                11.1
              ],
              [
                12.7,
                11.5
              ],
              [
                9.7,
                42.7
              ],
              [
                5.5,
                45.8
              ]
            ],
            o: [
              [
                -8.6,
                -11.8
              ],
              [
                10.6,
                -9.9
              ],
              [
                10.9,
                9.6
              ],
              [
                0,
                0
              ],
              [
                6.7,
                -9.8
              ],
              [
                -8.9,
                -7.9
              ],
              [
                -8.7,
                8.1
              ],
              [
                0,
                0
              ],
              [
                0,
                0
              ]
            ]
          }
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 67,
    ty: 3,
    parent: 64,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 64,
    ty: 3,
    parent: 10,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 10,
    ty: 3,
    parent: 2,
    ks: {
      p: {
        a: 0,
        k: [
          100,
          165
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 509,
    ty: 0,
    parent: 69,
    ks: {
      a: {
        a: 0,
        k: [
          -21,
          -6
        ]
      },
      p: {
        a: 0,
        k: [
          -21,
          -6
        ]
      }
    },
    w: 297,
    h: 256,
    ip: 0,
    op: 361,
    st: 0,
    refId: "507"
  },
  {
    ind: 69,
    ty: 3,
    parent: 2,
    ks: {
      p: {
        a: 0,
        k: [
          -21,
          -6
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 511,
    ty: 4,
    parent: 510,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            1,
            1,
            1
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 510,
    ty: 3,
    parent: 2,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  }
];
const meta$8 = {
  g: "https://jitter.video"
};
const op$8 = 360;
const v$8 = "5.7.4";
const w$8 = 255;
const CandidateInvitationsEn = {
  assets: assets$8,
  fr: fr$8,
  h: h$8,
  ip: ip$8,
  layers: layers$8,
  meta: meta$8,
  op: op$8,
  v: v$8,
  w: w$8
};
const assets$7 = [
  {
    h: 3,
    id: "0",
    p: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGMAAAADCAYAAACHz7HPAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAYSURBVHgB7cAxAQAAAMKg9U9tDQ8ogGsDBKcAAbNbxGsAAAAASUVORK5CYII=",
    u: "",
    w: 99,
    e: 1
  },
  {
    id: "7",
    layers: [
      {
        ind: 6,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 20
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "18",
    layers: [
      {
        ind: 15,
        ty: 4,
        parent: 14,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    2.4
                  ],
                  [
                    3.1,
                    0
                  ],
                  [
                    0.6,
                    -2.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    2,
                    -1.7
                  ]
                ],
                v: [
                  [
                    6.5,
                    9.7
                  ],
                  [
                    1,
                    14.4
                  ],
                  [
                    1,
                    16.6
                  ],
                  [
                    11.9,
                    16.6
                  ],
                  [
                    11.9,
                    14.4
                  ],
                  [
                    4.4,
                    14.4
                  ],
                  [
                    8.1,
                    11.3
                  ],
                  [
                    11.9,
                    5.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    1.2,
                    4.5
                  ],
                  [
                    3.4,
                    5.4
                  ],
                  [
                    6.5,
                    3
                  ],
                  [
                    9.3,
                    5.6
                  ],
                  [
                    6.5,
                    9.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.5,
                    -2.1
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -2.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 14,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              12.901,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 17,
        ty: 4,
        parent: 16,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 16,
        ty: 3,
        parent: 13,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 13,
        ty: 3,
        parent: 12,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 12,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              0.52,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "25",
    layers: [
      {
        ind: 24,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    3.8
                  ],
                  [
                    1.3,
                    5.7
                  ],
                  [
                    4.8,
                    3.6
                  ],
                  [
                    4.8,
                    16.6
                  ],
                  [
                    7.2,
                    16.6
                  ],
                  [
                    7.2,
                    1
                  ],
                  [
                    4.9,
                    1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "29",
    layers: [
      {
        ind: 28,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "32",
    layers: [
      {
        ind: 27,
        ty: 0,
        parent: 23,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 64.545907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 95.04,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 64.548,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 95.04,
                s: [
                  12.901,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  12.901,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 8,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "25"
      },
      {
        ind: 31,
        ty: 0,
        parent: 23,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 63.54,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 73.564806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 63.54,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 73.566,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "29"
      },
      {
        ind: 23,
        ty: 3,
        parent: 22,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 22,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              1.691,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 21,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "40",
    layers: [
      {
        ind: 39,
        ty: 4,
        parent: 38,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    4.5
                  ],
                  [
                    3.9,
                    0
                  ],
                  [
                    0,
                    -4.5
                  ],
                  [
                    -3.9,
                    0
                  ]
                ],
                v: [
                  [
                    7.6,
                    16.7
                  ],
                  [
                    14.2,
                    8.7
                  ],
                  [
                    7.6,
                    0.7
                  ],
                  [
                    1.1,
                    8.8
                  ],
                  [
                    7.6,
                    16.7
                  ]
                ],
                o: [
                  [
                    3.9,
                    0
                  ],
                  [
                    0,
                    -4.5
                  ],
                  [
                    -3.9,
                    0
                  ],
                  [
                    0,
                    4.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -2.5,
                    0
                  ],
                  [
                    0,
                    -3.1
                  ],
                  [
                    2.5,
                    0
                  ],
                  [
                    0,
                    3.2
                  ]
                ],
                v: [
                  [
                    3.6,
                    8.7
                  ],
                  [
                    7.6,
                    3
                  ],
                  [
                    11.6,
                    8.8
                  ],
                  [
                    7.6,
                    14.5
                  ],
                  [
                    3.6,
                    8.7
                  ]
                ],
                o: [
                  [
                    0,
                    -3.2
                  ],
                  [
                    2.5,
                    0
                  ],
                  [
                    0,
                    3.2
                  ],
                  [
                    -2.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 38,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -1,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "44",
    layers: [
      {
        ind: 43,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "47",
    layers: [
      {
        ind: 42,
        ty: 0,
        parent: 37,
        ks: {
          a: {
            a: 0,
            k: [
              -1,
              0
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 137.145907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 167.64,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 137.148,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 167.64,
                s: [
                  12.901,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  12.901,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 14,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "40"
      },
      {
        ind: 46,
        ty: 0,
        parent: 37,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 136.14,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 146.164806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 136.14,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 146.166,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "44"
      },
      {
        ind: 37,
        ty: 3,
        parent: 36,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 36,
        ty: 3,
        parent: 35,
        ks: {
          p: {
            a: 0,
            k: [
              -0.933,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 35,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "54",
    layers: [
      {
        ind: 53,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.9,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    4.4
                  ],
                  [
                    1.2,
                    1.2
                  ],
                  [
                    1.9,
                    0
                  ],
                  [
                    0,
                    -3.2
                  ],
                  [
                    -3,
                    0
                  ],
                  [
                    -0.9,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    3,
                    0
                  ]
                ],
                v: [
                  [
                    5.7,
                    14.6
                  ],
                  [
                    2.5,
                    13.3
                  ],
                  [
                    1.2,
                    15
                  ],
                  [
                    5.7,
                    16.8
                  ],
                  [
                    12.6,
                    8.2
                  ],
                  [
                    10.8,
                    2.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    0.9,
                    6.1
                  ],
                  [
                    6.1,
                    11.2
                  ],
                  [
                    10.2,
                    9.2
                  ],
                  [
                    10.2,
                    9.2
                  ],
                  [
                    5.7,
                    14.6
                  ]
                ],
                o: [
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    1.1
                  ],
                  [
                    4.9,
                    0
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -1,
                    -1
                  ],
                  [
                    -3.1,
                    0
                  ],
                  [
                    0,
                    3
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    2.6
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    -2
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0,
                    1.9
                  ]
                ],
                v: [
                  [
                    3.4,
                    6
                  ],
                  [
                    6.6,
                    3
                  ],
                  [
                    9.9,
                    6
                  ],
                  [
                    6.6,
                    9.1
                  ],
                  [
                    3.4,
                    6
                  ]
                ],
                o: [
                  [
                    0,
                    -1.9
                  ],
                  [
                    1.9,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    -1.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "58",
    layers: [
      {
        ind: 57,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    2.4
                  ],
                  [
                    3.1,
                    0
                  ],
                  [
                    0.6,
                    -2.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    2,
                    -1.7
                  ]
                ],
                v: [
                  [
                    6.5,
                    9.7
                  ],
                  [
                    1,
                    14.4
                  ],
                  [
                    1,
                    16.6
                  ],
                  [
                    11.9,
                    16.6
                  ],
                  [
                    11.9,
                    14.4
                  ],
                  [
                    4.4,
                    14.4
                  ],
                  [
                    8.1,
                    11.3
                  ],
                  [
                    11.9,
                    5.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    1.2,
                    4.5
                  ],
                  [
                    3.4,
                    5.4
                  ],
                  [
                    6.5,
                    3
                  ],
                  [
                    9.3,
                    5.6
                  ],
                  [
                    6.5,
                    9.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.5,
                    -2.1
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -2.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "61",
    layers: [
      {
        ind: 56,
        ty: 0,
        parent: 52,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 209.745907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 240.24,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  13.101,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 209.748,
                s: [
                  13.101,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 240.24,
                s: [
                  13.101,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  13.101,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 13,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "54"
      },
      {
        ind: 60,
        ty: 0,
        parent: 52,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 208.74,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 218.764806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 208.74,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 218.766,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "58"
      },
      {
        ind: 52,
        ty: 3,
        parent: 51,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 51,
        ty: 3,
        parent: 50,
        ks: {
          p: {
            a: 0,
            k: [
              -1.028,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 50,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "94",
    layers: [
      {
        ind: 93,
        ty: 2,
        parent: 92,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 92,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "110",
    layers: [
      {
        ind: 109,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                17.5,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                35,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.41,
                            -1.91
                          ],
                          [
                            -0.92,
                            0.09
                          ],
                          [
                            1.69,
                            -0.98
                          ],
                          [
                            -0.37,
                            -0.85
                          ],
                          [
                            1.45,
                            1.31
                          ],
                          [
                            0.69,
                            -0.62
                          ],
                          [
                            -0.79,
                            1.79
                          ],
                          [
                            0.8,
                            0.46
                          ],
                          [
                            -1.95,
                            -0.21
                          ],
                          [
                            -0.2,
                            0.91
                          ]
                        ],
                        o: [
                          [
                            0.41,
                            -1.91
                          ],
                          [
                            0.19,
                            0.91
                          ],
                          [
                            1.94,
                            -0.21
                          ],
                          [
                            -0.8,
                            0.46
                          ],
                          [
                            0.8,
                            1.79
                          ],
                          [
                            -0.69,
                            -0.62
                          ],
                          [
                            -1.45,
                            1.31
                          ],
                          [
                            0.38,
                            -0.85
                          ],
                          [
                            -1.7,
                            -0.98
                          ],
                          [
                            0.92,
                            0.09
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            11.5,
                            8.55
                          ],
                          [
                            15.05,
                            8.55
                          ],
                          [
                            17.01,
                            9.98
                          ],
                          [
                            18.1,
                            13.34
                          ],
                          [
                            17.35,
                            15.65
                          ],
                          [
                            14.49,
                            17.73
                          ],
                          [
                            12.06,
                            17.73
                          ],
                          [
                            9.2,
                            15.65
                          ],
                          [
                            8.45,
                            13.34
                          ],
                          [
                            9.54,
                            9.98
                          ],
                          [
                            11.5,
                            8.55
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.9,
                        0.01,
                        0.47,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "118",
    layers: [
      {
        ind: 117,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "121",
    layers: [
      {
        ind: 120,
        ty: 0,
        parent: 116,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "118"
      },
      {
        ind: 116,
        ty: 3,
        parent: 115,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 115,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              7.796,
              2.612
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "127",
    layers: [
      {
        ind: 123,
        ty: 0,
        parent: 114,
        ks: {
          a: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          },
          p: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          }
        },
        w: 39.4996,
        h: 39.7037,
        ip: 0,
        op: 361,
        st: 0,
        refId: "121"
      },
      {
        ind: 114,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 125,
        ty: 4,
        parent: 124,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.299,
                1.306
              ]
            },
            s: {
              a: 0,
              k: [
                2.599,
                2.612
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 124,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.024,
              45.102
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 126,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.333,
                26.469
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.666,
                52.938
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "132",
    layers: [
      {
        ind: 78,
        ty: 4,
        parent: 77,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.03
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    -0.91
                  ],
                  [
                    -0.95,
                    0
                  ],
                  [
                    -0.28,
                    0.51
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.39,
                    0
                  ],
                  [
                    0.01,
                    0.49
                  ]
                ],
                v: [
                  [
                    1,
                    4.56
                  ],
                  [
                    3.43,
                    4.56
                  ],
                  [
                    3.44,
                    4.34
                  ],
                  [
                    1.88,
                    2.68
                  ],
                  [
                    0.26,
                    4.34
                  ],
                  [
                    1.94,
                    6.01
                  ],
                  [
                    3.31,
                    5.24
                  ],
                  [
                    2.8,
                    4.89
                  ],
                  [
                    1.93,
                    5.39
                  ],
                  [
                    1,
                    4.56
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.01,
                    -0.08
                  ],
                  [
                    0,
                    -0.97
                  ],
                  [
                    -0.93,
                    0
                  ],
                  [
                    0,
                    0.95
                  ],
                  [
                    0.56,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.19,
                    0.35
                  ],
                  [
                    -0.47,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.44,
                    0
                  ],
                  [
                    -0.02,
                    -0.46
                  ]
                ],
                v: [
                  [
                    1.03,
                    4.02
                  ],
                  [
                    1.88,
                    3.29
                  ],
                  [
                    2.71,
                    4.02
                  ]
                ],
                o: [
                  [
                    0.07,
                    -0.44
                  ],
                  [
                    0.44,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 77,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              13.464,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 80,
        ty: 4,
        parent: 79,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.69,
                    0
                  ],
                  [
                    -0.13,
                    0.05
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.07,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.44,
                    4.8
                  ],
                  [
                    1.44,
                    3.39
                  ],
                  [
                    2.23,
                    3.39
                  ],
                  [
                    2.23,
                    2.75
                  ],
                  [
                    1.44,
                    2.75
                  ],
                  [
                    1.44,
                    1.76
                  ],
                  [
                    0.73,
                    1.76
                  ],
                  [
                    0.73,
                    2.75
                  ],
                  [
                    0.14,
                    2.75
                  ],
                  [
                    0.14,
                    3.39
                  ],
                  [
                    0.73,
                    3.39
                  ],
                  [
                    0.73,
                    4.92
                  ],
                  [
                    1.84,
                    5.99
                  ],
                  [
                    2.22,
                    5.92
                  ],
                  [
                    2.22,
                    5.31
                  ],
                  [
                    1.99,
                    5.34
                  ],
                  [
                    1.44,
                    4.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.71
                  ],
                  [
                    0.09,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.09,
                    0.02
                  ],
                  [
                    -0.35,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 79,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              11.022,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 82,
        ty: 4,
        parent: 81,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ]
                ],
                v: [
                  [
                    0.37,
                    1.71
                  ],
                  [
                    0.83,
                    2.17
                  ],
                  [
                    1.3,
                    1.71
                  ],
                  [
                    0.83,
                    1.23
                  ],
                  [
                    0.37,
                    1.71
                  ]
                ],
                o: [
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.48,
                    5.95
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    1.19,
                    2.75
                  ],
                  [
                    1.19,
                    5.95
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 81,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              9.354,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 84,
        ty: 4,
        parent: 83,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    2.75
                  ],
                  [
                    1.46,
                    5.95
                  ],
                  [
                    2.17,
                    5.95
                  ],
                  [
                    3.51,
                    2.75
                  ],
                  [
                    2.76,
                    2.75
                  ],
                  [
                    1.82,
                    5.12
                  ],
                  [
                    0.88,
                    2.75
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 83,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              5.73,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 86,
        ty: 4,
        parent: 85,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.82,
                    0
                  ],
                  [
                    0.31,
                    -0.41
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    -0.46
                  ]
                ],
                v: [
                  [
                    2.64,
                    4.15
                  ],
                  [
                    2.64,
                    5.95
                  ],
                  [
                    3.35,
                    5.95
                  ],
                  [
                    3.35,
                    3.97
                  ],
                  [
                    2.15,
                    2.68
                  ],
                  [
                    1.18,
                    3.18
                  ],
                  [
                    1.18,
                    2.75
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    0.48,
                    5.95
                  ],
                  [
                    1.19,
                    5.95
                  ],
                  [
                    1.19,
                    4.17
                  ],
                  [
                    1.93,
                    3.36
                  ],
                  [
                    2.64,
                    4.15
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    -0.25,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.49
                  ],
                  [
                    0.49,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 85,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              1.932,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 88,
        ty: 4,
        parent: 87,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    5.95
                  ],
                  [
                    1.34,
                    5.95
                  ],
                  [
                    1.34,
                    1.25
                  ],
                  [
                    0.6,
                    1.25
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 87,
        ty: 3,
        parent: 76,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 76,
        ty: 3,
        parent: 75,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 75,
        ty: 3,
        parent: 74,
        ks: {
          p: {
            a: 0,
            k: [
              2.072,
              1.141
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 74,
        ty: 3,
        parent: 73,
        ks: {
          p: {
            a: 0,
            k: [
              34.315,
              4.741
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 90,
        ty: 4,
        parent: 89,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 89,
        ty: 3,
        parent: 73,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 73,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 96,
        ty: 0,
        parent: 91,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "94"
      },
      {
        ind: 91,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 100,
        ty: 4,
        parent: 99,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 99,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 102,
        ty: 4,
        parent: 101,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 101,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 105,
        ty: 4,
        parent: 104,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 104,
        ty: 3,
        parent: 103,
        ks: {
          p: {
            a: 0,
            k: [
              14.813,
              4.266
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 112,
        ty: 0,
        parent: 108,
        ks: {},
        w: 35,
        h: 35,
        ip: 0,
        op: 361,
        st: 0,
        refId: "110"
      },
      {
        ind: 108,
        ty: 3,
        parent: 107,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 107,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              -10.395,
              -5.224
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 106,
        ty: 3,
        parent: 103,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 103,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              -0.173
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 98,
        ty: 3,
        parent: 97,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 97,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 130,
        ty: 4,
        td: 1,
        parent: 113,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.333,
                26.469
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.666,
                52.938
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 129,
        ty: 0,
        tt: 1,
        parent: 113,
        ks: {},
        w: 53,
        h: 53,
        ip: 0,
        op: 361,
        st: 0,
        refId: "127"
      },
      {
        ind: 113,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              10.649,
              22.519
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 131,
        ty: 4,
        parent: 72,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 72,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              6
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "136",
    layers: [
      {
        ind: 134,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              13,
              6
            ]
          },
          p: {
            a: 0,
            k: [
              58.6,
              51.6
            ]
          }
        },
        w: 191,
        h: 102,
        ip: 0,
        op: 361,
        st: 0,
        refId: "132"
      },
      {
        ind: 135,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                147.345,
                99.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                294.69,
                199.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "162",
    layers: [
      {
        ind: 161,
        ty: 2,
        parent: 160,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 160,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "178",
    layers: [
      {
        ind: 177,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "181",
    layers: [
      {
        ind: 180,
        ty: 0,
        parent: 176,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "178"
      },
      {
        ind: 176,
        ty: 3,
        parent: 175,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 175,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "187",
    layers: [
      {
        ind: 183,
        ty: 0,
        parent: 174,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "181"
      },
      {
        ind: 174,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 185,
        ty: 4,
        parent: 184,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 184,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 186,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "192",
    layers: [
      {
        ind: 146,
        ty: 4,
        parent: 145,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.05
                  ],
                  [
                    1.49,
                    0
                  ],
                  [
                    0,
                    -1.36
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    -0.41,
                    0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.59,
                    0
                  ],
                  [
                    0.02,
                    0.74
                  ]
                ],
                v: [
                  [
                    1.49,
                    6.84
                  ],
                  [
                    5.14,
                    6.84
                  ],
                  [
                    5.16,
                    6.5
                  ],
                  [
                    2.82,
                    4.02
                  ],
                  [
                    0.4,
                    6.51
                  ],
                  [
                    2.92,
                    9.01
                  ],
                  [
                    4.97,
                    7.85
                  ],
                  [
                    4.19,
                    7.34
                  ],
                  [
                    2.9,
                    8.09
                  ],
                  [
                    1.49,
                    6.84
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.02,
                    -0.12
                  ],
                  [
                    0,
                    -1.45
                  ],
                  [
                    -1.39,
                    0
                  ],
                  [
                    0,
                    1.43
                  ],
                  [
                    0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.28,
                    0.53
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    -0.04,
                    -0.68
                  ]
                ],
                v: [
                  [
                    1.55,
                    6.03
                  ],
                  [
                    2.82,
                    4.94
                  ],
                  [
                    4.07,
                    6.03
                  ]
                ],
                o: [
                  [
                    0.1,
                    -0.66
                  ],
                  [
                    0.67,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 145,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              20.196,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 148,
        ty: 4,
        parent: 147,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    -0.2,
                    0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.6
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.2
                  ],
                  [
                    2.2,
                    5.1
                  ],
                  [
                    3.3,
                    5.1
                  ],
                  [
                    3.3,
                    4.1
                  ],
                  [
                    2.2,
                    4.1
                  ],
                  [
                    2.2,
                    2.6
                  ],
                  [
                    1.1,
                    2.6
                  ],
                  [
                    1.1,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ],
                  [
                    0.2,
                    5.1
                  ],
                  [
                    1.1,
                    5.1
                  ],
                  [
                    1.1,
                    7.4
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    3.3,
                    8
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    2.2,
                    7.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 147,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              16.533,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 150,
        ty: 4,
        parent: 149,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ]
                ],
                v: [
                  [
                    0.5,
                    2.6
                  ],
                  [
                    1.3,
                    3.3
                  ],
                  [
                    2,
                    2.6
                  ],
                  [
                    1.3,
                    1.8
                  ],
                  [
                    0.5,
                    2.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.7,
                    8.9
                  ],
                  [
                    0.7,
                    4.1
                  ],
                  [
                    1.8,
                    4.1
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 149,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              14.031,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 152,
        ty: 4,
        parent: 151,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.2,
                    4.1
                  ],
                  [
                    2.2,
                    8.9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    5.3,
                    4.1
                  ],
                  [
                    4.1,
                    4.1
                  ],
                  [
                    2.7,
                    7.7
                  ],
                  [
                    1.3,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 151,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              8.595,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 154,
        ty: 4,
        parent: 153,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 153,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              2.898,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 156,
        ty: 4,
        parent: 155,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.9,
                    8.9
                  ],
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    1.9
                  ],
                  [
                    0.9,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 155,
        ty: 3,
        parent: 144,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 144,
        ty: 3,
        parent: 143,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 143,
        ty: 3,
        parent: 142,
        ks: {
          p: {
            a: 0,
            k: [
              2.517,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 142,
        ty: 3,
        parent: 141,
        ks: {
          p: {
            a: 0,
            k: [
              29.582,
              2.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 158,
        ty: 4,
        parent: 157,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 157,
        ty: 3,
        parent: 141,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 141,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 164,
        ty: 0,
        parent: 159,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "162"
      },
      {
        ind: 159,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 168,
        ty: 4,
        parent: 167,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 167,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 170,
        ty: 4,
        parent: 169,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 169,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 172,
        ty: 4,
        parent: 171,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.883,
                5.926
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.767,
                11.852
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 171,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 166,
        ty: 3,
        parent: 165,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 165,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 190,
        ty: 4,
        td: 1,
        parent: 173,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 189,
        ty: 0,
        tt: 1,
        parent: 173,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "187"
      },
      {
        ind: 173,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 191,
        ty: 4,
        parent: 140,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 140,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              1
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "196",
    layers: [
      {
        ind: 194,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              3,
              1
            ]
          },
          p: {
            a: 0,
            k: [
              48.6,
              46.6
            ]
          }
        },
        w: 181,
        h: 97,
        ip: 0,
        op: 361,
        st: 0,
        refId: "192"
      },
      {
        ind: 195,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                137.345,
                94.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                274.69,
                189.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "222",
    layers: [
      {
        ind: 221,
        ty: 2,
        parent: 220,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 220,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "238",
    layers: [
      {
        ind: 237,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "241",
    layers: [
      {
        ind: 240,
        ty: 0,
        parent: 236,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "238"
      },
      {
        ind: 236,
        ty: 3,
        parent: 235,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 235,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "247",
    layers: [
      {
        ind: 243,
        ty: 0,
        parent: 234,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "241"
      },
      {
        ind: 234,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 245,
        ty: 4,
        parent: 244,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 244,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 246,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "252",
    layers: [
      {
        ind: 206,
        ty: 4,
        parent: 205,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -1.4,
                    0
                  ]
                ],
                v: [
                  [
                    2.7,
                    9
                  ],
                  [
                    4.3,
                    8.1
                  ],
                  [
                    4.3,
                    8.9
                  ],
                  [
                    2.8,
                    10.4
                  ],
                  [
                    1.3,
                    9.4
                  ],
                  [
                    0.4,
                    9.9
                  ],
                  [
                    2.9,
                    11.3
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    4.1
                  ],
                  [
                    4.3,
                    4.1
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.7,
                    9
                  ]
                ],
                o: [
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.9
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.3,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 205,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              29.817,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 208,
        ty: 4,
        parent: 207,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 207,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              24.12,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 210,
        ty: 4,
        parent: 209,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.32,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.94,
                    0
                  ],
                  [
                    -0.35,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.12,
                    0.26
                  ],
                  [
                    0.81,
                    0
                  ],
                  [
                    0.31,
                    -0.66
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.37
                  ]
                ],
                v: [
                  [
                    3.47,
                    5.67
                  ],
                  [
                    3.02,
                    6.07
                  ],
                  [
                    2.42,
                    6.07
                  ],
                  [
                    0.42,
                    7.54
                  ],
                  [
                    0.42,
                    7.59
                  ],
                  [
                    2.04,
                    9
                  ],
                  [
                    3.46,
                    8.27
                  ],
                  [
                    3.46,
                    8.92
                  ],
                  [
                    4.51,
                    8.92
                  ],
                  [
                    4.51,
                    5.86
                  ],
                  [
                    4.35,
                    5
                  ],
                  [
                    2.55,
                    4.02
                  ],
                  [
                    0.5,
                    5.24
                  ],
                  [
                    1.32,
                    5.71
                  ],
                  [
                    2.52,
                    4.95
                  ],
                  [
                    3.47,
                    5.67
                  ]
                ],
                o: [
                  [
                    0,
                    0.23
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.83
                  ],
                  [
                    0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.32
                  ],
                  [
                    -0.32,
                    -0.69
                  ],
                  [
                    -1.12,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.21,
                    -0.43
                  ],
                  [
                    0.63,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.73,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.49,
                    7.49
                  ],
                  [
                    2.42,
                    6.83
                  ],
                  [
                    3.46,
                    6.83
                  ],
                  [
                    3.46,
                    6.98
                  ],
                  [
                    2.26,
                    8.15
                  ],
                  [
                    1.49,
                    7.49
                  ]
                ],
                o: [
                  [
                    0,
                    -0.45
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.72
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 209,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              18.945,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 212,
        ty: 4,
        parent: 211,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    4.4,
                    8.2
                  ],
                  [
                    4.4,
                    8.9
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    1.6
                  ],
                  [
                    4.3,
                    1.6
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.3
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 211,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              12.816,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 214,
        ty: 4,
        parent: 213,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 213,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              7.119,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 216,
        ty: 4,
        parent: 215,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.9,
                    0
                  ],
                  [
                    0,
                    1.7
                  ]
                ],
                v: [
                  [
                    6.3,
                    6.2
                  ],
                  [
                    6.3,
                    1.9
                  ],
                  [
                    5.2,
                    1.9
                  ],
                  [
                    5.2,
                    6.2
                  ],
                  [
                    3.6,
                    8
                  ],
                  [
                    1.9,
                    6.2
                  ],
                  [
                    1.9,
                    1.9
                  ],
                  [
                    0.8,
                    1.9
                  ],
                  [
                    0.8,
                    6.2
                  ],
                  [
                    3.5,
                    9
                  ],
                  [
                    6.3,
                    6.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 215,
        ty: 3,
        parent: 204,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 204,
        ty: 3,
        parent: 203,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 203,
        ty: 3,
        parent: 202,
        ks: {
          p: {
            a: 0,
            k: [
              -2.573,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 202,
        ty: 3,
        parent: 201,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 52.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.986,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 62.382,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 65.778,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 67.476,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 69.174,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 70.026,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 70.878,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 71.304,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.736,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.952,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.168,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 218,
        ty: 4,
        parent: 217,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 59.562,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 72.6,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 217,
        ty: 3,
        parent: 201,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 52.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.968,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 62.352,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 65.736,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 67.428,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 69.126,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 69.978,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 70.824,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 71.25,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.676,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.892,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.108,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 201,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 224,
        ty: 0,
        parent: 219,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "222"
      },
      {
        ind: 219,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 228,
        ty: 4,
        parent: 227,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 227,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 230,
        ty: 4,
        parent: 229,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 229,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 232,
        ty: 4,
        parent: 231,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 231,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 226,
        ty: 3,
        parent: 225,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 225,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 250,
        ty: 4,
        td: 1,
        parent: 233,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 249,
        ty: 0,
        tt: 1,
        parent: 233,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "247"
      },
      {
        ind: 233,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 251,
        ty: 4,
        parent: 200,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 200,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "256",
    layers: [
      {
        ind: 254,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "252"
      },
      {
        ind: 255,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "282",
    layers: [
      {
        ind: 281,
        ty: 2,
        parent: 280,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 280,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "298",
    layers: [
      {
        ind: 297,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "301",
    layers: [
      {
        ind: 300,
        ty: 0,
        parent: 296,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "298"
      },
      {
        ind: 296,
        ty: 3,
        parent: 295,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 295,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "307",
    layers: [
      {
        ind: 303,
        ty: 0,
        parent: 294,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "301"
      },
      {
        ind: 294,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 305,
        ty: 4,
        parent: 304,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 304,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 306,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "312",
    layers: [
      {
        ind: 266,
        ty: 4,
        parent: 265,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -1.4,
                    0
                  ]
                ],
                v: [
                  [
                    2.7,
                    9
                  ],
                  [
                    4.3,
                    8.1
                  ],
                  [
                    4.3,
                    8.9
                  ],
                  [
                    2.8,
                    10.4
                  ],
                  [
                    1.3,
                    9.4
                  ],
                  [
                    0.4,
                    9.9
                  ],
                  [
                    2.9,
                    11.3
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    4.1
                  ],
                  [
                    4.3,
                    4.1
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.7,
                    9
                  ]
                ],
                o: [
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.9
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.3,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 265,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              29.817,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 268,
        ty: 4,
        parent: 267,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 267,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              24.12,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 270,
        ty: 4,
        parent: 269,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.32,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.94,
                    0
                  ],
                  [
                    -0.35,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.12,
                    0.26
                  ],
                  [
                    0.81,
                    0
                  ],
                  [
                    0.31,
                    -0.66
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.37
                  ]
                ],
                v: [
                  [
                    3.47,
                    5.67
                  ],
                  [
                    3.02,
                    6.07
                  ],
                  [
                    2.42,
                    6.07
                  ],
                  [
                    0.42,
                    7.54
                  ],
                  [
                    0.42,
                    7.59
                  ],
                  [
                    2.04,
                    9
                  ],
                  [
                    3.46,
                    8.27
                  ],
                  [
                    3.46,
                    8.92
                  ],
                  [
                    4.51,
                    8.92
                  ],
                  [
                    4.51,
                    5.86
                  ],
                  [
                    4.35,
                    5
                  ],
                  [
                    2.55,
                    4.02
                  ],
                  [
                    0.5,
                    5.24
                  ],
                  [
                    1.32,
                    5.71
                  ],
                  [
                    2.52,
                    4.95
                  ],
                  [
                    3.47,
                    5.67
                  ]
                ],
                o: [
                  [
                    0,
                    0.23
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.83
                  ],
                  [
                    0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.32
                  ],
                  [
                    -0.32,
                    -0.69
                  ],
                  [
                    -1.12,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.21,
                    -0.43
                  ],
                  [
                    0.63,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.73,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.49,
                    7.49
                  ],
                  [
                    2.42,
                    6.83
                  ],
                  [
                    3.46,
                    6.83
                  ],
                  [
                    3.46,
                    6.98
                  ],
                  [
                    2.26,
                    8.15
                  ],
                  [
                    1.49,
                    7.49
                  ]
                ],
                o: [
                  [
                    0,
                    -0.45
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.72
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 269,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              18.945,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 272,
        ty: 4,
        parent: 271,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    4.4,
                    8.2
                  ],
                  [
                    4.4,
                    8.9
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    1.6
                  ],
                  [
                    4.3,
                    1.6
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.3
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 271,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              12.816,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 274,
        ty: 4,
        parent: 273,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 273,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              7.119,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 276,
        ty: 4,
        parent: 275,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.9,
                    0
                  ],
                  [
                    0,
                    1.7
                  ]
                ],
                v: [
                  [
                    6.3,
                    6.2
                  ],
                  [
                    6.3,
                    1.9
                  ],
                  [
                    5.2,
                    1.9
                  ],
                  [
                    5.2,
                    6.2
                  ],
                  [
                    3.6,
                    8
                  ],
                  [
                    1.9,
                    6.2
                  ],
                  [
                    1.9,
                    1.9
                  ],
                  [
                    0.8,
                    1.9
                  ],
                  [
                    0.8,
                    6.2
                  ],
                  [
                    3.5,
                    9
                  ],
                  [
                    6.3,
                    6.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 275,
        ty: 3,
        parent: 264,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 264,
        ty: 3,
        parent: 263,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 263,
        ty: 3,
        parent: 262,
        ks: {
          p: {
            a: 0,
            k: [
              -2.573,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 262,
        ty: 3,
        parent: 261,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 124.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 131.586,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 134.982,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 138.378,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 140.076,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 141.774,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 142.626,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 143.478,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 143.904,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.336,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.552,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.768,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 278,
        ty: 4,
        parent: 277,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 132.162,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 145.2,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 277,
        ty: 3,
        parent: 261,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 124.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 131.568,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 134.952,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 138.336,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 140.028,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 141.726,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 142.578,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 143.424,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 143.85,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.276,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.492,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.708,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 261,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 284,
        ty: 0,
        parent: 279,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "282"
      },
      {
        ind: 279,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 288,
        ty: 4,
        parent: 287,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 287,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 290,
        ty: 4,
        parent: 289,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 289,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 292,
        ty: 4,
        parent: 291,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 291,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 286,
        ty: 3,
        parent: 285,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 285,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 310,
        ty: 4,
        td: 1,
        parent: 293,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 309,
        ty: 0,
        tt: 1,
        parent: 293,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "307"
      },
      {
        ind: 293,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 311,
        ty: 4,
        parent: 260,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 260,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "316",
    layers: [
      {
        ind: 314,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "312"
      },
      {
        ind: 315,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "342",
    layers: [
      {
        ind: 341,
        ty: 2,
        parent: 340,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 340,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "358",
    layers: [
      {
        ind: 357,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "361",
    layers: [
      {
        ind: 360,
        ty: 0,
        parent: 356,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "358"
      },
      {
        ind: 356,
        ty: 3,
        parent: 355,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 355,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "367",
    layers: [
      {
        ind: 363,
        ty: 0,
        parent: 354,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "361"
      },
      {
        ind: 354,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 365,
        ty: 4,
        parent: 364,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 364,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 366,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "372",
    layers: [
      {
        ind: 326,
        ty: 4,
        parent: 325,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -1.4,
                    0
                  ]
                ],
                v: [
                  [
                    2.7,
                    9
                  ],
                  [
                    4.3,
                    8.1
                  ],
                  [
                    4.3,
                    8.9
                  ],
                  [
                    2.8,
                    10.4
                  ],
                  [
                    1.3,
                    9.4
                  ],
                  [
                    0.4,
                    9.9
                  ],
                  [
                    2.9,
                    11.3
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    4.1
                  ],
                  [
                    4.3,
                    4.1
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.7,
                    9
                  ]
                ],
                o: [
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.9
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.3,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 325,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              29.817,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 328,
        ty: 4,
        parent: 327,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 327,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              24.12,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 330,
        ty: 4,
        parent: 329,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.32,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.94,
                    0
                  ],
                  [
                    -0.35,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.12,
                    0.26
                  ],
                  [
                    0.81,
                    0
                  ],
                  [
                    0.31,
                    -0.66
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.37
                  ]
                ],
                v: [
                  [
                    3.47,
                    5.67
                  ],
                  [
                    3.02,
                    6.07
                  ],
                  [
                    2.42,
                    6.07
                  ],
                  [
                    0.42,
                    7.54
                  ],
                  [
                    0.42,
                    7.59
                  ],
                  [
                    2.04,
                    9
                  ],
                  [
                    3.46,
                    8.27
                  ],
                  [
                    3.46,
                    8.92
                  ],
                  [
                    4.51,
                    8.92
                  ],
                  [
                    4.51,
                    5.86
                  ],
                  [
                    4.35,
                    5
                  ],
                  [
                    2.55,
                    4.02
                  ],
                  [
                    0.5,
                    5.24
                  ],
                  [
                    1.32,
                    5.71
                  ],
                  [
                    2.52,
                    4.95
                  ],
                  [
                    3.47,
                    5.67
                  ]
                ],
                o: [
                  [
                    0,
                    0.23
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.83
                  ],
                  [
                    0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.32
                  ],
                  [
                    -0.32,
                    -0.69
                  ],
                  [
                    -1.12,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.21,
                    -0.43
                  ],
                  [
                    0.63,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.73,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.49,
                    7.49
                  ],
                  [
                    2.42,
                    6.83
                  ],
                  [
                    3.46,
                    6.83
                  ],
                  [
                    3.46,
                    6.98
                  ],
                  [
                    2.26,
                    8.15
                  ],
                  [
                    1.49,
                    7.49
                  ]
                ],
                o: [
                  [
                    0,
                    -0.45
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.72
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 329,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              18.945,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 332,
        ty: 4,
        parent: 331,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    4.4,
                    8.2
                  ],
                  [
                    4.4,
                    8.9
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    1.6
                  ],
                  [
                    4.3,
                    1.6
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.3
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 331,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              12.816,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 334,
        ty: 4,
        parent: 333,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 333,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              7.119,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 336,
        ty: 4,
        parent: 335,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.9,
                    0
                  ],
                  [
                    0,
                    1.7
                  ]
                ],
                v: [
                  [
                    6.3,
                    6.2
                  ],
                  [
                    6.3,
                    1.9
                  ],
                  [
                    5.2,
                    1.9
                  ],
                  [
                    5.2,
                    6.2
                  ],
                  [
                    3.6,
                    8
                  ],
                  [
                    1.9,
                    6.2
                  ],
                  [
                    1.9,
                    1.9
                  ],
                  [
                    0.8,
                    1.9
                  ],
                  [
                    0.8,
                    6.2
                  ],
                  [
                    3.5,
                    9
                  ],
                  [
                    6.3,
                    6.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 335,
        ty: 3,
        parent: 324,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 324,
        ty: 3,
        parent: 323,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 323,
        ty: 3,
        parent: 322,
        ks: {
          p: {
            a: 0,
            k: [
              -2.573,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 322,
        ty: 3,
        parent: 321,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 200.1,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.25,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 209.322,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 212.4,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 213.936,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 215.478,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 216.252,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 217.02,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 217.41,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.794,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.992,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 218.19,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 338,
        ty: 4,
        parent: 337,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 205.1286,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 218.58,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 337,
        ty: 3,
        parent: 321,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 200.1,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.25,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 209.322,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 212.4,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 213.936,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 215.478,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 216.252,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 217.02,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 217.41,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.794,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.992,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 218.19,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 321,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 344,
        ty: 0,
        parent: 339,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "342"
      },
      {
        ind: 339,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 348,
        ty: 4,
        parent: 347,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 347,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 350,
        ty: 4,
        parent: 349,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 349,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 352,
        ty: 4,
        parent: 351,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 351,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 346,
        ty: 3,
        parent: 345,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 345,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 370,
        ty: 4,
        td: 1,
        parent: 353,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 369,
        ty: 0,
        tt: 1,
        parent: 353,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "367"
      },
      {
        ind: 353,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 371,
        ty: 4,
        parent: 320,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 320,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "376",
    layers: [
      {
        ind: 374,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "372"
      },
      {
        ind: 375,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "402",
    layers: [
      {
        ind: 401,
        ty: 2,
        parent: 400,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 400,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "418",
    layers: [
      {
        ind: 417,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "421",
    layers: [
      {
        ind: 420,
        ty: 0,
        parent: 416,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "418"
      },
      {
        ind: 416,
        ty: 3,
        parent: 415,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 415,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "427",
    layers: [
      {
        ind: 423,
        ty: 0,
        parent: 414,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "421"
      },
      {
        ind: 414,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 425,
        ty: 4,
        parent: 424,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 424,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 426,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "432",
    layers: [
      {
        ind: 386,
        ty: 4,
        parent: 385,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -1.4,
                    0
                  ]
                ],
                v: [
                  [
                    2.7,
                    9
                  ],
                  [
                    4.3,
                    8.1
                  ],
                  [
                    4.3,
                    8.9
                  ],
                  [
                    2.8,
                    10.4
                  ],
                  [
                    1.3,
                    9.4
                  ],
                  [
                    0.4,
                    9.9
                  ],
                  [
                    2.9,
                    11.3
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    4.1
                  ],
                  [
                    4.3,
                    4.1
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.7,
                    9
                  ]
                ],
                o: [
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.9
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.3,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 385,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              29.817,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 388,
        ty: 4,
        parent: 387,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 387,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              24.12,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 390,
        ty: 4,
        parent: 389,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.32,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.94,
                    0
                  ],
                  [
                    -0.35,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.12,
                    0.26
                  ],
                  [
                    0.81,
                    0
                  ],
                  [
                    0.31,
                    -0.66
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.37
                  ]
                ],
                v: [
                  [
                    3.47,
                    5.67
                  ],
                  [
                    3.02,
                    6.07
                  ],
                  [
                    2.42,
                    6.07
                  ],
                  [
                    0.42,
                    7.54
                  ],
                  [
                    0.42,
                    7.59
                  ],
                  [
                    2.04,
                    9
                  ],
                  [
                    3.46,
                    8.27
                  ],
                  [
                    3.46,
                    8.92
                  ],
                  [
                    4.51,
                    8.92
                  ],
                  [
                    4.51,
                    5.86
                  ],
                  [
                    4.35,
                    5
                  ],
                  [
                    2.55,
                    4.02
                  ],
                  [
                    0.5,
                    5.24
                  ],
                  [
                    1.32,
                    5.71
                  ],
                  [
                    2.52,
                    4.95
                  ],
                  [
                    3.47,
                    5.67
                  ]
                ],
                o: [
                  [
                    0,
                    0.23
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.83
                  ],
                  [
                    0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.32
                  ],
                  [
                    -0.32,
                    -0.69
                  ],
                  [
                    -1.12,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.21,
                    -0.43
                  ],
                  [
                    0.63,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.73,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.49,
                    7.49
                  ],
                  [
                    2.42,
                    6.83
                  ],
                  [
                    3.46,
                    6.83
                  ],
                  [
                    3.46,
                    6.98
                  ],
                  [
                    2.26,
                    8.15
                  ],
                  [
                    1.49,
                    7.49
                  ]
                ],
                o: [
                  [
                    0,
                    -0.45
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.72
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 389,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              18.945,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 392,
        ty: 4,
        parent: 391,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    4.4,
                    8.2
                  ],
                  [
                    4.4,
                    8.9
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    1.6
                  ],
                  [
                    4.3,
                    1.6
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.3
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 391,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              12.816,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 394,
        ty: 4,
        parent: 393,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 393,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              7.119,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 396,
        ty: 4,
        parent: 395,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.9,
                    0
                  ],
                  [
                    0,
                    1.7
                  ]
                ],
                v: [
                  [
                    6.3,
                    6.2
                  ],
                  [
                    6.3,
                    1.9
                  ],
                  [
                    5.2,
                    1.9
                  ],
                  [
                    5.2,
                    6.2
                  ],
                  [
                    3.6,
                    8
                  ],
                  [
                    1.9,
                    6.2
                  ],
                  [
                    1.9,
                    1.9
                  ],
                  [
                    0.8,
                    1.9
                  ],
                  [
                    0.8,
                    6.2
                  ],
                  [
                    3.5,
                    9
                  ],
                  [
                    6.3,
                    6.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 395,
        ty: 3,
        parent: 384,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 384,
        ty: 3,
        parent: 383,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 383,
        ty: 3,
        parent: 382,
        ks: {
          p: {
            a: 0,
            k: [
              -2.573,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 382,
        ty: 3,
        parent: 381,
        ks: {
          p: {
            a: 0,
            k: [
              29.582,
              2.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 398,
        ty: 4,
        parent: 397,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    1,
                    1,
                    1
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 397,
        ty: 3,
        parent: 381,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 381,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 404,
        ty: 0,
        parent: 399,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "402"
      },
      {
        ind: 399,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 408,
        ty: 4,
        parent: 407,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 407,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 410,
        ty: 4,
        parent: 409,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 409,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 412,
        ty: 4,
        parent: 411,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 411,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 406,
        ty: 3,
        parent: 405,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 405,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 430,
        ty: 4,
        td: 1,
        parent: 413,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 429,
        ty: 0,
        tt: 1,
        parent: 413,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "427"
      },
      {
        ind: 413,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 431,
        ty: 4,
        parent: 380,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 380,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              1
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "436",
    layers: [
      {
        ind: 434,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              3,
              1
            ]
          },
          p: {
            a: 0,
            k: [
              48.6,
              46.6
            ]
          }
        },
        w: 181,
        h: 97,
        ip: 0,
        op: 361,
        st: 0,
        refId: "432"
      },
      {
        ind: 435,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                137.345,
                94.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                274.69,
                189.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "462",
    layers: [
      {
        ind: 461,
        ty: 2,
        parent: 460,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 460,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "478",
    layers: [
      {
        ind: 477,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                17.5,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                35,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.41,
                            -1.91
                          ],
                          [
                            -0.92,
                            0.09
                          ],
                          [
                            1.69,
                            -0.98
                          ],
                          [
                            -0.37,
                            -0.85
                          ],
                          [
                            1.45,
                            1.31
                          ],
                          [
                            0.69,
                            -0.62
                          ],
                          [
                            -0.79,
                            1.79
                          ],
                          [
                            0.8,
                            0.46
                          ],
                          [
                            -1.95,
                            -0.21
                          ],
                          [
                            -0.2,
                            0.91
                          ]
                        ],
                        o: [
                          [
                            0.41,
                            -1.91
                          ],
                          [
                            0.19,
                            0.91
                          ],
                          [
                            1.94,
                            -0.21
                          ],
                          [
                            -0.8,
                            0.46
                          ],
                          [
                            0.8,
                            1.79
                          ],
                          [
                            -0.69,
                            -0.62
                          ],
                          [
                            -1.45,
                            1.31
                          ],
                          [
                            0.38,
                            -0.85
                          ],
                          [
                            -1.7,
                            -0.98
                          ],
                          [
                            0.92,
                            0.09
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            11.5,
                            8.55
                          ],
                          [
                            15.05,
                            8.55
                          ],
                          [
                            17.01,
                            9.98
                          ],
                          [
                            18.1,
                            13.34
                          ],
                          [
                            17.35,
                            15.65
                          ],
                          [
                            14.49,
                            17.73
                          ],
                          [
                            12.06,
                            17.73
                          ],
                          [
                            9.2,
                            15.65
                          ],
                          [
                            8.45,
                            13.34
                          ],
                          [
                            9.54,
                            9.98
                          ],
                          [
                            11.5,
                            8.55
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.9,
                        0.01,
                        0.47,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "486",
    layers: [
      {
        ind: 485,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "489",
    layers: [
      {
        ind: 488,
        ty: 0,
        parent: 484,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "486"
      },
      {
        ind: 484,
        ty: 3,
        parent: 483,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 483,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.233,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "495",
    layers: [
      {
        ind: 491,
        ty: 0,
        parent: 482,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "489"
      },
      {
        ind: 482,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 493,
        ty: 4,
        parent: 492,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 492,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 494,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "500",
    layers: [
      {
        ind: 446,
        ty: 4,
        parent: 445,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.03
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    -0.91
                  ],
                  [
                    -0.95,
                    0
                  ],
                  [
                    -0.28,
                    0.51
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.39,
                    0
                  ],
                  [
                    0.01,
                    0.49
                  ]
                ],
                v: [
                  [
                    1,
                    4.56
                  ],
                  [
                    3.43,
                    4.56
                  ],
                  [
                    3.44,
                    4.34
                  ],
                  [
                    1.88,
                    2.68
                  ],
                  [
                    0.26,
                    4.34
                  ],
                  [
                    1.94,
                    6.01
                  ],
                  [
                    3.31,
                    5.24
                  ],
                  [
                    2.8,
                    4.89
                  ],
                  [
                    1.93,
                    5.39
                  ],
                  [
                    1,
                    4.56
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.01,
                    -0.08
                  ],
                  [
                    0,
                    -0.97
                  ],
                  [
                    -0.93,
                    0
                  ],
                  [
                    0,
                    0.95
                  ],
                  [
                    0.56,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.19,
                    0.35
                  ],
                  [
                    -0.47,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.44,
                    0
                  ],
                  [
                    -0.02,
                    -0.46
                  ]
                ],
                v: [
                  [
                    1.03,
                    4.02
                  ],
                  [
                    1.88,
                    3.29
                  ],
                  [
                    2.71,
                    4.02
                  ]
                ],
                o: [
                  [
                    0.07,
                    -0.44
                  ],
                  [
                    0.44,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 445,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              13.464,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 448,
        ty: 4,
        parent: 447,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.69,
                    0
                  ],
                  [
                    -0.13,
                    0.05
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.07,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.44,
                    4.8
                  ],
                  [
                    1.44,
                    3.39
                  ],
                  [
                    2.23,
                    3.39
                  ],
                  [
                    2.23,
                    2.75
                  ],
                  [
                    1.44,
                    2.75
                  ],
                  [
                    1.44,
                    1.76
                  ],
                  [
                    0.73,
                    1.76
                  ],
                  [
                    0.73,
                    2.75
                  ],
                  [
                    0.14,
                    2.75
                  ],
                  [
                    0.14,
                    3.39
                  ],
                  [
                    0.73,
                    3.39
                  ],
                  [
                    0.73,
                    4.92
                  ],
                  [
                    1.84,
                    5.99
                  ],
                  [
                    2.22,
                    5.92
                  ],
                  [
                    2.22,
                    5.31
                  ],
                  [
                    1.99,
                    5.34
                  ],
                  [
                    1.44,
                    4.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.71
                  ],
                  [
                    0.09,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.09,
                    0.02
                  ],
                  [
                    -0.35,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 447,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              11.022,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 450,
        ty: 4,
        parent: 449,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ]
                ],
                v: [
                  [
                    0.37,
                    1.71
                  ],
                  [
                    0.83,
                    2.17
                  ],
                  [
                    1.3,
                    1.71
                  ],
                  [
                    0.83,
                    1.23
                  ],
                  [
                    0.37,
                    1.71
                  ]
                ],
                o: [
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.48,
                    5.95
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    1.19,
                    2.75
                  ],
                  [
                    1.19,
                    5.95
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 449,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              9.354,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 452,
        ty: 4,
        parent: 451,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    2.75
                  ],
                  [
                    1.46,
                    5.95
                  ],
                  [
                    2.17,
                    5.95
                  ],
                  [
                    3.51,
                    2.75
                  ],
                  [
                    2.76,
                    2.75
                  ],
                  [
                    1.82,
                    5.12
                  ],
                  [
                    0.88,
                    2.75
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 451,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              5.73,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 454,
        ty: 4,
        parent: 453,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.82,
                    0
                  ],
                  [
                    0.31,
                    -0.41
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    -0.46
                  ]
                ],
                v: [
                  [
                    2.64,
                    4.15
                  ],
                  [
                    2.64,
                    5.95
                  ],
                  [
                    3.35,
                    5.95
                  ],
                  [
                    3.35,
                    3.97
                  ],
                  [
                    2.15,
                    2.68
                  ],
                  [
                    1.18,
                    3.18
                  ],
                  [
                    1.18,
                    2.75
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    0.48,
                    5.95
                  ],
                  [
                    1.19,
                    5.95
                  ],
                  [
                    1.19,
                    4.17
                  ],
                  [
                    1.93,
                    3.36
                  ],
                  [
                    2.64,
                    4.15
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    -0.25,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.49
                  ],
                  [
                    0.49,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 453,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              1.932,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 456,
        ty: 4,
        parent: 455,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    5.95
                  ],
                  [
                    1.34,
                    5.95
                  ],
                  [
                    1.34,
                    1.25
                  ],
                  [
                    0.6,
                    1.25
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 455,
        ty: 3,
        parent: 444,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 444,
        ty: 3,
        parent: 443,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 443,
        ty: 3,
        parent: 442,
        ks: {
          p: {
            a: 0,
            k: [
              2.072,
              1.141
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 442,
        ty: 3,
        parent: 441,
        ks: {
          p: {
            a: 0,
            k: [
              34.315,
              4.741
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 458,
        ty: 4,
        parent: 457,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.129,
                    0.322,
                    0.584
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    1,
                    1,
                    1
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 457,
        ty: 3,
        parent: 441,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 441,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 464,
        ty: 0,
        parent: 459,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "462"
      },
      {
        ind: 459,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 468,
        ty: 4,
        parent: 467,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 467,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 470,
        ty: 4,
        parent: 469,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 469,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 473,
        ty: 4,
        parent: 472,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 472,
        ty: 3,
        parent: 471,
        ks: {
          p: {
            a: 0,
            k: [
              14.813,
              4.266
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 480,
        ty: 0,
        parent: 476,
        ks: {},
        w: 35,
        h: 35,
        ip: 0,
        op: 361,
        st: 0,
        refId: "478"
      },
      {
        ind: 476,
        ty: 3,
        parent: 475,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 475,
        ty: 3,
        parent: 474,
        ks: {
          p: {
            a: 0,
            k: [
              -10.395,
              -5.224
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 474,
        ty: 3,
        parent: 471,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 471,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              -0.173
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 466,
        ty: 3,
        parent: 465,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 465,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 498,
        ty: 4,
        td: 1,
        parent: 481,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 497,
        ty: 0,
        tt: 1,
        parent: 481,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "495"
      },
      {
        ind: 481,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 499,
        ty: 4,
        parent: 440,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 440,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              6
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "504",
    layers: [
      {
        ind: 502,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              13,
              6
            ]
          },
          p: {
            a: 0,
            k: [
              58.6,
              51.6
            ]
          }
        },
        w: 191,
        h: 102,
        ip: 0,
        op: 361,
        st: 0,
        refId: "500"
      },
      {
        ind: 503,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                147.345,
                99.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                294.69,
                199.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "507",
    layers: [
      {
        ind: 138,
        ty: 0,
        parent: 71,
        ks: {
          p: {
            a: 0,
            k: [
              -58.6,
              -51.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 294.69,
        h: 199.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "136"
      },
      {
        ind: 71,
        ty: 3,
        parent: 70,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 198,
        ty: 0,
        parent: 139,
        ks: {
          p: {
            a: 0,
            k: [
              -48.6,
              -46.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 274.69,
        h: 189.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "196"
      },
      {
        ind: 139,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              194.056,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 258,
        ty: 0,
        parent: 199,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "256"
      },
      {
        ind: 199,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              388,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 318,
        ty: 0,
        parent: 259,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "316"
      },
      {
        ind: 259,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              582.167,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 378,
        ty: 0,
        parent: 319,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "376"
      },
      {
        ind: 319,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              776.223,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 438,
        ty: 0,
        parent: 379,
        ks: {
          p: {
            a: 0,
            k: [
              -48.6,
              -46.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 274.69,
        h: 189.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "436"
      },
      {
        ind: 379,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              970.279,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 506,
        ty: 0,
        parent: 439,
        ks: {
          p: {
            a: 0,
            k: [
              -58.6,
              -51.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 294.69,
        h: 199.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "504"
      },
      {
        ind: 439,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              1164.335,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 70,
        ty: 3,
        ks: {
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  -328.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.6,
                s: [
                  -328.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 120.6,
                s: [
                  -523.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 145.2,
                s: [
                  -523.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 193.2,
                s: [
                  -718.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.8,
                s: [
                  -718.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 265.8,
                s: [
                  -913.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  -913.709,
                  58.074
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  }
];
const fr$7 = 60;
const h$7 = 238;
const ip$7 = 0;
const layers$7 = [
  {
    ind: 5,
    ty: 4,
    td: 1,
    parent: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 4,
    ty: 4,
    tt: 1,
    parent: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 3,
    ty: 3,
    parent: 2,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 9,
    ty: 0,
    parent: 2,
    ks: {
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 48,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 281.4,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 329.4,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              100
            ],
            h: 1
          }
        ]
      }
    },
    w: 255,
    h: 238,
    ip: 0,
    op: 361,
    st: 0,
    refId: "7"
  },
  {
    ind: 20,
    ty: 0,
    parent: 11,
    ks: {
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 60.6,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 63.54,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          -0.386,
          0
        ]
      }
    },
    w: 26,
    h: 17,
    ip: 0,
    op: 361,
    st: 0,
    refId: "18"
  },
  {
    ind: 34,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          0,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 63.54,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 72.54,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 132.54,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 136.14,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          0.386,
          0
        ]
      }
    },
    w: 22,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "32"
  },
  {
    ind: 49,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          1,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 136.14,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 145.14,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 205.14,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 208.74,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      }
    },
    w: 29,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "47"
  },
  {
    ind: 63,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          1,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 208.74,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 217.74,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              100
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          0.773,
          0
        ]
      }
    },
    w: 27,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "61"
  },
  {
    ind: 11,
    ty: 3,
    parent: 10,
    ks: {
      p: {
        a: 0,
        k: [
          14.679,
          20.415
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 66,
    ty: 4,
    parent: 65,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "sh",
        ks: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35.1,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 64,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 65,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 66,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 67,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.5,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 68,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.5,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 69,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.4,
                      1.2
                    ],
                    [
                      34.9,
                      6.3
                    ],
                    [
                      9.4,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 70,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.4
                    ],
                    [
                      36.3,
                      1.2
                    ],
                    [
                      34.9,
                      6.3
                    ],
                    [
                      9.3,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 71,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.9,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.4
                    ],
                    [
                      36.2,
                      1.2
                    ],
                    [
                      34.8,
                      6.2
                    ],
                    [
                      9.3,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.9
                    ],
                    [
                      6.9,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.8
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 72,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.9,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.6,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.5
                    ],
                    [
                      36.1,
                      1.1
                    ],
                    [
                      34.7,
                      6.2
                    ],
                    [
                      9.3,
                      15.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.9
                    ],
                    [
                      6.9,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.8
                    ],
                    [
                      -5.6,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 73,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.6,
                      -8.3
                    ],
                    [
                      -6,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.9,
                      12.6
                    ],
                    [
                      35.9,
                      1.1
                    ],
                    [
                      34.5,
                      6.2
                    ],
                    [
                      9.2,
                      15.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.8
                    ],
                    [
                      6.8,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.7
                    ],
                    [
                      -5.6,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 74,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.5,
                      -8.3
                    ],
                    [
                      -5.9,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.8,
                      12.7
                    ],
                    [
                      35.6,
                      1
                    ],
                    [
                      34.3,
                      6.1
                    ],
                    [
                      9.2,
                      15.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.8
                    ],
                    [
                      6.8,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.6
                    ],
                    [
                      -5.5,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 75,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.7,
                      10.2
                    ],
                    [
                      -11.6,
                      -3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.4,
                      -8.3
                    ],
                    [
                      -5.9,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.7,
                      12.8
                    ],
                    [
                      35.3,
                      0.9
                    ],
                    [
                      34,
                      6
                    ],
                    [
                      9.1,
                      15.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.2,
                      -9.8
                    ],
                    [
                      6.7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.5
                    ],
                    [
                      -5.4,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 76,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      10.1
                    ],
                    [
                      -11.6,
                      -2.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.3,
                      -8.3
                    ],
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.6,
                      13
                    ],
                    [
                      34.8,
                      0.8
                    ],
                    [
                      33.6,
                      5.9
                    ],
                    [
                      9,
                      15.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.2,
                      -9.7
                    ],
                    [
                      6.5,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.3
                    ],
                    [
                      -5.3,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 77,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      10.1
                    ],
                    [
                      -11.5,
                      -2.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.2,
                      -8.3
                    ],
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.4,
                      13.3
                    ],
                    [
                      34.1,
                      0.6
                    ],
                    [
                      33.1,
                      5.8
                    ],
                    [
                      8.8,
                      16.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.1,
                      -9.6
                    ],
                    [
                      6.3,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.4,
                      -2
                    ],
                    [
                      -5.2,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 78,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.1,
                      10.1
                    ],
                    [
                      -11.5,
                      -2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5,
                      -8.2
                    ],
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.2,
                      13.6
                    ],
                    [
                      33.5,
                      0.5
                    ],
                    [
                      32.5,
                      5.7
                    ],
                    [
                      8.7,
                      16.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7,
                      -9.5
                    ],
                    [
                      6.1,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.4,
                      -1.8
                    ],
                    [
                      -5,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 79,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6,
                      10.1
                    ],
                    [
                      -11.4,
                      -1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.9,
                      -8.2
                    ],
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.1,
                      13.9
                    ],
                    [
                      32.9,
                      0.4
                    ],
                    [
                      32,
                      5.6
                    ],
                    [
                      8.5,
                      16.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7,
                      -9.4
                    ],
                    [
                      6,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.3,
                      -1.5
                    ],
                    [
                      -4.9,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 80,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.9,
                      10
                    ],
                    [
                      -11.4,
                      -1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.8,
                      -8.2
                    ],
                    [
                      -5.6,
                      -7.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4,
                      14.1
                    ],
                    [
                      32.4,
                      0.3
                    ],
                    [
                      31.6,
                      5.6
                    ],
                    [
                      8.5,
                      16.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.9,
                      -9.3
                    ],
                    [
                      5.9,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.3,
                      -1.4
                    ],
                    [
                      -4.8,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 81,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.7,
                      10
                    ],
                    [
                      -11.3,
                      -1.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.7,
                      -8.2
                    ],
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.9,
                      14.3
                    ],
                    [
                      32,
                      0.2
                    ],
                    [
                      31.3,
                      5.5
                    ],
                    [
                      8.4,
                      16.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.9,
                      -9.3
                    ],
                    [
                      5.7,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1.2
                    ],
                    [
                      -4.7,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 82,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.7,
                      10
                    ],
                    [
                      -11.3,
                      -1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.6,
                      -8.1
                    ],
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.8,
                      14.4
                    ],
                    [
                      31.7,
                      0.2
                    ],
                    [
                      31.1,
                      5.5
                    ],
                    [
                      8.3,
                      17.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.2
                    ],
                    [
                      5.7,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1.1
                    ],
                    [
                      -4.6,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 83,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.6,
                      10
                    ],
                    [
                      -11.2,
                      -1.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.6,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.7,
                      14.5
                    ],
                    [
                      31.4,
                      0.2
                    ],
                    [
                      30.9,
                      5.5
                    ],
                    [
                      8.3,
                      17.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.2
                    ],
                    [
                      5.6,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1
                    ],
                    [
                      -4.6,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 84,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.5,
                      10
                    ],
                    [
                      -11.2,
                      -1.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.5,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.7,
                      14.6
                    ],
                    [
                      31.2,
                      0.2
                    ],
                    [
                      30.7,
                      5.4
                    ],
                    [
                      8.2,
                      17.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.1
                    ],
                    [
                      5.5,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -1
                    ],
                    [
                      -4.5,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 85,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.5,
                      10
                    ],
                    [
                      -11.2,
                      -1.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.5,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.6,
                      14.7
                    ],
                    [
                      31,
                      0.1
                    ],
                    [
                      30.5,
                      5.4
                    ],
                    [
                      8.2,
                      17.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.1
                    ],
                    [
                      5.5,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.9
                    ],
                    [
                      -4.5,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 86,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.6,
                      14.8
                    ],
                    [
                      30.9,
                      0.1
                    ],
                    [
                      30.4,
                      5.4
                    ],
                    [
                      8.1,
                      17.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9.1
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.8
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 87,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      14.8
                    ],
                    [
                      30.7,
                      0.1
                    ],
                    [
                      30.3,
                      5.4
                    ],
                    [
                      8.1,
                      17.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9.1
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.8
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 88,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      14.9
                    ],
                    [
                      30.6,
                      0.1
                    ],
                    [
                      30.2,
                      5.4
                    ],
                    [
                      8.1,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.7
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 89,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      15
                    ],
                    [
                      30.5,
                      0.1
                    ],
                    [
                      30.1,
                      5.4
                    ],
                    [
                      8.1,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.7
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 90,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      15
                    ],
                    [
                      30.4,
                      0.1
                    ],
                    [
                      30,
                      5.4
                    ],
                    [
                      8,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.7
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 91,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15
                    ],
                    [
                      30.3,
                      0.1
                    ],
                    [
                      29.9,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 92,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30.2,
                      0.1
                    ],
                    [
                      29.8,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 93,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30.1,
                      0.1
                    ],
                    [
                      29.8,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 94,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30,
                      0.1
                    ],
                    [
                      29.7,
                      5.4
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 95,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.2
                    ],
                    [
                      30,
                      0.1
                    ],
                    [
                      29.7,
                      5.4
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 96,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.9,
                      0
                    ],
                    [
                      29.6,
                      5.3
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 97,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.9,
                      0
                    ],
                    [
                      29.6,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 98,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 99,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 100,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 101,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 102,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 103,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 104,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 105,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 106,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 107,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 108,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 109,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 110,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 111,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 112,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 137,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 138,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 139,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 140,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 141,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.2,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 142,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.4,
                      0
                    ],
                    [
                      29.2,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 143,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.5
                    ],
                    [
                      29.3,
                      0
                    ],
                    [
                      29.1,
                      5.3
                    ],
                    [
                      7.8,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 144,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.5
                    ],
                    [
                      29.2,
                      0
                    ],
                    [
                      29,
                      5.3
                    ],
                    [
                      7.8,
                      18
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.8
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 145,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.1,
                      15.6
                    ],
                    [
                      29,
                      0
                    ],
                    [
                      28.9,
                      5.3
                    ],
                    [
                      7.8,
                      18
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.8
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.2
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 146,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.9,
                      9.8
                    ],
                    [
                      -10.8,
                      -0.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.1,
                      15.7
                    ],
                    [
                      28.8,
                      0
                    ],
                    [
                      28.7,
                      5.3
                    ],
                    [
                      7.7,
                      18.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.8
                    ],
                    [
                      4.9,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.8,
                      -0.2
                    ],
                    [
                      -4,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 147,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.8,
                      9.8
                    ],
                    [
                      -10.8,
                      -0.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.9,
                      -8
                    ],
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3,
                      15.8
                    ],
                    [
                      28.5,
                      0
                    ],
                    [
                      28.5,
                      5.3
                    ],
                    [
                      7.7,
                      18.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.7
                    ],
                    [
                      4.8,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.8,
                      -0.1
                    ],
                    [
                      -3.9,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 148,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.7,
                      9.7
                    ],
                    [
                      -10.7,
                      0.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.9,
                      -7.9
                    ],
                    [
                      -5.2,
                      -7.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.9,
                      16
                    ],
                    [
                      28.1,
                      0
                    ],
                    [
                      28.1,
                      5.3
                    ],
                    [
                      7.6,
                      18.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.4,
                      -8.7
                    ],
                    [
                      4.7,
                      -9.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.7,
                      0.1
                    ],
                    [
                      -3.9,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 149,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.6,
                      9.7
                    ],
                    [
                      -10.6,
                      0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.7,
                      -7.9
                    ],
                    [
                      -5.2,
                      -7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.8,
                      16.3
                    ],
                    [
                      27.5,
                      0
                    ],
                    [
                      27.7,
                      5.3
                    ],
                    [
                      7.5,
                      18.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.4,
                      -8.6
                    ],
                    [
                      4.6,
                      -9.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.6,
                      0.2
                    ],
                    [
                      -3.7,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 150,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.4,
                      9.6
                    ],
                    [
                      -10.5,
                      0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.6,
                      -7.9
                    ],
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.7,
                      16.6
                    ],
                    [
                      26.8,
                      0
                    ],
                    [
                      27.1,
                      5.3
                    ],
                    [
                      7.4,
                      18.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.3,
                      -8.5
                    ],
                    [
                      4.4,
                      -9.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.5,
                      0.5
                    ],
                    [
                      -3.6,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 151,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.3,
                      9.6
                    ],
                    [
                      -10.3,
                      0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.5,
                      -7.8
                    ],
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.5,
                      16.9
                    ],
                    [
                      26.2,
                      0.1
                    ],
                    [
                      26.6,
                      5.4
                    ],
                    [
                      7.3,
                      19.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.2,
                      -8.4
                    ],
                    [
                      4.3,
                      -9.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.4,
                      0.7
                    ],
                    [
                      -3.5,
                      7.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 152,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.1,
                      9.5
                    ],
                    [
                      -10.2,
                      1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.4,
                      -7.8
                    ],
                    [
                      -5,
                      -6.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.4,
                      17.2
                    ],
                    [
                      25.6,
                      0.1
                    ],
                    [
                      26.1,
                      5.4
                    ],
                    [
                      7.2,
                      19.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.2,
                      -8.3
                    ],
                    [
                      4.1,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.3,
                      0.8
                    ],
                    [
                      -3.4,
                      7.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 153,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4,
                      9.5
                    ],
                    [
                      -10.1,
                      1.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.3,
                      -7.7
                    ],
                    [
                      -5,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.3,
                      17.4
                    ],
                    [
                      25.2,
                      0.2
                    ],
                    [
                      25.7,
                      5.5
                    ],
                    [
                      7.1,
                      19.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.1,
                      -8.3
                    ],
                    [
                      4,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.2,
                      0.9
                    ],
                    [
                      -3.3,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 154,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.9,
                      9.5
                    ],
                    [
                      -10,
                      1.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.2,
                      -7.7
                    ],
                    [
                      -5,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.2,
                      17.5
                    ],
                    [
                      24.8,
                      0.2
                    ],
                    [
                      25.5,
                      5.5
                    ],
                    [
                      7.1,
                      19.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.1,
                      -8.2
                    ],
                    [
                      3.9,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.2,
                      1
                    ],
                    [
                      -3.2,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 155,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.9,
                      9.4
                    ],
                    [
                      -10,
                      1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.2,
                      -7.7
                    ],
                    [
                      -4.9,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.2,
                      17.7
                    ],
                    [
                      24.5,
                      0.3
                    ],
                    [
                      25.2,
                      5.5
                    ],
                    [
                      7,
                      19.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.2
                    ],
                    [
                      3.9,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.1,
                      1.1
                    ],
                    [
                      -3.2,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 156,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.8,
                      9.4
                    ],
                    [
                      -9.9,
                      1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.1,
                      -7.7
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      17.8
                    ],
                    [
                      24.3,
                      0.3
                    ],
                    [
                      25,
                      5.5
                    ],
                    [
                      7,
                      19.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.8,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.1,
                      1.2
                    ],
                    [
                      -3.1,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 157,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.8,
                      9.4
                    ],
                    [
                      -9.9,
                      1.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.1,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      17.9
                    ],
                    [
                      24.1,
                      0.3
                    ],
                    [
                      24.8,
                      5.6
                    ],
                    [
                      6.9,
                      19.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.8,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.2
                    ],
                    [
                      -3.1,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 158,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.4
                    ],
                    [
                      -9.8,
                      1.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      18
                    ],
                    [
                      23.9,
                      0.4
                    ],
                    [
                      24.7,
                      5.6
                    ],
                    [
                      6.9,
                      20
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.7,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.3
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 159,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.3
                    ],
                    [
                      -9.8,
                      1.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.1
                    ],
                    [
                      23.7,
                      0.4
                    ],
                    [
                      24.6,
                      5.6
                    ],
                    [
                      6.9,
                      20
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8
                    ],
                    [
                      3.7,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.3
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 160,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.3
                    ],
                    [
                      -9.7,
                      1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.1
                    ],
                    [
                      23.6,
                      0.4
                    ],
                    [
                      24.5,
                      5.6
                    ],
                    [
                      6.9,
                      20.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.7,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 161,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.2
                    ],
                    [
                      23.5,
                      0.4
                    ],
                    [
                      24.4,
                      5.7
                    ],
                    [
                      6.8,
                      20.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 162,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.2
                    ],
                    [
                      23.3,
                      0.4
                    ],
                    [
                      24.3,
                      5.7
                    ],
                    [
                      6.8,
                      20.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 163,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.3
                    ],
                    [
                      23.2,
                      0.5
                    ],
                    [
                      24.2,
                      5.7
                    ],
                    [
                      6.8,
                      20.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 164,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.6,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.3
                    ],
                    [
                      23.2,
                      0.5
                    ],
                    [
                      24.1,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 165,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      23.1,
                      0.5
                    ],
                    [
                      24,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 166,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      23,
                      0.5
                    ],
                    [
                      24,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 167,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      22.9,
                      0.5
                    ],
                    [
                      23.9,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 168,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.9,
                      0.5
                    ],
                    [
                      23.9,
                      5.7
                    ],
                    [
                      6.8,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 169,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.8,
                      0.5
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 170,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.8,
                      0.6
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 171,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.5,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 172,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 173,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 174,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 175,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 176,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 177,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 178,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 179,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 180,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 181,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 182,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 183,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 184,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 185,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 209,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 210,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 211,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 212,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 213,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.4,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 214,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.4,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 215,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.8
                    ],
                    [
                      22.3,
                      0.7
                    ],
                    [
                      23.4,
                      5.8
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 216,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.8
                    ],
                    [
                      22.2,
                      0.7
                    ],
                    [
                      23.3,
                      5.9
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 217,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.2
                    ],
                    [
                      -9.4,
                      2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      18.9
                    ],
                    [
                      22.1,
                      0.7
                    ],
                    [
                      23.2,
                      5.9
                    ],
                    [
                      6.6,
                      20.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.8
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 218,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.1
                    ],
                    [
                      -9.3,
                      2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      19
                    ],
                    [
                      21.9,
                      0.8
                    ],
                    [
                      23.1,
                      5.9
                    ],
                    [
                      6.6,
                      20.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.8
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 219,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.2,
                      9.1
                    ],
                    [
                      -9.3,
                      2.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.6,
                      -7.4
                    ],
                    [
                      -4.7,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      19.1
                    ],
                    [
                      21.6,
                      0.8
                    ],
                    [
                      22.9,
                      6
                    ],
                    [
                      6.6,
                      20.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      3.2,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.9
                    ],
                    [
                      -2.6,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 220,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.1,
                      9.1
                    ],
                    [
                      -9.2,
                      2.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.6,
                      -7.4
                    ],
                    [
                      -4.6,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.6,
                      19.2
                    ],
                    [
                      21.3,
                      0.9
                    ],
                    [
                      22.6,
                      6
                    ],
                    [
                      6.5,
                      21
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      3.1,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.5,
                      1.9
                    ],
                    [
                      -2.6,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 221,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3,
                      9
                    ],
                    [
                      -9.1,
                      2.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.5,
                      -7.4
                    ],
                    [
                      -4.6,
                      -6.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.5,
                      19.5
                    ],
                    [
                      20.9,
                      1
                    ],
                    [
                      22.2,
                      6.1
                    ],
                    [
                      6.5,
                      21.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.6,
                      -7.6
                    ],
                    [
                      3,
                      -9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.4,
                      2
                    ],
                    [
                      -2.5,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 222,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.9,
                      9
                    ],
                    [
                      -8.9,
                      2.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.4,
                      -7.3
                    ],
                    [
                      -4.6,
                      -6.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.4,
                      19.8
                    ],
                    [
                      20.3,
                      1.2
                    ],
                    [
                      21.7,
                      6.3
                    ],
                    [
                      6.4,
                      21.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      2.9,
                      -9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.3,
                      2.2
                    ],
                    [
                      -2.4,
                      7.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 223,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.8,
                      8.9
                    ],
                    [
                      -8.7,
                      2.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.2,
                      -7.2
                    ],
                    [
                      -4.5,
                      -6.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.3,
                      20.1
                    ],
                    [
                      19.6,
                      1.4
                    ],
                    [
                      21.2,
                      6.5
                    ],
                    [
                      6.3,
                      21.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      2.8,
                      -8.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.1,
                      2.3
                    ],
                    [
                      -2.2,
                      7.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 224,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.6,
                      8.8
                    ],
                    [
                      -8.5,
                      3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.1,
                      -7.2
                    ],
                    [
                      -4.4,
                      -6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.2,
                      20.4
                    ],
                    [
                      19,
                      1.6
                    ],
                    [
                      20.7,
                      6.6
                    ],
                    [
                      6.2,
                      22
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      2.6,
                      -8.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      2.5
                    ],
                    [
                      -2.1,
                      7.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 225,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.5,
                      8.7
                    ],
                    [
                      -8.4,
                      3.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.1,
                      -7.1
                    ],
                    [
                      -4.4,
                      -5.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.1,
                      20.7
                    ],
                    [
                      18.5,
                      1.8
                    ],
                    [
                      20.3,
                      6.8
                    ],
                    [
                      6.2,
                      22.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      2.5,
                      -8.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      2.6
                    ],
                    [
                      -2.1,
                      7.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 226,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.4,
                      8.7
                    ],
                    [
                      -8.3,
                      3.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2,
                      -7.1
                    ],
                    [
                      -4.4,
                      -5.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.1,
                      20.9
                    ],
                    [
                      18.1,
                      1.9
                    ],
                    [
                      20,
                      6.9
                    ],
                    [
                      6.1,
                      22.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      2.4,
                      -8.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      2.6
                    ],
                    [
                      -2,
                      7.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 227,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.4,
                      8.6
                    ],
                    [
                      -8.2,
                      3.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21
                    ],
                    [
                      17.8,
                      2
                    ],
                    [
                      19.8,
                      7
                    ],
                    [
                      6.1,
                      22.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      2.4,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.7,
                      2.7
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 228,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.3,
                      8.6
                    ],
                    [
                      -8.1,
                      3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.1
                    ],
                    [
                      17.6,
                      2.1
                    ],
                    [
                      19.5,
                      7.1
                    ],
                    [
                      6,
                      22.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      2.3,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.6,
                      2.8
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 229,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.3,
                      8.6
                    ],
                    [
                      -8.1,
                      3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.3
                    ],
                    [
                      17.4,
                      2.2
                    ],
                    [
                      19.4,
                      7.1
                    ],
                    [
                      6,
                      22.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      2.3,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.6,
                      2.8
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 230,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -8,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.4
                    ],
                    [
                      17.2,
                      2.3
                    ],
                    [
                      19.2,
                      7.2
                    ],
                    [
                      6,
                      22.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7.1
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      2.8
                    ],
                    [
                      -1.8,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 231,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -7.9,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.3,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.4
                    ],
                    [
                      17,
                      2.4
                    ],
                    [
                      19.1,
                      7.3
                    ],
                    [
                      6,
                      22.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 232,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -7.9,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.5
                    ],
                    [
                      16.9,
                      2.5
                    ],
                    [
                      19,
                      7.3
                    ],
                    [
                      6,
                      22.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 233,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.5
                    ],
                    [
                      -7.9,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.6
                    ],
                    [
                      16.7,
                      2.5
                    ],
                    [
                      18.9,
                      7.4
                    ],
                    [
                      6,
                      22.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 234,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.5
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.6
                    ],
                    [
                      16.6,
                      2.6
                    ],
                    [
                      18.8,
                      7.4
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 235,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.7
                    ],
                    [
                      16.5,
                      2.6
                    ],
                    [
                      18.7,
                      7.4
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 236,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.7
                    ],
                    [
                      16.4,
                      2.6
                    ],
                    [
                      18.6,
                      7.5
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 237,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.8
                    ],
                    [
                      16.4,
                      2.7
                    ],
                    [
                      18.6,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 238,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.8
                    ],
                    [
                      16.3,
                      2.7
                    ],
                    [
                      18.5,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 239,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.2,
                      2.7
                    ],
                    [
                      18.5,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 240,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.2,
                      2.8
                    ],
                    [
                      18.4,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 241,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.1,
                      2.8
                    ],
                    [
                      18.4,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 242,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.1,
                      2.8
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 243,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.8
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 244,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.9
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 245,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 246,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 247,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 248,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 249,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 250,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 251,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 252,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 253,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 254,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 255,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 256,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 257,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.902,
            8e-3,
            0.471
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 65,
    ty: 3,
    parent: 64,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 68,
    ty: 4,
    parent: 67,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "sh",
        ks: {
          a: 0,
          k: {
            c: true,
            i: [
              [
                0,
                0
              ],
              [
                -10.6,
                9.9
              ],
              [
                -10.9,
                -9.6
              ],
              [
                8.3,
                -12
              ],
              [
                0,
                0
              ],
              [
                8.9,
                7.9
              ],
              [
                8.7,
                -8.1
              ],
              [
                -7,
                -9.6
              ],
              [
                0,
                0
              ]
            ],
            v: [
              [
                5.5,
                45.8
              ],
              [
                9.1,
                7.6
              ],
              [
                46.9,
                7.1
              ],
              [
                51.5,
                45.2
              ],
              [
                47.2,
                42.2
              ],
              [
                43.5,
                11.1
              ],
              [
                12.7,
                11.5
              ],
              [
                9.7,
                42.7
              ],
              [
                5.5,
                45.8
              ]
            ],
            o: [
              [
                -8.6,
                -11.8
              ],
              [
                10.6,
                -9.9
              ],
              [
                10.9,
                9.6
              ],
              [
                0,
                0
              ],
              [
                6.7,
                -9.8
              ],
              [
                -8.9,
                -7.9
              ],
              [
                -8.7,
                8.1
              ],
              [
                0,
                0
              ],
              [
                0,
                0
              ]
            ]
          }
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 67,
    ty: 3,
    parent: 64,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 64,
    ty: 3,
    parent: 10,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 10,
    ty: 3,
    parent: 2,
    ks: {
      p: {
        a: 0,
        k: [
          100,
          165
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 509,
    ty: 0,
    parent: 69,
    ks: {
      a: {
        a: 0,
        k: [
          -21,
          -6
        ]
      },
      p: {
        a: 0,
        k: [
          -21,
          -6
        ]
      }
    },
    w: 297,
    h: 256,
    ip: 0,
    op: 361,
    st: 0,
    refId: "507"
  },
  {
    ind: 69,
    ty: 3,
    parent: 2,
    ks: {
      p: {
        a: 0,
        k: [
          -21,
          -6
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 511,
    ty: 4,
    parent: 510,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            1,
            1,
            1
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 510,
    ty: 3,
    parent: 2,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  }
];
const meta$7 = {
  g: "https://jitter.video"
};
const op$7 = 360;
const v$7 = "5.7.4";
const w$7 = 255;
const CandidateInvitationsId = {
  assets: assets$7,
  fr: fr$7,
  h: h$7,
  ip: ip$7,
  layers: layers$7,
  meta: meta$7,
  op: op$7,
  v: v$7,
  w: w$7
};
const assets$6 = [
  {
    h: 3,
    id: "0",
    p: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGMAAAADCAYAAACHz7HPAAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAYSURBVHgB7cAxAQAAAMKg9U9tDQ8ogGsDBKcAAbNbxGsAAAAASUVORK5CYII=",
    u: "",
    w: 99,
    e: 1
  },
  {
    id: "7",
    layers: [
      {
        ind: 6,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 20
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "18",
    layers: [
      {
        ind: 15,
        ty: 4,
        parent: 14,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    2.4
                  ],
                  [
                    3.1,
                    0
                  ],
                  [
                    0.6,
                    -2.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    2,
                    -1.7
                  ]
                ],
                v: [
                  [
                    6.5,
                    9.7
                  ],
                  [
                    1,
                    14.4
                  ],
                  [
                    1,
                    16.6
                  ],
                  [
                    11.9,
                    16.6
                  ],
                  [
                    11.9,
                    14.4
                  ],
                  [
                    4.4,
                    14.4
                  ],
                  [
                    8.1,
                    11.3
                  ],
                  [
                    11.9,
                    5.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    1.2,
                    4.5
                  ],
                  [
                    3.4,
                    5.4
                  ],
                  [
                    6.5,
                    3
                  ],
                  [
                    9.3,
                    5.6
                  ],
                  [
                    6.5,
                    9.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.5,
                    -2.1
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -2.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 14,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              12.901,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 17,
        ty: 4,
        parent: 16,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 16,
        ty: 3,
        parent: 13,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 13,
        ty: 3,
        parent: 12,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 12,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              0.52,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "25",
    layers: [
      {
        ind: 24,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    3.8
                  ],
                  [
                    1.3,
                    5.7
                  ],
                  [
                    4.8,
                    3.6
                  ],
                  [
                    4.8,
                    16.6
                  ],
                  [
                    7.2,
                    16.6
                  ],
                  [
                    7.2,
                    1
                  ],
                  [
                    4.9,
                    1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "29",
    layers: [
      {
        ind: 28,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "32",
    layers: [
      {
        ind: 27,
        ty: 0,
        parent: 23,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 64.545907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 95.04,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 64.548,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 95.04,
                s: [
                  12.901,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  12.901,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 8,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "25"
      },
      {
        ind: 31,
        ty: 0,
        parent: 23,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 63.54,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 73.564806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 63.54,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 73.566,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "29"
      },
      {
        ind: 23,
        ty: 3,
        parent: 22,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 22,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              1.691,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 21,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "40",
    layers: [
      {
        ind: 39,
        ty: 4,
        parent: 38,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    4.5
                  ],
                  [
                    3.9,
                    0
                  ],
                  [
                    0,
                    -4.5
                  ],
                  [
                    -3.9,
                    0
                  ]
                ],
                v: [
                  [
                    7.6,
                    16.7
                  ],
                  [
                    14.2,
                    8.7
                  ],
                  [
                    7.6,
                    0.7
                  ],
                  [
                    1.1,
                    8.8
                  ],
                  [
                    7.6,
                    16.7
                  ]
                ],
                o: [
                  [
                    3.9,
                    0
                  ],
                  [
                    0,
                    -4.5
                  ],
                  [
                    -3.9,
                    0
                  ],
                  [
                    0,
                    4.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -2.5,
                    0
                  ],
                  [
                    0,
                    -3.1
                  ],
                  [
                    2.5,
                    0
                  ],
                  [
                    0,
                    3.2
                  ]
                ],
                v: [
                  [
                    3.6,
                    8.7
                  ],
                  [
                    7.6,
                    3
                  ],
                  [
                    11.6,
                    8.8
                  ],
                  [
                    7.6,
                    14.5
                  ],
                  [
                    3.6,
                    8.7
                  ]
                ],
                o: [
                  [
                    0,
                    -3.2
                  ],
                  [
                    2.5,
                    0
                  ],
                  [
                    0,
                    3.2
                  ],
                  [
                    -2.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 38,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -1,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "44",
    layers: [
      {
        ind: 43,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0.7,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0.7,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.4,
                    0
                  ],
                  [
                    0,
                    2.5
                  ]
                ],
                v: [
                  [
                    11.8,
                    12.2
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    9.1,
                    8.5
                  ],
                  [
                    11.5,
                    4.9
                  ],
                  [
                    6.2,
                    0.7
                  ],
                  [
                    1,
                    4.2
                  ],
                  [
                    3.2,
                    5.2
                  ],
                  [
                    6.2,
                    3
                  ],
                  [
                    9,
                    5.2
                  ],
                  [
                    6.2,
                    7.5
                  ],
                  [
                    4.7,
                    7.5
                  ],
                  [
                    4.7,
                    9.7
                  ],
                  [
                    6.3,
                    9.7
                  ],
                  [
                    9.3,
                    12.1
                  ],
                  [
                    6.3,
                    14.5
                  ],
                  [
                    2.9,
                    12.2
                  ],
                  [
                    0.8,
                    13.2
                  ],
                  [
                    6.3,
                    16.7
                  ],
                  [
                    11.8,
                    12.2
                  ]
                ],
                o: [
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    -0.6
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -2.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    2.6
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "47",
    layers: [
      {
        ind: 42,
        ty: 0,
        parent: 37,
        ks: {
          a: {
            a: 0,
            k: [
              -1,
              0
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 137.145907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 167.64,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 137.148,
                s: [
                  12.901,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 167.64,
                s: [
                  12.901,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  12.901,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 14,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "40"
      },
      {
        ind: 46,
        ty: 0,
        parent: 37,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 136.14,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 146.164806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 136.14,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 146.166,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "44"
      },
      {
        ind: 37,
        ty: 3,
        parent: 36,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 36,
        ty: 3,
        parent: 35,
        ks: {
          p: {
            a: 0,
            k: [
              -0.933,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 35,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "54",
    layers: [
      {
        ind: 53,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.9,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    4.4
                  ],
                  [
                    1.2,
                    1.2
                  ],
                  [
                    1.9,
                    0
                  ],
                  [
                    0,
                    -3.2
                  ],
                  [
                    -3,
                    0
                  ],
                  [
                    -0.9,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    3,
                    0
                  ]
                ],
                v: [
                  [
                    5.7,
                    14.6
                  ],
                  [
                    2.5,
                    13.3
                  ],
                  [
                    1.2,
                    15
                  ],
                  [
                    5.7,
                    16.8
                  ],
                  [
                    12.6,
                    8.2
                  ],
                  [
                    10.8,
                    2.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    0.9,
                    6.1
                  ],
                  [
                    6.1,
                    11.2
                  ],
                  [
                    10.2,
                    9.2
                  ],
                  [
                    10.2,
                    9.2
                  ],
                  [
                    5.7,
                    14.6
                  ]
                ],
                o: [
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    1.1
                  ],
                  [
                    4.9,
                    0
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -1,
                    -1
                  ],
                  [
                    -3.1,
                    0
                  ],
                  [
                    0,
                    3
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    2.6
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    -2
                  ],
                  [
                    1.7,
                    0
                  ],
                  [
                    0,
                    1.9
                  ]
                ],
                v: [
                  [
                    3.4,
                    6
                  ],
                  [
                    6.6,
                    3
                  ],
                  [
                    9.9,
                    6
                  ],
                  [
                    6.6,
                    9.1
                  ],
                  [
                    3.4,
                    6
                  ]
                ],
                o: [
                  [
                    0,
                    -1.9
                  ],
                  [
                    1.9,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    -1.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "58",
    layers: [
      {
        ind: 57,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    2.4
                  ],
                  [
                    3.1,
                    0
                  ],
                  [
                    0.6,
                    -2.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    2,
                    -1.7
                  ]
                ],
                v: [
                  [
                    6.5,
                    9.7
                  ],
                  [
                    1,
                    14.4
                  ],
                  [
                    1,
                    16.6
                  ],
                  [
                    11.9,
                    16.6
                  ],
                  [
                    11.9,
                    14.4
                  ],
                  [
                    4.4,
                    14.4
                  ],
                  [
                    8.1,
                    11.3
                  ],
                  [
                    11.9,
                    5.4
                  ],
                  [
                    6.6,
                    0.7
                  ],
                  [
                    1.2,
                    4.5
                  ],
                  [
                    3.4,
                    5.4
                  ],
                  [
                    6.5,
                    3
                  ],
                  [
                    9.3,
                    5.6
                  ],
                  [
                    6.5,
                    9.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.5,
                    -2.1
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -2.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    -1.5
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.18,
                0.22,
                0.286
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "61",
    layers: [
      {
        ind: 56,
        ty: 0,
        parent: 52,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 209.745907,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 240.24,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  13.101,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 209.748,
                s: [
                  13.101,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 240.24,
                s: [
                  13.101,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  13.101,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 13,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "54"
      },
      {
        ind: 60,
        ty: 0,
        parent: 52,
        ks: {
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 208.74,
                s: [
                  0
                ],
                i: {
                  x: 1,
                  y: 1
                },
                o: {
                  x: 0,
                  y: 0
                }
              },
              {
                t: 218.764806,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 360,
                s: [
                  100
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 208.74,
                s: [
                  0,
                  -8.763
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 218.766,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  0,
                  0
                ],
                h: 1
              }
            ]
          }
        },
        w: 12,
        h: 17,
        ip: 0,
        op: 361,
        st: 0,
        refId: "58"
      },
      {
        ind: 52,
        ty: 3,
        parent: 51,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 51,
        ty: 3,
        parent: 50,
        ks: {
          p: {
            a: 0,
            k: [
              -1.028,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 50,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              9
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "94",
    layers: [
      {
        ind: 93,
        ty: 2,
        parent: 92,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 92,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "110",
    layers: [
      {
        ind: 109,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                17.5,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                35,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.41,
                            -1.91
                          ],
                          [
                            -0.92,
                            0.09
                          ],
                          [
                            1.69,
                            -0.98
                          ],
                          [
                            -0.37,
                            -0.85
                          ],
                          [
                            1.45,
                            1.31
                          ],
                          [
                            0.69,
                            -0.62
                          ],
                          [
                            -0.79,
                            1.79
                          ],
                          [
                            0.8,
                            0.46
                          ],
                          [
                            -1.95,
                            -0.21
                          ],
                          [
                            -0.2,
                            0.91
                          ]
                        ],
                        o: [
                          [
                            0.41,
                            -1.91
                          ],
                          [
                            0.19,
                            0.91
                          ],
                          [
                            1.94,
                            -0.21
                          ],
                          [
                            -0.8,
                            0.46
                          ],
                          [
                            0.8,
                            1.79
                          ],
                          [
                            -0.69,
                            -0.62
                          ],
                          [
                            -1.45,
                            1.31
                          ],
                          [
                            0.38,
                            -0.85
                          ],
                          [
                            -1.7,
                            -0.98
                          ],
                          [
                            0.92,
                            0.09
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            11.5,
                            8.55
                          ],
                          [
                            15.05,
                            8.55
                          ],
                          [
                            17.01,
                            9.98
                          ],
                          [
                            18.1,
                            13.34
                          ],
                          [
                            17.35,
                            15.65
                          ],
                          [
                            14.49,
                            17.73
                          ],
                          [
                            12.06,
                            17.73
                          ],
                          [
                            9.2,
                            15.65
                          ],
                          [
                            8.45,
                            13.34
                          ],
                          [
                            9.54,
                            9.98
                          ],
                          [
                            11.5,
                            8.55
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.9,
                        0.01,
                        0.47,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "118",
    layers: [
      {
        ind: 117,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "121",
    layers: [
      {
        ind: 120,
        ty: 0,
        parent: 116,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "118"
      },
      {
        ind: 116,
        ty: 3,
        parent: 115,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 115,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              7.796,
              2.612
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "127",
    layers: [
      {
        ind: 123,
        ty: 0,
        parent: 114,
        ks: {
          a: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          },
          p: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          }
        },
        w: 39.4996,
        h: 39.7037,
        ip: 0,
        op: 361,
        st: 0,
        refId: "121"
      },
      {
        ind: 114,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.583,
              6.617
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 125,
        ty: 4,
        parent: 124,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.299,
                1.306
              ]
            },
            s: {
              a: 0,
              k: [
                2.599,
                2.612
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 124,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.024,
              45.102
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 126,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.333,
                26.469
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.666,
                52.938
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "132",
    layers: [
      {
        ind: 78,
        ty: 4,
        parent: 77,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.03
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    -0.91
                  ],
                  [
                    -0.95,
                    0
                  ],
                  [
                    -0.28,
                    0.51
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.39,
                    0
                  ],
                  [
                    0.01,
                    0.49
                  ]
                ],
                v: [
                  [
                    1,
                    4.56
                  ],
                  [
                    3.43,
                    4.56
                  ],
                  [
                    3.44,
                    4.34
                  ],
                  [
                    1.88,
                    2.68
                  ],
                  [
                    0.26,
                    4.34
                  ],
                  [
                    1.94,
                    6.01
                  ],
                  [
                    3.31,
                    5.24
                  ],
                  [
                    2.8,
                    4.89
                  ],
                  [
                    1.93,
                    5.39
                  ],
                  [
                    1,
                    4.56
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.01,
                    -0.08
                  ],
                  [
                    0,
                    -0.97
                  ],
                  [
                    -0.93,
                    0
                  ],
                  [
                    0,
                    0.95
                  ],
                  [
                    0.56,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.19,
                    0.35
                  ],
                  [
                    -0.47,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.44,
                    0
                  ],
                  [
                    -0.02,
                    -0.46
                  ]
                ],
                v: [
                  [
                    1.03,
                    4.02
                  ],
                  [
                    1.88,
                    3.29
                  ],
                  [
                    2.71,
                    4.02
                  ]
                ],
                o: [
                  [
                    0.07,
                    -0.44
                  ],
                  [
                    0.44,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 77,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              13.464,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 80,
        ty: 4,
        parent: 79,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.69,
                    0
                  ],
                  [
                    -0.13,
                    0.05
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.07,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.44,
                    4.8
                  ],
                  [
                    1.44,
                    3.39
                  ],
                  [
                    2.23,
                    3.39
                  ],
                  [
                    2.23,
                    2.75
                  ],
                  [
                    1.44,
                    2.75
                  ],
                  [
                    1.44,
                    1.76
                  ],
                  [
                    0.73,
                    1.76
                  ],
                  [
                    0.73,
                    2.75
                  ],
                  [
                    0.14,
                    2.75
                  ],
                  [
                    0.14,
                    3.39
                  ],
                  [
                    0.73,
                    3.39
                  ],
                  [
                    0.73,
                    4.92
                  ],
                  [
                    1.84,
                    5.99
                  ],
                  [
                    2.22,
                    5.92
                  ],
                  [
                    2.22,
                    5.31
                  ],
                  [
                    1.99,
                    5.34
                  ],
                  [
                    1.44,
                    4.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.71
                  ],
                  [
                    0.09,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.09,
                    0.02
                  ],
                  [
                    -0.35,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 79,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              11.022,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 82,
        ty: 4,
        parent: 81,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ]
                ],
                v: [
                  [
                    0.37,
                    1.71
                  ],
                  [
                    0.83,
                    2.17
                  ],
                  [
                    1.3,
                    1.71
                  ],
                  [
                    0.83,
                    1.23
                  ],
                  [
                    0.37,
                    1.71
                  ]
                ],
                o: [
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.48,
                    5.95
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    1.19,
                    2.75
                  ],
                  [
                    1.19,
                    5.95
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 81,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              9.354,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 84,
        ty: 4,
        parent: 83,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    2.75
                  ],
                  [
                    1.46,
                    5.95
                  ],
                  [
                    2.17,
                    5.95
                  ],
                  [
                    3.51,
                    2.75
                  ],
                  [
                    2.76,
                    2.75
                  ],
                  [
                    1.82,
                    5.12
                  ],
                  [
                    0.88,
                    2.75
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 83,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              5.73,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 86,
        ty: 4,
        parent: 85,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.82,
                    0
                  ],
                  [
                    0.31,
                    -0.41
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    -0.46
                  ]
                ],
                v: [
                  [
                    2.64,
                    4.15
                  ],
                  [
                    2.64,
                    5.95
                  ],
                  [
                    3.35,
                    5.95
                  ],
                  [
                    3.35,
                    3.97
                  ],
                  [
                    2.15,
                    2.68
                  ],
                  [
                    1.18,
                    3.18
                  ],
                  [
                    1.18,
                    2.75
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    0.48,
                    5.95
                  ],
                  [
                    1.19,
                    5.95
                  ],
                  [
                    1.19,
                    4.17
                  ],
                  [
                    1.93,
                    3.36
                  ],
                  [
                    2.64,
                    4.15
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    -0.25,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.49
                  ],
                  [
                    0.49,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 85,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              1.932,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 88,
        ty: 4,
        parent: 87,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    5.95
                  ],
                  [
                    1.34,
                    5.95
                  ],
                  [
                    1.34,
                    1.25
                  ],
                  [
                    0.6,
                    1.25
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 87,
        ty: 3,
        parent: 76,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 76,
        ty: 3,
        parent: 75,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 75,
        ty: 3,
        parent: 74,
        ks: {
          p: {
            a: 0,
            k: [
              2.072,
              1.141
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 74,
        ty: 3,
        parent: 73,
        ks: {
          p: {
            a: 0,
            k: [
              34.315,
              4.741
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 90,
        ty: 4,
        parent: 89,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 89,
        ty: 3,
        parent: 73,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 73,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 96,
        ty: 0,
        parent: 91,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "94"
      },
      {
        ind: 91,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 100,
        ty: 4,
        parent: 99,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 99,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 102,
        ty: 4,
        parent: 101,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 101,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 105,
        ty: 4,
        parent: 104,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 104,
        ty: 3,
        parent: 103,
        ks: {
          p: {
            a: 0,
            k: [
              14.813,
              4.266
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 112,
        ty: 0,
        parent: 108,
        ks: {},
        w: 35,
        h: 35,
        ip: 0,
        op: 361,
        st: 0,
        refId: "110"
      },
      {
        ind: 108,
        ty: 3,
        parent: 107,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 107,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              -10.395,
              -5.224
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 106,
        ty: 3,
        parent: 103,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 103,
        ty: 3,
        parent: 98,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              -0.173
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 98,
        ty: 3,
        parent: 97,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 97,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 130,
        ty: 4,
        td: 1,
        parent: 113,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                26.333,
                26.469
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                52.666,
                52.938
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 129,
        ty: 0,
        tt: 1,
        parent: 113,
        ks: {},
        w: 53,
        h: 53,
        ip: 0,
        op: 361,
        st: 0,
        refId: "127"
      },
      {
        ind: 113,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              10.649,
              22.519
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 131,
        ty: 4,
        parent: 72,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 72,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              6
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "136",
    layers: [
      {
        ind: 134,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              13,
              6
            ]
          },
          p: {
            a: 0,
            k: [
              58.6,
              51.6
            ]
          }
        },
        w: 191,
        h: 102,
        ip: 0,
        op: 361,
        st: 0,
        refId: "132"
      },
      {
        ind: 135,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                147.345,
                99.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                294.69,
                199.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "162",
    layers: [
      {
        ind: 161,
        ty: 2,
        parent: 160,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 160,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "178",
    layers: [
      {
        ind: 177,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "181",
    layers: [
      {
        ind: 180,
        ty: 0,
        parent: 176,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "178"
      },
      {
        ind: 176,
        ty: 3,
        parent: 175,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 175,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "187",
    layers: [
      {
        ind: 183,
        ty: 0,
        parent: 174,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "181"
      },
      {
        ind: 174,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 185,
        ty: 4,
        parent: 184,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 184,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 186,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "192",
    layers: [
      {
        ind: 146,
        ty: 4,
        parent: 145,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.05
                  ],
                  [
                    1.49,
                    0
                  ],
                  [
                    0,
                    -1.36
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    -0.41,
                    0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.59,
                    0
                  ],
                  [
                    0.02,
                    0.74
                  ]
                ],
                v: [
                  [
                    1.49,
                    6.84
                  ],
                  [
                    5.14,
                    6.84
                  ],
                  [
                    5.16,
                    6.5
                  ],
                  [
                    2.82,
                    4.02
                  ],
                  [
                    0.4,
                    6.51
                  ],
                  [
                    2.92,
                    9.01
                  ],
                  [
                    4.97,
                    7.85
                  ],
                  [
                    4.19,
                    7.34
                  ],
                  [
                    2.9,
                    8.09
                  ],
                  [
                    1.49,
                    6.84
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.02,
                    -0.12
                  ],
                  [
                    0,
                    -1.45
                  ],
                  [
                    -1.39,
                    0
                  ],
                  [
                    0,
                    1.43
                  ],
                  [
                    0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.28,
                    0.53
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    -0.04,
                    -0.68
                  ]
                ],
                v: [
                  [
                    1.55,
                    6.03
                  ],
                  [
                    2.82,
                    4.94
                  ],
                  [
                    4.07,
                    6.03
                  ]
                ],
                o: [
                  [
                    0.1,
                    -0.66
                  ],
                  [
                    0.67,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 145,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              20.196,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 148,
        ty: 4,
        parent: 147,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    -0.2,
                    0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.6
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.2
                  ],
                  [
                    2.2,
                    5.1
                  ],
                  [
                    3.3,
                    5.1
                  ],
                  [
                    3.3,
                    4.1
                  ],
                  [
                    2.2,
                    4.1
                  ],
                  [
                    2.2,
                    2.6
                  ],
                  [
                    1.1,
                    2.6
                  ],
                  [
                    1.1,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ],
                  [
                    0.2,
                    5.1
                  ],
                  [
                    1.1,
                    5.1
                  ],
                  [
                    1.1,
                    7.4
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    3.3,
                    8
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    2.2,
                    7.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 147,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              16.533,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 150,
        ty: 4,
        parent: 149,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ]
                ],
                v: [
                  [
                    0.5,
                    2.6
                  ],
                  [
                    1.3,
                    3.3
                  ],
                  [
                    2,
                    2.6
                  ],
                  [
                    1.3,
                    1.8
                  ],
                  [
                    0.5,
                    2.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.4
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.7,
                    8.9
                  ],
                  [
                    0.7,
                    4.1
                  ],
                  [
                    1.8,
                    4.1
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 149,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              14.031,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 152,
        ty: 4,
        parent: 151,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.2,
                    4.1
                  ],
                  [
                    2.2,
                    8.9
                  ],
                  [
                    3.3,
                    8.9
                  ],
                  [
                    5.3,
                    4.1
                  ],
                  [
                    4.1,
                    4.1
                  ],
                  [
                    2.7,
                    7.7
                  ],
                  [
                    1.3,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 151,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              8.595,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 154,
        ty: 4,
        parent: 153,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 153,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              2.898,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 156,
        ty: 4,
        parent: 155,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.9,
                    8.9
                  ],
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    1.9
                  ],
                  [
                    0.9,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 155,
        ty: 3,
        parent: 144,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 144,
        ty: 3,
        parent: 143,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 143,
        ty: 3,
        parent: 142,
        ks: {
          p: {
            a: 0,
            k: [
              2.517,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 142,
        ty: 3,
        parent: 141,
        ks: {
          p: {
            a: 0,
            k: [
              29.582,
              2.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 158,
        ty: 4,
        parent: 157,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 157,
        ty: 3,
        parent: 141,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 141,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 164,
        ty: 0,
        parent: 159,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "162"
      },
      {
        ind: 159,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 168,
        ty: 4,
        parent: 167,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 167,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 170,
        ty: 4,
        parent: 169,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 169,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 172,
        ty: 4,
        parent: 171,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.883,
                5.926
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.767,
                11.852
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 171,
        ty: 3,
        parent: 166,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 166,
        ty: 3,
        parent: 165,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 165,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 190,
        ty: 4,
        td: 1,
        parent: 173,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 189,
        ty: 0,
        tt: 1,
        parent: 173,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "187"
      },
      {
        ind: 173,
        ty: 3,
        parent: 140,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 191,
        ty: 4,
        parent: 140,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 140,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              1
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "196",
    layers: [
      {
        ind: 194,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              3,
              1
            ]
          },
          p: {
            a: 0,
            k: [
              48.6,
              46.6
            ]
          }
        },
        w: 181,
        h: 97,
        ip: 0,
        op: 361,
        st: 0,
        refId: "192"
      },
      {
        ind: 195,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                137.345,
                94.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                274.69,
                189.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "222",
    layers: [
      {
        ind: 221,
        ty: 2,
        parent: 220,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 220,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "238",
    layers: [
      {
        ind: 237,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "241",
    layers: [
      {
        ind: 240,
        ty: 0,
        parent: 236,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "238"
      },
      {
        ind: 236,
        ty: 3,
        parent: 235,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 235,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "247",
    layers: [
      {
        ind: 243,
        ty: 0,
        parent: 234,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "241"
      },
      {
        ind: 234,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 245,
        ty: 4,
        parent: 244,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 244,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 246,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "252",
    layers: [
      {
        ind: 206,
        ty: 4,
        parent: 205,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.1,
                    -1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    -0.3,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ]
                ],
                v: [
                  [
                    6.5,
                    10.8
                  ],
                  [
                    8.8,
                    8.6
                  ],
                  [
                    8.8,
                    3.4
                  ],
                  [
                    7.7,
                    3.4
                  ],
                  [
                    7.7,
                    6.3
                  ],
                  [
                    6.6,
                    7.5
                  ],
                  [
                    5.5,
                    6.3
                  ],
                  [
                    5.5,
                    5.6
                  ],
                  [
                    3,
                    3.3
                  ],
                  [
                    0.3,
                    5.5
                  ],
                  [
                    1.2,
                    5.9
                  ],
                  [
                    1.2,
                    7.6
                  ],
                  [
                    2.5,
                    9
                  ],
                  [
                    2.9,
                    9
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.3,
                    7.5
                  ],
                  [
                    2.3,
                    5.5
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.9,
                    4.3
                  ],
                  [
                    4.4,
                    5.7
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    6.3,
                    8.6
                  ],
                  [
                    7.7,
                    7.8
                  ],
                  [
                    7.7,
                    8.6
                  ],
                  [
                    6.5,
                    9.8
                  ],
                  [
                    5.1,
                    8.9
                  ],
                  [
                    4.2,
                    9.4
                  ],
                  [
                    6.5,
                    10.8
                  ]
                ],
                o: [
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.5
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 205,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              31.842,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 208,
        ty: 4,
        parent: 207,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.2
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.3
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.4
                  ]
                ],
                v: [
                  [
                    5,
                    6.9
                  ],
                  [
                    5,
                    6.4
                  ],
                  [
                    4.4,
                    5.1
                  ],
                  [
                    5.1,
                    3.8
                  ],
                  [
                    5.1,
                    3.2
                  ],
                  [
                    4,
                    3.2
                  ],
                  [
                    4,
                    3.8
                  ],
                  [
                    3.3,
                    4.6
                  ],
                  [
                    3.1,
                    4.6
                  ],
                  [
                    3.1,
                    5.5
                  ],
                  [
                    3.2,
                    5.5
                  ],
                  [
                    3.9,
                    6.4
                  ],
                  [
                    3.9,
                    6.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    1.7,
                    6.8
                  ],
                  [
                    1.7,
                    6.7
                  ],
                  [
                    2,
                    5.6
                  ],
                  [
                    2.5,
                    4.4
                  ],
                  [
                    1.4,
                    3.4
                  ],
                  [
                    0.6,
                    3.4
                  ],
                  [
                    0.6,
                    4.4
                  ],
                  [
                    1.1,
                    4.4
                  ],
                  [
                    1.4,
                    4.7
                  ],
                  [
                    0.6,
                    6.6
                  ],
                  [
                    0.6,
                    6.9
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    5,
                    6.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.4,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0.4,
                    -0.5
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    1.6
                  ],
                  [
                    0.6,
                    2.5
                  ],
                  [
                    5,
                    2.5
                  ],
                  [
                    5,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 207,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              26.145,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 210,
        ty: 4,
        parent: 209,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ]
                ],
                v: [
                  [
                    2.4,
                    9
                  ],
                  [
                    2.8,
                    8.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.6,
                    8
                  ],
                  [
                    2,
                    7.4
                  ],
                  [
                    2,
                    3.4
                  ],
                  [
                    1,
                    3.4
                  ],
                  [
                    1,
                    7.6
                  ],
                  [
                    2.4,
                    9
                  ]
                ],
                o: [
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 209,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              23.058,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 212,
        ty: 4,
        parent: 211,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ]
                ],
                v: [
                  [
                    3.1,
                    5.8
                  ],
                  [
                    1.8,
                    6.5
                  ],
                  [
                    1.8,
                    5.6
                  ],
                  [
                    3.1,
                    4.3
                  ],
                  [
                    4.5,
                    5.6
                  ],
                  [
                    4.5,
                    8.9
                  ],
                  [
                    5.6,
                    8.9
                  ],
                  [
                    5.6,
                    5.6
                  ],
                  [
                    3.1,
                    3.3
                  ],
                  [
                    0.7,
                    5.6
                  ],
                  [
                    0.7,
                    8.9
                  ],
                  [
                    1.8,
                    8.9
                  ],
                  [
                    1.8,
                    7.8
                  ],
                  [
                    3,
                    6.8
                  ],
                  [
                    3.4,
                    6.8
                  ],
                  [
                    3.4,
                    5.8
                  ],
                  [
                    3.1,
                    5.8
                  ]
                ],
                o: [
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.2,
                    -0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    10.5,
                    5.2
                  ],
                  [
                    10.5,
                    8.9
                  ],
                  [
                    9.4,
                    8.9
                  ],
                  [
                    9.4,
                    5.3
                  ],
                  [
                    8.4,
                    4.3
                  ],
                  [
                    7.3,
                    4.9
                  ],
                  [
                    6.4,
                    4.4
                  ],
                  [
                    8.5,
                    3.3
                  ],
                  [
                    10.5,
                    5.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.7
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ]
                ],
                v: [
                  [
                    3.7,
                    1.6
                  ],
                  [
                    5,
                    2.8
                  ],
                  [
                    6.2,
                    1.6
                  ],
                  [
                    5,
                    0.4
                  ],
                  [
                    3.7,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.7
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ]
                ],
                v: [
                  [
                    4.5,
                    1.6
                  ],
                  [
                    5,
                    1.1
                  ],
                  [
                    5.5,
                    1.6
                  ],
                  [
                    5,
                    2.2
                  ],
                  [
                    4.5,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 211,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              11.952,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 214,
        ty: 4,
        parent: 213,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.6,
                    8.9
                  ],
                  [
                    5.2,
                    6
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    2.3,
                    3.3
                  ],
                  [
                    2.3,
                    4.3
                  ],
                  [
                    2.6,
                    4.3
                  ],
                  [
                    4.1,
                    6
                  ],
                  [
                    2.6,
                    7.9
                  ],
                  [
                    2.5,
                    7.9
                  ],
                  [
                    1.3,
                    4.9
                  ],
                  [
                    0.2,
                    4.9
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    -1.8
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 213,
        ty: 3,
        parent: 204,
        ks: {
          p: {
            a: 0,
            k: [
              6.201,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 216,
        ty: 4,
        parent: 215,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.4,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0.3
                  ],
                  [
                    -0.5,
                    0
                  ]
                ],
                v: [
                  [
                    6.1,
                    4.3
                  ],
                  [
                    6.1,
                    3.3
                  ],
                  [
                    4.5,
                    3.8
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    0.5,
                    4.6
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.8,
                    4.3
                  ],
                  [
                    4.1,
                    5.5
                  ],
                  [
                    4.1,
                    5.8
                  ],
                  [
                    2.7,
                    5.8
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    2.3,
                    9
                  ],
                  [
                    2.9,
                    8.9
                  ],
                  [
                    2.9,
                    8.1
                  ],
                  [
                    2.6,
                    8.1
                  ],
                  [
                    1.6,
                    7.4
                  ],
                  [
                    2.8,
                    6.6
                  ],
                  [
                    4.1,
                    6.6
                  ],
                  [
                    4.1,
                    8.9
                  ],
                  [
                    5.2,
                    8.9
                  ],
                  [
                    5.2,
                    5.4
                  ],
                  [
                    5,
                    4.5
                  ],
                  [
                    6.1,
                    4.3
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    -0.4,
                    -0.3
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.2,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.5,
                    0.6
                  ],
                  [
                    4.5,
                    2.5
                  ],
                  [
                    5.5,
                    2.5
                  ],
                  [
                    5.5,
                    0.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 48,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 59.562,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 72.6,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 215,
        ty: 3,
        parent: 204,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 204,
        ty: 3,
        parent: 203,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 203,
        ty: 3,
        parent: 202,
        ks: {
          p: {
            a: 0,
            k: [
              -5.313,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 202,
        ty: 3,
        parent: 201,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 52.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.986,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 62.382,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 65.778,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 67.476,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 69.174,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 70.026,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 70.878,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 71.304,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.736,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.952,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.168,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 218,
        ty: 4,
        parent: 217,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 59.562,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 72.6,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 217,
        ty: 3,
        parent: 201,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 52.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.968,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 62.352,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 65.736,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 67.428,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 69.126,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 69.978,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 70.824,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 71.25,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.676,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 71.892,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.108,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 201,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 224,
        ty: 0,
        parent: 219,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "222"
      },
      {
        ind: 219,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 228,
        ty: 4,
        parent: 227,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 227,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 230,
        ty: 4,
        parent: 229,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 229,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 232,
        ty: 4,
        parent: 231,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 231,
        ty: 3,
        parent: 226,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 226,
        ty: 3,
        parent: 225,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 225,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 250,
        ty: 4,
        td: 1,
        parent: 233,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 249,
        ty: 0,
        tt: 1,
        parent: 233,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "247"
      },
      {
        ind: 233,
        ty: 3,
        parent: 200,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 251,
        ty: 4,
        parent: 200,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 200,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "256",
    layers: [
      {
        ind: 254,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "252"
      },
      {
        ind: 255,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "282",
    layers: [
      {
        ind: 281,
        ty: 2,
        parent: 280,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 280,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "298",
    layers: [
      {
        ind: 297,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "301",
    layers: [
      {
        ind: 300,
        ty: 0,
        parent: 296,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "298"
      },
      {
        ind: 296,
        ty: 3,
        parent: 295,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 295,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "307",
    layers: [
      {
        ind: 303,
        ty: 0,
        parent: 294,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "301"
      },
      {
        ind: 294,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 305,
        ty: 4,
        parent: 304,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 304,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 306,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "312",
    layers: [
      {
        ind: 266,
        ty: 4,
        parent: 265,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.1,
                    -1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    -0.3,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ]
                ],
                v: [
                  [
                    6.5,
                    10.8
                  ],
                  [
                    8.8,
                    8.6
                  ],
                  [
                    8.8,
                    3.4
                  ],
                  [
                    7.7,
                    3.4
                  ],
                  [
                    7.7,
                    6.3
                  ],
                  [
                    6.6,
                    7.5
                  ],
                  [
                    5.5,
                    6.3
                  ],
                  [
                    5.5,
                    5.6
                  ],
                  [
                    3,
                    3.3
                  ],
                  [
                    0.3,
                    5.5
                  ],
                  [
                    1.2,
                    5.9
                  ],
                  [
                    1.2,
                    7.6
                  ],
                  [
                    2.5,
                    9
                  ],
                  [
                    2.9,
                    9
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.3,
                    7.5
                  ],
                  [
                    2.3,
                    5.5
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.9,
                    4.3
                  ],
                  [
                    4.4,
                    5.7
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    6.3,
                    8.6
                  ],
                  [
                    7.7,
                    7.8
                  ],
                  [
                    7.7,
                    8.6
                  ],
                  [
                    6.5,
                    9.8
                  ],
                  [
                    5.1,
                    8.9
                  ],
                  [
                    4.2,
                    9.4
                  ],
                  [
                    6.5,
                    10.8
                  ]
                ],
                o: [
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.5
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 265,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              31.842,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 268,
        ty: 4,
        parent: 267,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.2
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.3
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.4
                  ]
                ],
                v: [
                  [
                    5,
                    6.9
                  ],
                  [
                    5,
                    6.4
                  ],
                  [
                    4.4,
                    5.1
                  ],
                  [
                    5.1,
                    3.8
                  ],
                  [
                    5.1,
                    3.2
                  ],
                  [
                    4,
                    3.2
                  ],
                  [
                    4,
                    3.8
                  ],
                  [
                    3.3,
                    4.6
                  ],
                  [
                    3.1,
                    4.6
                  ],
                  [
                    3.1,
                    5.5
                  ],
                  [
                    3.2,
                    5.5
                  ],
                  [
                    3.9,
                    6.4
                  ],
                  [
                    3.9,
                    6.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    1.7,
                    6.8
                  ],
                  [
                    1.7,
                    6.7
                  ],
                  [
                    2,
                    5.6
                  ],
                  [
                    2.5,
                    4.4
                  ],
                  [
                    1.4,
                    3.4
                  ],
                  [
                    0.6,
                    3.4
                  ],
                  [
                    0.6,
                    4.4
                  ],
                  [
                    1.1,
                    4.4
                  ],
                  [
                    1.4,
                    4.7
                  ],
                  [
                    0.6,
                    6.6
                  ],
                  [
                    0.6,
                    6.9
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    5,
                    6.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.4,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0.4,
                    -0.5
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    1.6
                  ],
                  [
                    0.6,
                    2.5
                  ],
                  [
                    5,
                    2.5
                  ],
                  [
                    5,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 267,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              26.145,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 270,
        ty: 4,
        parent: 269,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ]
                ],
                v: [
                  [
                    2.4,
                    9
                  ],
                  [
                    2.8,
                    8.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.6,
                    8
                  ],
                  [
                    2,
                    7.4
                  ],
                  [
                    2,
                    3.4
                  ],
                  [
                    1,
                    3.4
                  ],
                  [
                    1,
                    7.6
                  ],
                  [
                    2.4,
                    9
                  ]
                ],
                o: [
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 269,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              23.058,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 272,
        ty: 4,
        parent: 271,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ]
                ],
                v: [
                  [
                    3.1,
                    5.8
                  ],
                  [
                    1.8,
                    6.5
                  ],
                  [
                    1.8,
                    5.6
                  ],
                  [
                    3.1,
                    4.3
                  ],
                  [
                    4.5,
                    5.6
                  ],
                  [
                    4.5,
                    8.9
                  ],
                  [
                    5.6,
                    8.9
                  ],
                  [
                    5.6,
                    5.6
                  ],
                  [
                    3.1,
                    3.3
                  ],
                  [
                    0.7,
                    5.6
                  ],
                  [
                    0.7,
                    8.9
                  ],
                  [
                    1.8,
                    8.9
                  ],
                  [
                    1.8,
                    7.8
                  ],
                  [
                    3,
                    6.8
                  ],
                  [
                    3.4,
                    6.8
                  ],
                  [
                    3.4,
                    5.8
                  ],
                  [
                    3.1,
                    5.8
                  ]
                ],
                o: [
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.2,
                    -0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    10.5,
                    5.2
                  ],
                  [
                    10.5,
                    8.9
                  ],
                  [
                    9.4,
                    8.9
                  ],
                  [
                    9.4,
                    5.3
                  ],
                  [
                    8.4,
                    4.3
                  ],
                  [
                    7.3,
                    4.9
                  ],
                  [
                    6.4,
                    4.4
                  ],
                  [
                    8.5,
                    3.3
                  ],
                  [
                    10.5,
                    5.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.7
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ]
                ],
                v: [
                  [
                    3.7,
                    1.6
                  ],
                  [
                    5,
                    2.8
                  ],
                  [
                    6.2,
                    1.6
                  ],
                  [
                    5,
                    0.4
                  ],
                  [
                    3.7,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.7
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ]
                ],
                v: [
                  [
                    4.5,
                    1.6
                  ],
                  [
                    5,
                    1.1
                  ],
                  [
                    5.5,
                    1.6
                  ],
                  [
                    5,
                    2.2
                  ],
                  [
                    4.5,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 271,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              11.952,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 274,
        ty: 4,
        parent: 273,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.6,
                    8.9
                  ],
                  [
                    5.2,
                    6
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    2.3,
                    3.3
                  ],
                  [
                    2.3,
                    4.3
                  ],
                  [
                    2.6,
                    4.3
                  ],
                  [
                    4.1,
                    6
                  ],
                  [
                    2.6,
                    7.9
                  ],
                  [
                    2.5,
                    7.9
                  ],
                  [
                    1.3,
                    4.9
                  ],
                  [
                    0.2,
                    4.9
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    -1.8
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 273,
        ty: 3,
        parent: 264,
        ks: {
          p: {
            a: 0,
            k: [
              6.201,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 276,
        ty: 4,
        parent: 275,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.4,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0.3
                  ],
                  [
                    -0.5,
                    0
                  ]
                ],
                v: [
                  [
                    6.1,
                    4.3
                  ],
                  [
                    6.1,
                    3.3
                  ],
                  [
                    4.5,
                    3.8
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    0.5,
                    4.6
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.8,
                    4.3
                  ],
                  [
                    4.1,
                    5.5
                  ],
                  [
                    4.1,
                    5.8
                  ],
                  [
                    2.7,
                    5.8
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    2.3,
                    9
                  ],
                  [
                    2.9,
                    8.9
                  ],
                  [
                    2.9,
                    8.1
                  ],
                  [
                    2.6,
                    8.1
                  ],
                  [
                    1.6,
                    7.4
                  ],
                  [
                    2.8,
                    6.6
                  ],
                  [
                    4.1,
                    6.6
                  ],
                  [
                    4.1,
                    8.9
                  ],
                  [
                    5.2,
                    8.9
                  ],
                  [
                    5.2,
                    5.4
                  ],
                  [
                    5,
                    4.5
                  ],
                  [
                    6.1,
                    4.3
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    -0.4,
                    -0.3
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.2,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.5,
                    0.6
                  ],
                  [
                    4.5,
                    2.5
                  ],
                  [
                    5.5,
                    2.5
                  ],
                  [
                    5.5,
                    0.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 120.6,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 132.162,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 145.2,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 275,
        ty: 3,
        parent: 264,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 264,
        ty: 3,
        parent: 263,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 263,
        ty: 3,
        parent: 262,
        ks: {
          p: {
            a: 0,
            k: [
              -5.313,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 262,
        ty: 3,
        parent: 261,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 124.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 131.586,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 134.982,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 138.378,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 140.076,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 141.774,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 142.626,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 143.478,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 143.904,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.336,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.552,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.768,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 278,
        ty: 4,
        parent: 277,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 132.162,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 145.2,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 277,
        ty: 3,
        parent: 261,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 124.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 131.568,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 134.952,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 138.336,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 140.028,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 141.726,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 142.578,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 143.424,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 143.85,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.276,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.492,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 144.708,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 261,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 284,
        ty: 0,
        parent: 279,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "282"
      },
      {
        ind: 279,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 288,
        ty: 4,
        parent: 287,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 287,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 290,
        ty: 4,
        parent: 289,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 289,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 292,
        ty: 4,
        parent: 291,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 291,
        ty: 3,
        parent: 286,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 286,
        ty: 3,
        parent: 285,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 285,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 310,
        ty: 4,
        td: 1,
        parent: 293,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 309,
        ty: 0,
        tt: 1,
        parent: 293,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "307"
      },
      {
        ind: 293,
        ty: 3,
        parent: 260,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 311,
        ty: 4,
        parent: 260,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 260,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "316",
    layers: [
      {
        ind: 314,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "312"
      },
      {
        ind: 315,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "342",
    layers: [
      {
        ind: 341,
        ty: 2,
        parent: 340,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 340,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "358",
    layers: [
      {
        ind: 357,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "361",
    layers: [
      {
        ind: 360,
        ty: 0,
        parent: 356,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "358"
      },
      {
        ind: 356,
        ty: 3,
        parent: 355,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 355,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "367",
    layers: [
      {
        ind: 363,
        ty: 0,
        parent: 354,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "361"
      },
      {
        ind: 354,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 365,
        ty: 4,
        parent: 364,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 364,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 366,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "372",
    layers: [
      {
        ind: 326,
        ty: 4,
        parent: 325,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.1,
                    -1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    -0.3,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ]
                ],
                v: [
                  [
                    6.5,
                    10.8
                  ],
                  [
                    8.8,
                    8.6
                  ],
                  [
                    8.8,
                    3.4
                  ],
                  [
                    7.7,
                    3.4
                  ],
                  [
                    7.7,
                    6.3
                  ],
                  [
                    6.6,
                    7.5
                  ],
                  [
                    5.5,
                    6.3
                  ],
                  [
                    5.5,
                    5.6
                  ],
                  [
                    3,
                    3.3
                  ],
                  [
                    0.3,
                    5.5
                  ],
                  [
                    1.2,
                    5.9
                  ],
                  [
                    1.2,
                    7.6
                  ],
                  [
                    2.5,
                    9
                  ],
                  [
                    2.9,
                    9
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.3,
                    7.5
                  ],
                  [
                    2.3,
                    5.5
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.9,
                    4.3
                  ],
                  [
                    4.4,
                    5.7
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    6.3,
                    8.6
                  ],
                  [
                    7.7,
                    7.8
                  ],
                  [
                    7.7,
                    8.6
                  ],
                  [
                    6.5,
                    9.8
                  ],
                  [
                    5.1,
                    8.9
                  ],
                  [
                    4.2,
                    9.4
                  ],
                  [
                    6.5,
                    10.8
                  ]
                ],
                o: [
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.5
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 325,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              31.842,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 328,
        ty: 4,
        parent: 327,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.2
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.3
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.4
                  ]
                ],
                v: [
                  [
                    5,
                    6.9
                  ],
                  [
                    5,
                    6.4
                  ],
                  [
                    4.4,
                    5.1
                  ],
                  [
                    5.1,
                    3.8
                  ],
                  [
                    5.1,
                    3.2
                  ],
                  [
                    4,
                    3.2
                  ],
                  [
                    4,
                    3.8
                  ],
                  [
                    3.3,
                    4.6
                  ],
                  [
                    3.1,
                    4.6
                  ],
                  [
                    3.1,
                    5.5
                  ],
                  [
                    3.2,
                    5.5
                  ],
                  [
                    3.9,
                    6.4
                  ],
                  [
                    3.9,
                    6.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    1.7,
                    6.8
                  ],
                  [
                    1.7,
                    6.7
                  ],
                  [
                    2,
                    5.6
                  ],
                  [
                    2.5,
                    4.4
                  ],
                  [
                    1.4,
                    3.4
                  ],
                  [
                    0.6,
                    3.4
                  ],
                  [
                    0.6,
                    4.4
                  ],
                  [
                    1.1,
                    4.4
                  ],
                  [
                    1.4,
                    4.7
                  ],
                  [
                    0.6,
                    6.6
                  ],
                  [
                    0.6,
                    6.9
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    5,
                    6.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.4,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0.4,
                    -0.5
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    1.6
                  ],
                  [
                    0.6,
                    2.5
                  ],
                  [
                    5,
                    2.5
                  ],
                  [
                    5,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 327,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              26.145,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 330,
        ty: 4,
        parent: 329,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ]
                ],
                v: [
                  [
                    2.4,
                    9
                  ],
                  [
                    2.8,
                    8.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.6,
                    8
                  ],
                  [
                    2,
                    7.4
                  ],
                  [
                    2,
                    3.4
                  ],
                  [
                    1,
                    3.4
                  ],
                  [
                    1,
                    7.6
                  ],
                  [
                    2.4,
                    9
                  ]
                ],
                o: [
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 329,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              23.058,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 332,
        ty: 4,
        parent: 331,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ]
                ],
                v: [
                  [
                    3.1,
                    5.8
                  ],
                  [
                    1.8,
                    6.5
                  ],
                  [
                    1.8,
                    5.6
                  ],
                  [
                    3.1,
                    4.3
                  ],
                  [
                    4.5,
                    5.6
                  ],
                  [
                    4.5,
                    8.9
                  ],
                  [
                    5.6,
                    8.9
                  ],
                  [
                    5.6,
                    5.6
                  ],
                  [
                    3.1,
                    3.3
                  ],
                  [
                    0.7,
                    5.6
                  ],
                  [
                    0.7,
                    8.9
                  ],
                  [
                    1.8,
                    8.9
                  ],
                  [
                    1.8,
                    7.8
                  ],
                  [
                    3,
                    6.8
                  ],
                  [
                    3.4,
                    6.8
                  ],
                  [
                    3.4,
                    5.8
                  ],
                  [
                    3.1,
                    5.8
                  ]
                ],
                o: [
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.2,
                    -0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    10.5,
                    5.2
                  ],
                  [
                    10.5,
                    8.9
                  ],
                  [
                    9.4,
                    8.9
                  ],
                  [
                    9.4,
                    5.3
                  ],
                  [
                    8.4,
                    4.3
                  ],
                  [
                    7.3,
                    4.9
                  ],
                  [
                    6.4,
                    4.4
                  ],
                  [
                    8.5,
                    3.3
                  ],
                  [
                    10.5,
                    5.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.7
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ]
                ],
                v: [
                  [
                    3.7,
                    1.6
                  ],
                  [
                    5,
                    2.8
                  ],
                  [
                    6.2,
                    1.6
                  ],
                  [
                    5,
                    0.4
                  ],
                  [
                    3.7,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.7
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ]
                ],
                v: [
                  [
                    4.5,
                    1.6
                  ],
                  [
                    5,
                    1.1
                  ],
                  [
                    5.5,
                    1.6
                  ],
                  [
                    5,
                    2.2
                  ],
                  [
                    4.5,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 331,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              11.952,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 334,
        ty: 4,
        parent: 333,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.6,
                    8.9
                  ],
                  [
                    5.2,
                    6
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    2.3,
                    3.3
                  ],
                  [
                    2.3,
                    4.3
                  ],
                  [
                    2.6,
                    4.3
                  ],
                  [
                    4.1,
                    6
                  ],
                  [
                    2.6,
                    7.9
                  ],
                  [
                    2.5,
                    7.9
                  ],
                  [
                    1.3,
                    4.9
                  ],
                  [
                    0.2,
                    4.9
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    -1.8
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 333,
        ty: 3,
        parent: 324,
        ks: {
          p: {
            a: 0,
            k: [
              6.201,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 336,
        ty: 4,
        parent: 335,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.4,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0.3
                  ],
                  [
                    -0.5,
                    0
                  ]
                ],
                v: [
                  [
                    6.1,
                    4.3
                  ],
                  [
                    6.1,
                    3.3
                  ],
                  [
                    4.5,
                    3.8
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    0.5,
                    4.6
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.8,
                    4.3
                  ],
                  [
                    4.1,
                    5.5
                  ],
                  [
                    4.1,
                    5.8
                  ],
                  [
                    2.7,
                    5.8
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    2.3,
                    9
                  ],
                  [
                    2.9,
                    8.9
                  ],
                  [
                    2.9,
                    8.1
                  ],
                  [
                    2.6,
                    8.1
                  ],
                  [
                    1.6,
                    7.4
                  ],
                  [
                    2.8,
                    6.6
                  ],
                  [
                    4.1,
                    6.6
                  ],
                  [
                    4.1,
                    8.9
                  ],
                  [
                    5.2,
                    8.9
                  ],
                  [
                    5.2,
                    5.4
                  ],
                  [
                    5,
                    4.5
                  ],
                  [
                    6.1,
                    4.3
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    -0.4,
                    -0.3
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.2,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.5,
                    0.6
                  ],
                  [
                    4.5,
                    2.5
                  ],
                  [
                    5.5,
                    2.5
                  ],
                  [
                    5.5,
                    0.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  h: 1
                },
                {
                  t: 193.2,
                  s: [
                    0.118,
                    0.278,
                    0.663
                  ],
                  i: {
                    x: 0.75,
                    y: 1
                  },
                  o: {
                    x: 0.5,
                    y: 0
                  }
                },
                {
                  t: 205.1286,
                  s: [
                    0,
                    0,
                    0.325
                  ],
                  i: {
                    x: 0,
                    y: 1
                  },
                  o: {
                    x: 0.25,
                    y: 0
                  }
                },
                {
                  t: 218.58,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                },
                {
                  t: 360,
                  s: [
                    1,
                    1,
                    1
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 335,
        ty: 3,
        parent: 324,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 324,
        ty: 3,
        parent: 323,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 323,
        ty: 3,
        parent: 322,
        ks: {
          p: {
            a: 0,
            k: [
              -5.313,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 322,
        ty: 3,
        parent: 321,
        ks: {
          a: {
            a: 0,
            k: [
              15.382,
              6.519
            ]
          },
          p: {
            a: 0,
            k: [
              44.964,
              9.481
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 200.1,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.25,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 209.322,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 212.4,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 213.936,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 215.478,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 216.252,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 217.02,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 217.41,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.794,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.992,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 218.19,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 338,
        ty: 4,
        parent: 337,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        1,
                        1,
                        1
                      ],
                      h: 1
                    },
                    {
                      t: 205.1286,
                      s: [
                        1,
                        1,
                        1
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.25,
                        y: 0
                      }
                    },
                    {
                      t: 218.58,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 360,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    }
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 337,
        ty: 3,
        parent: 321,
        ks: {
          a: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          p: {
            a: 0,
            k: [
              44.373,
              10.074
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 200.1,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.741,
                    0.741
                  ],
                  y: [
                    0.482,
                    0.482
                  ]
                },
                o: {
                  x: [
                    0.4,
                    0.4
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.25,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.679,
                    0.679
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.347,
                    0.347
                  ],
                  y: [
                    0.693,
                    0.693
                  ]
                }
              },
              {
                t: 209.322,
                s: [
                  114.99,
                  114.99
                ],
                i: {
                  x: [
                    0.733,
                    0.733
                  ],
                  y: [
                    0.466,
                    0.466
                  ]
                },
                o: {
                  x: [
                    0.394,
                    0.394
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 212.4,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.608,
                    0.608
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.27,
                    0.27
                  ],
                  y: [
                    0.539,
                    0.539
                  ]
                }
              },
              {
                t: 213.936,
                s: [
                  118.75,
                  118.75
                ],
                i: {
                  x: [
                    0.564,
                    0.564
                  ],
                  y: [
                    0.129,
                    0.129
                  ]
                },
                o: {
                  x: [
                    0.214,
                    0.214
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 215.478,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.583,
                    0.583
                  ],
                  y: [
                    0.998,
                    0.998
                  ]
                },
                o: {
                  x: [
                    0.236,
                    0.236
                  ],
                  y: [
                    0.473,
                    0.473
                  ]
                }
              },
              {
                t: 216.252,
                s: [
                  119.68,
                  119.68
                ],
                i: {
                  x: [
                    0.533,
                    0.533
                  ],
                  y: [
                    0.07,
                    0.07
                  ]
                },
                o: {
                  x: [
                    0.17,
                    0.17
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 217.02,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    0.971,
                    0.971
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.539,
                    0.539
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 217.41,
                s: [
                  119.92,
                  119.92
                ],
                i: {
                  x: [
                    0.454,
                    0.454
                  ],
                  y: [
                    0,
                    0
                  ]
                },
                o: {
                  x: [
                    0.011,
                    0.011
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.794,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.992,
                s: [
                  119.98,
                  119.98
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 218.19,
                s: [
                  120,
                  120
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  120,
                  120
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 321,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 344,
        ty: 0,
        parent: 339,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "342"
      },
      {
        ind: 339,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 348,
        ty: 4,
        parent: 347,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 347,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 350,
        ty: 4,
        parent: 349,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 349,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 352,
        ty: 4,
        parent: 351,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 351,
        ty: 3,
        parent: 346,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 346,
        ty: 3,
        parent: 345,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 345,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 370,
        ty: 4,
        td: 1,
        parent: 353,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 369,
        ty: 0,
        tt: 1,
        parent: 353,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "367"
      },
      {
        ind: 353,
        ty: 3,
        parent: 320,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 371,
        ty: 4,
        parent: 320,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 320,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              10,
              3
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "376",
    layers: [
      {
        ind: 374,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              10,
              3
            ]
          },
          p: {
            a: 0,
            k: [
              55.6,
              48.6
            ]
          }
        },
        w: 188,
        h: 99,
        ip: 0,
        op: 361,
        st: 0,
        refId: "372"
      },
      {
        ind: 375,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                144.345,
                96.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                288.69,
                193.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "402",
    layers: [
      {
        ind: 401,
        ty: 2,
        parent: 400,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 400,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "418",
    layers: [
      {
        ind: 417,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "421",
    layers: [
      {
        ind: 420,
        ty: 0,
        parent: 416,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "418"
      },
      {
        ind: 416,
        ty: 3,
        parent: 415,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 415,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.232,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "427",
    layers: [
      {
        ind: 423,
        ty: 0,
        parent: 414,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "421"
      },
      {
        ind: 414,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 425,
        ty: 4,
        parent: 424,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 424,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 426,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "432",
    layers: [
      {
        ind: 386,
        ty: 4,
        parent: 385,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.1,
                    -1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    -0.3,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ]
                ],
                v: [
                  [
                    6.5,
                    10.8
                  ],
                  [
                    8.8,
                    8.6
                  ],
                  [
                    8.8,
                    3.4
                  ],
                  [
                    7.7,
                    3.4
                  ],
                  [
                    7.7,
                    6.3
                  ],
                  [
                    6.6,
                    7.5
                  ],
                  [
                    5.5,
                    6.3
                  ],
                  [
                    5.5,
                    5.6
                  ],
                  [
                    3,
                    3.3
                  ],
                  [
                    0.3,
                    5.5
                  ],
                  [
                    1.2,
                    5.9
                  ],
                  [
                    1.2,
                    7.6
                  ],
                  [
                    2.5,
                    9
                  ],
                  [
                    2.9,
                    9
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.3,
                    7.5
                  ],
                  [
                    2.3,
                    5.5
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.9,
                    4.3
                  ],
                  [
                    4.4,
                    5.7
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    6.3,
                    8.6
                  ],
                  [
                    7.7,
                    7.8
                  ],
                  [
                    7.7,
                    8.6
                  ],
                  [
                    6.5,
                    9.8
                  ],
                  [
                    5.1,
                    8.9
                  ],
                  [
                    4.2,
                    9.4
                  ],
                  [
                    6.5,
                    10.8
                  ]
                ],
                o: [
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.5
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 385,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              31.842,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 388,
        ty: 4,
        parent: 387,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.2
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.3
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.4
                  ]
                ],
                v: [
                  [
                    5,
                    6.9
                  ],
                  [
                    5,
                    6.4
                  ],
                  [
                    4.4,
                    5.1
                  ],
                  [
                    5.1,
                    3.8
                  ],
                  [
                    5.1,
                    3.2
                  ],
                  [
                    4,
                    3.2
                  ],
                  [
                    4,
                    3.8
                  ],
                  [
                    3.3,
                    4.6
                  ],
                  [
                    3.1,
                    4.6
                  ],
                  [
                    3.1,
                    5.5
                  ],
                  [
                    3.2,
                    5.5
                  ],
                  [
                    3.9,
                    6.4
                  ],
                  [
                    3.9,
                    6.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    1.7,
                    6.8
                  ],
                  [
                    1.7,
                    6.7
                  ],
                  [
                    2,
                    5.6
                  ],
                  [
                    2.5,
                    4.4
                  ],
                  [
                    1.4,
                    3.4
                  ],
                  [
                    0.6,
                    3.4
                  ],
                  [
                    0.6,
                    4.4
                  ],
                  [
                    1.1,
                    4.4
                  ],
                  [
                    1.4,
                    4.7
                  ],
                  [
                    0.6,
                    6.6
                  ],
                  [
                    0.6,
                    6.9
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    5,
                    6.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.4,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0.4,
                    -0.5
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    1.6
                  ],
                  [
                    0.6,
                    2.5
                  ],
                  [
                    5,
                    2.5
                  ],
                  [
                    5,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 387,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              26.145,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 390,
        ty: 4,
        parent: 389,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ]
                ],
                v: [
                  [
                    2.4,
                    9
                  ],
                  [
                    2.8,
                    8.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.6,
                    8
                  ],
                  [
                    2,
                    7.4
                  ],
                  [
                    2,
                    3.4
                  ],
                  [
                    1,
                    3.4
                  ],
                  [
                    1,
                    7.6
                  ],
                  [
                    2.4,
                    9
                  ]
                ],
                o: [
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 389,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              23.058,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 392,
        ty: 4,
        parent: 391,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ]
                ],
                v: [
                  [
                    3.1,
                    5.8
                  ],
                  [
                    1.8,
                    6.5
                  ],
                  [
                    1.8,
                    5.6
                  ],
                  [
                    3.1,
                    4.3
                  ],
                  [
                    4.5,
                    5.6
                  ],
                  [
                    4.5,
                    8.9
                  ],
                  [
                    5.6,
                    8.9
                  ],
                  [
                    5.6,
                    5.6
                  ],
                  [
                    3.1,
                    3.3
                  ],
                  [
                    0.7,
                    5.6
                  ],
                  [
                    0.7,
                    8.9
                  ],
                  [
                    1.8,
                    8.9
                  ],
                  [
                    1.8,
                    7.8
                  ],
                  [
                    3,
                    6.8
                  ],
                  [
                    3.4,
                    6.8
                  ],
                  [
                    3.4,
                    5.8
                  ],
                  [
                    3.1,
                    5.8
                  ]
                ],
                o: [
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.2,
                    -0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    10.5,
                    5.2
                  ],
                  [
                    10.5,
                    8.9
                  ],
                  [
                    9.4,
                    8.9
                  ],
                  [
                    9.4,
                    5.3
                  ],
                  [
                    8.4,
                    4.3
                  ],
                  [
                    7.3,
                    4.9
                  ],
                  [
                    6.4,
                    4.4
                  ],
                  [
                    8.5,
                    3.3
                  ],
                  [
                    10.5,
                    5.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.7
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ]
                ],
                v: [
                  [
                    3.7,
                    1.6
                  ],
                  [
                    5,
                    2.8
                  ],
                  [
                    6.2,
                    1.6
                  ],
                  [
                    5,
                    0.4
                  ],
                  [
                    3.7,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    0.7
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ]
                ],
                v: [
                  [
                    4.5,
                    1.6
                  ],
                  [
                    5,
                    1.1
                  ],
                  [
                    5.5,
                    1.6
                  ],
                  [
                    5,
                    2.2
                  ],
                  [
                    4.5,
                    1.6
                  ]
                ],
                o: [
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 391,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              11.952,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 394,
        ty: 4,
        parent: 393,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.6,
                    8.9
                  ],
                  [
                    5.2,
                    6
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    2.3,
                    3.3
                  ],
                  [
                    2.3,
                    4.3
                  ],
                  [
                    2.6,
                    4.3
                  ],
                  [
                    4.1,
                    6
                  ],
                  [
                    2.6,
                    7.9
                  ],
                  [
                    2.5,
                    7.9
                  ],
                  [
                    1.3,
                    4.9
                  ],
                  [
                    0.2,
                    4.9
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    -1.8
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 393,
        ty: 3,
        parent: 384,
        ks: {
          p: {
            a: 0,
            k: [
              6.201,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 396,
        ty: 4,
        parent: 395,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.4,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0.3
                  ],
                  [
                    -0.5,
                    0
                  ]
                ],
                v: [
                  [
                    6.1,
                    4.3
                  ],
                  [
                    6.1,
                    3.3
                  ],
                  [
                    4.5,
                    3.8
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    0.5,
                    4.6
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.8,
                    4.3
                  ],
                  [
                    4.1,
                    5.5
                  ],
                  [
                    4.1,
                    5.8
                  ],
                  [
                    2.7,
                    5.8
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    2.3,
                    9
                  ],
                  [
                    2.9,
                    8.9
                  ],
                  [
                    2.9,
                    8.1
                  ],
                  [
                    2.6,
                    8.1
                  ],
                  [
                    1.6,
                    7.4
                  ],
                  [
                    2.8,
                    6.6
                  ],
                  [
                    4.1,
                    6.6
                  ],
                  [
                    4.1,
                    8.9
                  ],
                  [
                    5.2,
                    8.9
                  ],
                  [
                    5.2,
                    5.4
                  ],
                  [
                    5,
                    4.5
                  ],
                  [
                    6.1,
                    4.3
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    -0.4,
                    -0.3
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0.2,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.5,
                    0.6
                  ],
                  [
                    4.5,
                    2.5
                  ],
                  [
                    5.5,
                    2.5
                  ],
                  [
                    5.5,
                    0.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 395,
        ty: 3,
        parent: 384,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 384,
        ty: 3,
        parent: 383,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 383,
        ty: 3,
        parent: 382,
        ks: {
          p: {
            a: 0,
            k: [
              -5.313,
              1.119
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 382,
        ty: 3,
        parent: 381,
        ks: {
          p: {
            a: 0,
            k: [
              29.582,
              2.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 398,
        ty: 4,
        parent: 397,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    1,
                    1,
                    1
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 397,
        ty: 3,
        parent: 381,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 381,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 404,
        ty: 0,
        parent: 399,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "402"
      },
      {
        ind: 399,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 408,
        ty: 4,
        parent: 407,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 407,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 410,
        ty: 4,
        parent: 409,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 409,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 412,
        ty: 4,
        parent: 411,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 411,
        ty: 3,
        parent: 406,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              3.556
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 406,
        ty: 3,
        parent: 405,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 405,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 430,
        ty: 4,
        td: 1,
        parent: 413,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 429,
        ty: 0,
        tt: 1,
        parent: 413,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "427"
      },
      {
        ind: 413,
        ty: 3,
        parent: 380,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 431,
        ty: 4,
        parent: 380,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 380,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6,
              1
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "436",
    layers: [
      {
        ind: 434,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              6,
              1
            ]
          },
          p: {
            a: 0,
            k: [
              51.6,
              46.6
            ]
          }
        },
        w: 184,
        h: 97,
        ip: 0,
        op: 361,
        st: 0,
        refId: "432"
      },
      {
        ind: 435,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                140.345,
                94.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                280.69,
                189.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "462",
    layers: [
      {
        ind: 461,
        ty: 2,
        parent: 460,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        refId: "0"
      },
      {
        ind: 460,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              -0.111,
              0
            ]
          },
          s: {
            a: 0,
            k: [
              43.53,
              43.53
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "478",
    layers: [
      {
        ind: 477,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                17.5,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                35,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.41,
                            -1.91
                          ],
                          [
                            -0.92,
                            0.09
                          ],
                          [
                            1.69,
                            -0.98
                          ],
                          [
                            -0.37,
                            -0.85
                          ],
                          [
                            1.45,
                            1.31
                          ],
                          [
                            0.69,
                            -0.62
                          ],
                          [
                            -0.79,
                            1.79
                          ],
                          [
                            0.8,
                            0.46
                          ],
                          [
                            -1.95,
                            -0.21
                          ],
                          [
                            -0.2,
                            0.91
                          ]
                        ],
                        o: [
                          [
                            0.41,
                            -1.91
                          ],
                          [
                            0.19,
                            0.91
                          ],
                          [
                            1.94,
                            -0.21
                          ],
                          [
                            -0.8,
                            0.46
                          ],
                          [
                            0.8,
                            1.79
                          ],
                          [
                            -0.69,
                            -0.62
                          ],
                          [
                            -1.45,
                            1.31
                          ],
                          [
                            0.38,
                            -0.85
                          ],
                          [
                            -1.7,
                            -0.98
                          ],
                          [
                            0.92,
                            0.09
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            11.5,
                            8.55
                          ],
                          [
                            15.05,
                            8.55
                          ],
                          [
                            17.01,
                            9.98
                          ],
                          [
                            18.1,
                            13.34
                          ],
                          [
                            17.35,
                            15.65
                          ],
                          [
                            14.49,
                            17.73
                          ],
                          [
                            12.06,
                            17.73
                          ],
                          [
                            9.2,
                            15.65
                          ],
                          [
                            8.45,
                            13.34
                          ],
                          [
                            9.54,
                            9.98
                          ],
                          [
                            11.5,
                            8.55
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.9,
                        0.01,
                        0.47,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "486",
    layers: [
      {
        ind: 485,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                14,
                19.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                28,
                39
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            1.17,
                            0.79
                          ],
                          [
                            0.54,
                            1.3
                          ],
                          [
                            -0.28,
                            1.38
                          ],
                          [
                            -1,
                            1
                          ],
                          [
                            -1.38,
                            0.27
                          ],
                          [
                            -1.3,
                            -0.54
                          ],
                          [
                            -0.78,
                            -1.17
                          ],
                          [
                            0,
                            -1.41
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            1.89,
                            0
                          ]
                        ],
                        o: [
                          [
                            -1.41,
                            0
                          ],
                          [
                            -1.17,
                            -0.78
                          ],
                          [
                            -0.54,
                            -1.3
                          ],
                          [
                            0.27,
                            -1.38
                          ],
                          [
                            1,
                            -1
                          ],
                          [
                            1.38,
                            -0.28
                          ],
                          [
                            1.3,
                            0.54
                          ],
                          [
                            0.79,
                            1.17
                          ],
                          [
                            0,
                            1.89
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            14.85
                          ],
                          [
                            6.39,
                            13.65
                          ],
                          [
                            3.77,
                            10.45
                          ],
                          [
                            3.36,
                            6.34
                          ],
                          [
                            5.31,
                            2.69
                          ],
                          [
                            8.96,
                            0.74
                          ],
                          [
                            13.08,
                            1.14
                          ],
                          [
                            16.27,
                            3.77
                          ],
                          [
                            17.48,
                            7.73
                          ],
                          [
                            15.39,
                            12.76
                          ],
                          [
                            10.35,
                            14.85
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.7,
                            -0.47
                          ],
                          [
                            0.32,
                            -0.78
                          ],
                          [
                            -0.16,
                            -0.83
                          ],
                          [
                            -0.6,
                            -0.6
                          ],
                          [
                            -0.83,
                            -0.16
                          ],
                          [
                            -0.78,
                            0.32
                          ],
                          [
                            -0.47,
                            0.7
                          ],
                          [
                            0,
                            0.84
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.85,
                            0
                          ],
                          [
                            -0.7,
                            0.47
                          ],
                          [
                            -0.32,
                            0.78
                          ],
                          [
                            0.16,
                            0.83
                          ],
                          [
                            0.6,
                            0.6
                          ],
                          [
                            0.83,
                            0.16
                          ],
                          [
                            0.78,
                            -0.32
                          ],
                          [
                            0.47,
                            -0.7
                          ],
                          [
                            0,
                            -1.14
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            10.35,
                            3.45
                          ],
                          [
                            7.98,
                            4.17
                          ],
                          [
                            6.4,
                            6.09
                          ],
                          [
                            6.16,
                            8.56
                          ],
                          [
                            7.33,
                            10.75
                          ],
                          [
                            9.52,
                            11.92
                          ],
                          [
                            11.98,
                            11.68
                          ],
                          [
                            13.91,
                            10.1
                          ],
                          [
                            14.63,
                            7.73
                          ],
                          [
                            13.37,
                            4.7
                          ],
                          [
                            10.35,
                            3.45
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            0.8
                          ],
                          [
                            1.13,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.8,
                            -0.8
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ],
                          [
                            0.27,
                            0.27
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            1.34
                          ],
                          [
                            -1.89,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.34,
                            -1.34
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.27,
                            -0.27
                          ],
                          [
                            0.38,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.37,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.13
                          ],
                          [
                            -0.8,
                            -0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.13,
                            0
                          ],
                          [
                            -0.8,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            -0.38,
                            0
                          ],
                          [
                            -0.27,
                            -0.27
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.89
                          ],
                          [
                            1.34,
                            -1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            1.89,
                            0
                          ],
                          [
                            1.34,
                            1.34
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.38
                          ],
                          [
                            -0.27,
                            0.27
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            18.9,
                            29.1
                          ],
                          [
                            17.89,
                            28.68
                          ],
                          [
                            17.48,
                            27.68
                          ],
                          [
                            17.48,
                            24.82
                          ],
                          [
                            16.22,
                            21.8
                          ],
                          [
                            13.2,
                            20.55
                          ],
                          [
                            7.5,
                            20.55
                          ],
                          [
                            4.48,
                            21.8
                          ],
                          [
                            3.23,
                            24.82
                          ],
                          [
                            3.23,
                            27.68
                          ],
                          [
                            2.81,
                            28.68
                          ],
                          [
                            1.8,
                            29.1
                          ],
                          [
                            0.79,
                            28.68
                          ],
                          [
                            0.38,
                            27.68
                          ],
                          [
                            0.38,
                            24.82
                          ],
                          [
                            2.46,
                            19.79
                          ],
                          [
                            7.5,
                            17.7
                          ],
                          [
                            13.2,
                            17.7
                          ],
                          [
                            18.23,
                            19.79
                          ],
                          [
                            20.32,
                            24.82
                          ],
                          [
                            20.32,
                            27.68
                          ],
                          [
                            19.91,
                            28.68
                          ],
                          [
                            18.9,
                            29.1
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.18,
                        0.22,
                        0.29,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "489",
    layers: [
      {
        ind: 488,
        ty: 0,
        parent: 484,
        ks: {},
        w: 28,
        h: 39,
        ip: 0,
        op: 361,
        st: 0,
        refId: "486"
      },
      {
        ind: 484,
        ty: 3,
        parent: 483,
        ks: {
          s: {
            a: 0,
            k: [
              91.47,
              91.62
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 483,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              8.233,
              2.749
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "495",
    layers: [
      {
        ind: 491,
        ty: 0,
        parent: 482,
        ks: {
          a: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          },
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        w: 41.7102,
        h: 41.7778,
        ip: 0,
        op: 361,
        st: 0,
        refId: "489"
      },
      {
        ind: 482,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              6.952,
              6.963
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 493,
        ty: 4,
        parent: 492,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "el",
            p: {
              a: 0,
              k: [
                1.372,
                1.374
              ]
            },
            s: {
              a: 0,
              k: [
                2.744,
                2.749
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 492,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              5.305,
              47.458
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 494,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.996,
                0.937,
                0.98
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "500",
    layers: [
      {
        ind: 446,
        ty: 4,
        parent: 445,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.03
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    -0.91
                  ],
                  [
                    -0.95,
                    0
                  ],
                  [
                    -0.28,
                    0.51
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.39,
                    0
                  ],
                  [
                    0.01,
                    0.49
                  ]
                ],
                v: [
                  [
                    1,
                    4.56
                  ],
                  [
                    3.43,
                    4.56
                  ],
                  [
                    3.44,
                    4.34
                  ],
                  [
                    1.88,
                    2.68
                  ],
                  [
                    0.26,
                    4.34
                  ],
                  [
                    1.94,
                    6.01
                  ],
                  [
                    3.31,
                    5.24
                  ],
                  [
                    2.8,
                    4.89
                  ],
                  [
                    1.93,
                    5.39
                  ],
                  [
                    1,
                    4.56
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.01,
                    -0.08
                  ],
                  [
                    0,
                    -0.97
                  ],
                  [
                    -0.93,
                    0
                  ],
                  [
                    0,
                    0.95
                  ],
                  [
                    0.56,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.19,
                    0.35
                  ],
                  [
                    -0.47,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.44,
                    0
                  ],
                  [
                    -0.02,
                    -0.46
                  ]
                ],
                v: [
                  [
                    1.03,
                    4.02
                  ],
                  [
                    1.88,
                    3.29
                  ],
                  [
                    2.71,
                    4.02
                  ]
                ],
                o: [
                  [
                    0.07,
                    -0.44
                  ],
                  [
                    0.44,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 445,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              13.464,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 448,
        ty: 4,
        parent: 447,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.69,
                    0
                  ],
                  [
                    -0.13,
                    0.05
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.07,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.44,
                    4.8
                  ],
                  [
                    1.44,
                    3.39
                  ],
                  [
                    2.23,
                    3.39
                  ],
                  [
                    2.23,
                    2.75
                  ],
                  [
                    1.44,
                    2.75
                  ],
                  [
                    1.44,
                    1.76
                  ],
                  [
                    0.73,
                    1.76
                  ],
                  [
                    0.73,
                    2.75
                  ],
                  [
                    0.14,
                    2.75
                  ],
                  [
                    0.14,
                    3.39
                  ],
                  [
                    0.73,
                    3.39
                  ],
                  [
                    0.73,
                    4.92
                  ],
                  [
                    1.84,
                    5.99
                  ],
                  [
                    2.22,
                    5.92
                  ],
                  [
                    2.22,
                    5.31
                  ],
                  [
                    1.99,
                    5.34
                  ],
                  [
                    1.44,
                    4.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.71
                  ],
                  [
                    0.09,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.09,
                    0.02
                  ],
                  [
                    -0.35,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 447,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              11.022,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 450,
        ty: 4,
        parent: 449,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ]
                ],
                v: [
                  [
                    0.37,
                    1.71
                  ],
                  [
                    0.83,
                    2.17
                  ],
                  [
                    1.3,
                    1.71
                  ],
                  [
                    0.83,
                    1.23
                  ],
                  [
                    0.37,
                    1.71
                  ]
                ],
                o: [
                  [
                    0,
                    0.26
                  ],
                  [
                    0.26,
                    0
                  ],
                  [
                    0,
                    -0.26
                  ],
                  [
                    -0.26,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.48,
                    5.95
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    1.19,
                    2.75
                  ],
                  [
                    1.19,
                    5.95
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 449,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              9.354,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 452,
        ty: 4,
        parent: 451,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.1,
                    2.75
                  ],
                  [
                    1.46,
                    5.95
                  ],
                  [
                    2.17,
                    5.95
                  ],
                  [
                    3.51,
                    2.75
                  ],
                  [
                    2.76,
                    2.75
                  ],
                  [
                    1.82,
                    5.12
                  ],
                  [
                    0.88,
                    2.75
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 451,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              5.73,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 454,
        ty: 4,
        parent: 453,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.82,
                    0
                  ],
                  [
                    0.31,
                    -0.41
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    -0.46
                  ]
                ],
                v: [
                  [
                    2.64,
                    4.15
                  ],
                  [
                    2.64,
                    5.95
                  ],
                  [
                    3.35,
                    5.95
                  ],
                  [
                    3.35,
                    3.97
                  ],
                  [
                    2.15,
                    2.68
                  ],
                  [
                    1.18,
                    3.18
                  ],
                  [
                    1.18,
                    2.75
                  ],
                  [
                    0.48,
                    2.75
                  ],
                  [
                    0.48,
                    5.95
                  ],
                  [
                    1.19,
                    5.95
                  ],
                  [
                    1.19,
                    4.17
                  ],
                  [
                    1.93,
                    3.36
                  ],
                  [
                    2.64,
                    4.15
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    -0.25,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.49
                  ],
                  [
                    0.49,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 453,
        ty: 3,
        parent: 444,
        ks: {
          p: {
            a: 0,
            k: [
              1.932,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 456,
        ty: 4,
        parent: 455,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.6,
                    5.95
                  ],
                  [
                    1.34,
                    5.95
                  ],
                  [
                    1.34,
                    1.25
                  ],
                  [
                    0.6,
                    1.25
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.137,
                0.318,
                0.569
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 455,
        ty: 3,
        parent: 444,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 444,
        ty: 3,
        parent: 443,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 443,
        ty: 3,
        parent: 442,
        ks: {
          p: {
            a: 0,
            k: [
              2.072,
              1.141
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 442,
        ty: 3,
        parent: 441,
        ks: {
          p: {
            a: 0,
            k: [
              34.315,
              4.741
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 458,
        ty: 4,
        parent: 457,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    89.745,
                    21.148
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    44.373,
                    10.074
                  ]
                },
                r: {
                  a: 0,
                  k: 100
                },
                s: {
                  a: 0,
                  k: [
                    88.745,
                    20.148
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.129,
                    0.322,
                    0.584
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 1
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    1,
                    1,
                    1
                  ]
                },
                o: {
                  a: 0,
                  k: 100
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 457,
        ty: 3,
        parent: 441,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 441,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              65.185
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 464,
        ty: 0,
        parent: 459,
        ks: {},
        w: 42.8779,
        h: 1.306,
        ip: 0,
        op: 361,
        st: 0,
        refId: "462"
      },
      {
        ind: 459,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              18.191,
              68.785
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 468,
        ty: 4,
        parent: 467,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 467,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              36.222
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 470,
        ty: 4,
        parent: 469,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                44.437,
                3.309
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                88.874,
                6.617
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 469,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              24.642
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 473,
        ty: 4,
        parent: 472,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                29.885,
                5.66
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                59.769,
                11.319
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 472,
        ty: 3,
        parent: 471,
        ks: {
          p: {
            a: 0,
            k: [
              14.813,
              4.266
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 480,
        ty: 0,
        parent: 476,
        ks: {},
        w: 35,
        h: 35,
        ip: 0,
        op: 361,
        st: 0,
        refId: "478"
      },
      {
        ind: 476,
        ty: 3,
        parent: 475,
        ks: {
          s: {
            a: 0,
            k: [
              86.62,
              87.07
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 475,
        ty: 3,
        parent: 474,
        ks: {
          p: {
            a: 0,
            k: [
              -10.395,
              -5.224
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 474,
        ty: 3,
        parent: 471,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 471,
        ty: 3,
        parent: 466,
        ks: {
          p: {
            a: 0,
            k: [
              2.237,
              -0.173
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 466,
        ty: 3,
        parent: 465,
        ks: {
          p: {
            a: 0,
            k: [
              -2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 465,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              76.912,
              13.037
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 498,
        ty: 4,
        td: 1,
        parent: 481,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.807,
                27.852
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                55.614,
                55.704
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 497,
        ty: 0,
        tt: 1,
        parent: 481,
        ks: {},
        w: 56,
        h: 56,
        ip: 0,
        op: 361,
        st: 0,
        refId: "495"
      },
      {
        ind: 481,
        ty: 3,
        parent: 440,
        ks: {
          p: {
            a: 0,
            k: [
              9.466,
              21.333
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 499,
        ty: 4,
        parent: 440,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                88.745,
                48
              ]
            },
            r: {
              a: 0,
              k: 10
            },
            s: {
              a: 0,
              k: [
                177.49,
                96
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 440,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              6
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  },
  {
    id: "504",
    layers: [
      {
        ind: 502,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              13,
              6
            ]
          },
          p: {
            a: 0,
            k: [
              58.6,
              51.6
            ]
          }
        },
        w: 191,
        h: 102,
        ip: 0,
        op: 361,
        st: 0,
        refId: "500"
      },
      {
        ind: 503,
        ty: 4,
        ks: {},
        ip: 0,
        op: 361,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                147.345,
                99.6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                294.69,
                199.2
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "507",
    layers: [
      {
        ind: 138,
        ty: 0,
        parent: 71,
        ks: {
          p: {
            a: 0,
            k: [
              -58.6,
              -51.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 294.69,
        h: 199.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "136"
      },
      {
        ind: 71,
        ty: 3,
        parent: 70,
        ks: {},
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 198,
        ty: 0,
        parent: 139,
        ks: {
          p: {
            a: 0,
            k: [
              -48.6,
              -46.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 274.69,
        h: 189.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "196"
      },
      {
        ind: 139,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              194.056,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 258,
        ty: 0,
        parent: 199,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "256"
      },
      {
        ind: 199,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              388.112,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 318,
        ty: 0,
        parent: 259,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "316"
      },
      {
        ind: 259,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              582.167,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 378,
        ty: 0,
        parent: 319,
        ks: {
          p: {
            a: 0,
            k: [
              -55.6,
              -48.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 288.69,
        h: 193.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "376"
      },
      {
        ind: 319,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              776.223,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 438,
        ty: 0,
        parent: 379,
        ks: {
          p: {
            a: 0,
            k: [
              -51.6,
              -46.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 280.69,
        h: 189.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "436"
      },
      {
        ind: 379,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              970.279,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 506,
        ty: 0,
        parent: 439,
        ks: {
          p: {
            a: 0,
            k: [
              -58.6,
              -51.6
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 0,
                  k: [
                    0.553,
                    0.553,
                    0.553
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 0,
                  k: 46
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 45.6
                }
              }
            ]
          }
        ],
        w: 294.69,
        h: 199.2,
        ip: 0,
        op: 361,
        st: 0,
        refId: "504"
      },
      {
        ind: 439,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              1164.335,
              0
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      },
      {
        ind: 70,
        ty: 3,
        ks: {
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  -328.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 72.6,
                s: [
                  -328.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 120.6,
                s: [
                  -523.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 145.2,
                s: [
                  -523.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 193.2,
                s: [
                  -718.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 217.8,
                s: [
                  -718.709,
                  58.074
                ],
                i: {
                  x: [
                    0,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 265.8,
                s: [
                  -913.709,
                  58.074
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 360,
                s: [
                  -913.709,
                  58.074
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 361,
        st: 0
      }
    ]
  }
];
const fr$6 = 60;
const h$6 = 238;
const ip$6 = 0;
const layers$6 = [
  {
    ind: 5,
    ty: 4,
    td: 1,
    parent: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 4,
    ty: 4,
    tt: 1,
    parent: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 3,
    ty: 3,
    parent: 2,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 9,
    ty: 0,
    parent: 2,
    ks: {
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 48,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 281.4,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 329.4,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              100
            ],
            h: 1
          }
        ]
      }
    },
    w: 255,
    h: 238,
    ip: 0,
    op: 361,
    st: 0,
    refId: "7"
  },
  {
    ind: 20,
    ty: 0,
    parent: 11,
    ks: {
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 60.6,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 63.54,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          -0.386,
          0
        ]
      }
    },
    w: 26,
    h: 17,
    ip: 0,
    op: 361,
    st: 0,
    refId: "18"
  },
  {
    ind: 34,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          0,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 63.54,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 72.54,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 132.54,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 136.14,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          0.386,
          0
        ]
      }
    },
    w: 22,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "32"
  },
  {
    ind: 49,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          1,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 136.14,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 145.14,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 205.14,
            s: [
              100
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 208.74,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              0
            ],
            h: 1
          }
        ]
      }
    },
    w: 29,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "47"
  },
  {
    ind: 63,
    ty: 0,
    parent: 11,
    ks: {
      a: {
        a: 0,
        k: [
          1,
          9
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 208.74,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 217.74,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 360,
            s: [
              100
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 0,
        k: [
          0.773,
          0
        ]
      }
    },
    w: 27,
    h: 26,
    ip: 0,
    op: 361,
    st: 0,
    refId: "61"
  },
  {
    ind: 11,
    ty: 3,
    parent: 10,
    ks: {
      p: {
        a: 0,
        k: [
          14.679,
          20.415
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 66,
    ty: 4,
    parent: 65,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "sh",
        ks: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35.1,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 64,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 65,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.3
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -10
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 66,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.6,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -3
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 67,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.5,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 68,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.5,
                      1.2
                    ],
                    [
                      35,
                      6.3
                    ],
                    [
                      9.4,
                      15.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 69,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5.1,
                      12.3
                    ],
                    [
                      36.4,
                      1.2
                    ],
                    [
                      34.9,
                      6.3
                    ],
                    [
                      9.4,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 70,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.4
                    ],
                    [
                      36.3,
                      1.2
                    ],
                    [
                      34.9,
                      6.3
                    ],
                    [
                      9.3,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.4,
                      -9.9
                    ],
                    [
                      7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.6,
                      -2.9
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 71,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.9,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.7,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.4
                    ],
                    [
                      36.2,
                      1.2
                    ],
                    [
                      34.8,
                      6.2
                    ],
                    [
                      9.3,
                      15.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.9
                    ],
                    [
                      6.9,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.8
                    ],
                    [
                      -5.7,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 72,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.9,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.6,
                      -8.3
                    ],
                    [
                      -6,
                      -8.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      5,
                      12.5
                    ],
                    [
                      36.1,
                      1.1
                    ],
                    [
                      34.7,
                      6.2
                    ],
                    [
                      9.3,
                      15.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.9
                    ],
                    [
                      6.9,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.8
                    ],
                    [
                      -5.6,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 73,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.6,
                      -8.3
                    ],
                    [
                      -6,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.9,
                      12.6
                    ],
                    [
                      35.9,
                      1.1
                    ],
                    [
                      34.5,
                      6.2
                    ],
                    [
                      9.2,
                      15.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.8
                    ],
                    [
                      6.8,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.7
                    ],
                    [
                      -5.6,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 74,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      10.2
                    ],
                    [
                      -11.7,
                      -3.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.5,
                      -8.3
                    ],
                    [
                      -5.9,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.8,
                      12.7
                    ],
                    [
                      35.6,
                      1
                    ],
                    [
                      34.3,
                      6.1
                    ],
                    [
                      9.2,
                      15.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.3,
                      -9.8
                    ],
                    [
                      6.8,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.6
                    ],
                    [
                      -5.5,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 75,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.7,
                      10.2
                    ],
                    [
                      -11.6,
                      -3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.4,
                      -8.3
                    ],
                    [
                      -5.9,
                      -8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.7,
                      12.8
                    ],
                    [
                      35.3,
                      0.9
                    ],
                    [
                      34,
                      6
                    ],
                    [
                      9.1,
                      15.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.2,
                      -9.8
                    ],
                    [
                      6.7,
                      -10.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.5
                    ],
                    [
                      -5.4,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 76,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      10.1
                    ],
                    [
                      -11.6,
                      -2.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.3,
                      -8.3
                    ],
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.6,
                      13
                    ],
                    [
                      34.8,
                      0.8
                    ],
                    [
                      33.6,
                      5.9
                    ],
                    [
                      9,
                      15.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.2,
                      -9.7
                    ],
                    [
                      6.5,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.5,
                      -2.3
                    ],
                    [
                      -5.3,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 77,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      10.1
                    ],
                    [
                      -11.5,
                      -2.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5.2,
                      -8.3
                    ],
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.4,
                      13.3
                    ],
                    [
                      34.1,
                      0.6
                    ],
                    [
                      33.1,
                      5.8
                    ],
                    [
                      8.8,
                      16.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7.1,
                      -9.6
                    ],
                    [
                      6.3,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.4,
                      -2
                    ],
                    [
                      -5.2,
                      8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 78,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6.1,
                      10.1
                    ],
                    [
                      -11.5,
                      -2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      5,
                      -8.2
                    ],
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.2,
                      13.6
                    ],
                    [
                      33.5,
                      0.5
                    ],
                    [
                      32.5,
                      5.7
                    ],
                    [
                      8.7,
                      16.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7,
                      -9.5
                    ],
                    [
                      6.1,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.4,
                      -1.8
                    ],
                    [
                      -5,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 79,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -6,
                      10.1
                    ],
                    [
                      -11.4,
                      -1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.9,
                      -8.2
                    ],
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4.1,
                      13.9
                    ],
                    [
                      32.9,
                      0.4
                    ],
                    [
                      32,
                      5.6
                    ],
                    [
                      8.5,
                      16.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -7,
                      -9.4
                    ],
                    [
                      6,
                      -10.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.3,
                      -1.5
                    ],
                    [
                      -4.9,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 80,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.9,
                      10
                    ],
                    [
                      -11.4,
                      -1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.8,
                      -8.2
                    ],
                    [
                      -5.6,
                      -7.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      4,
                      14.1
                    ],
                    [
                      32.4,
                      0.3
                    ],
                    [
                      31.6,
                      5.6
                    ],
                    [
                      8.5,
                      16.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.9,
                      -9.3
                    ],
                    [
                      5.9,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.3,
                      -1.4
                    ],
                    [
                      -4.8,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 81,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.7,
                      10
                    ],
                    [
                      -11.3,
                      -1.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.7,
                      -8.2
                    ],
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.9,
                      14.3
                    ],
                    [
                      32,
                      0.2
                    ],
                    [
                      31.3,
                      5.5
                    ],
                    [
                      8.4,
                      16.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.9,
                      -9.3
                    ],
                    [
                      5.7,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1.2
                    ],
                    [
                      -4.7,
                      8.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 82,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.7,
                      10
                    ],
                    [
                      -11.3,
                      -1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.6,
                      -8.1
                    ],
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.8,
                      14.4
                    ],
                    [
                      31.7,
                      0.2
                    ],
                    [
                      31.1,
                      5.5
                    ],
                    [
                      8.3,
                      17.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.2
                    ],
                    [
                      5.7,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1.1
                    ],
                    [
                      -4.6,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 83,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.6,
                      10
                    ],
                    [
                      -11.2,
                      -1.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.6,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.7,
                      14.5
                    ],
                    [
                      31.4,
                      0.2
                    ],
                    [
                      30.9,
                      5.5
                    ],
                    [
                      8.3,
                      17.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.2
                    ],
                    [
                      5.6,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.2,
                      -1
                    ],
                    [
                      -4.6,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 84,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.5,
                      10
                    ],
                    [
                      -11.2,
                      -1.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.5,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.7,
                      14.6
                    ],
                    [
                      31.2,
                      0.2
                    ],
                    [
                      30.7,
                      5.4
                    ],
                    [
                      8.2,
                      17.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.1
                    ],
                    [
                      5.5,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -1
                    ],
                    [
                      -4.5,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 85,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.5,
                      10
                    ],
                    [
                      -11.2,
                      -1.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.5,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.6,
                      14.7
                    ],
                    [
                      31,
                      0.1
                    ],
                    [
                      30.5,
                      5.4
                    ],
                    [
                      8.2,
                      17.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.8,
                      -9.1
                    ],
                    [
                      5.5,
                      -10
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.9
                    ],
                    [
                      -4.5,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 86,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.6,
                      14.8
                    ],
                    [
                      30.9,
                      0.1
                    ],
                    [
                      30.4,
                      5.4
                    ],
                    [
                      8.1,
                      17.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9.1
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.8
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 87,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      14.8
                    ],
                    [
                      30.7,
                      0.1
                    ],
                    [
                      30.3,
                      5.4
                    ],
                    [
                      8.1,
                      17.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9.1
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.8
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 88,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.4,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      14.9
                    ],
                    [
                      30.6,
                      0.1
                    ],
                    [
                      30.2,
                      5.4
                    ],
                    [
                      8.1,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.4,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9.1,
                      -0.7
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 89,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.4,
                      -8.1
                    ],
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      15
                    ],
                    [
                      30.5,
                      0.1
                    ],
                    [
                      30.1,
                      5.4
                    ],
                    [
                      8.1,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.7
                    ],
                    [
                      -4.4,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 90,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.5,
                      15
                    ],
                    [
                      30.4,
                      0.1
                    ],
                    [
                      30,
                      5.4
                    ],
                    [
                      8,
                      17.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.7
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 91,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11.1,
                      -0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15
                    ],
                    [
                      30.3,
                      0.1
                    ],
                    [
                      29.9,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 92,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.3,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30.2,
                      0.1
                    ],
                    [
                      29.8,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.3,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 93,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30.1,
                      0.1
                    ],
                    [
                      29.8,
                      5.4
                    ],
                    [
                      8,
                      17.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.7,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 94,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.1
                    ],
                    [
                      30,
                      0.1
                    ],
                    [
                      29.7,
                      5.4
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.6
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 95,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.3,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.4,
                      15.2
                    ],
                    [
                      30,
                      0.1
                    ],
                    [
                      29.7,
                      5.4
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.3,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 96,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.9,
                      0
                    ],
                    [
                      29.6,
                      5.3
                    ],
                    [
                      8,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 97,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.9,
                      0
                    ],
                    [
                      29.6,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 98,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8.1
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.2
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 99,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 100,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.2,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.8,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.2,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 101,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.5,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.5
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 102,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 103,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 104,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.7,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 105,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 106,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 107,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 108,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 109,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 110,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 111,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.4,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 112,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.3
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 137,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 138,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -11,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.6,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 139,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.3,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 140,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.9
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.2,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.3,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.2,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 141,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.5,
                      0
                    ],
                    [
                      29.2,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.4
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 142,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5.1,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.4
                    ],
                    [
                      29.4,
                      0
                    ],
                    [
                      29.2,
                      5.3
                    ],
                    [
                      7.9,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5.1,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 143,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.4,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.5
                    ],
                    [
                      29.3,
                      0
                    ],
                    [
                      29.1,
                      5.3
                    ],
                    [
                      7.8,
                      17.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.9
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 144,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.2,
                      15.5
                    ],
                    [
                      29.2,
                      0
                    ],
                    [
                      29,
                      5.3
                    ],
                    [
                      7.8,
                      18
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.6,
                      -8.8
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.3
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 145,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -5,
                      9.8
                    ],
                    [
                      -10.9,
                      -0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4.1,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.1,
                      15.6
                    ],
                    [
                      29,
                      0
                    ],
                    [
                      28.9,
                      5.3
                    ],
                    [
                      7.8,
                      18
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.8
                    ],
                    [
                      5,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.9,
                      -0.2
                    ],
                    [
                      -4.1,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 146,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.9,
                      9.8
                    ],
                    [
                      -10.8,
                      -0.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      4,
                      -8
                    ],
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3.1,
                      15.7
                    ],
                    [
                      28.8,
                      0
                    ],
                    [
                      28.7,
                      5.3
                    ],
                    [
                      7.7,
                      18.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.8
                    ],
                    [
                      4.9,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.8,
                      -0.2
                    ],
                    [
                      -4,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 147,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.8,
                      9.8
                    ],
                    [
                      -10.8,
                      -0.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.9,
                      -8
                    ],
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      3,
                      15.8
                    ],
                    [
                      28.5,
                      0
                    ],
                    [
                      28.5,
                      5.3
                    ],
                    [
                      7.7,
                      18.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.5,
                      -8.7
                    ],
                    [
                      4.8,
                      -9.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.8,
                      -0.1
                    ],
                    [
                      -3.9,
                      8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 148,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.7,
                      9.7
                    ],
                    [
                      -10.7,
                      0.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.9,
                      -7.9
                    ],
                    [
                      -5.2,
                      -7.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.9,
                      16
                    ],
                    [
                      28.1,
                      0
                    ],
                    [
                      28.1,
                      5.3
                    ],
                    [
                      7.6,
                      18.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.4,
                      -8.7
                    ],
                    [
                      4.7,
                      -9.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.7,
                      0.1
                    ],
                    [
                      -3.9,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 149,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.6,
                      9.7
                    ],
                    [
                      -10.6,
                      0.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.7,
                      -7.9
                    ],
                    [
                      -5.2,
                      -7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.8,
                      16.3
                    ],
                    [
                      27.5,
                      0
                    ],
                    [
                      27.7,
                      5.3
                    ],
                    [
                      7.5,
                      18.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.4,
                      -8.6
                    ],
                    [
                      4.6,
                      -9.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.6,
                      0.2
                    ],
                    [
                      -3.7,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 150,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.4,
                      9.6
                    ],
                    [
                      -10.5,
                      0.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.6,
                      -7.9
                    ],
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.7,
                      16.6
                    ],
                    [
                      26.8,
                      0
                    ],
                    [
                      27.1,
                      5.3
                    ],
                    [
                      7.4,
                      18.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.3,
                      -8.5
                    ],
                    [
                      4.4,
                      -9.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.5,
                      0.5
                    ],
                    [
                      -3.6,
                      7.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 151,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.3,
                      9.6
                    ],
                    [
                      -10.3,
                      0.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.5,
                      -7.8
                    ],
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.5,
                      16.9
                    ],
                    [
                      26.2,
                      0.1
                    ],
                    [
                      26.6,
                      5.4
                    ],
                    [
                      7.3,
                      19.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.2,
                      -8.4
                    ],
                    [
                      4.3,
                      -9.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.4,
                      0.7
                    ],
                    [
                      -3.5,
                      7.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 152,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4.1,
                      9.5
                    ],
                    [
                      -10.2,
                      1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.4,
                      -7.8
                    ],
                    [
                      -5,
                      -6.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.4,
                      17.2
                    ],
                    [
                      25.6,
                      0.1
                    ],
                    [
                      26.1,
                      5.4
                    ],
                    [
                      7.2,
                      19.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.2,
                      -8.3
                    ],
                    [
                      4.1,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.3,
                      0.8
                    ],
                    [
                      -3.4,
                      7.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 153,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -4,
                      9.5
                    ],
                    [
                      -10.1,
                      1.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.3,
                      -7.7
                    ],
                    [
                      -5,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.3,
                      17.4
                    ],
                    [
                      25.2,
                      0.2
                    ],
                    [
                      25.7,
                      5.5
                    ],
                    [
                      7.1,
                      19.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.1,
                      -8.3
                    ],
                    [
                      4,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.2,
                      0.9
                    ],
                    [
                      -3.3,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 154,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.9,
                      9.5
                    ],
                    [
                      -10,
                      1.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.2,
                      -7.7
                    ],
                    [
                      -5,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.2,
                      17.5
                    ],
                    [
                      24.8,
                      0.2
                    ],
                    [
                      25.5,
                      5.5
                    ],
                    [
                      7.1,
                      19.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6.1,
                      -8.2
                    ],
                    [
                      3.9,
                      -9.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.2,
                      1
                    ],
                    [
                      -3.2,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 155,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.9,
                      9.4
                    ],
                    [
                      -10,
                      1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.2,
                      -7.7
                    ],
                    [
                      -4.9,
                      -6.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.2,
                      17.7
                    ],
                    [
                      24.5,
                      0.3
                    ],
                    [
                      25.2,
                      5.5
                    ],
                    [
                      7,
                      19.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.2
                    ],
                    [
                      3.9,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.1,
                      1.1
                    ],
                    [
                      -3.2,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 156,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.8,
                      9.4
                    ],
                    [
                      -9.9,
                      1.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.1,
                      -7.7
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      17.8
                    ],
                    [
                      24.3,
                      0.3
                    ],
                    [
                      25,
                      5.5
                    ],
                    [
                      7,
                      19.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.8,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8.1,
                      1.2
                    ],
                    [
                      -3.1,
                      7.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 157,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.8,
                      9.4
                    ],
                    [
                      -9.9,
                      1.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3.1,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      17.9
                    ],
                    [
                      24.1,
                      0.3
                    ],
                    [
                      24.8,
                      5.6
                    ],
                    [
                      6.9,
                      19.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.8,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.2
                    ],
                    [
                      -3.1,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 158,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.4
                    ],
                    [
                      -9.8,
                      1.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2.1,
                      18
                    ],
                    [
                      23.9,
                      0.4
                    ],
                    [
                      24.7,
                      5.6
                    ],
                    [
                      6.9,
                      20
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8.1
                    ],
                    [
                      3.7,
                      -9.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.3
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 159,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.3
                    ],
                    [
                      -9.8,
                      1.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.9,
                      -6.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.1
                    ],
                    [
                      23.7,
                      0.4
                    ],
                    [
                      24.6,
                      5.6
                    ],
                    [
                      6.9,
                      20
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -6,
                      -8
                    ],
                    [
                      3.7,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -8,
                      1.3
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 160,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.7,
                      9.3
                    ],
                    [
                      -9.7,
                      1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.1
                    ],
                    [
                      23.6,
                      0.4
                    ],
                    [
                      24.5,
                      5.6
                    ],
                    [
                      6.9,
                      20.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.7,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 161,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      3,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.2
                    ],
                    [
                      23.5,
                      0.4
                    ],
                    [
                      24.4,
                      5.7
                    ],
                    [
                      6.8,
                      20.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -3,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 162,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      2,
                      18.2
                    ],
                    [
                      23.3,
                      0.4
                    ],
                    [
                      24.3,
                      5.7
                    ],
                    [
                      6.8,
                      20.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.4
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 163,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.7,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.3
                    ],
                    [
                      23.2,
                      0.5
                    ],
                    [
                      24.2,
                      5.7
                    ],
                    [
                      6.8,
                      20.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 164,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.6,
                      9.3
                    ],
                    [
                      -9.6,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.3
                    ],
                    [
                      23.2,
                      0.5
                    ],
                    [
                      24.1,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -8
                    ],
                    [
                      3.6,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.9,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 165,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      23.1,
                      0.5
                    ],
                    [
                      24,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 166,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      23,
                      0.5
                    ],
                    [
                      24,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 167,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.9,
                      -7.6
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.4
                    ],
                    [
                      22.9,
                      0.5
                    ],
                    [
                      23.9,
                      5.7
                    ],
                    [
                      6.8,
                      20.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.9,
                      7.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 168,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.9,
                      0.5
                    ],
                    [
                      23.9,
                      5.7
                    ],
                    [
                      6.8,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.5
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 169,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.3
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.5
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.8,
                      0.5
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 170,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.6,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.8,
                      0.6
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 171,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.5,
                      1.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.9,
                      18.5
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.8,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 172,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.5,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.9,
                      -7.9
                    ],
                    [
                      3.5,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 173,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.7,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 174,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 175,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.7,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.8,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 176,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 177,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 178,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.6,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 179,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 180,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 181,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 182,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 183,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 184,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 185,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 209,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 210,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.6
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 211,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.6,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.6
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 212,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.8,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.5,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 213,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.5,
                      2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.4,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.5
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.9
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 214,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.8,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.7
                    ],
                    [
                      22.4,
                      0.6
                    ],
                    [
                      23.5,
                      5.8
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.8,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 215,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.4,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.8
                    ],
                    [
                      22.3,
                      0.7
                    ],
                    [
                      23.4,
                      5.8
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.4,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 216,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.2
                    ],
                    [
                      -9.4,
                      2.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.8,
                      18.8
                    ],
                    [
                      22.2,
                      0.7
                    ],
                    [
                      23.3,
                      5.9
                    ],
                    [
                      6.7,
                      20.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.7,
                      1.7
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 217,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.2
                    ],
                    [
                      -9.4,
                      2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.4
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      18.9
                    ],
                    [
                      22.1,
                      0.7
                    ],
                    [
                      23.2,
                      5.9
                    ],
                    [
                      6.6,
                      20.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.8
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 218,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.3,
                      9.1
                    ],
                    [
                      -9.3,
                      2.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.7,
                      -7.5
                    ],
                    [
                      -4.7,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      19
                    ],
                    [
                      21.9,
                      0.8
                    ],
                    [
                      23.1,
                      5.9
                    ],
                    [
                      6.6,
                      20.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.8,
                      -7.8
                    ],
                    [
                      3.3,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.8
                    ],
                    [
                      -2.7,
                      7.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 219,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.2,
                      9.1
                    ],
                    [
                      -9.3,
                      2.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.6,
                      -7.4
                    ],
                    [
                      -4.7,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.7,
                      19.1
                    ],
                    [
                      21.6,
                      0.8
                    ],
                    [
                      22.9,
                      6
                    ],
                    [
                      6.6,
                      20.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      3.2,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.6,
                      1.9
                    ],
                    [
                      -2.6,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 220,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3.1,
                      9.1
                    ],
                    [
                      -9.2,
                      2.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.6,
                      -7.4
                    ],
                    [
                      -4.6,
                      -6.3
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.6,
                      19.2
                    ],
                    [
                      21.3,
                      0.9
                    ],
                    [
                      22.6,
                      6
                    ],
                    [
                      6.5,
                      21
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.7,
                      -7.7
                    ],
                    [
                      3.1,
                      -9.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.5,
                      1.9
                    ],
                    [
                      -2.6,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 221,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -3,
                      9
                    ],
                    [
                      -9.1,
                      2.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.5,
                      -7.4
                    ],
                    [
                      -4.6,
                      -6.2
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.5,
                      19.5
                    ],
                    [
                      20.9,
                      1
                    ],
                    [
                      22.2,
                      6.1
                    ],
                    [
                      6.5,
                      21.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.6,
                      -7.6
                    ],
                    [
                      3,
                      -9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.4,
                      2
                    ],
                    [
                      -2.5,
                      7.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 222,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.9,
                      9
                    ],
                    [
                      -8.9,
                      2.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.4,
                      -7.3
                    ],
                    [
                      -4.6,
                      -6.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.4,
                      19.8
                    ],
                    [
                      20.3,
                      1.2
                    ],
                    [
                      21.7,
                      6.3
                    ],
                    [
                      6.4,
                      21.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.6,
                      -7.5
                    ],
                    [
                      2.9,
                      -9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.3,
                      2.2
                    ],
                    [
                      -2.4,
                      7.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 223,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.8,
                      8.9
                    ],
                    [
                      -8.7,
                      2.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.2,
                      -7.2
                    ],
                    [
                      -4.5,
                      -6.1
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.3,
                      20.1
                    ],
                    [
                      19.6,
                      1.4
                    ],
                    [
                      21.2,
                      6.5
                    ],
                    [
                      6.3,
                      21.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.5,
                      -7.4
                    ],
                    [
                      2.8,
                      -8.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7.1,
                      2.3
                    ],
                    [
                      -2.2,
                      7.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 224,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.6,
                      8.8
                    ],
                    [
                      -8.5,
                      3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.1,
                      -7.2
                    ],
                    [
                      -4.4,
                      -6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.2,
                      20.4
                    ],
                    [
                      19,
                      1.6
                    ],
                    [
                      20.7,
                      6.6
                    ],
                    [
                      6.2,
                      22
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      2.6,
                      -8.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -7,
                      2.5
                    ],
                    [
                      -2.1,
                      7.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 225,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.5,
                      8.7
                    ],
                    [
                      -8.4,
                      3.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2.1,
                      -7.1
                    ],
                    [
                      -4.4,
                      -5.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.1,
                      20.7
                    ],
                    [
                      18.5,
                      1.8
                    ],
                    [
                      20.3,
                      6.8
                    ],
                    [
                      6.2,
                      22.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.4,
                      -7.3
                    ],
                    [
                      2.5,
                      -8.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      2.6
                    ],
                    [
                      -2.1,
                      7.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 226,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.4,
                      8.7
                    ],
                    [
                      -8.3,
                      3.2
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      2,
                      -7.1
                    ],
                    [
                      -4.4,
                      -5.9
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1.1,
                      20.9
                    ],
                    [
                      18.1,
                      1.9
                    ],
                    [
                      20,
                      6.9
                    ],
                    [
                      6.1,
                      22.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      2.4,
                      -8.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.8,
                      2.6
                    ],
                    [
                      -2,
                      7.1
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 227,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.4,
                      8.6
                    ],
                    [
                      -8.2,
                      3.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21
                    ],
                    [
                      17.8,
                      2
                    ],
                    [
                      19.8,
                      7
                    ],
                    [
                      6.1,
                      22.4
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.2
                    ],
                    [
                      2.4,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.7,
                      2.7
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 228,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.3,
                      8.6
                    ],
                    [
                      -8.1,
                      3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.1
                    ],
                    [
                      17.6,
                      2.1
                    ],
                    [
                      19.5,
                      7.1
                    ],
                    [
                      6,
                      22.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      2.3,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.6,
                      2.8
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 229,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.3,
                      8.6
                    ],
                    [
                      -8.1,
                      3.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.9,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.3
                    ],
                    [
                      17.4,
                      2.2
                    ],
                    [
                      19.4,
                      7.1
                    ],
                    [
                      6,
                      22.6
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.3,
                      -7.1
                    ],
                    [
                      2.3,
                      -8.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.6,
                      2.8
                    ],
                    [
                      -1.9,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 230,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -8,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -7
                    ],
                    [
                      -4.3,
                      -5.8
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      1,
                      21.4
                    ],
                    [
                      17.2,
                      2.3
                    ],
                    [
                      19.2,
                      7.2
                    ],
                    [
                      6,
                      22.7
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7.1
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      2.8
                    ],
                    [
                      -1.8,
                      7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 231,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -7.9,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.3,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.4
                    ],
                    [
                      17,
                      2.4
                    ],
                    [
                      19.1,
                      7.3
                    ],
                    [
                      6,
                      22.8
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.5,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 232,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.2,
                      8.5
                    ],
                    [
                      -7.9,
                      3.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.5
                    ],
                    [
                      16.9,
                      2.5
                    ],
                    [
                      19,
                      7.3
                    ],
                    [
                      6,
                      22.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.2,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 233,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.5
                    ],
                    [
                      -7.9,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.8,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.6
                    ],
                    [
                      16.7,
                      2.5
                    ],
                    [
                      18.9,
                      7.4
                    ],
                    [
                      6,
                      22.9
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.8,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 234,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.5
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.6
                    ],
                    [
                      16.6,
                      2.6
                    ],
                    [
                      18.8,
                      7.4
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.5
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      2.9
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 235,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.7
                    ],
                    [
                      16.5,
                      2.6
                    ],
                    [
                      18.7,
                      7.4
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -7
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.4,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 236,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.8,
                      3.6
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.9,
                      21.7
                    ],
                    [
                      16.4,
                      2.6
                    ],
                    [
                      18.6,
                      7.5
                    ],
                    [
                      5.9,
                      23
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.2,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 237,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.7
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.8
                    ],
                    [
                      16.4,
                      2.7
                    ],
                    [
                      18.6,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 238,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2.1,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.8
                    ],
                    [
                      16.3,
                      2.7
                    ],
                    [
                      18.5,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2.1,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 239,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.9
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.2,
                      2.7
                    ],
                    [
                      18.5,
                      7.5
                    ],
                    [
                      5.9,
                      23.1
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.9
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 240,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.7,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.2,
                      2.8
                    ],
                    [
                      18.4,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.7,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 241,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.1,
                      2.8
                    ],
                    [
                      18.4,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.3,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 242,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.7,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      21.9
                    ],
                    [
                      16.1,
                      2.8
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 243,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.8
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 244,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.9
                    ],
                    [
                      18.3,
                      7.6
                    ],
                    [
                      5.9,
                      23.2
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 245,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.7
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      16,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 246,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.2,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 247,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 248,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.4
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.4
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 249,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 250,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.9,
                      2.9
                    ],
                    [
                      18.2,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 251,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 252,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.9
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 253,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      2.9
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 254,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 255,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 256,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            },
            {
              t: 257,
              s: [
                {
                  c: true,
                  i: [
                    [
                      0,
                      0
                    ],
                    [
                      -2,
                      8.3
                    ],
                    [
                      -7.6,
                      3.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      1.6,
                      -6.8
                    ],
                    [
                      -4.1,
                      -5.6
                    ],
                    [
                      0,
                      0
                    ]
                  ],
                  v: [
                    [
                      5.7,
                      46.1
                    ],
                    [
                      0.8,
                      22.1
                    ],
                    [
                      15.8,
                      3
                    ],
                    [
                      18.1,
                      7.7
                    ],
                    [
                      5.9,
                      23.3
                    ],
                    [
                      9.9,
                      42.9
                    ],
                    [
                      5.7,
                      46.1
                    ]
                  ],
                  o: [
                    [
                      -5.1,
                      -6.8
                    ],
                    [
                      2,
                      -8.3
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      -6.2,
                      3.1
                    ],
                    [
                      -1.6,
                      6.8
                    ],
                    [
                      0,
                      0
                    ],
                    [
                      0,
                      0
                    ]
                  ]
                }
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.902,
            8e-3,
            0.471
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 65,
    ty: 3,
    parent: 64,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 68,
    ty: 4,
    parent: 67,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "sh",
        ks: {
          a: 0,
          k: {
            c: true,
            i: [
              [
                0,
                0
              ],
              [
                -10.6,
                9.9
              ],
              [
                -10.9,
                -9.6
              ],
              [
                8.3,
                -12
              ],
              [
                0,
                0
              ],
              [
                8.9,
                7.9
              ],
              [
                8.7,
                -8.1
              ],
              [
                -7,
                -9.6
              ],
              [
                0,
                0
              ]
            ],
            v: [
              [
                5.5,
                45.8
              ],
              [
                9.1,
                7.6
              ],
              [
                46.9,
                7.1
              ],
              [
                51.5,
                45.2
              ],
              [
                47.2,
                42.2
              ],
              [
                43.5,
                11.1
              ],
              [
                12.7,
                11.5
              ],
              [
                9.7,
                42.7
              ],
              [
                5.5,
                45.8
              ]
            ],
            o: [
              [
                -8.6,
                -11.8
              ],
              [
                10.6,
                -9.9
              ],
              [
                10.9,
                9.6
              ],
              [
                0,
                0
              ],
              [
                6.7,
                -9.8
              ],
              [
                -8.9,
                -7.9
              ],
              [
                -8.7,
                8.1
              ],
              [
                0,
                0
              ],
              [
                0,
                0
              ]
            ]
          }
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0.933,
            0.941,
            0.957
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 67,
    ty: 3,
    parent: 64,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 64,
    ty: 3,
    parent: 10,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 10,
    ty: 3,
    parent: 2,
    ks: {
      p: {
        a: 0,
        k: [
          100,
          165
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 509,
    ty: 0,
    parent: 69,
    ks: {
      a: {
        a: 0,
        k: [
          -21,
          -6
        ]
      },
      p: {
        a: 0,
        k: [
          -21,
          -6
        ]
      }
    },
    w: 297,
    h: 256,
    ip: 0,
    op: 361,
    st: 0,
    refId: "507"
  },
  {
    ind: 69,
    ty: 3,
    parent: 2,
    ks: {
      p: {
        a: 0,
        k: [
          -21,
          -6
        ]
      }
    },
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 511,
    ty: 4,
    parent: 510,
    ks: {},
    ip: 0,
    op: 361,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            1,
            1,
            1
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 510,
    ty: 3,
    parent: 2,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    ks: {},
    ip: 0,
    op: 361,
    st: 0
  }
];
const meta$6 = {
  g: "https://jitter.video"
};
const op$6 = 360;
const v$6 = "5.7.4";
const w$6 = 255;
const CandidateInvitationsTh = {
  assets: assets$6,
  fr: fr$6,
  h: h$6,
  ip: ip$6,
  layers: layers$6,
  meta: meta$6,
  op: op$6,
  v: v$6,
  w: w$6
};
const CandidateInvitationsAnimation = ({ alt }) => {
  const { language } = useLanguage();
  if (language.startsWith("th")) {
    return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: CandidateInvitationsTh, altText: alt });
  }
  if (language.startsWith("id")) {
    return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: CandidateInvitationsId, altText: alt });
  }
  return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: CandidateInvitationsEn, altText: alt });
};
const assets$5 = [
  {
    id: "74",
    layers: [
      {
        ind: 15,
        ty: 4,
        parent: 14,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ]
                ],
                v: [
                  [
                    0.5,
                    6.5
                  ],
                  [
                    2.7,
                    9
                  ],
                  [
                    4.6,
                    7.9
                  ],
                  [
                    4.6,
                    8.9
                  ],
                  [
                    5.3,
                    8.9
                  ],
                  [
                    5.3,
                    1.6
                  ],
                  [
                    4.6,
                    1.6
                  ],
                  [
                    4.6,
                    5.1
                  ],
                  [
                    2.7,
                    4
                  ],
                  [
                    0.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.3
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1
                  ]
                ],
                v: [
                  [
                    1.2,
                    6.5
                  ],
                  [
                    2.8,
                    4.7
                  ],
                  [
                    4.6,
                    6.5
                  ],
                  [
                    2.8,
                    8.4
                  ],
                  [
                    1.2,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 14,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              5.112,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 17,
        ty: 4,
        parent: 16,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.04
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    -0.38,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.11,
                    0.26
                  ],
                  [
                    1.02,
                    0
                  ],
                  [
                    0.33,
                    -0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.74,
                    0
                  ],
                  [
                    0,
                    -0.77
                  ]
                ],
                v: [
                  [
                    3.69,
                    5.83
                  ],
                  [
                    3.69,
                    6.15
                  ],
                  [
                    2.36,
                    6.15
                  ],
                  [
                    0.46,
                    7.54
                  ],
                  [
                    0.46,
                    7.59
                  ],
                  [
                    2.07,
                    9
                  ],
                  [
                    3.69,
                    8.08
                  ],
                  [
                    3.7,
                    8.08
                  ],
                  [
                    3.7,
                    8.92
                  ],
                  [
                    4.38,
                    8.92
                  ],
                  [
                    4.38,
                    5.82
                  ],
                  [
                    4.24,
                    5.03
                  ],
                  [
                    2.48,
                    4.04
                  ],
                  [
                    0.51,
                    5.28
                  ],
                  [
                    1.09,
                    5.6
                  ],
                  [
                    2.47,
                    4.66
                  ],
                  [
                    3.69,
                    5.83
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.85,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.81
                  ],
                  [
                    0.72,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.15
                  ],
                  [
                    -0.23,
                    -0.53
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.23,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.51,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.86,
                    0
                  ],
                  [
                    0,
                    0.54
                  ]
                ],
                v: [
                  [
                    1.16,
                    7.54
                  ],
                  [
                    2.35,
                    6.73
                  ],
                  [
                    3.69,
                    6.73
                  ],
                  [
                    3.69,
                    6.9
                  ],
                  [
                    2.2,
                    8.38
                  ],
                  [
                    1.16,
                    7.54
                  ]
                ],
                o: [
                  [
                    0,
                    -0.65
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.88
                  ],
                  [
                    -0.58,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 16,
        ty: 3,
        parent: 13,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 13,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              48.87,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 20,
        ty: 4,
        parent: 19,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.4,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    1.3
                  ]
                ],
                v: [
                  [
                    5.6,
                    6.5
                  ],
                  [
                    3.3,
                    4
                  ],
                  [
                    1.5,
                    5.2
                  ],
                  [
                    1.5,
                    1.6
                  ],
                  [
                    0.8,
                    1.6
                  ],
                  [
                    0.8,
                    8.9
                  ],
                  [
                    1.5,
                    8.9
                  ],
                  [
                    1.5,
                    7.9
                  ],
                  [
                    3.3,
                    9
                  ],
                  [
                    5.6,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1.3
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.6
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    1.1,
                    0
                  ],
                  [
                    0,
                    1
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    3.2,
                    4.7
                  ],
                  [
                    4.9,
                    6.5
                  ],
                  [
                    3.2,
                    8.4
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1
                  ],
                  [
                    1.1,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 19,
        ty: 3,
        parent: 18,
        ks: {
          p: {
            a: 0,
            k: [
              8.217,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 22,
        ty: 4,
        parent: 21,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.42
                  ],
                  [
                    1.39,
                    0
                  ],
                  [
                    0,
                    -1.42
                  ],
                  [
                    -1.39,
                    0
                  ]
                ],
                v: [
                  [
                    2.92,
                    9
                  ],
                  [
                    5.41,
                    6.5
                  ],
                  [
                    2.93,
                    4.04
                  ],
                  [
                    0.44,
                    6.54
                  ],
                  [
                    2.92,
                    9
                  ]
                ],
                o: [
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.38
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    0,
                    1.37
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.04,
                    0
                  ],
                  [
                    0,
                    -1.01
                  ],
                  [
                    1.03,
                    0
                  ],
                  [
                    0,
                    1.01
                  ]
                ],
                v: [
                  [
                    1.16,
                    6.5
                  ],
                  [
                    2.92,
                    4.65
                  ],
                  [
                    4.69,
                    6.54
                  ],
                  [
                    2.93,
                    8.38
                  ],
                  [
                    1.16,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1.01
                  ],
                  [
                    1.04,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1.05,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 21,
        ty: 3,
        parent: 18,
        ks: {
          p: {
            a: 0,
            k: [
              2.367,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 24,
        ty: 4,
        parent: 23,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ]
                ],
                v: [
                  [
                    0.7,
                    2.4
                  ],
                  [
                    1.2,
                    2.9
                  ],
                  [
                    1.7,
                    2.4
                  ],
                  [
                    1.2,
                    1.9
                  ],
                  [
                    0.7,
                    2.4
                  ]
                ],
                o: [
                  [
                    0,
                    0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    0
                  ],
                  [
                    0.1,
                    0
                  ]
                ],
                v: [
                  [
                    -0.2,
                    11.3
                  ],
                  [
                    0.2,
                    11.3
                  ],
                  [
                    1.6,
                    9.9
                  ],
                  [
                    1.6,
                    4.1
                  ],
                  [
                    0.9,
                    4.1
                  ],
                  [
                    0.9,
                    9.9
                  ],
                  [
                    0.1,
                    10.7
                  ],
                  [
                    -0.2,
                    10.7
                  ]
                ],
                o: [
                  [
                    0.1,
                    0
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 23,
        ty: 3,
        parent: 18,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 18,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              32.085,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 27,
        ty: 4,
        parent: 26,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.13,
                    0
                  ],
                  [
                    -0.09,
                    -0.01
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.05,
                    0
                  ],
                  [
                    0.36,
                    -0.75
                  ]
                ],
                v: [
                  [
                    1.49,
                    5.07
                  ],
                  [
                    1.49,
                    4.12
                  ],
                  [
                    0.79,
                    4.12
                  ],
                  [
                    0.79,
                    8.92
                  ],
                  [
                    1.49,
                    8.92
                  ],
                  [
                    1.49,
                    6.57
                  ],
                  [
                    3,
                    4.78
                  ],
                  [
                    3.27,
                    4.8
                  ],
                  [
                    3.27,
                    4.05
                  ],
                  [
                    3.03,
                    4.05
                  ],
                  [
                    1.49,
                    5.07
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.86
                  ],
                  [
                    0.09,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.05,
                    -0.01
                  ],
                  [
                    -0.55,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 26,
        ty: 3,
        parent: 25,
        ks: {
          p: {
            a: 0,
            k: [
              26.424,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 29,
        ty: 4,
        parent: 28,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.04
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    -0.38,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.11,
                    0.26
                  ],
                  [
                    1.02,
                    0
                  ],
                  [
                    0.33,
                    -0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.74,
                    0
                  ],
                  [
                    0,
                    -0.77
                  ]
                ],
                v: [
                  [
                    3.69,
                    5.83
                  ],
                  [
                    3.69,
                    6.15
                  ],
                  [
                    2.36,
                    6.15
                  ],
                  [
                    0.46,
                    7.54
                  ],
                  [
                    0.46,
                    7.59
                  ],
                  [
                    2.07,
                    9
                  ],
                  [
                    3.69,
                    8.08
                  ],
                  [
                    3.7,
                    8.08
                  ],
                  [
                    3.7,
                    8.92
                  ],
                  [
                    4.38,
                    8.92
                  ],
                  [
                    4.38,
                    5.82
                  ],
                  [
                    4.24,
                    5.03
                  ],
                  [
                    2.48,
                    4.04
                  ],
                  [
                    0.51,
                    5.28
                  ],
                  [
                    1.09,
                    5.6
                  ],
                  [
                    2.47,
                    4.66
                  ],
                  [
                    3.69,
                    5.83
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.85,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.81
                  ],
                  [
                    0.72,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.15
                  ],
                  [
                    -0.23,
                    -0.53
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.23,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.51,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.86,
                    0
                  ],
                  [
                    0,
                    0.54
                  ]
                ],
                v: [
                  [
                    1.16,
                    7.54
                  ],
                  [
                    2.35,
                    6.73
                  ],
                  [
                    3.69,
                    6.73
                  ],
                  [
                    3.69,
                    6.9
                  ],
                  [
                    2.2,
                    8.38
                  ],
                  [
                    1.16,
                    7.54
                  ]
                ],
                o: [
                  [
                    0,
                    -0.65
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.88
                  ],
                  [
                    -0.58,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 28,
        ty: 3,
        parent: 25,
        ks: {
          p: {
            a: 0,
            k: [
              21.312,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 31,
        ty: 4,
        parent: 30,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    1.5,
                    1.6
                  ],
                  [
                    0.8,
                    1.6
                  ],
                  [
                    0.8,
                    8.9
                  ],
                  [
                    1.5,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 30,
        ty: 3,
        parent: 25,
        ks: {
          p: {
            a: 0,
            k: [
              19.017,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 33,
        ty: 4,
        parent: 32,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ]
                ],
                v: [
                  [
                    0.6,
                    2.4
                  ],
                  [
                    1.1,
                    2.9
                  ],
                  [
                    1.7,
                    2.4
                  ],
                  [
                    1.1,
                    1.9
                  ],
                  [
                    0.6,
                    2.4
                  ]
                ],
                o: [
                  [
                    0,
                    0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    8.9
                  ],
                  [
                    1.5,
                    8.9
                  ],
                  [
                    1.5,
                    4.1
                  ],
                  [
                    0.8,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 32,
        ty: 3,
        parent: 25,
        ks: {
          p: {
            a: 0,
            k: [
              16.722,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 35,
        ty: 4,
        parent: 34,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0.4,
                    -0.6
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.4,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ]
                ],
                v: [
                  [
                    4.1,
                    6.1
                  ],
                  [
                    4.1,
                    8.9
                  ],
                  [
                    4.8,
                    8.9
                  ],
                  [
                    4.8,
                    6.2
                  ],
                  [
                    6.1,
                    4.7
                  ],
                  [
                    7.3,
                    6.1
                  ],
                  [
                    7.3,
                    8.9
                  ],
                  [
                    8,
                    8.9
                  ],
                  [
                    8,
                    6
                  ],
                  [
                    6.3,
                    4
                  ],
                  [
                    4.6,
                    5
                  ],
                  [
                    3,
                    4
                  ],
                  [
                    1.5,
                    5
                  ],
                  [
                    1.5,
                    4.1
                  ],
                  [
                    0.8,
                    4.1
                  ],
                  [
                    0.8,
                    8.9
                  ],
                  [
                    1.5,
                    8.9
                  ],
                  [
                    1.5,
                    6.2
                  ],
                  [
                    2.8,
                    4.7
                  ],
                  [
                    4.1,
                    6.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.3,
                    -0.6
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 34,
        ty: 3,
        parent: 25,
        ks: {
          p: {
            a: 0,
            k: [
              7.947,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 37,
        ty: 4,
        parent: 36,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ]
                ],
                v: [
                  [
                    0.6,
                    2.4
                  ],
                  [
                    1.1,
                    2.9
                  ],
                  [
                    1.7,
                    2.4
                  ],
                  [
                    1.1,
                    1.9
                  ],
                  [
                    0.6,
                    2.4
                  ]
                ],
                o: [
                  [
                    0,
                    0.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    8.9
                  ],
                  [
                    1.5,
                    8.9
                  ],
                  [
                    1.5,
                    4.1
                  ],
                  [
                    0.8,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 36,
        ty: 3,
        parent: 25,
        ks: {
          p: {
            a: 0,
            k: [
              5.652,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 39,
        ty: 4,
        parent: 38,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    0.3,
                    0.3
                  ],
                  [
                    0.8,
                    0.2
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.2,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -1.4,
                    -0.4
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0.3,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ]
                ],
                v: [
                  [
                    2.9,
                    9
                  ],
                  [
                    5.1,
                    7
                  ],
                  [
                    4.7,
                    5.8
                  ],
                  [
                    2.9,
                    4.9
                  ],
                  [
                    1.4,
                    3.7
                  ],
                  [
                    2.8,
                    2.5
                  ],
                  [
                    4.5,
                    3.6
                  ],
                  [
                    5.1,
                    3.3
                  ],
                  [
                    2.9,
                    1.8
                  ],
                  [
                    0.6,
                    3.7
                  ],
                  [
                    2.8,
                    5.7
                  ],
                  [
                    4.4,
                    7.1
                  ],
                  [
                    2.9,
                    8.3
                  ],
                  [
                    1.1,
                    6.9
                  ],
                  [
                    0.4,
                    7.3
                  ],
                  [
                    2.9,
                    9
                  ]
                ],
                o: [
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    -0.4,
                    -0.4
                  ],
                  [
                    -1.1,
                    -0.3
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    -0.8
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    1,
                    0.3
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    1.1
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 38,
        ty: 3,
        parent: 25,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 25,
        ty: 3,
        parent: 12,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 12,
        ty: 3,
        parent: 11,
        ks: {
          p: {
            a: 0,
            k: [
              3.481,
              0.1
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 11,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              67,
              12
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 41,
        ty: 4,
        parent: 40,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                101,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                202,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 40,
        ty: 3,
        parent: 10,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 10,
        ty: 3,
        parent: 9,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 43,
        ty: 4,
        parent: 42,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 42,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              98
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 45,
        ty: 4,
        parent: 44,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 44,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              83
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 47,
        ty: 4,
        parent: 46,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 46,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              281
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 49,
        ty: 4,
        parent: 48,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 48,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              201
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 51,
        ty: 4,
        parent: 50,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 50,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              251
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 53,
        ty: 4,
        parent: 52,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 52,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              171
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 55,
        ty: 4,
        parent: 54,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 54,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              266
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 57,
        ty: 4,
        parent: 56,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 56,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              186
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 59,
        ty: 4,
        parent: 58,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 58,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              236
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 61,
        ty: 4,
        parent: 60,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 60,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              156
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 63,
        ty: 4,
        parent: 62,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 62,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              68
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 65,
        ty: 4,
        parent: 64,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                20.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 1
            },
            s: {
              a: 0,
              k: [
                41,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 64,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              50,
              113
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 67,
        ty: 4,
        parent: 66,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                20.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 1
            },
            s: {
              a: 0,
              k: [
                41,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 66,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              113
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 69,
        ty: 4,
        parent: 68,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                10
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 68,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              218
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 71,
        ty: 4,
        parent: 70,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                10
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 70,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              138
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 73,
        ty: 4,
        parent: 72,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 72,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              47
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 9,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.8,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 106.8,
                s: [
                  10,
                  -157
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 216,
                s: [
                  10,
                  -157
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 264,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 270.6,
                s: [
                  10,
                  10
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 8,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              157
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "85",
    layers: [
      {
        ind: 84,
        ty: 4,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                5.5,
                8
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                11,
                16
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.09,
                            0.06
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.1,
                            -0.01
                          ],
                          [
                            0.09,
                            0.05
                          ],
                          [
                            0.05,
                            0.09
                          ],
                          [
                            0,
                            0.11
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.29,
                            0.32
                          ],
                          [
                            -0.43,
                            0.02
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.29,
                            -0.32
                          ],
                          [
                            0.02,
                            -0.43
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.03,
                            -0.07
                          ],
                          [
                            0.05,
                            -0.05
                          ],
                          [
                            0.07,
                            -0.03
                          ],
                          [
                            0.07,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.11,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.09,
                            0.05
                          ],
                          [
                            -0.1,
                            0
                          ],
                          [
                            -0.09,
                            -0.05
                          ],
                          [
                            -0.05,
                            -0.09
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.02,
                            -0.43
                          ],
                          [
                            0.29,
                            -0.32
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.43,
                            0.02
                          ],
                          [
                            0.29,
                            0.32
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.07
                          ],
                          [
                            -0.03,
                            0.07
                          ],
                          [
                            -0.05,
                            0.05
                          ],
                          [
                            -0.07,
                            0.03
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            7.33,
                            11.28
                          ],
                          [
                            7.02,
                            11.18
                          ],
                          [
                            3.95,
                            9.14
                          ],
                          [
                            0.88,
                            11.18
                          ],
                          [
                            0.59,
                            11.28
                          ],
                          [
                            0.3,
                            11.21
                          ],
                          [
                            0.08,
                            11
                          ],
                          [
                            0,
                            10.71
                          ],
                          [
                            0,
                            1.69
                          ],
                          [
                            0.42,
                            0.53
                          ],
                          [
                            1.53,
                            0
                          ],
                          [
                            6.36,
                            0
                          ],
                          [
                            7.48,
                            0.53
                          ],
                          [
                            7.89,
                            1.69
                          ],
                          [
                            7.89,
                            10.71
                          ],
                          [
                            7.85,
                            10.93
                          ],
                          [
                            7.73,
                            11.11
                          ],
                          [
                            7.55,
                            11.23
                          ],
                          [
                            7.33,
                            11.28
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.09,
                            -0.06
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.22,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -0.3
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.11,
                            0
                          ]
                        ],
                        o: [
                          [
                            0.11,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -0.3
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.22,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.09,
                            -0.06
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            3.95,
                            7.89
                          ],
                          [
                            4.26,
                            7.99
                          ],
                          [
                            6.77,
                            9.66
                          ],
                          [
                            6.77,
                            1.69
                          ],
                          [
                            6.36,
                            1.13
                          ],
                          [
                            1.53,
                            1.13
                          ],
                          [
                            1.13,
                            1.69
                          ],
                          [
                            1.13,
                            9.66
                          ],
                          [
                            3.64,
                            7.99
                          ],
                          [
                            3.95,
                            7.89
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.12,
                        0.28,
                        0.66,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "105",
    layers: [
      {
        ind: 87,
        ty: 0,
        parent: 83,
        ks: {},
        w: 11,
        h: 16,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "85"
      },
      {
        ind: 83,
        ty: 3,
        parent: 82,
        ks: {
          s: {
            a: 0,
            k: [
              116.35,
              100
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 82,
        ty: 3,
        parent: 81,
        ks: {
          p: {
            a: 0,
            k: [
              104,
              6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 92,
        ty: 4,
        parent: 91,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    4.4,
                    8.2
                  ],
                  [
                    4.4,
                    8.9
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    1.6
                  ],
                  [
                    4.3,
                    1.6
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.3
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 91,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              5.175,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 94,
        ty: 4,
        parent: 93,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.32,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.94,
                    0
                  ],
                  [
                    -0.35,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.12,
                    0.26
                  ],
                  [
                    0.81,
                    0
                  ],
                  [
                    0.31,
                    -0.66
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.37
                  ]
                ],
                v: [
                  [
                    3.47,
                    5.67
                  ],
                  [
                    3.02,
                    6.07
                  ],
                  [
                    2.42,
                    6.07
                  ],
                  [
                    0.42,
                    7.54
                  ],
                  [
                    0.42,
                    7.59
                  ],
                  [
                    2.04,
                    9
                  ],
                  [
                    3.46,
                    8.27
                  ],
                  [
                    3.46,
                    8.92
                  ],
                  [
                    4.51,
                    8.92
                  ],
                  [
                    4.51,
                    5.86
                  ],
                  [
                    4.35,
                    5
                  ],
                  [
                    2.55,
                    4.02
                  ],
                  [
                    0.5,
                    5.24
                  ],
                  [
                    1.32,
                    5.71
                  ],
                  [
                    2.52,
                    4.95
                  ],
                  [
                    3.47,
                    5.67
                  ]
                ],
                o: [
                  [
                    0,
                    0.23
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.83
                  ],
                  [
                    0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.32
                  ],
                  [
                    -0.32,
                    -0.69
                  ],
                  [
                    -1.12,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.21,
                    -0.43
                  ],
                  [
                    0.63,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.73,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.49,
                    7.49
                  ],
                  [
                    2.42,
                    6.83
                  ],
                  [
                    3.46,
                    6.83
                  ],
                  [
                    3.46,
                    6.98
                  ],
                  [
                    2.26,
                    8.15
                  ],
                  [
                    1.49,
                    7.49
                  ]
                ],
                o: [
                  [
                    0,
                    -0.45
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.72
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 93,
        ty: 3,
        parent: 90,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 90,
        ty: 3,
        parent: 89,
        ks: {
          p: {
            a: 0,
            k: [
              22.887,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 97,
        ty: 4,
        parent: 96,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.06,
                    0
                  ],
                  [
                    0.3,
                    -0.54
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.82,
                    0
                  ],
                  [
                    -0.09,
                    -0.02
                  ]
                ],
                v: [
                  [
                    3.35,
                    5.2
                  ],
                  [
                    3.35,
                    4.1
                  ],
                  [
                    3.09,
                    4.07
                  ],
                  [
                    1.76,
                    4.88
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.48
                  ],
                  [
                    3.04,
                    5.17
                  ],
                  [
                    3.35,
                    5.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.06,
                    -0.02
                  ],
                  [
                    -0.52,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.83
                  ],
                  [
                    0.05,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 96,
        ty: 3,
        parent: 95,
        ks: {
          p: {
            a: 0,
            k: [
              17.226,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 99,
        ty: 4,
        parent: 98,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.21,
                    0
                  ],
                  [
                    -0.46,
                    0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.68,
                    0
                  ],
                  [
                    0,
                    0.68
                  ]
                ],
                v: [
                  [
                    1.74,
                    6.81
                  ],
                  [
                    1.74,
                    4.12
                  ],
                  [
                    0.67,
                    4.12
                  ],
                  [
                    0.67,
                    7.07
                  ],
                  [
                    2.47,
                    9.02
                  ],
                  [
                    3.93,
                    8.28
                  ],
                  [
                    3.93,
                    8.92
                  ],
                  [
                    4.98,
                    8.92
                  ],
                  [
                    4.98,
                    4.12
                  ],
                  [
                    3.91,
                    4.12
                  ],
                  [
                    3.91,
                    6.79
                  ],
                  [
                    2.8,
                    8.01
                  ],
                  [
                    1.74,
                    6.81
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.73
                  ],
                  [
                    -0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 98,
        ty: 3,
        parent: 95,
        ks: {
          p: {
            a: 0,
            k: [
              11.529,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 101,
        ty: 4,
        parent: 100,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.9,
                    9
                  ],
                  [
                    5.5,
                    6.5
                  ],
                  [
                    2.9,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.4
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 100,
        ty: 3,
        parent: 95,
        ks: {
          p: {
            a: 0,
            k: [
              5.679,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 103,
        ty: 4,
        parent: 102,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.2,
                    1.9
                  ],
                  [
                    2.8,
                    6.1
                  ],
                  [
                    2.8,
                    8.9
                  ],
                  [
                    3.9,
                    8.9
                  ],
                  [
                    3.9,
                    6.1
                  ],
                  [
                    6.6,
                    1.9
                  ],
                  [
                    5.3,
                    1.9
                  ],
                  [
                    3.4,
                    5
                  ],
                  [
                    1.5,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 102,
        ty: 3,
        parent: 95,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 95,
        ty: 3,
        parent: 89,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 89,
        ty: 3,
        parent: 88,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              0.6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 88,
        ty: 3,
        parent: 81,
        ks: {
          p: {
            a: 0,
            k: [
              8,
              6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 104,
        ty: 4,
        parent: 81,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    62,
                    28
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    62,
                    28
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    124,
                    56
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    124,
                    56
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 81,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              62,
              28
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "109",
    layers: [
      {
        ind: 80,
        ty: 4,
        parent: 79,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    31,
                    14
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    188,
                    86
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 94.2,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 142.2,
                      s: [
                        62,
                        28
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 270.6,
                      s: [
                        62,
                        28
                      ],
                      h: 1
                    }
                  ]
                },
                r: {
                  a: 0,
                  k: 4
                },
                s: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 94.2,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 142.2,
                      s: [
                        124,
                        56
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 270.6,
                      s: [
                        124,
                        56
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.122,
                    0.278,
                    0.655
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 108,
        ty: 4,
        td: 1,
        parent: 79,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    62,
                    28
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    62,
                    28
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    124,
                    56
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    124,
                    56
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 107,
        ty: 0,
        tt: 1,
        parent: 79,
        ks: {
          a: {
            a: 0,
            k: [
              62,
              28
            ]
          }
        },
        w: 186,
        h: 84,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "105"
      },
      {
        ind: 79,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              63,
              29
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "113",
    layers: [
      {
        ind: 111,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              63,
              29
            ]
          },
          p: {
            a: 0,
            k: [
              86.211,
              52.211
            ]
          }
        },
        w: 188,
        h: 86,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "109"
      },
      {
        ind: 112,
        ty: 4,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                148.711,
                80.711
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                297.422,
                161.422
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "116",
    layers: [
      {
        ind: 115,
        ty: 0,
        parent: 78,
        ks: {
          p: {
            a: 0,
            k: [
              -86.211,
              -52.211
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.5,
                        y: 0
                      }
                    },
                    {
                      t: 150,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        0
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.5,
                        y: 0
                      }
                    },
                    {
                      t: 150,
                      s: [
                        26
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        26
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        135
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        135
                      ],
                      i: {
                        x: 0.842,
                        y: 0.419
                      },
                      o: {
                        x: 0.597,
                        y: 0
                      }
                    },
                    {
                      t: 114,
                      s: [
                        138.04
                      ],
                      i: {
                        x: 0.236,
                        y: 0.769
                      },
                      o: {
                        x: 0.222,
                        y: 0.36
                      }
                    },
                    {
                      t: 126,
                      s: [
                        144.95
                      ],
                      i: {
                        x: 0.494,
                        y: 1
                      },
                      o: {
                        x: 0.192,
                        y: 0.59
                      }
                    },
                    {
                      t: 150,
                      s: [
                        146.31
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        146.31
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        5.657
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        5.657
                      ],
                      i: {
                        x: 0.373,
                        y: 0.653
                      },
                      o: {
                        x: 0.767,
                        y: 0
                      }
                    },
                    {
                      t: 126,
                      s: [
                        6.965
                      ],
                      i: {
                        x: 0.58,
                        y: 1
                      },
                      o: {
                        x: 0.24,
                        y: 0.704
                      }
                    },
                    {
                      t: 150,
                      s: [
                        7.211
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        7.211
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 16
                }
              }
            ]
          }
        ],
        w: 297.4222,
        h: 161.4222,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "113"
      },
      {
        ind: 78,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              87,
              53
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  }
];
const fr$5 = 60;
const h$5 = 238;
const ip$5 = 0;
const layers$5 = [
  {
    ind: 4,
    ty: 4,
    td: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 3,
    ty: 4,
    tt: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 7,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                111,
                102
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                222,
                204
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 6,
    ty: 4,
    tt: 2,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                111,
                86
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                226,
                240
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 58.8,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 106.8,
                  s: [
                    111,
                    70
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 216,
                  s: [
                    111,
                    70
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 264,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    111,
                    102
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 58.8,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 106.8,
                  s: [
                    222,
                    140
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 216,
                  s: [
                    222,
                    140
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 264,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    222,
                    204
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 77,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                111,
                102
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                222,
                204
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 76,
    ty: 0,
    tt: 1,
    parent: 5,
    ks: {
      a: {
        a: 0,
        k: [
          1,
          157
        ]
      }
    },
    w: 213,
    h: 455,
    ip: 0,
    op: 271.6,
    st: 0,
    refId: "74"
  },
  {
    ind: 5,
    ty: 3,
    parent: 1,
    ks: {
      p: {
        a: 0,
        k: [
          17,
          17
        ]
      }
    },
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 118,
    ty: 0,
    parent: 1,
    ks: {
      a: {
        a: 0,
        k: [
          87,
          53
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 94.2,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 99.6,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 270.6,
            s: [
              100
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 58.8,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                0.125
              ],
              y: [
                1,
                0.83
              ]
            },
            o: {
              x: [
                0,
                0.609
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 94.2,
            s: [
              15,
              171.681
            ],
            i: {
              x: [
                1,
                0.599
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0.272
              ],
              y: [
                0,
                0.622
              ]
            }
          },
          {
            t: 106.8,
            s: [
              15,
              167
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 216,
            s: [
              15,
              167
            ],
            i: {
              x: [
                1,
                0
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0.5
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 264,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 270.6,
            s: [
              15,
              327
            ],
            h: 1
          }
        ]
      }
    },
    w: 299,
    h: 163,
    ip: 0,
    op: 271.6,
    st: 0,
    refId: "116"
  },
  {
    ind: 120,
    ty: 4,
    parent: 119,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            1,
            1,
            1
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 119,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    parent: 0,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 0,
    ty: 3,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  }
];
const meta$5 = {
  g: "https://jitter.video"
};
const op$5 = 270.6;
const v$5 = "5.7.4";
const w$5 = 255;
const CompetitivePlacementEn = {
  assets: assets$5,
  fr: fr$5,
  h: h$5,
  ip: ip$5,
  layers: layers$5,
  meta: meta$5,
  op: op$5,
  v: v$5,
  w: w$5
};
const assets$4 = [
  {
    id: "77",
    layers: [
      {
        ind: 15,
        ty: 4,
        parent: 14,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.04
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    -0.38,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.11,
                    0.26
                  ],
                  [
                    1.02,
                    0
                  ],
                  [
                    0.33,
                    -0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.74,
                    0
                  ],
                  [
                    0,
                    -0.77
                  ]
                ],
                v: [
                  [
                    3.69,
                    5.83
                  ],
                  [
                    3.69,
                    6.15
                  ],
                  [
                    2.36,
                    6.15
                  ],
                  [
                    0.46,
                    7.54
                  ],
                  [
                    0.46,
                    7.59
                  ],
                  [
                    2.07,
                    9
                  ],
                  [
                    3.69,
                    8.08
                  ],
                  [
                    3.7,
                    8.08
                  ],
                  [
                    3.7,
                    8.92
                  ],
                  [
                    4.38,
                    8.92
                  ],
                  [
                    4.38,
                    5.82
                  ],
                  [
                    4.24,
                    5.03
                  ],
                  [
                    2.48,
                    4.04
                  ],
                  [
                    0.51,
                    5.28
                  ],
                  [
                    1.09,
                    5.6
                  ],
                  [
                    2.47,
                    4.66
                  ],
                  [
                    3.69,
                    5.83
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.85,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.81
                  ],
                  [
                    0.72,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.15
                  ],
                  [
                    -0.23,
                    -0.53
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.23,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.51,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.86,
                    0
                  ],
                  [
                    0,
                    0.54
                  ]
                ],
                v: [
                  [
                    1.16,
                    7.54
                  ],
                  [
                    2.35,
                    6.73
                  ],
                  [
                    3.69,
                    6.73
                  ],
                  [
                    3.69,
                    6.9
                  ],
                  [
                    2.2,
                    8.38
                  ],
                  [
                    1.16,
                    7.54
                  ]
                ],
                o: [
                  [
                    0,
                    -0.65
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.88
                  ],
                  [
                    -0.58,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 14,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              25.065,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 17,
        ty: 4,
        parent: 16,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.4,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    1.3
                  ]
                ],
                v: [
                  [
                    5.6,
                    6.5
                  ],
                  [
                    3.3,
                    4
                  ],
                  [
                    1.5,
                    5.2
                  ],
                  [
                    1.5,
                    4.1
                  ],
                  [
                    0.8,
                    4.1
                  ],
                  [
                    0.8,
                    11.3
                  ],
                  [
                    1.5,
                    11.3
                  ],
                  [
                    1.5,
                    7.9
                  ],
                  [
                    3.3,
                    9
                  ],
                  [
                    5.6,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1.3
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.6
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    1.1,
                    0
                  ],
                  [
                    0,
                    1
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    3.2,
                    4.7
                  ],
                  [
                    4.9,
                    6.5
                  ],
                  [
                    3.2,
                    8.4
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1
                  ],
                  [
                    1.1,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 16,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              19.008,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 19,
        ty: 4,
        parent: 18,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.18,
                    0
                  ],
                  [
                    -0.38,
                    0.66
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.84,
                    0
                  ],
                  [
                    0,
                    0.85
                  ]
                ],
                v: [
                  [
                    1.43,
                    6.92
                  ],
                  [
                    1.43,
                    4.12
                  ],
                  [
                    0.74,
                    4.12
                  ],
                  [
                    0.74,
                    7.09
                  ],
                  [
                    2.56,
                    9.01
                  ],
                  [
                    4.2,
                    8.04
                  ],
                  [
                    4.2,
                    8.92
                  ],
                  [
                    4.89,
                    8.92
                  ],
                  [
                    4.89,
                    4.12
                  ],
                  [
                    4.19,
                    4.12
                  ],
                  [
                    4.19,
                    6.86
                  ],
                  [
                    2.76,
                    8.37
                  ],
                  [
                    1.43,
                    6.92
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.13
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.85
                  ],
                  [
                    -0.89,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 18,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              13.32,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 21,
        ty: 4,
        parent: 20,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.13,
                    0
                  ],
                  [
                    -0.09,
                    -0.01
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.05,
                    0
                  ],
                  [
                    0.36,
                    -0.75
                  ]
                ],
                v: [
                  [
                    1.49,
                    5.07
                  ],
                  [
                    1.49,
                    4.12
                  ],
                  [
                    0.79,
                    4.12
                  ],
                  [
                    0.79,
                    8.92
                  ],
                  [
                    1.49,
                    8.92
                  ],
                  [
                    1.49,
                    6.57
                  ],
                  [
                    3,
                    4.78
                  ],
                  [
                    3.27,
                    4.8
                  ],
                  [
                    3.27,
                    4.05
                  ],
                  [
                    3.03,
                    4.05
                  ],
                  [
                    1.49,
                    5.07
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.86
                  ],
                  [
                    0.09,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.05,
                    -0.01
                  ],
                  [
                    -0.55,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 20,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              9.9,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 23,
        ty: 4,
        parent: 22,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.08
                  ],
                  [
                    1.49,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.39,
                    0
                  ],
                  [
                    -0.41,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.05,
                    0.94
                  ]
                ],
                v: [
                  [
                    1.15,
                    6.74
                  ],
                  [
                    5.04,
                    6.74
                  ],
                  [
                    5.05,
                    6.5
                  ],
                  [
                    2.78,
                    4.04
                  ],
                  [
                    0.44,
                    6.51
                  ],
                  [
                    2.88,
                    9
                  ],
                  [
                    4.91,
                    7.76
                  ],
                  [
                    4.39,
                    7.42
                  ],
                  [
                    2.87,
                    8.38
                  ],
                  [
                    1.15,
                    6.74
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.01,
                    -0.07
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.35,
                    0
                  ],
                  [
                    0,
                    1.39
                  ],
                  [
                    0.85,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.27,
                    0.55
                  ],
                  [
                    -0.94,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    -0.09,
                    -0.87
                  ]
                ],
                v: [
                  [
                    1.16,
                    6.14
                  ],
                  [
                    2.78,
                    4.65
                  ],
                  [
                    4.34,
                    6.14
                  ]
                ],
                o: [
                  [
                    0.12,
                    -0.8
                  ],
                  [
                    0.92,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 22,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              4.437,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 25,
        ty: 4,
        parent: 24,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.21,
                    0.21
                  ],
                  [
                    0.59,
                    0.13
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.52,
                    0
                  ],
                  [
                    -0.22,
                    -0.46
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.95,
                    0
                  ],
                  [
                    0,
                    -0.87
                  ],
                  [
                    -0.17,
                    -0.2
                  ],
                  [
                    -0.64,
                    -0.14
                  ],
                  [
                    0,
                    -0.42
                  ],
                  [
                    0.52,
                    0
                  ],
                  [
                    0.23,
                    0.48
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    0,
                    0.84
                  ]
                ],
                v: [
                  [
                    3.98,
                    7.56
                  ],
                  [
                    3.64,
                    6.76
                  ],
                  [
                    2.38,
                    6.17
                  ],
                  [
                    1.25,
                    5.41
                  ],
                  [
                    2.22,
                    4.66
                  ],
                  [
                    3.35,
                    5.47
                  ],
                  [
                    3.92,
                    5.15
                  ],
                  [
                    2.22,
                    4.04
                  ],
                  [
                    0.55,
                    5.47
                  ],
                  [
                    0.81,
                    6.19
                  ],
                  [
                    2.06,
                    6.78
                  ],
                  [
                    3.27,
                    7.6
                  ],
                  [
                    2.24,
                    8.38
                  ],
                  [
                    0.95,
                    7.45
                  ],
                  [
                    0.37,
                    7.76
                  ],
                  [
                    2.25,
                    9
                  ],
                  [
                    3.98,
                    7.56
                  ]
                ],
                o: [
                  [
                    0,
                    -0.31
                  ],
                  [
                    -0.29,
                    -0.3
                  ],
                  [
                    -0.89,
                    -0.19
                  ],
                  [
                    0,
                    -0.41
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.26,
                    -0.59
                  ],
                  [
                    -0.81,
                    0
                  ],
                  [
                    0,
                    0.27
                  ],
                  [
                    0.27,
                    0.31
                  ],
                  [
                    0.86,
                    0.2
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    -0.72,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.33,
                    0.75
                  ],
                  [
                    0.99,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 24,
        ty: 3,
        parent: 13,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 13,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              49.59,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 28,
        ty: 4,
        parent: 27,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.18,
                    0
                  ],
                  [
                    0.38,
                    -0.65
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    -0.85
                  ]
                ],
                v: [
                  [
                    4.26,
                    6.12
                  ],
                  [
                    4.26,
                    8.92
                  ],
                  [
                    4.96,
                    8.92
                  ],
                  [
                    4.96,
                    5.95
                  ],
                  [
                    3.14,
                    4.03
                  ],
                  [
                    1.49,
                    5
                  ],
                  [
                    1.49,
                    4.12
                  ],
                  [
                    0.79,
                    4.12
                  ],
                  [
                    0.79,
                    8.92
                  ],
                  [
                    1.49,
                    8.92
                  ],
                  [
                    1.49,
                    6.19
                  ],
                  [
                    2.93,
                    4.67
                  ],
                  [
                    4.26,
                    6.12
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    -0.71,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.86
                  ],
                  [
                    0.89,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 27,
        ty: 3,
        parent: 26,
        ks: {
          p: {
            a: 0,
            k: [
              41.391,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 30,
        ty: 4,
        parent: 29,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.04
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    -0.38,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.11,
                    0.26
                  ],
                  [
                    1.02,
                    0
                  ],
                  [
                    0.33,
                    -0.76
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.74,
                    0
                  ],
                  [
                    0,
                    -0.77
                  ]
                ],
                v: [
                  [
                    3.69,
                    5.83
                  ],
                  [
                    3.69,
                    6.15
                  ],
                  [
                    2.36,
                    6.15
                  ],
                  [
                    0.46,
                    7.54
                  ],
                  [
                    0.46,
                    7.59
                  ],
                  [
                    2.07,
                    9
                  ],
                  [
                    3.69,
                    8.08
                  ],
                  [
                    3.7,
                    8.08
                  ],
                  [
                    3.7,
                    8.92
                  ],
                  [
                    4.38,
                    8.92
                  ],
                  [
                    4.38,
                    5.82
                  ],
                  [
                    4.24,
                    5.03
                  ],
                  [
                    2.48,
                    4.04
                  ],
                  [
                    0.51,
                    5.28
                  ],
                  [
                    1.09,
                    5.6
                  ],
                  [
                    2.47,
                    4.66
                  ],
                  [
                    3.69,
                    5.83
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.85,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.81
                  ],
                  [
                    0.72,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.15
                  ],
                  [
                    -0.23,
                    -0.53
                  ],
                  [
                    -0.97,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.23,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.51,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.86,
                    0
                  ],
                  [
                    0,
                    0.54
                  ]
                ],
                v: [
                  [
                    1.16,
                    7.54
                  ],
                  [
                    2.35,
                    6.73
                  ],
                  [
                    3.69,
                    6.73
                  ],
                  [
                    3.69,
                    6.9
                  ],
                  [
                    2.2,
                    8.38
                  ],
                  [
                    1.16,
                    7.54
                  ]
                ],
                o: [
                  [
                    0,
                    -0.65
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.88
                  ],
                  [
                    -0.58,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 29,
        ty: 3,
        parent: 26,
        ks: {
          p: {
            a: 0,
            k: [
              36.279,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 32,
        ty: 4,
        parent: 31,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0.3,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ]
                ],
                v: [
                  [
                    4.6,
                    7.9
                  ],
                  [
                    4.6,
                    9
                  ],
                  [
                    2.8,
                    10.7
                  ],
                  [
                    1,
                    9.4
                  ],
                  [
                    0.5,
                    9.8
                  ],
                  [
                    2.9,
                    11.3
                  ],
                  [
                    5.3,
                    8.9
                  ],
                  [
                    5.3,
                    4.1
                  ],
                  [
                    4.6,
                    4.1
                  ],
                  [
                    4.6,
                    5.1
                  ],
                  [
                    2.7,
                    4
                  ],
                  [
                    0.5,
                    6.5
                  ],
                  [
                    2.7,
                    8.9
                  ],
                  [
                    4.6,
                    7.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.9
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1
                  ]
                ],
                v: [
                  [
                    1.2,
                    6.5
                  ],
                  [
                    2.8,
                    4.7
                  ],
                  [
                    4.6,
                    6.5
                  ],
                  [
                    2.8,
                    8.3
                  ],
                  [
                    1.2,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 31,
        ty: 3,
        parent: 26,
        ks: {
          p: {
            a: 0,
            k: [
              30.222,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 34,
        ty: 4,
        parent: 33,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.18,
                    0
                  ],
                  [
                    0.38,
                    -0.65
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    -0.85
                  ]
                ],
                v: [
                  [
                    4.26,
                    6.12
                  ],
                  [
                    4.26,
                    8.92
                  ],
                  [
                    4.96,
                    8.92
                  ],
                  [
                    4.96,
                    5.95
                  ],
                  [
                    3.14,
                    4.03
                  ],
                  [
                    1.49,
                    5
                  ],
                  [
                    1.49,
                    4.12
                  ],
                  [
                    0.79,
                    4.12
                  ],
                  [
                    0.79,
                    8.92
                  ],
                  [
                    1.49,
                    8.92
                  ],
                  [
                    1.49,
                    6.19
                  ],
                  [
                    2.93,
                    4.67
                  ],
                  [
                    4.26,
                    6.12
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    -0.71,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.86
                  ],
                  [
                    0.89,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 33,
        ty: 3,
        parent: 26,
        ks: {
          p: {
            a: 0,
            k: [
              24.534,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 36,
        ty: 4,
        parent: 35,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.42
                  ],
                  [
                    1.39,
                    0
                  ],
                  [
                    0,
                    -1.42
                  ],
                  [
                    -1.39,
                    0
                  ]
                ],
                v: [
                  [
                    2.92,
                    9
                  ],
                  [
                    5.41,
                    6.5
                  ],
                  [
                    2.93,
                    4.04
                  ],
                  [
                    0.44,
                    6.54
                  ],
                  [
                    2.92,
                    9
                  ]
                ],
                o: [
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.38
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    0,
                    1.37
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.04,
                    0
                  ],
                  [
                    0,
                    -1.01
                  ],
                  [
                    1.03,
                    0
                  ],
                  [
                    0,
                    1.01
                  ]
                ],
                v: [
                  [
                    1.16,
                    6.5
                  ],
                  [
                    2.92,
                    4.65
                  ],
                  [
                    4.69,
                    6.54
                  ],
                  [
                    2.93,
                    8.38
                  ],
                  [
                    1.16,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1.01
                  ],
                  [
                    1.04,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1.05,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 35,
        ty: 3,
        parent: 26,
        ks: {
          p: {
            a: 0,
            k: [
              18.684,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 38,
        ty: 4,
        parent: 37,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    1.8,
                    8.9
                  ],
                  [
                    2.6,
                    8.9
                  ],
                  [
                    3.9,
                    5
                  ],
                  [
                    3.9,
                    5
                  ],
                  [
                    5.2,
                    8.9
                  ],
                  [
                    5.9,
                    8.9
                  ],
                  [
                    7.6,
                    4.1
                  ],
                  [
                    6.8,
                    4.1
                  ],
                  [
                    5.6,
                    8.1
                  ],
                  [
                    5.6,
                    8.1
                  ],
                  [
                    4.2,
                    4.1
                  ],
                  [
                    3.5,
                    4.1
                  ],
                  [
                    2.2,
                    8.1
                  ],
                  [
                    2.2,
                    8.1
                  ],
                  [
                    1,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 37,
        ty: 3,
        parent: 26,
        ks: {
          p: {
            a: 0,
            k: [
              10.989,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 40,
        ty: 4,
        parent: 39,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.42
                  ],
                  [
                    1.39,
                    0
                  ],
                  [
                    0,
                    -1.42
                  ],
                  [
                    -1.39,
                    0
                  ]
                ],
                v: [
                  [
                    2.92,
                    9
                  ],
                  [
                    5.41,
                    6.5
                  ],
                  [
                    2.93,
                    4.04
                  ],
                  [
                    0.44,
                    6.54
                  ],
                  [
                    2.92,
                    9
                  ]
                ],
                o: [
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.38
                  ],
                  [
                    -1.42,
                    0
                  ],
                  [
                    0,
                    1.37
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.04,
                    0
                  ],
                  [
                    0,
                    -1.01
                  ],
                  [
                    1.03,
                    0
                  ],
                  [
                    0,
                    1.01
                  ]
                ],
                v: [
                  [
                    1.16,
                    6.5
                  ],
                  [
                    2.92,
                    4.65
                  ],
                  [
                    4.69,
                    6.54
                  ],
                  [
                    2.93,
                    8.38
                  ],
                  [
                    1.16,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -1.01
                  ],
                  [
                    1.04,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1.05,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 39,
        ty: 3,
        parent: 26,
        ks: {
          p: {
            a: 0,
            k: [
              5.139,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 42,
        ty: 4,
        parent: 41,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    1.7,
                    1.9
                  ],
                  [
                    1,
                    1.9
                  ],
                  [
                    1,
                    8.9
                  ],
                  [
                    5.1,
                    8.9
                  ],
                  [
                    5.1,
                    8.2
                  ],
                  [
                    1.7,
                    8.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 41,
        ty: 3,
        parent: 26,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 26,
        ty: 3,
        parent: 12,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 12,
        ty: 3,
        parent: 11,
        ks: {
          p: {
            a: 0,
            k: [
              11.616,
              0.1
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 11,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              49,
              12
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 44,
        ty: 4,
        parent: 43,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                101,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                202,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 43,
        ty: 3,
        parent: 10,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 10,
        ty: 3,
        parent: 9,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 46,
        ty: 4,
        parent: 45,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 45,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              98
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 48,
        ty: 4,
        parent: 47,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 47,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              83
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 50,
        ty: 4,
        parent: 49,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 49,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              281
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 52,
        ty: 4,
        parent: 51,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 51,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              201
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 54,
        ty: 4,
        parent: 53,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 53,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              251
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 56,
        ty: 4,
        parent: 55,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 55,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              171
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 58,
        ty: 4,
        parent: 57,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 57,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              266
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 60,
        ty: 4,
        parent: 59,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 59,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              186
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 62,
        ty: 4,
        parent: 61,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 61,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              236
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 64,
        ty: 4,
        parent: 63,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 63,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              156
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 66,
        ty: 4,
        parent: 65,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 65,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              68
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 68,
        ty: 4,
        parent: 67,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                20.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 1
            },
            s: {
              a: 0,
              k: [
                41,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 67,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              50,
              113
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 70,
        ty: 4,
        parent: 69,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                20.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 1
            },
            s: {
              a: 0,
              k: [
                41,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 69,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              113
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 72,
        ty: 4,
        parent: 71,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                10
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 71,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              218
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 74,
        ty: 4,
        parent: 73,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                10
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 73,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              138
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 76,
        ty: 4,
        parent: 75,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 75,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              47
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 9,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.8,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 106.8,
                s: [
                  10,
                  -157
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 216,
                s: [
                  10,
                  -157
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 264,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 270.6,
                s: [
                  10,
                  10
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 8,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              157
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "88",
    layers: [
      {
        ind: 87,
        ty: 4,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                5.5,
                8
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                11,
                16
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.09,
                            0.06
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.1,
                            -0.01
                          ],
                          [
                            0.09,
                            0.05
                          ],
                          [
                            0.05,
                            0.09
                          ],
                          [
                            0,
                            0.11
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.29,
                            0.32
                          ],
                          [
                            -0.43,
                            0.02
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.29,
                            -0.32
                          ],
                          [
                            0.02,
                            -0.43
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.03,
                            -0.07
                          ],
                          [
                            0.05,
                            -0.05
                          ],
                          [
                            0.07,
                            -0.03
                          ],
                          [
                            0.07,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.11,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.09,
                            0.05
                          ],
                          [
                            -0.1,
                            0
                          ],
                          [
                            -0.09,
                            -0.05
                          ],
                          [
                            -0.05,
                            -0.09
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.02,
                            -0.43
                          ],
                          [
                            0.29,
                            -0.32
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.43,
                            0.02
                          ],
                          [
                            0.29,
                            0.32
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.07
                          ],
                          [
                            -0.03,
                            0.07
                          ],
                          [
                            -0.05,
                            0.05
                          ],
                          [
                            -0.07,
                            0.03
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            7.33,
                            11.28
                          ],
                          [
                            7.02,
                            11.18
                          ],
                          [
                            3.95,
                            9.14
                          ],
                          [
                            0.88,
                            11.18
                          ],
                          [
                            0.59,
                            11.28
                          ],
                          [
                            0.3,
                            11.21
                          ],
                          [
                            0.08,
                            11
                          ],
                          [
                            0,
                            10.71
                          ],
                          [
                            0,
                            1.69
                          ],
                          [
                            0.42,
                            0.53
                          ],
                          [
                            1.53,
                            0
                          ],
                          [
                            6.36,
                            0
                          ],
                          [
                            7.48,
                            0.53
                          ],
                          [
                            7.89,
                            1.69
                          ],
                          [
                            7.89,
                            10.71
                          ],
                          [
                            7.85,
                            10.93
                          ],
                          [
                            7.73,
                            11.11
                          ],
                          [
                            7.55,
                            11.23
                          ],
                          [
                            7.33,
                            11.28
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.09,
                            -0.06
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.22,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -0.3
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.11,
                            0
                          ]
                        ],
                        o: [
                          [
                            0.11,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -0.3
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.22,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.09,
                            -0.06
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            3.95,
                            7.89
                          ],
                          [
                            4.26,
                            7.99
                          ],
                          [
                            6.77,
                            9.66
                          ],
                          [
                            6.77,
                            1.69
                          ],
                          [
                            6.36,
                            1.13
                          ],
                          [
                            1.53,
                            1.13
                          ],
                          [
                            1.13,
                            1.69
                          ],
                          [
                            1.13,
                            9.66
                          ],
                          [
                            3.64,
                            7.99
                          ],
                          [
                            3.95,
                            7.89
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.12,
                        0.28,
                        0.66,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "120",
    layers: [
      {
        ind: 90,
        ty: 0,
        parent: 86,
        ks: {},
        w: 11,
        h: 16,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "88"
      },
      {
        ind: 86,
        ty: 3,
        parent: 85,
        ks: {
          s: {
            a: 0,
            k: [
              116.35,
              100
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 85,
        ty: 3,
        parent: 84,
        ks: {
          p: {
            a: 0,
            k: [
              104,
              6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 95,
        ty: 4,
        parent: 94,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.32,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.94,
                    0
                  ],
                  [
                    -0.35,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.12,
                    0.26
                  ],
                  [
                    0.81,
                    0
                  ],
                  [
                    0.31,
                    -0.66
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.37
                  ]
                ],
                v: [
                  [
                    3.47,
                    5.67
                  ],
                  [
                    3.02,
                    6.07
                  ],
                  [
                    2.42,
                    6.07
                  ],
                  [
                    0.42,
                    7.54
                  ],
                  [
                    0.42,
                    7.59
                  ],
                  [
                    2.04,
                    9
                  ],
                  [
                    3.46,
                    8.27
                  ],
                  [
                    3.46,
                    8.92
                  ],
                  [
                    4.51,
                    8.92
                  ],
                  [
                    4.51,
                    5.86
                  ],
                  [
                    4.35,
                    5
                  ],
                  [
                    2.55,
                    4.02
                  ],
                  [
                    0.5,
                    5.24
                  ],
                  [
                    1.32,
                    5.71
                  ],
                  [
                    2.52,
                    4.95
                  ],
                  [
                    3.47,
                    5.67
                  ]
                ],
                o: [
                  [
                    0,
                    0.23
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.83
                  ],
                  [
                    0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.32
                  ],
                  [
                    -0.32,
                    -0.69
                  ],
                  [
                    -1.12,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.21,
                    -0.43
                  ],
                  [
                    0.63,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.73,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.49,
                    7.49
                  ],
                  [
                    2.42,
                    6.83
                  ],
                  [
                    3.46,
                    6.83
                  ],
                  [
                    3.46,
                    6.98
                  ],
                  [
                    2.26,
                    8.15
                  ],
                  [
                    1.49,
                    7.49
                  ]
                ],
                o: [
                  [
                    0,
                    -0.45
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.72
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 94,
        ty: 3,
        parent: 93,
        ks: {
          p: {
            a: 0,
            k: [
              18.873,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 97,
        ty: 4,
        parent: 96,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    4.4,
                    8.2
                  ],
                  [
                    4.4,
                    8.9
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    1.6
                  ],
                  [
                    4.3,
                    1.6
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.3
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 96,
        ty: 3,
        parent: 93,
        ks: {
          p: {
            a: 0,
            k: [
              12.744,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 99,
        ty: 4,
        parent: 98,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 98,
        ty: 3,
        parent: 93,
        ks: {
          p: {
            a: 0,
            k: [
              7.047,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 101,
        ty: 4,
        parent: 100,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0,
                    8.9
                  ],
                  [
                    1.1,
                    8.9
                  ],
                  [
                    1.8,
                    7.3
                  ],
                  [
                    5.2,
                    7.3
                  ],
                  [
                    5.9,
                    8.9
                  ],
                  [
                    7,
                    8.9
                  ],
                  [
                    4.2,
                    1.9
                  ],
                  [
                    2.9,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.2,
                    6.3
                  ],
                  [
                    3.5,
                    2.9
                  ],
                  [
                    4.9,
                    6.3
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 100,
        ty: 3,
        parent: 93,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 93,
        ty: 3,
        parent: 92,
        ks: {
          p: {
            a: 0,
            k: [
              49.518,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 104,
        ty: 4,
        parent: 103,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 103,
        ty: 3,
        parent: 102,
        ks: {
          p: {
            a: 0,
            k: [
              41.688,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 106,
        ty: 4,
        parent: 105,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.32,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.12
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.94,
                    0
                  ],
                  [
                    -0.35,
                    0.59
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.12,
                    0.26
                  ],
                  [
                    0.81,
                    0
                  ],
                  [
                    0.31,
                    -0.66
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.37
                  ]
                ],
                v: [
                  [
                    3.47,
                    5.67
                  ],
                  [
                    3.02,
                    6.07
                  ],
                  [
                    2.42,
                    6.07
                  ],
                  [
                    0.42,
                    7.54
                  ],
                  [
                    0.42,
                    7.59
                  ],
                  [
                    2.04,
                    9
                  ],
                  [
                    3.46,
                    8.27
                  ],
                  [
                    3.46,
                    8.92
                  ],
                  [
                    4.51,
                    8.92
                  ],
                  [
                    4.51,
                    5.86
                  ],
                  [
                    4.35,
                    5
                  ],
                  [
                    2.55,
                    4.02
                  ],
                  [
                    0.5,
                    5.24
                  ],
                  [
                    1.32,
                    5.71
                  ],
                  [
                    2.52,
                    4.95
                  ],
                  [
                    3.47,
                    5.67
                  ]
                ],
                o: [
                  [
                    0,
                    0.23
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.83
                  ],
                  [
                    0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.32
                  ],
                  [
                    -0.32,
                    -0.69
                  ],
                  [
                    -1.12,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.21,
                    -0.43
                  ],
                  [
                    0.63,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.73,
                    0
                  ],
                  [
                    0,
                    0.4
                  ]
                ],
                v: [
                  [
                    1.49,
                    7.49
                  ],
                  [
                    2.42,
                    6.83
                  ],
                  [
                    3.46,
                    6.83
                  ],
                  [
                    3.46,
                    6.98
                  ],
                  [
                    2.26,
                    8.15
                  ],
                  [
                    1.49,
                    7.49
                  ]
                ],
                o: [
                  [
                    0,
                    -0.45
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.72
                  ],
                  [
                    -0.45,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 105,
        ty: 3,
        parent: 102,
        ks: {
          p: {
            a: 0,
            k: [
              36.513,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 108,
        ty: 4,
        parent: 107,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0.2,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -1.4,
                    0
                  ]
                ],
                v: [
                  [
                    2.7,
                    9
                  ],
                  [
                    4.3,
                    8.1
                  ],
                  [
                    4.3,
                    8.9
                  ],
                  [
                    2.8,
                    10.4
                  ],
                  [
                    1.3,
                    9.4
                  ],
                  [
                    0.4,
                    9.9
                  ],
                  [
                    2.9,
                    11.3
                  ],
                  [
                    5.4,
                    8.9
                  ],
                  [
                    5.4,
                    4.1
                  ],
                  [
                    4.3,
                    4.1
                  ],
                  [
                    4.3,
                    4.8
                  ],
                  [
                    2.8,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.7,
                    9
                  ]
                ],
                o: [
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.9
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.3,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 107,
        ty: 3,
        parent: 102,
        ks: {
          p: {
            a: 0,
            k: [
              30.42,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 110,
        ty: 4,
        parent: 109,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.22,
                    0
                  ],
                  [
                    0.46,
                    -0.62
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.67,
                    0
                  ],
                  [
                    0,
                    -0.68
                  ]
                ],
                v: [
                  [
                    3.96,
                    6.23
                  ],
                  [
                    3.96,
                    8.92
                  ],
                  [
                    5.03,
                    8.92
                  ],
                  [
                    5.03,
                    5.96
                  ],
                  [
                    3.23,
                    4.02
                  ],
                  [
                    1.76,
                    4.77
                  ],
                  [
                    1.76,
                    4.12
                  ],
                  [
                    0.72,
                    4.12
                  ],
                  [
                    0.72,
                    8.92
                  ],
                  [
                    1.79,
                    8.92
                  ],
                  [
                    1.79,
                    6.25
                  ],
                  [
                    2.9,
                    5.04
                  ],
                  [
                    3.96,
                    6.23
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.09
                  ],
                  [
                    -0.38,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.73
                  ],
                  [
                    0.74,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 109,
        ty: 3,
        parent: 102,
        ks: {
          p: {
            a: 0,
            k: [
              24.723,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 112,
        ty: 4,
        parent: 111,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.9,
                    9
                  ],
                  [
                    5.5,
                    6.5
                  ],
                  [
                    2.9,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.4
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 111,
        ty: 3,
        parent: 102,
        ks: {
          p: {
            a: 0,
            k: [
              18.873,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 114,
        ty: 4,
        parent: 113,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    6.6,
                    4.1
                  ],
                  [
                    5.6,
                    7.5
                  ],
                  [
                    5.6,
                    7.5
                  ],
                  [
                    4.5,
                    4.1
                  ],
                  [
                    3.4,
                    4.1
                  ],
                  [
                    2.3,
                    7.5
                  ],
                  [
                    2.3,
                    7.5
                  ],
                  [
                    1.3,
                    4.1
                  ],
                  [
                    0.2,
                    4.1
                  ],
                  [
                    1.7,
                    8.9
                  ],
                  [
                    2.8,
                    8.9
                  ],
                  [
                    3.9,
                    5.5
                  ],
                  [
                    4,
                    5.5
                  ],
                  [
                    5.1,
                    8.9
                  ],
                  [
                    6.2,
                    8.9
                  ],
                  [
                    7.7,
                    4.1
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 113,
        ty: 3,
        parent: 102,
        ks: {
          p: {
            a: 0,
            k: [
              11.07,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 116,
        ty: 4,
        parent: 115,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ]
                ],
                v: [
                  [
                    0.4,
                    6.5
                  ],
                  [
                    2.9,
                    9
                  ],
                  [
                    5.5,
                    6.5
                  ],
                  [
                    2.9,
                    4
                  ],
                  [
                    0.4,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    1.4
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.5
                  ],
                  [
                    2.9,
                    5
                  ],
                  [
                    4.4,
                    6.5
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    1.5,
                    6.5
                  ]
                ],
                o: [
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 115,
        ty: 3,
        parent: 102,
        ks: {
          p: {
            a: 0,
            k: [
              5.328,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 118,
        ty: 4,
        parent: 117,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2,
                    7.9
                  ],
                  [
                    2,
                    1.9
                  ],
                  [
                    0.9,
                    1.9
                  ],
                  [
                    0.9,
                    8.9
                  ],
                  [
                    5.2,
                    8.9
                  ],
                  [
                    5.2,
                    7.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 117,
        ty: 3,
        parent: 102,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 102,
        ty: 3,
        parent: 92,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 92,
        ty: 3,
        parent: 91,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              0.6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 91,
        ty: 3,
        parent: 84,
        ks: {
          p: {
            a: 0,
            k: [
              8,
              6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 119,
        ty: 4,
        parent: 84,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    62,
                    28
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    62,
                    28
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    124,
                    56
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    124,
                    56
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 84,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              62,
              28
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "124",
    layers: [
      {
        ind: 83,
        ty: 4,
        parent: 82,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    31,
                    14
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    188,
                    86
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 94.2,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 142.2,
                      s: [
                        62,
                        28
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 270.6,
                      s: [
                        62,
                        28
                      ],
                      h: 1
                    }
                  ]
                },
                r: {
                  a: 0,
                  k: 4
                },
                s: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 94.2,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 142.2,
                      s: [
                        124,
                        56
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 270.6,
                      s: [
                        124,
                        56
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.122,
                    0.278,
                    0.655
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 123,
        ty: 4,
        td: 1,
        parent: 82,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    62,
                    28
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    62,
                    28
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    124,
                    56
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    124,
                    56
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 122,
        ty: 0,
        tt: 1,
        parent: 82,
        ks: {
          a: {
            a: 0,
            k: [
              62,
              28
            ]
          }
        },
        w: 186,
        h: 84,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "120"
      },
      {
        ind: 82,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              63,
              29
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "128",
    layers: [
      {
        ind: 126,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              63,
              29
            ]
          },
          p: {
            a: 0,
            k: [
              86.211,
              52.211
            ]
          }
        },
        w: 188,
        h: 86,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "124"
      },
      {
        ind: 127,
        ty: 4,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                148.711,
                80.711
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                297.422,
                161.422
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "131",
    layers: [
      {
        ind: 130,
        ty: 0,
        parent: 81,
        ks: {
          p: {
            a: 0,
            k: [
              -86.211,
              -52.211
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.5,
                        y: 0
                      }
                    },
                    {
                      t: 150,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        0
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.5,
                        y: 0
                      }
                    },
                    {
                      t: 150,
                      s: [
                        26
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        26
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        135
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        135
                      ],
                      i: {
                        x: 0.842,
                        y: 0.419
                      },
                      o: {
                        x: 0.597,
                        y: 0
                      }
                    },
                    {
                      t: 114,
                      s: [
                        138.04
                      ],
                      i: {
                        x: 0.236,
                        y: 0.769
                      },
                      o: {
                        x: 0.222,
                        y: 0.36
                      }
                    },
                    {
                      t: 126,
                      s: [
                        144.95
                      ],
                      i: {
                        x: 0.494,
                        y: 1
                      },
                      o: {
                        x: 0.192,
                        y: 0.59
                      }
                    },
                    {
                      t: 150,
                      s: [
                        146.31
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        146.31
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        5.657
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        5.657
                      ],
                      i: {
                        x: 0.373,
                        y: 0.653
                      },
                      o: {
                        x: 0.767,
                        y: 0
                      }
                    },
                    {
                      t: 126,
                      s: [
                        6.965
                      ],
                      i: {
                        x: 0.58,
                        y: 1
                      },
                      o: {
                        x: 0.24,
                        y: 0.704
                      }
                    },
                    {
                      t: 150,
                      s: [
                        7.211
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        7.211
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 16
                }
              }
            ]
          }
        ],
        w: 297.4222,
        h: 161.4222,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "128"
      },
      {
        ind: 81,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              87,
              53
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  }
];
const fr$4 = 60;
const h$4 = 238;
const ip$4 = 0;
const layers$4 = [
  {
    ind: 4,
    ty: 4,
    td: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 3,
    ty: 4,
    tt: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 7,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                111,
                102
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                222,
                204
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 6,
    ty: 4,
    tt: 2,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                111,
                86
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                226,
                240
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 58.8,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 106.8,
                  s: [
                    111,
                    70
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 216,
                  s: [
                    111,
                    70
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 264,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    111,
                    102
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 58.8,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 106.8,
                  s: [
                    222,
                    140
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 216,
                  s: [
                    222,
                    140
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 264,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    222,
                    204
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 80,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                111,
                102
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                222,
                204
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 79,
    ty: 0,
    tt: 1,
    parent: 5,
    ks: {
      a: {
        a: 0,
        k: [
          0,
          157
        ]
      }
    },
    w: 212,
    h: 455,
    ip: 0,
    op: 271.6,
    st: 0,
    refId: "77"
  },
  {
    ind: 5,
    ty: 3,
    parent: 1,
    ks: {
      p: {
        a: 0,
        k: [
          17,
          17
        ]
      }
    },
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 133,
    ty: 0,
    parent: 1,
    ks: {
      a: {
        a: 0,
        k: [
          87,
          53
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 94.2,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 99.6,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 270.6,
            s: [
              100
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 58.8,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                0.125
              ],
              y: [
                1,
                0.83
              ]
            },
            o: {
              x: [
                0,
                0.609
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 94.2,
            s: [
              15,
              171.681
            ],
            i: {
              x: [
                1,
                0.599
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0.272
              ],
              y: [
                0,
                0.622
              ]
            }
          },
          {
            t: 106.8,
            s: [
              15,
              167
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 216,
            s: [
              15,
              167
            ],
            i: {
              x: [
                1,
                0
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0.5
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 264,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 270.6,
            s: [
              15,
              327
            ],
            h: 1
          }
        ]
      }
    },
    w: 299,
    h: 163,
    ip: 0,
    op: 271.6,
    st: 0,
    refId: "131"
  },
  {
    ind: 135,
    ty: 4,
    parent: 134,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            1,
            1,
            1
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 134,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    parent: 0,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 0,
    ty: 3,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  }
];
const meta$4 = {
  g: "https://jitter.video"
};
const op$4 = 270.6;
const v$4 = "5.7.4";
const w$4 = 255;
const CompetitivePlacementId = {
  assets: assets$4,
  fr: fr$4,
  h: h$4,
  ip: ip$4,
  layers: layers$4,
  meta: meta$4,
  op: op$4,
  v: v$4,
  w: w$4
};
const assets$3 = [
  {
    id: "74",
    layers: [
      {
        ind: 15,
        ty: 4,
        parent: 14,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    -0.4,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    3.4
                  ],
                  [
                    0.8,
                    7
                  ],
                  [
                    2.7,
                    9
                  ],
                  [
                    4.4,
                    8
                  ],
                  [
                    4.4,
                    8.9
                  ],
                  [
                    5.1,
                    8.9
                  ],
                  [
                    5.1,
                    3.4
                  ],
                  [
                    4.4,
                    3.4
                  ],
                  [
                    4.4,
                    6.7
                  ],
                  [
                    2.9,
                    8.3
                  ],
                  [
                    1.5,
                    6.8
                  ],
                  [
                    1.5,
                    3.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 14,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              67.203,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 17,
        ty: 4,
        parent: 16,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.1,
                    -1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    5.4
                  ],
                  [
                    1.1,
                    5
                  ],
                  [
                    2.9,
                    3.9
                  ],
                  [
                    4.5,
                    5.5
                  ],
                  [
                    4.5,
                    8.9
                  ],
                  [
                    5.3,
                    8.9
                  ],
                  [
                    5.3,
                    5.5
                  ],
                  [
                    2.9,
                    3.3
                  ],
                  [
                    0.4,
                    5.4
                  ],
                  [
                    1.3,
                    5.8
                  ],
                  [
                    1.3,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.7
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.1
                  ],
                  [
                    -0.8,
                    0
                  ]
                ],
                v: [
                  [
                    4,
                    2.4
                  ],
                  [
                    6.3,
                    2.4
                  ],
                  [
                    6.3,
                    1.7
                  ],
                  [
                    4.1,
                    1.7
                  ],
                  [
                    3.6,
                    1.3
                  ],
                  [
                    3.6,
                    1
                  ],
                  [
                    2.9,
                    1
                  ],
                  [
                    2.9,
                    1.4
                  ],
                  [
                    4,
                    2.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    -0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.1
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 16,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              61.191,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 19,
        ty: 4,
        parent: 18,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    -0.6,
                    -0.2
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    -0.2,
                    -0.1
                  ]
                ],
                v: [
                  [
                    2.8,
                    4
                  ],
                  [
                    2.8,
                    3.4
                  ],
                  [
                    2.2,
                    3.3
                  ],
                  [
                    0.7,
                    4.7
                  ],
                  [
                    1.7,
                    6.1
                  ],
                  [
                    0.7,
                    7.4
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    5.2,
                    6.9
                  ],
                  [
                    5.2,
                    3.4
                  ],
                  [
                    4.4,
                    3.4
                  ],
                  [
                    4.4,
                    6.9
                  ],
                  [
                    2.8,
                    8.4
                  ],
                  [
                    1.5,
                    7.3
                  ],
                  [
                    2.4,
                    6.3
                  ],
                  [
                    3,
                    6.3
                  ],
                  [
                    3,
                    5.7
                  ],
                  [
                    2.4,
                    5.7
                  ],
                  [
                    1.3,
                    4.8
                  ],
                  [
                    2.3,
                    3.9
                  ],
                  [
                    2.8,
                    4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    -0.6,
                    0.2
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 18,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              55.269,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 21,
        ty: 4,
        parent: 20,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0.4,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ]
                ],
                v: [
                  [
                    3.4,
                    5.2
                  ],
                  [
                    3.4,
                    8.9
                  ],
                  [
                    4.1,
                    8.9
                  ],
                  [
                    4.1,
                    5.1
                  ],
                  [
                    2.1,
                    3.3
                  ],
                  [
                    0.2,
                    4.5
                  ],
                  [
                    0.8,
                    4.8
                  ],
                  [
                    2.1,
                    3.9
                  ],
                  [
                    3.4,
                    5.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 20,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              50.391,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 23,
        ty: 4,
        parent: 22,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0.4,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ]
                ],
                v: [
                  [
                    0.5,
                    7.3
                  ],
                  [
                    0.5,
                    7.4
                  ],
                  [
                    2.3,
                    9
                  ],
                  [
                    2.9,
                    8.9
                  ],
                  [
                    2.9,
                    8.3
                  ],
                  [
                    2.4,
                    8.3
                  ],
                  [
                    1.2,
                    7.3
                  ],
                  [
                    2.7,
                    6.4
                  ],
                  [
                    4.3,
                    6.4
                  ],
                  [
                    4.3,
                    8.9
                  ],
                  [
                    5,
                    8.9
                  ],
                  [
                    5,
                    5.3
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    0.5,
                    4.7
                  ],
                  [
                    1.1,
                    5
                  ],
                  [
                    2.8,
                    3.9
                  ],
                  [
                    4.3,
                    5.4
                  ],
                  [
                    4.3,
                    5.8
                  ],
                  [
                    2.7,
                    5.8
                  ],
                  [
                    0.5,
                    7.3
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.6
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    5.7,
                    2.4
                  ],
                  [
                    5.7,
                    1.8
                  ],
                  [
                    3.7,
                    1.8
                  ],
                  [
                    3.7,
                    1.5
                  ],
                  [
                    2.8,
                    0.7
                  ],
                  [
                    2.4,
                    0.7
                  ],
                  [
                    2.4,
                    1.3
                  ],
                  [
                    2.7,
                    1.3
                  ],
                  [
                    3,
                    1.6
                  ],
                  [
                    3,
                    2.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 22,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              44.604,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 25,
        ty: 4,
        parent: 24,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.6
                  ]
                ],
                v: [
                  [
                    1.5,
                    6.6
                  ],
                  [
                    1.5,
                    5.5
                  ],
                  [
                    3,
                    3.9
                  ],
                  [
                    4.6,
                    5.5
                  ],
                  [
                    4.6,
                    8.9
                  ],
                  [
                    5.3,
                    8.9
                  ],
                  [
                    5.3,
                    5.5
                  ],
                  [
                    3.1,
                    3.3
                  ],
                  [
                    0.8,
                    5.5
                  ],
                  [
                    0.8,
                    8.9
                  ],
                  [
                    1.5,
                    8.9
                  ],
                  [
                    1.5,
                    7.8
                  ],
                  [
                    3,
                    6.5
                  ],
                  [
                    3.4,
                    6.5
                  ],
                  [
                    3.4,
                    5.8
                  ],
                  [
                    3,
                    5.8
                  ],
                  [
                    1.5,
                    6.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 24,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              38.511,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 27,
        ty: 4,
        parent: 26,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0.4,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    5.2,
                    8.9
                  ],
                  [
                    5.2,
                    5.3
                  ],
                  [
                    3.3,
                    3.3
                  ],
                  [
                    1.5,
                    4.3
                  ],
                  [
                    1.5,
                    3.4
                  ],
                  [
                    0.8,
                    3.4
                  ],
                  [
                    0.8,
                    8.9
                  ],
                  [
                    1.5,
                    8.9
                  ],
                  [
                    1.5,
                    5.5
                  ],
                  [
                    3.1,
                    3.9
                  ],
                  [
                    4.5,
                    5.5
                  ],
                  [
                    4.5,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    1.1,
                    2.4
                  ],
                  [
                    5.2,
                    2.4
                  ],
                  [
                    5.2,
                    0.9
                  ],
                  [
                    4.5,
                    0.9
                  ],
                  [
                    4.5,
                    1.7
                  ],
                  [
                    1.1,
                    1.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.5,
                    0.6
                  ],
                  [
                    4.5,
                    2.4
                  ],
                  [
                    5.2,
                    2.4
                  ],
                  [
                    5.2,
                    0.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 26,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              32.562,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 29,
        ty: 4,
        parent: 28,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    -1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0.3
                  ],
                  [
                    -0.5,
                    0
                  ]
                ],
                v: [
                  [
                    6.2,
                    3.9
                  ],
                  [
                    6.2,
                    3.3
                  ],
                  [
                    4.7,
                    3.8
                  ],
                  [
                    3.1,
                    3.3
                  ],
                  [
                    0.8,
                    5.5
                  ],
                  [
                    0.8,
                    8.9
                  ],
                  [
                    1.5,
                    8.9
                  ],
                  [
                    1.5,
                    7.8
                  ],
                  [
                    3,
                    6.5
                  ],
                  [
                    3.4,
                    6.5
                  ],
                  [
                    3.4,
                    5.8
                  ],
                  [
                    3,
                    5.8
                  ],
                  [
                    1.5,
                    6.6
                  ],
                  [
                    1.5,
                    5.5
                  ],
                  [
                    3,
                    3.9
                  ],
                  [
                    4.6,
                    5.5
                  ],
                  [
                    4.6,
                    8.9
                  ],
                  [
                    5.3,
                    8.9
                  ],
                  [
                    5.3,
                    5.5
                  ],
                  [
                    5.1,
                    4.3
                  ],
                  [
                    6.2,
                    3.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    -0.4,
                    -0.4
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0.2,
                    -0.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 28,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              26.055,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 31,
        ty: 4,
        parent: 30,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0.4,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ]
                ],
                v: [
                  [
                    3.4,
                    5.2
                  ],
                  [
                    3.4,
                    8.9
                  ],
                  [
                    4.1,
                    8.9
                  ],
                  [
                    4.1,
                    5.1
                  ],
                  [
                    2.1,
                    3.3
                  ],
                  [
                    0.2,
                    4.5
                  ],
                  [
                    0.8,
                    4.8
                  ],
                  [
                    2.1,
                    3.9
                  ],
                  [
                    3.4,
                    5.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 30,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              21.177,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 33,
        ty: 4,
        parent: 32,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.1,
                    -1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2,
                    8.9
                  ],
                  [
                    2,
                    5.4
                  ],
                  [
                    1.1,
                    5
                  ],
                  [
                    2.9,
                    3.9
                  ],
                  [
                    4.5,
                    5.5
                  ],
                  [
                    4.5,
                    8.9
                  ],
                  [
                    5.3,
                    8.9
                  ],
                  [
                    5.3,
                    5.5
                  ],
                  [
                    2.9,
                    3.3
                  ],
                  [
                    0.4,
                    5.4
                  ],
                  [
                    1.3,
                    5.8
                  ],
                  [
                    1.3,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.7
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 32,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              15.165,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 35,
        ty: 4,
        parent: 34,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.11
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.24
                  ],
                  [
                    -0.01,
                    0.06
                  ]
                ],
                v: [
                  [
                    1.48,
                    6.72
                  ],
                  [
                    0.8,
                    6.72
                  ],
                  [
                    0.77,
                    7.02
                  ],
                  [
                    1.7,
                    7.87
                  ],
                  [
                    3.28,
                    7.87
                  ],
                  [
                    3.28,
                    7.24
                  ],
                  [
                    1.85,
                    7.24
                  ],
                  [
                    1.46,
                    6.9
                  ],
                  [
                    1.48,
                    6.72
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.03,
                    0.09
                  ],
                  [
                    0,
                    0.48
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.24,
                    0
                  ],
                  [
                    0,
                    -0.05
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.66,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.24
                  ],
                  [
                    -0.01,
                    0.06
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.11
                  ]
                ],
                v: [
                  [
                    0.77,
                    4.54
                  ],
                  [
                    1.7,
                    5.39
                  ],
                  [
                    3.28,
                    5.39
                  ],
                  [
                    3.28,
                    4.76
                  ],
                  [
                    1.85,
                    4.76
                  ],
                  [
                    1.46,
                    4.41
                  ],
                  [
                    1.48,
                    4.23
                  ],
                  [
                    0.8,
                    4.23
                  ],
                  [
                    0.77,
                    4.54
                  ]
                ],
                o: [
                  [
                    0,
                    0.48
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.24,
                    0
                  ],
                  [
                    0,
                    -0.06
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.03,
                    0.09
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 34,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              11.124,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 37,
        ty: 4,
        parent: 36,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0.7,
                    0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    -0.2,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.1,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    -0.7,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.3,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ]
                ],
                v: [
                  [
                    2.6,
                    9
                  ],
                  [
                    4.6,
                    7.4
                  ],
                  [
                    3,
                    5.9
                  ],
                  [
                    2.3,
                    5.7
                  ],
                  [
                    1.3,
                    4.9
                  ],
                  [
                    2.6,
                    3.9
                  ],
                  [
                    4,
                    4.9
                  ],
                  [
                    4.6,
                    4.6
                  ],
                  [
                    2.6,
                    3.3
                  ],
                  [
                    0.6,
                    4.9
                  ],
                  [
                    2.1,
                    6.3
                  ],
                  [
                    2.8,
                    6.5
                  ],
                  [
                    3.9,
                    7.4
                  ],
                  [
                    2.6,
                    8.4
                  ],
                  [
                    1,
                    7.3
                  ],
                  [
                    0.4,
                    7.6
                  ],
                  [
                    2.6,
                    9
                  ]
                ],
                o: [
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    -0.2
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    -0.6
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0.2
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 36,
        ty: 3,
        parent: 13,
        ks: {
          p: {
            a: 0,
            k: [
              6.039,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 39,
        ty: 4,
        parent: 38,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    6.8
                  ],
                  [
                    3,
                    9
                  ],
                  [
                    5.3,
                    6.8
                  ],
                  [
                    5.3,
                    1.7
                  ],
                  [
                    4.6,
                    1.7
                  ],
                  [
                    4.6,
                    6.8
                  ],
                  [
                    3,
                    8.3
                  ],
                  [
                    1.5,
                    6.8
                  ],
                  [
                    1.5,
                    3.4
                  ],
                  [
                    0.8,
                    3.4
                  ]
                ],
                o: [
                  [
                    0,
                    1.3
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.361,
                0.412,
                0.502
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 38,
        ty: 3,
        parent: 13,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 13,
        ty: 3,
        parent: 12,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 12,
        ty: 3,
        parent: 11,
        ks: {
          p: {
            a: 0,
            k: [
              -3.076,
              0.1
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 11,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              67,
              12
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 41,
        ty: 4,
        parent: 40,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                101,
                17.5
              ]
            },
            r: {
              a: 0,
              k: 3
            },
            s: {
              a: 0,
              k: [
                202,
                35
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 40,
        ty: 3,
        parent: 10,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 10,
        ty: 3,
        parent: 9,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 43,
        ty: 4,
        parent: 42,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 42,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              98
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 45,
        ty: 4,
        parent: 44,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 44,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              83
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 47,
        ty: 4,
        parent: 46,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 46,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              281
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 49,
        ty: 4,
        parent: 48,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 48,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              201
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 51,
        ty: 4,
        parent: 50,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 50,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              251
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 53,
        ty: 4,
        parent: 52,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 52,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              171
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 55,
        ty: 4,
        parent: 54,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 54,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              266
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 57,
        ty: 4,
        parent: 56,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 56,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              186
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 59,
        ty: 4,
        parent: 58,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 58,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              236
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 61,
        ty: 4,
        parent: 60,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                100.5,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                201,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 60,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              156
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 63,
        ty: 4,
        parent: 62,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                48,
                3.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                96,
                7
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 62,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              68
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 65,
        ty: 4,
        parent: 64,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                20.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 1
            },
            s: {
              a: 0,
              k: [
                41,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 64,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              50,
              113
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 67,
        ty: 4,
        parent: 66,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                20.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 1
            },
            s: {
              a: 0,
              k: [
                41,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 66,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              113
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 69,
        ty: 4,
        parent: 68,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                10
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 68,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              218
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 71,
        ty: 4,
        parent: 70,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                10
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 70,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              138
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 73,
        ty: 4,
        parent: 72,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                27.5,
                6.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                55,
                13
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.933,
                0.941,
                0.957
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 72,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              47
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 9,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 58.8,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 106.8,
                s: [
                  10,
                  -157
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 216,
                s: [
                  10,
                  -157
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 264,
                s: [
                  10,
                  10
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 270.6,
                s: [
                  10,
                  10
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 8,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              157
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "85",
    layers: [
      {
        ind: 84,
        ty: 4,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                5.5,
                8
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                11,
                16
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.09,
                            0.06
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.1,
                            -0.01
                          ],
                          [
                            0.09,
                            0.05
                          ],
                          [
                            0.05,
                            0.09
                          ],
                          [
                            0,
                            0.11
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.29,
                            0.32
                          ],
                          [
                            -0.43,
                            0.02
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.29,
                            -0.32
                          ],
                          [
                            0.02,
                            -0.43
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.03,
                            -0.07
                          ],
                          [
                            0.05,
                            -0.05
                          ],
                          [
                            0.07,
                            -0.03
                          ],
                          [
                            0.07,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.11,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.09,
                            0.05
                          ],
                          [
                            -0.1,
                            0
                          ],
                          [
                            -0.09,
                            -0.05
                          ],
                          [
                            -0.05,
                            -0.09
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.02,
                            -0.43
                          ],
                          [
                            0.29,
                            -0.32
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.43,
                            0.02
                          ],
                          [
                            0.29,
                            0.32
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.07
                          ],
                          [
                            -0.03,
                            0.07
                          ],
                          [
                            -0.05,
                            0.05
                          ],
                          [
                            -0.07,
                            0.03
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            7.33,
                            11.28
                          ],
                          [
                            7.02,
                            11.18
                          ],
                          [
                            3.95,
                            9.14
                          ],
                          [
                            0.88,
                            11.18
                          ],
                          [
                            0.59,
                            11.28
                          ],
                          [
                            0.3,
                            11.21
                          ],
                          [
                            0.08,
                            11
                          ],
                          [
                            0,
                            10.71
                          ],
                          [
                            0,
                            1.69
                          ],
                          [
                            0.42,
                            0.53
                          ],
                          [
                            1.53,
                            0
                          ],
                          [
                            6.36,
                            0
                          ],
                          [
                            7.48,
                            0.53
                          ],
                          [
                            7.89,
                            1.69
                          ],
                          [
                            7.89,
                            10.71
                          ],
                          [
                            7.85,
                            10.93
                          ],
                          [
                            7.73,
                            11.11
                          ],
                          [
                            7.55,
                            11.23
                          ],
                          [
                            7.33,
                            11.28
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.09,
                            -0.06
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.22,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -0.3
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.11,
                            0
                          ]
                        ],
                        o: [
                          [
                            0.11,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -0.3
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.22,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.09,
                            -0.06
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            3.95,
                            7.89
                          ],
                          [
                            4.26,
                            7.99
                          ],
                          [
                            6.77,
                            9.66
                          ],
                          [
                            6.77,
                            1.69
                          ],
                          [
                            6.36,
                            1.13
                          ],
                          [
                            1.53,
                            1.13
                          ],
                          [
                            1.13,
                            1.69
                          ],
                          [
                            1.13,
                            9.66
                          ],
                          [
                            3.64,
                            7.99
                          ],
                          [
                            3.95,
                            7.89
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        0.12,
                        0.28,
                        0.66,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "114",
    layers: [
      {
        ind: 87,
        ty: 0,
        parent: 83,
        ks: {},
        w: 11,
        h: 16,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "85"
      },
      {
        ind: 83,
        ty: 3,
        parent: 82,
        ks: {
          s: {
            a: 0,
            k: [
              116.35,
              100
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 82,
        ty: 3,
        parent: 81,
        ks: {
          p: {
            a: 0,
            k: [
              104,
              6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 92,
        ty: 4,
        parent: 91,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.1,
                    -1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.7
                  ]
                ],
                v: [
                  [
                    5.5,
                    6.7
                  ],
                  [
                    5.5,
                    5.6
                  ],
                  [
                    3,
                    3.3
                  ],
                  [
                    0.3,
                    5.5
                  ],
                  [
                    1.2,
                    5.9
                  ],
                  [
                    1.2,
                    7.6
                  ],
                  [
                    2.5,
                    9
                  ],
                  [
                    2.9,
                    9
                  ],
                  [
                    2.9,
                    8
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    2.3,
                    7.5
                  ],
                  [
                    2.3,
                    5.5
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    2.9,
                    4.3
                  ],
                  [
                    4.4,
                    5.7
                  ],
                  [
                    4.4,
                    6.9
                  ],
                  [
                    6.3,
                    9
                  ],
                  [
                    7.9,
                    8.1
                  ],
                  [
                    7.9,
                    8.1
                  ],
                  [
                    7.9,
                    8.9
                  ],
                  [
                    9,
                    8.9
                  ],
                  [
                    9,
                    3.4
                  ],
                  [
                    7.9,
                    3.4
                  ],
                  [
                    7.9,
                    6.7
                  ],
                  [
                    6.6,
                    8
                  ],
                  [
                    5.5,
                    6.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.5
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 91,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              56.835,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 94,
        ty: 4,
        parent: 93,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ]
                ],
                v: [
                  [
                    3.1,
                    5.8
                  ],
                  [
                    1.8,
                    6.5
                  ],
                  [
                    1.8,
                    5.6
                  ],
                  [
                    3.1,
                    4.3
                  ],
                  [
                    4.5,
                    5.6
                  ],
                  [
                    4.5,
                    8.9
                  ],
                  [
                    5.6,
                    8.9
                  ],
                  [
                    5.6,
                    5.6
                  ],
                  [
                    3.1,
                    3.3
                  ],
                  [
                    0.7,
                    5.6
                  ],
                  [
                    0.7,
                    8.9
                  ],
                  [
                    1.8,
                    8.9
                  ],
                  [
                    1.8,
                    7.8
                  ],
                  [
                    3,
                    6.8
                  ],
                  [
                    3.4,
                    6.8
                  ],
                  [
                    3.4,
                    5.8
                  ],
                  [
                    3.1,
                    5.8
                  ]
                ],
                o: [
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.3
                  ]
                ],
                v: [
                  [
                    4.5,
                    10.9
                  ],
                  [
                    4.5,
                    11.9
                  ],
                  [
                    5.5,
                    11.9
                  ],
                  [
                    5.5,
                    10.7
                  ],
                  [
                    4.4,
                    9.6
                  ],
                  [
                    3.9,
                    9.6
                  ],
                  [
                    3.9,
                    10.5
                  ],
                  [
                    4.1,
                    10.5
                  ],
                  [
                    4.5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 93,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              50.607,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 96,
        ty: 4,
        parent: 95,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.6,
                    8.9
                  ],
                  [
                    5.2,
                    6
                  ],
                  [
                    2.8,
                    3.3
                  ],
                  [
                    2.3,
                    3.3
                  ],
                  [
                    2.3,
                    4.3
                  ],
                  [
                    2.6,
                    4.3
                  ],
                  [
                    4.1,
                    6
                  ],
                  [
                    2.6,
                    7.9
                  ],
                  [
                    2.5,
                    7.9
                  ],
                  [
                    1.3,
                    4.9
                  ],
                  [
                    0.2,
                    4.9
                  ],
                  [
                    1.8,
                    8.9
                  ]
                ],
                o: [
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    -1.8
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 95,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              44.856,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 98,
        ty: 4,
        parent: 97,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0.4,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    1.1,
                    0
                  ],
                  [
                    0.1,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.1
                  ],
                  [
                    -1.7,
                    0
                  ]
                ],
                v: [
                  [
                    3,
                    9
                  ],
                  [
                    5.7,
                    6.1
                  ],
                  [
                    3,
                    3.3
                  ],
                  [
                    0.6,
                    4.6
                  ],
                  [
                    1.5,
                    5.2
                  ],
                  [
                    3,
                    4.3
                  ],
                  [
                    4.6,
                    6.1
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    1.5,
                    6.8
                  ],
                  [
                    2.6,
                    6.8
                  ],
                  [
                    2.6,
                    5.9
                  ],
                  [
                    0.5,
                    5.9
                  ],
                  [
                    0.4,
                    6.3
                  ],
                  [
                    3,
                    9
                  ]
                ],
                o: [
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    -1.6
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    1.1,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.2
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 97,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              38.709,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 100,
        ty: 4,
        parent: 99,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.3
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ],
                  [
                    0,
                    -1.1
                  ]
                ],
                v: [
                  [
                    0.6,
                    6.6
                  ],
                  [
                    0.6,
                    6.9
                  ],
                  [
                    2.8,
                    9
                  ],
                  [
                    5,
                    6.9
                  ],
                  [
                    5,
                    3.4
                  ],
                  [
                    3.9,
                    3.4
                  ],
                  [
                    3.9,
                    6.9
                  ],
                  [
                    2.8,
                    8
                  ],
                  [
                    1.7,
                    6.8
                  ],
                  [
                    1.7,
                    6.7
                  ],
                  [
                    2,
                    5.6
                  ],
                  [
                    2.5,
                    4.4
                  ],
                  [
                    1.4,
                    3.4
                  ],
                  [
                    0.6,
                    3.4
                  ],
                  [
                    0.6,
                    4.4
                  ],
                  [
                    1.1,
                    4.4
                  ],
                  [
                    1.4,
                    4.7
                  ],
                  [
                    0.6,
                    6.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0.4,
                    -0.5
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 99,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              33.03,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 102,
        ty: 4,
        parent: 101,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0.3
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    -0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    5.6,
                    5.6
                  ],
                  [
                    5.4,
                    4.5
                  ],
                  [
                    6.4,
                    4.3
                  ],
                  [
                    6.4,
                    3.3
                  ],
                  [
                    4.8,
                    3.8
                  ],
                  [
                    3.1,
                    3.3
                  ],
                  [
                    0.7,
                    5.6
                  ],
                  [
                    0.7,
                    8.9
                  ],
                  [
                    1.8,
                    8.9
                  ],
                  [
                    1.8,
                    7.8
                  ],
                  [
                    3,
                    6.8
                  ],
                  [
                    3.4,
                    6.8
                  ],
                  [
                    3.4,
                    5.8
                  ],
                  [
                    3.1,
                    5.8
                  ],
                  [
                    1.8,
                    6.5
                  ],
                  [
                    1.8,
                    5.6
                  ],
                  [
                    3.1,
                    4.3
                  ],
                  [
                    4.5,
                    5.6
                  ],
                  [
                    4.5,
                    8.9
                  ],
                  [
                    5.6,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.2,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    -0.4,
                    -0.3
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 101,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              26.46,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 104,
        ty: 4,
        parent: 103,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0.4,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    3.2,
                    5.3
                  ],
                  [
                    3.2,
                    8.9
                  ],
                  [
                    4.3,
                    8.9
                  ],
                  [
                    4.3,
                    5.2
                  ],
                  [
                    2.2,
                    3.3
                  ],
                  [
                    0.2,
                    4.4
                  ],
                  [
                    1,
                    4.9
                  ],
                  [
                    2.2,
                    4.3
                  ],
                  [
                    3.2,
                    5.3
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.4
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 103,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              21.582,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 106,
        ty: 4,
        parent: 105,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0.1,
                    -1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    5.5,
                    8.9
                  ],
                  [
                    5.5,
                    5.6
                  ],
                  [
                    3,
                    3.3
                  ],
                  [
                    0.3,
                    5.5
                  ],
                  [
                    1.2,
                    5.9
                  ],
                  [
                    1.2,
                    8.9
                  ],
                  [
                    2.3,
                    8.9
                  ],
                  [
                    2.3,
                    5.5
                  ],
                  [
                    1.4,
                    5.1
                  ],
                  [
                    3,
                    4.3
                  ],
                  [
                    4.4,
                    5.7
                  ],
                  [
                    4.4,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.5
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 105,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              15.273,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 108,
        ty: 4,
        parent: 107,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.14
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.18
                  ]
                ],
                v: [
                  [
                    1.65,
                    6.72
                  ],
                  [
                    1.65,
                    6.54
                  ],
                  [
                    0.68,
                    6.54
                  ],
                  [
                    0.65,
                    6.95
                  ],
                  [
                    1.82,
                    7.96
                  ],
                  [
                    3.32,
                    7.96
                  ],
                  [
                    3.32,
                    7.03
                  ],
                  [
                    2.03,
                    7.03
                  ],
                  [
                    1.65,
                    6.72
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.02,
                    0.13
                  ],
                  [
                    0,
                    0.63
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.31,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.84,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.18
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.14
                  ]
                ],
                v: [
                  [
                    0.65,
                    4.53
                  ],
                  [
                    1.82,
                    5.55
                  ],
                  [
                    3.32,
                    5.55
                  ],
                  [
                    3.32,
                    4.61
                  ],
                  [
                    2.03,
                    4.61
                  ],
                  [
                    1.65,
                    4.3
                  ],
                  [
                    1.65,
                    4.12
                  ],
                  [
                    0.68,
                    4.12
                  ],
                  [
                    0.65,
                    4.53
                  ]
                ],
                o: [
                  [
                    0,
                    0.64
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.31,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.02,
                    0.13
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 107,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              11.304,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 110,
        ty: 4,
        parent: 109,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1
                  ],
                  [
                    1.1,
                    0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    -0.2,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.1,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    -1,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0.3,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ]
                ],
                v: [
                  [
                    2.7,
                    9
                  ],
                  [
                    4.8,
                    7.3
                  ],
                  [
                    3,
                    5.7
                  ],
                  [
                    2.5,
                    5.6
                  ],
                  [
                    1.7,
                    4.9
                  ],
                  [
                    2.6,
                    4.2
                  ],
                  [
                    3.8,
                    5
                  ],
                  [
                    4.7,
                    4.5
                  ],
                  [
                    2.6,
                    3.3
                  ],
                  [
                    0.6,
                    5
                  ],
                  [
                    2.2,
                    6.6
                  ],
                  [
                    2.7,
                    6.7
                  ],
                  [
                    3.7,
                    7.4
                  ],
                  [
                    2.7,
                    8.1
                  ],
                  [
                    1.2,
                    7.2
                  ],
                  [
                    0.4,
                    7.7
                  ],
                  [
                    2.7,
                    9
                  ]
                ],
                o: [
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    -0.1
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    -0.6
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0.2
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 109,
        ty: 3,
        parent: 90,
        ks: {
          p: {
            a: 0,
            k: [
              6.048,
              0
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 112,
        ty: 4,
        parent: 111,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ]
                ],
                v: [
                  [
                    3,
                    9
                  ],
                  [
                    5.4,
                    6.7
                  ],
                  [
                    5.4,
                    1.6
                  ],
                  [
                    4.3,
                    1.6
                  ],
                  [
                    4.3,
                    6.7
                  ],
                  [
                    3,
                    8
                  ],
                  [
                    1.8,
                    6.7
                  ],
                  [
                    1.8,
                    3.4
                  ],
                  [
                    0.7,
                    3.4
                  ],
                  [
                    0.7,
                    6.8
                  ],
                  [
                    3,
                    9
                  ]
                ],
                o: [
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.129,
                0.278,
                0.639
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 111,
        ty: 3,
        parent: 90,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 90,
        ty: 3,
        parent: 89,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 89,
        ty: 3,
        parent: 88,
        ks: {
          p: {
            a: 0,
            k: [
              0,
              0.6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 88,
        ty: 3,
        parent: 81,
        ks: {
          p: {
            a: 0,
            k: [
              8,
              6
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      },
      {
        ind: 113,
        ty: 4,
        parent: 81,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    62,
                    28
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    62,
                    28
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    124,
                    56
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    124,
                    56
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 81,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              62,
              28
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "118",
    layers: [
      {
        ind: 80,
        ty: 4,
        parent: 79,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    31,
                    14
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    188,
                    86
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 94.2,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 142.2,
                      s: [
                        62,
                        28
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 270.6,
                      s: [
                        62,
                        28
                      ],
                      h: 1
                    }
                  ]
                },
                r: {
                  a: 0,
                  k: 4
                },
                s: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 94.2,
                      s: [
                        0,
                        0
                      ],
                      i: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 142.2,
                      s: [
                        124,
                        56
                      ],
                      i: {
                        x: [
                          1,
                          1
                        ],
                        y: [
                          1,
                          1
                        ]
                      },
                      o: {
                        x: [
                          0,
                          0
                        ],
                        y: [
                          0,
                          0
                        ]
                      }
                    },
                    {
                      t: 270.6,
                      s: [
                        124,
                        56
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.122,
                    0.278,
                    0.655
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 117,
        ty: 4,
        td: 1,
        parent: 79,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    62,
                    28
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    62,
                    28
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 94.2,
                  s: [
                    0,
                    0
                  ],
                  i: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 142.2,
                  s: [
                    124,
                    56
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    124,
                    56
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 116,
        ty: 0,
        tt: 1,
        parent: 79,
        ks: {
          a: {
            a: 0,
            k: [
              62,
              28
            ]
          }
        },
        w: 186,
        h: 84,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "114"
      },
      {
        ind: 79,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              63,
              29
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  },
  {
    id: "122",
    layers: [
      {
        ind: 120,
        ty: 0,
        ks: {
          a: {
            a: 0,
            k: [
              63,
              29
            ]
          },
          p: {
            a: 0,
            k: [
              86.211,
              52.211
            ]
          }
        },
        w: 188,
        h: 86,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "118"
      },
      {
        ind: 121,
        ty: 4,
        ks: {},
        ip: 0,
        op: 271.6,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                148.711,
                80.711
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                297.422,
                161.422
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      }
    ]
  },
  {
    id: "125",
    layers: [
      {
        ind: 124,
        ty: 0,
        parent: 78,
        ks: {
          p: {
            a: 0,
            k: [
              -86.211,
              -52.211
            ]
          }
        },
        ef: [
          {
            ty: 25,
            ef: [
              {
                ty: 2,
                nm: "Shadow Color",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        0.118,
                        0.278,
                        0.663
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.5,
                        y: 0
                      }
                    },
                    {
                      t: 150,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        0,
                        0,
                        0
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Opacity",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        0
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        0
                      ],
                      i: {
                        x: 0,
                        y: 1
                      },
                      o: {
                        x: 0.5,
                        y: 0
                      }
                    },
                    {
                      t: 150,
                      s: [
                        26
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        26
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 1,
                nm: "Direction",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        135
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        135
                      ],
                      i: {
                        x: 0.842,
                        y: 0.419
                      },
                      o: {
                        x: 0.597,
                        y: 0
                      }
                    },
                    {
                      t: 114,
                      s: [
                        138.04
                      ],
                      i: {
                        x: 0.236,
                        y: 0.769
                      },
                      o: {
                        x: 0.222,
                        y: 0.36
                      }
                    },
                    {
                      t: 126,
                      s: [
                        144.95
                      ],
                      i: {
                        x: 0.494,
                        y: 1
                      },
                      o: {
                        x: 0.192,
                        y: 0.59
                      }
                    },
                    {
                      t: 150,
                      s: [
                        146.31
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        146.31
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Distance",
                v: {
                  a: 1,
                  k: [
                    {
                      t: 0,
                      s: [
                        5.657
                      ],
                      h: 1
                    },
                    {
                      t: 102,
                      s: [
                        5.657
                      ],
                      i: {
                        x: 0.373,
                        y: 0.653
                      },
                      o: {
                        x: 0.767,
                        y: 0
                      }
                    },
                    {
                      t: 126,
                      s: [
                        6.965
                      ],
                      i: {
                        x: 0.58,
                        y: 1
                      },
                      o: {
                        x: 0.24,
                        y: 0.704
                      }
                    },
                    {
                      t: 150,
                      s: [
                        7.211
                      ],
                      h: 1
                    },
                    {
                      t: 270.6,
                      s: [
                        7.211
                      ],
                      h: 1
                    }
                  ]
                }
              },
              {
                ty: 0,
                nm: "Softness",
                v: {
                  a: 0,
                  k: 16
                }
              }
            ]
          }
        ],
        w: 297.4222,
        h: 161.4222,
        ip: 0,
        op: 271.6,
        st: 0,
        refId: "122"
      },
      {
        ind: 78,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              87,
              53
            ]
          }
        },
        ip: 0,
        op: 271.6,
        st: 0
      }
    ]
  }
];
const fr$3 = 60;
const h$3 = 238;
const ip$3 = 0;
const layers$3 = [
  {
    ind: 4,
    ty: 4,
    td: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 3,
    ty: 4,
    tt: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 7,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                111,
                102
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                222,
                204
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 6,
    ty: 4,
    tt: 2,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                111,
                86
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                226,
                240
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 58.8,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 106.8,
                  s: [
                    111,
                    70
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 216,
                  s: [
                    111,
                    70
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 264,
                  s: [
                    111,
                    102
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    111,
                    102
                  ],
                  h: 1
                }
              ]
            },
            r: {
              a: 0,
              k: 4
            },
            s: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 58.8,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 106.8,
                  s: [
                    222,
                    140
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 216,
                  s: [
                    222,
                    140
                  ],
                  i: {
                    x: [
                      1,
                      0
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0.5
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 264,
                  s: [
                    222,
                    204
                  ],
                  i: {
                    x: [
                      1,
                      1
                    ],
                    y: [
                      1,
                      1
                    ]
                  },
                  o: {
                    x: [
                      0,
                      0
                    ],
                    y: [
                      0,
                      0
                    ]
                  }
                },
                {
                  t: 270.6,
                  s: [
                    222,
                    204
                  ],
                  h: 1
                }
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 77,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                111,
                70
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                111,
                102
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                111,
                102
              ],
              h: 1
            }
          ]
        },
        r: {
          a: 0,
          k: 4
        },
        s: {
          a: 1,
          k: [
            {
              t: 0,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 58.8,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 106.8,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 216,
              s: [
                222,
                140
              ],
              i: {
                x: [
                  1,
                  0
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0.5
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 264,
              s: [
                222,
                204
              ],
              i: {
                x: [
                  1,
                  1
                ],
                y: [
                  1,
                  1
                ]
              },
              o: {
                x: [
                  0,
                  0
                ],
                y: [
                  0,
                  0
                ]
              }
            },
            {
              t: 270.6,
              s: [
                222,
                204
              ],
              h: 1
            }
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 76,
    ty: 0,
    tt: 1,
    parent: 5,
    ks: {
      a: {
        a: 0,
        k: [
          3,
          157
        ]
      }
    },
    w: 215,
    h: 455,
    ip: 0,
    op: 271.6,
    st: 0,
    refId: "74"
  },
  {
    ind: 5,
    ty: 3,
    parent: 1,
    ks: {
      p: {
        a: 0,
        k: [
          17,
          17
        ]
      }
    },
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 127,
    ty: 0,
    parent: 1,
    ks: {
      a: {
        a: 0,
        k: [
          87,
          53
        ]
      },
      o: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              0
            ],
            h: 1
          },
          {
            t: 94.2,
            s: [
              0
            ],
            i: {
              x: 1,
              y: 1
            },
            o: {
              x: 0,
              y: 0
            }
          },
          {
            t: 99.6,
            s: [
              100
            ],
            h: 1
          },
          {
            t: 270.6,
            s: [
              100
            ],
            h: 1
          }
        ]
      },
      p: {
        a: 1,
        k: [
          {
            t: 0,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 58.8,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                0.125
              ],
              y: [
                1,
                0.83
              ]
            },
            o: {
              x: [
                0,
                0.609
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 94.2,
            s: [
              15,
              171.681
            ],
            i: {
              x: [
                1,
                0.599
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0.272
              ],
              y: [
                0,
                0.622
              ]
            }
          },
          {
            t: 106.8,
            s: [
              15,
              167
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 216,
            s: [
              15,
              167
            ],
            i: {
              x: [
                1,
                0
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0.5
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 264,
            s: [
              15,
              327
            ],
            i: {
              x: [
                1,
                1
              ],
              y: [
                1,
                1
              ]
            },
            o: {
              x: [
                0,
                0
              ],
              y: [
                0,
                0
              ]
            }
          },
          {
            t: 270.6,
            s: [
              15,
              327
            ],
            h: 1
          }
        ]
      }
    },
    w: 299,
    h: 163,
    ip: 0,
    op: 271.6,
    st: 0,
    refId: "125"
  },
  {
    ind: 129,
    ty: 4,
    parent: 128,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            1,
            1,
            1
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 128,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    parent: 0,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  },
  {
    ind: 0,
    ty: 3,
    ks: {},
    ip: 0,
    op: 271.6,
    st: 0
  }
];
const meta$3 = {
  g: "https://jitter.video"
};
const op$3 = 270.6;
const v$3 = "5.7.4";
const w$3 = 255;
const CompetitivePlacementTh = {
  assets: assets$3,
  fr: fr$3,
  h: h$3,
  ip: ip$3,
  layers: layers$3,
  meta: meta$3,
  op: op$3,
  v: v$3,
  w: w$3
};
const CompetitivePlacementAnimation = ({ alt }) => {
  const { language } = useLanguage();
  if (language.startsWith("th")) {
    return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: CompetitivePlacementTh, altText: alt });
  }
  if (language.startsWith("id")) {
    return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: CompetitivePlacementId, altText: alt });
  }
  return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: CompetitivePlacementEn, altText: alt });
};
const assets$2 = [
  {
    id: "14",
    layers: [
      {
        ind: 13,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                7.5,
                8
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                15,
                16
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        o: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            4.17,
                            11.88
                          ],
                          [
                            4.17,
                            7.37
                          ],
                          [
                            0,
                            0.49
                          ],
                          [
                            2.72,
                            0.49
                          ],
                          [
                            5.35,
                            5.04
                          ],
                          [
                            8.01,
                            0.49
                          ],
                          [
                            10.7,
                            0.49
                          ],
                          [
                            6.53,
                            7.27
                          ],
                          [
                            6.53,
                            11.88
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "20",
    layers: [
      {
        ind: 19,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                6.5,
                6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                13,
                12
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -2.36,
                            0
                          ],
                          [
                            0,
                            -2.31
                          ],
                          [
                            2.34,
                            0
                          ],
                          [
                            0,
                            2.34
                          ]
                        ],
                        o: [
                          [
                            0,
                            -2.31
                          ],
                          [
                            2.34,
                            0
                          ],
                          [
                            0,
                            2.34
                          ],
                          [
                            -2.35,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            0.63,
                            4.26
                          ],
                          [
                            4.89,
                            0.19
                          ],
                          [
                            9.11,
                            4.21
                          ],
                          [
                            4.86,
                            8.3
                          ],
                          [
                            0.63,
                            4.26
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -1.06,
                            0
                          ],
                          [
                            0,
                            1.18
                          ],
                          [
                            1.06,
                            0
                          ],
                          [
                            0,
                            -1.16
                          ]
                        ],
                        o: [
                          [
                            0,
                            1.18
                          ],
                          [
                            1.06,
                            0
                          ],
                          [
                            0,
                            -1.16
                          ],
                          [
                            -1.06,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            2.96,
                            4.23
                          ],
                          [
                            4.88,
                            6.22
                          ],
                          [
                            6.78,
                            4.23
                          ],
                          [
                            4.86,
                            2.25
                          ],
                          [
                            2.96,
                            4.23
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "26",
    layers: [
      {
        ind: 25,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                5,
                6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                10,
                12
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.95,
                            0
                          ],
                          [
                            0,
                            0.8
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.22,
                            0
                          ],
                          [
                            0,
                            1.6
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        o: [
                          [
                            0,
                            0.86
                          ],
                          [
                            0.96,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.79,
                            1.13
                          ],
                          [
                            -1.93,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            2.41,
                            4.7
                          ],
                          [
                            3.75,
                            6.18
                          ],
                          [
                            5.14,
                            4.7
                          ],
                          [
                            5.14,
                            0.36
                          ],
                          [
                            7.41,
                            0.36
                          ],
                          [
                            7.41,
                            8.13
                          ],
                          [
                            5.18,
                            8.13
                          ],
                          [
                            5.18,
                            7.16
                          ],
                          [
                            5.16,
                            7.16
                          ],
                          [
                            3,
                            8.29
                          ],
                          [
                            0.15,
                            5.23
                          ],
                          [
                            0.15,
                            0.36
                          ],
                          [
                            2.41,
                            0.36
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "32",
    layers: [
      {
        ind: 31,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                4,
                5.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                8,
                11
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.2,
                            0
                          ],
                          [
                            0,
                            -1.28
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.09,
                            0
                          ],
                          [
                            -0.09,
                            -0.03
                          ]
                        ],
                        o: [
                          [
                            -0.18,
                            -0.03
                          ],
                          [
                            -1.55,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.3,
                            -0.67
                          ],
                          [
                            0.16,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            5.41,
                            2.57
                          ],
                          [
                            4.88,
                            2.53
                          ],
                          [
                            2.77,
                            4.84
                          ],
                          [
                            2.77,
                            8.13
                          ],
                          [
                            0.5,
                            8.13
                          ],
                          [
                            0.5,
                            0.36
                          ],
                          [
                            2.75,
                            0.36
                          ],
                          [
                            2.75,
                            1.55
                          ],
                          [
                            2.77,
                            1.55
                          ],
                          [
                            4.96,
                            0.28
                          ],
                          [
                            5.41,
                            0.33
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "38",
    layers: [
      {
        ind: 37,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                3.5,
                10.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                7,
                21
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.77,
                            0
                          ],
                          [
                            0,
                            -0.79
                          ],
                          [
                            0.79,
                            0
                          ],
                          [
                            0,
                            0.77
                          ]
                        ],
                        o: [
                          [
                            0,
                            -0.79
                          ],
                          [
                            0.79,
                            0
                          ],
                          [
                            0,
                            0.77
                          ],
                          [
                            -0.77,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            2.29,
                            1.41
                          ],
                          [
                            3.69,
                            0
                          ],
                          [
                            5.11,
                            1.41
                          ],
                          [
                            3.69,
                            2.8
                          ],
                          [
                            2.29,
                            1.41
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.12,
                            0
                          ],
                          [
                            0,
                            1.07
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            2.23,
                            0
                          ],
                          [
                            0.19,
                            0.06
                          ]
                        ],
                        o: [
                          [
                            0,
                            0
                          ],
                          [
                            0.13,
                            0.03
                          ],
                          [
                            0.95,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            2.23
                          ],
                          [
                            -0.3,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            0.71,
                            15.61
                          ],
                          [
                            0.71,
                            13.7
                          ],
                          [
                            1.1,
                            13.75
                          ],
                          [
                            2.57,
                            12.34
                          ],
                          [
                            2.57,
                            4.11
                          ],
                          [
                            4.84,
                            4.11
                          ],
                          [
                            4.84,
                            12.46
                          ],
                          [
                            1.51,
                            15.71
                          ],
                          [
                            0.71,
                            15.61
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "44",
    layers: [
      {
        ind: 43,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                6,
                6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                12,
                12
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -2.36,
                            0
                          ],
                          [
                            0,
                            -2.31
                          ],
                          [
                            2.34,
                            0
                          ],
                          [
                            0,
                            2.34
                          ]
                        ],
                        o: [
                          [
                            0,
                            -2.31
                          ],
                          [
                            2.34,
                            0
                          ],
                          [
                            0,
                            2.34
                          ],
                          [
                            -2.36,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            0.46,
                            4.26
                          ],
                          [
                            4.72,
                            0.19
                          ],
                          [
                            8.93,
                            4.21
                          ],
                          [
                            4.69,
                            8.3
                          ],
                          [
                            0.46,
                            4.26
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -1.06,
                            0
                          ],
                          [
                            0,
                            1.18
                          ],
                          [
                            1.06,
                            0
                          ],
                          [
                            0,
                            -1.16
                          ]
                        ],
                        o: [
                          [
                            0,
                            1.18
                          ],
                          [
                            1.06,
                            0
                          ],
                          [
                            0,
                            -1.16
                          ],
                          [
                            -1.06,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            2.79,
                            4.23
                          ],
                          [
                            4.7,
                            6.22
                          ],
                          [
                            6.61,
                            4.23
                          ],
                          [
                            4.69,
                            2.25
                          ],
                          [
                            2.79,
                            4.23
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "50",
    layers: [
      {
        ind: 49,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                6,
                8.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                12,
                17
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -0.46,
                            0
                          ],
                          [
                            0,
                            -1.96
                          ],
                          [
                            2.25,
                            0
                          ],
                          [
                            0.74,
                            0.97
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        o: [
                          [
                            0.77,
                            -0.99
                          ],
                          [
                            2.28,
                            0
                          ],
                          [
                            0,
                            2.02
                          ],
                          [
                            -0.51,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            2.34,
                            5.04
                          ],
                          [
                            4.7,
                            3.97
                          ],
                          [
                            8.39,
                            7.98
                          ],
                          [
                            4.64,
                            12.02
                          ],
                          [
                            2.29,
                            10.95
                          ],
                          [
                            2.26,
                            10.95
                          ],
                          [
                            2.26,
                            11.88
                          ],
                          [
                            0.05,
                            11.88
                          ],
                          [
                            0.05,
                            0.04
                          ],
                          [
                            2.32,
                            0.04
                          ],
                          [
                            2.32,
                            5.04
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -1.06,
                            0
                          ],
                          [
                            0,
                            1.18
                          ],
                          [
                            1.07,
                            0
                          ],
                          [
                            0,
                            -1.16
                          ]
                        ],
                        o: [
                          [
                            0,
                            1.18
                          ],
                          [
                            1.06,
                            0
                          ],
                          [
                            0,
                            -1.16
                          ],
                          [
                            -1.05,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            2.26,
                            7.98
                          ],
                          [
                            4.18,
                            9.97
                          ],
                          [
                            6.09,
                            7.98
                          ],
                          [
                            4.16,
                            6.02
                          ],
                          [
                            2.26,
                            7.98
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "56",
    layers: [
      {
        ind: 55,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                5.5,
                6
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                11,
                12
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.81
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -0.61
                          ],
                          [
                            -0.54,
                            0
                          ]
                        ],
                        o: [
                          [
                            1,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.61,
                            0
                          ],
                          [
                            0,
                            0.55
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            3.74,
                            6.61
                          ],
                          [
                            5.3,
                            5.12
                          ],
                          [
                            5.3,
                            4.94
                          ],
                          [
                            3.93,
                            4.94
                          ],
                          [
                            2.75,
                            5.83
                          ],
                          [
                            3.74,
                            6.61
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.57,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0.32
                          ],
                          [
                            0.86,
                            0
                          ],
                          [
                            0.38,
                            -0.68
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -1.76,
                            0
                          ],
                          [
                            0,
                            -2.09
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.84,
                            0
                          ],
                          [
                            0,
                            1.44
                          ]
                        ],
                        o: [
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            -1.92
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.42,
                            0
                          ],
                          [
                            0,
                            -0.5
                          ],
                          [
                            -0.82,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0.63,
                            -1.15
                          ],
                          [
                            1.98,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            -0.58,
                            0.83
                          ],
                          [
                            -1.4,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            0.47,
                            5.96
                          ],
                          [
                            0.47,
                            5.93
                          ],
                          [
                            3.93,
                            3.55
                          ],
                          [
                            4.7,
                            3.55
                          ],
                          [
                            5.31,
                            2.98
                          ],
                          [
                            4.06,
                            2.02
                          ],
                          [
                            2.33,
                            3.07
                          ],
                          [
                            0.6,
                            2.04
                          ],
                          [
                            4.16,
                            0.19
                          ],
                          [
                            7.54,
                            3.31
                          ],
                          [
                            7.54,
                            8.13
                          ],
                          [
                            5.44,
                            8.13
                          ],
                          [
                            5.44,
                            7.16
                          ],
                          [
                            5.41,
                            7.15
                          ],
                          [
                            3.09,
                            8.27
                          ],
                          [
                            0.47,
                            5.96
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "62",
    layers: [
      {
        ind: 61,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                6,
                8.5
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                12,
                17
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            0.52,
                            0
                          ],
                          [
                            0,
                            2.09
                          ],
                          [
                            -2.23,
                            0
                          ],
                          [
                            -0.8,
                            -0.95
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        o: [
                          [
                            -0.73,
                            1.06
                          ],
                          [
                            -2.25,
                            0
                          ],
                          [
                            0,
                            -1.96
                          ],
                          [
                            0.45,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            6.19,
                            10.89
                          ],
                          [
                            3.78,
                            12.02
                          ],
                          [
                            0.09,
                            7.98
                          ],
                          [
                            3.81,
                            3.97
                          ],
                          [
                            6.13,
                            5
                          ],
                          [
                            6.16,
                            5
                          ],
                          [
                            6.16,
                            0.04
                          ],
                          [
                            8.41,
                            0.04
                          ],
                          [
                            8.41,
                            11.88
                          ],
                          [
                            6.22,
                            11.88
                          ],
                          [
                            6.22,
                            10.89
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "sh",
                    ks: {
                      a: 0,
                      k: {
                        c: true,
                        i: [
                          [
                            0,
                            0
                          ],
                          [
                            -1.06,
                            0
                          ],
                          [
                            0,
                            1.18
                          ],
                          [
                            1.06,
                            0
                          ],
                          [
                            0,
                            -1.16
                          ]
                        ],
                        o: [
                          [
                            0,
                            1.18
                          ],
                          [
                            1.06,
                            0
                          ],
                          [
                            0,
                            -1.16
                          ],
                          [
                            -1.06,
                            0
                          ],
                          [
                            0,
                            0
                          ]
                        ],
                        v: [
                          [
                            2.38,
                            7.98
                          ],
                          [
                            4.3,
                            9.97
                          ],
                          [
                            6.22,
                            7.98
                          ],
                          [
                            4.3,
                            6.02
                          ],
                          [
                            2.38,
                            7.98
                          ]
                        ]
                      }
                    }
                  },
                  {
                    ty: "fl",
                    c: {
                      a: 0,
                      k: [
                        1,
                        1,
                        1,
                        1
                      ]
                    },
                    o: {
                      a: 0,
                      k: 100
                    }
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "69",
    layers: [
      {
        ind: 68,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "73",
    layers: [
      {
        ind: 71,
        ty: 0,
        parent: 67,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "69"
      },
      {
        ind: 67,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 72,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "79",
    layers: [
      {
        ind: 78,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "83",
    layers: [
      {
        ind: 81,
        ty: 0,
        parent: 77,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "79"
      },
      {
        ind: 77,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 82,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "87",
    layers: [
      {
        ind: 86,
        ty: 4,
        td: 1,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 85,
        ty: 0,
        tt: 1,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "83"
      }
    ]
  },
  {
    id: "92",
    layers: [
      {
        ind: 91,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "96",
    layers: [
      {
        ind: 94,
        ty: 0,
        parent: 90,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "92"
      },
      {
        ind: 90,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 95,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "100",
    layers: [
      {
        ind: 99,
        ty: 4,
        td: 1,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 98,
        ty: 0,
        tt: 1,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "96"
      }
    ]
  },
  {
    id: "149",
    layers: [
      {
        ind: 148,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "152",
    layers: [
      {
        ind: 151,
        ty: 0,
        parent: 147,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "149"
      },
      {
        ind: 147,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "160",
    layers: [
      {
        ind: 159,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "163",
    layers: [
      {
        ind: 162,
        ty: 0,
        parent: 158,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "160"
      },
      {
        ind: 158,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "171",
    layers: [
      {
        ind: 170,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "174",
    layers: [
      {
        ind: 173,
        ty: 0,
        parent: 169,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "171"
      },
      {
        ind: 169,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "182",
    layers: [
      {
        ind: 181,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "185",
    layers: [
      {
        ind: 184,
        ty: 0,
        parent: 180,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "182"
      },
      {
        ind: 180,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "199",
    layers: [
      {
        ind: 198,
        ty: 4,
        parent: 197,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    14,
                    14
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    33.37,
                    33.37
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    14,
                    14
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    28,
                    28
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 5.37
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 197,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              3
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "204",
    layers: [
      {
        ind: 16,
        ty: 0,
        parent: 12,
        ks: {},
        w: 15,
        h: 16,
        ip: 0,
        op: 283,
        st: 0,
        refId: "14"
      },
      {
        ind: 12,
        ty: 3,
        parent: 11,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 11,
        ty: 3,
        parent: 10,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 22,
        ty: 0,
        parent: 18,
        ks: {},
        w: 13,
        h: 12,
        ip: 0,
        op: 283,
        st: 0,
        refId: "20"
      },
      {
        ind: 18,
        ty: 3,
        parent: 17,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 17,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              12.809,
              5.008
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 28,
        ty: 0,
        parent: 24,
        ks: {},
        w: 10,
        h: 12,
        ip: 0,
        op: 283,
        st: 0,
        refId: "26"
      },
      {
        ind: 24,
        ty: 3,
        parent: 23,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 23,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              27.754,
              5.008
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 34,
        ty: 0,
        parent: 30,
        ks: {},
        w: 8,
        h: 11,
        ip: 0,
        op: 283,
        st: 0,
        refId: "32"
      },
      {
        ind: 30,
        ty: 3,
        parent: 29,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 29,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              40.563,
              5.008
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 40,
        ty: 0,
        parent: 36,
        ks: {},
        w: 7,
        h: 21,
        ip: 0,
        op: 283,
        st: 0,
        refId: "38"
      },
      {
        ind: 36,
        ty: 3,
        parent: 35,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 35,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              51.238,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 46,
        ty: 0,
        parent: 42,
        ks: {},
        w: 12,
        h: 12,
        ip: 0,
        op: 283,
        st: 0,
        refId: "44"
      },
      {
        ind: 42,
        ty: 3,
        parent: 41,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 41,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              59.777,
              5.008
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 52,
        ty: 0,
        parent: 48,
        ks: {},
        w: 12,
        h: 17,
        ip: 0,
        op: 283,
        st: 0,
        refId: "50"
      },
      {
        ind: 48,
        ty: 3,
        parent: 47,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 47,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              74.721,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 58,
        ty: 0,
        parent: 54,
        ks: {},
        w: 11,
        h: 12,
        ip: 0,
        op: 283,
        st: 0,
        refId: "56"
      },
      {
        ind: 54,
        ty: 3,
        parent: 53,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 53,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              92.868,
              5.008
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 64,
        ty: 0,
        parent: 60,
        ks: {},
        w: 12,
        h: 17,
        ip: 0,
        op: 283,
        st: 0,
        refId: "62"
      },
      {
        ind: 60,
        ty: 3,
        parent: 59,
        ks: {
          s: {
            a: 0,
            k: [
              106.74,
              100.16
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 59,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              105.677,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 10,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              17.467,
              12.526
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 65,
        ty: 4,
        parent: 9,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                76,
                21
              ]
            },
            r: {
              a: 0,
              k: 131
            },
            s: {
              a: 0,
              k: [
                152,
                42
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 9,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 0,
            k: [
              -10,
              54
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 76,
        ty: 4,
        td: 1,
        parent: 66,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 75,
        ty: 0,
        tt: 1,
        parent: 66,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "73"
      },
      {
        ind: 66,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 0,
            k: [
              43.694,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 8,
        ty: 3,
        parent: 7,
        ks: {
          p: {
            a: 0,
            k: [
              275.423,
              321.327
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 89,
        ty: 0,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              21.883,
              21.883
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  340.883,
                  342.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  340.883,
                  342.883
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  163.883,
                  385.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  163.883,
                  385.883
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "87"
      },
      {
        ind: 102,
        ty: 0,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              21.883,
              21.883
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  340.883,
                  343.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  340.883,
                  343.883
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  504.883,
                  173.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  504.883,
                  173.883
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "100"
      },
      {
        ind: 108,
        ty: 4,
        parent: 107,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    -0.3,
                    0.1
                  ]
                ],
                v: [
                  [
                    4.4,
                    10.9
                  ],
                  [
                    4.4,
                    9.5
                  ],
                  [
                    4,
                    9.5
                  ],
                  [
                    3,
                    8.6
                  ],
                  [
                    3,
                    6.5
                  ],
                  [
                    4.5,
                    6.5
                  ],
                  [
                    4.5,
                    5
                  ],
                  [
                    3,
                    5
                  ],
                  [
                    3,
                    3.2
                  ],
                  [
                    1.3,
                    3.2
                  ],
                  [
                    1.3,
                    5
                  ],
                  [
                    0.2,
                    5
                  ],
                  [
                    0.2,
                    6.5
                  ],
                  [
                    1.3,
                    6.5
                  ],
                  [
                    1.3,
                    8.8
                  ],
                  [
                    3.6,
                    11
                  ],
                  [
                    4.4,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 107,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              40.128,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 110,
        ty: 4,
        parent: 109,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    -0.2,
                    -0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    7.3,
                    5
                  ],
                  [
                    3,
                    5
                  ],
                  [
                    3,
                    4.4
                  ],
                  [
                    4.1,
                    3.4
                  ],
                  [
                    4.6,
                    3.4
                  ],
                  [
                    4.6,
                    2
                  ],
                  [
                    3.8,
                    1.9
                  ],
                  [
                    1.3,
                    4.4
                  ],
                  [
                    1.3,
                    5
                  ],
                  [
                    0.3,
                    5
                  ],
                  [
                    0.3,
                    6.5
                  ],
                  [
                    1.3,
                    6.5
                  ],
                  [
                    1.3,
                    10.9
                  ],
                  [
                    3.1,
                    10.9
                  ],
                  [
                    3.1,
                    6.5
                  ],
                  [
                    5.6,
                    6.5
                  ],
                  [
                    5.6,
                    10.9
                  ],
                  [
                    7.3,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    -0.1
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    5.3,
                    3
                  ],
                  [
                    6.4,
                    4
                  ],
                  [
                    7.5,
                    3
                  ],
                  [
                    6.4,
                    1.9
                  ],
                  [
                    5.3,
                    3
                  ]
                ],
                o: [
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 109,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              32.054,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 112,
        ty: 4,
        parent: 111,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.33,
                    6.48
                  ],
                  [
                    0.7,
                    6.48
                  ],
                  [
                    0.7,
                    8.18
                  ],
                  [
                    4.33,
                    8.18
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 111,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              27.016,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 114,
        ty: 4,
        parent: 113,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.5,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    4.6,
                    7.7
                  ],
                  [
                    4.6,
                    10.9
                  ],
                  [
                    6.3,
                    10.9
                  ],
                  [
                    6.3,
                    7.3
                  ],
                  [
                    4.1,
                    4.9
                  ],
                  [
                    2.5,
                    5.7
                  ],
                  [
                    2.5,
                    5.7
                  ],
                  [
                    2.5,
                    2
                  ],
                  [
                    0.8,
                    2
                  ],
                  [
                    0.8,
                    10.9
                  ],
                  [
                    2.5,
                    10.9
                  ],
                  [
                    2.5,
                    7.6
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    4.6,
                    7.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 113,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              19.987,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 116,
        ty: 4,
        parent: 115,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    -0.5,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0.3,
                    0.5
                  ]
                ],
                v: [
                  [
                    2,
                    11.4
                  ],
                  [
                    0.6,
                    12.2
                  ],
                  [
                    3.5,
                    13.8
                  ],
                  [
                    6.7,
                    10.9
                  ],
                  [
                    6.7,
                    5
                  ],
                  [
                    5,
                    5
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    3.2,
                    4.9
                  ],
                  [
                    0.4,
                    7.9
                  ],
                  [
                    3.3,
                    10.9
                  ],
                  [
                    5,
                    10.1
                  ],
                  [
                    5,
                    10.1
                  ],
                  [
                    5,
                    11
                  ],
                  [
                    3.5,
                    12.3
                  ],
                  [
                    2,
                    11.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.9
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    -0.7
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.9
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5,
                    7.9
                  ],
                  [
                    3.6,
                    9.4
                  ],
                  [
                    2.2,
                    7.9
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 115,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              12.485,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 118,
        ty: 4,
        parent: 117,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    0.6,
                    3
                  ],
                  [
                    1.6,
                    4
                  ],
                  [
                    2.7,
                    3
                  ],
                  [
                    1.6,
                    1.9
                  ],
                  [
                    0.6,
                    3
                  ]
                ],
                o: [
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    10.9
                  ],
                  [
                    0.8,
                    5
                  ],
                  [
                    2.5,
                    5
                  ],
                  [
                    2.5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 117,
        ty: 3,
        parent: 106,
        ks: {
          p: {
            a: 0,
            k: [
              9.174,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 120,
        ty: 4,
        parent: 119,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.8,
                    7.4
                  ],
                  [
                    6.4,
                    7.4
                  ],
                  [
                    6.4,
                    10.9
                  ],
                  [
                    8.2,
                    10.9
                  ],
                  [
                    8.2,
                    2.3
                  ],
                  [
                    6.4,
                    2.3
                  ],
                  [
                    6.4,
                    5.7
                  ],
                  [
                    2.8,
                    5.7
                  ],
                  [
                    2.8,
                    2.3
                  ],
                  [
                    1,
                    2.3
                  ],
                  [
                    1,
                    10.9
                  ],
                  [
                    2.8,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 119,
        ty: 3,
        parent: 106,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 106,
        ty: 3,
        parent: 105,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 105,
        ty: 3,
        parent: 104,
        ks: {
          p: {
            a: 0,
            k: [
              11.461,
              0.9
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 104,
        ty: 3,
        parent: 103,
        ks: {
          p: {
            a: 0,
            k: [
              -9,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 122,
        ty: 4,
        parent: 121,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                39,
                9.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                78,
                19
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.902,
                8e-3,
                0.471
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 121,
        ty: 3,
        parent: 103,
        ks: {
          p: {
            a: 0,
            k: [
              -15,
              -2
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 103,
        ty: 3,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              26,
              7.5
            ]
          },
          p: {
            a: 0,
            k: [
              555,
              235.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.725,
                    0.725
                  ],
                  y: [
                    0.45,
                    0.45
                  ]
                },
                o: {
                  x: [
                    0.387,
                    0.387
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 102.636,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    0.614,
                    0.614
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.277,
                    0.277
                  ],
                  y: [
                    0.553,
                    0.553
                  ]
                }
              },
              {
                t: 106.296,
                s: [
                  185.06,
                  185.06
                ],
                i: {
                  x: [
                    0.952,
                    0.952
                  ],
                  y: [
                    0.93,
                    0.93
                  ]
                },
                o: {
                  x: [
                    0.552,
                    0.552
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 109.956,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    0.657,
                    0.657
                  ],
                  y: [
                    0.997,
                    0.997
                  ]
                },
                o: {
                  x: [
                    0.329,
                    0.329
                  ],
                  y: [
                    0.655,
                    0.655
                  ]
                }
              },
              {
                t: 111.126,
                s: [
                  204.02,
                  204.02
                ],
                i: {
                  x: [
                    0.936,
                    0.936
                  ],
                  y: [
                    0.906,
                    0.906
                  ]
                },
                o: {
                  x: [
                    0.549,
                    0.549
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 112.308,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 112.68,
                s: [
                  205.97,
                  205.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.064,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  206.2,
                  206.2
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 128,
        ty: 4,
        parent: 127,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    -0.3,
                    0.1
                  ]
                ],
                v: [
                  [
                    4.4,
                    10.9
                  ],
                  [
                    4.4,
                    9.5
                  ],
                  [
                    4,
                    9.5
                  ],
                  [
                    3,
                    8.6
                  ],
                  [
                    3,
                    6.5
                  ],
                  [
                    4.5,
                    6.5
                  ],
                  [
                    4.5,
                    5
                  ],
                  [
                    3,
                    5
                  ],
                  [
                    3,
                    3.2
                  ],
                  [
                    1.3,
                    3.2
                  ],
                  [
                    1.3,
                    5
                  ],
                  [
                    0.2,
                    5
                  ],
                  [
                    0.2,
                    6.5
                  ],
                  [
                    1.3,
                    6.5
                  ],
                  [
                    1.3,
                    8.8
                  ],
                  [
                    3.6,
                    11
                  ],
                  [
                    4.4,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 127,
        ty: 3,
        parent: 126,
        ks: {
          p: {
            a: 0,
            k: [
              40.128,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 130,
        ty: 4,
        parent: 129,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    -0.2,
                    -0.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    -1.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    7.3,
                    5
                  ],
                  [
                    3,
                    5
                  ],
                  [
                    3,
                    4.4
                  ],
                  [
                    4.1,
                    3.4
                  ],
                  [
                    4.6,
                    3.4
                  ],
                  [
                    4.6,
                    2
                  ],
                  [
                    3.8,
                    1.9
                  ],
                  [
                    1.3,
                    4.4
                  ],
                  [
                    1.3,
                    5
                  ],
                  [
                    0.3,
                    5
                  ],
                  [
                    0.3,
                    6.5
                  ],
                  [
                    1.3,
                    6.5
                  ],
                  [
                    1.3,
                    10.9
                  ],
                  [
                    3.1,
                    10.9
                  ],
                  [
                    3.1,
                    6.5
                  ],
                  [
                    5.6,
                    6.5
                  ],
                  [
                    5.6,
                    10.9
                  ],
                  [
                    7.3,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    -0.1
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    5.3,
                    3
                  ],
                  [
                    6.4,
                    4
                  ],
                  [
                    7.5,
                    3
                  ],
                  [
                    6.4,
                    1.9
                  ],
                  [
                    5.3,
                    3
                  ]
                ],
                o: [
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 129,
        ty: 3,
        parent: 126,
        ks: {
          p: {
            a: 0,
            k: [
              32.054,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 132,
        ty: 4,
        parent: 131,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.33,
                    6.48
                  ],
                  [
                    0.7,
                    6.48
                  ],
                  [
                    0.7,
                    8.18
                  ],
                  [
                    4.33,
                    8.18
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 131,
        ty: 3,
        parent: 126,
        ks: {
          p: {
            a: 0,
            k: [
              27.016,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 134,
        ty: 4,
        parent: 133,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.5,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    4.6,
                    7.7
                  ],
                  [
                    4.6,
                    10.9
                  ],
                  [
                    6.3,
                    10.9
                  ],
                  [
                    6.3,
                    7.3
                  ],
                  [
                    4.1,
                    4.9
                  ],
                  [
                    2.5,
                    5.7
                  ],
                  [
                    2.5,
                    5.7
                  ],
                  [
                    2.5,
                    2
                  ],
                  [
                    0.8,
                    2
                  ],
                  [
                    0.8,
                    10.9
                  ],
                  [
                    2.5,
                    10.9
                  ],
                  [
                    2.5,
                    7.6
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    4.6,
                    7.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 133,
        ty: 3,
        parent: 126,
        ks: {
          p: {
            a: 0,
            k: [
              19.987,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 136,
        ty: 4,
        parent: 135,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    1.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    -0.5,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0.3,
                    0.5
                  ]
                ],
                v: [
                  [
                    2,
                    11.4
                  ],
                  [
                    0.6,
                    12.2
                  ],
                  [
                    3.5,
                    13.8
                  ],
                  [
                    6.7,
                    10.9
                  ],
                  [
                    6.7,
                    5
                  ],
                  [
                    5,
                    5
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    3.2,
                    4.9
                  ],
                  [
                    0.4,
                    7.9
                  ],
                  [
                    3.3,
                    10.9
                  ],
                  [
                    5,
                    10.1
                  ],
                  [
                    5,
                    10.1
                  ],
                  [
                    5,
                    11
                  ],
                  [
                    3.5,
                    12.3
                  ],
                  [
                    2,
                    11.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0.9
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    -0.7
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    7.9
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5,
                    7.9
                  ],
                  [
                    3.6,
                    9.4
                  ],
                  [
                    2.2,
                    7.9
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 135,
        ty: 3,
        parent: 126,
        ks: {
          p: {
            a: 0,
            k: [
              12.485,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 138,
        ty: 4,
        parent: 137,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    0.6,
                    3
                  ],
                  [
                    1.6,
                    4
                  ],
                  [
                    2.7,
                    3
                  ],
                  [
                    1.6,
                    1.9
                  ],
                  [
                    0.6,
                    3
                  ]
                ],
                o: [
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    10.9
                  ],
                  [
                    0.8,
                    5
                  ],
                  [
                    2.5,
                    5
                  ],
                  [
                    2.5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 137,
        ty: 3,
        parent: 126,
        ks: {
          p: {
            a: 0,
            k: [
              9.174,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 140,
        ty: 4,
        parent: 139,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.8,
                    7.4
                  ],
                  [
                    6.4,
                    7.4
                  ],
                  [
                    6.4,
                    10.9
                  ],
                  [
                    8.2,
                    10.9
                  ],
                  [
                    8.2,
                    2.3
                  ],
                  [
                    6.4,
                    2.3
                  ],
                  [
                    6.4,
                    5.7
                  ],
                  [
                    2.8,
                    5.7
                  ],
                  [
                    2.8,
                    2.3
                  ],
                  [
                    1,
                    2.3
                  ],
                  [
                    1,
                    10.9
                  ],
                  [
                    2.8,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 139,
        ty: 3,
        parent: 126,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 126,
        ty: 3,
        parent: 125,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 125,
        ty: 3,
        parent: 124,
        ks: {
          p: {
            a: 0,
            k: [
              11.461,
              0.9
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 124,
        ty: 3,
        parent: 123,
        ks: {
          p: {
            a: 0,
            k: [
              -9,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 142,
        ty: 4,
        parent: 141,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                39,
                9.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                78,
                19
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.902,
                8e-3,
                0.471
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 141,
        ty: 3,
        parent: 123,
        ks: {
          p: {
            a: 0,
            k: [
              -13,
              -2
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 123,
        ty: 3,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              26,
              7.5
            ]
          },
          p: {
            a: 0,
            k: [
              123,
              463.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.725,
                    0.725
                  ],
                  y: [
                    0.45,
                    0.45
                  ]
                },
                o: {
                  x: [
                    0.387,
                    0.387
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 102.636,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    0.614,
                    0.614
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.277,
                    0.277
                  ],
                  y: [
                    0.553,
                    0.553
                  ]
                }
              },
              {
                t: 106.296,
                s: [
                  185.05,
                  185.05
                ],
                i: {
                  x: [
                    0.952,
                    0.952
                  ],
                  y: [
                    0.93,
                    0.93
                  ]
                },
                o: {
                  x: [
                    0.552,
                    0.552
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 109.956,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    0.657,
                    0.657
                  ],
                  y: [
                    0.997,
                    0.997
                  ]
                },
                o: {
                  x: [
                    0.329,
                    0.329
                  ],
                  y: [
                    0.655,
                    0.655
                  ]
                }
              },
              {
                t: 111.126,
                s: [
                  204.02,
                  204.02
                ],
                i: {
                  x: [
                    0.936,
                    0.936
                  ],
                  y: [
                    0.906,
                    0.906
                  ]
                },
                o: {
                  x: [
                    0.549,
                    0.549
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 112.308,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 112.68,
                s: [
                  205.97,
                  205.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.064,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  206.2,
                  206.2
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 154,
        ty: 0,
        parent: 146,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "152"
      },
      {
        ind: 146,
        ty: 3,
        parent: 145,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 155,
        ty: 4,
        parent: 145,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 145,
        ty: 3,
        parent: 144,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 0,
            k: [
              523.021,
              496.171
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  69.81,
                  69.81
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  60.34,
                  60.34
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  57.37,
                  57.37
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  56.43,
                  56.43
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  56.14,
                  56.14
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  56,
                  56
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 165,
        ty: 0,
        parent: 157,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "163"
      },
      {
        ind: 157,
        ty: 3,
        parent: 156,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 166,
        ty: 4,
        parent: 156,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 156,
        ty: 3,
        parent: 144,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 0,
            k: [
              184.706,
              106.84
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  69.81,
                  69.81
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  60.34,
                  60.34
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  57.37,
                  57.37
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  56.43,
                  56.43
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  56.14,
                  56.14
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  56,
                  56
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 176,
        ty: 0,
        parent: 168,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "174"
      },
      {
        ind: 168,
        ty: 3,
        parent: 167,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 177,
        ty: 4,
        parent: 167,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  h: 1
                },
                {
                  t: 91.2,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  i: {
                    x: 0.613,
                    y: 0.589
                  },
                  o: {
                    x: 0.321,
                    y: 0.033
                  }
                },
                {
                  t: 93.25128,
                  s: [
                    0.973,
                    0.941,
                    0.98
                  ],
                  i: {
                    x: 0.591,
                    y: 0.993
                  },
                  o: {
                    x: 0.319,
                    y: 0.678
                  }
                },
                {
                  t: 95.30256,
                  s: [
                    1,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.447,
                    y: 0.987
                  },
                  o: {
                    x: 0.279,
                    y: 0.02
                  }
                },
                {
                  t: 99.40512,
                  s: [
                    0.992,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.442,
                    y: 0.994
                  },
                  o: {
                    x: 0.279,
                    y: 0
                  }
                },
                {
                  t: 103.55652,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                },
                {
                  t: 282,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 167,
        ty: 3,
        parent: 144,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 181.2,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 194.94,
                s: [
                  122.95,
                  412.99
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.4,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  122.95,
                  420.99
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  112.84,
                  112.84
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  116.86,
                  116.86
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  118.13,
                  118.13
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  118.53,
                  118.53
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  118.65,
                  118.65
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  118.71,
                  118.71
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 187,
        ty: 0,
        parent: 179,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "185"
      },
      {
        ind: 179,
        ty: 3,
        parent: 178,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 188,
        ty: 4,
        parent: 178,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  h: 1
                },
                {
                  t: 91.2,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  i: {
                    x: 0.613,
                    y: 0.589
                  },
                  o: {
                    x: 0.321,
                    y: 0.033
                  }
                },
                {
                  t: 93.25128,
                  s: [
                    0.973,
                    0.941,
                    0.98
                  ],
                  i: {
                    x: 0.591,
                    y: 0.993
                  },
                  o: {
                    x: 0.319,
                    y: 0.678
                  }
                },
                {
                  t: 95.30256,
                  s: [
                    1,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.447,
                    y: 0.987
                  },
                  o: {
                    x: 0.279,
                    y: 0.02
                  }
                },
                {
                  t: 99.40512,
                  s: [
                    0.992,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.442,
                    y: 0.994
                  },
                  o: {
                    x: 0.279,
                    y: 0
                  }
                },
                {
                  t: 103.55652,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                },
                {
                  t: 282,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 178,
        ty: 3,
        parent: 144,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 181.2,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 194.94,
                s: [
                  555.399,
                  189.399
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.4,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  555.399,
                  197.399
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  112.83,
                  112.83
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  116.86,
                  116.86
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  118.12,
                  118.12
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  118.52,
                  118.52
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  118.65,
                  118.65
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.208,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.4,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  118.71,
                  118.71
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 190,
        ty: 4,
        parent: 189,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    341,
                    341
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    684.623,
                    684.623
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    341,
                    341
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    682,
                    682
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 189,
        ty: 3,
        parent: 144,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 192,
        ty: 4,
        parent: 191,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    237.388,
                    237.388
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    477.4,
                    477.4
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    237.388,
                    237.388
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    474.777,
                    474.777
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 191,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              103.612,
              103.612
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 194,
        ty: 4,
        parent: 193,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    131.154,
                    131.154
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    264.931,
                    264.931
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    131.154,
                    131.154
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    262.308,
                    262.308
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 193,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              209.846,
              209.846
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 196,
        ty: 4,
        parent: 195,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    45.904,
                    45.904
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    94.431,
                    94.431
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    45.904,
                    45.904
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    91.808,
                    91.808
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 195,
        ty: 3,
        parent: 144,
        ks: {
          p: {
            a: 0,
            k: [
              295.096,
              295.096
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 201,
        ty: 0,
        parent: 144,
        ks: {
          a: {
            a: 0,
            k: [
              17,
              17
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 46.8,
                s: [
                  100
                ],
                i: {
                  x: 0.75,
                  y: 1
                },
                o: {
                  x: 0.47,
                  y: 0
                }
              },
              {
                t: 65.6352,
                s: [
                  184
                ],
                i: {
                  x: 0.04,
                  y: 1
                },
                o: {
                  x: 0.25,
                  y: 0
                }
              },
              {
                t: 90,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 0,
            k: [
              341,
              341
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 46.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 94.8,
                s: [
                  4320.09,
                  4320.09
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  4320.09,
                  4320.09
                ],
                h: 1
              }
            ]
          }
        },
        w: 34,
        h: 34,
        ip: 0,
        op: 283,
        st: 0,
        refId: "199"
      },
      {
        ind: 144,
        ty: 3,
        parent: 143,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 143,
        ty: 3,
        parent: 7,
        ks: {
          p: {
            a: 0,
            k: [
              -3,
              2
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 7,
        ty: 3,
        parent: 6,
        ks: {
          a: {
            a: 0,
            k: [
              341,
              341
            ]
          },
          p: {
            a: 0,
            k: [
              128,
              119
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 48,
                s: [
                  37.68,
                  37.68
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 214.2,
                s: [
                  37.68,
                  37.68
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 262.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  100,
                  100
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 203,
        ty: 4,
        parent: 202,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 202,
        ty: 3,
        parent: 6,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 6,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              610,
              616
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  }
];
const fr$2 = 60;
const h$2 = 238;
const ip$2 = 0;
const layers$2 = [
  {
    ind: 4,
    ty: 4,
    td: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 3,
    ty: 4,
    tt: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 207,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 206,
    ty: 0,
    tt: 1,
    parent: 5,
    ks: {
      a: {
        a: 0,
        k: [
          610,
          616
        ]
      }
    },
    w: 1672,
    h: 1680,
    ip: 0,
    op: 283,
    st: 0,
    refId: "204"
  },
  {
    ind: 5,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    parent: 0,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 0,
    ty: 3,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  }
];
const meta$2 = {
  g: "https://jitter.video"
};
const op$2 = 282;
const v$2 = "5.7.4";
const w$2 = 255;
const ExclusiveTargetingEn = {
  assets: assets$2,
  fr: fr$2,
  h: h$2,
  ip: ip$2,
  layers: layers$2,
  meta: meta$2,
  op: op$2,
  v: v$2,
  w: w$2
};
const assets$1 = [
  {
    id: "42",
    layers: [
      {
        ind: 41,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "46",
    layers: [
      {
        ind: 44,
        ty: 0,
        parent: 40,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "42"
      },
      {
        ind: 40,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 45,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "52",
    layers: [
      {
        ind: 51,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "56",
    layers: [
      {
        ind: 54,
        ty: 0,
        parent: 50,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "52"
      },
      {
        ind: 50,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 55,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "60",
    layers: [
      {
        ind: 59,
        ty: 4,
        td: 1,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 58,
        ty: 0,
        tt: 1,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "56"
      }
    ]
  },
  {
    id: "65",
    layers: [
      {
        ind: 64,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "69",
    layers: [
      {
        ind: 67,
        ty: 0,
        parent: 63,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "65"
      },
      {
        ind: 63,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 68,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "73",
    layers: [
      {
        ind: 72,
        ty: 4,
        td: 1,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 71,
        ty: 0,
        tt: 1,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "69"
      }
    ]
  },
  {
    id: "151",
    layers: [
      {
        ind: 150,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "154",
    layers: [
      {
        ind: 153,
        ty: 0,
        parent: 149,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "151"
      },
      {
        ind: 149,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "162",
    layers: [
      {
        ind: 161,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "165",
    layers: [
      {
        ind: 164,
        ty: 0,
        parent: 160,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "162"
      },
      {
        ind: 160,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "173",
    layers: [
      {
        ind: 172,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "176",
    layers: [
      {
        ind: 175,
        ty: 0,
        parent: 171,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "173"
      },
      {
        ind: 171,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "184",
    layers: [
      {
        ind: 183,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "187",
    layers: [
      {
        ind: 186,
        ty: 0,
        parent: 182,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "184"
      },
      {
        ind: 182,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "201",
    layers: [
      {
        ind: 200,
        ty: 4,
        parent: 199,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    14,
                    14
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    33.37,
                    33.37
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    14,
                    14
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    28,
                    28
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 5.37
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 199,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              3
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "204",
    layers: [
      {
        ind: 156,
        ty: 0,
        parent: 148,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "154"
      },
      {
        ind: 148,
        ty: 3,
        parent: 147,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 157,
        ty: 4,
        parent: 147,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 147,
        ty: 3,
        parent: 146,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 0,
            k: [
              523.021,
              496.171
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  69.81,
                  69.81
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  60.34,
                  60.34
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  57.37,
                  57.37
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  56.43,
                  56.43
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  56.14,
                  56.14
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  56,
                  56
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 167,
        ty: 0,
        parent: 159,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "165"
      },
      {
        ind: 159,
        ty: 3,
        parent: 158,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 168,
        ty: 4,
        parent: 158,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 158,
        ty: 3,
        parent: 146,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 0,
            k: [
              184.706,
              106.84
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  69.81,
                  69.81
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  60.34,
                  60.34
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  57.37,
                  57.37
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  56.43,
                  56.43
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  56.14,
                  56.14
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  56,
                  56
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 178,
        ty: 0,
        parent: 170,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "176"
      },
      {
        ind: 170,
        ty: 3,
        parent: 169,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 179,
        ty: 4,
        parent: 169,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  h: 1
                },
                {
                  t: 91.2,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  i: {
                    x: 0.613,
                    y: 0.589
                  },
                  o: {
                    x: 0.321,
                    y: 0.033
                  }
                },
                {
                  t: 93.25128,
                  s: [
                    0.973,
                    0.941,
                    0.98
                  ],
                  i: {
                    x: 0.591,
                    y: 0.993
                  },
                  o: {
                    x: 0.319,
                    y: 0.678
                  }
                },
                {
                  t: 95.30256,
                  s: [
                    1,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.447,
                    y: 0.987
                  },
                  o: {
                    x: 0.279,
                    y: 0.02
                  }
                },
                {
                  t: 99.40512,
                  s: [
                    0.992,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.442,
                    y: 0.994
                  },
                  o: {
                    x: 0.279,
                    y: 0
                  }
                },
                {
                  t: 103.55652,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                },
                {
                  t: 282,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 169,
        ty: 3,
        parent: 146,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 181.2,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 194.94,
                s: [
                  122.95,
                  412.99
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.4,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  122.95,
                  420.99
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  112.84,
                  112.84
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  116.86,
                  116.86
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  118.13,
                  118.13
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  118.53,
                  118.53
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  118.65,
                  118.65
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  118.71,
                  118.71
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 189,
        ty: 0,
        parent: 181,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "187"
      },
      {
        ind: 181,
        ty: 3,
        parent: 180,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 190,
        ty: 4,
        parent: 180,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  h: 1
                },
                {
                  t: 91.2,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  i: {
                    x: 0.613,
                    y: 0.589
                  },
                  o: {
                    x: 0.321,
                    y: 0.033
                  }
                },
                {
                  t: 93.25128,
                  s: [
                    0.973,
                    0.941,
                    0.98
                  ],
                  i: {
                    x: 0.591,
                    y: 0.993
                  },
                  o: {
                    x: 0.319,
                    y: 0.678
                  }
                },
                {
                  t: 95.30256,
                  s: [
                    1,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.447,
                    y: 0.987
                  },
                  o: {
                    x: 0.279,
                    y: 0.02
                  }
                },
                {
                  t: 99.40512,
                  s: [
                    0.992,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.442,
                    y: 0.994
                  },
                  o: {
                    x: 0.279,
                    y: 0
                  }
                },
                {
                  t: 103.55652,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                },
                {
                  t: 282,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 180,
        ty: 3,
        parent: 146,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 181.2,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 194.94,
                s: [
                  555.399,
                  189.399
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.4,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  555.399,
                  197.399
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  112.83,
                  112.83
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  116.86,
                  116.86
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  118.12,
                  118.12
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  118.52,
                  118.52
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  118.65,
                  118.65
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.208,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.4,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  118.71,
                  118.71
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 192,
        ty: 4,
        parent: 191,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    341,
                    341
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    684.623,
                    684.623
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    341,
                    341
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    682,
                    682
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 191,
        ty: 3,
        parent: 146,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 194,
        ty: 4,
        parent: 193,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    237.388,
                    237.388
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    477.4,
                    477.4
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    237.388,
                    237.388
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    474.777,
                    474.777
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 193,
        ty: 3,
        parent: 146,
        ks: {
          p: {
            a: 0,
            k: [
              103.612,
              103.612
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 196,
        ty: 4,
        parent: 195,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    131.154,
                    131.154
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    264.931,
                    264.931
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    131.154,
                    131.154
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    262.308,
                    262.308
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 195,
        ty: 3,
        parent: 146,
        ks: {
          p: {
            a: 0,
            k: [
              209.846,
              209.846
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 198,
        ty: 4,
        parent: 197,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    45.904,
                    45.904
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    94.431,
                    94.431
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    45.904,
                    45.904
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    91.808,
                    91.808
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 197,
        ty: 3,
        parent: 146,
        ks: {
          p: {
            a: 0,
            k: [
              295.096,
              295.096
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 203,
        ty: 0,
        parent: 146,
        ks: {
          a: {
            a: 0,
            k: [
              17,
              17
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 46.8,
                s: [
                  100
                ],
                i: {
                  x: 0.75,
                  y: 1
                },
                o: {
                  x: 0.47,
                  y: 0
                }
              },
              {
                t: 65.6352,
                s: [
                  184
                ],
                i: {
                  x: 0.04,
                  y: 1
                },
                o: {
                  x: 0.25,
                  y: 0
                }
              },
              {
                t: 90,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 0,
            k: [
              341,
              341
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 46.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 94.8,
                s: [
                  4320.09,
                  4320.09
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  4320.09,
                  4320.09
                ],
                h: 1
              }
            ]
          }
        },
        w: 34,
        h: 34,
        ip: 0,
        op: 283,
        st: 0,
        refId: "201"
      },
      {
        ind: 146,
        ty: 3,
        parent: 145,
        ks: {
          p: {
            a: 0,
            k: [
              -4,
              -23
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 145,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              398,
              417
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "210",
    layers: [
      {
        ind: 14,
        ty: 4,
        parent: 13,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.4,
                    0
                  ]
                ],
                v: [
                  [
                    5.6,
                    18.6
                  ],
                  [
                    4.2,
                    17.5
                  ],
                  [
                    5.9,
                    16.2
                  ],
                  [
                    7.9,
                    16.2
                  ],
                  [
                    7.9,
                    16.5
                  ],
                  [
                    5.6,
                    18.6
                  ]
                ],
                o: [
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -2,
                    0
                  ],
                  [
                    -0.8,
                    1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.9,
                    0
                  ],
                  [
                    0.9,
                    -1.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -2.8
                  ]
                ],
                v: [
                  [
                    0.9,
                    17.7
                  ],
                  [
                    4.7,
                    21
                  ],
                  [
                    8,
                    19.4
                  ],
                  [
                    8.1,
                    19.4
                  ],
                  [
                    8.1,
                    20.8
                  ],
                  [
                    11.1,
                    20.8
                  ],
                  [
                    11.1,
                    13.9
                  ],
                  [
                    6.2,
                    9.4
                  ],
                  [
                    1.1,
                    12
                  ],
                  [
                    3.6,
                    13.5
                  ],
                  [
                    6.1,
                    12
                  ],
                  [
                    7.9,
                    13.4
                  ],
                  [
                    7,
                    14.2
                  ],
                  [
                    5.9,
                    14.2
                  ],
                  [
                    0.9,
                    17.7
                  ]
                ],
                o: [
                  [
                    0,
                    2.1
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -3
                  ],
                  [
                    -2.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    -1
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 13,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              44.457,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 16,
        ty: 4,
        parent: 15,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -3.3,
                    0
                  ],
                  [
                    -1,
                    1.5
                  ]
                ],
                v: [
                  [
                    9.7,
                    19.4
                  ],
                  [
                    9.7,
                    19.4
                  ],
                  [
                    9.7,
                    20.8
                  ],
                  [
                    12.9,
                    20.8
                  ],
                  [
                    12.9,
                    3.7
                  ],
                  [
                    9.6,
                    3.7
                  ],
                  [
                    9.6,
                    10.9
                  ],
                  [
                    9.6,
                    10.9
                  ],
                  [
                    6.2,
                    9.4
                  ],
                  [
                    0.8,
                    15.2
                  ],
                  [
                    6.2,
                    21
                  ],
                  [
                    9.7,
                    19.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    -1.4
                  ],
                  [
                    -3.2,
                    0
                  ],
                  [
                    0,
                    3
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    1.7
                  ]
                ],
                v: [
                  [
                    4.2,
                    15.2
                  ],
                  [
                    6.9,
                    12.4
                  ],
                  [
                    9.7,
                    15.2
                  ],
                  [
                    6.9,
                    18.1
                  ],
                  [
                    4.2,
                    15.2
                  ]
                ],
                o: [
                  [
                    0,
                    -1.7
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 15,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              30.051,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 18,
        ty: 4,
        parent: 17,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.9,
                    0
                  ],
                  [
                    0.9,
                    -1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ]
                ],
                v: [
                  [
                    8.8,
                    14.6
                  ],
                  [
                    8.8,
                    20.8
                  ],
                  [
                    12,
                    20.8
                  ],
                  [
                    12,
                    13.8
                  ],
                  [
                    8,
                    9.4
                  ],
                  [
                    4.8,
                    11.1
                  ],
                  [
                    4.8,
                    11.1
                  ],
                  [
                    4.8,
                    9.6
                  ],
                  [
                    1.5,
                    9.6
                  ],
                  [
                    1.5,
                    20.8
                  ],
                  [
                    4.8,
                    20.8
                  ],
                  [
                    4.8,
                    14.6
                  ],
                  [
                    6.8,
                    12.4
                  ],
                  [
                    8.8,
                    14.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 17,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              16.632,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 20,
        ty: 4,
        parent: 19,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    3.4,
                    20.8
                  ],
                  [
                    4.6,
                    17.5
                  ],
                  [
                    11.9,
                    17.5
                  ],
                  [
                    13.1,
                    20.8
                  ],
                  [
                    16.7,
                    20.8
                  ],
                  [
                    10.4,
                    4.4
                  ],
                  [
                    6.3,
                    4.4
                  ],
                  [
                    0,
                    20.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    5.8,
                    14.4
                  ],
                  [
                    8.3,
                    7.7
                  ],
                  [
                    10.8,
                    14.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 19,
        ty: 3,
        parent: 12,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 12,
        ty: 3,
        parent: 11,
        ks: {
          p: {
            a: 0,
            k: [
              117.327,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 23,
        ty: 4,
        parent: 22,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.9,
                    0
                  ],
                  [
                    0.9,
                    -1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ]
                ],
                v: [
                  [
                    8.8,
                    14.6
                  ],
                  [
                    8.8,
                    20.8
                  ],
                  [
                    12,
                    20.8
                  ],
                  [
                    12,
                    13.8
                  ],
                  [
                    8,
                    9.4
                  ],
                  [
                    4.8,
                    11.1
                  ],
                  [
                    4.8,
                    11.1
                  ],
                  [
                    4.8,
                    9.6
                  ],
                  [
                    1.5,
                    9.6
                  ],
                  [
                    1.5,
                    20.8
                  ],
                  [
                    4.8,
                    20.8
                  ],
                  [
                    4.8,
                    14.6
                  ],
                  [
                    6.8,
                    12.4
                  ],
                  [
                    8.8,
                    14.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 22,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              99.141,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 25,
        ty: 4,
        parent: 24,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.4,
                    0
                  ]
                ],
                v: [
                  [
                    5.6,
                    18.6
                  ],
                  [
                    4.2,
                    17.5
                  ],
                  [
                    5.9,
                    16.2
                  ],
                  [
                    7.9,
                    16.2
                  ],
                  [
                    7.9,
                    16.5
                  ],
                  [
                    5.6,
                    18.6
                  ]
                ],
                o: [
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -2,
                    0
                  ],
                  [
                    -0.8,
                    1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.9,
                    0
                  ],
                  [
                    0.9,
                    -1.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -2.8
                  ]
                ],
                v: [
                  [
                    0.9,
                    17.7
                  ],
                  [
                    4.7,
                    21
                  ],
                  [
                    8,
                    19.4
                  ],
                  [
                    8.1,
                    19.4
                  ],
                  [
                    8.1,
                    20.8
                  ],
                  [
                    11.1,
                    20.8
                  ],
                  [
                    11.1,
                    13.9
                  ],
                  [
                    6.2,
                    9.4
                  ],
                  [
                    1.1,
                    12
                  ],
                  [
                    3.6,
                    13.5
                  ],
                  [
                    6.1,
                    12
                  ],
                  [
                    7.9,
                    13.4
                  ],
                  [
                    7,
                    14.2
                  ],
                  [
                    5.9,
                    14.2
                  ],
                  [
                    0.9,
                    17.7
                  ]
                ],
                o: [
                  [
                    0,
                    2.1
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -3
                  ],
                  [
                    -2.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    -1
                  ],
                  [
                    1.2,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 24,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              86.646,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 27,
        ty: 4,
        parent: 26,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -3.3,
                    0
                  ],
                  [
                    0,
                    3.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    -2.8
                  ],
                  [
                    -3.2,
                    0
                  ],
                  [
                    -1,
                    1.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0.5,
                    1
                  ]
                ],
                v: [
                  [
                    3.7,
                    21.7
                  ],
                  [
                    1.1,
                    23.2
                  ],
                  [
                    6.7,
                    26.4
                  ],
                  [
                    12.8,
                    20.9
                  ],
                  [
                    12.8,
                    9.6
                  ],
                  [
                    9.6,
                    9.6
                  ],
                  [
                    9.6,
                    11
                  ],
                  [
                    9.5,
                    11
                  ],
                  [
                    6.1,
                    9.4
                  ],
                  [
                    0.8,
                    15.1
                  ],
                  [
                    6.3,
                    20.7
                  ],
                  [
                    9.5,
                    19.2
                  ],
                  [
                    9.5,
                    19.2
                  ],
                  [
                    9.5,
                    20.9
                  ],
                  [
                    6.7,
                    23.6
                  ],
                  [
                    3.7,
                    21.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    1.7
                  ],
                  [
                    3.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    -1.4
                  ],
                  [
                    -3.2,
                    0
                  ],
                  [
                    0,
                    3.1
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    1.7
                  ]
                ],
                v: [
                  [
                    4.2,
                    15.1
                  ],
                  [
                    6.9,
                    12.4
                  ],
                  [
                    9.6,
                    15.1
                  ],
                  [
                    6.9,
                    17.9
                  ],
                  [
                    4.2,
                    15.1
                  ]
                ],
                o: [
                  [
                    0,
                    -1.6
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 26,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              72.324,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 29,
        ty: 4,
        parent: 28,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.9,
                    0
                  ],
                  [
                    0.9,
                    -1.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    -1.3
                  ]
                ],
                v: [
                  [
                    8.8,
                    14.6
                  ],
                  [
                    8.8,
                    20.8
                  ],
                  [
                    12,
                    20.8
                  ],
                  [
                    12,
                    13.8
                  ],
                  [
                    8,
                    9.4
                  ],
                  [
                    4.8,
                    11.1
                  ],
                  [
                    4.8,
                    11.1
                  ],
                  [
                    4.8,
                    9.6
                  ],
                  [
                    1.5,
                    9.6
                  ],
                  [
                    1.5,
                    20.8
                  ],
                  [
                    4.8,
                    20.8
                  ],
                  [
                    4.8,
                    14.6
                  ],
                  [
                    6.8,
                    12.4
                  ],
                  [
                    8.8,
                    14.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -2.3
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 28,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              58.905,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 31,
        ty: 4,
        parent: 30,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -3.4,
                    0
                  ],
                  [
                    0,
                    3.4
                  ],
                  [
                    3.4,
                    0
                  ],
                  [
                    0,
                    -3.3
                  ]
                ],
                v: [
                  [
                    0.8,
                    15.2
                  ],
                  [
                    6.9,
                    21.1
                  ],
                  [
                    13.1,
                    15.2
                  ],
                  [
                    7,
                    9.4
                  ],
                  [
                    0.8,
                    15.2
                  ]
                ],
                o: [
                  [
                    0,
                    3.4
                  ],
                  [
                    3.4,
                    0
                  ],
                  [
                    0,
                    -3.3
                  ],
                  [
                    -3.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    1.7
                  ]
                ],
                v: [
                  [
                    4.2,
                    15.2
                  ],
                  [
                    6.9,
                    12.3
                  ],
                  [
                    9.7,
                    15.2
                  ],
                  [
                    7,
                    18.1
                  ],
                  [
                    4.2,
                    15.2
                  ]
                ],
                o: [
                  [
                    0,
                    -1.7
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 30,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              45.024,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 33,
        ty: 4,
        parent: 32,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    13,
                    16.6
                  ],
                  [
                    10.9,
                    9.6
                  ],
                  [
                    7.9,
                    9.6
                  ],
                  [
                    5.8,
                    16.6
                  ],
                  [
                    5.8,
                    16.6
                  ],
                  [
                    3.7,
                    9.6
                  ],
                  [
                    0.4,
                    9.6
                  ],
                  [
                    3.9,
                    20.8
                  ],
                  [
                    7,
                    20.8
                  ],
                  [
                    9.3,
                    13.7
                  ],
                  [
                    9.4,
                    13.7
                  ],
                  [
                    11.7,
                    20.8
                  ],
                  [
                    14.9,
                    20.8
                  ],
                  [
                    18.4,
                    9.6
                  ],
                  [
                    15.1,
                    9.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 32,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              26.565,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 35,
        ty: 4,
        parent: 34,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -3.4,
                    0
                  ],
                  [
                    0,
                    3.4
                  ],
                  [
                    3.4,
                    0
                  ],
                  [
                    0,
                    -3.3
                  ]
                ],
                v: [
                  [
                    0.8,
                    15.2
                  ],
                  [
                    6.9,
                    21.1
                  ],
                  [
                    13.1,
                    15.2
                  ],
                  [
                    7,
                    9.4
                  ],
                  [
                    0.8,
                    15.2
                  ]
                ],
                o: [
                  [
                    0,
                    3.4
                  ],
                  [
                    3.4,
                    0
                  ],
                  [
                    0,
                    -3.3
                  ],
                  [
                    -3.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    1.7
                  ]
                ],
                v: [
                  [
                    4.2,
                    15.2
                  ],
                  [
                    6.9,
                    12.3
                  ],
                  [
                    9.7,
                    15.2
                  ],
                  [
                    7,
                    18.1
                  ],
                  [
                    4.2,
                    15.2
                  ]
                ],
                o: [
                  [
                    0,
                    -1.7
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 34,
        ty: 3,
        parent: 21,
        ks: {
          p: {
            a: 0,
            k: [
              12.684,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 37,
        ty: 4,
        parent: 36,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    1.9,
                    4.4
                  ],
                  [
                    1.9,
                    20.8
                  ],
                  [
                    12.3,
                    20.8
                  ],
                  [
                    12.3,
                    17.6
                  ],
                  [
                    5.3,
                    17.6
                  ],
                  [
                    5.3,
                    4.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 36,
        ty: 3,
        parent: 21,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 21,
        ty: 3,
        parent: 11,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 11,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              35.512,
              1.9
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 10,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              -20.329,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 38,
        ty: 4,
        parent: 9,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                103,
                21
              ]
            },
            r: {
              a: 0,
              k: 131
            },
            s: {
              a: 0,
              k: [
                206,
                42
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 9,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 0,
            k: [
              -37,
              54
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 49,
        ty: 4,
        td: 1,
        parent: 39,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 48,
        ty: 0,
        tt: 1,
        parent: 39,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "46"
      },
      {
        ind: 39,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 0,
            k: [
              43.694,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 8,
        ty: 3,
        parent: 7,
        ks: {
          p: {
            a: 0,
            k: [
              275.423,
              321.327
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 62,
        ty: 0,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              21.883,
              21.883
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  340.883,
                  342.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  340.883,
                  342.883
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  163.883,
                  385.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  163.883,
                  385.883
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "60"
      },
      {
        ind: 75,
        ty: 0,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              21.883,
              21.883
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  340.883,
                  343.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  340.883,
                  343.883
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  504.883,
                  173.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  504.883,
                  173.883
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "73"
      },
      {
        ind: 81,
        ty: 4,
        parent: 80,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.5,
                    8.1
                  ],
                  [
                    2.6,
                    8.1
                  ],
                  [
                    4.6,
                    10.9
                  ],
                  [
                    6.8,
                    10.9
                  ],
                  [
                    4.4,
                    7.7
                  ],
                  [
                    6.7,
                    5
                  ],
                  [
                    4.5,
                    5
                  ],
                  [
                    2.5,
                    7.3
                  ],
                  [
                    2.5,
                    1.9
                  ],
                  [
                    0.8,
                    1.9
                  ],
                  [
                    0.8,
                    10.9
                  ],
                  [
                    2.5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 80,
        ty: 3,
        parent: 79,
        ks: {
          p: {
            a: 0,
            k: [
              28.072,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 83,
        ty: 4,
        parent: 82,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ]
                ],
                v: [
                  [
                    0.4,
                    8
                  ],
                  [
                    3.6,
                    11
                  ],
                  [
                    6.8,
                    7.9
                  ],
                  [
                    3.7,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    1.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5.1,
                    8
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 82,
        ty: 3,
        parent: 79,
        ks: {
          p: {
            a: 0,
            k: [
              20.801,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 85,
        ty: 4,
        parent: 84,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.2,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    -1.8,
                    0
                  ]
                ],
                v: [
                  [
                    3.6,
                    11
                  ],
                  [
                    6.4,
                    9.2
                  ],
                  [
                    4.9,
                    8.6
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    4.9,
                    7.3
                  ],
                  [
                    6.4,
                    6.7
                  ],
                  [
                    3.6,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ],
                  [
                    3.6,
                    11
                  ]
                ],
                o: [
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.4
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -1.2
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 84,
        ty: 3,
        parent: 79,
        ks: {
          p: {
            a: 0,
            k: [
              14.036,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 87,
        ty: 4,
        parent: 86,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ]
                ],
                v: [
                  [
                    0.4,
                    8
                  ],
                  [
                    3.6,
                    11
                  ],
                  [
                    6.8,
                    7.9
                  ],
                  [
                    3.7,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    1.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5.1,
                    8
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 86,
        ty: 3,
        parent: 79,
        ks: {
          p: {
            a: 0,
            k: [
              6.765,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 89,
        ty: 4,
        parent: 88,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.2,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    -1.8,
                    0
                  ]
                ],
                v: [
                  [
                    3.6,
                    11
                  ],
                  [
                    6.4,
                    9.2
                  ],
                  [
                    4.9,
                    8.6
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    4.9,
                    7.3
                  ],
                  [
                    6.4,
                    6.7
                  ],
                  [
                    3.6,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ],
                  [
                    3.6,
                    11
                  ]
                ],
                o: [
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.4
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -1.2
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 88,
        ty: 3,
        parent: 79,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 79,
        ty: 3,
        parent: 78,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 78,
        ty: 3,
        parent: 77,
        ks: {
          p: {
            a: 0,
            k: [
              16.56,
              7.5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 93,
        ty: 4,
        parent: 92,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    -0.3,
                    0.1
                  ]
                ],
                v: [
                  [
                    4.4,
                    10.9
                  ],
                  [
                    4.4,
                    9.5
                  ],
                  [
                    4,
                    9.5
                  ],
                  [
                    3,
                    8.6
                  ],
                  [
                    3,
                    6.5
                  ],
                  [
                    4.5,
                    6.5
                  ],
                  [
                    4.5,
                    5
                  ],
                  [
                    3,
                    5
                  ],
                  [
                    3,
                    3.2
                  ],
                  [
                    1.3,
                    3.2
                  ],
                  [
                    1.3,
                    5
                  ],
                  [
                    0.2,
                    5
                  ],
                  [
                    0.2,
                    6.5
                  ],
                  [
                    1.3,
                    6.5
                  ],
                  [
                    1.3,
                    8.8
                  ],
                  [
                    3.6,
                    11
                  ],
                  [
                    4.4,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 92,
        ty: 3,
        parent: 91,
        ks: {
          p: {
            a: 0,
            k: [
              46.464,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 95,
        ty: 4,
        parent: 94,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ]
                ],
                v: [
                  [
                    2.9,
                    9.8
                  ],
                  [
                    2.2,
                    9.2
                  ],
                  [
                    3.1,
                    8.5
                  ],
                  [
                    4.1,
                    8.5
                  ],
                  [
                    4.1,
                    8.6
                  ],
                  [
                    2.9,
                    9.8
                  ]
                ],
                o: [
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.5,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ]
                ],
                v: [
                  [
                    0.5,
                    9.3
                  ],
                  [
                    2.4,
                    11
                  ],
                  [
                    4.2,
                    10.2
                  ],
                  [
                    4.2,
                    10.2
                  ],
                  [
                    4.2,
                    10.9
                  ],
                  [
                    5.8,
                    10.9
                  ],
                  [
                    5.8,
                    7.3
                  ],
                  [
                    3.3,
                    4.9
                  ],
                  [
                    0.6,
                    6.3
                  ],
                  [
                    1.9,
                    7.1
                  ],
                  [
                    3.2,
                    6.3
                  ],
                  [
                    4.1,
                    7
                  ],
                  [
                    3.7,
                    7.4
                  ],
                  [
                    3.1,
                    7.4
                  ],
                  [
                    0.5,
                    9.2
                  ]
                ],
                o: [
                  [
                    0,
                    1.1
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.6
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 94,
        ty: 3,
        parent: 91,
        ks: {
          p: {
            a: 0,
            k: [
              39.985,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 97,
        ty: 4,
        parent: 96,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    -0.5,
                    0.8
                  ]
                ],
                v: [
                  [
                    5.1,
                    10.2
                  ],
                  [
                    5.1,
                    10.2
                  ],
                  [
                    5.1,
                    10.9
                  ],
                  [
                    6.7,
                    10.9
                  ],
                  [
                    6.7,
                    2
                  ],
                  [
                    5,
                    2
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    3.3,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ],
                  [
                    3.2,
                    11
                  ],
                  [
                    5.1,
                    10.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    -0.7
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5.1,
                    8
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 96,
        ty: 3,
        parent: 91,
        ks: {
          p: {
            a: 0,
            k: [
              32.439,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 99,
        ty: 4,
        parent: 98,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    0.6,
                    3
                  ],
                  [
                    1.6,
                    4
                  ],
                  [
                    2.7,
                    3
                  ],
                  [
                    1.6,
                    1.9
                  ],
                  [
                    0.6,
                    3
                  ]
                ],
                o: [
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    10.9
                  ],
                  [
                    0.8,
                    5
                  ],
                  [
                    2.5,
                    5
                  ],
                  [
                    2.5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 98,
        ty: 3,
        parent: 91,
        ks: {
          p: {
            a: 0,
            k: [
              29.128,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 101,
        ty: 4,
        parent: 100,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    -0.5,
                    0.8
                  ]
                ],
                v: [
                  [
                    5.1,
                    10.2
                  ],
                  [
                    5.1,
                    10.2
                  ],
                  [
                    5.1,
                    10.9
                  ],
                  [
                    6.7,
                    10.9
                  ],
                  [
                    6.7,
                    2
                  ],
                  [
                    5,
                    2
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    3.3,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ],
                  [
                    3.2,
                    11
                  ],
                  [
                    5.1,
                    10.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    -0.7
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5.1,
                    8
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 100,
        ty: 3,
        parent: 91,
        ks: {
          p: {
            a: 0,
            k: [
              21.582,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 103,
        ty: 4,
        parent: 102,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.5,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ]
                ],
                v: [
                  [
                    4.6,
                    7.7
                  ],
                  [
                    4.6,
                    10.9
                  ],
                  [
                    6.3,
                    10.9
                  ],
                  [
                    6.3,
                    7.3
                  ],
                  [
                    4.2,
                    4.9
                  ],
                  [
                    2.5,
                    5.8
                  ],
                  [
                    2.5,
                    5.8
                  ],
                  [
                    2.5,
                    5
                  ],
                  [
                    0.8,
                    5
                  ],
                  [
                    0.8,
                    10.9
                  ],
                  [
                    2.5,
                    10.9
                  ],
                  [
                    2.5,
                    7.7
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    4.6,
                    7.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 102,
        ty: 3,
        parent: 91,
        ks: {
          p: {
            a: 0,
            k: [
              14.553,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 105,
        ty: 4,
        parent: 104,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ]
                ],
                v: [
                  [
                    2.9,
                    9.8
                  ],
                  [
                    2.2,
                    9.2
                  ],
                  [
                    3.1,
                    8.5
                  ],
                  [
                    4.1,
                    8.5
                  ],
                  [
                    4.1,
                    8.6
                  ],
                  [
                    2.9,
                    9.8
                  ]
                ],
                o: [
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.5,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ]
                ],
                v: [
                  [
                    0.5,
                    9.3
                  ],
                  [
                    2.4,
                    11
                  ],
                  [
                    4.2,
                    10.2
                  ],
                  [
                    4.2,
                    10.2
                  ],
                  [
                    4.2,
                    10.9
                  ],
                  [
                    5.8,
                    10.9
                  ],
                  [
                    5.8,
                    7.3
                  ],
                  [
                    3.3,
                    4.9
                  ],
                  [
                    0.6,
                    6.3
                  ],
                  [
                    1.9,
                    7.1
                  ],
                  [
                    3.2,
                    6.3
                  ],
                  [
                    4.1,
                    7
                  ],
                  [
                    3.7,
                    7.4
                  ],
                  [
                    3.1,
                    7.4
                  ],
                  [
                    0.5,
                    9.2
                  ]
                ],
                o: [
                  [
                    0,
                    1.1
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.6
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 104,
        ty: 3,
        parent: 91,
        ks: {
          p: {
            a: 0,
            k: [
              8.008,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 107,
        ty: 4,
        parent: 106,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.8,
                    10.9
                  ],
                  [
                    2.8,
                    6.8
                  ],
                  [
                    5.9,
                    10.9
                  ],
                  [
                    8.2,
                    10.9
                  ],
                  [
                    4.7,
                    6.4
                  ],
                  [
                    8,
                    2.3
                  ],
                  [
                    5.7,
                    2.3
                  ],
                  [
                    2.8,
                    6
                  ],
                  [
                    2.8,
                    2.3
                  ],
                  [
                    1,
                    2.3
                  ],
                  [
                    1,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 106,
        ty: 3,
        parent: 91,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 91,
        ty: 3,
        parent: 90,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 90,
        ty: 3,
        parent: 77,
        ks: {
          p: {
            a: 0,
            k: [
              8.293,
              -5.7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 77,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              -9,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 109,
        ty: 4,
        parent: 108,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                39,
                16.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                78,
                33
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.902,
                8e-3,
                0.471
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 108,
        ty: 3,
        parent: 76,
        ks: {
          p: {
            a: 0,
            k: [
              -15,
              -9
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 76,
        ty: 3,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              26,
              7.5
            ]
          },
          p: {
            a: 0,
            k: [
              555,
              257.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.725,
                    0.725
                  ],
                  y: [
                    0.45,
                    0.45
                  ]
                },
                o: {
                  x: [
                    0.387,
                    0.387
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 102.636,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    0.614,
                    0.614
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.277,
                    0.277
                  ],
                  y: [
                    0.553,
                    0.553
                  ]
                }
              },
              {
                t: 106.296,
                s: [
                  185.06,
                  185.06
                ],
                i: {
                  x: [
                    0.952,
                    0.952
                  ],
                  y: [
                    0.93,
                    0.93
                  ]
                },
                o: {
                  x: [
                    0.552,
                    0.552
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 109.956,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    0.657,
                    0.657
                  ],
                  y: [
                    0.997,
                    0.997
                  ]
                },
                o: {
                  x: [
                    0.329,
                    0.329
                  ],
                  y: [
                    0.655,
                    0.655
                  ]
                }
              },
              {
                t: 111.126,
                s: [
                  204.02,
                  204.02
                ],
                i: {
                  x: [
                    0.936,
                    0.936
                  ],
                  y: [
                    0.906,
                    0.906
                  ]
                },
                o: {
                  x: [
                    0.549,
                    0.549
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 112.308,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 112.68,
                s: [
                  205.97,
                  205.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.064,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  206.2,
                  206.2
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 115,
        ty: 4,
        parent: 114,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.5,
                    8.1
                  ],
                  [
                    2.6,
                    8.1
                  ],
                  [
                    4.6,
                    10.9
                  ],
                  [
                    6.8,
                    10.9
                  ],
                  [
                    4.4,
                    7.7
                  ],
                  [
                    6.7,
                    5
                  ],
                  [
                    4.5,
                    5
                  ],
                  [
                    2.5,
                    7.3
                  ],
                  [
                    2.5,
                    1.9
                  ],
                  [
                    0.8,
                    1.9
                  ],
                  [
                    0.8,
                    10.9
                  ],
                  [
                    2.5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 114,
        ty: 3,
        parent: 113,
        ks: {
          p: {
            a: 0,
            k: [
              28.072,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 117,
        ty: 4,
        parent: 116,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ]
                ],
                v: [
                  [
                    0.4,
                    8
                  ],
                  [
                    3.6,
                    11
                  ],
                  [
                    6.8,
                    7.9
                  ],
                  [
                    3.7,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    1.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5.1,
                    8
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 116,
        ty: 3,
        parent: 113,
        ks: {
          p: {
            a: 0,
            k: [
              20.801,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 119,
        ty: 4,
        parent: 118,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.2,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    -1.8,
                    0
                  ]
                ],
                v: [
                  [
                    3.6,
                    11
                  ],
                  [
                    6.4,
                    9.2
                  ],
                  [
                    4.9,
                    8.6
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    4.9,
                    7.3
                  ],
                  [
                    6.4,
                    6.7
                  ],
                  [
                    3.6,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ],
                  [
                    3.6,
                    11
                  ]
                ],
                o: [
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.4
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -1.2
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 118,
        ty: 3,
        parent: 113,
        ks: {
          p: {
            a: 0,
            k: [
              14.036,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 121,
        ty: 4,
        parent: 120,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ]
                ],
                v: [
                  [
                    0.4,
                    8
                  ],
                  [
                    3.6,
                    11
                  ],
                  [
                    6.8,
                    7.9
                  ],
                  [
                    3.7,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    1.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5.1,
                    8
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 120,
        ty: 3,
        parent: 113,
        ks: {
          p: {
            a: 0,
            k: [
              6.765,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 123,
        ty: 4,
        parent: 122,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    -0.2,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    -1.8,
                    0
                  ]
                ],
                v: [
                  [
                    3.6,
                    11
                  ],
                  [
                    6.4,
                    9.2
                  ],
                  [
                    4.9,
                    8.6
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    4.9,
                    7.3
                  ],
                  [
                    6.4,
                    6.7
                  ],
                  [
                    3.6,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ],
                  [
                    3.6,
                    11
                  ]
                ],
                o: [
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0.4
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    -1.2
                  ],
                  [
                    -1.8,
                    0
                  ],
                  [
                    0,
                    1.7
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 122,
        ty: 3,
        parent: 113,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 113,
        ty: 3,
        parent: 112,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 112,
        ty: 3,
        parent: 111,
        ks: {
          p: {
            a: 0,
            k: [
              16.56,
              7.5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 127,
        ty: 4,
        parent: 126,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    -0.3,
                    0.1
                  ]
                ],
                v: [
                  [
                    4.4,
                    10.9
                  ],
                  [
                    4.4,
                    9.5
                  ],
                  [
                    4,
                    9.5
                  ],
                  [
                    3,
                    8.6
                  ],
                  [
                    3,
                    6.5
                  ],
                  [
                    4.5,
                    6.5
                  ],
                  [
                    4.5,
                    5
                  ],
                  [
                    3,
                    5
                  ],
                  [
                    3,
                    3.2
                  ],
                  [
                    1.3,
                    3.2
                  ],
                  [
                    1.3,
                    5
                  ],
                  [
                    0.2,
                    5
                  ],
                  [
                    0.2,
                    6.5
                  ],
                  [
                    1.3,
                    6.5
                  ],
                  [
                    1.3,
                    8.8
                  ],
                  [
                    3.6,
                    11
                  ],
                  [
                    4.4,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.3
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 126,
        ty: 3,
        parent: 125,
        ks: {
          p: {
            a: 0,
            k: [
              46.464,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 129,
        ty: 4,
        parent: 128,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ]
                ],
                v: [
                  [
                    2.9,
                    9.8
                  ],
                  [
                    2.2,
                    9.2
                  ],
                  [
                    3.1,
                    8.5
                  ],
                  [
                    4.1,
                    8.5
                  ],
                  [
                    4.1,
                    8.6
                  ],
                  [
                    2.9,
                    9.8
                  ]
                ],
                o: [
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.5,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ]
                ],
                v: [
                  [
                    0.5,
                    9.3
                  ],
                  [
                    2.4,
                    11
                  ],
                  [
                    4.2,
                    10.2
                  ],
                  [
                    4.2,
                    10.2
                  ],
                  [
                    4.2,
                    10.9
                  ],
                  [
                    5.8,
                    10.9
                  ],
                  [
                    5.8,
                    7.3
                  ],
                  [
                    3.3,
                    4.9
                  ],
                  [
                    0.6,
                    6.3
                  ],
                  [
                    1.9,
                    7.1
                  ],
                  [
                    3.2,
                    6.3
                  ],
                  [
                    4.1,
                    7
                  ],
                  [
                    3.7,
                    7.4
                  ],
                  [
                    3.1,
                    7.4
                  ],
                  [
                    0.5,
                    9.2
                  ]
                ],
                o: [
                  [
                    0,
                    1.1
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.6
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 128,
        ty: 3,
        parent: 125,
        ks: {
          p: {
            a: 0,
            k: [
              39.985,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 131,
        ty: 4,
        parent: 130,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    -0.5,
                    0.8
                  ]
                ],
                v: [
                  [
                    5.1,
                    10.2
                  ],
                  [
                    5.1,
                    10.2
                  ],
                  [
                    5.1,
                    10.9
                  ],
                  [
                    6.7,
                    10.9
                  ],
                  [
                    6.7,
                    2
                  ],
                  [
                    5,
                    2
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    3.3,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ],
                  [
                    3.2,
                    11
                  ],
                  [
                    5.1,
                    10.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    -0.7
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5.1,
                    8
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 130,
        ty: 3,
        parent: 125,
        ks: {
          p: {
            a: 0,
            k: [
              32.439,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 133,
        ty: 4,
        parent: 132,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ]
                ],
                v: [
                  [
                    0.6,
                    3
                  ],
                  [
                    1.6,
                    4
                  ],
                  [
                    2.7,
                    3
                  ],
                  [
                    1.6,
                    1.9
                  ],
                  [
                    0.6,
                    3
                  ]
                ],
                o: [
                  [
                    0,
                    0.6
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    10.9
                  ],
                  [
                    0.8,
                    5
                  ],
                  [
                    2.5,
                    5
                  ],
                  [
                    2.5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 132,
        ty: 3,
        parent: 125,
        ks: {
          p: {
            a: 0,
            k: [
              29.128,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 135,
        ty: 4,
        parent: 134,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    -0.5,
                    0.8
                  ]
                ],
                v: [
                  [
                    5.1,
                    10.2
                  ],
                  [
                    5.1,
                    10.2
                  ],
                  [
                    5.1,
                    10.9
                  ],
                  [
                    6.7,
                    10.9
                  ],
                  [
                    6.7,
                    2
                  ],
                  [
                    5,
                    2
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    5,
                    5.7
                  ],
                  [
                    3.3,
                    4.9
                  ],
                  [
                    0.4,
                    8
                  ],
                  [
                    3.2,
                    11
                  ],
                  [
                    5.1,
                    10.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    -0.7
                  ],
                  [
                    -1.7,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ]
                ],
                v: [
                  [
                    2.2,
                    8
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    5.1,
                    8
                  ],
                  [
                    3.6,
                    9.5
                  ],
                  [
                    2.2,
                    8
                  ]
                ],
                o: [
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.9
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 134,
        ty: 3,
        parent: 125,
        ks: {
          p: {
            a: 0,
            k: [
              21.582,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 137,
        ty: 4,
        parent: 136,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.5,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ]
                ],
                v: [
                  [
                    4.6,
                    7.7
                  ],
                  [
                    4.6,
                    10.9
                  ],
                  [
                    6.3,
                    10.9
                  ],
                  [
                    6.3,
                    7.3
                  ],
                  [
                    4.2,
                    4.9
                  ],
                  [
                    2.5,
                    5.8
                  ],
                  [
                    2.5,
                    5.8
                  ],
                  [
                    2.5,
                    5
                  ],
                  [
                    0.8,
                    5
                  ],
                  [
                    0.8,
                    10.9
                  ],
                  [
                    2.5,
                    10.9
                  ],
                  [
                    2.5,
                    7.7
                  ],
                  [
                    3.6,
                    6.5
                  ],
                  [
                    4.6,
                    7.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 136,
        ty: 3,
        parent: 125,
        ks: {
          p: {
            a: 0,
            k: [
              14.553,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 139,
        ty: 4,
        parent: 138,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ]
                ],
                v: [
                  [
                    2.9,
                    9.8
                  ],
                  [
                    2.2,
                    9.2
                  ],
                  [
                    3.1,
                    8.5
                  ],
                  [
                    4.1,
                    8.5
                  ],
                  [
                    4.1,
                    8.6
                  ],
                  [
                    2.9,
                    9.8
                  ]
                ],
                o: [
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    -0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.5,
                    0
                  ],
                  [
                    0.5,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ]
                ],
                v: [
                  [
                    0.5,
                    9.3
                  ],
                  [
                    2.4,
                    11
                  ],
                  [
                    4.2,
                    10.2
                  ],
                  [
                    4.2,
                    10.2
                  ],
                  [
                    4.2,
                    10.9
                  ],
                  [
                    5.8,
                    10.9
                  ],
                  [
                    5.8,
                    7.3
                  ],
                  [
                    3.3,
                    4.9
                  ],
                  [
                    0.6,
                    6.3
                  ],
                  [
                    1.9,
                    7.1
                  ],
                  [
                    3.2,
                    6.3
                  ],
                  [
                    4.1,
                    7
                  ],
                  [
                    3.7,
                    7.4
                  ],
                  [
                    3.1,
                    7.4
                  ],
                  [
                    0.5,
                    9.2
                  ]
                ],
                o: [
                  [
                    0,
                    1.1
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.6
                  ],
                  [
                    -1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    -0.5
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 138,
        ty: 3,
        parent: 125,
        ks: {
          p: {
            a: 0,
            k: [
              8.008,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 141,
        ty: 4,
        parent: 140,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    2.8,
                    10.9
                  ],
                  [
                    2.8,
                    6.8
                  ],
                  [
                    5.9,
                    10.9
                  ],
                  [
                    8.2,
                    10.9
                  ],
                  [
                    4.7,
                    6.4
                  ],
                  [
                    8,
                    2.3
                  ],
                  [
                    5.7,
                    2.3
                  ],
                  [
                    2.8,
                    6
                  ],
                  [
                    2.8,
                    2.3
                  ],
                  [
                    1,
                    2.3
                  ],
                  [
                    1,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 140,
        ty: 3,
        parent: 125,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 125,
        ty: 3,
        parent: 124,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 124,
        ty: 3,
        parent: 111,
        ks: {
          p: {
            a: 0,
            k: [
              8.293,
              -5.7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 111,
        ty: 3,
        parent: 110,
        ks: {
          p: {
            a: 0,
            k: [
              -9,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 143,
        ty: 4,
        parent: 142,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                39,
                16.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                78,
                33
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.902,
                8e-3,
                0.471
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 142,
        ty: 3,
        parent: 110,
        ks: {
          p: {
            a: 0,
            k: [
              -13,
              -9
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 110,
        ty: 3,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              26,
              7.5
            ]
          },
          p: {
            a: 0,
            k: [
              123,
              480.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.725,
                    0.725
                  ],
                  y: [
                    0.45,
                    0.45
                  ]
                },
                o: {
                  x: [
                    0.387,
                    0.387
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 102.636,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    0.614,
                    0.614
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.277,
                    0.277
                  ],
                  y: [
                    0.553,
                    0.553
                  ]
                }
              },
              {
                t: 106.296,
                s: [
                  185.05,
                  185.05
                ],
                i: {
                  x: [
                    0.952,
                    0.952
                  ],
                  y: [
                    0.93,
                    0.93
                  ]
                },
                o: {
                  x: [
                    0.552,
                    0.552
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 109.956,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    0.657,
                    0.657
                  ],
                  y: [
                    0.997,
                    0.997
                  ]
                },
                o: {
                  x: [
                    0.329,
                    0.329
                  ],
                  y: [
                    0.655,
                    0.655
                  ]
                }
              },
              {
                t: 111.126,
                s: [
                  204.02,
                  204.02
                ],
                i: {
                  x: [
                    0.936,
                    0.936
                  ],
                  y: [
                    0.906,
                    0.906
                  ]
                },
                o: {
                  x: [
                    0.549,
                    0.549
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 112.308,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 112.68,
                s: [
                  205.97,
                  205.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.064,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  206.2,
                  206.2
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 207,
        ty: 4,
        td: 1,
        parent: 144,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                338.5,
                316
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                677,
                632
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 206,
        ty: 0,
        tt: 1,
        parent: 144,
        ks: {
          a: {
            a: 0,
            k: [
              398,
              417
            ]
          }
        },
        w: 1460,
        h: 1479,
        ip: 0,
        op: 283,
        st: 0,
        refId: "204"
      },
      {
        ind: 144,
        ty: 3,
        parent: 7,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              25
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 7,
        ty: 3,
        parent: 6,
        ks: {
          a: {
            a: 0,
            k: [
              341,
              341
            ]
          },
          p: {
            a: 0,
            k: [
              128,
              119
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 48,
                s: [
                  37.68,
                  37.68
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 214.2,
                s: [
                  37.68,
                  37.68
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 262.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  100,
                  100
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 209,
        ty: 4,
        parent: 208,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 208,
        ty: 3,
        parent: 6,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 6,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              271,
              231
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  }
];
const fr$1 = 60;
const h$1 = 238;
const ip$1 = 0;
const layers$1 = [
  {
    ind: 4,
    ty: 4,
    td: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 3,
    ty: 4,
    tt: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 213,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 212,
    ty: 0,
    tt: 1,
    parent: 5,
    ks: {
      a: {
        a: 0,
        k: [
          271,
          231
        ]
      }
    },
    w: 949,
    h: 888,
    ip: 0,
    op: 283,
    st: 0,
    refId: "210"
  },
  {
    ind: 5,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    parent: 0,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 0,
    ty: 3,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  }
];
const meta$1 = {
  g: "https://jitter.video"
};
const op$1 = 282;
const v$1 = "5.7.4";
const w$1 = 255;
const ExclusiveTargetingId = {
  assets: assets$1,
  fr: fr$1,
  h: h$1,
  ip: ip$1,
  layers: layers$1,
  meta: meta$1,
  op: op$1,
  v: v$1,
  w: w$1
};
const assets = [
  {
    id: "35",
    layers: [
      {
        ind: 34,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "39",
    layers: [
      {
        ind: 37,
        ty: 0,
        parent: 33,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "35"
      },
      {
        ind: 33,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 38,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "45",
    layers: [
      {
        ind: 44,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "49",
    layers: [
      {
        ind: 47,
        ty: 0,
        parent: 43,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "45"
      },
      {
        ind: 43,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 48,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "53",
    layers: [
      {
        ind: 52,
        ty: 4,
        td: 1,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 51,
        ty: 0,
        tt: 1,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "49"
      }
    ]
  },
  {
    id: "58",
    layers: [
      {
        ind: 57,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                15
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                30
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.33,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ]
                            ],
                            o: [
                              [
                                -0.14,
                                -2.19
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -2.19,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                2.33
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.32,
                                0
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                27.84,
                                4.05
                              ],
                              [
                                23.74,
                                0.22
                              ],
                              [
                                4.59,
                                0.22
                              ],
                              [
                                0.48,
                                4.05
                              ],
                              [
                                0.48,
                                18
                              ],
                              [
                                4.59,
                                22.11
                              ],
                              [
                                23.74,
                                22.11
                              ],
                              [
                                27.84,
                                18
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.55,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                0.55,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                -0.41
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                4.59,
                                2.96
                              ],
                              [
                                23.74,
                                2.96
                              ],
                              [
                                24.97,
                                3.64
                              ],
                              [
                                14.16,
                                10.89
                              ],
                              [
                                3.36,
                                3.64
                              ],
                              [
                                4.59,
                                2.96
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -0.27,
                                0
                              ],
                              [
                                -0.27,
                                0.14
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.82,
                                0
                              ]
                            ],
                            o: [
                              [
                                0,
                                0
                              ],
                              [
                                -0.82,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.27,
                                0.14
                              ],
                              [
                                0.27,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.82
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                23.74,
                                19.37
                              ],
                              [
                                4.59,
                                19.37
                              ],
                              [
                                3.22,
                                18
                              ],
                              [
                                3.22,
                                6.92
                              ],
                              [
                                13.34,
                                13.76
                              ],
                              [
                                14.16,
                                14.04
                              ],
                              [
                                14.98,
                                13.76
                              ],
                              [
                                25.11,
                                6.92
                              ],
                              [
                                25.11,
                                18
                              ],
                              [
                                23.74,
                                19.37
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "62",
    layers: [
      {
        ind: 60,
        ty: 0,
        parent: 56,
        ks: {},
        w: 38,
        h: 30,
        ip: 0,
        op: 283,
        st: 0,
        refId: "58"
      },
      {
        ind: 56,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 61,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    id: "66",
    layers: [
      {
        ind: 65,
        ty: 4,
        td: 1,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 64,
        ty: 0,
        tt: 1,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "62"
      }
    ]
  },
  {
    id: "108",
    layers: [
      {
        ind: 107,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "111",
    layers: [
      {
        ind: 110,
        ty: 0,
        parent: 106,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "108"
      },
      {
        ind: 106,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "119",
    layers: [
      {
        ind: 118,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "122",
    layers: [
      {
        ind: 121,
        ty: 0,
        parent: 117,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "119"
      },
      {
        ind: 117,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "130",
    layers: [
      {
        ind: 129,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "133",
    layers: [
      {
        ind: 132,
        ty: 0,
        parent: 128,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "130"
      },
      {
        ind: 128,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "141",
    layers: [
      {
        ind: 140,
        ty: 4,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                19,
                27
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                38,
                54
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          }
        ]
      },
      {
        ind: 0,
        ty: 4,
        ks: {
          s: {
            a: 0,
            k: [
              133.33,
              133.33
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "gr",
                it: [
                  {
                    ty: "gr",
                    it: [
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                1.63,
                                1.09
                              ],
                              [
                                0.75,
                                1.82
                              ],
                              [
                                -0.38,
                                1.93
                              ],
                              [
                                -1.39,
                                1.39
                              ],
                              [
                                -1.93,
                                0.38
                              ],
                              [
                                -1.82,
                                -0.75
                              ],
                              [
                                -1.09,
                                -1.63
                              ],
                              [
                                0,
                                -1.96
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                2.64,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.96,
                                0
                              ],
                              [
                                -1.63,
                                -1.09
                              ],
                              [
                                -0.75,
                                -1.82
                              ],
                              [
                                0.39,
                                -1.93
                              ],
                              [
                                1.39,
                                -1.39
                              ],
                              [
                                1.93,
                                -0.38
                              ],
                              [
                                1.82,
                                0.75
                              ],
                              [
                                1.09,
                                1.63
                              ],
                              [
                                0,
                                2.64
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                20.1
                              ],
                              [
                                8.58,
                                18.43
                              ],
                              [
                                4.92,
                                13.96
                              ],
                              [
                                4.35,
                                8.22
                              ],
                              [
                                7.07,
                                3.14
                              ],
                              [
                                12.16,
                                0.41
                              ],
                              [
                                17.9,
                                0.98
                              ],
                              [
                                22.36,
                                4.64
                              ],
                              [
                                24.04,
                                10.16
                              ],
                              [
                                21.12,
                                17.18
                              ],
                              [
                                14.1,
                                20.1
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.98,
                                -0.65
                              ],
                              [
                                0.45,
                                -1.09
                              ],
                              [
                                -0.23,
                                -1.16
                              ],
                              [
                                -0.83,
                                -0.84
                              ],
                              [
                                -1.16,
                                -0.23
                              ],
                              [
                                -1.09,
                                0.45
                              ],
                              [
                                -0.66,
                                0.98
                              ],
                              [
                                0,
                                1.18
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ]
                            ],
                            o: [
                              [
                                -1.18,
                                0
                              ],
                              [
                                -0.98,
                                0.66
                              ],
                              [
                                -0.45,
                                1.09
                              ],
                              [
                                0.23,
                                1.16
                              ],
                              [
                                0.84,
                                0.83
                              ],
                              [
                                1.16,
                                0.23
                              ],
                              [
                                1.09,
                                -0.45
                              ],
                              [
                                0.65,
                                -0.98
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                14.1,
                                4.2
                              ],
                              [
                                10.79,
                                5.2
                              ],
                              [
                                8.59,
                                7.88
                              ],
                              [
                                8.25,
                                11.32
                              ],
                              [
                                9.88,
                                14.38
                              ],
                              [
                                12.94,
                                16.01
                              ],
                              [
                                16.38,
                                15.67
                              ],
                              [
                                19.06,
                                13.47
                              ],
                              [
                                20.06,
                                10.16
                              ],
                              [
                                18.31,
                                5.95
                              ],
                              [
                                14.1,
                                4.2
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "sh",
                        ks: {
                          a: 0,
                          k: {
                            c: true,
                            i: [
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                1.12
                              ],
                              [
                                1.58,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                1.12,
                                -1.12
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.38,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ],
                              [
                                0.37,
                                0.38
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                1.86
                              ],
                              [
                                -2.64,
                                0
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.86,
                                -1.86
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0.37,
                                -0.37
                              ],
                              [
                                0.53,
                                0
                              ]
                            ],
                            o: [
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -1.58
                              ],
                              [
                                -1.12,
                                -1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                -1.58,
                                0
                              ],
                              [
                                -1.12,
                                1.12
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                -0.53,
                                0
                              ],
                              [
                                -0.37,
                                -0.37
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                -2.64
                              ],
                              [
                                1.86,
                                -1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                2.63,
                                0
                              ],
                              [
                                1.86,
                                1.86
                              ],
                              [
                                0,
                                0
                              ],
                              [
                                0,
                                0.53
                              ],
                              [
                                -0.37,
                                0.38
                              ],
                              [
                                0,
                                0
                              ]
                            ],
                            v: [
                              [
                                26.02,
                                39.97
                              ],
                              [
                                24.62,
                                39.39
                              ],
                              [
                                24.04,
                                37.98
                              ],
                              [
                                24.04,
                                34.01
                              ],
                              [
                                22.29,
                                29.8
                              ],
                              [
                                18.07,
                                28.05
                              ],
                              [
                                10.13,
                                28.05
                              ],
                              [
                                5.91,
                                29.8
                              ],
                              [
                                4.16,
                                34.01
                              ],
                              [
                                4.16,
                                37.98
                              ],
                              [
                                3.58,
                                39.39
                              ],
                              [
                                2.18,
                                39.97
                              ],
                              [
                                0.77,
                                39.39
                              ],
                              [
                                0.19,
                                37.98
                              ],
                              [
                                0.19,
                                34.01
                              ],
                              [
                                3.1,
                                26.99
                              ],
                              [
                                10.13,
                                24.07
                              ],
                              [
                                18.07,
                                24.07
                              ],
                              [
                                25.1,
                                26.99
                              ],
                              [
                                28.01,
                                34.01
                              ],
                              [
                                28.01,
                                37.98
                              ],
                              [
                                27.43,
                                39.39
                              ],
                              [
                                26.02,
                                39.97
                              ]
                            ]
                          }
                        }
                      },
                      {
                        ty: "fl",
                        c: {
                          a: 0,
                          k: [
                            0.18,
                            0.22,
                            0.29,
                            1
                          ]
                        },
                        o: {
                          a: 0,
                          k: 100
                        }
                      },
                      {
                        ty: "tr",
                        o: {
                          a: 0,
                          k: 100
                        }
                      }
                    ]
                  },
                  {
                    ty: "tr",
                    o: {
                      a: 0,
                      k: 100
                    }
                  }
                ]
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: "144",
    layers: [
      {
        ind: 143,
        ty: 0,
        parent: 139,
        ks: {},
        w: 38,
        h: 54,
        ip: 0,
        op: 283,
        st: 0,
        refId: "141"
      },
      {
        ind: 139,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              13,
              5
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "158",
    layers: [
      {
        ind: 157,
        ty: 4,
        parent: 156,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    14,
                    14
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    33.37,
                    33.37
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    14,
                    14
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    28,
                    28
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.118,
                    0.278,
                    0.663
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 5.37
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 156,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              3,
              3
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "161",
    layers: [
      {
        ind: 113,
        ty: 0,
        parent: 105,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "111"
      },
      {
        ind: 105,
        ty: 3,
        parent: 104,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 114,
        ty: 4,
        parent: 104,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 104,
        ty: 3,
        parent: 103,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 0,
            k: [
              523.021,
              496.171
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  69.81,
                  69.81
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  60.34,
                  60.34
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  57.37,
                  57.37
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  56.43,
                  56.43
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  56.14,
                  56.14
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  56,
                  56
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 124,
        ty: 0,
        parent: 116,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "122"
      },
      {
        ind: 116,
        ty: 3,
        parent: 115,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 125,
        ty: 4,
        parent: 115,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.89,
                0.949,
                0.984
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 115,
        ty: 3,
        parent: 103,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 0,
            k: [
              184.706,
              106.84
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  69.81,
                  69.81
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  60.34,
                  60.34
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  57.37,
                  57.37
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  56.43,
                  56.43
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  56.14,
                  56.14
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  56,
                  56
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  56,
                  56
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 135,
        ty: 0,
        parent: 127,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "133"
      },
      {
        ind: 127,
        ty: 3,
        parent: 126,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 136,
        ty: 4,
        parent: 126,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  h: 1
                },
                {
                  t: 91.2,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  i: {
                    x: 0.613,
                    y: 0.589
                  },
                  o: {
                    x: 0.321,
                    y: 0.033
                  }
                },
                {
                  t: 93.25128,
                  s: [
                    0.973,
                    0.941,
                    0.98
                  ],
                  i: {
                    x: 0.591,
                    y: 0.993
                  },
                  o: {
                    x: 0.319,
                    y: 0.678
                  }
                },
                {
                  t: 95.30256,
                  s: [
                    1,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.447,
                    y: 0.987
                  },
                  o: {
                    x: 0.279,
                    y: 0.02
                  }
                },
                {
                  t: 99.40512,
                  s: [
                    0.992,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.442,
                    y: 0.994
                  },
                  o: {
                    x: 0.279,
                    y: 0
                  }
                },
                {
                  t: 103.55652,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                },
                {
                  t: 282,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 126,
        ty: 3,
        parent: 103,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 181.2,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 194.94,
                s: [
                  122.95,
                  412.99
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.4,
                s: [
                  122.95,
                  420.99
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  122.95,
                  420.99
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  112.84,
                  112.84
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  116.86,
                  116.86
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  118.13,
                  118.13
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  118.53,
                  118.53
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  118.65,
                  118.65
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  118.71,
                  118.71
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 146,
        ty: 0,
        parent: 138,
        ks: {
          a: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          },
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        w: 63.5988,
        h: 63.5988,
        ip: 0,
        op: 283,
        st: 0,
        refId: "144"
      },
      {
        ind: 138,
        ty: 3,
        parent: 137,
        ks: {
          p: {
            a: 0,
            k: [
              10.6,
              10.6
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 147,
        ty: 4,
        parent: 137,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                42.399,
                42.399
              ]
            },
            r: {
              a: 0,
              k: 45
            },
            s: {
              a: 0,
              k: [
                84.798,
                84.798
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 1,
              k: [
                {
                  t: 0,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  h: 1
                },
                {
                  t: 91.2,
                  s: [
                    0.89,
                    0.949,
                    0.984
                  ],
                  i: {
                    x: 0.613,
                    y: 0.589
                  },
                  o: {
                    x: 0.321,
                    y: 0.033
                  }
                },
                {
                  t: 93.25128,
                  s: [
                    0.973,
                    0.941,
                    0.98
                  ],
                  i: {
                    x: 0.591,
                    y: 0.993
                  },
                  o: {
                    x: 0.319,
                    y: 0.678
                  }
                },
                {
                  t: 95.30256,
                  s: [
                    1,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.447,
                    y: 0.987
                  },
                  o: {
                    x: 0.279,
                    y: 0.02
                  }
                },
                {
                  t: 99.40512,
                  s: [
                    0.992,
                    0.937,
                    0.98
                  ],
                  i: {
                    x: 0.442,
                    y: 0.994
                  },
                  o: {
                    x: 0.279,
                    y: 0
                  }
                },
                {
                  t: 103.55652,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                },
                {
                  t: 282,
                  s: [
                    0.996,
                    0.937,
                    0.98
                  ],
                  h: 1
                }
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 137,
        ty: 3,
        parent: 103,
        ks: {
          a: {
            a: 0,
            k: [
              42.399,
              42.399
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 181.2,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 194.94,
                s: [
                  555.399,
                  189.399
                ],
                i: {
                  x: [
                    1,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 206.4,
                s: [
                  555.399,
                  197.399
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  555.399,
                  197.399
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0.654,
                    0.654
                  ],
                  y: [
                    0.309,
                    0.309
                  ]
                },
                o: {
                  x: [
                    0.322,
                    0.322
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 97.452,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.605,
                    0.605
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.265,
                    0.265
                  ],
                  y: [
                    0.53,
                    0.53
                  ]
                }
              },
              {
                t: 100.956,
                s: [
                  112.83,
                  112.83
                ],
                i: {
                  x: [
                    0.574,
                    0.574
                  ],
                  y: [
                    0.149,
                    0.149
                  ]
                },
                o: {
                  x: [
                    0.227,
                    0.227
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 104.46,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.607,
                    0.607
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.269,
                    0.269
                  ],
                  y: [
                    0.538,
                    0.538
                  ]
                }
              },
              {
                t: 106.422,
                s: [
                  116.86,
                  116.86
                ],
                i: {
                  x: [
                    0.746,
                    0.746
                  ],
                  y: [
                    0.493,
                    0.493
                  ]
                },
                o: {
                  x: [
                    0.406,
                    0.406
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 108.384,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.836,
                    0.836
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.469,
                    0.469
                  ],
                  y: [
                    0.937,
                    0.937
                  ]
                }
              },
              {
                t: 109.488,
                s: [
                  118.12,
                  118.12
                ],
                i: {
                  x: [
                    0.589,
                    0.589
                  ],
                  y: [
                    0.181,
                    0.181
                  ]
                },
                o: {
                  x: [
                    0.251,
                    0.251
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 110.586,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    0.568,
                    0.568
                  ],
                  y: [
                    0.996,
                    0.996
                  ]
                },
                o: {
                  x: [
                    0.213,
                    0.213
                  ],
                  y: [
                    0.426,
                    0.426
                  ]
                }
              },
              {
                t: 111.204,
                s: [
                  118.52,
                  118.52
                ],
                i: {
                  x: [
                    0.521,
                    0.521
                  ],
                  y: [
                    0.045,
                    0.045
                  ]
                },
                o: {
                  x: [
                    0.151,
                    0.151
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 111.828,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    0.956,
                    0.956
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.535,
                    0.535
                  ],
                  y: [
                    1,
                    1
                  ]
                }
              },
              {
                t: 112.176,
                s: [
                  118.65,
                  118.65
                ],
                i: {
                  x: [
                    0.561,
                    0.561
                  ],
                  y: [
                    0.127,
                    0.127
                  ]
                },
                o: {
                  x: [
                    0.222,
                    0.222
                  ],
                  y: [
                    4e-3,
                    4e-3
                  ]
                }
              },
              {
                t: 112.524,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.208,
                s: [
                  118.7,
                  118.7
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.4,
                s: [
                  118.71,
                  118.71
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  118.71,
                  118.71
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 149,
        ty: 4,
        parent: 148,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    341,
                    341
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    684.623,
                    684.623
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    341,
                    341
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    682,
                    682
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 148,
        ty: 3,
        parent: 103,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 151,
        ty: 4,
        parent: 150,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    237.388,
                    237.388
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    477.4,
                    477.4
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    237.388,
                    237.388
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    474.777,
                    474.777
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 150,
        ty: 3,
        parent: 103,
        ks: {
          p: {
            a: 0,
            k: [
              103.612,
              103.612
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 153,
        ty: 4,
        parent: 152,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    131.154,
                    131.154
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    264.931,
                    264.931
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    131.154,
                    131.154
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    262.308,
                    262.308
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 152,
        ty: 3,
        parent: 103,
        ks: {
          p: {
            a: 0,
            k: [
              209.846,
              209.846
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 155,
        ty: 4,
        parent: 154,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "gr",
            it: [
              {
                ty: "rc",
                p: {
                  a: 0,
                  k: [
                    45.904,
                    45.904
                  ]
                },
                r: {
                  a: 0,
                  k: 0
                },
                s: {
                  a: 0,
                  k: [
                    94.431,
                    94.431
                  ]
                }
              },
              {
                ty: "fl",
                c: {
                  a: 0,
                  k: [
                    0,
                    0,
                    0,
                    0
                  ]
                },
                o: {
                  a: 0,
                  k: 0
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          },
          {
            ty: "gr",
            it: [
              {
                ty: "el",
                p: {
                  a: 0,
                  k: [
                    45.904,
                    45.904
                  ]
                },
                s: {
                  a: 0,
                  k: [
                    91.808,
                    91.808
                  ]
                }
              },
              {
                ty: "st",
                c: {
                  a: 0,
                  k: [
                    0.933,
                    0.941,
                    0.957
                  ]
                },
                lc: 2,
                lj: 1,
                ml: 10,
                o: {
                  a: 0,
                  k: 100
                },
                w: {
                  a: 0,
                  k: 2.623
                }
              },
              {
                ty: "tr",
                o: {
                  a: 0,
                  k: 100
                }
              }
            ]
          }
        ]
      },
      {
        ind: 154,
        ty: 3,
        parent: 103,
        ks: {
          p: {
            a: 0,
            k: [
              295.096,
              295.096
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 160,
        ty: 0,
        parent: 103,
        ks: {
          a: {
            a: 0,
            k: [
              17,
              17
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 46.8,
                s: [
                  100
                ],
                i: {
                  x: 0.75,
                  y: 1
                },
                o: {
                  x: 0.47,
                  y: 0
                }
              },
              {
                t: 65.6352,
                s: [
                  184
                ],
                i: {
                  x: 0.04,
                  y: 1
                },
                o: {
                  x: 0.25,
                  y: 0
                }
              },
              {
                t: 90,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 0,
            k: [
              341,
              341
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 46.8,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 94.8,
                s: [
                  4320.09,
                  4320.09
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  4320.09,
                  4320.09
                ],
                h: 1
              }
            ]
          }
        },
        w: 34,
        h: 34,
        ip: 0,
        op: 283,
        st: 0,
        refId: "158"
      },
      {
        ind: 103,
        ty: 3,
        parent: 102,
        ks: {
          p: {
            a: 0,
            k: [
              -4,
              -23
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 102,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              398,
              417
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  },
  {
    id: "167",
    layers: [
      {
        ind: 14,
        ty: 4,
        parent: 13,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -3,
                    0
                  ],
                  [
                    -0.7,
                    1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    12.9,
                    20.8
                  ],
                  [
                    12.9,
                    7.9
                  ],
                  [
                    9.6,
                    7.9
                  ],
                  [
                    9.6,
                    15.3
                  ],
                  [
                    7.1,
                    18
                  ],
                  [
                    4.8,
                    15.3
                  ],
                  [
                    4.8,
                    7.9
                  ],
                  [
                    1.4,
                    7.9
                  ],
                  [
                    1.4,
                    16.1
                  ],
                  [
                    6,
                    21.1
                  ],
                  [
                    9.6,
                    19.2
                  ],
                  [
                    9.6,
                    19.2
                  ],
                  [
                    9.6,
                    20.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    2.9
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 13,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              104.097,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 16,
        ty: 4,
        parent: 15,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    3,
                    0
                  ],
                  [
                    0.9,
                    -1.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    -1
                  ]
                ],
                v: [
                  [
                    7,
                    12.4
                  ],
                  [
                    7,
                    20.8
                  ],
                  [
                    10.3,
                    20.8
                  ],
                  [
                    10.3,
                    12.2
                  ],
                  [
                    5.5,
                    7.7
                  ],
                  [
                    0.5,
                    10.3
                  ],
                  [
                    3,
                    11.9
                  ],
                  [
                    5.2,
                    10.6
                  ],
                  [
                    7,
                    12.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -2.9
                  ],
                  [
                    -2.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    -0.8
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 15,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              92.337,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 18,
        ty: 4,
        parent: 17,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    -2.5
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    4.3
                  ],
                  [
                    3.1,
                    0
                  ],
                  [
                    0.3,
                    0
                  ]
                ],
                v: [
                  [
                    5.9,
                    7.8
                  ],
                  [
                    5.9,
                    10.7
                  ],
                  [
                    6.7,
                    10.7
                  ],
                  [
                    9.3,
                    14.1
                  ],
                  [
                    6.5,
                    17.9
                  ],
                  [
                    6.3,
                    17.9
                  ],
                  [
                    3.8,
                    11.4
                  ],
                  [
                    0.3,
                    11.4
                  ],
                  [
                    4.1,
                    20.8
                  ],
                  [
                    6.7,
                    20.8
                  ],
                  [
                    12.7,
                    14
                  ],
                  [
                    7.2,
                    7.7
                  ],
                  [
                    5.9,
                    7.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    2.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    -4.1
                  ],
                  [
                    -0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 17,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              78.708,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 20,
        ty: 4,
        parent: 19,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    -0.8
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    -3.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    0
                  ],
                  [
                    0.6,
                    -1.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2,
                    0
                  ],
                  [
                    0,
                    -1.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.3,
                    0.7
                  ],
                  [
                    -1,
                    -0.1
                  ]
                ],
                v: [
                  [
                    15.8,
                    10.5
                  ],
                  [
                    15.8,
                    7.6
                  ],
                  [
                    11.9,
                    8.8
                  ],
                  [
                    7.7,
                    7.7
                  ],
                  [
                    1.4,
                    13.4
                  ],
                  [
                    1.4,
                    20.8
                  ],
                  [
                    4.7,
                    20.8
                  ],
                  [
                    4.7,
                    19.3
                  ],
                  [
                    7.9,
                    16.5
                  ],
                  [
                    8.6,
                    16.5
                  ],
                  [
                    8.6,
                    13.6
                  ],
                  [
                    7.9,
                    13.5
                  ],
                  [
                    4.7,
                    16
                  ],
                  [
                    4.7,
                    13.4
                  ],
                  [
                    7.7,
                    10.6
                  ],
                  [
                    10.6,
                    13.5
                  ],
                  [
                    10.6,
                    20.8
                  ],
                  [
                    13.9,
                    20.8
                  ],
                  [
                    13.9,
                    13.3
                  ],
                  [
                    13.5,
                    10.9
                  ],
                  [
                    15.8,
                    10.5
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    -0.1
                  ],
                  [
                    -1,
                    -0.7
                  ],
                  [
                    -4.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.7
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.5,
                    -0.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 19,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              62.79,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 22,
        ty: 4,
        parent: 21,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    3,
                    0
                  ],
                  [
                    0.9,
                    -1.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    -1
                  ]
                ],
                v: [
                  [
                    7,
                    12.4
                  ],
                  [
                    7,
                    20.8
                  ],
                  [
                    10.3,
                    20.8
                  ],
                  [
                    10.3,
                    12.2
                  ],
                  [
                    5.5,
                    7.7
                  ],
                  [
                    0.5,
                    10.3
                  ],
                  [
                    3,
                    11.9
                  ],
                  [
                    5.2,
                    10.6
                  ],
                  [
                    7,
                    12.4
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -2.9
                  ],
                  [
                    -2.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    -0.8
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 21,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              51.03,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 24,
        ty: 4,
        parent: 23,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    0,
                    -1.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    3.8,
                    0
                  ],
                  [
                    0.3,
                    -3.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    5.9,
                    20.8
                  ],
                  [
                    5.9,
                    13.1
                  ],
                  [
                    3.9,
                    12.3
                  ],
                  [
                    7.1,
                    10.6
                  ],
                  [
                    10,
                    13.6
                  ],
                  [
                    10,
                    20.8
                  ],
                  [
                    13.3,
                    20.8
                  ],
                  [
                    13.3,
                    13.1
                  ],
                  [
                    7.3,
                    7.7
                  ],
                  [
                    0.7,
                    13.2
                  ],
                  [
                    2.6,
                    14
                  ],
                  [
                    2.6,
                    20.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    -0.9
                  ],
                  [
                    1.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -3.2
                  ],
                  [
                    -3.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 23,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              36.309,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 26,
        ty: 4,
        parent: 25,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ],
                  [
                    -2.3,
                    0
                  ]
                ],
                v: [
                  [
                    4.8,
                    18.8
                  ],
                  [
                    7.9,
                    18.8
                  ],
                  [
                    7.9,
                    16.1
                  ],
                  [
                    5,
                    16.1
                  ],
                  [
                    4.2,
                    15.5
                  ],
                  [
                    4.2,
                    15
                  ],
                  [
                    1.5,
                    15
                  ],
                  [
                    1.4,
                    16
                  ],
                  [
                    4.8,
                    18.8
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    2.1
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    -2.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ]
                ],
                v: [
                  [
                    1.4,
                    10.4
                  ],
                  [
                    4.8,
                    13.3
                  ],
                  [
                    7.9,
                    13.3
                  ],
                  [
                    7.9,
                    10.5
                  ],
                  [
                    5,
                    10.5
                  ],
                  [
                    4.2,
                    9.9
                  ],
                  [
                    4.2,
                    9.4
                  ],
                  [
                    1.5,
                    9.4
                  ],
                  [
                    1.4,
                    10.4
                  ]
                ],
                o: [
                  [
                    0,
                    2.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 25,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              26.964,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 28,
        ty: 4,
        parent: 27,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    2.4
                  ],
                  [
                    3,
                    0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    -0.9,
                    0
                  ],
                  [
                    -0.5,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.6,
                    0
                  ],
                  [
                    0,
                    -2.4
                  ],
                  [
                    -2.6,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0.6,
                    1.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -2.9,
                    0
                  ]
                ],
                v: [
                  [
                    6.4,
                    21.1
                  ],
                  [
                    11.6,
                    17
                  ],
                  [
                    7.1,
                    13
                  ],
                  [
                    6.1,
                    12.8
                  ],
                  [
                    4.7,
                    11.5
                  ],
                  [
                    6.3,
                    10.4
                  ],
                  [
                    8.9,
                    11.9
                  ],
                  [
                    11.4,
                    10.4
                  ],
                  [
                    6.3,
                    7.7
                  ],
                  [
                    1.4,
                    11.7
                  ],
                  [
                    5.5,
                    15.7
                  ],
                  [
                    6.4,
                    15.9
                  ],
                  [
                    8.3,
                    17.2
                  ],
                  [
                    6.5,
                    18.3
                  ],
                  [
                    3.3,
                    16.5
                  ],
                  [
                    0.7,
                    18
                  ],
                  [
                    6.4,
                    21.1
                  ]
                ],
                o: [
                  [
                    3.3,
                    0
                  ],
                  [
                    0,
                    -2.1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    -0.2
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    1.3,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    -1.5
                  ],
                  [
                    -2.7,
                    0
                  ],
                  [
                    0,
                    2.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.4,
                    0.3
                  ],
                  [
                    0,
                    0.6
                  ],
                  [
                    -1.5,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1,
                    1.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 27,
        ty: 3,
        parent: 12,
        ks: {
          p: {
            a: 0,
            k: [
              14.301,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 30,
        ty: 4,
        parent: 29,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    3.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0,
                    1.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -3.8,
                    0
                  ]
                ],
                v: [
                  [
                    7.1,
                    21.1
                  ],
                  [
                    12.9,
                    15.6
                  ],
                  [
                    12.9,
                    3.4
                  ],
                  [
                    9.6,
                    3.4
                  ],
                  [
                    9.6,
                    15.7
                  ],
                  [
                    7.1,
                    18
                  ],
                  [
                    4.8,
                    15.6
                  ],
                  [
                    4.8,
                    7.9
                  ],
                  [
                    1.4,
                    7.9
                  ],
                  [
                    1.4,
                    15.7
                  ],
                  [
                    7.1,
                    21.1
                  ]
                ],
                o: [
                  [
                    3.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.4
                  ],
                  [
                    -1.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    3.3
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 29,
        ty: 3,
        parent: 12,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 12,
        ty: 3,
        parent: 11,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 11,
        ty: 3,
        parent: 10,
        ks: {
          p: {
            a: 0,
            k: [
              -5.793,
              1.9
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 10,
        ty: 3,
        parent: 9,
        ks: {
          p: {
            a: 0,
            k: [
              22,
              7
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 31,
        ty: 4,
        parent: 9,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                76,
                21
              ]
            },
            r: {
              a: 0,
              k: 131
            },
            s: {
              a: 0,
              k: [
                152,
                42
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.118,
                0.278,
                0.663
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 9,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 0,
            k: [
              -10,
              54
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 42,
        ty: 4,
        td: 1,
        parent: 32,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                21.883,
                21.883
              ]
            },
            r: {
              a: 0,
              k: 61
            },
            s: {
              a: 0,
              k: [
                43.767,
                43.767
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 41,
        ty: 0,
        tt: 1,
        parent: 32,
        ks: {},
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "39"
      },
      {
        ind: 32,
        ty: 3,
        parent: 8,
        ks: {
          p: {
            a: 0,
            k: [
              43.694,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 8,
        ty: 3,
        parent: 7,
        ks: {
          p: {
            a: 0,
            k: [
              275.423,
              321.327
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 55,
        ty: 0,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              21.883,
              21.883
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  340.883,
                  342.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  340.883,
                  342.883
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  163.883,
                  385.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  163.883,
                  385.883
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "53"
      },
      {
        ind: 68,
        ty: 0,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              21.883,
              21.883
            ]
          },
          o: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  100
                ],
                h: 1
              },
              {
                t: 184.8,
                s: [
                  0
                ],
                h: 1
              },
              {
                t: 282,
                s: [
                  0
                ],
                h: 1
              }
            ]
          },
          p: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  340.883,
                  343.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  340.883,
                  343.883
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  504.883,
                  173.883
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  504.883,
                  173.883
                ],
                h: 1
              }
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 149.4,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 185.4,
                s: [
                  150,
                  150
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  150,
                  150
                ],
                h: 1
              }
            ]
          }
        },
        w: 44,
        h: 44,
        ip: 0,
        op: 283,
        st: 0,
        refId: "66"
      },
      {
        ind: 74,
        ty: 4,
        parent: 73,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    0.3
                  ],
                  [
                    -0.1,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.5,
                    0
                  ]
                ],
                v: [
                  [
                    3.5,
                    11
                  ],
                  [
                    6.3,
                    8.3
                  ],
                  [
                    6.3,
                    7.7
                  ],
                  [
                    5.6,
                    6.1
                  ],
                  [
                    6.5,
                    4.6
                  ],
                  [
                    6.5,
                    3.9
                  ],
                  [
                    4.8,
                    3.9
                  ],
                  [
                    4.7,
                    4.7
                  ],
                  [
                    4.1,
                    5.4
                  ],
                  [
                    3.9,
                    5.4
                  ],
                  [
                    3.9,
                    6.8
                  ],
                  [
                    3.9,
                    6.8
                  ],
                  [
                    4.6,
                    7.6
                  ],
                  [
                    4.6,
                    8.4
                  ],
                  [
                    3.5,
                    9.5
                  ],
                  [
                    2.5,
                    8.3
                  ],
                  [
                    2.5,
                    8.1
                  ],
                  [
                    3.3,
                    5.6
                  ],
                  [
                    1.8,
                    4.2
                  ],
                  [
                    0.6,
                    4.2
                  ],
                  [
                    0.6,
                    5.7
                  ],
                  [
                    1.2,
                    5.7
                  ],
                  [
                    1.6,
                    6
                  ],
                  [
                    0.7,
                    8.1
                  ],
                  [
                    0.7,
                    8.6
                  ],
                  [
                    3.5,
                    11
                  ]
                ],
                o: [
                  [
                    1.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0.5,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.7,
                    0.7
                  ],
                  [
                    4.7,
                    3.2
                  ],
                  [
                    6.2,
                    3.2
                  ],
                  [
                    6.2,
                    0.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 73,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              27.313,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 76,
        ty: 4,
        parent: 75,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0.3
                  ],
                  [
                    -0.1,
                    0.3
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.5,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.4,
                    -0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.3
                  ]
                ],
                v: [
                  [
                    3,
                    8.9
                  ],
                  [
                    3,
                    4.8
                  ],
                  [
                    3.5,
                    3.8
                  ],
                  [
                    4.2,
                    2.9
                  ],
                  [
                    4.4,
                    2
                  ],
                  [
                    2.1,
                    -0.1
                  ],
                  [
                    -0.2,
                    1.2
                  ],
                  [
                    1,
                    2.1
                  ],
                  [
                    2,
                    1.4
                  ],
                  [
                    2.8,
                    2.1
                  ],
                  [
                    2.3,
                    3.1
                  ],
                  [
                    1.9,
                    3.5
                  ],
                  [
                    1.2,
                    4.9
                  ],
                  [
                    1.2,
                    9.1
                  ],
                  [
                    3.1,
                    11
                  ],
                  [
                    3.7,
                    10.9
                  ],
                  [
                    3.7,
                    9.5
                  ],
                  [
                    3.5,
                    9.5
                  ],
                  [
                    3,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.3,
                    -0.3
                  ],
                  [
                    0.2,
                    -0.3
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.4
                  ],
                  [
                    0.5,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 75,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              23.221,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 78,
        ty: 4,
        parent: 77,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0.4,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    4.2
                  ],
                  [
                    0.8,
                    10.9
                  ],
                  [
                    2.6,
                    10.9
                  ],
                  [
                    2.6,
                    7
                  ],
                  [
                    3.9,
                    5.6
                  ],
                  [
                    5.1,
                    7
                  ],
                  [
                    5.1,
                    10.9
                  ],
                  [
                    6.8,
                    10.9
                  ],
                  [
                    6.8,
                    6.7
                  ],
                  [
                    4.4,
                    4
                  ],
                  [
                    2.6,
                    5
                  ],
                  [
                    2.6,
                    5
                  ],
                  [
                    2.6,
                    4.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    1.1,
                    3.2
                  ],
                  [
                    6.8,
                    3.2
                  ],
                  [
                    6.8,
                    0.7
                  ],
                  [
                    5.2,
                    0.7
                  ],
                  [
                    5.2,
                    1.9
                  ],
                  [
                    1.1,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    5.2,
                    0.7
                  ],
                  [
                    5.2,
                    3.2
                  ],
                  [
                    6.8,
                    3.2
                  ],
                  [
                    6.8,
                    0.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 77,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              15.631,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 80,
        ty: 4,
        parent: 79,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    6.8,
                    10.9
                  ],
                  [
                    6.8,
                    4.2
                  ],
                  [
                    5,
                    4.2
                  ],
                  [
                    5,
                    8
                  ],
                  [
                    3.7,
                    9.4
                  ],
                  [
                    2.5,
                    8
                  ],
                  [
                    2.5,
                    4.2
                  ],
                  [
                    0.7,
                    4.2
                  ],
                  [
                    0.7,
                    8.4
                  ],
                  [
                    3.1,
                    11
                  ],
                  [
                    5,
                    10
                  ],
                  [
                    5,
                    10
                  ],
                  [
                    5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 79,
        ty: 3,
        parent: 72,
        ks: {
          p: {
            a: 0,
            k: [
              8.041,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 82,
        ty: 4,
        parent: 81,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0.3,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.2,
                    0
                  ],
                  [
                    0,
                    -1.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ]
                ],
                v: [
                  [
                    4.1,
                    8.6
                  ],
                  [
                    4.5,
                    8.6
                  ],
                  [
                    4.5,
                    7.1
                  ],
                  [
                    4.2,
                    7.1
                  ],
                  [
                    2.5,
                    8.4
                  ],
                  [
                    2.5,
                    7
                  ],
                  [
                    4,
                    5.5
                  ],
                  [
                    5.6,
                    7
                  ],
                  [
                    5.6,
                    10.9
                  ],
                  [
                    7.3,
                    10.9
                  ],
                  [
                    7.3,
                    7
                  ],
                  [
                    4,
                    4
                  ],
                  [
                    0.7,
                    7
                  ],
                  [
                    0.7,
                    10.9
                  ],
                  [
                    2.5,
                    10.9
                  ],
                  [
                    2.5,
                    10.1
                  ],
                  [
                    4.1,
                    8.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.8
                  ],
                  [
                    -2.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 81,
        ty: 3,
        parent: 72,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 72,
        ty: 3,
        parent: 71,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 71,
        ty: 3,
        parent: 70,
        ks: {
          p: {
            a: 0,
            k: [
              16.807,
              0.9
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 70,
        ty: 3,
        parent: 69,
        ks: {
          p: {
            a: 0,
            k: [
              -9,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 84,
        ty: 4,
        parent: 83,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                39,
                9.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                78,
                19
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.902,
                8e-3,
                0.471
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 83,
        ty: 3,
        parent: 69,
        ks: {
          p: {
            a: 0,
            k: [
              -15,
              -2
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 69,
        ty: 3,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              26,
              7.5
            ]
          },
          p: {
            a: 0,
            k: [
              555,
              235.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.725,
                    0.725
                  ],
                  y: [
                    0.45,
                    0.45
                  ]
                },
                o: {
                  x: [
                    0.387,
                    0.387
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 102.636,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    0.614,
                    0.614
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.277,
                    0.277
                  ],
                  y: [
                    0.553,
                    0.553
                  ]
                }
              },
              {
                t: 106.296,
                s: [
                  185.06,
                  185.06
                ],
                i: {
                  x: [
                    0.952,
                    0.952
                  ],
                  y: [
                    0.93,
                    0.93
                  ]
                },
                o: {
                  x: [
                    0.552,
                    0.552
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 109.956,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    0.657,
                    0.657
                  ],
                  y: [
                    0.997,
                    0.997
                  ]
                },
                o: {
                  x: [
                    0.329,
                    0.329
                  ],
                  y: [
                    0.655,
                    0.655
                  ]
                }
              },
              {
                t: 111.126,
                s: [
                  204.02,
                  204.02
                ],
                i: {
                  x: [
                    0.936,
                    0.936
                  ],
                  y: [
                    0.906,
                    0.906
                  ]
                },
                o: {
                  x: [
                    0.549,
                    0.549
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 112.308,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 112.68,
                s: [
                  205.97,
                  205.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.064,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  206.2,
                  206.2
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 90,
        ty: 4,
        parent: 89,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.5,
                    0.3
                  ],
                  [
                    -0.1,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.6,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.2
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.5,
                    0
                  ]
                ],
                v: [
                  [
                    3.5,
                    11
                  ],
                  [
                    6.3,
                    8.3
                  ],
                  [
                    6.3,
                    7.7
                  ],
                  [
                    5.6,
                    6.1
                  ],
                  [
                    6.5,
                    4.6
                  ],
                  [
                    6.5,
                    3.9
                  ],
                  [
                    4.8,
                    3.9
                  ],
                  [
                    4.7,
                    4.7
                  ],
                  [
                    4.1,
                    5.4
                  ],
                  [
                    3.9,
                    5.4
                  ],
                  [
                    3.9,
                    6.8
                  ],
                  [
                    3.9,
                    6.8
                  ],
                  [
                    4.6,
                    7.6
                  ],
                  [
                    4.6,
                    8.4
                  ],
                  [
                    3.5,
                    9.5
                  ],
                  [
                    2.5,
                    8.3
                  ],
                  [
                    2.5,
                    8.1
                  ],
                  [
                    3.3,
                    5.6
                  ],
                  [
                    1.8,
                    4.2
                  ],
                  [
                    0.6,
                    4.2
                  ],
                  [
                    0.6,
                    5.7
                  ],
                  [
                    1.2,
                    5.7
                  ],
                  [
                    1.6,
                    6
                  ],
                  [
                    0.7,
                    8.1
                  ],
                  [
                    0.7,
                    8.6
                  ],
                  [
                    3.5,
                    11
                  ]
                ],
                o: [
                  [
                    1.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.7
                  ],
                  [
                    0.5,
                    -0.2
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.4,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    0
                  ],
                  [
                    0,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.8
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    4.7,
                    0.7
                  ],
                  [
                    4.7,
                    3.2
                  ],
                  [
                    6.2,
                    3.2
                  ],
                  [
                    6.2,
                    0.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 89,
        ty: 3,
        parent: 88,
        ks: {
          p: {
            a: 0,
            k: [
              27.313,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 92,
        ty: 4,
        parent: 91,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.4,
                    0.3
                  ],
                  [
                    -0.1,
                    0.3
                  ],
                  [
                    0,
                    0.4
                  ],
                  [
                    1.4,
                    0
                  ],
                  [
                    0.5,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.4,
                    -0.4
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.2,
                    0
                  ],
                  [
                    -0.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.3
                  ]
                ],
                v: [
                  [
                    3,
                    8.9
                  ],
                  [
                    3,
                    4.8
                  ],
                  [
                    3.5,
                    3.8
                  ],
                  [
                    4.2,
                    2.9
                  ],
                  [
                    4.4,
                    2
                  ],
                  [
                    2.1,
                    -0.1
                  ],
                  [
                    -0.2,
                    1.2
                  ],
                  [
                    1,
                    2.1
                  ],
                  [
                    2,
                    1.4
                  ],
                  [
                    2.8,
                    2.1
                  ],
                  [
                    2.3,
                    3.1
                  ],
                  [
                    1.9,
                    3.5
                  ],
                  [
                    1.2,
                    4.9
                  ],
                  [
                    1.2,
                    9.1
                  ],
                  [
                    3.1,
                    11
                  ],
                  [
                    3.7,
                    10.9
                  ],
                  [
                    3.7,
                    9.5
                  ],
                  [
                    3.5,
                    9.5
                  ],
                  [
                    3,
                    8.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.4
                  ],
                  [
                    0.3,
                    -0.3
                  ],
                  [
                    0.2,
                    -0.3
                  ],
                  [
                    0,
                    -1.2
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.2,
                    -0.4
                  ],
                  [
                    0.5,
                    0
                  ],
                  [
                    0,
                    0.3
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.5,
                    0.5
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.2
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.3,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 91,
        ty: 3,
        parent: 88,
        ks: {
          p: {
            a: 0,
            k: [
              23.221,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 94,
        ty: 4,
        parent: 93,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    1.6,
                    0
                  ],
                  [
                    0.4,
                    -0.7
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    0.8,
                    4.2
                  ],
                  [
                    0.8,
                    10.9
                  ],
                  [
                    2.6,
                    10.9
                  ],
                  [
                    2.6,
                    7
                  ],
                  [
                    3.9,
                    5.6
                  ],
                  [
                    5.1,
                    7
                  ],
                  [
                    5.1,
                    10.9
                  ],
                  [
                    6.8,
                    10.9
                  ],
                  [
                    6.8,
                    6.7
                  ],
                  [
                    4.4,
                    4
                  ],
                  [
                    2.6,
                    5
                  ],
                  [
                    2.6,
                    5
                  ],
                  [
                    2.6,
                    4.2
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.8
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.5
                  ],
                  [
                    -0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    1.1,
                    3.2
                  ],
                  [
                    6.8,
                    3.2
                  ],
                  [
                    6.8,
                    0.7
                  ],
                  [
                    5.2,
                    0.7
                  ],
                  [
                    5.2,
                    1.9
                  ],
                  [
                    1.1,
                    1.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    5.2,
                    0.7
                  ],
                  [
                    5.2,
                    3.2
                  ],
                  [
                    6.8,
                    3.2
                  ],
                  [
                    6.8,
                    0.7
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 93,
        ty: 3,
        parent: 88,
        ks: {
          p: {
            a: 0,
            k: [
              15.631,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 96,
        ty: 4,
        parent: 95,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.8,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.6,
                    0
                  ],
                  [
                    -0.4,
                    0.6
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ],
                v: [
                  [
                    6.8,
                    10.9
                  ],
                  [
                    6.8,
                    4.2
                  ],
                  [
                    5,
                    4.2
                  ],
                  [
                    5,
                    8
                  ],
                  [
                    3.7,
                    9.4
                  ],
                  [
                    2.5,
                    8
                  ],
                  [
                    2.5,
                    4.2
                  ],
                  [
                    0.7,
                    4.2
                  ],
                  [
                    0.7,
                    8.4
                  ],
                  [
                    3.1,
                    11
                  ],
                  [
                    5,
                    10
                  ],
                  [
                    5,
                    10
                  ],
                  [
                    5,
                    10.9
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0.8
                  ],
                  [
                    -0.8,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    1.5
                  ],
                  [
                    0.7,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 95,
        ty: 3,
        parent: 88,
        ks: {
          p: {
            a: 0,
            k: [
              8.041,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 98,
        ty: 4,
        parent: 97,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "sh",
            ks: {
              a: 0,
              k: {
                c: true,
                i: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0.1,
                    0
                  ],
                  [
                    0.3,
                    -0.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    2.2,
                    0
                  ],
                  [
                    0,
                    -1.9
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -1.1,
                    0
                  ]
                ],
                v: [
                  [
                    4.1,
                    8.6
                  ],
                  [
                    4.5,
                    8.6
                  ],
                  [
                    4.5,
                    7.1
                  ],
                  [
                    4.2,
                    7.1
                  ],
                  [
                    2.5,
                    8.4
                  ],
                  [
                    2.5,
                    7
                  ],
                  [
                    4,
                    5.5
                  ],
                  [
                    5.6,
                    7
                  ],
                  [
                    5.6,
                    10.9
                  ],
                  [
                    7.3,
                    10.9
                  ],
                  [
                    7.3,
                    7
                  ],
                  [
                    4,
                    4
                  ],
                  [
                    0.7,
                    7
                  ],
                  [
                    0.7,
                    10.9
                  ],
                  [
                    2.5,
                    10.9
                  ],
                  [
                    2.5,
                    10.1
                  ],
                  [
                    4.1,
                    8.6
                  ]
                ],
                o: [
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    -0.1,
                    0
                  ],
                  [
                    -0.6,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -0.9
                  ],
                  [
                    0.9,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1.8
                  ],
                  [
                    -2.2,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    0
                  ],
                  [
                    0,
                    -1
                  ],
                  [
                    0,
                    0
                  ]
                ]
              }
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 97,
        ty: 3,
        parent: 88,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 88,
        ty: 3,
        parent: 87,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 87,
        ty: 3,
        parent: 86,
        ks: {
          p: {
            a: 0,
            k: [
              16.807,
              0.9
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 86,
        ty: 3,
        parent: 85,
        ks: {
          p: {
            a: 0,
            k: [
              -9,
              0
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 100,
        ty: 4,
        parent: 99,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                39,
                9.5
              ]
            },
            r: {
              a: 0,
              k: 100
            },
            s: {
              a: 0,
              k: [
                78,
                19
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0.902,
                8e-3,
                0.471
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 99,
        ty: 3,
        parent: 85,
        ks: {
          p: {
            a: 0,
            k: [
              -13,
              -2
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 85,
        ty: 3,
        parent: 7,
        ks: {
          a: {
            a: 0,
            k: [
              26,
              7.5
            ]
          },
          p: {
            a: 0,
            k: [
              123,
              463.5
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 91.2,
                s: [
                  0,
                  0
                ],
                i: {
                  x: [
                    0.725,
                    0.725
                  ],
                  y: [
                    0.45,
                    0.45
                  ]
                },
                o: {
                  x: [
                    0.387,
                    0.387
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 102.636,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    0.614,
                    0.614
                  ],
                  y: [
                    0.999,
                    0.999
                  ]
                },
                o: {
                  x: [
                    0.277,
                    0.277
                  ],
                  y: [
                    0.553,
                    0.553
                  ]
                }
              },
              {
                t: 106.296,
                s: [
                  185.05,
                  185.05
                ],
                i: {
                  x: [
                    0.952,
                    0.952
                  ],
                  y: [
                    0.93,
                    0.93
                  ]
                },
                o: {
                  x: [
                    0.552,
                    0.552
                  ],
                  y: [
                    1e-3,
                    1e-3
                  ]
                }
              },
              {
                t: 109.956,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    0.657,
                    0.657
                  ],
                  y: [
                    0.997,
                    0.997
                  ]
                },
                o: {
                  x: [
                    0.329,
                    0.329
                  ],
                  y: [
                    0.655,
                    0.655
                  ]
                }
              },
              {
                t: 111.126,
                s: [
                  204.02,
                  204.02
                ],
                i: {
                  x: [
                    0.936,
                    0.936
                  ],
                  y: [
                    0.906,
                    0.906
                  ]
                },
                o: {
                  x: [
                    0.549,
                    0.549
                  ],
                  y: [
                    2e-3,
                    2e-3
                  ]
                }
              },
              {
                t: 112.308,
                s: [
                  206.19,
                  206.19
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 112.68,
                s: [
                  205.97,
                  205.97
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 113.064,
                s: [
                  206.2,
                  206.2
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  206.2,
                  206.2
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 164,
        ty: 4,
        td: 1,
        parent: 101,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                338.5,
                316
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                677,
                632
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 163,
        ty: 0,
        tt: 1,
        parent: 101,
        ks: {
          a: {
            a: 0,
            k: [
              398,
              417
            ]
          }
        },
        w: 1460,
        h: 1479,
        ip: 0,
        op: 283,
        st: 0,
        refId: "161"
      },
      {
        ind: 101,
        ty: 3,
        parent: 7,
        ks: {
          p: {
            a: 0,
            k: [
              1,
              25
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 7,
        ty: 3,
        parent: 6,
        ks: {
          a: {
            a: 0,
            k: [
              341,
              341
            ]
          },
          p: {
            a: 0,
            k: [
              128,
              119
            ]
          },
          s: {
            a: 1,
            k: [
              {
                t: 0,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 48,
                s: [
                  37.68,
                  37.68
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 214.2,
                s: [
                  37.68,
                  37.68
                ],
                i: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0.5,
                    0.5
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 262.2,
                s: [
                  100,
                  100
                ],
                i: {
                  x: [
                    1,
                    1
                  ],
                  y: [
                    1,
                    1
                  ]
                },
                o: {
                  x: [
                    0,
                    0
                  ],
                  y: [
                    0,
                    0
                  ]
                }
              },
              {
                t: 282,
                s: [
                  100,
                  100
                ],
                h: 1
              }
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 166,
        ty: 4,
        parent: 165,
        ks: {},
        ip: 0,
        op: 283,
        st: 0,
        shapes: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                1,
                1,
                1
              ]
            },
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ind: 165,
        ty: 3,
        parent: 6,
        ks: {},
        ip: 0,
        op: 283,
        st: 0
      },
      {
        ind: 6,
        ty: 3,
        ks: {
          p: {
            a: 0,
            k: [
              229,
              224
            ]
          }
        },
        ip: 0,
        op: 283,
        st: 0
      }
    ]
  }
];
const fr = 60;
const h = 238;
const ip = 0;
const layers = [
  {
    ind: 4,
    ty: 4,
    td: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 3,
    ty: 4,
    tt: 1,
    parent: 2,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 0
            },
            s: {
              a: 0,
              k: [
                259,
                242
              ]
            }
          },
          {
            ty: "fl",
            c: {
              a: 0,
              k: [
                0,
                0,
                0,
                0
              ]
            },
            o: {
              a: 0,
              k: 0
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      },
      {
        ty: "gr",
        it: [
          {
            ty: "rc",
            p: {
              a: 0,
              k: [
                127.5,
                119
              ]
            },
            r: {
              a: 0,
              k: 16
            },
            s: {
              a: 0,
              k: [
                255,
                238
              ]
            }
          },
          {
            ty: "st",
            c: {
              a: 0,
              k: [
                0.824,
                0.843,
                0.875
              ]
            },
            lc: 2,
            lj: 1,
            ml: 10,
            o: {
              a: 0,
              k: 100
            },
            w: {
              a: 0,
              k: 4
            }
          },
          {
            ty: "tr",
            o: {
              a: 0,
              k: 100
            }
          }
        ]
      }
    ]
  },
  {
    ind: 2,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 170,
    ty: 4,
    td: 1,
    parent: 5,
    ks: {},
    ip: 0,
    op: 283,
    st: 0,
    shapes: [
      {
        ty: "rc",
        p: {
          a: 0,
          k: [
            127.5,
            119
          ]
        },
        r: {
          a: 0,
          k: 16
        },
        s: {
          a: 0,
          k: [
            255,
            238
          ]
        }
      },
      {
        ty: "fl",
        c: {
          a: 0,
          k: [
            0,
            0,
            0
          ]
        },
        o: {
          a: 0,
          k: 100
        }
      }
    ]
  },
  {
    ind: 169,
    ty: 0,
    tt: 1,
    parent: 5,
    ks: {
      a: {
        a: 0,
        k: [
          229,
          224
        ]
      }
    },
    w: 907,
    h: 881,
    ip: 0,
    op: 283,
    st: 0,
    refId: "167"
  },
  {
    ind: 5,
    ty: 3,
    parent: 1,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 1,
    ty: 3,
    parent: 0,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  },
  {
    ind: 0,
    ty: 3,
    ks: {},
    ip: 0,
    op: 283,
    st: 0
  }
];
const meta = {
  g: "https://jitter.video"
};
const op = 282;
const v = "5.7.4";
const w = 255;
const ExclusiveTargetingTh = {
  assets,
  fr,
  h,
  ip,
  layers,
  meta,
  op,
  v,
  w
};
const ExclusiveTargetingAnimation = ({ alt }) => {
  const { language } = useLanguage();
  if (language.startsWith("th")) {
    return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: ExclusiveTargetingTh, altText: alt });
  }
  if (language.startsWith("id")) {
    return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: ExclusiveTargetingId, altText: alt });
  }
  return /* @__PURE__ */ jsx(LottieAnimation$1, { lottieFile: ExclusiveTargetingEn, altText: alt });
};
const emailImage = "data:image/png;base64,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";
const priorityListingANZ = "data:image/png;base64,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";
const priorityListingAsia = "data:image/png;base64,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";
const LottieAnimation = loadable$2(
  () => Promise.resolve().then(() => LottieAnimation$2),
  {
    fallback: /* @__PURE__ */ jsx(AnimationFallback, {}),
    resolveComponent: (components) => components.LottieAnimation
  }
);
LottieAnimation.preload();
const HelpTipImage = ({
  id,
  alt
}) => {
  switch (id) {
    case "email":
      return /* @__PURE__ */ jsx("img", { src: emailImage, alt, width: "80%" });
    case "priority-listing-anz":
      return /* @__PURE__ */ jsx("img", { src: priorityListingANZ, alt, width: "80%" });
    case "priority-listing-asia":
      return /* @__PURE__ */ jsx("img", { src: priorityListingAsia, alt, width: "80%" });
    case "branding-logo-and-cover":
      return /* @__PURE__ */ jsx(BrandingAnimation, { alt });
    case "proactive-targeting":
      return /* @__PURE__ */ jsx(ExclusiveTargetingAnimation, { alt });
    case "candidate-invitations":
      return /* @__PURE__ */ jsx(CandidateInvitationsAnimation, { alt });
    case "competitive-placement":
      return /* @__PURE__ */ jsx(CompetitivePlacementAnimation, { alt });
    case "visibility-candidates":
      return /* @__PURE__ */ jsx(AiTargetingAnimation, { alt });
    default:
      return null;
  }
};
const HelpTipDialog = ({
  name: name2,
  helpTip,
  isOpen,
  closeHelpTip,
  customContent
}) => {
  const { t } = useTranslations(translations$5);
  if (!isOpen) return null;
  const description = helpTip ? /* @__PURE__ */ jsx(Box, { paddingTop: "xsmall", children: /* @__PURE__ */ jsx(Stack, { space: "large", children: helpTip.content.map(({ value: value2 }) => /* @__PURE__ */ jsx(Text, { children: value2 }, value2.substring(0, 20))) }) }) : void 0;
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      id: name2,
      title: name2,
      description,
      open: isOpen,
      onClose: closeHelpTip,
      closeLabel: t("Close"),
      illustration: (helpTip == null ? void 0 : helpTip.image) && /* @__PURE__ */ jsx(HelpTipImage, { id: helpTip.image.id, alt: helpTip.image.description }),
      children: customContent ? customContent : null
    }
  );
};
const HelpTipIcon = ({
  name: name2,
  openHelpTip,
  tone
}) => /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
  TextLinkButton,
  {
    "aria-label": `Tooltip for: ${name2}`,
    weight: "weak",
    hitArea: "standard",
    onClick: openHelpTip,
    children: /* @__PURE__ */ jsx(IconInfo, { tone })
  }
) });
const HelpTip = ({
  name: name2,
  tone,
  helpTip,
  tracking,
  customContent
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { trackEvent } = useContext(TrackingContext);
  if (!helpTip && !customContent) return null;
  const trackingData = {
    type: "tooltip",
    name: name2,
    content: (tracking == null ? void 0 : tracking.id) ?? (helpTip == null ? void 0 : helpTip.title) ?? (helpTip == null ? void 0 : helpTip.content.map(({ value: value2 }) => value2).join(" "))
  };
  const openHelpTip = () => {
    setIsOpen(true);
    trackEvent({
      ...trackingData,
      action: "opened"
    });
  };
  const closeHelpTip = () => {
    setIsOpen(false);
    trackEvent({
      ...trackingData,
      action: "closed"
    });
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(HelpTipIcon, { name: name2, openHelpTip, tone }),
    /* @__PURE__ */ jsx(
      HelpTipDialog,
      {
        name: (helpTip == null ? void 0 : helpTip.title) ?? name2,
        helpTip,
        isOpen,
        closeHelpTip,
        customContent
      }
    )
  ] });
};
const ExpectationTooltipItem = ({
  icon,
  title,
  content: content2
}) => /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
  /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { children: icon }) }),
  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
    /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(Strong, { children: title }) }),
    /* @__PURE__ */ jsx(Text, { children: content2 })
  ] }) })
] });
const ExpectationsTooltip = () => {
  const { t } = useTranslations(translations$6);
  return /* @__PURE__ */ jsx(
    HelpTip,
    {
      name: t("Estimated performance"),
      tone: "secondary",
      tracking: { id: "expectations" },
      customContent: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        /* @__PURE__ */ jsx(
          ExpectationTooltipItem,
          {
            icon: /* @__PURE__ */ jsx(IconResume, { tone: "brandAccent" }),
            title: t("Total applications:"),
            content: t("Number of applications you are estimated to receive.")
          }
        ),
        /* @__PURE__ */ jsx(
          ExpectationTooltipItem,
          {
            icon: /* @__PURE__ */ jsx(IconRecommended, { tone: "brandAccent" }),
            title: t("High-fit applications:"),
            content: t(
              "Number of applications determined to be high-fit based on SEEK data and how likely hirers are to shortlist similar candidates."
            )
          }
        ),
        /* @__PURE__ */ jsx(
          ExpectationTooltipItem,
          {
            icon: /* @__PURE__ */ jsx(IconVisibility, { tone: "brandAccent" }),
            title: t("Views:"),
            content: t(
              "How many times your job ad is estimated to be viewed compared to a Basic Ad."
            )
          }
        )
      ] })
    }
  );
};
const ExpectationItem = ({
  expectation: { key: key2, unit, value: value2, enabled }
}) => {
  const { t } = useTranslations(translations$6);
  if (enabled === false) return null;
  if (key2 === "applications" && unit === "applications") {
    return /* @__PURE__ */ jsx(Text, { icon: /* @__PURE__ */ jsx(IconResume, { tone: "brandAccent" }), children: t("<count> total applications", {
      Strong: (children) => /* @__PURE__ */ jsx(Strong, { children }),
      count: value2
    }) });
  }
  if (key2 === "high-fit-applications" && unit === "applications") {
    return /* @__PURE__ */ jsx(Text, { icon: /* @__PURE__ */ jsx(IconRecommended, { tone: "brandAccent" }), children: t("<count> high-fit applications", {
      Strong: (children) => /* @__PURE__ */ jsx(Strong, { children }),
      count: value2
    }) });
  }
  if (key2 === "views" && unit === "ratio") {
    return /* @__PURE__ */ jsx(Text, { icon: /* @__PURE__ */ jsx(IconVisibility, { tone: "brandAccent" }), children: t("{count} views", {
      Strong: (children) => /* @__PURE__ */ jsx(Strong, { children }),
      count: value2
    }) });
  }
  return null;
};
const ExpectationsListed = ({
  expectations
}) => {
  var _a;
  const { t } = useTranslations(translations$6);
  if (!shouldShowExpectations(expectations)) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsxs(Box, { display: "flex", alignItems: "flexStart", justifyContent: "spaceBetween", children: [
      /* @__PURE__ */ jsx(Text, { size: "small", weight: "strong", children: t("Estimated performance:") }),
      /* @__PURE__ */ jsx(ExpectationsTooltip, {})
    ] }),
    (_a = expectations.content) == null ? void 0 : _a.map((expectation) => /* @__PURE__ */ jsx(ExpectationItem, { expectation }, expectation.key))
  ] });
};
const VARIANT = {
  featured: "promoteLight",
  deprioritised: "neutralSoft",
  secondary: "neutralLight",
  listed: void 0,
  "secondary-emphasised": "neutralLight"
};
const shouldShowExpectations = (expectations) => Boolean(
  (expectations == null ? void 0 : expectations.content) && expectations.content.some((expectation) => expectation.enabled)
);
const Expectations = ({
  expectations
}) => {
  var _a;
  const { t } = useTranslations(translations$6);
  if (!expectations) return null;
  if (expectations.variant === "listed") {
    return /* @__PURE__ */ jsx(ExpectationsListed, { expectations });
  }
  const applicationsCompleted = (_a = expectations.content) == null ? void 0 : _a.find(
    (expectation) => expectation.key === "applications"
  );
  if (!applicationsCompleted || (applicationsCompleted == null ? void 0 : applicationsCompleted.enabled) === false)
    return null;
  const { value: count } = applicationsCompleted;
  if (expectations.variant === "secondary") {
    return /* @__PURE__ */ jsx(
      Box,
      {
        background: VARIANT[expectations.variant],
        borderRadius: "standard",
        paddingX: "xsmall",
        paddingY: "small",
        children: /* @__PURE__ */ jsx(Text, { align: "center", children: t("<count> estimated applications", { count }) })
      }
    );
  }
  if (expectations.variant === "secondary-emphasised") {
    return /* @__PURE__ */ jsx(
      Box,
      {
        background: VARIANT[expectations.variant],
        borderRadius: "large",
        paddingX: "xsmall",
        paddingY: "small",
        children: /* @__PURE__ */ jsxs(Text, { align: "center", children: [
          /* @__PURE__ */ jsx(Strong, { children: count }),
          " applications estimated"
        ] })
      }
    );
  }
  return /* @__PURE__ */ jsx(
    Box,
    {
      background: VARIANT[expectations.variant],
      borderRadius: "standard",
      paddingX: "xsmall",
      paddingY: "medium",
      children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", align: "center", children: [
        /* @__PURE__ */ jsx(Text, { size: "small", children: t("Estimated results") }),
        /* @__PURE__ */ jsx(Text, { size: "large", weight: "strong", children: t("<count> applications", { count }) })
      ] })
    }
  );
};
const StyledContentItem = ({
  styledContent
}) => {
  if (styledContent.variant === "bold") {
    return /* @__PURE__ */ jsx(Strong, { children: styledContent.content });
  }
  return /* @__PURE__ */ jsx(Fragment, { children: styledContent.content });
};
const IconBullet = () => /* @__PURE__ */ jsx(IconRenderer, { children: ({ className }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    className,
    "aria-hidden": true,
    children: /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "3" })
  }
) });
const InclusionIcon = ({
  icon,
  type
}) => {
  const { tone } = icon ?? {};
  switch (icon == null ? void 0 : icon.id) {
    case "bullet":
      return /* @__PURE__ */ jsx(IconBullet, {});
    case "none":
      return null;
  }
  switch (type) {
    case "included":
      return /* @__PURE__ */ jsx(IconTick, { tone: tone ?? "info" });
    case "excluded":
      return /* @__PURE__ */ jsx(IconMinus, { tone });
  }
  return null;
};
const Inclusion = ({
  helpTip,
  name: name2,
  icon,
  type,
  inclusions,
  styledContents
}) => {
  const isIncluded = type === "included" || typeof type === "undefined";
  const tone = isIncluded ? void 0 : "secondary";
  const excludedStyle = !isIncluded ? { opacity: 0.5 } : {};
  return /* @__PURE__ */ jsx(Box, { style: excludedStyle, children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: "top", children: [
      /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
        Text,
        {
          weight: "regular",
          tone,
          icon: /* @__PURE__ */ jsx(InclusionIcon, { icon, type }),
          component: isIncluded ? "span" : "s",
          children: styledContents && styledContents.length ? styledContents.map((item) => /* @__PURE__ */ jsx(
            StyledContentItem,
            {
              styledContent: item
            },
            item.content
          )) : name2
        }
      ) }),
      /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(HelpTip, { name: name2, helpTip, tone }) })
    ] }),
    /* @__PURE__ */ jsx(NestedInclusions, { inclusions })
  ] }) });
};
const NestedInclusions = ({
  inclusions
}) => {
  if (!(inclusions == null ? void 0 : inclusions.length)) return null;
  return /* @__PURE__ */ jsx(Box, { marginLeft: "xsmall", children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: inclusions.map((nestedInclusion) => /* @__PURE__ */ createElement(
    Inclusion,
    {
      ...nestedInclusion,
      icon: nestedInclusion.icon ?? { id: "bullet" },
      key: `nested-${nestedInclusion.name}`
    }
  )) }) });
};
const Inclusions = ({
  inclusions
}) => {
  const { t } = useTranslations(translations$7);
  if (!(inclusions == null ? void 0 : inclusions.content) || inclusions.content.length === 0) return null;
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Text, { weight: "strong", children: inclusions.title ? inclusions.title.content : t("Included:") }),
    inclusions.content.map((inclusion) => /* @__PURE__ */ createElement(Inclusion, { ...inclusion, key: `inclusion-${inclusion.name}` }))
  ] });
};
const getTaxText = (tax) => {
  if (tax && tax.displayRecommendation) {
    return {
      text: tax.displayRecommendation === "inclusive" ? "<taxPrefix> <taxCode>" : "<taxPrefix><taxCode>",
      taxPrefix: tax.displayRecommendation === "inclusive" ? "incl." : "+",
      taxCode: tax.code
    };
  }
  return void 0;
};
const TaxText = ({ tax }) => {
  const { t } = useTranslations(translations$8);
  const taxText = getTaxText(tax);
  if (!taxText) {
    return null;
  }
  return /* @__PURE__ */ jsx(Fragment, { children: t(taxText.text, {
    taxPrefix: t(taxText.taxPrefix),
    taxCode: taxText.taxCode
  }) });
};
const shouldShowCappedPrice = (price) => isPositiveCappedUnitPrice(price) && !isBillableUnitsHighlight(price.variant);
const CappedPrice = ({
  price
}) => {
  const { t } = useTranslations(translations$9);
  const { language } = useLanguage();
  if (!shouldShowCappedPrice(price)) {
    return null;
  }
  const { displayNetPrice } = getDisplayPrices(price.cappedPrice);
  return /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children: t("Capped at <price> <TaxSuffix>", {
    TaxSuffix: () => /* @__PURE__ */ jsx(TaxText, { tax: displayNetPrice.tax }),
    price: formatExactPrice(
      displayNetPrice.price.value,
      displayNetPrice.price.currency,
      language
    )
  }) });
};
const PriceTooltipRenderer = ({
  tooltip
}) => {
  const size = "small";
  const tone = "secondary";
  const { t } = useTranslations(translations$a);
  return /* @__PURE__ */ jsx(Text, { size, tone, children: /* @__PURE__ */ jsx(
    TooltipRenderer,
    {
      id: "rounded-price-tooltip",
      tooltip: (
        // Tooltip contents
        /* @__PURE__ */ jsx(Text, { align: "center", children: t(tooltip.tooltipText, {
          price: tooltip.exactPrice
        }) })
      ),
      children: ({ triggerProps }) => (
        // Tooltip trigger icon
        /* @__PURE__ */ jsx(Box, { ...triggerProps, cursor: "pointer", children: /* @__PURE__ */ jsx(IconInfo, { tone }) })
      )
    }
  ) });
};
const TooltipWrapper = ({
  children,
  tooltip
}) => {
  const size = "small";
  const tone = "secondary";
  return /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", children: [
    /* @__PURE__ */ jsx(Text, { size, tone, children }),
    tooltip && /* @__PURE__ */ jsx(PriceTooltipRenderer, { tooltip })
  ] });
};
const getTooltipText = (displayNetPrice, language) => {
  if (isPriceRounded(displayNetPrice.price.value)) {
    const exactPrice = formatExactPrice(
      displayNetPrice.price.value,
      displayNetPrice.price.currency,
      language
    );
    return {
      exactPrice,
      tooltipText: "Rounded price. Exact price <price>"
    };
  }
  return void 0;
};
const getDetailsText = (language, price) => {
  if (price.type === "HiddenPrice") return void 0;
  if (price.type === "CappedUnitPrice" && price.unitType === "application") {
    if (isPriceFree(price.unitPrice)) return void 0;
    if (isBillableUnitsHighlight(price.variant)) {
      const { displayNetPrice: displayNetPrice2 } = getDisplayPrices(price.cappedPrice);
      return {
        type: "billableUnitsHighlight",
        tax: displayNetPrice2.tax,
        tooltip: getTooltipText(displayNetPrice2, language)
      };
    }
    const { displayNetPrice } = getDisplayPrices(price.unitPrice);
    const tax = price.unitPrice.tax;
    return {
      type: "payPerApplication",
      tax,
      text: "per applicant",
      tooltip: getTooltipText(displayNetPrice, language)
    };
  }
  if (price.type === "FixedPrice") {
    if (isPriceFree(price)) return void 0;
    const tax = price.tax;
    const isDiscounted = isPriceDiscounted(price);
    const { displayNetPrice } = getDisplayPrices(price);
    const tooltip = getTooltipText(displayNetPrice, language);
    if (isDiscounted) {
      return {
        type: "discountedPrice",
        tax,
        text: "discount applied",
        tooltip
      };
    }
    return {
      type: "taxOnly",
      tax,
      tooltip
    };
  }
  return void 0;
};
const DetailsText = ({
  price
}) => {
  const { t } = useTranslations(translations$a);
  const { language } = useLanguage();
  const detailsText = getDetailsText(language, price);
  if (!detailsText) {
    return null;
  }
  if (detailsText.type === "taxOnly" || detailsText.type === "billableUnitsHighlight") {
    return /* @__PURE__ */ jsx(TooltipWrapper, { tooltip: detailsText.tooltip, children: /* @__PURE__ */ jsx(TaxText, { tax: detailsText.tax }) });
  }
  if (detailsText.type === "discountedPrice" || detailsText.type === "payPerApplication") {
    if (!detailsText.tax) {
      return /* @__PURE__ */ jsx(TooltipWrapper, { tooltip: detailsText.tooltip, children: t(detailsText.text) });
    }
    return /* @__PURE__ */ jsx(TooltipWrapper, { tooltip: detailsText.tooltip, children: t("<taxSuffix>, <unitType>", {
      TaxSuffix: () => /* @__PURE__ */ jsx(TaxText, { tax: detailsText.tax }),
      unitType: t(detailsText.text)
    }) });
  }
  return null;
};
const DiscountApplied = ({ price }) => {
  const { t } = useTranslations(translations$a);
  if (!isPriceDiscounted(price)) {
    return null;
  }
  return /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", children: t("discount applied") });
};
const PriceTooltip = ({ price }) => {
  const { language } = useLanguage();
  const detailsText = getDetailsText(language, price);
  const tooltip = detailsText == null ? void 0 : detailsText.tooltip;
  if (!tooltip) {
    return null;
  }
  return /* @__PURE__ */ jsx(PriceTooltipRenderer, { tooltip });
};
const Discount = ({
  price
}) => {
  const { language } = useLanguage();
  if (!isPriceDiscounted(price)) {
    return null;
  }
  const { displayRetailPrice } = getDisplayPrices(price);
  return /* @__PURE__ */ jsx(
    Text,
    {
      size: "small",
      data: { testid: "discount" },
      component: "s",
      tone: "secondary",
      children: formatExactPrice(
        displayRetailPrice.price.value,
        displayRetailPrice.price.currency,
        language
      )
    }
  );
};
const getMainTextPrice = (price) => {
  if (price.type === "CappedUnitPrice") {
    if (isBillableUnitsHighlight(price.variant)) {
      return price.cappedPrice;
    }
    return price.unitPrice;
  }
  return price;
};
const getMainText = (language, price) => {
  const mainTextPrice = getMainTextPrice(price);
  if (isPriceHidden(mainTextPrice)) {
    return void 0;
  }
  if (isPriceFree(mainTextPrice)) {
    return {
      type: "free",
      text: "Free"
    };
  }
  const prefix = mainTextPrice.isAdditionalPrice ? "+" : "";
  const { displayNetPrice } = getDisplayPrices(mainTextPrice);
  if (isPriceRounded(displayNetPrice.price.value)) {
    const roundedPrice = formatRoundedPrice(
      displayNetPrice.price.value,
      displayNetPrice.price.currency,
      language
    );
    return {
      type: "roundedPrice",
      price: prefix + roundedPrice
    };
  }
  const exactPrice = formatExactPrice(
    displayNetPrice.price.value,
    displayNetPrice.price.currency,
    language
  );
  return {
    type: "exactPrice",
    price: prefix + exactPrice
  };
};
const SIZE = {
  small: "4",
  medium: "3",
  large: "2",
  xlarge: "1"
};
const TextWrapper = ({
  children,
  size
}) => {
  if (size === "small") {
    return /* @__PURE__ */ jsx(Text, { size: "large", weight: "strong", children });
  }
  return /* @__PURE__ */ jsx(Heading, { level: SIZE[size], component: "p", weight: "regular", children });
};
const MainText = ({
  price,
  size
}) => {
  const { language } = useLanguage();
  const { t } = useTranslations(translations$b);
  const mainText = getMainText(language, price);
  if (!mainText) {
    return null;
  }
  if (mainText.type === "free") {
    return /* @__PURE__ */ jsx(TextWrapper, { size, children: t(mainText.text) });
  }
  if (mainText.type === "roundedPrice" || mainText.type === "exactPrice") {
    return /* @__PURE__ */ jsx(TextWrapper, { size, children: mainText.price });
  }
  return null;
};
const Tax = ({ price }) => {
  const { language } = useLanguage();
  const detailsText = getDetailsText(language, price);
  const tax = detailsText == null ? void 0 : detailsText.tax;
  if (!tax) {
    return null;
  }
  return /* @__PURE__ */ jsx(Text, { tone: "secondary", size: "small", children: /* @__PURE__ */ jsx(TaxText, { tax }) });
};
const Price = ({
  price,
  size = "xlarge"
}) => {
  if (isSlimVariant(price)) {
    if (isPriceDiscounted(price)) {
      return /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
        /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", alignY: "center", children: [
          /* @__PURE__ */ jsx(MainText, { price, size: "small" }),
          /* @__PURE__ */ jsx(Tax, { price })
        ] }),
        /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", alignY: "center", children: [
          /* @__PURE__ */ jsx(DiscountApplied, { price }),
          /* @__PURE__ */ jsx(Discount, { price }),
          /* @__PURE__ */ jsx(PriceTooltip, { price })
        ] })
      ] });
    }
    return /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", alignY: "center", children: [
      /* @__PURE__ */ jsx(MainText, { price, size: "small" }),
      /* @__PURE__ */ jsx(Tax, { price }),
      /* @__PURE__ */ jsx(PriceTooltip, { price })
    ] });
  }
  return /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    priceHasTax(price) || isPriceDiscounted(price) || shouldShowCappedPrice(price) ? /* @__PURE__ */ jsx(StackedPriceText, { price, size }) : /* @__PURE__ */ jsx(InlinePriceText, { price, size }),
    shouldShowCappedPrice(price) && /* @__PURE__ */ jsx(CappedPrice, { price }),
    isPriceDiscounted(price) && /* @__PURE__ */ jsx(Discount, { price })
  ] });
};
const InlinePriceText = ({
  price,
  size
}) => /* @__PURE__ */ jsxs(Inline, { space: "none", alignY: "top", children: [
  /* @__PURE__ */ jsx(MainText, { price, size }),
  /* @__PURE__ */ jsx(DetailsText, { price })
] });
const StackedPriceText = ({
  price,
  size
}) => /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
  /* @__PURE__ */ jsx(MainText, { price, size }),
  /* @__PURE__ */ jsx(DetailsText, { price })
] });
const Rating = ({
  value: value2,
  variant = "circles",
  size = "medium",
  align = "left"
}) => {
  switch (variant) {
    case "circles": {
      return /* @__PURE__ */ jsx(Circles, { value: value2, size, align });
    }
    case "bar-chart": {
      return /* @__PURE__ */ jsx(BarChart, { value: value2, size, align });
    }
    case "pie-chart": {
      return /* @__PURE__ */ jsx(PieChart, { value: value2, size });
    }
    default:
      return null;
  }
};
const Tagline = ({
  content: content2,
  variant
}) => {
  switch (variant) {
    case "bolded":
      return (
        // workaround for cut text when tagline section has background
        /* @__PURE__ */ jsx(Box, { zIndex: 1, children: /* @__PURE__ */ jsx(Heading, { level: "4", children: content2 }) })
      );
    case "featured":
      return /* @__PURE__ */ jsx(Text, { size: "large", weight: "medium", align: "center", children: content2 });
  }
  return /* @__PURE__ */ jsx(Text, { tone: "secondary", children: content2 });
};
const ICON = {
  recommended: /* @__PURE__ */ jsx(IconRecommended, { data: { testid: "recommended-icon" } })
};
const Title = ({
  content: content2,
  icon,
  variant
}) => {
  const iconComponent = icon ? ICON[icon] : void 0;
  switch (variant) {
    case "small":
      return /* @__PURE__ */ jsx(Text, { size: "small", tone: "secondary", component: "h3", icon: iconComponent, children: content2 });
    default:
      return /* @__PURE__ */ jsx(Heading, { level: "3", weight: "regular", icon: iconComponent, children: content2 });
  }
};
const Wrapper$1 = ({
  children,
  product
}) => {
  const { trackEvent } = useContext(TrackingContext);
  return /* @__PURE__ */ jsx(
    TrackingContext.Provider,
    {
      value: {
        trackEvent: (data2, inputMeta) => trackEvent(data2, { ...inputMeta, product })
      },
      children
    }
  );
};
const getSectionBackground = (variant) => variant === "highlight-background" ? "brand" : void 0;
const ColumnPanel = ({
  product,
  isSelected,
  isLoading,
  onSelect,
  align = "left",
  isSticky
}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  return /* @__PURE__ */ jsxs(Wrapper$1, { product, children: [
    product.banner ? /* @__PURE__ */ jsx(
      Section,
      {
        gridArea: "banner",
        background: getSectionBackground((_a = product == null ? void 0 : product.panel) == null ? void 0 : _a.variant),
        isSticky,
        children: /* @__PURE__ */ jsx(Banner, { type: product.banner.type, variant: product.banner.variant })
      }
    ) : null,
    product.title || product.tagline ? /* @__PURE__ */ jsx(
      Section,
      {
        gridArea: "title",
        background: getSectionBackground((_b = product == null ? void 0 : product.panel) == null ? void 0 : _b.variant),
        isSticky,
        children: /* @__PURE__ */ jsxs(Stack, { space: "small", align, children: [
          product.title ? /* @__PURE__ */ jsx(Title, { ...product.title }) : null,
          product.tagline ? /* @__PURE__ */ jsx(
            Tagline,
            {
              content: product.tagline.content,
              variant: product.tagline.variant
            }
          ) : null
        ] })
      }
    ) : null,
    product.rating ? /* @__PURE__ */ jsx(
      Section,
      {
        gridArea: "rating",
        background: getSectionBackground((_c = product == null ? void 0 : product.panel) == null ? void 0 : _c.variant),
        isSticky,
        children: /* @__PURE__ */ jsxs(Stack, { space: "none", align, children: [
          /* @__PURE__ */ jsx(
            Rating,
            {
              value: product.rating.value,
              variant: product.rating.variant,
              size: product.rating.size,
              align
            }
          ),
          /* @__PURE__ */ jsx(Box, { paddingTop: isSticky ? "small" : "medium", children: ((_d = product.panel) == null ? void 0 : _d.variant) !== "highlight-background" && /* @__PURE__ */ jsx(Divider, {}) })
        ] })
      }
    ) : null,
    product.price.type !== "HiddenPrice" ? /* @__PURE__ */ jsx(Section, { gridArea: "price", isSticky, children: /* @__PURE__ */ jsxs(Stack, { space: "medium", align, children: [
      align === "center" ? /* @__PURE__ */ jsx(Divider, {}) : null,
      /* @__PURE__ */ jsx(Price, { price: product.price, size: "xlarge" })
    ] }) }) : null,
    shouldShowExpectations(product.expectations) ? /* @__PURE__ */ jsx(Section, { gridArea: "expectations", isSticky, children: /* @__PURE__ */ jsx(Stack, { space: "xxsmall", children: /* @__PURE__ */ jsx(Expectations, { expectations: product.expectations }) }) }) : null,
    (product == null ? void 0 : product.cta) ? /* @__PURE__ */ jsx(Section, { gridArea: "cta", isSticky, children: /* @__PURE__ */ jsx(
      CallToAction,
      {
        id: product.id,
        isSelected,
        isLoading,
        onClick: (intent) => onSelect(product.id, product.type, intent),
        url: (_e = product == null ? void 0 : product.cta) == null ? void 0 : _e.url,
        variant: (_f = product.cta) == null ? void 0 : _f.variant
      }
    ) }) : null,
    product.inclusions ? /* @__PURE__ */ jsx(Section, { gridArea: "inclusions", isSticky, children: ((_h = (_g = product.inclusions) == null ? void 0 : _g.content) == null ? void 0 : _h.length) ? /* @__PURE__ */ jsx(Inclusions, { inclusions: product.inclusions }) : null }) : null,
    product.custom ? /* @__PURE__ */ jsx(Section, { gridArea: "custom", isSticky, children: (_i = product.custom) == null ? void 0 : _i.map((id) => /* @__PURE__ */ jsx(Stack, { space: "medium", children: /* @__PURE__ */ jsx(Custom, { id, price: product.price }) }, id)) }) : null,
    product.image ? /* @__PURE__ */ jsx(Section, { gridArea: "image", isSticky, children: /* @__PURE__ */ jsx(Stack, { space: "none", align, children: /* @__PURE__ */ jsx(Image$1, { image: product.image }) }) }) : null
  ] });
};
const Disclaimer = ({
  products,
  variant
}) => {
  const { t } = useTranslations(translations$c);
  const hasExpectations = products.some(
    (product) => shouldShowExpectations(product.expectations)
  );
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Text, { size: "xsmall", tone: "secondary", children: variant === "matrix" ? t("matrix-pricing-disclaimer") : t("Ad prices vary based on a number of factors...") }),
    hasExpectations && /* @__PURE__ */ jsx(Text, { size: "xsmall", tone: "secondary", children: variant === "matrix" ? t("matrix-estimates-disclaimer") : t("Applications for similar roles estimates are based on...") })
  ] });
};
const getProductIsSelectedLoading = ({
  selectedProductIds,
  loadingProductIds,
  product,
  forceSelected = false
}) => {
  if (forceSelected) return { isSelected: true, isLoading: false };
  const isSelected = Boolean(selectedProductIds == null ? void 0 : selectedProductIds.includes(product.id));
  const isLoading = Boolean(loadingProductIds == null ? void 0 : loadingProductIds.includes(product.id));
  return { isSelected, isLoading };
};
const Wrapper = ({
  children,
  products
}) => {
  const { trackEvent } = useContext(TrackingContext);
  useDeepCompareEffect(() => {
    trackEvent({ type: "products", action: "displayed" }, { products });
  }, [products]);
  return /* @__PURE__ */ jsx(
    TrackingContext.Provider,
    {
      value: {
        trackEvent: (data2, inputMeta) => trackEvent(data2, { ...inputMeta, products })
      },
      children
    }
  );
};
const getAddOnsList = ({
  currentParentProduct,
  selectedParentProduct
}) => {
  if (selectedParentProduct) {
    const purchaseableAddOns = selectedParentProduct.addOns.filter((addOn) => addOn.purchaseState === "PURCHASABLE").map((addOn) => ({
      addOn,
      parentProductPurchaseState: selectedParentProduct.purchaseState
    }));
    const purchasedAddOns = (currentParentProduct == null ? void 0 : currentParentProduct.addOns.filter((addOn) => addOn.purchaseState === "PURCHASED").map((addOn) => ({
      addOn,
      parentProductPurchaseState: "PURCHASED"
    }))) ?? [];
    return purchaseableAddOns.concat(purchasedAddOns);
  }
  if (currentParentProduct) {
    return currentParentProduct.addOns.filter((addOn) => addOn.purchaseState === "PURCHASABLE").map((addOn) => ({
      addOn,
      parentProductPurchaseState: currentParentProduct.purchaseState
    }));
  }
  return [];
};
const getCurrentParentProduct = ({
  products
}) => {
  const currentProduct = products.find(
    (product) => product.purchaseState === "PURCHASED"
  );
  return currentProduct;
};
const getSelectedParentProduct = ({
  selectedProductId,
  products
}) => {
  if (!selectedProductId) {
    return void 0;
  }
  return products.find((product) => {
    const targetIds = product.upgradePaths.map(
      (targetProduct) => targetProduct.id
    );
    return product.id === selectedProductId || targetIds.includes(selectedProductId);
  });
};
const getParentProductsAndAddons = ({
  products,
  selectedProductIds
}) => {
  const currentParentProduct = getCurrentParentProduct({ products });
  const selectedParentProduct = selectedProductIds == null ? void 0 : selectedProductIds.map(
    (selectedProductId) => getSelectedParentProduct({
      selectedProductId,
      products
    })
  ).find((productId) => typeof productId !== "undefined");
  const singleParentProduct = selectedParentProduct ?? currentParentProduct;
  const addOnsList = getAddOnsList({
    currentParentProduct,
    selectedParentProduct
  });
  return {
    currentParentProduct,
    selectedParentProduct,
    singleParentProduct,
    addOnsList
  };
};
const getGridColumn = (index2, numOfProducts) => {
  if (numOfProducts <= 1 || isNaN(numOfProducts)) {
    return "1 / -1";
  }
  const span = Math.floor(GRID_COLUMNS / numOfProducts);
  const start = index2 * span + 1;
  return `${start} / span ${span}`;
};
const LayoutSections = [
  "banner",
  "title",
  "rating",
  "price",
  "expectations",
  "cta",
  "inclusions",
  "custom",
  "image"
];
const fillArray = (existingArray = [], allItemsArray) => {
  const missingItems = allItemsArray.filter(
    (item) => !existingArray.includes(item)
  );
  return [...existingArray, ...missingItems];
};
const getGridTemplateAreas = (sections) => fillArray(sections, [...LayoutSections]).map((section) => `"${section}"`).join(" ");
const CustomContentBlock = ({
  children,
  width: width2
}) => {
  if (width2 !== "xlarge") {
    return /* @__PURE__ */ jsx(ContentBlock, { width: width2, children });
  }
  return /* @__PURE__ */ jsx(Box, { style: { maxWidth: "1600px" }, className: marginAuto, children });
};
const getWidth = ({
  showMatrix,
  numOfColumns
}) => {
  if (showMatrix) {
    return "xlarge";
  }
  return numOfColumns <= 3 ? "medium" : "large";
};
const GridLayout = ({
  layoutDisplayRules = {},
  products,
  selectedProductIds,
  loadingProductIds,
  onProductSelect,
  prefixContent,
  hideDisclaimer
}) => {
  var _a;
  const [isSticky, setIsSticky] = useState(false);
  const responsiveValue = useResponsiveValue();
  const isDesktopOrAbove = responsiveValue({
    mobile: false,
    desktop: true
  });
  const isWide = responsiveValue({
    mobile: false,
    tablet: false,
    desktop: false,
    wide: true
  });
  const isGridWithMatrix = (_a = layoutDisplayRules.format) == null ? void 0 : _a.startsWith("grid-with-matrix");
  const isGridMatrixCompact = layoutDisplayRules.format === "grid-with-matrix-compact";
  const showMatrix = Boolean(isGridWithMatrix && isDesktopOrAbove);
  const { selectedParentProduct, singleParentProduct, addOnsList } = getParentProductsAndAddons({ products, selectedProductIds });
  const numOfColumns = products.length + (showMatrix ? 1 : 0);
  const gridTemplateAreas = getGridTemplateAreas(
    layoutDisplayRules.sectionOrder
  );
  const { adProducts, addOnProductsList, upsellProducts, allProducts } = getProductsForDisplay({
    parentProducts: products,
    addOns: addOnsList,
    singleParentProduct
  });
  return /* @__PURE__ */ jsx(Wrapper, { products, children: /* @__PURE__ */ jsx(CustomContentBlock, { width: getWidth({ showMatrix, numOfColumns }), children: /* @__PURE__ */ jsxs(Stack, { space: "medium", align: "center", children: [
    adProducts.length > 0 && (prefixContent == null ? void 0 : prefixContent.products) && /* @__PURE__ */ jsx(GridRow, { className: gridRowPanelConstrainedWidth, children: prefixContent.products }),
    adProducts.length > 0 && /* @__PURE__ */ jsx(
      Grid,
      {
        className: [
          gridContainer,
          showMatrix ? gridContainerSticky : "",
          showMatrix && isSticky ? gridContainerStickyActive : ""
        ],
        onIsSticky: setIsSticky,
        children: adProducts.map((product, index2) => {
          const isCurrentProduct = product.purchaseState === "PURCHASED";
          const { isSelected, isLoading } = getProductIsSelectedLoading({
            selectedProductIds,
            loadingProductIds,
            product,
            forceSelected: isCurrentProduct && !selectedParentProduct || product.id === (selectedParentProduct == null ? void 0 : selectedParentProduct.id)
          });
          const adProductColumnConfigFullWidth = getGridColumn(
            index2 + (showMatrix && isSticky ? 1 : 0),
            numOfColumns - (showMatrix && !isSticky ? 1 : 0)
          );
          const adProductColumnConfigNarrow = getGridColumn(
            index2 + (showMatrix ? 1 : 0),
            numOfColumns
          );
          return /* @__PURE__ */ jsx(
            GridColumn,
            {
              className: [
                panelShared,
                getPanelStyle({
                  panel: product.panel,
                  isSelected,
                  isCurrentProduct,
                  productType: product.type
                })
              ],
              gridColumns: adProducts.length >= 4 && !isWide ? adProductColumnConfigFullWidth : adProductColumnConfigNarrow,
              gridTemplateAreas,
              gridRowGap: "0",
              children: /* @__PURE__ */ jsx(
                ColumnPanel,
                {
                  product: {
                    ...product,
                    // When the matrix is visible we want to hide inclusions and expectations as they are already in
                    // the matrix content. When the panel is "sticky" then we want to hide certain
                    // parts of the panel to slim it down as you scroll
                    ...showMatrix ? {
                      inclusions: void 0,
                      ...isSticky ? {
                        banner: void 0,
                        tagline: void 0,
                        custom: void 0,
                        expectations: void 0
                      } : {}
                    } : {}
                  },
                  isSelected,
                  isLoading,
                  onSelect: onProductSelect,
                  isSticky
                }
              )
            },
            product.id
          );
        })
      }
    ),
    adProducts.length > 0 && showMatrix && layoutDisplayRules.comparativeSections && /* @__PURE__ */ jsx(
      ComparativesMatrix,
      {
        numOfColumns,
        products,
        selectedProductIds,
        selectedParentProduct,
        variant: isGridMatrixCompact ? "compact" : void 0,
        sections: layoutDisplayRules.comparativeSections
      }
    ),
    (addOnProductsList.length > 0 || upsellProducts.length > 0) && /* @__PURE__ */ jsx(
      Upsells,
      {
        singleParentProduct,
        addOnProductsList,
        upsellProducts,
        onProductSelect,
        prefixContent,
        selectedProductIds,
        loadingProductIds,
        gridRowClassName: gridRowPanelConstrainedWidth
      }
    ),
    allProducts.length > 0 && !hideDisclaimer && /* @__PURE__ */ jsx(GridRow, { className: [gridRowPanelConstrainedWidth], children: /* @__PURE__ */ jsx(
      Disclaimer,
      {
        products,
        variant: isGridWithMatrix ? "matrix" : "default"
      }
    ) })
  ] }) }) });
};
export {
  Banner,
  CallToAction,
  Disclaimer,
  GridLayout,
  HelpTip,
  Inclusions,
  LayoutSections,
  Price,
  ProductSelectionContext,
  Tagline,
  Title,
  TrackingContext,
  Wrapper,
  Wrapper$1,
  getDisplayPrices,
  getGridColumn,
  getParentProductsAndAddons,
  getProductIsSelectedLoading,
  getProductsForDisplay
};
